//     Underscore.js 1.8.3
//     http://underscorejs.org
//     (c) 2009-2015 Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
//     Underscore may be freely distributed under the MIT license.

(function() {

  // Baseline setup
  // --------------

  // Establish the root object, `window` in the browser, or `exports` on the server.
  var root = this;

  // Save the previous value of the `_` variable.
  var previousUnderscore = root._;

  // Save bytes in the minified (but not gzipped) version:
  var ArrayProto = Array.prototype, ObjProto = Object.prototype, FuncProto = Function.prototype;

  // Create quick reference variables for speed access to core prototypes.
  var
    push             = ArrayProto.push,
    slice            = ArrayProto.slice,
    toString         = ObjProto.toString,
    hasOwnProperty   = ObjProto.hasOwnProperty;

  // All **ECMAScript 5** native function implementations that we hope to use
  // are declared here.
  var
    nativeIsArray      = Array.isArray,
    nativeKeys         = Object.keys,
    nativeBind         = FuncProto.bind,
    nativeCreate       = Object.create;

  // Naked function reference for surrogate-prototype-swapping.
  var Ctor = function(){};

  // Create a safe reference to the Underscore object for use below.
  var _ = function(obj) {
    if (obj instanceof _) return obj;
    if (!(this instanceof _)) return new _(obj);
    this._wrapped = obj;
  };

  // Export the Underscore object for **Node.js**, with
  // backwards-compatibility for the old `require()` API. If we're in
  // the browser, add `_` as a global object.
  if (typeof exports !== 'undefined') {
    if (typeof module !== 'undefined' && module.exports) {
      exports = module.exports = _;
    }
    exports._ = _;
  } else {
    root._ = _;
  }

  // Current version.
  _.VERSION = '1.8.3';

  // Internal function that returns an efficient (for current engines) version
  // of the passed-in callback, to be repeatedly applied in other Underscore
  // functions.
  var optimizeCb = function(func, context, argCount) {
    if (context === void 0) return func;
    switch (argCount == null ? 3 : argCount) {
      case 1: return function(value) {
        return func.call(context, value);
      };
      case 2: return function(value, other) {
        return func.call(context, value, other);
      };
      case 3: return function(value, index, collection) {
        return func.call(context, value, index, collection);
      };
      case 4: return function(accumulator, value, index, collection) {
        return func.call(context, accumulator, value, index, collection);
      };
    }
    return function() {
      return func.apply(context, arguments);
    };
  };

  // A mostly-internal function to generate callbacks that can be applied
  // to each element in a collection, returning the desired result — either
  // identity, an arbitrary callback, a property matcher, or a property accessor.
  var cb = function(value, context, argCount) {
    if (value == null) return _.identity;
    if (_.isFunction(value)) return optimizeCb(value, context, argCount);
    if (_.isObject(value)) return _.matcher(value);
    return _.property(value);
  };
  _.iteratee = function(value, context) {
    return cb(value, context, Infinity);
  };

  // An internal function for creating assigner functions.
  var createAssigner = function(keysFunc, undefinedOnly) {
    return function(obj) {
      var length = arguments.length;
      if (length < 2 || obj == null) return obj;
      for (var index = 1; index < length; index++) {
        var source = arguments[index],
            keys = keysFunc(source),
            l = keys.length;
        for (var i = 0; i < l; i++) {
          var key = keys[i];
          if (!undefinedOnly || obj[key] === void 0) obj[key] = source[key];
        }
      }
      return obj;
    };
  };

  // An internal function for creating a new object that inherits from another.
  var baseCreate = function(prototype) {
    if (!_.isObject(prototype)) return {};
    if (nativeCreate) return nativeCreate(prototype);
    Ctor.prototype = prototype;
    var result = new Ctor;
    Ctor.prototype = null;
    return result;
  };

  var property = function(key) {
    return function(obj) {
      return obj == null ? void 0 : obj[key];
    };
  };

  // Helper for collection methods to determine whether a collection
  // should be iterated as an array or as an object
  // Related: http://people.mozilla.org/~jorendorff/es6-draft.html#sec-tolength
  // Avoids a very nasty iOS 8 JIT bug on ARM-64. #2094
  var MAX_ARRAY_INDEX = Math.pow(2, 53) - 1;
  var getLength = property('length');
  var isArrayLike = function(collection) {
    var length = getLength(collection);
    return typeof length == 'number' && length >= 0 && length <= MAX_ARRAY_INDEX;
  };

  // Collection Functions
  // --------------------

  // The cornerstone, an `each` implementation, aka `forEach`.
  // Handles raw objects in addition to array-likes. Treats all
  // sparse array-likes as if they were dense.
  _.each = _.forEach = function(obj, iteratee, context) {
    iteratee = optimizeCb(iteratee, context);
    var i, length;
    if (isArrayLike(obj)) {
      for (i = 0, length = obj.length; i < length; i++) {
        iteratee(obj[i], i, obj);
      }
    } else {
      var keys = _.keys(obj);
      for (i = 0, length = keys.length; i < length; i++) {
        iteratee(obj[keys[i]], keys[i], obj);
      }
    }
    return obj;
  };

  // Return the results of applying the iteratee to each element.
  _.map = _.collect = function(obj, iteratee, context) {
    iteratee = cb(iteratee, context);
    var keys = !isArrayLike(obj) && _.keys(obj),
        length = (keys || obj).length,
        results = Array(length);
    for (var index = 0; index < length; index++) {
      var currentKey = keys ? keys[index] : index;
      results[index] = iteratee(obj[currentKey], currentKey, obj);
    }
    return results;
  };

  // Create a reducing function iterating left or right.
  function createReduce(dir) {
    // Optimized iterator function as using arguments.length
    // in the main function will deoptimize the, see #1991.
    function iterator(obj, iteratee, memo, keys, index, length) {
      for (; index >= 0 && index < length; index += dir) {
        var currentKey = keys ? keys[index] : index;
        memo = iteratee(memo, obj[currentKey], currentKey, obj);
      }
      return memo;
    }

    return function(obj, iteratee, memo, context) {
      iteratee = optimizeCb(iteratee, context, 4);
      var keys = !isArrayLike(obj) && _.keys(obj),
          length = (keys || obj).length,
          index = dir > 0 ? 0 : length - 1;
      // Determine the initial value if none is provided.
      if (arguments.length < 3) {
        memo = obj[keys ? keys[index] : index];
        index += dir;
      }
      return iterator(obj, iteratee, memo, keys, index, length);
    };
  }

  // **Reduce** builds up a single result from a list of values, aka `inject`,
  // or `foldl`.
  _.reduce = _.foldl = _.inject = createReduce(1);

  // The right-associative version of reduce, also known as `foldr`.
  _.reduceRight = _.foldr = createReduce(-1);

  // Return the first value which passes a truth test. Aliased as `detect`.
  _.find = _.detect = function(obj, predicate, context) {
    var key;
    if (isArrayLike(obj)) {
      key = _.findIndex(obj, predicate, context);
    } else {
      key = _.findKey(obj, predicate, context);
    }
    if (key !== void 0 && key !== -1) return obj[key];
  };

  // Return all the elements that pass a truth test.
  // Aliased as `select`.
  _.filter = _.select = function(obj, predicate, context) {
    var results = [];
    predicate = cb(predicate, context);
    _.each(obj, function(value, index, list) {
      if (predicate(value, index, list)) results.push(value);
    });
    return results;
  };

  // Return all the elements for which a truth test fails.
  _.reject = function(obj, predicate, context) {
    return _.filter(obj, _.negate(cb(predicate)), context);
  };

  // Determine whether all of the elements match a truth test.
  // Aliased as `all`.
  _.every = _.all = function(obj, predicate, context) {
    predicate = cb(predicate, context);
    var keys = !isArrayLike(obj) && _.keys(obj),
        length = (keys || obj).length;
    for (var index = 0; index < length; index++) {
      var currentKey = keys ? keys[index] : index;
      if (!predicate(obj[currentKey], currentKey, obj)) return false;
    }
    return true;
  };

  // Determine if at least one element in the object matches a truth test.
  // Aliased as `any`.
  _.some = _.any = function(obj, predicate, context) {
    predicate = cb(predicate, context);
    var keys = !isArrayLike(obj) && _.keys(obj),
        length = (keys || obj).length;
    for (var index = 0; index < length; index++) {
      var currentKey = keys ? keys[index] : index;
      if (predicate(obj[currentKey], currentKey, obj)) return true;
    }
    return false;
  };

  // Determine if the array or object contains a given item (using `===`).
  // Aliased as `includes` and `include`.
  _.contains = _.includes = _.include = function(obj, item, fromIndex, guard) {
    if (!isArrayLike(obj)) obj = _.values(obj);
    if (typeof fromIndex != 'number' || guard) fromIndex = 0;
    return _.indexOf(obj, item, fromIndex) >= 0;
  };

  // Invoke a method (with arguments) on every item in a collection.
  _.invoke = function(obj, method) {
    var args = slice.call(arguments, 2);
    var isFunc = _.isFunction(method);
    return _.map(obj, function(value) {
      var func = isFunc ? method : value[method];
      return func == null ? func : func.apply(value, args);
    });
  };

  // Convenience version of a common use case of `map`: fetching a property.
  _.pluck = function(obj, key) {
    return _.map(obj, _.property(key));
  };

  // Convenience version of a common use case of `filter`: selecting only objects
  // containing specific `key:value` pairs.
  _.where = function(obj, attrs) {
    return _.filter(obj, _.matcher(attrs));
  };

  // Convenience version of a common use case of `find`: getting the first object
  // containing specific `key:value` pairs.
  _.findWhere = function(obj, attrs) {
    return _.find(obj, _.matcher(attrs));
  };

  // Return the maximum element (or element-based computation).
  _.max = function(obj, iteratee, context) {
    var result = -Infinity, lastComputed = -Infinity,
        value, computed;
    if (iteratee == null && obj != null) {
      obj = isArrayLike(obj) ? obj : _.values(obj);
      for (var i = 0, length = obj.length; i < length; i++) {
        value = obj[i];
        if (value > result) {
          result = value;
        }
      }
    } else {
      iteratee = cb(iteratee, context);
      _.each(obj, function(value, index, list) {
        computed = iteratee(value, index, list);
        if (computed > lastComputed || computed === -Infinity && result === -Infinity) {
          result = value;
          lastComputed = computed;
        }
      });
    }
    return result;
  };

  // Return the minimum element (or element-based computation).
  _.min = function(obj, iteratee, context) {
    var result = Infinity, lastComputed = Infinity,
        value, computed;
    if (iteratee == null && obj != null) {
      obj = isArrayLike(obj) ? obj : _.values(obj);
      for (var i = 0, length = obj.length; i < length; i++) {
        value = obj[i];
        if (value < result) {
          result = value;
        }
      }
    } else {
      iteratee = cb(iteratee, context);
      _.each(obj, function(value, index, list) {
        computed = iteratee(value, index, list);
        if (computed < lastComputed || computed === Infinity && result === Infinity) {
          result = value;
          lastComputed = computed;
        }
      });
    }
    return result;
  };

  // Shuffle a collection, using the modern version of the
  // [Fisher-Yates shuffle](http://en.wikipedia.org/wiki/Fisher–Yates_shuffle).
  _.shuffle = function(obj) {
    var set = isArrayLike(obj) ? obj : _.values(obj);
    var length = set.length;
    var shuffled = Array(length);
    for (var index = 0, rand; index < length; index++) {
      rand = _.random(0, index);
      if (rand !== index) shuffled[index] = shuffled[rand];
      shuffled[rand] = set[index];
    }
    return shuffled;
  };

  // Sample **n** random values from a collection.
  // If **n** is not specified, returns a single random element.
  // The internal `guard` argument allows it to work with `map`.
  _.sample = function(obj, n, guard) {
    if (n == null || guard) {
      if (!isArrayLike(obj)) obj = _.values(obj);
      return obj[_.random(obj.length - 1)];
    }
    return _.shuffle(obj).slice(0, Math.max(0, n));
  };

  // Sort the object's values by a criterion produced by an iteratee.
  _.sortBy = function(obj, iteratee, context) {
    iteratee = cb(iteratee, context);
    return _.pluck(_.map(obj, function(value, index, list) {
      return {
        value: value,
        index: index,
        criteria: iteratee(value, index, list)
      };
    }).sort(function(left, right) {
      var a = left.criteria;
      var b = right.criteria;
      if (a !== b) {
        if (a > b || a === void 0) return 1;
        if (a < b || b === void 0) return -1;
      }
      return left.index - right.index;
    }), 'value');
  };

  // An internal function used for aggregate "group by" operations.
  var group = function(behavior) {
    return function(obj, iteratee, context) {
      var result = {};
      iteratee = cb(iteratee, context);
      _.each(obj, function(value, index) {
        var key = iteratee(value, index, obj);
        behavior(result, value, key);
      });
      return result;
    };
  };

  // Groups the object's values by a criterion. Pass either a string attribute
  // to group by, or a function that returns the criterion.
  _.groupBy = group(function(result, value, key) {
    if (_.has(result, key)) result[key].push(value); else result[key] = [value];
  });

  // Indexes the object's values by a criterion, similar to `groupBy`, but for
  // when you know that your index values will be unique.
  _.indexBy = group(function(result, value, key) {
    result[key] = value;
  });

  // Counts instances of an object that group by a certain criterion. Pass
  // either a string attribute to count by, or a function that returns the
  // criterion.
  _.countBy = group(function(result, value, key) {
    if (_.has(result, key)) result[key]++; else result[key] = 1;
  });

  // Safely create a real, live array from anything iterable.
  _.toArray = function(obj) {
    if (!obj) return [];
    if (_.isArray(obj)) return slice.call(obj);
    if (isArrayLike(obj)) return _.map(obj, _.identity);
    return _.values(obj);
  };

  // Return the number of elements in an object.
  _.size = function(obj) {
    if (obj == null) return 0;
    return isArrayLike(obj) ? obj.length : _.keys(obj).length;
  };

  // Split a collection into two arrays: one whose elements all satisfy the given
  // predicate, and one whose elements all do not satisfy the predicate.
  _.partition = function(obj, predicate, context) {
    predicate = cb(predicate, context);
    var pass = [], fail = [];
    _.each(obj, function(value, key, obj) {
      (predicate(value, key, obj) ? pass : fail).push(value);
    });
    return [pass, fail];
  };

  // Array Functions
  // ---------------

  // Get the first element of an array. Passing **n** will return the first N
  // values in the array. Aliased as `head` and `take`. The **guard** check
  // allows it to work with `_.map`.
  _.first = _.head = _.take = function(array, n, guard) {
    if (array == null) return void 0;
    if (n == null || guard) return array[0];
    return _.initial(array, array.length - n);
  };

  // Returns everything but the last entry of the array. Especially useful on
  // the arguments object. Passing **n** will return all the values in
  // the array, excluding the last N.
  _.initial = function(array, n, guard) {
    return slice.call(array, 0, Math.max(0, array.length - (n == null || guard ? 1 : n)));
  };

  // Get the last element of an array. Passing **n** will return the last N
  // values in the array.
  _.last = function(array, n, guard) {
    if (array == null) return void 0;
    if (n == null || guard) return array[array.length - 1];
    return _.rest(array, Math.max(0, array.length - n));
  };

  // Returns everything but the first entry of the array. Aliased as `tail` and `drop`.
  // Especially useful on the arguments object. Passing an **n** will return
  // the rest N values in the array.
  _.rest = _.tail = _.drop = function(array, n, guard) {
    return slice.call(array, n == null || guard ? 1 : n);
  };

  // Trim out all falsy values from an array.
  _.compact = function(array) {
    return _.filter(array, _.identity);
  };

  // Internal implementation of a recursive `flatten` function.
  var flatten = function(input, shallow, strict, startIndex) {
    var output = [], idx = 0;
    for (var i = startIndex || 0, length = getLength(input); i < length; i++) {
      var value = input[i];
      if (isArrayLike(value) && (_.isArray(value) || _.isArguments(value))) {
        //flatten current level of array or arguments object
        if (!shallow) value = flatten(value, shallow, strict);
        var j = 0, len = value.length;
        output.length += len;
        while (j < len) {
          output[idx++] = value[j++];
        }
      } else if (!strict) {
        output[idx++] = value;
      }
    }
    return output;
  };

  // Flatten out an array, either recursively (by default), or just one level.
  _.flatten = function(array, shallow) {
    return flatten(array, shallow, false);
  };

  // Return a version of the array that does not contain the specified value(s).
  _.without = function(array) {
    return _.difference(array, slice.call(arguments, 1));
  };

  // Produce a duplicate-free version of the array. If the array has already
  // been sorted, you have the option of using a faster algorithm.
  // Aliased as `unique`.
  _.uniq = _.unique = function(array, isSorted, iteratee, context) {
    if (!_.isBoolean(isSorted)) {
      context = iteratee;
      iteratee = isSorted;
      isSorted = false;
    }
    if (iteratee != null) iteratee = cb(iteratee, context);
    var result = [];
    var seen = [];
    for (var i = 0, length = getLength(array); i < length; i++) {
      var value = array[i],
          computed = iteratee ? iteratee(value, i, array) : value;
      if (isSorted) {
        if (!i || seen !== computed) result.push(value);
        seen = computed;
      } else if (iteratee) {
        if (!_.contains(seen, computed)) {
          seen.push(computed);
          result.push(value);
        }
      } else if (!_.contains(result, value)) {
        result.push(value);
      }
    }
    return result;
  };

  // Produce an array that contains the union: each distinct element from all of
  // the passed-in arrays.
  _.union = function() {
    return _.uniq(flatten(arguments, true, true));
  };

  // Produce an array that contains every item shared between all the
  // passed-in arrays.
  _.intersection = function(array) {
    var result = [];
    var argsLength = arguments.length;
    for (var i = 0, length = getLength(array); i < length; i++) {
      var item = array[i];
      if (_.contains(result, item)) continue;
      for (var j = 1; j < argsLength; j++) {
        if (!_.contains(arguments[j], item)) break;
      }
      if (j === argsLength) result.push(item);
    }
    return result;
  };

  // Take the difference between one array and a number of other arrays.
  // Only the elements present in just the first array will remain.
  _.difference = function(array) {
    var rest = flatten(arguments, true, true, 1);
    return _.filter(array, function(value){
      return !_.contains(rest, value);
    });
  };

  // Zip together multiple lists into a single array -- elements that share
  // an index go together.
  _.zip = function() {
    return _.unzip(arguments);
  };

  // Complement of _.zip. Unzip accepts an array of arrays and groups
  // each array's elements on shared indices
  _.unzip = function(array) {
    var length = array && _.max(array, getLength).length || 0;
    var result = Array(length);

    for (var index = 0; index < length; index++) {
      result[index] = _.pluck(array, index);
    }
    return result;
  };

  // Converts lists into objects. Pass either a single array of `[key, value]`
  // pairs, or two parallel arrays of the same length -- one of keys, and one of
  // the corresponding values.
  _.object = function(list, values) {
    var result = {};
    for (var i = 0, length = getLength(list); i < length; i++) {
      if (values) {
        result[list[i]] = values[i];
      } else {
        result[list[i][0]] = list[i][1];
      }
    }
    return result;
  };

  // Generator function to create the findIndex and findLastIndex functions
  function createPredicateIndexFinder(dir) {
    return function(array, predicate, context) {
      predicate = cb(predicate, context);
      var length = getLength(array);
      var index = dir > 0 ? 0 : length - 1;
      for (; index >= 0 && index < length; index += dir) {
        if (predicate(array[index], index, array)) return index;
      }
      return -1;
    };
  }

  // Returns the first index on an array-like that passes a predicate test
  _.findIndex = createPredicateIndexFinder(1);
  _.findLastIndex = createPredicateIndexFinder(-1);

  // Use a comparator function to figure out the smallest index at which
  // an object should be inserted so as to maintain order. Uses binary search.
  _.sortedIndex = function(array, obj, iteratee, context) {
    iteratee = cb(iteratee, context, 1);
    var value = iteratee(obj);
    var low = 0, high = getLength(array);
    while (low < high) {
      var mid = Math.floor((low + high) / 2);
      if (iteratee(array[mid]) < value) low = mid + 1; else high = mid;
    }
    return low;
  };

  // Generator function to create the indexOf and lastIndexOf functions
  function createIndexFinder(dir, predicateFind, sortedIndex) {
    return function(array, item, idx) {
      var i = 0, length = getLength(array);
      if (typeof idx == 'number') {
        if (dir > 0) {
            i = idx >= 0 ? idx : Math.max(idx + length, i);
        } else {
            length = idx >= 0 ? Math.min(idx + 1, length) : idx + length + 1;
        }
      } else if (sortedIndex && idx && length) {
        idx = sortedIndex(array, item);
        return array[idx] === item ? idx : -1;
      }
      if (item !== item) {
        idx = predicateFind(slice.call(array, i, length), _.isNaN);
        return idx >= 0 ? idx + i : -1;
      }
      for (idx = dir > 0 ? i : length - 1; idx >= 0 && idx < length; idx += dir) {
        if (array[idx] === item) return idx;
      }
      return -1;
    };
  }

  // Return the position of the first occurrence of an item in an array,
  // or -1 if the item is not included in the array.
  // If the array is large and already in sort order, pass `true`
  // for **isSorted** to use binary search.
  _.indexOf = createIndexFinder(1, _.findIndex, _.sortedIndex);
  _.lastIndexOf = createIndexFinder(-1, _.findLastIndex);

  // Generate an integer Array containing an arithmetic progression. A port of
  // the native Python `range()` function. See
  // [the Python documentation](http://docs.python.org/library/functions.html#range).
  _.range = function(start, stop, step) {
    if (stop == null) {
      stop = start || 0;
      start = 0;
    }
    step = step || 1;

    var length = Math.max(Math.ceil((stop - start) / step), 0);
    var range = Array(length);

    for (var idx = 0; idx < length; idx++, start += step) {
      range[idx] = start;
    }

    return range;
  };

  // Function (ahem) Functions
  // ------------------

  // Determines whether to execute a function as a constructor
  // or a normal function with the provided arguments
  var executeBound = function(sourceFunc, boundFunc, context, callingContext, args) {
    if (!(callingContext instanceof boundFunc)) return sourceFunc.apply(context, args);
    var self = baseCreate(sourceFunc.prototype);
    var result = sourceFunc.apply(self, args);
    if (_.isObject(result)) return result;
    return self;
  };

  // Create a function bound to a given object (assigning `this`, and arguments,
  // optionally). Delegates to **ECMAScript 5**'s native `Function.bind` if
  // available.
  _.bind = function(func, context) {
    if (nativeBind && func.bind === nativeBind) return nativeBind.apply(func, slice.call(arguments, 1));
    if (!_.isFunction(func)) throw new TypeError('Bind must be called on a function');
    var args = slice.call(arguments, 2);
    var bound = function() {
      return executeBound(func, bound, context, this, args.concat(slice.call(arguments)));
    };
    return bound;
  };

  // Partially apply a function by creating a version that has had some of its
  // arguments pre-filled, without changing its dynamic `this` context. _ acts
  // as a placeholder, allowing any combination of arguments to be pre-filled.
  _.partial = function(func) {
    var boundArgs = slice.call(arguments, 1);
    var bound = function() {
      var position = 0, length = boundArgs.length;
      var args = Array(length);
      for (var i = 0; i < length; i++) {
        args[i] = boundArgs[i] === _ ? arguments[position++] : boundArgs[i];
      }
      while (position < arguments.length) args.push(arguments[position++]);
      return executeBound(func, bound, this, this, args);
    };
    return bound;
  };

  // Bind a number of an object's methods to that object. Remaining arguments
  // are the method names to be bound. Useful for ensuring that all callbacks
  // defined on an object belong to it.
  _.bindAll = function(obj) {
    var i, length = arguments.length, key;
    if (length <= 1) throw new Error('bindAll must be passed function names');
    for (i = 1; i < length; i++) {
      key = arguments[i];
      obj[key] = _.bind(obj[key], obj);
    }
    return obj;
  };

  // Memoize an expensive function by storing its results.
  _.memoize = function(func, hasher) {
    var memoize = function(key) {
      var cache = memoize.cache;
      var address = '' + (hasher ? hasher.apply(this, arguments) : key);
      if (!_.has(cache, address)) cache[address] = func.apply(this, arguments);
      return cache[address];
    };
    memoize.cache = {};
    return memoize;
  };

  // Delays a function for the given number of milliseconds, and then calls
  // it with the arguments supplied.
  _.delay = function(func, wait) {
    var args = slice.call(arguments, 2);
    return setTimeout(function(){
      return func.apply(null, args);
    }, wait);
  };

  // Defers a function, scheduling it to run after the current call stack has
  // cleared.
  _.defer = _.partial(_.delay, _, 1);

  // Returns a function, that, when invoked, will only be triggered at most once
  // during a given window of time. Normally, the throttled function will run
  // as much as it can, without ever going more than once per `wait` duration;
  // but if you'd like to disable the execution on the leading edge, pass
  // `{leading: false}`. To disable execution on the trailing edge, ditto.
  _.throttle = function(func, wait, options) {
    var context, args, result;
    var timeout = null;
    var previous = 0;
    if (!options) options = {};
    var later = function() {
      previous = options.leading === false ? 0 : _.now();
      timeout = null;
      result = func.apply(context, args);
      if (!timeout) context = args = null;
    };
    return function() {
      var now = _.now();
      if (!previous && options.leading === false) previous = now;
      var remaining = wait - (now - previous);
      context = this;
      args = arguments;
      if (remaining <= 0 || remaining > wait) {
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }
        previous = now;
        result = func.apply(context, args);
        if (!timeout) context = args = null;
      } else if (!timeout && options.trailing !== false) {
        timeout = setTimeout(later, remaining);
      }
      return result;
    };
  };

  // Returns a function, that, as long as it continues to be invoked, will not
  // be triggered. The function will be called after it stops being called for
  // N milliseconds. If `immediate` is passed, trigger the function on the
  // leading edge, instead of the trailing.
  _.debounce = function(func, wait, immediate) {
    var timeout, args, context, timestamp, result;

    var later = function() {
      var last = _.now() - timestamp;

      if (last < wait && last >= 0) {
        timeout = setTimeout(later, wait - last);
      } else {
        timeout = null;
        if (!immediate) {
          result = func.apply(context, args);
          if (!timeout) context = args = null;
        }
      }
    };

    return function() {
      context = this;
      args = arguments;
      timestamp = _.now();
      var callNow = immediate && !timeout;
      if (!timeout) timeout = setTimeout(later, wait);
      if (callNow) {
        result = func.apply(context, args);
        context = args = null;
      }

      return result;
    };
  };

  // Returns the first function passed as an argument to the second,
  // allowing you to adjust arguments, run code before and after, and
  // conditionally execute the original function.
  _.wrap = function(func, wrapper) {
    return _.partial(wrapper, func);
  };

  // Returns a negated version of the passed-in predicate.
  _.negate = function(predicate) {
    return function() {
      return !predicate.apply(this, arguments);
    };
  };

  // Returns a function that is the composition of a list of functions, each
  // consuming the return value of the function that follows.
  _.compose = function() {
    var args = arguments;
    var start = args.length - 1;
    return function() {
      var i = start;
      var result = args[start].apply(this, arguments);
      while (i--) result = args[i].call(this, result);
      return result;
    };
  };

  // Returns a function that will only be executed on and after the Nth call.
  _.after = function(times, func) {
    return function() {
      if (--times < 1) {
        return func.apply(this, arguments);
      }
    };
  };

  // Returns a function that will only be executed up to (but not including) the Nth call.
  _.before = function(times, func) {
    var memo;
    return function() {
      if (--times > 0) {
        memo = func.apply(this, arguments);
      }
      if (times <= 1) func = null;
      return memo;
    };
  };

  // Returns a function that will be executed at most one time, no matter how
  // often you call it. Useful for lazy initialization.
  _.once = _.partial(_.before, 2);

  // Object Functions
  // ----------------

  // Keys in IE < 9 that won't be iterated by `for key in ...` and thus missed.
  var hasEnumBug = !{toString: null}.propertyIsEnumerable('toString');
  var nonEnumerableProps = ['valueOf', 'isPrototypeOf', 'toString',
                      'propertyIsEnumerable', 'hasOwnProperty', 'toLocaleString'];

  function collectNonEnumProps(obj, keys) {
    var nonEnumIdx = nonEnumerableProps.length;
    var constructor = obj.constructor;
    var proto = (_.isFunction(constructor) && constructor.prototype) || ObjProto;

    // Constructor is a special case.
    var prop = 'constructor';
    if (_.has(obj, prop) && !_.contains(keys, prop)) keys.push(prop);

    while (nonEnumIdx--) {
      prop = nonEnumerableProps[nonEnumIdx];
      if (prop in obj && obj[prop] !== proto[prop] && !_.contains(keys, prop)) {
        keys.push(prop);
      }
    }
  }

  // Retrieve the names of an object's own properties.
  // Delegates to **ECMAScript 5**'s native `Object.keys`
  _.keys = function(obj) {
    if (!_.isObject(obj)) return [];
    if (nativeKeys) return nativeKeys(obj);
    var keys = [];
    for (var key in obj) if (_.has(obj, key)) keys.push(key);
    // Ahem, IE < 9.
    if (hasEnumBug) collectNonEnumProps(obj, keys);
    return keys;
  };

  // Retrieve all the property names of an object.
  _.allKeys = function(obj) {
    if (!_.isObject(obj)) return [];
    var keys = [];
    for (var key in obj) keys.push(key);
    // Ahem, IE < 9.
    if (hasEnumBug) collectNonEnumProps(obj, keys);
    return keys;
  };

  // Retrieve the values of an object's properties.
  _.values = function(obj) {
    var keys = _.keys(obj);
    var length = keys.length;
    var values = Array(length);
    for (var i = 0; i < length; i++) {
      values[i] = obj[keys[i]];
    }
    return values;
  };

  // Returns the results of applying the iteratee to each element of the object
  // In contrast to _.map it returns an object
  _.mapObject = function(obj, iteratee, context) {
    iteratee = cb(iteratee, context);
    var keys =  _.keys(obj),
          length = keys.length,
          results = {},
          currentKey;
      for (var index = 0; index < length; index++) {
        currentKey = keys[index];
        results[currentKey] = iteratee(obj[currentKey], currentKey, obj);
      }
      return results;
  };

  // Convert an object into a list of `[key, value]` pairs.
  _.pairs = function(obj) {
    var keys = _.keys(obj);
    var length = keys.length;
    var pairs = Array(length);
    for (var i = 0; i < length; i++) {
      pairs[i] = [keys[i], obj[keys[i]]];
    }
    return pairs;
  };

  // Invert the keys and values of an object. The values must be serializable.
  _.invert = function(obj) {
    var result = {};
    var keys = _.keys(obj);
    for (var i = 0, length = keys.length; i < length; i++) {
      result[obj[keys[i]]] = keys[i];
    }
    return result;
  };

  // Return a sorted list of the function names available on the object.
  // Aliased as `methods`
  _.functions = _.methods = function(obj) {
    var names = [];
    for (var key in obj) {
      if (_.isFunction(obj[key])) names.push(key);
    }
    return names.sort();
  };

  // Extend a given object with all the properties in passed-in object(s).
  _.extend = createAssigner(_.allKeys);

  // Assigns a given object with all the own properties in the passed-in object(s)
  // (https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Object/assign)
  _.extendOwn = _.assign = createAssigner(_.keys);

  // Returns the first key on an object that passes a predicate test
  _.findKey = function(obj, predicate, context) {
    predicate = cb(predicate, context);
    var keys = _.keys(obj), key;
    for (var i = 0, length = keys.length; i < length; i++) {
      key = keys[i];
      if (predicate(obj[key], key, obj)) return key;
    }
  };

  // Return a copy of the object only containing the whitelisted properties.
  _.pick = function(object, oiteratee, context) {
    var result = {}, obj = object, iteratee, keys;
    if (obj == null) return result;
    if (_.isFunction(oiteratee)) {
      keys = _.allKeys(obj);
      iteratee = optimizeCb(oiteratee, context);
    } else {
      keys = flatten(arguments, false, false, 1);
      iteratee = function(value, key, obj) { return key in obj; };
      obj = Object(obj);
    }
    for (var i = 0, length = keys.length; i < length; i++) {
      var key = keys[i];
      var value = obj[key];
      if (iteratee(value, key, obj)) result[key] = value;
    }
    return result;
  };

   // Return a copy of the object without the blacklisted properties.
  _.omit = function(obj, iteratee, context) {
    if (_.isFunction(iteratee)) {
      iteratee = _.negate(iteratee);
    } else {
      var keys = _.map(flatten(arguments, false, false, 1), String);
      iteratee = function(value, key) {
        return !_.contains(keys, key);
      };
    }
    return _.pick(obj, iteratee, context);
  };

  // Fill in a given object with default properties.
  _.defaults = createAssigner(_.allKeys, true);

  // Creates an object that inherits from the given prototype object.
  // If additional properties are provided then they will be added to the
  // created object.
  _.create = function(prototype, props) {
    var result = baseCreate(prototype);
    if (props) _.extendOwn(result, props);
    return result;
  };

  // Create a (shallow-cloned) duplicate of an object.
  _.clone = function(obj) {
    if (!_.isObject(obj)) return obj;
    return _.isArray(obj) ? obj.slice() : _.extend({}, obj);
  };

  // Invokes interceptor with the obj, and then returns obj.
  // The primary purpose of this method is to "tap into" a method chain, in
  // order to perform operations on intermediate results within the chain.
  _.tap = function(obj, interceptor) {
    interceptor(obj);
    return obj;
  };

  // Returns whether an object has a given set of `key:value` pairs.
  _.isMatch = function(object, attrs) {
    var keys = _.keys(attrs), length = keys.length;
    if (object == null) return !length;
    var obj = Object(object);
    for (var i = 0; i < length; i++) {
      var key = keys[i];
      if (attrs[key] !== obj[key] || !(key in obj)) return false;
    }
    return true;
  };


  // Internal recursive comparison function for `isEqual`.
  var eq = function(a, b, aStack, bStack) {
    // Identical objects are equal. `0 === -0`, but they aren't identical.
    // See the [Harmony `egal` proposal](http://wiki.ecmascript.org/doku.php?id=harmony:egal).
    if (a === b) return a !== 0 || 1 / a === 1 / b;
    // A strict comparison is necessary because `null == undefined`.
    if (a == null || b == null) return a === b;
    // Unwrap any wrapped objects.
    if (a instanceof _) a = a._wrapped;
    if (b instanceof _) b = b._wrapped;
    // Compare `[[Class]]` names.
    var className = toString.call(a);
    if (className !== toString.call(b)) return false;
    switch (className) {
      // Strings, numbers, regular expressions, dates, and booleans are compared by value.
      case '[object RegExp]':
      // RegExps are coerced to strings for comparison (Note: '' + /a/i === '/a/i')
      case '[object String]':
        // Primitives and their corresponding object wrappers are equivalent; thus, `"5"` is
        // equivalent to `new String("5")`.
        return '' + a === '' + b;
      case '[object Number]':
        // `NaN`s are equivalent, but non-reflexive.
        // Object(NaN) is equivalent to NaN
        if (+a !== +a) return +b !== +b;
        // An `egal` comparison is performed for other numeric values.
        return +a === 0 ? 1 / +a === 1 / b : +a === +b;
      case '[object Date]':
      case '[object Boolean]':
        // Coerce dates and booleans to numeric primitive values. Dates are compared by their
        // millisecond representations. Note that invalid dates with millisecond representations
        // of `NaN` are not equivalent.
        return +a === +b;
    }

    var areArrays = className === '[object Array]';
    if (!areArrays) {
      if (typeof a != 'object' || typeof b != 'object') return false;

      // Objects with different constructors are not equivalent, but `Object`s or `Array`s
      // from different frames are.
      var aCtor = a.constructor, bCtor = b.constructor;
      if (aCtor !== bCtor && !(_.isFunction(aCtor) && aCtor instanceof aCtor &&
                               _.isFunction(bCtor) && bCtor instanceof bCtor)
                          && ('constructor' in a && 'constructor' in b)) {
        return false;
      }
    }
    // Assume equality for cyclic structures. The algorithm for detecting cyclic
    // structures is adapted from ES 5.1 section 15.12.3, abstract operation `JO`.

    // Initializing stack of traversed objects.
    // It's done here since we only need them for objects and arrays comparison.
    aStack = aStack || [];
    bStack = bStack || [];
    var length = aStack.length;
    while (length--) {
      // Linear search. Performance is inversely proportional to the number of
      // unique nested structures.
      if (aStack[length] === a) return bStack[length] === b;
    }

    // Add the first object to the stack of traversed objects.
    aStack.push(a);
    bStack.push(b);

    // Recursively compare objects and arrays.
    if (areArrays) {
      // Compare array lengths to determine if a deep comparison is necessary.
      length = a.length;
      if (length !== b.length) return false;
      // Deep compare the contents, ignoring non-numeric properties.
      while (length--) {
        if (!eq(a[length], b[length], aStack, bStack)) return false;
      }
    } else {
      // Deep compare objects.
      var keys = _.keys(a), key;
      length = keys.length;
      // Ensure that both objects contain the same number of properties before comparing deep equality.
      if (_.keys(b).length !== length) return false;
      while (length--) {
        // Deep compare each member
        key = keys[length];
        if (!(_.has(b, key) && eq(a[key], b[key], aStack, bStack))) return false;
      }
    }
    // Remove the first object from the stack of traversed objects.
    aStack.pop();
    bStack.pop();
    return true;
  };

  // Perform a deep comparison to check if two objects are equal.
  _.isEqual = function(a, b) {
    return eq(a, b);
  };

  // Is a given array, string, or object empty?
  // An "empty" object has no enumerable own-properties.
  _.isEmpty = function(obj) {
    if (obj == null) return true;
    if (isArrayLike(obj) && (_.isArray(obj) || _.isString(obj) || _.isArguments(obj))) return obj.length === 0;
    return _.keys(obj).length === 0;
  };

  // Is a given value a DOM element?
  _.isElement = function(obj) {
    return !!(obj && obj.nodeType === 1);
  };

  // Is a given value an array?
  // Delegates to ECMA5's native Array.isArray
  _.isArray = nativeIsArray || function(obj) {
    return toString.call(obj) === '[object Array]';
  };

  // Is a given variable an object?
  _.isObject = function(obj) {
    var type = typeof obj;
    return type === 'function' || type === 'object' && !!obj;
  };

  // Add some isType methods: isArguments, isFunction, isString, isNumber, isDate, isRegExp, isError.
  _.each(['Arguments', 'Function', 'String', 'Number', 'Date', 'RegExp', 'Error'], function(name) {
    _['is' + name] = function(obj) {
      return toString.call(obj) === '[object ' + name + ']';
    };
  });

  // Define a fallback version of the method in browsers (ahem, IE < 9), where
  // there isn't any inspectable "Arguments" type.
  if (!_.isArguments(arguments)) {
    _.isArguments = function(obj) {
      return _.has(obj, 'callee');
    };
  }

  // Optimize `isFunction` if appropriate. Work around some typeof bugs in old v8,
  // IE 11 (#1621), and in Safari 8 (#1929).
  if (typeof /./ != 'function' && typeof Int8Array != 'object') {
    _.isFunction = function(obj) {
      return typeof obj == 'function' || false;
    };
  }

  // Is a given object a finite number?
  _.isFinite = function(obj) {
    return isFinite(obj) && !isNaN(parseFloat(obj));
  };

  // Is the given value `NaN`? (NaN is the only number which does not equal itself).
  _.isNaN = function(obj) {
    return _.isNumber(obj) && obj !== +obj;
  };

  // Is a given value a boolean?
  _.isBoolean = function(obj) {
    return obj === true || obj === false || toString.call(obj) === '[object Boolean]';
  };

  // Is a given value equal to null?
  _.isNull = function(obj) {
    return obj === null;
  };

  // Is a given variable undefined?
  _.isUndefined = function(obj) {
    return obj === void 0;
  };

  // Shortcut function for checking if an object has a given property directly
  // on itself (in other words, not on a prototype).
  _.has = function(obj, key) {
    return obj != null && hasOwnProperty.call(obj, key);
  };

  // Utility Functions
  // -----------------

  // Run Underscore.js in *noConflict* mode, returning the `_` variable to its
  // previous owner. Returns a reference to the Underscore object.
  _.noConflict = function() {
    root._ = previousUnderscore;
    return this;
  };

  // Keep the identity function around for default iteratees.
  _.identity = function(value) {
    return value;
  };

  // Predicate-generating functions. Often useful outside of Underscore.
  _.constant = function(value) {
    return function() {
      return value;
    };
  };

  _.noop = function(){};

  _.property = property;

  // Generates a function for a given object that returns a given property.
  _.propertyOf = function(obj) {
    return obj == null ? function(){} : function(key) {
      return obj[key];
    };
  };

  // Returns a predicate for checking whether an object has a given set of
  // `key:value` pairs.
  _.matcher = _.matches = function(attrs) {
    attrs = _.extendOwn({}, attrs);
    return function(obj) {
      return _.isMatch(obj, attrs);
    };
  };

  // Run a function **n** times.
  _.times = function(n, iteratee, context) {
    var accum = Array(Math.max(0, n));
    iteratee = optimizeCb(iteratee, context, 1);
    for (var i = 0; i < n; i++) accum[i] = iteratee(i);
    return accum;
  };

  // Return a random integer between min and max (inclusive).
  _.random = function(min, max) {
    if (max == null) {
      max = min;
      min = 0;
    }
    return min + Math.floor(Math.random() * (max - min + 1));
  };

  // A (possibly faster) way to get the current timestamp as an integer.
  _.now = Date.now || function() {
    return new Date().getTime();
  };

   // List of HTML entities for escaping.
  var escapeMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '`': '&#x60;'
  };
  var unescapeMap = _.invert(escapeMap);

  // Functions for escaping and unescaping strings to/from HTML interpolation.
  var createEscaper = function(map) {
    var escaper = function(match) {
      return map[match];
    };
    // Regexes for identifying a key that needs to be escaped
    var source = '(?:' + _.keys(map).join('|') + ')';
    var testRegexp = RegExp(source);
    var replaceRegexp = RegExp(source, 'g');
    return function(string) {
      string = string == null ? '' : '' + string;
      return testRegexp.test(string) ? string.replace(replaceRegexp, escaper) : string;
    };
  };
  _.escape = createEscaper(escapeMap);
  _.unescape = createEscaper(unescapeMap);

  // If the value of the named `property` is a function then invoke it with the
  // `object` as context; otherwise, return it.
  _.result = function(object, property, fallback) {
    var value = object == null ? void 0 : object[property];
    if (value === void 0) {
      value = fallback;
    }
    return _.isFunction(value) ? value.call(object) : value;
  };

  // Generate a unique integer id (unique within the entire client session).
  // Useful for temporary DOM ids.
  var idCounter = 0;
  _.uniqueId = function(prefix) {
    var id = ++idCounter + '';
    return prefix ? prefix + id : id;
  };

  // By default, Underscore uses ERB-style template delimiters, change the
  // following template settings to use alternative delimiters.
  _.templateSettings = {
    evaluate    : /<%([\s\S]+?)%>/g,
    interpolate : /<%=([\s\S]+?)%>/g,
    escape      : /<%-([\s\S]+?)%>/g
  };

  // When customizing `templateSettings`, if you don't want to define an
  // interpolation, evaluation or escaping regex, we need one that is
  // guaranteed not to match.
  var noMatch = /(.)^/;

  // Certain characters need to be escaped so that they can be put into a
  // string literal.
  var escapes = {
    "'":      "'",
    '\\':     '\\',
    '\r':     'r',
    '\n':     'n',
    '\u2028': 'u2028',
    '\u2029': 'u2029'
  };

  var escaper = /\\|'|\r|\n|\u2028|\u2029/g;

  var escapeChar = function(match) {
    return '\\' + escapes[match];
  };

  // JavaScript micro-templating, similar to John Resig's implementation.
  // Underscore templating handles arbitrary delimiters, preserves whitespace,
  // and correctly escapes quotes within interpolated code.
  // NB: `oldSettings` only exists for backwards compatibility.
  _.template = function(text, settings, oldSettings) {
    if (!settings && oldSettings) settings = oldSettings;
    settings = _.defaults({}, settings, _.templateSettings);

    // Combine delimiters into one regular expression via alternation.
    var matcher = RegExp([
      (settings.escape || noMatch).source,
      (settings.interpolate || noMatch).source,
      (settings.evaluate || noMatch).source
    ].join('|') + '|$', 'g');

    // Compile the template source, escaping string literals appropriately.
    var index = 0;
    var source = "__p+='";
    text.replace(matcher, function(match, escape, interpolate, evaluate, offset) {
      source += text.slice(index, offset).replace(escaper, escapeChar);
      index = offset + match.length;

      if (escape) {
        source += "'+\n((__t=(" + escape + "))==null?'':_.escape(__t))+\n'";
      } else if (interpolate) {
        source += "'+\n((__t=(" + interpolate + "))==null?'':__t)+\n'";
      } else if (evaluate) {
        source += "';\n" + evaluate + "\n__p+='";
      }

      // Adobe VMs need the match returned to produce the correct offest.
      return match;
    });
    source += "';\n";

    // If a variable is not specified, place data values in local scope.
    if (!settings.variable) source = 'with(obj||{}){\n' + source + '}\n';

    source = "var __t,__p='',__j=Array.prototype.join," +
      "print=function(){__p+=__j.call(arguments,'');};\n" +
      source + 'return __p;\n';

    try {
      var render = new Function(settings.variable || 'obj', '_', source);
    } catch (e) {
      e.source = source;
      throw e;
    }

    var template = function(data) {
      return render.call(this, data, _);
    };

    // Provide the compiled source as a convenience for precompilation.
    var argument = settings.variable || 'obj';
    template.source = 'function(' + argument + '){\n' + source + '}';

    return template;
  };

  // Add a "chain" function. Start chaining a wrapped Underscore object.
  _.chain = function(obj) {
    var instance = _(obj);
    instance._chain = true;
    return instance;
  };

  // OOP
  // ---------------
  // If Underscore is called as a function, it returns a wrapped object that
  // can be used OO-style. This wrapper holds altered versions of all the
  // underscore functions. Wrapped objects may be chained.

  // Helper function to continue chaining intermediate results.
  var result = function(instance, obj) {
    return instance._chain ? _(obj).chain() : obj;
  };

  // Add your own custom functions to the Underscore object.
  _.mixin = function(obj) {
    _.each(_.functions(obj), function(name) {
      var func = _[name] = obj[name];
      _.prototype[name] = function() {
        var args = [this._wrapped];
        push.apply(args, arguments);
        return result(this, func.apply(_, args));
      };
    });
  };

  // Add all of the Underscore functions to the wrapper object.
  _.mixin(_);

  // Add all mutator Array functions to the wrapper.
  _.each(['pop', 'push', 'reverse', 'shift', 'sort', 'splice', 'unshift'], function(name) {
    var method = ArrayProto[name];
    _.prototype[name] = function() {
      var obj = this._wrapped;
      method.apply(obj, arguments);
      if ((name === 'shift' || name === 'splice') && obj.length === 0) delete obj[0];
      return result(this, obj);
    };
  });

  // Add all accessor Array functions to the wrapper.
  _.each(['concat', 'join', 'slice'], function(name) {
    var method = ArrayProto[name];
    _.prototype[name] = function() {
      return result(this, method.apply(this._wrapped, arguments));
    };
  });

  // Extracts the result from a wrapped and chained object.
  _.prototype.value = function() {
    return this._wrapped;
  };

  // Provide unwrapping proxy for some methods used in engine operations
  // such as arithmetic and JSON stringification.
  _.prototype.valueOf = _.prototype.toJSON = _.prototype.value;

  _.prototype.toString = function() {
    return '' + this._wrapped;
  };

  // AMD registration happens at the end for compatibility with AMD loaders
  // that may not enforce next-turn semantics on modules. Even though general
  // practice for AMD registration is to be anonymous, underscore registers
  // as a named module because, like jQuery, it is a base library that is
  // popular enough to be bundled in a third party lib, but not be part of
  // an AMD load request. Those cases could generate an error when an
  // anonymous define() is called outside of a loader request.
  if (typeof define === 'function' && define.amd) {
    define('underscore', [], function() {
      return _;
    });
  }
}.call(this));;//     Backbone.js 1.1.2

//     (c) 2010-2014 Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
//     Backbone may be freely distributed under the MIT license.
//     For all details and documentation:
//     http://backbonejs.org

(function(root, factory) {

  // Set up Backbone appropriately for the environment. Start with AMD.
  if (typeof define === 'function' && define.amd) {
    define(['underscore', 'jquery', 'exports'], function(_, $, exports) {
      // Export global even in AMD case in case this script is loaded with
      // others that may still expect a global Backbone.
      root.Backbone = factory(root, exports, _, $);
    });

  // Next for Node.js or CommonJS. jQuery may not be needed as a module.
  } else if (typeof exports !== 'undefined') {
    var _ = require('underscore');
    factory(root, exports, _);

  // Finally, as a browser global.
  } else {
    root.Backbone = factory(root, {}, root._, (root.jQuery || root.Zepto || root.ender || root.$));
  }

}(this, function(root, Backbone, _, $) {

  // Initial Setup
  // -------------

  // Save the previous value of the `Backbone` variable, so that it can be
  // restored later on, if `noConflict` is used.
  var previousBackbone = root.Backbone;

  // Create local references to array methods we'll want to use later.
  var array = [];
  var push = array.push;
  var slice = array.slice;
  var splice = array.splice;

  // Current version of the library. Keep in sync with `package.json`.
  Backbone.VERSION = '1.1.2';

  // For Backbone's purposes, jQuery, Zepto, Ender, or My Library (kidding) owns
  // the `$` variable.
  Backbone.$ = $;

  // Runs Backbone.js in *noConflict* mode, returning the `Backbone` variable
  // to its previous owner. Returns a reference to this Backbone object.
  Backbone.noConflict = function() {
    root.Backbone = previousBackbone;
    return this;
  };

  // Turn on `emulateHTTP` to support legacy HTTP servers. Setting this option
  // will fake `"PATCH"`, `"PUT"` and `"DELETE"` requests via the `_method` parameter and
  // set a `X-Http-Method-Override` header.
  Backbone.emulateHTTP = false;

  // Turn on `emulateJSON` to support legacy servers that can't deal with direct
  // `application/json` requests ... will encode the body as
  // `application/x-www-form-urlencoded` instead and will send the model in a
  // form param named `model`.
  Backbone.emulateJSON = false;

  // Backbone.Events
  // ---------------

  // A module that can be mixed in to *any object* in order to provide it with
  // custom events. You may bind with `on` or remove with `off` callback
  // functions to an event; `trigger`-ing an event fires all callbacks in
  // succession.
  //
  //     var object = {};
  //     _.extend(object, Backbone.Events);
  //     object.on('expand', function(){ alert('expanded'); });
  //     object.trigger('expand');
  //
  var Events = Backbone.Events = {

    // Bind an event to a `callback` function. Passing `"all"` will bind
    // the callback to all events fired.
    on: function(name, callback, context) {
      if (!eventsApi(this, 'on', name, [callback, context]) || !callback) return this;
      this._events || (this._events = {});
      var events = this._events[name] || (this._events[name] = []);
      events.push({callback: callback, context: context, ctx: context || this});
      return this;
    },

    // Bind an event to only be triggered a single time. After the first time
    // the callback is invoked, it will be removed.
    once: function(name, callback, context) {
      if (!eventsApi(this, 'once', name, [callback, context]) || !callback) return this;
      var self = this;
      var once = _.once(function() {
        self.off(name, once);
        callback.apply(this, arguments);
      });
      once._callback = callback;
      return this.on(name, once, context);
    },

    // Remove one or many callbacks. If `context` is null, removes all
    // callbacks with that function. If `callback` is null, removes all
    // callbacks for the event. If `name` is null, removes all bound
    // callbacks for all events.
    off: function(name, callback, context) {
      var retain, ev, events, names, i, l, j, k;
      if (!this._events || !eventsApi(this, 'off', name, [callback, context])) return this;
      if (!name && !callback && !context) {
        this._events = void 0;
        return this;
      }
      names = name ? [name] : _.keys(this._events);
      for (i = 0, l = names.length; i < l; i++) {
        name = names[i];
        if (events = this._events[name]) {
          this._events[name] = retain = [];
          if (callback || context) {
            for (j = 0, k = events.length; j < k; j++) {
              ev = events[j];
              if ((callback && callback !== ev.callback && callback !== ev.callback._callback) ||
                  (context && context !== ev.context)) {
                retain.push(ev);
              }
            }
          }
          if (!retain.length) delete this._events[name];
        }
      }

      return this;
    },

    // Trigger one or many events, firing all bound callbacks. Callbacks are
    // passed the same arguments as `trigger` is, apart from the event name
    // (unless you're listening on `"all"`, which will cause your callback to
    // receive the true name of the event as the first argument).
    trigger: function(name) {
      if (!this._events) return this;
      var args = slice.call(arguments, 1);
      if (!eventsApi(this, 'trigger', name, args)) return this;
      var events = this._events[name];
      var allEvents = this._events.all;
      if (events) triggerEvents(events, args);
      if (allEvents) triggerEvents(allEvents, arguments);
      return this;
    },

    // Tell this object to stop listening to either specific events ... or
    // to every object it's currently listening to.
    stopListening: function(obj, name, callback) {
      var listeningTo = this._listeningTo;
      if (!listeningTo) return this;
      var remove = !name && !callback;
      if (!callback && typeof name === 'object') callback = this;
      if (obj) (listeningTo = {})[obj._listenId] = obj;
      for (var id in listeningTo) {
        obj = listeningTo[id];
        obj.off(name, callback, this);
        if (remove || _.isEmpty(obj._events)) delete this._listeningTo[id];
      }
      return this;
    }

  };

  // Regular expression used to split event strings.
  var eventSplitter = /\s+/;

  // Implement fancy features of the Events API such as multiple event
  // names `"change blur"` and jQuery-style event maps `{change: action}`
  // in terms of the existing API.
  var eventsApi = function(obj, action, name, rest) {
    if (!name) return true;

    // Handle event maps.
    if (typeof name === 'object') {
      for (var key in name) {
        obj[action].apply(obj, [key, name[key]].concat(rest));
      }
      return false;
    }

    // Handle space separated event names.
    if (eventSplitter.test(name)) {
      var names = name.split(eventSplitter);
      for (var i = 0, l = names.length; i < l; i++) {
        obj[action].apply(obj, [names[i]].concat(rest));
      }
      return false;
    }

    return true;
  };

  // A difficult-to-believe, but optimized internal dispatch function for
  // triggering events. Tries to keep the usual cases speedy (most internal
  // Backbone events have 3 arguments).
  var triggerEvents = function(events, args) {
    var ev, i = -1, l = events.length, a1 = args[0], a2 = args[1], a3 = args[2];
    switch (args.length) {
      case 0: while (++i < l) (ev = events[i]).callback.call(ev.ctx); return;
      case 1: while (++i < l) (ev = events[i]).callback.call(ev.ctx, a1); return;
      case 2: while (++i < l) (ev = events[i]).callback.call(ev.ctx, a1, a2); return;
      case 3: while (++i < l) (ev = events[i]).callback.call(ev.ctx, a1, a2, a3); return;
      default: while (++i < l) (ev = events[i]).callback.apply(ev.ctx, args); return;
    }
  };

  var listenMethods = {listenTo: 'on', listenToOnce: 'once'};

  // Inversion-of-control versions of `on` and `once`. Tell *this* object to
  // listen to an event in another object ... keeping track of what it's
  // listening to.
  _.each(listenMethods, function(implementation, method) {
    Events[method] = function(obj, name, callback) {
      var listeningTo = this._listeningTo || (this._listeningTo = {});
      var id = obj._listenId || (obj._listenId = _.uniqueId('l'));
      listeningTo[id] = obj;
      if (!callback && typeof name === 'object') callback = this;
      obj[implementation](name, callback, this);
      return this;
    };
  });

  // Aliases for backwards compatibility.
  Events.bind   = Events.on;
  Events.unbind = Events.off;

  // Allow the `Backbone` object to serve as a global event bus, for folks who
  // want global "pubsub" in a convenient place.
  _.extend(Backbone, Events);

  // Backbone.Model
  // --------------

  // Backbone **Models** are the basic data object in the framework --
  // frequently representing a row in a table in a database on your server.
  // A discrete chunk of data and a bunch of useful, related methods for
  // performing computations and transformations on that data.

  // Create a new model with the specified attributes. A client id (`cid`)
  // is automatically generated and assigned for you.
  var Model = Backbone.Model = function(attributes, options) {
    var attrs = attributes || {};
    options || (options = {});
    this.cid = _.uniqueId('c');
    this.attributes = {};
    if (options.collection) this.collection = options.collection;
    if (options.parse) attrs = this.parse(attrs, options) || {};
    attrs = _.defaults({}, attrs, _.result(this, 'defaults'));
    this.set(attrs, options);
    this.changed = {};
    this.initialize.apply(this, arguments);
  };

  // Attach all inheritable methods to the Model prototype.
  _.extend(Model.prototype, Events, {

    // A hash of attributes whose current and previous value differ.
    changed: null,

    // The value returned during the last failed validation.
    validationError: null,

    // The default name for the JSON `id` attribute is `"id"`. MongoDB and
    // CouchDB users may want to set this to `"_id"`.
    idAttribute: 'id',

    // Initialize is an empty function by default. Override it with your own
    // initialization logic.
    initialize: function(){},

    // Return a copy of the model's `attributes` object.
    toJSON: function(options) {
      return _.clone(this.attributes);
    },

    // Proxy `Backbone.sync` by default -- but override this if you need
    // custom syncing semantics for *this* particular model.
    sync: function() {
      return Backbone.sync.apply(this, arguments);
    },

    // Get the value of an attribute.
    get: function(attr) {
      return this.attributes[attr];
    },

    // Get the HTML-escaped value of an attribute.
    escape: function(attr) {
      return _.escape(this.get(attr));
    },

    // Returns `true` if the attribute contains a value that is not null
    // or undefined.
    has: function(attr) {
      return this.get(attr) != null;
    },

    // Set a hash of model attributes on the object, firing `"change"`. This is
    // the core primitive operation of a model, updating the data and notifying
    // anyone who needs to know about the change in state. The heart of the beast.
    set: function(key, val, options) {
      var attr, attrs, unset, changes, silent, changing, prev, current;
      if (key == null) return this;

      // Handle both `"key", value` and `{key: value}` -style arguments.
      if (typeof key === 'object') {
        attrs = key;
        options = val;
      } else {
        (attrs = {})[key] = val;
      }

      options || (options = {});

      // Run validation.
      if (!this._validate(attrs, options)) return false;

      // Extract attributes and options.
      unset           = options.unset;
      silent          = options.silent;
      changes         = [];
      changing        = this._changing;
      this._changing  = true;

      if (!changing) {
        this._previousAttributes = _.clone(this.attributes);
        this.changed = {};
      }
      current = this.attributes, prev = this._previousAttributes;

      // Check for changes of `id`.
      if (this.idAttribute in attrs) this.id = attrs[this.idAttribute];

      // For each `set` attribute, update or delete the current value.
      for (attr in attrs) {
        val = attrs[attr];
        if (!_.isEqual(current[attr], val)) changes.push(attr);
        if (!_.isEqual(prev[attr], val)) {
          this.changed[attr] = val;
        } else {
          delete this.changed[attr];
        }
        unset ? delete current[attr] : current[attr] = val;
      }

      // Trigger all relevant attribute changes.
      if (!silent) {
        if (changes.length) this._pending = options;
        for (var i = 0, l = changes.length; i < l; i++) {
          this.trigger('change:' + changes[i], this, current[changes[i]], options);
        }
      }

      // You might be wondering why there's a `while` loop here. Changes can
      // be recursively nested within `"change"` events.
      if (changing) return this;
      if (!silent) {
        while (this._pending) {
          options = this._pending;
          this._pending = false;
          this.trigger('change', this, options);
        }
      }
      this._pending = false;
      this._changing = false;
      return this;
    },

    // Remove an attribute from the model, firing `"change"`. `unset` is a noop
    // if the attribute doesn't exist.
    unset: function(attr, options) {
      return this.set(attr, void 0, _.extend({}, options, {unset: true}));
    },

    // Clear all attributes on the model, firing `"change"`.
    clear: function(options) {
      var attrs = {};
      for (var key in this.attributes) attrs[key] = void 0;
      return this.set(attrs, _.extend({}, options, {unset: true}));
    },

    // Determine if the model has changed since the last `"change"` event.
    // If you specify an attribute name, determine if that attribute has changed.
    hasChanged: function(attr) {
      if (attr == null) return !_.isEmpty(this.changed);
      return _.has(this.changed, attr);
    },

    // Return an object containing all the attributes that have changed, or
    // false if there are no changed attributes. Useful for determining what
    // parts of a view need to be updated and/or what attributes need to be
    // persisted to the server. Unset attributes will be set to undefined.
    // You can also pass an attributes object to diff against the model,
    // determining if there *would be* a change.
    changedAttributes: function(diff) {
      if (!diff) return this.hasChanged() ? _.clone(this.changed) : false;
      var val, changed = false;
      var old = this._changing ? this._previousAttributes : this.attributes;
      for (var attr in diff) {
        if (_.isEqual(old[attr], (val = diff[attr]))) continue;
        (changed || (changed = {}))[attr] = val;
      }
      return changed;
    },

    // Get the previous value of an attribute, recorded at the time the last
    // `"change"` event was fired.
    previous: function(attr) {
      if (attr == null || !this._previousAttributes) return null;
      return this._previousAttributes[attr];
    },

    // Get all of the attributes of the model at the time of the previous
    // `"change"` event.
    previousAttributes: function() {
      return _.clone(this._previousAttributes);
    },

    // Fetch the model from the server. If the server's representation of the
    // model differs from its current attributes, they will be overridden,
    // triggering a `"change"` event.
    fetch: function(options) {
      options = options ? _.clone(options) : {};
      if (options.parse === void 0) options.parse = true;
      var model = this;
      var success = options.success;
      options.success = function(resp) {
        if (!model.set(model.parse(resp, options), options)) return false;
        if (success) success(model, resp, options);
        model.trigger('sync', model, resp, options);
      };
      wrapError(this, options);
      return this.sync('read', this, options);
    },

    // Set a hash of model attributes, and sync the model to the server.
    // If the server returns an attributes hash that differs, the model's
    // state will be `set` again.
    save: function(key, val, options) {
      var attrs, method, xhr, attributes = this.attributes;

      // Handle both `"key", value` and `{key: value}` -style arguments.
      if (key == null || typeof key === 'object') {
        attrs = key;
        options = val;
      } else {
        (attrs = {})[key] = val;
      }

      options = _.extend({validate: true}, options);

      // If we're not waiting and attributes exist, save acts as
      // `set(attr).save(null, opts)` with validation. Otherwise, check if
      // the model will be valid when the attributes, if any, are set.
      if (attrs && !options.wait) {
        if (!this.set(attrs, options)) return false;
      } else {
        if (!this._validate(attrs, options)) return false;
      }

      // Set temporary attributes if `{wait: true}`.
      if (attrs && options.wait) {
        this.attributes = _.extend({}, attributes, attrs);
      }

      // After a successful server-side save, the client is (optionally)
      // updated with the server-side state.
      if (options.parse === void 0) options.parse = true;
      var model = this;
      var success = options.success;
      options.success = function(resp) {
        // Ensure attributes are restored during synchronous saves.
        model.attributes = attributes;
        var serverAttrs = model.parse(resp, options);
        if (options.wait) serverAttrs = _.extend(attrs || {}, serverAttrs);
        if (_.isObject(serverAttrs) && !model.set(serverAttrs, options)) {
          return false;
        }
        if (success) success(model, resp, options);
        model.trigger('sync', model, resp, options);
      };
      wrapError(this, options);

      method = this.isNew() ? 'create' : (options.patch ? 'patch' : 'update');
      if (method === 'patch') options.attrs = attrs;
      xhr = this.sync(method, this, options);

      // Restore attributes.
      if (attrs && options.wait) this.attributes = attributes;

      return xhr;
    },

    // Destroy this model on the server if it was already persisted.
    // Optimistically removes the model from its collection, if it has one.
    // If `wait: true` is passed, waits for the server to respond before removal.
    destroy: function(options) {
      options = options ? _.clone(options) : {};
      var model = this;
      var success = options.success;

      var destroy = function() {
        model.trigger('destroy', model, model.collection, options);
      };

      options.success = function(resp) {
        if (options.wait || model.isNew()) destroy();
        if (success) success(model, resp, options);
        if (!model.isNew()) model.trigger('sync', model, resp, options);
      };

      if (this.isNew()) {
        options.success();
        return false;
      }
      wrapError(this, options);

      var xhr = this.sync('delete', this, options);
      if (!options.wait) destroy();
      return xhr;
    },

    // Default URL for the model's representation on the server -- if you're
    // using Backbone's restful methods, override this to change the endpoint
    // that will be called.
    url: function() {
      var base =
        _.result(this, 'urlRoot') ||
        _.result(this.collection, 'url') ||
        urlError();
      if (this.isNew()) return base;
      return base.replace(/([^\/])$/, '$1/') + encodeURIComponent(this.id);
    },

    // **parse** converts a response into the hash of attributes to be `set` on
    // the model. The default implementation is just to pass the response along.
    parse: function(resp, options) {
      return resp;
    },

    // Create a new model with identical attributes to this one.
    clone: function() {
      return new this.constructor(this.attributes);
    },

    // A model is new if it has never been saved to the server, and lacks an id.
    isNew: function() {
      return !this.has(this.idAttribute);
    },

    // Check if the model is currently in a valid state.
    isValid: function(options) {
      return this._validate({}, _.extend(options || {}, { validate: true }));
    },

    // Run validation against the next complete set of model attributes,
    // returning `true` if all is well. Otherwise, fire an `"invalid"` event.
    _validate: function(attrs, options) {
      if (!options.validate || !this.validate) return true;
      attrs = _.extend({}, this.attributes, attrs);
      var error = this.validationError = this.validate(attrs, options) || null;
      if (!error) return true;
      this.trigger('invalid', this, error, _.extend(options, {validationError: error}));
      return false;
    }

  });

  // Underscore methods that we want to implement on the Model.
  var modelMethods = ['keys', 'values', 'pairs', 'invert', 'pick', 'omit'];

  // Mix in each Underscore method as a proxy to `Model#attributes`.
  _.each(modelMethods, function(method) {
    Model.prototype[method] = function() {
      var args = slice.call(arguments);
      args.unshift(this.attributes);
      return _[method].apply(_, args);
    };
  });

  // Backbone.Collection
  // -------------------

  // If models tend to represent a single row of data, a Backbone Collection is
  // more analagous to a table full of data ... or a small slice or page of that
  // table, or a collection of rows that belong together for a particular reason
  // -- all of the messages in this particular folder, all of the documents
  // belonging to this particular author, and so on. Collections maintain
  // indexes of their models, both in order, and for lookup by `id`.

  // Create a new **Collection**, perhaps to contain a specific type of `model`.
  // If a `comparator` is specified, the Collection will maintain
  // its models in sort order, as they're added and removed.
  var Collection = Backbone.Collection = function(models, options) {
    options || (options = {});
    if (options.model) this.model = options.model;
    if (options.comparator !== void 0) this.comparator = options.comparator;
    this._reset();
    this.initialize.apply(this, arguments);
    if (models) this.reset(models, _.extend({silent: true}, options));
  };

  // Default options for `Collection#set`.
  var setOptions = {add: true, remove: true, merge: true};
  var addOptions = {add: true, remove: false};

  // Define the Collection's inheritable methods.
  _.extend(Collection.prototype, Events, {

    // The default model for a collection is just a **Backbone.Model**.
    // This should be overridden in most cases.
    model: Model,

    // Initialize is an empty function by default. Override it with your own
    // initialization logic.
    initialize: function(){},

    // The JSON representation of a Collection is an array of the
    // models' attributes.
    toJSON: function(options) {
      return this.map(function(model){ return model.toJSON(options); });
    },

    // Proxy `Backbone.sync` by default.
    sync: function() {
      return Backbone.sync.apply(this, arguments);
    },

    // Add a model, or list of models to the set.
    add: function(models, options) {
      return this.set(models, _.extend({merge: false}, options, addOptions));
    },

    // Remove a model, or a list of models from the set.
    remove: function(models, options) {
      var singular = !_.isArray(models);
      models = singular ? [models] : _.clone(models);
      options || (options = {});
      var i, l, index, model;
      for (i = 0, l = models.length; i < l; i++) {
        model = models[i] = this.get(models[i]);
        if (!model) continue;
        delete this._byId[model.id];
        delete this._byId[model.cid];
        index = this.indexOf(model);
        this.models.splice(index, 1);
        this.length--;
        if (!options.silent) {
          options.index = index;
          model.trigger('remove', model, this, options);
        }
        this._removeReference(model, options);
      }
      return singular ? models[0] : models;
    },

    // Update a collection by `set`-ing a new list of models, adding new ones,
    // removing models that are no longer present, and merging models that
    // already exist in the collection, as necessary. Similar to **Model#set**,
    // the core operation for updating the data contained by the collection.
    set: function(models, options) {
      options = _.defaults({}, options, setOptions);
      if (options.parse) models = this.parse(models, options);
      var singular = !_.isArray(models);
      models = singular ? (models ? [models] : []) : _.clone(models);
      var i, l, id, model, attrs, existing, sort;
      var at = options.at;
      var targetModel = this.model;
      var sortable = this.comparator && (at == null) && options.sort !== false;
      var sortAttr = _.isString(this.comparator) ? this.comparator : null;
      var toAdd = [], toRemove = [], modelMap = {};
      var add = options.add, merge = options.merge, remove = options.remove;
      var order = !sortable && add && remove ? [] : false;

      // Turn bare objects into model references, and prevent invalid models
      // from being added.
      for (i = 0, l = models.length; i < l; i++) {
        attrs = models[i] || {};
        if (attrs instanceof Model) {
          id = model = attrs;
        } else {
          id = attrs[targetModel.prototype.idAttribute || 'id'];
        }

        // If a duplicate is found, prevent it from being added and
        // optionally merge it into the existing model.
        if (existing = this.get(id)) {
          if (remove) modelMap[existing.cid] = true;
          if (merge) {
            attrs = attrs === model ? model.attributes : attrs;
            if (options.parse) attrs = existing.parse(attrs, options);
            existing.set(attrs, options);
            if (sortable && !sort && existing.hasChanged(sortAttr)) sort = true;
          }
          models[i] = existing;

        // If this is a new, valid model, push it to the `toAdd` list.
        } else if (add) {
          model = models[i] = this._prepareModel(attrs, options);
          if (!model) continue;
          toAdd.push(model);
          this._addReference(model, options);
        }

        // Do not add multiple models with the same `id`.
        model = existing || model;
        if (order && (model.isNew() || !modelMap[model.id])) order.push(model);
        modelMap[model.id] = true;
      }

      // Remove nonexistent models if appropriate.
      if (remove) {
        for (i = 0, l = this.length; i < l; ++i) {
          if (!modelMap[(model = this.models[i]).cid]) toRemove.push(model);
        }
        if (toRemove.length) this.remove(toRemove, options);
      }

      // See if sorting is needed, update `length` and splice in new models.
      if (toAdd.length || (order && order.length)) {
        if (sortable) sort = true;
        this.length += toAdd.length;
        if (at != null) {
          for (i = 0, l = toAdd.length; i < l; i++) {
            this.models.splice(at + i, 0, toAdd[i]);
          }
        } else {
          if (order) this.models.length = 0;
          var orderedModels = order || toAdd;
          for (i = 0, l = orderedModels.length; i < l; i++) {
            this.models.push(orderedModels[i]);
          }
        }
      }

      // Silently sort the collection if appropriate.
      if (sort) this.sort({silent: true});

      // Unless silenced, it's time to fire all appropriate add/sort events.
      if (!options.silent) {
        for (i = 0, l = toAdd.length; i < l; i++) {
          (model = toAdd[i]).trigger('add', model, this, options);
        }
        if (sort || (order && order.length)) this.trigger('sort', this, options);
      }

      // Return the added (or merged) model (or models).
      return singular ? models[0] : models;
    },

    // When you have more items than you want to add or remove individually,
    // you can reset the entire set with a new list of models, without firing
    // any granular `add` or `remove` events. Fires `reset` when finished.
    // Useful for bulk operations and optimizations.
    reset: function(models, options) {
      options || (options = {});
      for (var i = 0, l = this.models.length; i < l; i++) {
        this._removeReference(this.models[i], options);
      }
      options.previousModels = this.models;
      this._reset();
      models = this.add(models, _.extend({silent: true}, options));
      if (!options.silent) this.trigger('reset', this, options);
      return models;
    },

    // Add a model to the end of the collection.
    push: function(model, options) {
      return this.add(model, _.extend({at: this.length}, options));
    },

    // Remove a model from the end of the collection.
    pop: function(options) {
      var model = this.at(this.length - 1);
      this.remove(model, options);
      return model;
    },

    // Add a model to the beginning of the collection.
    unshift: function(model, options) {
      return this.add(model, _.extend({at: 0}, options));
    },

    // Remove a model from the beginning of the collection.
    shift: function(options) {
      var model = this.at(0);
      this.remove(model, options);
      return model;
    },

    // Slice out a sub-array of models from the collection.
    slice: function() {
      return slice.apply(this.models, arguments);
    },

    // Get a model from the set by id.
    get: function(obj) {
      if (obj == null) return void 0;
      return this._byId[obj] || this._byId[obj.id] || this._byId[obj.cid];
    },

    // Get the model at the given index.
    at: function(index) {
      return this.models[index];
    },

    // Return models with matching attributes. Useful for simple cases of
    // `filter`.
    where: function(attrs, first) {
      if (_.isEmpty(attrs)) return first ? void 0 : [];
      return this[first ? 'find' : 'filter'](function(model) {
        for (var key in attrs) {
          if (attrs[key] !== model.get(key)) return false;
        }
        return true;
      });
    },

    // Return the first model with matching attributes. Useful for simple cases
    // of `find`.
    findWhere: function(attrs) {
      return this.where(attrs, true);
    },

    // Force the collection to re-sort itself. You don't need to call this under
    // normal circumstances, as the set will maintain sort order as each item
    // is added.
    sort: function(options) {
      if (!this.comparator) throw new Error('Cannot sort a set without a comparator');
      options || (options = {});

      // Run sort based on type of `comparator`.
      if (_.isString(this.comparator) || this.comparator.length === 1) {
        this.models = this.sortBy(this.comparator, this);
      } else {
        this.models.sort(_.bind(this.comparator, this));
      }

      if (!options.silent) this.trigger('sort', this, options);
      return this;
    },

    // Pluck an attribute from each model in the collection.
    pluck: function(attr) {
      return _.invoke(this.models, 'get', attr);
    },

    // Fetch the default set of models for this collection, resetting the
    // collection when they arrive. If `reset: true` is passed, the response
    // data will be passed through the `reset` method instead of `set`.
    fetch: function(options) {
      options = options ? _.clone(options) : {};
      if (options.parse === void 0) options.parse = true;
      var success = options.success;
      var collection = this;
      options.success = function(resp) {
        var method = options.reset ? 'reset' : 'set';
        collection[method](resp, options);
        if (success) success(collection, resp, options);
        collection.trigger('sync', collection, resp, options);
      };
      wrapError(this, options);
      return this.sync('read', this, options);
    },

    // Create a new instance of a model in this collection. Add the model to the
    // collection immediately, unless `wait: true` is passed, in which case we
    // wait for the server to agree.
    create: function(model, options) {
      options = options ? _.clone(options) : {};
      if (!(model = this._prepareModel(model, options))) return false;
      if (!options.wait) this.add(model, options);
      var collection = this;
      var success = options.success;
      options.success = function(model, resp) {
        if (options.wait) collection.add(model, options);
        if (success) success(model, resp, options);
      };
      model.save(null, options);
      return model;
    },

    // **parse** converts a response into a list of models to be added to the
    // collection. The default implementation is just to pass it through.
    parse: function(resp, options) {
      return resp;
    },

    // Create a new collection with an identical list of models as this one.
    clone: function() {
      return new this.constructor(this.models);
    },

    // Private method to reset all internal state. Called when the collection
    // is first initialized or reset.
    _reset: function() {
      this.length = 0;
      this.models = [];
      this._byId  = {};
    },

    // Prepare a hash of attributes (or other model) to be added to this
    // collection.
    _prepareModel: function(attrs, options) {
      if (attrs instanceof Model) return attrs;
      options = options ? _.clone(options) : {};
      options.collection = this;
      var model = new this.model(attrs, options);
      if (!model.validationError) return model;
      this.trigger('invalid', this, model.validationError, options);
      return false;
    },

    // Internal method to create a model's ties to a collection.
    _addReference: function(model, options) {
      this._byId[model.cid] = model;
      if (model.id != null) this._byId[model.id] = model;
      if (!model.collection) model.collection = this;
      model.on('all', this._onModelEvent, this);
    },

    // Internal method to sever a model's ties to a collection.
    _removeReference: function(model, options) {
      if (this === model.collection) delete model.collection;
      model.off('all', this._onModelEvent, this);
    },

    // Internal method called every time a model in the set fires an event.
    // Sets need to update their indexes when models change ids. All other
    // events simply proxy through. "add" and "remove" events that originate
    // in other collections are ignored.
    _onModelEvent: function(event, model, collection, options) {
      if ((event === 'add' || event === 'remove') && collection !== this) return;
      if (event === 'destroy') this.remove(model, options);
      if (model && event === 'change:' + model.idAttribute) {
        delete this._byId[model.previous(model.idAttribute)];
        if (model.id != null) this._byId[model.id] = model;
      }
      this.trigger.apply(this, arguments);
    }

  });

  // Underscore methods that we want to implement on the Collection.
  // 90% of the core usefulness of Backbone Collections is actually implemented
  // right here:
  var methods = ['forEach', 'each', 'map', 'collect', 'reduce', 'foldl',
    'inject', 'reduceRight', 'foldr', 'find', 'detect', 'filter', 'select',
    'reject', 'every', 'all', 'some', 'any', 'include', 'contains', 'invoke',
    'max', 'min', 'toArray', 'size', 'first', 'head', 'take', 'initial', 'rest',
    'tail', 'drop', 'last', 'without', 'difference', 'indexOf', 'shuffle',
    'lastIndexOf', 'isEmpty', 'chain', 'sample'];

  // Mix in each Underscore method as a proxy to `Collection#models`.
  _.each(methods, function(method) {
    Collection.prototype[method] = function() {
      var args = slice.call(arguments);
      args.unshift(this.models);
      return _[method].apply(_, args);
    };
  });

  // Underscore methods that take a property name as an argument.
  var attributeMethods = ['groupBy', 'countBy', 'sortBy', 'indexBy'];

  // Use attributes instead of properties.
  _.each(attributeMethods, function(method) {
    Collection.prototype[method] = function(value, context) {
      var iterator = _.isFunction(value) ? value : function(model) {
        return model.get(value);
      };
      return _[method](this.models, iterator, context);
    };
  });

  // Backbone.View
  // -------------

  // Backbone Views are almost more convention than they are actual code. A View
  // is simply a JavaScript object that represents a logical chunk of UI in the
  // DOM. This might be a single item, an entire list, a sidebar or panel, or
  // even the surrounding frame which wraps your whole app. Defining a chunk of
  // UI as a **View** allows you to define your DOM events declaratively, without
  // having to worry about render order ... and makes it easy for the view to
  // react to specific changes in the state of your models.

  // Creating a Backbone.View creates its initial element outside of the DOM,
  // if an existing element is not provided...
  var View = Backbone.View = function(options) {
    this.cid = _.uniqueId('view');
    options || (options = {});
    _.extend(this, _.pick(options, viewOptions));
    this._ensureElement();
    this.initialize.apply(this, arguments);
    this.delegateEvents();
  };

  // Cached regex to split keys for `delegate`.
  var delegateEventSplitter = /^(\S+)\s*(.*)$/;

  // List of view options to be merged as properties.
  var viewOptions = ['model', 'collection', 'el', 'id', 'attributes', 'className', 'tagName', 'events'];

  // Set up all inheritable **Backbone.View** properties and methods.
  _.extend(View.prototype, Events, {

    // The default `tagName` of a View's element is `"div"`.
    tagName: 'div',

    // jQuery delegate for element lookup, scoped to DOM elements within the
    // current view. This should be preferred to global lookups where possible.
    $: function(selector) {
      return this.$el.find(selector);
    },

    // Initialize is an empty function by default. Override it with your own
    // initialization logic.
    initialize: function(){},

    // **render** is the core function that your view should override, in order
    // to populate its element (`this.el`), with the appropriate HTML. The
    // convention is for **render** to always return `this`.
    render: function() {
      return this;
    },

    // Remove this view by taking the element out of the DOM, and removing any
    // applicable Backbone.Events listeners.
    remove: function() {
      this.$el.remove();
      this.stopListening();
      return this;
    },

    // Change the view's element (`this.el` property), including event
    // re-delegation.
    setElement: function(element, delegate) {
      if (this.$el) this.undelegateEvents();
      this.$el = element instanceof Backbone.$ ? element : Backbone.$(element);
      this.el = this.$el[0];
      if (delegate !== false) this.delegateEvents();
      return this;
    },

    // Set callbacks, where `this.events` is a hash of
    //
    // *{"event selector": "callback"}*
    //
    //     {
    //       'mousedown .title':  'edit',
    //       'click .button':     'save',
    //       'click .open':       function(e) { ... }
    //     }
    //
    // pairs. Callbacks will be bound to the view, with `this` set properly.
    // Uses event delegation for efficiency.
    // Omitting the selector binds the event to `this.el`.
    // This only works for delegate-able events: not `focus`, `blur`, and
    // not `change`, `submit`, and `reset` in Internet Explorer.
    delegateEvents: function(events) {
      if (!(events || (events = _.result(this, 'events')))) return this;
      this.undelegateEvents();
      for (var key in events) {
        var method = events[key];
        if (!_.isFunction(method)) method = this[events[key]];
        if (!method) continue;

        var match = key.match(delegateEventSplitter);
        var eventName = match[1], selector = match[2];
        method = _.bind(method, this);
        eventName += '.delegateEvents' + this.cid;
        if (selector === '') {
          this.$el.on(eventName, method);
        } else {
          this.$el.on(eventName, selector, method);
        }
      }
      return this;
    },

    // Clears all callbacks previously bound to the view with `delegateEvents`.
    // You usually don't need to use this, but may wish to if you have multiple
    // Backbone views attached to the same DOM element.
    undelegateEvents: function() {
      this.$el.off('.delegateEvents' + this.cid);
      return this;
    },

    // Ensure that the View has a DOM element to render into.
    // If `this.el` is a string, pass it through `$()`, take the first
    // matching element, and re-assign it to `el`. Otherwise, create
    // an element from the `id`, `className` and `tagName` properties.
    _ensureElement: function() {
      if (!this.el) {
        var attrs = _.extend({}, _.result(this, 'attributes'));
        if (this.id) attrs.id = _.result(this, 'id');
        if (this.className) attrs['class'] = _.result(this, 'className');
        var $el = Backbone.$('<' + _.result(this, 'tagName') + '>').attr(attrs);
        this.setElement($el, false);
      } else {
        this.setElement(_.result(this, 'el'), false);
      }
    }

  });

  // Backbone.sync
  // -------------

  // Override this function to change the manner in which Backbone persists
  // models to the server. You will be passed the type of request, and the
  // model in question. By default, makes a RESTful Ajax request
  // to the model's `url()`. Some possible customizations could be:
  //
  // * Use `setTimeout` to batch rapid-fire updates into a single request.
  // * Send up the models as XML instead of JSON.
  // * Persist models via WebSockets instead of Ajax.
  //
  // Turn on `Backbone.emulateHTTP` in order to send `PUT` and `DELETE` requests
  // as `POST`, with a `_method` parameter containing the true HTTP method,
  // as well as all requests with the body as `application/x-www-form-urlencoded`
  // instead of `application/json` with the model in a param named `model`.
  // Useful when interfacing with server-side languages like **PHP** that make
  // it difficult to read the body of `PUT` requests.
  Backbone.sync = function(method, model, options) {
    var type = methodMap[method];

    // Default options, unless specified.
    _.defaults(options || (options = {}), {
      emulateHTTP: Backbone.emulateHTTP,
      emulateJSON: Backbone.emulateJSON
    });

    // Default JSON-request options.
    var params = {type: type, dataType: 'json'};

    // Ensure that we have a URL.
    if (!options.url) {
      params.url = _.result(model, 'url') || urlError();
    }

    // Ensure that we have the appropriate request data.
    if (options.data == null && model && (method === 'create' || method === 'update' || method === 'patch')) {
      params.contentType = 'application/json';
      params.data = JSON.stringify(options.attrs || model.toJSON(options));
    }

    // For older servers, emulate JSON by encoding the request into an HTML-form.
    if (options.emulateJSON) {
      params.contentType = 'application/x-www-form-urlencoded';
      params.data = params.data ? {model: params.data} : {};
    }

    // For older servers, emulate HTTP by mimicking the HTTP method with `_method`
    // And an `X-HTTP-Method-Override` header.
    if (options.emulateHTTP && (type === 'PUT' || type === 'DELETE' || type === 'PATCH')) {
      params.type = 'POST';
      if (options.emulateJSON) params.data._method = type;
      var beforeSend = options.beforeSend;
      options.beforeSend = function(xhr) {
        xhr.setRequestHeader('X-HTTP-Method-Override', type);
        if (beforeSend) return beforeSend.apply(this, arguments);
      };
    }

    // Don't process data on a non-GET request.
    if (params.type !== 'GET' && !options.emulateJSON) {
      params.processData = false;
    }

    // If we're sending a `PATCH` request, and we're in an old Internet Explorer
    // that still has ActiveX enabled by default, override jQuery to use that
    // for XHR instead. Remove this line when jQuery supports `PATCH` on IE8.
    if (params.type === 'PATCH' && noXhrPatch) {
      params.xhr = function() {
        return new ActiveXObject("Microsoft.XMLHTTP");
      };
    }

    // Make the request, allowing the user to override any Ajax options.
    var xhr = options.xhr = Backbone.ajax(_.extend(params, options));
    model.trigger('request', model, xhr, options);
    return xhr;
  };

  var noXhrPatch =
    typeof window !== 'undefined' && !!window.ActiveXObject &&
      !(window.XMLHttpRequest && (new XMLHttpRequest).dispatchEvent);

  // Map from CRUD to HTTP for our default `Backbone.sync` implementation.
  var methodMap = {
    'create': 'POST',
    'update': 'PUT',
    'patch':  'PATCH',
    'delete': 'DELETE',
    'read':   'GET'
  };

  // Set the default implementation of `Backbone.ajax` to proxy through to `$`.
  // Override this if you'd like to use a different library.
  Backbone.ajax = function() {
    return Backbone.$.ajax.apply(Backbone.$, arguments);
  };

  // Backbone.Router
  // ---------------

  // Routers map faux-URLs to actions, and fire events when routes are
  // matched. Creating a new one sets its `routes` hash, if not set statically.
  var Router = Backbone.Router = function(options) {
    options || (options = {});
    if (options.routes) this.routes = options.routes;
    this._bindRoutes();
    this.initialize.apply(this, arguments);
  };

  // Cached regular expressions for matching named param parts and splatted
  // parts of route strings.
  var optionalParam = /\((.*?)\)/g;
  var namedParam    = /(\(\?)?:\w+/g;
  var splatParam    = /\*\w+/g;
  var escapeRegExp  = /[\-{}\[\]+?.,\\\^$|#\s]/g;

  // Set up all inheritable **Backbone.Router** properties and methods.
  _.extend(Router.prototype, Events, {

    // Initialize is an empty function by default. Override it with your own
    // initialization logic.
    initialize: function(){},

    // Manually bind a single named route to a callback. For example:
    //
    //     this.route('search/:query/p:num', 'search', function(query, num) {
    //       ...
    //     });
    //
    route: function(route, name, callback) {
      if (!_.isRegExp(route)) route = this._routeToRegExp(route);
      if (_.isFunction(name)) {
        callback = name;
        name = '';
      }
      if (!callback) callback = this[name];
      var router = this;
      Backbone.history.route(route, function(fragment) {
        var args = router._extractParameters(route, fragment);
        router.execute(callback, args);
        router.trigger.apply(router, ['route:' + name].concat(args));
        router.trigger('route', name, args);
        Backbone.history.trigger('route', router, name, args);
      });
      return this;
    },

    // Execute a route handler with the provided parameters.  This is an
    // excellent place to do pre-route setup or post-route cleanup.
    execute: function(callback, args) {
      if (callback) callback.apply(this, args);
    },

    // Simple proxy to `Backbone.history` to save a fragment into the history.
    navigate: function(fragment, options) {
      Backbone.history.navigate(fragment, options);
      return this;
    },

    // Bind all defined routes to `Backbone.history`. We have to reverse the
    // order of the routes here to support behavior where the most general
    // routes can be defined at the bottom of the route map.
    _bindRoutes: function() {
      if (!this.routes) return;
      this.routes = _.result(this, 'routes');
      var route, routes = _.keys(this.routes);
      while ((route = routes.pop()) != null) {
        this.route(route, this.routes[route]);
      }
    },

    // Convert a route string into a regular expression, suitable for matching
    // against the current location hash.
    _routeToRegExp: function(route) {
      route = route.replace(escapeRegExp, '\\$&')
                   .replace(optionalParam, '(?:$1)?')
                   .replace(namedParam, function(match, optional) {
                     return optional ? match : '([^/?]+)';
                   })
                   .replace(splatParam, '([^?]*?)');
      return new RegExp('^' + route + '(?:\\?([\\s\\S]*))?$');
    },

    // Given a route, and a URL fragment that it matches, return the array of
    // extracted decoded parameters. Empty or unmatched parameters will be
    // treated as `null` to normalize cross-browser behavior.
    _extractParameters: function(route, fragment) {
      var params = route.exec(fragment).slice(1);
      return _.map(params, function(param, i) {
        // Don't decode the search params.
        if (i === params.length - 1) return param || null;
        return param ? decodeURIComponent(param) : null;
      });
    }

  });

  // Backbone.History
  // ----------------

  // Handles cross-browser history management, based on either
  // [pushState](http://diveintohtml5.info/history.html) and real URLs, or
  // [onhashchange](https://developer.mozilla.org/en-US/docs/DOM/window.onhashchange)
  // and URL fragments. If the browser supports neither (old IE, natch),
  // falls back to polling.
  var History = Backbone.History = function() {
    this.handlers = [];
    _.bindAll(this, 'checkUrl');

    // Ensure that `History` can be used outside of the browser.
    if (typeof window !== 'undefined') {
      this.location = window.location;
      this.history = window.history;
    }
  };

  // Cached regex for stripping a leading hash/slash and trailing space.
  var routeStripper = /^[#\/]|\s+$/g;

  // Cached regex for stripping leading and trailing slashes.
  var rootStripper = /^\/+|\/+$/g;

  // Cached regex for detecting MSIE.
  var isExplorer = /msie [\w.]+/;

  // Cached regex for removing a trailing slash.
  var trailingSlash = /\/$/;

  // Cached regex for stripping urls of hash.
  var pathStripper = /#.*$/;

  // Has the history handling already been started?
  History.started = false;

  // Set up all inheritable **Backbone.History** properties and methods.
  _.extend(History.prototype, Events, {

    // The default interval to poll for hash changes, if necessary, is
    // twenty times a second.
    interval: 50,

    // Are we at the app root?
    atRoot: function() {
      return this.location.pathname.replace(/[^\/]$/, '$&/') === this.root;
    },

    // Gets the true hash value. Cannot use location.hash directly due to bug
    // in Firefox where location.hash will always be decoded.
    getHash: function(window) {
      var match = (window || this).location.href.match(/#(.*)$/);
      return match ? match[1] : '';
    },

    // Get the cross-browser normalized URL fragment, either from the URL,
    // the hash, or the override.
    getFragment: function(fragment, forcePushState) {
      if (fragment == null) {
        if (this._hasPushState || !this._wantsHashChange || forcePushState) {
          fragment = decodeURI(this.location.pathname + this.location.search);
          var root = this.root.replace(trailingSlash, '');
          if (!fragment.indexOf(root)) fragment = fragment.slice(root.length);
        } else {
          fragment = this.getHash();
        }
      }
      return fragment.replace(routeStripper, '');
    },

    // Start the hash change handling, returning `true` if the current URL matches
    // an existing route, and `false` otherwise.
    start: function(options) {
      if (History.started) throw new Error("Backbone.history has already been started");
      History.started = true;

      // Figure out the initial configuration. Do we need an iframe?
      // Is pushState desired ... is it available?
      this.options          = _.extend({root: '/'}, this.options, options);
      this.root             = this.options.root;
      this._wantsHashChange = this.options.hashChange !== false;
      this._wantsPushState  = !!this.options.pushState;
      this._hasPushState    = !!(this.options.pushState && this.history && this.history.pushState);
      var fragment          = this.getFragment();
      var docMode           = document.documentMode;
      var oldIE             = (isExplorer.exec(navigator.userAgent.toLowerCase()) && (!docMode || docMode <= 7));

      // Normalize root to always include a leading and trailing slash.
      this.root = ('/' + this.root + '/').replace(rootStripper, '/');

      if (oldIE && this._wantsHashChange) {
        var frame = Backbone.$('<iframe src="javascript:0" tabindex="-1">');
        this.iframe = frame.hide().appendTo('body')[0].contentWindow;
        this.navigate(fragment);
      }

      // Depending on whether we're using pushState or hashes, and whether
      // 'onhashchange' is supported, determine how we check the URL state.
      if (this._hasPushState) {
        Backbone.$(window).on('popstate', this.checkUrl);
      } else if (this._wantsHashChange && ('onhashchange' in window) && !oldIE) {
        Backbone.$(window).on('hashchange', this.checkUrl);
      } else if (this._wantsHashChange) {
        this._checkUrlInterval = setInterval(this.checkUrl, this.interval);
      }

      // Determine if we need to change the base url, for a pushState link
      // opened by a non-pushState browser.
      this.fragment = fragment;
      var loc = this.location;

      // Transition from hashChange to pushState or vice versa if both are
      // requested.
      if (this._wantsHashChange && this._wantsPushState) {

        // If we've started off with a route from a `pushState`-enabled
        // browser, but we're currently in a browser that doesn't support it...
        if (!this._hasPushState && !this.atRoot()) {
          this.fragment = this.getFragment(null, true);
          this.location.replace(this.root + '#' + this.fragment);
          // Return immediately as browser will do redirect to new url
          return true;

        // Or if we've started out with a hash-based route, but we're currently
        // in a browser where it could be `pushState`-based instead...
        } else if (this._hasPushState && this.atRoot() && loc.hash) {
          this.fragment = this.getHash().replace(routeStripper, '');
          this.history.replaceState({}, document.title, this.root + this.fragment);
        }

      }

      if (!this.options.silent) return this.loadUrl();
    },

    // Disable Backbone.history, perhaps temporarily. Not useful in a real app,
    // but possibly useful for unit testing Routers.
    stop: function() {
      Backbone.$(window).off('popstate', this.checkUrl).off('hashchange', this.checkUrl);
      if (this._checkUrlInterval) clearInterval(this._checkUrlInterval);
      History.started = false;
    },

    // Add a route to be tested when the fragment changes. Routes added later
    // may override previous routes.
    route: function(route, callback) {
      this.handlers.unshift({route: route, callback: callback});
    },

    // Checks the current URL to see if it has changed, and if it has,
    // calls `loadUrl`, normalizing across the hidden iframe.
    checkUrl: function(e) {
      var current = this.getFragment();
      if (current === this.fragment && this.iframe) {
        current = this.getFragment(this.getHash(this.iframe));
      }
      if (current === this.fragment) return false;
      if (this.iframe) this.navigate(current);
      this.loadUrl();
    },

    // Attempt to load the current URL fragment. If a route succeeds with a
    // match, returns `true`. If no defined routes matches the fragment,
    // returns `false`.
    loadUrl: function(fragment) {
      fragment = this.fragment = this.getFragment(fragment);
      return _.any(this.handlers, function(handler) {
        if (handler.route.test(fragment)) {
          handler.callback(fragment);
          return true;
        }
      });
    },

    // Save a fragment into the hash history, or replace the URL state if the
    // 'replace' option is passed. You are responsible for properly URL-encoding
    // the fragment in advance.
    //
    // The options object can contain `trigger: true` if you wish to have the
    // route callback be fired (not usually desirable), or `replace: true`, if
    // you wish to modify the current URL without adding an entry to the history.
    navigate: function(fragment, options) {
      if (!History.started) return false;
      if (!options || options === true) options = {trigger: !!options};

      var url = this.root + (fragment = this.getFragment(fragment || ''));

      // Strip the hash for matching.
      fragment = fragment.replace(pathStripper, '');

      if (this.fragment === fragment) return;
      this.fragment = fragment;

      // Don't include a trailing slash on the root.
      if (fragment === '' && url !== '/') url = url.slice(0, -1);

      // If pushState is available, we use it to set the fragment as a real URL.
      if (this._hasPushState) {
        this.history[options.replace ? 'replaceState' : 'pushState']({}, document.title, url);

      // If hash changes haven't been explicitly disabled, update the hash
      // fragment to store history.
      } else if (this._wantsHashChange) {
        this._updateHash(this.location, fragment, options.replace);
        if (this.iframe && (fragment !== this.getFragment(this.getHash(this.iframe)))) {
          // Opening and closing the iframe tricks IE7 and earlier to push a
          // history entry on hash-tag change.  When replace is true, we don't
          // want this.
          if(!options.replace) this.iframe.document.open().close();
          this._updateHash(this.iframe.location, fragment, options.replace);
        }

      // If you've told us that you explicitly don't want fallback hashchange-
      // based history, then `navigate` becomes a page refresh.
      } else {
        return this.location.assign(url);
      }
      if (options.trigger) return this.loadUrl(fragment);
    },

    // Update the hash location, either replacing the current entry, or adding
    // a new one to the browser history.
    _updateHash: function(location, fragment, replace) {
      if (replace) {
        var href = location.href.replace(/(javascript:|#).*$/, '');
        location.replace(href + '#' + fragment);
      } else {
        // Some browsers require that `hash` contains a leading #.
        location.hash = '#' + fragment;
      }
    }

  });

  // Create the default Backbone.history.
  Backbone.history = new History;

  // Helpers
  // -------

  // Helper function to correctly set up the prototype chain, for subclasses.
  // Similar to `goog.inherits`, but uses a hash of prototype properties and
  // class properties to be extended.
  var extend = function(protoProps, staticProps) {
    var parent = this;
    var child;

    // The constructor function for the new subclass is either defined by you
    // (the "constructor" property in your `extend` definition), or defaulted
    // by us to simply call the parent's constructor.
    if (protoProps && _.has(protoProps, 'constructor')) {
      child = protoProps.constructor;
    } else {
      child = function(){ return parent.apply(this, arguments); };
    }

    // Add static properties to the constructor function, if supplied.
    _.extend(child, parent, staticProps);

    // Set the prototype chain to inherit from `parent`, without calling
    // `parent`'s constructor function.
    var Surrogate = function(){ this.constructor = child; };
    Surrogate.prototype = parent.prototype;
    child.prototype = new Surrogate;

    // Add prototype properties (instance properties) to the subclass,
    // if supplied.
    if (protoProps) _.extend(child.prototype, protoProps);

    // Set a convenience property in case the parent's prototype is needed
    // later.
    child.__super__ = parent.prototype;

    return child;
  };

  // Set up inheritance for the model, collection, router, view and history.
  Model.extend = Collection.extend = Router.extend = View.extend = History.extend = extend;

  // Throw an error when a URL is needed, and none is supplied.
  var urlError = function() {
    throw new Error('A "url" property or function must be specified');
  };

  // Wrap an optional error callback with a fallback error event.
  var wrapError = function(model, options) {
    var error = options.error;
    options.error = function(resp) {
      if (error) error(model, resp, options);
      model.trigger('error', model, resp, options);
    };
  };

  return Backbone;

}));;(function webpackUniversalModuleDefinition(root, factory) {
	if(typeof exports === 'object' && typeof module === 'object')
		module.exports = factory();
	else if(typeof define === 'function' && define.amd)
		define([], factory);
	else if(typeof exports === 'object')
		exports["Handlebars"] = factory();
	else
		root["Handlebars"] = factory();
})(this, function() {
return /******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};

/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {

/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId])
/******/ 			return installedModules[moduleId].exports;

/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			exports: {},
/******/ 			id: moduleId,
/******/ 			loaded: false
/******/ 		};

/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);

/******/ 		// Flag the module as loaded
/******/ 		module.loaded = true;

/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}


/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;

/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;

/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";

/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(0);
/******/ })
/************************************************************************/
/******/ ([
/* 0 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireWildcard = __webpack_require__(1)['default'];

	var _interopRequireDefault = __webpack_require__(2)['default'];

	exports.__esModule = true;

	var _handlebarsBase = __webpack_require__(3);

	var base = _interopRequireWildcard(_handlebarsBase);

	// Each of these augment the Handlebars object. No need to setup here.
	// (This is done to easily share code between commonjs and browse envs)

	var _handlebarsSafeString = __webpack_require__(36);

	var _handlebarsSafeString2 = _interopRequireDefault(_handlebarsSafeString);

	var _handlebarsException = __webpack_require__(5);

	var _handlebarsException2 = _interopRequireDefault(_handlebarsException);

	var _handlebarsUtils = __webpack_require__(4);

	var Utils = _interopRequireWildcard(_handlebarsUtils);

	var _handlebarsRuntime = __webpack_require__(37);

	var runtime = _interopRequireWildcard(_handlebarsRuntime);

	var _handlebarsNoConflict = __webpack_require__(43);

	var _handlebarsNoConflict2 = _interopRequireDefault(_handlebarsNoConflict);

	// For compatibility and usage outside of module systems, make the Handlebars object a namespace
	function create() {
	  var hb = new base.HandlebarsEnvironment();

	  Utils.extend(hb, base);
	  hb.SafeString = _handlebarsSafeString2['default'];
	  hb.Exception = _handlebarsException2['default'];
	  hb.Utils = Utils;
	  hb.escapeExpression = Utils.escapeExpression;

	  hb.VM = runtime;
	  hb.template = function (spec) {
	    return runtime.template(spec, hb);
	  };

	  return hb;
	}

	var inst = create();
	inst.create = create;

	_handlebarsNoConflict2['default'](inst);

	inst['default'] = inst;

	exports['default'] = inst;
	module.exports = exports['default'];

/***/ }),
/* 1 */
/***/ (function(module, exports) {

	"use strict";

	exports["default"] = function (obj) {
	  if (obj && obj.__esModule) {
	    return obj;
	  } else {
	    var newObj = {};

	    if (obj != null) {
	      for (var key in obj) {
	        if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key];
	      }
	    }

	    newObj["default"] = obj;
	    return newObj;
	  }
	};

	exports.__esModule = true;

/***/ }),
/* 2 */
/***/ (function(module, exports) {

	"use strict";

	exports["default"] = function (obj) {
	  return obj && obj.__esModule ? obj : {
	    "default": obj
	  };
	};

	exports.__esModule = true;

/***/ }),
/* 3 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(2)['default'];

	exports.__esModule = true;
	exports.HandlebarsEnvironment = HandlebarsEnvironment;

	var _utils = __webpack_require__(4);

	var _exception = __webpack_require__(5);

	var _exception2 = _interopRequireDefault(_exception);

	var _helpers = __webpack_require__(9);

	var _decorators = __webpack_require__(29);

	var _logger = __webpack_require__(31);

	var _logger2 = _interopRequireDefault(_logger);

	var _internalProtoAccess = __webpack_require__(32);

	var VERSION = '4.7.7';
	exports.VERSION = VERSION;
	var COMPILER_REVISION = 8;
	exports.COMPILER_REVISION = COMPILER_REVISION;
	var LAST_COMPATIBLE_COMPILER_REVISION = 7;

	exports.LAST_COMPATIBLE_COMPILER_REVISION = LAST_COMPATIBLE_COMPILER_REVISION;
	var REVISION_CHANGES = {
	  1: '<= 1.0.rc.2', // 1.0.rc.2 is actually rev2 but doesn't report it
	  2: '== 1.0.0-rc.3',
	  3: '== 1.0.0-rc.4',
	  4: '== 1.x.x',
	  5: '== 2.0.0-alpha.x',
	  6: '>= 2.0.0-beta.1',
	  7: '>= 4.0.0 <4.3.0',
	  8: '>= 4.3.0'
	};

	exports.REVISION_CHANGES = REVISION_CHANGES;
	var objectType = '[object Object]';

	function HandlebarsEnvironment(helpers, partials, decorators) {
	  this.helpers = helpers || {};
	  this.partials = partials || {};
	  this.decorators = decorators || {};

	  _helpers.registerDefaultHelpers(this);
	  _decorators.registerDefaultDecorators(this);
	}

	HandlebarsEnvironment.prototype = {
	  constructor: HandlebarsEnvironment,

	  logger: _logger2['default'],
	  log: _logger2['default'].log,

	  registerHelper: function registerHelper(name, fn) {
	    if (_utils.toString.call(name) === objectType) {
	      if (fn) {
	        throw new _exception2['default']('Arg not supported with multiple helpers');
	      }
	      _utils.extend(this.helpers, name);
	    } else {
	      this.helpers[name] = fn;
	    }
	  },
	  unregisterHelper: function unregisterHelper(name) {
	    delete this.helpers[name];
	  },

	  registerPartial: function registerPartial(name, partial) {
	    if (_utils.toString.call(name) === objectType) {
	      _utils.extend(this.partials, name);
	    } else {
	      if (typeof partial === 'undefined') {
	        throw new _exception2['default']('Attempting to register a partial called "' + name + '" as undefined');
	      }
	      this.partials[name] = partial;
	    }
	  },
	  unregisterPartial: function unregisterPartial(name) {
	    delete this.partials[name];
	  },

	  registerDecorator: function registerDecorator(name, fn) {
	    if (_utils.toString.call(name) === objectType) {
	      if (fn) {
	        throw new _exception2['default']('Arg not supported with multiple decorators');
	      }
	      _utils.extend(this.decorators, name);
	    } else {
	      this.decorators[name] = fn;
	    }
	  },
	  unregisterDecorator: function unregisterDecorator(name) {
	    delete this.decorators[name];
	  },
	  /**
	   * Reset the memory of illegal property accesses that have already been logged.
	   * @deprecated should only be used in handlebars test-cases
	   */
	  resetLoggedPropertyAccesses: function resetLoggedPropertyAccesses() {
	    _internalProtoAccess.resetLoggedProperties();
	  }
	};

	var log = _logger2['default'].log;

	exports.log = log;
	exports.createFrame = _utils.createFrame;
	exports.logger = _logger2['default'];

/***/ }),
/* 4 */
/***/ (function(module, exports) {

	'use strict';

	exports.__esModule = true;
	exports.extend = extend;
	exports.indexOf = indexOf;
	exports.escapeExpression = escapeExpression;
	exports.isEmpty = isEmpty;
	exports.createFrame = createFrame;
	exports.blockParams = blockParams;
	exports.appendContextPath = appendContextPath;
	var escape = {
	  '&': '&amp;',
	  '<': '&lt;',
	  '>': '&gt;',
	  '"': '&quot;',
	  "'": '&#x27;',
	  '`': '&#x60;',
	  '=': '&#x3D;'
	};

	var badChars = /[&<>"'`=]/g,
	    possible = /[&<>"'`=]/;

	function escapeChar(chr) {
	  return escape[chr];
	}

	function extend(obj /* , ...source */) {
	  for (var i = 1; i < arguments.length; i++) {
	    for (var key in arguments[i]) {
	      if (Object.prototype.hasOwnProperty.call(arguments[i], key)) {
	        obj[key] = arguments[i][key];
	      }
	    }
	  }

	  return obj;
	}

	var toString = Object.prototype.toString;

	exports.toString = toString;
	// Sourced from lodash
	// https://github.com/bestiejs/lodash/blob/master/LICENSE.txt
	/* eslint-disable func-style */
	var isFunction = function isFunction(value) {
	  return typeof value === 'function';
	};
	// fallback for older versions of Chrome and Safari
	/* istanbul ignore next */
	if (isFunction(/x/)) {
	  exports.isFunction = isFunction = function (value) {
	    return typeof value === 'function' && toString.call(value) === '[object Function]';
	  };
	}
	exports.isFunction = isFunction;

	/* eslint-enable func-style */

	/* istanbul ignore next */
	var isArray = Array.isArray || function (value) {
	  return value && typeof value === 'object' ? toString.call(value) === '[object Array]' : false;
	};

	exports.isArray = isArray;
	// Older IE versions do not directly support indexOf so we must implement our own, sadly.

	function indexOf(array, value) {
	  for (var i = 0, len = array.length; i < len; i++) {
	    if (array[i] === value) {
	      return i;
	    }
	  }
	  return -1;
	}

	function escapeExpression(string) {
	  if (typeof string !== 'string') {
	    // don't escape SafeStrings, since they're already safe
	    if (string && string.toHTML) {
	      return string.toHTML();
	    } else if (string == null) {
	      return '';
	    } else if (!string) {
	      return string + '';
	    }

	    // Force a string conversion as this will be done by the append regardless and
	    // the regex test will do this transparently behind the scenes, causing issues if
	    // an object's to string has escaped characters in it.
	    string = '' + string;
	  }

	  if (!possible.test(string)) {
	    return string;
	  }
	  return string.replace(badChars, escapeChar);
	}

	function isEmpty(value) {
	  if (!value && value !== 0) {
	    return true;
	  } else if (isArray(value) && value.length === 0) {
	    return true;
	  } else {
	    return false;
	  }
	}

	function createFrame(object) {
	  var frame = extend({}, object);
	  frame._parent = object;
	  return frame;
	}

	function blockParams(params, ids) {
	  params.path = ids;
	  return params;
	}

	function appendContextPath(contextPath, id) {
	  return (contextPath ? contextPath + '.' : '') + id;
	}

/***/ }),
/* 5 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _Object$defineProperty = __webpack_require__(6)['default'];

	exports.__esModule = true;
	var errorProps = ['description', 'fileName', 'lineNumber', 'endLineNumber', 'message', 'name', 'number', 'stack'];

	function Exception(message, node) {
	  var loc = node && node.loc,
	      line = undefined,
	      endLineNumber = undefined,
	      column = undefined,
	      endColumn = undefined;

	  if (loc) {
	    line = loc.start.line;
	    endLineNumber = loc.end.line;
	    column = loc.start.column;
	    endColumn = loc.end.column;

	    message += ' - ' + line + ':' + column;
	  }

	  var tmp = Error.prototype.constructor.call(this, message);

	  // Unfortunately errors are not enumerable in Chrome (at least), so `for prop in tmp` doesn't work.
	  for (var idx = 0; idx < errorProps.length; idx++) {
	    this[errorProps[idx]] = tmp[errorProps[idx]];
	  }

	  /* istanbul ignore else */
	  if (Error.captureStackTrace) {
	    Error.captureStackTrace(this, Exception);
	  }

	  try {
	    if (loc) {
	      this.lineNumber = line;
	      this.endLineNumber = endLineNumber;

	      // Work around issue under safari where we can't directly set the column value
	      /* istanbul ignore next */
	      if (_Object$defineProperty) {
	        Object.defineProperty(this, 'column', {
	          value: column,
	          enumerable: true
	        });
	        Object.defineProperty(this, 'endColumn', {
	          value: endColumn,
	          enumerable: true
	        });
	      } else {
	        this.column = column;
	        this.endColumn = endColumn;
	      }
	    }
	  } catch (nop) {
	    /* Ignore if the browser is very particular */
	  }
	}

	Exception.prototype = new Error();

	exports['default'] = Exception;
	module.exports = exports['default'];

/***/ }),
/* 6 */
/***/ (function(module, exports, __webpack_require__) {

	module.exports = { "default": __webpack_require__(7), __esModule: true };

/***/ }),
/* 7 */
/***/ (function(module, exports, __webpack_require__) {

	var $ = __webpack_require__(8);
	module.exports = function defineProperty(it, key, desc){
	  return $.setDesc(it, key, desc);
	};

/***/ }),
/* 8 */
/***/ (function(module, exports) {

	var $Object = Object;
	module.exports = {
	  create:     $Object.create,
	  getProto:   $Object.getPrototypeOf,
	  isEnum:     {}.propertyIsEnumerable,
	  getDesc:    $Object.getOwnPropertyDescriptor,
	  setDesc:    $Object.defineProperty,
	  setDescs:   $Object.defineProperties,
	  getKeys:    $Object.keys,
	  getNames:   $Object.getOwnPropertyNames,
	  getSymbols: $Object.getOwnPropertySymbols,
	  each:       [].forEach
	};

/***/ }),
/* 9 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(2)['default'];

	exports.__esModule = true;
	exports.registerDefaultHelpers = registerDefaultHelpers;
	exports.moveHelperToHooks = moveHelperToHooks;

	var _helpersBlockHelperMissing = __webpack_require__(10);

	var _helpersBlockHelperMissing2 = _interopRequireDefault(_helpersBlockHelperMissing);

	var _helpersEach = __webpack_require__(11);

	var _helpersEach2 = _interopRequireDefault(_helpersEach);

	var _helpersHelperMissing = __webpack_require__(24);

	var _helpersHelperMissing2 = _interopRequireDefault(_helpersHelperMissing);

	var _helpersIf = __webpack_require__(25);

	var _helpersIf2 = _interopRequireDefault(_helpersIf);

	var _helpersLog = __webpack_require__(26);

	var _helpersLog2 = _interopRequireDefault(_helpersLog);

	var _helpersLookup = __webpack_require__(27);

	var _helpersLookup2 = _interopRequireDefault(_helpersLookup);

	var _helpersWith = __webpack_require__(28);

	var _helpersWith2 = _interopRequireDefault(_helpersWith);

	function registerDefaultHelpers(instance) {
	  _helpersBlockHelperMissing2['default'](instance);
	  _helpersEach2['default'](instance);
	  _helpersHelperMissing2['default'](instance);
	  _helpersIf2['default'](instance);
	  _helpersLog2['default'](instance);
	  _helpersLookup2['default'](instance);
	  _helpersWith2['default'](instance);
	}

	function moveHelperToHooks(instance, helperName, keepHelper) {
	  if (instance.helpers[helperName]) {
	    instance.hooks[helperName] = instance.helpers[helperName];
	    if (!keepHelper) {
	      delete instance.helpers[helperName];
	    }
	  }
	}

/***/ }),
/* 10 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	exports.__esModule = true;

	var _utils = __webpack_require__(4);

	exports['default'] = function (instance) {
	  instance.registerHelper('blockHelperMissing', function (context, options) {
	    var inverse = options.inverse,
	        fn = options.fn;

	    if (context === true) {
	      return fn(this);
	    } else if (context === false || context == null) {
	      return inverse(this);
	    } else if (_utils.isArray(context)) {
	      if (context.length > 0) {
	        if (options.ids) {
	          options.ids = [options.name];
	        }

	        return instance.helpers.each(context, options);
	      } else {
	        return inverse(this);
	      }
	    } else {
	      if (options.data && options.ids) {
	        var data = _utils.createFrame(options.data);
	        data.contextPath = _utils.appendContextPath(options.data.contextPath, options.name);
	        options = { data: data };
	      }

	      return fn(context, options);
	    }
	  });
	};

	module.exports = exports['default'];

/***/ }),
/* 11 */
/***/ (function(module, exports, __webpack_require__) {

	/* WEBPACK VAR INJECTION */(function(global) {'use strict';

	var _Object$keys = __webpack_require__(12)['default'];

	var _interopRequireDefault = __webpack_require__(2)['default'];

	exports.__esModule = true;

	var _utils = __webpack_require__(4);

	var _exception = __webpack_require__(5);

	var _exception2 = _interopRequireDefault(_exception);

	exports['default'] = function (instance) {
	  instance.registerHelper('each', function (context, options) {
	    if (!options) {
	      throw new _exception2['default']('Must pass iterator to #each');
	    }

	    var fn = options.fn,
	        inverse = options.inverse,
	        i = 0,
	        ret = '',
	        data = undefined,
	        contextPath = undefined;

	    if (options.data && options.ids) {
	      contextPath = _utils.appendContextPath(options.data.contextPath, options.ids[0]) + '.';
	    }

	    if (_utils.isFunction(context)) {
	      context = context.call(this);
	    }

	    if (options.data) {
	      data = _utils.createFrame(options.data);
	    }

	    function execIteration(field, index, last) {
	      if (data) {
	        data.key = field;
	        data.index = index;
	        data.first = index === 0;
	        data.last = !!last;

	        if (contextPath) {
	          data.contextPath = contextPath + field;
	        }
	      }

	      ret = ret + fn(context[field], {
	        data: data,
	        blockParams: _utils.blockParams([context[field], field], [contextPath + field, null])
	      });
	    }

	    if (context && typeof context === 'object') {
	      if (_utils.isArray(context)) {
	        for (var j = context.length; i < j; i++) {
	          if (i in context) {
	            execIteration(i, i, i === context.length - 1);
	          }
	        }
	      } else if (global.Symbol && context[global.Symbol.iterator]) {
	        var newContext = [];
	        var iterator = context[global.Symbol.iterator]();
	        for (var it = iterator.next(); !it.done; it = iterator.next()) {
	          newContext.push(it.value);
	        }
	        context = newContext;
	        for (var j = context.length; i < j; i++) {
	          execIteration(i, i, i === context.length - 1);
	        }
	      } else {
	        (function () {
	          var priorKey = undefined;

	          _Object$keys(context).forEach(function (key) {
	            // We're running the iterations one step out of sync so we can detect
	            // the last iteration without have to scan the object twice and create
	            // an itermediate keys array.
	            if (priorKey !== undefined) {
	              execIteration(priorKey, i - 1);
	            }
	            priorKey = key;
	            i++;
	          });
	          if (priorKey !== undefined) {
	            execIteration(priorKey, i - 1, true);
	          }
	        })();
	      }
	    }

	    if (i === 0) {
	      ret = inverse(this);
	    }

	    return ret;
	  });
	};

	module.exports = exports['default'];
	/* WEBPACK VAR INJECTION */}.call(exports, (function() { return this; }())))

/***/ }),
/* 12 */
/***/ (function(module, exports, __webpack_require__) {

	module.exports = { "default": __webpack_require__(13), __esModule: true };

/***/ }),
/* 13 */
/***/ (function(module, exports, __webpack_require__) {

	__webpack_require__(14);
	module.exports = __webpack_require__(20).Object.keys;

/***/ }),
/* 14 */
/***/ (function(module, exports, __webpack_require__) {

	// 19.1.2.14 Object.keys(O)
	var toObject = __webpack_require__(15);

	__webpack_require__(17)('keys', function($keys){
	  return function keys(it){
	    return $keys(toObject(it));
	  };
	});

/***/ }),
/* 15 */
/***/ (function(module, exports, __webpack_require__) {

	// 7.1.13 ToObject(argument)
	var defined = __webpack_require__(16);
	module.exports = function(it){
	  return Object(defined(it));
	};

/***/ }),
/* 16 */
/***/ (function(module, exports) {

	// 7.2.1 RequireObjectCoercible(argument)
	module.exports = function(it){
	  if(it == undefined)throw TypeError("Can't call method on  " + it);
	  return it;
	};

/***/ }),
/* 17 */
/***/ (function(module, exports, __webpack_require__) {

	// most Object methods by ES6 should accept primitives
	var $export = __webpack_require__(18)
	  , core    = __webpack_require__(20)
	  , fails   = __webpack_require__(23);
	module.exports = function(KEY, exec){
	  var fn  = (core.Object || {})[KEY] || Object[KEY]
	    , exp = {};
	  exp[KEY] = exec(fn);
	  $export($export.S + $export.F * fails(function(){ fn(1); }), 'Object', exp);
	};

/***/ }),
/* 18 */
/***/ (function(module, exports, __webpack_require__) {

	var global    = __webpack_require__(19)
	  , core      = __webpack_require__(20)
	  , ctx       = __webpack_require__(21)
	  , PROTOTYPE = 'prototype';

	var $export = function(type, name, source){
	  var IS_FORCED = type & $export.F
	    , IS_GLOBAL = type & $export.G
	    , IS_STATIC = type & $export.S
	    , IS_PROTO  = type & $export.P
	    , IS_BIND   = type & $export.B
	    , IS_WRAP   = type & $export.W
	    , exports   = IS_GLOBAL ? core : core[name] || (core[name] = {})
	    , target    = IS_GLOBAL ? global : IS_STATIC ? global[name] : (global[name] || {})[PROTOTYPE]
	    , key, own, out;
	  if(IS_GLOBAL)source = name;
	  for(key in source){
	    // contains in native
	    own = !IS_FORCED && target && key in target;
	    if(own && key in exports)continue;
	    // export native or passed
	    out = own ? target[key] : source[key];
	    // prevent global pollution for namespaces
	    exports[key] = IS_GLOBAL && typeof target[key] != 'function' ? source[key]
	    // bind timers to global for call from export context
	    : IS_BIND && own ? ctx(out, global)
	    // wrap global constructors for prevent change them in library
	    : IS_WRAP && target[key] == out ? (function(C){
	      var F = function(param){
	        return this instanceof C ? new C(param) : C(param);
	      };
	      F[PROTOTYPE] = C[PROTOTYPE];
	      return F;
	    // make static versions for prototype methods
	    })(out) : IS_PROTO && typeof out == 'function' ? ctx(Function.call, out) : out;
	    if(IS_PROTO)(exports[PROTOTYPE] || (exports[PROTOTYPE] = {}))[key] = out;
	  }
	};
	// type bitmap
	$export.F = 1;  // forced
	$export.G = 2;  // global
	$export.S = 4;  // static
	$export.P = 8;  // proto
	$export.B = 16; // bind
	$export.W = 32; // wrap
	module.exports = $export;

/***/ }),
/* 19 */
/***/ (function(module, exports) {

	// https://github.com/zloirock/core-js/issues/86#issuecomment-115759028
	var global = module.exports = typeof window != 'undefined' && window.Math == Math
	  ? window : typeof self != 'undefined' && self.Math == Math ? self : Function('return this')();
	if(typeof __g == 'number')__g = global; // eslint-disable-line no-undef

/***/ }),
/* 20 */
/***/ (function(module, exports) {

	var core = module.exports = {version: '1.2.6'};
	if(typeof __e == 'number')__e = core; // eslint-disable-line no-undef

/***/ }),
/* 21 */
/***/ (function(module, exports, __webpack_require__) {

	// optional / simple context binding
	var aFunction = __webpack_require__(22);
	module.exports = function(fn, that, length){
	  aFunction(fn);
	  if(that === undefined)return fn;
	  switch(length){
	    case 1: return function(a){
	      return fn.call(that, a);
	    };
	    case 2: return function(a, b){
	      return fn.call(that, a, b);
	    };
	    case 3: return function(a, b, c){
	      return fn.call(that, a, b, c);
	    };
	  }
	  return function(/* ...args */){
	    return fn.apply(that, arguments);
	  };
	};

/***/ }),
/* 22 */
/***/ (function(module, exports) {

	module.exports = function(it){
	  if(typeof it != 'function')throw TypeError(it + ' is not a function!');
	  return it;
	};

/***/ }),
/* 23 */
/***/ (function(module, exports) {

	module.exports = function(exec){
	  try {
	    return !!exec();
	  } catch(e){
	    return true;
	  }
	};

/***/ }),
/* 24 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(2)['default'];

	exports.__esModule = true;

	var _exception = __webpack_require__(5);

	var _exception2 = _interopRequireDefault(_exception);

	exports['default'] = function (instance) {
	  instance.registerHelper('helperMissing', function () /* [args, ]options */{
	    if (arguments.length === 1) {
	      // A missing field in a {{foo}} construct.
	      return undefined;
	    } else {
	      // Someone is actually trying to call something, blow up.
	      throw new _exception2['default']('Missing helper: "' + arguments[arguments.length - 1].name + '"');
	    }
	  });
	};

	module.exports = exports['default'];

/***/ }),
/* 25 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(2)['default'];

	exports.__esModule = true;

	var _utils = __webpack_require__(4);

	var _exception = __webpack_require__(5);

	var _exception2 = _interopRequireDefault(_exception);

	exports['default'] = function (instance) {
	  instance.registerHelper('if', function (conditional, options) {
	    if (arguments.length != 2) {
	      throw new _exception2['default']('#if requires exactly one argument');
	    }
	    if (_utils.isFunction(conditional)) {
	      conditional = conditional.call(this);
	    }

	    // Default behavior is to render the positive path if the value is truthy and not empty.
	    // The `includeZero` option may be set to treat the condtional as purely not empty based on the
	    // behavior of isEmpty. Effectively this determines if 0 is handled by the positive path or negative.
	    if (!options.hash.includeZero && !conditional || _utils.isEmpty(conditional)) {
	      return options.inverse(this);
	    } else {
	      return options.fn(this);
	    }
	  });

	  instance.registerHelper('unless', function (conditional, options) {
	    if (arguments.length != 2) {
	      throw new _exception2['default']('#unless requires exactly one argument');
	    }
	    return instance.helpers['if'].call(this, conditional, {
	      fn: options.inverse,
	      inverse: options.fn,
	      hash: options.hash
	    });
	  });
	};

	module.exports = exports['default'];

/***/ }),
/* 26 */
/***/ (function(module, exports) {

	'use strict';

	exports.__esModule = true;

	exports['default'] = function (instance) {
	  instance.registerHelper('log', function () /* message, options */{
	    var args = [undefined],
	        options = arguments[arguments.length - 1];
	    for (var i = 0; i < arguments.length - 1; i++) {
	      args.push(arguments[i]);
	    }

	    var level = 1;
	    if (options.hash.level != null) {
	      level = options.hash.level;
	    } else if (options.data && options.data.level != null) {
	      level = options.data.level;
	    }
	    args[0] = level;

	    instance.log.apply(instance, args);
	  });
	};

	module.exports = exports['default'];

/***/ }),
/* 27 */
/***/ (function(module, exports) {

	'use strict';

	exports.__esModule = true;

	exports['default'] = function (instance) {
	  instance.registerHelper('lookup', function (obj, field, options) {
	    if (!obj) {
	      // Note for 5.0: Change to "obj == null" in 5.0
	      return obj;
	    }
	    return options.lookupProperty(obj, field);
	  });
	};

	module.exports = exports['default'];

/***/ }),
/* 28 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(2)['default'];

	exports.__esModule = true;

	var _utils = __webpack_require__(4);

	var _exception = __webpack_require__(5);

	var _exception2 = _interopRequireDefault(_exception);

	exports['default'] = function (instance) {
	  instance.registerHelper('with', function (context, options) {
	    if (arguments.length != 2) {
	      throw new _exception2['default']('#with requires exactly one argument');
	    }
	    if (_utils.isFunction(context)) {
	      context = context.call(this);
	    }

	    var fn = options.fn;

	    if (!_utils.isEmpty(context)) {
	      var data = options.data;
	      if (options.data && options.ids) {
	        data = _utils.createFrame(options.data);
	        data.contextPath = _utils.appendContextPath(options.data.contextPath, options.ids[0]);
	      }

	      return fn(context, {
	        data: data,
	        blockParams: _utils.blockParams([context], [data && data.contextPath])
	      });
	    } else {
	      return options.inverse(this);
	    }
	  });
	};

	module.exports = exports['default'];

/***/ }),
/* 29 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _interopRequireDefault = __webpack_require__(2)['default'];

	exports.__esModule = true;
	exports.registerDefaultDecorators = registerDefaultDecorators;

	var _decoratorsInline = __webpack_require__(30);

	var _decoratorsInline2 = _interopRequireDefault(_decoratorsInline);

	function registerDefaultDecorators(instance) {
	  _decoratorsInline2['default'](instance);
	}

/***/ }),
/* 30 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	exports.__esModule = true;

	var _utils = __webpack_require__(4);

	exports['default'] = function (instance) {
	  instance.registerDecorator('inline', function (fn, props, container, options) {
	    var ret = fn;
	    if (!props.partials) {
	      props.partials = {};
	      ret = function (context, options) {
	        // Create a new partials stack frame prior to exec.
	        var original = container.partials;
	        container.partials = _utils.extend({}, original, props.partials);
	        var ret = fn(context, options);
	        container.partials = original;
	        return ret;
	      };
	    }

	    props.partials[options.args[0]] = options.fn;

	    return ret;
	  });
	};

	module.exports = exports['default'];

/***/ }),
/* 31 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	exports.__esModule = true;

	var _utils = __webpack_require__(4);

	var logger = {
	  methodMap: ['debug', 'info', 'warn', 'error'],
	  level: 'info',

	  // Maps a given level value to the `methodMap` indexes above.
	  lookupLevel: function lookupLevel(level) {
	    if (typeof level === 'string') {
	      var levelMap = _utils.indexOf(logger.methodMap, level.toLowerCase());
	      if (levelMap >= 0) {
	        level = levelMap;
	      } else {
	        level = parseInt(level, 10);
	      }
	    }

	    return level;
	  },

	  // Can be overridden in the host environment
	  log: function log(level) {
	    level = logger.lookupLevel(level);

	    if (typeof console !== 'undefined' && logger.lookupLevel(logger.level) <= level) {
	      var method = logger.methodMap[level];
	      // eslint-disable-next-line no-console
	      if (!console[method]) {
	        method = 'log';
	      }

	      for (var _len = arguments.length, message = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
	        message[_key - 1] = arguments[_key];
	      }

	      console[method].apply(console, message); // eslint-disable-line no-console
	    }
	  }
	};

	exports['default'] = logger;
	module.exports = exports['default'];

/***/ }),
/* 32 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _Object$create = __webpack_require__(33)['default'];

	var _Object$keys = __webpack_require__(12)['default'];

	var _interopRequireWildcard = __webpack_require__(1)['default'];

	exports.__esModule = true;
	exports.createProtoAccessControl = createProtoAccessControl;
	exports.resultIsAllowed = resultIsAllowed;
	exports.resetLoggedProperties = resetLoggedProperties;

	var _createNewLookupObject = __webpack_require__(35);

	var _logger = __webpack_require__(31);

	var logger = _interopRequireWildcard(_logger);

	var loggedProperties = _Object$create(null);

	function createProtoAccessControl(runtimeOptions) {
	  var defaultMethodWhiteList = _Object$create(null);
	  defaultMethodWhiteList['constructor'] = false;
	  defaultMethodWhiteList['__defineGetter__'] = false;
	  defaultMethodWhiteList['__defineSetter__'] = false;
	  defaultMethodWhiteList['__lookupGetter__'] = false;

	  var defaultPropertyWhiteList = _Object$create(null);
	  // eslint-disable-next-line no-proto
	  defaultPropertyWhiteList['__proto__'] = false;

	  return {
	    properties: {
	      whitelist: _createNewLookupObject.createNewLookupObject(defaultPropertyWhiteList, runtimeOptions.allowedProtoProperties),
	      defaultValue: runtimeOptions.allowProtoPropertiesByDefault
	    },
	    methods: {
	      whitelist: _createNewLookupObject.createNewLookupObject(defaultMethodWhiteList, runtimeOptions.allowedProtoMethods),
	      defaultValue: runtimeOptions.allowProtoMethodsByDefault
	    }
	  };
	}

	function resultIsAllowed(result, protoAccessControl, propertyName) {
	  if (typeof result === 'function') {
	    return checkWhiteList(protoAccessControl.methods, propertyName);
	  } else {
	    return checkWhiteList(protoAccessControl.properties, propertyName);
	  }
	}

	function checkWhiteList(protoAccessControlForType, propertyName) {
	  if (protoAccessControlForType.whitelist[propertyName] !== undefined) {
	    return protoAccessControlForType.whitelist[propertyName] === true;
	  }
	  if (protoAccessControlForType.defaultValue !== undefined) {
	    return protoAccessControlForType.defaultValue;
	  }
	  logUnexpecedPropertyAccessOnce(propertyName);
	  return false;
	}

	function logUnexpecedPropertyAccessOnce(propertyName) {
	  if (loggedProperties[propertyName] !== true) {
	    loggedProperties[propertyName] = true;
	    logger.log('error', 'Handlebars: Access has been denied to resolve the property "' + propertyName + '" because it is not an "own property" of its parent.\n' + 'You can add a runtime option to disable the check or this warning:\n' + 'See https://handlebarsjs.com/api-reference/runtime-options.html#options-to-control-prototype-access for details');
	  }
	}

	function resetLoggedProperties() {
	  _Object$keys(loggedProperties).forEach(function (propertyName) {
	    delete loggedProperties[propertyName];
	  });
	}

/***/ }),
/* 33 */
/***/ (function(module, exports, __webpack_require__) {

	module.exports = { "default": __webpack_require__(34), __esModule: true };

/***/ }),
/* 34 */
/***/ (function(module, exports, __webpack_require__) {

	var $ = __webpack_require__(8);
	module.exports = function create(P, D){
	  return $.create(P, D);
	};

/***/ }),
/* 35 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _Object$create = __webpack_require__(33)['default'];

	exports.__esModule = true;
	exports.createNewLookupObject = createNewLookupObject;

	var _utils = __webpack_require__(4);

	/**
	 * Create a new object with "null"-prototype to avoid truthy results on prototype properties.
	 * The resulting object can be used with "object[property]" to check if a property exists
	 * @param {...object} sources a varargs parameter of source objects that will be merged
	 * @returns {object}
	 */

	function createNewLookupObject() {
	  for (var _len = arguments.length, sources = Array(_len), _key = 0; _key < _len; _key++) {
	    sources[_key] = arguments[_key];
	  }

	  return _utils.extend.apply(undefined, [_Object$create(null)].concat(sources));
	}

/***/ }),
/* 36 */
/***/ (function(module, exports) {

	// Build out our basic SafeString type
	'use strict';

	exports.__esModule = true;
	function SafeString(string) {
	  this.string = string;
	}

	SafeString.prototype.toString = SafeString.prototype.toHTML = function () {
	  return '' + this.string;
	};

	exports['default'] = SafeString;
	module.exports = exports['default'];

/***/ }),
/* 37 */
/***/ (function(module, exports, __webpack_require__) {

	'use strict';

	var _Object$seal = __webpack_require__(38)['default'];

	var _Object$keys = __webpack_require__(12)['default'];

	var _interopRequireWildcard = __webpack_require__(1)['default'];

	var _interopRequireDefault = __webpack_require__(2)['default'];

	exports.__esModule = true;
	exports.checkRevision = checkRevision;
	exports.template = template;
	exports.wrapProgram = wrapProgram;
	exports.resolvePartial = resolvePartial;
	exports.invokePartial = invokePartial;
	exports.noop = noop;

	var _utils = __webpack_require__(4);

	var Utils = _interopRequireWildcard(_utils);

	var _exception = __webpack_require__(5);

	var _exception2 = _interopRequireDefault(_exception);

	var _base = __webpack_require__(3);

	var _helpers = __webpack_require__(9);

	var _internalWrapHelper = __webpack_require__(42);

	var _internalProtoAccess = __webpack_require__(32);

	function checkRevision(compilerInfo) {
	  var compilerRevision = compilerInfo && compilerInfo[0] || 1,
	      currentRevision = _base.COMPILER_REVISION;

	  if (compilerRevision >= _base.LAST_COMPATIBLE_COMPILER_REVISION && compilerRevision <= _base.COMPILER_REVISION) {
	    return;
	  }

	  if (compilerRevision < _base.LAST_COMPATIBLE_COMPILER_REVISION) {
	    var runtimeVersions = _base.REVISION_CHANGES[currentRevision],
	        compilerVersions = _base.REVISION_CHANGES[compilerRevision];
	    throw new _exception2['default']('Template was precompiled with an older version of Handlebars than the current runtime. ' + 'Please update your precompiler to a newer version (' + runtimeVersions + ') or downgrade your runtime to an older version (' + compilerVersions + ').');
	  } else {
	    // Use the embedded version info since the runtime doesn't know about this revision yet
	    throw new _exception2['default']('Template was precompiled with a newer version of Handlebars than the current runtime. ' + 'Please update your runtime to a newer version (' + compilerInfo[1] + ').');
	  }
	}

	function template(templateSpec, env) {
	  /* istanbul ignore next */
	  if (!env) {
	    throw new _exception2['default']('No environment passed to template');
	  }
	  if (!templateSpec || !templateSpec.main) {
	    throw new _exception2['default']('Unknown template object: ' + typeof templateSpec);
	  }

	  templateSpec.main.decorator = templateSpec.main_d;

	  // Note: Using env.VM references rather than local var references throughout this section to allow
	  // for external users to override these as pseudo-supported APIs.
	  env.VM.checkRevision(templateSpec.compiler);

	  // backwards compatibility for precompiled templates with compiler-version 7 (<4.3.0)
	  var templateWasPrecompiledWithCompilerV7 = templateSpec.compiler && templateSpec.compiler[0] === 7;

	  function invokePartialWrapper(partial, context, options) {
	    if (options.hash) {
	      context = Utils.extend({}, context, options.hash);
	      if (options.ids) {
	        options.ids[0] = true;
	      }
	    }
	    partial = env.VM.resolvePartial.call(this, partial, context, options);

	    var extendedOptions = Utils.extend({}, options, {
	      hooks: this.hooks,
	      protoAccessControl: this.protoAccessControl
	    });

	    var result = env.VM.invokePartial.call(this, partial, context, extendedOptions);

	    if (result == null && env.compile) {
	      options.partials[options.name] = env.compile(partial, templateSpec.compilerOptions, env);
	      result = options.partials[options.name](context, extendedOptions);
	    }
	    if (result != null) {
	      if (options.indent) {
	        var lines = result.split('\n');
	        for (var i = 0, l = lines.length; i < l; i++) {
	          if (!lines[i] && i + 1 === l) {
	            break;
	          }

	          lines[i] = options.indent + lines[i];
	        }
	        result = lines.join('\n');
	      }
	      return result;
	    } else {
	      throw new _exception2['default']('The partial ' + options.name + ' could not be compiled when running in runtime-only mode');
	    }
	  }

	  // Just add water
	  var container = {
	    strict: function strict(obj, name, loc) {
	      if (!obj || !(name in obj)) {
	        throw new _exception2['default']('"' + name + '" not defined in ' + obj, {
	          loc: loc
	        });
	      }
	      return container.lookupProperty(obj, name);
	    },
	    lookupProperty: function lookupProperty(parent, propertyName) {
	      var result = parent[propertyName];
	      if (result == null) {
	        return result;
	      }
	      if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
	        return result;
	      }

	      if (_internalProtoAccess.resultIsAllowed(result, container.protoAccessControl, propertyName)) {
	        return result;
	      }
	      return undefined;
	    },
	    lookup: function lookup(depths, name) {
	      var len = depths.length;
	      for (var i = 0; i < len; i++) {
	        var result = depths[i] && container.lookupProperty(depths[i], name);
	        if (result != null) {
	          return depths[i][name];
	        }
	      }
	    },
	    lambda: function lambda(current, context) {
	      return typeof current === 'function' ? current.call(context) : current;
	    },

	    escapeExpression: Utils.escapeExpression,
	    invokePartial: invokePartialWrapper,

	    fn: function fn(i) {
	      var ret = templateSpec[i];
	      ret.decorator = templateSpec[i + '_d'];
	      return ret;
	    },

	    programs: [],
	    program: function program(i, data, declaredBlockParams, blockParams, depths) {
	      var programWrapper = this.programs[i],
	          fn = this.fn(i);
	      if (data || depths || blockParams || declaredBlockParams) {
	        programWrapper = wrapProgram(this, i, fn, data, declaredBlockParams, blockParams, depths);
	      } else if (!programWrapper) {
	        programWrapper = this.programs[i] = wrapProgram(this, i, fn);
	      }
	      return programWrapper;
	    },

	    data: function data(value, depth) {
	      while (value && depth--) {
	        value = value._parent;
	      }
	      return value;
	    },
	    mergeIfNeeded: function mergeIfNeeded(param, common) {
	      var obj = param || common;

	      if (param && common && param !== common) {
	        obj = Utils.extend({}, common, param);
	      }

	      return obj;
	    },
	    // An empty object to use as replacement for null-contexts
	    nullContext: _Object$seal({}),

	    noop: env.VM.noop,
	    compilerInfo: templateSpec.compiler
	  };

	  function ret(context) {
	    var options = arguments.length <= 1 || arguments[1] === undefined ? {} : arguments[1];

	    var data = options.data;

	    ret._setup(options);
	    if (!options.partial && templateSpec.useData) {
	      data = initData(context, data);
	    }
	    var depths = undefined,
	        blockParams = templateSpec.useBlockParams ? [] : undefined;
	    if (templateSpec.useDepths) {
	      if (options.depths) {
	        depths = context != options.depths[0] ? [context].concat(options.depths) : options.depths;
	      } else {
	        depths = [context];
	      }
	    }

	    function main(context /*, options*/) {
	      return '' + templateSpec.main(container, context, container.helpers, container.partials, data, blockParams, depths);
	    }

	    main = executeDecorators(templateSpec.main, main, container, options.depths || [], data, blockParams);
	    return main(context, options);
	  }

	  ret.isTop = true;

	  ret._setup = function (options) {
	    if (!options.partial) {
	      var mergedHelpers = Utils.extend({}, env.helpers, options.helpers);
	      wrapHelpersToPassLookupProperty(mergedHelpers, container);
	      container.helpers = mergedHelpers;

	      if (templateSpec.usePartial) {
	        // Use mergeIfNeeded here to prevent compiling global partials multiple times
	        container.partials = container.mergeIfNeeded(options.partials, env.partials);
	      }
	      if (templateSpec.usePartial || templateSpec.useDecorators) {
	        container.decorators = Utils.extend({}, env.decorators, options.decorators);
	      }

	      container.hooks = {};
	      container.protoAccessControl = _internalProtoAccess.createProtoAccessControl(options);

	      var keepHelperInHelpers = options.allowCallsToHelperMissing || templateWasPrecompiledWithCompilerV7;
	      _helpers.moveHelperToHooks(container, 'helperMissing', keepHelperInHelpers);
	      _helpers.moveHelperToHooks(container, 'blockHelperMissing', keepHelperInHelpers);
	    } else {
	      container.protoAccessControl = options.protoAccessControl; // internal option
	      container.helpers = options.helpers;
	      container.partials = options.partials;
	      container.decorators = options.decorators;
	      container.hooks = options.hooks;
	    }
	  };

	  ret._child = function (i, data, blockParams, depths) {
	    if (templateSpec.useBlockParams && !blockParams) {
	      throw new _exception2['default']('must pass block params');
	    }
	    if (templateSpec.useDepths && !depths) {
	      throw new _exception2['default']('must pass parent depths');
	    }

	    return wrapProgram(container, i, templateSpec[i], data, 0, blockParams, depths);
	  };
	  return ret;
	}

	function wrapProgram(container, i, fn, data, declaredBlockParams, blockParams, depths) {
	  function prog(context) {
	    var options = arguments.length <= 1 || arguments[1] === undefined ? {} : arguments[1];

	    var currentDepths = depths;
	    if (depths && context != depths[0] && !(context === container.nullContext && depths[0] === null)) {
	      currentDepths = [context].concat(depths);
	    }

	    return fn(container, context, container.helpers, container.partials, options.data || data, blockParams && [options.blockParams].concat(blockParams), currentDepths);
	  }

	  prog = executeDecorators(fn, prog, container, depths, data, blockParams);

	  prog.program = i;
	  prog.depth = depths ? depths.length : 0;
	  prog.blockParams = declaredBlockParams || 0;
	  return prog;
	}

	/**
	 * This is currently part of the official API, therefore implementation details should not be changed.
	 */

	function resolvePartial(partial, context, options) {
	  if (!partial) {
	    if (options.name === '@partial-block') {
	      partial = options.data['partial-block'];
	    } else {
	      partial = options.partials[options.name];
	    }
	  } else if (!partial.call && !options.name) {
	    // This is a dynamic partial that returned a string
	    options.name = partial;
	    partial = options.partials[partial];
	  }
	  return partial;
	}

	function invokePartial(partial, context, options) {
	  // Use the current closure context to save the partial-block if this partial
	  var currentPartialBlock = options.data && options.data['partial-block'];
	  options.partial = true;
	  if (options.ids) {
	    options.data.contextPath = options.ids[0] || options.data.contextPath;
	  }

	  var partialBlock = undefined;
	  if (options.fn && options.fn !== noop) {
	    (function () {
	      options.data = _base.createFrame(options.data);
	      // Wrapper function to get access to currentPartialBlock from the closure
	      var fn = options.fn;
	      partialBlock = options.data['partial-block'] = function partialBlockWrapper(context) {
	        var options = arguments.length <= 1 || arguments[1] === undefined ? {} : arguments[1];

	        // Restore the partial-block from the closure for the execution of the block
	        // i.e. the part inside the block of the partial call.
	        options.data = _base.createFrame(options.data);
	        options.data['partial-block'] = currentPartialBlock;
	        return fn(context, options);
	      };
	      if (fn.partials) {
	        options.partials = Utils.extend({}, options.partials, fn.partials);
	      }
	    })();
	  }

	  if (partial === undefined && partialBlock) {
	    partial = partialBlock;
	  }

	  if (partial === undefined) {
	    throw new _exception2['default']('The partial ' + options.name + ' could not be found');
	  } else if (partial instanceof Function) {
	    return partial(context, options);
	  }
	}

	function noop() {
	  return '';
	}

	function initData(context, data) {
	  if (!data || !('root' in data)) {
	    data = data ? _base.createFrame(data) : {};
	    data.root = context;
	  }
	  return data;
	}

	function executeDecorators(fn, prog, container, depths, data, blockParams) {
	  if (fn.decorator) {
	    var props = {};
	    prog = fn.decorator(prog, props, container, depths && depths[0], data, blockParams, depths);
	    Utils.extend(prog, props);
	  }
	  return prog;
	}

	function wrapHelpersToPassLookupProperty(mergedHelpers, container) {
	  _Object$keys(mergedHelpers).forEach(function (helperName) {
	    var helper = mergedHelpers[helperName];
	    mergedHelpers[helperName] = passLookupPropertyOption(helper, container);
	  });
	}

	function passLookupPropertyOption(helper, container) {
	  var lookupProperty = container.lookupProperty;
	  return _internalWrapHelper.wrapHelper(helper, function (options) {
	    return Utils.extend({ lookupProperty: lookupProperty }, options);
	  });
	}

/***/ }),
/* 38 */
/***/ (function(module, exports, __webpack_require__) {

	module.exports = { "default": __webpack_require__(39), __esModule: true };

/***/ }),
/* 39 */
/***/ (function(module, exports, __webpack_require__) {

	__webpack_require__(40);
	module.exports = __webpack_require__(20).Object.seal;

/***/ }),
/* 40 */
/***/ (function(module, exports, __webpack_require__) {

	// 19.1.2.17 Object.seal(O)
	var isObject = __webpack_require__(41);

	__webpack_require__(17)('seal', function($seal){
	  return function seal(it){
	    return $seal && isObject(it) ? $seal(it) : it;
	  };
	});

/***/ }),
/* 41 */
/***/ (function(module, exports) {

	module.exports = function(it){
	  return typeof it === 'object' ? it !== null : typeof it === 'function';
	};

/***/ }),
/* 42 */
/***/ (function(module, exports) {

	'use strict';

	exports.__esModule = true;
	exports.wrapHelper = wrapHelper;

	function wrapHelper(helper, transformOptionsFn) {
	  if (typeof helper !== 'function') {
	    // This should not happen, but apparently it does in https://github.com/wycats/handlebars.js/issues/1639
	    // We try to make the wrapper least-invasive by not wrapping it, if the helper is not a function.
	    return helper;
	  }
	  var wrapper = function wrapper() /* dynamic arguments */{
	    var options = arguments[arguments.length - 1];
	    arguments[arguments.length - 1] = transformOptionsFn(options);
	    return helper.apply(this, arguments);
	  };
	  return wrapper;
	}

/***/ }),
/* 43 */
/***/ (function(module, exports) {

	/* WEBPACK VAR INJECTION */(function(global) {'use strict';

	exports.__esModule = true;

	exports['default'] = function (Handlebars) {
	  /* istanbul ignore next */
	  var root = typeof global !== 'undefined' ? global : window,
	      $Handlebars = root.Handlebars;
	  /* istanbul ignore next */
	  Handlebars.noConflict = function () {
	    if (root.Handlebars === Handlebars) {
	      root.Handlebars = $Handlebars;
	    }
	    return Handlebars;
	  };
	};

	module.exports = exports['default'];
	/* WEBPACK VAR INJECTION */}.call(exports, (function() { return this; }())))

/***/ })
/******/ ])
});
;;(function(root, factory) {
	if (typeof define === 'function' && define.amd) {
		define('sifter', factory);
	} else if (typeof exports === 'object') {
		module.exports = factory();
	} else {
		root.Sifter = factory();
	}
}(this, function() {

	/**
	 * Textually searches arrays and hashes of objects
	 * by property (or multiple properties). Designed
	 * specifically for autocomplete.
	 *
	 * @constructor
	 * @param {array|object} items
	 * @param {object} items
	 */
	var Sifter = function(items, settings) {
		this.items = items;
		this.settings = settings || {diacritics: true};
	};

	/**
	 * Splits a search string into an array of individual
	 * regexps to be used to match results.
	 *
	 * @param {string} query
	 * @returns {array}
	 */
	Sifter.prototype.tokenize = function(query) {
		query = trim(String(query || '').toLowerCase());
		if (!query || !query.length) return [];

		var i, n, regex, letter;
		var tokens = [];
		var words = query.split(/ +/);

		for (i = 0, n = words.length; i < n; i++) {
			regex = escape_regex(words[i]);
			if (this.settings.diacritics) {
				for (letter in DIACRITICS) {
					if (DIACRITICS.hasOwnProperty(letter)) {
						regex = regex.replace(new RegExp(letter, 'g'), DIACRITICS[letter]);
					}
				}
			}
			tokens.push({
				string : words[i],
				regex  : new RegExp(regex, 'i')
			});
		}

		return tokens;
	};

	/**
	 * Iterates over arrays and hashes.
	 *
	 * ```
	 * this.iterator(this.items, function(item, id) {
	 *    // invoked for each item
	 * });
	 * ```
	 *
	 * @param {array|object} object
	 */
	Sifter.prototype.iterator = function(object, callback) {
		var iterator;
		if (is_array(object)) {
			iterator = Array.prototype.forEach || function(callback) {
				for (var i = 0, n = this.length; i < n; i++) {
					callback(this[i], i, this);
				}
			};
		} else {
			iterator = function(callback) {
				for (var key in this) {
					if (this.hasOwnProperty(key)) {
						callback(this[key], key, this);
					}
				}
			};
		}

		iterator.apply(object, [callback]);
	};

	/**
	 * Returns a function to be used to score individual results.
	 *
	 * Good matches will have a higher score than poor matches.
	 * If an item is not a match, 0 will be returned by the function.
	 *
	 * @param {object|string} search
	 * @param {object} options (optional)
	 * @returns {function}
	 */
	Sifter.prototype.getScoreFunction = function(search, options) {
		var self, fields, tokens, token_count;

		self        = this;
		search      = self.prepareSearch(search, options);
		tokens      = search.tokens;
		fields      = search.options.fields;
		token_count = tokens.length;

		/**
		 * Calculates how close of a match the
		 * given value is against a search token.
		 *
		 * @param {mixed} value
		 * @param {object} token
		 * @return {number}
		 */
		var scoreValue = function(value, token) {
			var score, pos;

			if (!value) return 0;
			value = String(value || '');
			pos = value.search(token.regex);
			if (pos === -1) return 0;
			score = token.string.length / value.length;
			if (pos === 0) score += 0.5;
			return score;
		};

		/**
		 * Calculates the score of an object
		 * against the search query.
		 *
		 * @param {object} token
		 * @param {object} data
		 * @return {number}
		 */
		var scoreObject = (function() {
			var field_count = fields.length;
			if (!field_count) {
				return function() { return 0; };
			}
			if (field_count === 1) {
				return function(token, data) {
					return scoreValue(data[fields[0]], token);
				};
			}
			return function(token, data) {
				for (var i = 0, sum = 0; i < field_count; i++) {
					sum += scoreValue(data[fields[i]], token);
				}
				return sum / field_count;
			};
		})();

		if (!token_count) {
			return function() { return 0; };
		}
		if (token_count === 1) {
			return function(data) {
				return scoreObject(tokens[0], data);
			};
		}

		if (search.options.conjunction === 'and') {
			return function(data) {
				var score;
				for (var i = 0, sum = 0; i < token_count; i++) {
					score = scoreObject(tokens[i], data);
					if (score <= 0) return 0;
					sum += score;
				}
				return sum / token_count;
			};
		} else {
			return function(data) {
				for (var i = 0, sum = 0; i < token_count; i++) {
					sum += scoreObject(tokens[i], data);
				}
				return sum / token_count;
			};
		}
	};

	/**
	 * Returns a function that can be used to compare two
	 * results, for sorting purposes. If no sorting should
	 * be performed, `null` will be returned.
	 *
	 * @param {string|object} search
	 * @param {object} options
	 * @return function(a,b)
	 */
	Sifter.prototype.getSortFunction = function(search, options) {
		var i, n, self, field, fields, fields_count, multiplier, multipliers, get_field, implicit_score, sort;

		self   = this;
		search = self.prepareSearch(search, options);
		sort   = (!search.query && options.sort_empty) || options.sort;

		/**
		 * Fetches the specified sort field value
		 * from a search result item.
		 *
		 * @param  {string} name
		 * @param  {object} result
		 * @return {mixed}
		 */
		get_field  = function(name, result) {
			if (name === '$score') return result.score;
			return self.items[result.id][name];
		};

		// parse options
		fields = [];
		if (sort) {
			for (i = 0, n = sort.length; i < n; i++) {
				if (search.query || sort[i].field !== '$score') {
					fields.push(sort[i]);
				}
			}
		}

		// the "$score" field is implied to be the primary
		// sort field, unless it's manually specified
		if (search.query) {
			implicit_score = true;
			for (i = 0, n = fields.length; i < n; i++) {
				if (fields[i].field === '$score') {
					implicit_score = false;
					break;
				}
			}
			if (implicit_score) {
				fields.unshift({field: '$score', direction: 'desc'});
			}
		} else {
			for (i = 0, n = fields.length; i < n; i++) {
				if (fields[i].field === '$score') {
					fields.splice(i, 1);
					break;
				}
			}
		}

		multipliers = [];
		for (i = 0, n = fields.length; i < n; i++) {
			multipliers.push(fields[i].direction === 'desc' ? -1 : 1);
		}

		// build function
		fields_count = fields.length;
		if (!fields_count) {
			return null;
		} else if (fields_count === 1) {
			field = fields[0].field;
			multiplier = multipliers[0];
			return function(a, b) {
				return multiplier * cmp(
					get_field(field, a),
					get_field(field, b)
				);
			};
		} else {
			return function(a, b) {
				var i, result, a_value, b_value, field;
				for (i = 0; i < fields_count; i++) {
					field = fields[i].field;
					result = multipliers[i] * cmp(
						get_field(field, a),
						get_field(field, b)
					);
					if (result) return result;
				}
				return 0;
			};
		}
	};

	/**
	 * Parses a search query and returns an object
	 * with tokens and fields ready to be populated
	 * with results.
	 *
	 * @param {string} query
	 * @param {object} options
	 * @returns {object}
	 */
	Sifter.prototype.prepareSearch = function(query, options) {
		if (typeof query === 'object') return query;

		options = extend({}, options);

		var option_fields     = options.fields;
		var option_sort       = options.sort;
		var option_sort_empty = options.sort_empty;

		if (option_fields && !is_array(option_fields)) options.fields = [option_fields];
		if (option_sort && !is_array(option_sort)) options.sort = [option_sort];
		if (option_sort_empty && !is_array(option_sort_empty)) options.sort_empty = [option_sort_empty];

		return {
			options : options,
			query   : String(query || '').toLowerCase(),
			tokens  : this.tokenize(query),
			total   : 0,
			items   : []
		};
	};

	/**
	 * Searches through all items and returns a sorted array of matches.
	 *
	 * The `options` parameter can contain:
	 *
	 *   - fields {string|array}
	 *   - sort {array}
	 *   - score {function}
	 *   - filter {bool}
	 *   - limit {integer}
	 *
	 * Returns an object containing:
	 *
	 *   - options {object}
	 *   - query {string}
	 *   - tokens {array}
	 *   - total {int}
	 *   - items {array}
	 *
	 * @param {string} query
	 * @param {object} options
	 * @returns {object}
	 */
	Sifter.prototype.search = function(query, options) {
		var self = this, value, score, search, calculateScore;
		var fn_sort;
		var fn_score;

		search  = this.prepareSearch(query, options);
		options = search.options;
		query   = search.query;

		// generate result scoring function
		fn_score = options.score || self.getScoreFunction(search);

		// perform search and sort
		if (query.length) {
			self.iterator(self.items, function(item, id) {
				score = fn_score(item);
				if (options.filter === false || score > 0) {
					search.items.push({'score': score, 'id': id});
				}
			});
		} else {
			self.iterator(self.items, function(item, id) {
				search.items.push({'score': 1, 'id': id});
			});
		}

		fn_sort = self.getSortFunction(search, options);
		if (fn_sort) search.items.sort(fn_sort);

		// apply limits
		search.total = search.items.length;
		if (typeof options.limit === 'number') {
			search.items = search.items.slice(0, options.limit);
		}

		return search;
	};

	// utilities
	// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

	var cmp = function(a, b) {
		if (typeof a === 'number' && typeof b === 'number') {
			return a > b ? 1 : (a < b ? -1 : 0);
		}
		a = String(a || '').toLowerCase();
		b = String(b || '').toLowerCase();
		if (a > b) return 1;
		if (b > a) return -1;
		return 0;
	};

	var extend = function(a, b) {
		var i, n, k, object;
		for (i = 1, n = arguments.length; i < n; i++) {
			object = arguments[i];
			if (!object) continue;
			for (k in object) {
				if (object.hasOwnProperty(k)) {
					a[k] = object[k];
				}
			}
		}
		return a;
	};

	var trim = function(str) {
		return (str + '').replace(/^\s+|\s+$|/g, '');
	};

	var escape_regex = function(str) {
		return (str + '').replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1');
	};

	var is_array = Array.isArray || ($ && $.isArray) || function(object) {
		return Object.prototype.toString.call(object) === '[object Array]';
	};

	var DIACRITICS = {
		'a': '[aÀÁÂÃÄÅàáâãäå]',
		'c': '[cÇçćĆčČ]',
		'd': '[dđĐďĎ]',
		'e': '[eÈÉÊËèéêëěĚ]',
		'i': '[iÌÍÎÏìíîï]',
		'n': '[nÑñňŇ]',
		'o': '[oÒÓÔÕÕÖØòóôõöø]',
		'r': '[rřŘ]',
		's': '[sŠš]',
		't': '[tťŤ]',
		'u': '[uÙÚÛÜùúûüůŮ]',
		'y': '[yŸÿýÝ]',
		'z': '[zŽž]'
	};

	// export
	// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

	return Sifter;
}));



/**
 * microplugin.js
 * Copyright (c) 2013 Brian Reavis & contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not use this
 * file except in compliance with the License. You may obtain a copy of the License at:
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 *
 * @author Brian Reavis <brian@thirdroute.com>
 */

(function(root, factory) {
	if (typeof define === 'function' && define.amd) {
		define('microplugin', factory);
	} else if (typeof exports === 'object') {
		module.exports = factory();
	} else {
		root.MicroPlugin = factory();
	}
}(this, function() {
	var MicroPlugin = {};

	MicroPlugin.mixin = function(Interface) {
		Interface.plugins = {};

		/**
		 * Initializes the listed plugins (with options).
		 * Acceptable formats:
		 *
		 * List (without options):
		 *   ['a', 'b', 'c']
		 *
		 * List (with options):
		 *   [{'name': 'a', options: {}}, {'name': 'b', options: {}}]
		 *
		 * Hash (with options):
		 *   {'a': { ... }, 'b': { ... }, 'c': { ... }}
		 *
		 * @param {mixed} plugins
		 */
		Interface.prototype.initializePlugins = function(plugins) {
			var i, n, key;
			var self  = this;
			var queue = [];

			self.plugins = {
				names     : [],
				settings  : {},
				requested : {},
				loaded    : {}
			};

			if (utils.isArray(plugins)) {
				for (i = 0, n = plugins.length; i < n; i++) {
					if (typeof plugins[i] === 'string') {
						queue.push(plugins[i]);
					} else {
						self.plugins.settings[plugins[i].name] = plugins[i].options;
						queue.push(plugins[i].name);
					}
				}
			} else if (plugins) {
				for (key in plugins) {
					if (plugins.hasOwnProperty(key)) {
						self.plugins.settings[key] = plugins[key];
						queue.push(key);
					}
				}
			}

			while (queue.length) {
				self.require(queue.shift());
			}
		};

		Interface.prototype.loadPlugin = function(name) {
			var self    = this;
			var plugins = self.plugins;
			var plugin  = Interface.plugins[name];

			if (!Interface.plugins.hasOwnProperty(name)) {
				throw new Error('Unable to find "' +  name + '" plugin');
			}

			plugins.requested[name] = true;
			plugins.loaded[name] = plugin.fn.apply(self, [self.plugins.settings[name] || {}]);
			plugins.names.push(name);
		};

		/**
		 * Initializes a plugin.
		 *
		 * @param {string} name
		 */
		Interface.prototype.require = function(name) {
			var self = this;
			var plugins = self.plugins;

			if (!self.plugins.loaded.hasOwnProperty(name)) {
				if (plugins.requested[name]) {
					throw new Error('Plugin has circular dependency ("' + name + '")');
				}
				self.loadPlugin(name);
			}

			return plugins.loaded[name];
		};

		/**
		 * Registers a plugin.
		 *
		 * @param {string} name
		 * @param {function} fn
		 */
		Interface.define = function(name, fn) {
			Interface.plugins[name] = {
				'name' : name,
				'fn'   : fn
			};
		};
	};

	var utils = {
		isArray: Array.isArray || function(vArg) {
			return Object.prototype.toString.call(vArg) === '[object Array]';
		}
	};

	return MicroPlugin;
}));

/**
 * selectize.js (v0.10.1)
 * Copyright (c) 2013 Brian Reavis & contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not use this
 * file except in compliance with the License. You may obtain a copy of the License at:
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 *
 * @author Brian Reavis <brian@thirdroute.com>
 */

/*jshint curly:false */
/*jshint browser:true */

(function(root, factory) {
	if (typeof define === 'function' && define.amd) {
		define('selectize', ['jquery','sifter','microplugin'], factory);
	} else if (typeof exports === 'object') {
		module.exports = factory(require('jquery'), require('sifter'), require('microplugin'));
	} else {
		root.Selectize = factory(root.jQuery, root.Sifter, root.MicroPlugin);
	}
}(this, function($, Sifter, MicroPlugin) {
	'use strict';

	var highlight = function($element, pattern) {
		if (typeof pattern === 'string' && !pattern.length) return;
		var regex = (typeof pattern === 'string') ? new RegExp(pattern, 'i') : pattern;

		var highlight = function(node) {
			var skip = 0;
			if (node.nodeType === 3) {
				var pos = node.data.search(regex);
				if (pos >= 0 && node.data.length > 0) {
					var match = node.data.match(regex);
					var spannode = document.createElement('span');
					spannode.className = 'highlight';
					var middlebit = node.splitText(pos);
					var endbit = middlebit.splitText(match[0].length);
					var middleclone = middlebit.cloneNode(true);
					spannode.appendChild(middleclone);
					middlebit.parentNode.replaceChild(spannode, middlebit);
					skip = 1;
				}
			} else if (node.nodeType === 1 && node.childNodes && !/(script|style)/i.test(node.tagName)) {
				for (var i = 0; i < node.childNodes.length; ++i) {
					i += highlight(node.childNodes[i]);
				}
			}
			return skip;
		};

		return $element.each(function() {
			highlight(this);
		});
	};

	var MicroEvent = function() {};
	MicroEvent.prototype = {
		on: function(event, fct){
			this._events = this._events || {};
			this._events[event] = this._events[event] || [];
			this._events[event].push(fct);
		},
		off: function(event, fct){
			var n = arguments.length;
			if (n === 0) return delete this._events;
			if (n === 1) return delete this._events[event];

			this._events = this._events || {};
			if (event in this._events === false) return;
			this._events[event].splice(this._events[event].indexOf(fct), 1);
		},
		trigger: function(event /* , args... */){
			this._events = this._events || {};
			if (event in this._events === false) return;
			for (var i = 0; i < this._events[event].length; i++){
				this._events[event][i].apply(this, Array.prototype.slice.call(arguments, 1));
			}
		}
	};

	/**
	 * Mixin will delegate all MicroEvent.js function in the destination object.
	 *
	 * - MicroEvent.mixin(Foobar) will make Foobar able to use MicroEvent
	 *
	 * @param {object} the object which will support MicroEvent
	 */
	MicroEvent.mixin = function(destObject){
		var props = ['on', 'off', 'trigger'];
		for (var i = 0; i < props.length; i++){
			destObject.prototype[props[i]] = MicroEvent.prototype[props[i]];
		}
	};

	var IS_MAC        = /Mac/.test(navigator.userAgent);

	var KEY_A         = 65;
	var KEY_COMMA     = 188;
	var KEY_RETURN    = 13;
	var KEY_ESC       = 27;
	var KEY_LEFT      = 37;
	var KEY_UP        = 38;
	var KEY_P         = 80;
	var KEY_RIGHT     = 39;
	var KEY_DOWN      = 40;
	var KEY_N         = 78;
	var KEY_BACKSPACE = 8;
	var KEY_DELETE    = 46;
	var KEY_SHIFT     = 16;
	var KEY_CMD       = IS_MAC ? 91 : 17;
	var KEY_CTRL      = IS_MAC ? 18 : 17;
	var KEY_TAB       = 9;

	var TAG_SELECT    = 1;
	var TAG_INPUT     = 2;


	var isset = function(object) {
		return typeof object !== 'undefined';
	};

	/**
	 * Converts a scalar to its best string representation
	 * for hash keys and HTML attribute values.
	 *
	 * Transformations:
	 *   'str'     -> 'str'
	 *   null      -> ''
	 *   undefined -> ''
	 *   true      -> '1'
	 *   false     -> '0'
	 *   0         -> '0'
	 *   1         -> '1'
	 *
	 * @param {string} value
	 * @returns {string}
	 */
	var hash_key = function(value) {
		if (typeof value === 'undefined' || value === null) return '';
		if (typeof value === 'boolean') return value ? '1' : '0';
		return value + '';
	};

	/**
	 * Escapes a string for use within HTML.
	 *
	 * @param {string} str
	 * @returns {string}
	 */
	var escape_html = function(str) {
		return (str + '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	};

	/**
	 * Escapes "$" characters in replacement strings.
	 *
	 * @param {string} str
	 * @returns {string}
	 */
	var escape_replace = function(str) {
		return (str + '').replace(/\$/g, '$$$$');
	};

	var hook = {};

	/**
	 * Wraps `method` on `self` so that `fn`
	 * is invoked before the original method.
	 *
	 * @param {object} self
	 * @param {string} method
	 * @param {function} fn
	 */
	hook.before = function(self, method, fn) {
		var original = self[method];
		self[method] = function() {
			fn.apply(self, arguments);
			return original.apply(self, arguments);
		};
	};

	/**
	 * Wraps `method` on `self` so that `fn`
	 * is invoked after the original method.
	 *
	 * @param {object} self
	 * @param {string} method
	 * @param {function} fn
	 */
	hook.after = function(self, method, fn) {
		var original = self[method];
		self[method] = function() {
			var result = original.apply(self, arguments);
			fn.apply(self, arguments);
			return result;
		};
	};

	/**
	 * Builds a hash table out of an array of
	 * objects, using the specified `key` within
	 * each object.
	 *
	 * @param {string} key
	 * @param {mixed} objects
	 */
	var build_hash_table = function(key, objects) {
		if (!$.isArray(objects)) return objects;
		var i, n, table = {};
		for (i = 0, n = objects.length; i < n; i++) {
			if (objects[i].hasOwnProperty(key)) {
				table[objects[i][key]] = objects[i];
			}
		}
		return table;
	};

	/**
	 * Wraps `fn` so that it can only be invoked once.
	 *
	 * @param {function} fn
	 * @returns {function}
	 */
	var once = function(fn) {
		var called = false;
		return function() {
			if (called) return;
			called = true;
			fn.apply(this, arguments);
		};
	};

	/**
	 * Wraps `fn` so that it can only be called once
	 * every `delay` milliseconds (invoked on the falling edge).
	 *
	 * @param {function} fn
	 * @param {int} delay
	 * @returns {function}
	 */
	var debounce = function(fn, delay) {
		var timeout;
		return function() {
			var self = this;
			var args = arguments;
			window.clearTimeout(timeout);
			timeout = window.setTimeout(function() {
				fn.apply(self, args);
			}, delay);
		};
	};

	/**
	 * Debounce all fired events types listed in `types`
	 * while executing the provided `fn`.
	 *
	 * @param {object} self
	 * @param {array} types
	 * @param {function} fn
	 */
	var debounce_events = function(self, types, fn) {
		var type;
		var trigger = self.trigger;
		var event_args = {};

		// override trigger method
		self.trigger = function() {
			var type = arguments[0];
			if (types.indexOf(type) !== -1) {
				event_args[type] = arguments;
			} else {
				return trigger.apply(self, arguments);
			}
		};

		// invoke provided function
		fn.apply(self, []);
		self.trigger = trigger;

		// trigger queued events
		for (type in event_args) {
			if (event_args.hasOwnProperty(type)) {
				trigger.apply(self, event_args[type]);
			}
		}
	};

	/**
	 * A workaround for http://bugs.jquery.com/ticket/6696
	 *
	 * @param {object} $parent - Parent element to listen on.
	 * @param {string} event - Event name.
	 * @param {string} selector - Descendant selector to filter by.
	 * @param {function} fn - Event handler.
	 */
	var watchChildEvent = function($parent, event, selector, fn) {
		$parent.on(event, selector, function(e) {
			var child = e.target;
			while (child && child.parentNode !== $parent[0]) {
				child = child.parentNode;
			}
			e.currentTarget = child;
			return fn.apply(this, [e]);
		});
	};

	/**
	 * Determines the current selection within a text input control.
	 * Returns an object containing:
	 *   - start
	 *   - length
	 *
	 * @param {object} input
	 * @returns {object}
	 */
	var getSelection = function(input) {
		var result = {};
		if ('selectionStart' in input) {
			result.start = input.selectionStart;
			result.length = input.selectionEnd - result.start;
		} else if (document.selection) {
			input.focus();
			var sel = document.selection.createRange();
			var selLen = document.selection.createRange().text.length;
			sel.moveStart('character', -input.value.length);
			result.start = sel.text.length - selLen;
			result.length = selLen;
		}
		return result;
	};

	/**
	 * Copies CSS properties from one element to another.
	 *
	 * @param {object} $from
	 * @param {object} $to
	 * @param {array} properties
	 */
	var transferStyles = function($from, $to, properties) {
		var i, n, styles = {};
		if (properties) {
			for (i = 0, n = properties.length; i < n; i++) {
				styles[properties[i]] = $from.css(properties[i]);
			}
		} else {
			styles = $from.css();
		}
		$to.css(styles);
	};

	/**
	 * Measures the width of a string within a
	 * parent element (in pixels).
	 *
	 * @param {string} str
	 * @param {object} $parent
	 * @returns {int}
	 */
	var measureString = function(str, $parent) {
		if (!str) {
			return 0;
		}

		var $test = $('<test>').css({
			position: 'absolute',
			top: -99999,
			left: -99999,
			width: 'auto',
			padding: 0,
			whiteSpace: 'pre'
		}).text(str).appendTo('body');

		transferStyles($parent, $test, [
			'letterSpacing',
			'fontSize',
			'fontFamily',
			'fontWeight',
			'textTransform'
		]);

		var width = $test.width();
		$test.remove();

		return width;
	};

	/**
	 * Sets up an input to grow horizontally as the user
	 * types. If the value is changed manually, you can
	 * trigger the "update" handler to resize:
	 *
	 * $input.trigger('update');
	 *
	 * @param {object} $input
	 */
	var autoGrow = function($input) {
		var currentWidth = null;

		var update = function(e, options) {
			var value, keyCode, printable, placeholder, width;
			var shift, character, selection;
			e = e || window.event || {};
			options = options || {};

			if (e.metaKey || e.altKey) return;
			if (!options.force && $input.data('grow') === false) return;

			value = $input.val();
			if (e.type && e.type.toLowerCase() === 'keydown') {
				keyCode = e.keyCode;
				printable = (
					(keyCode >= 97 && keyCode <= 122) || // a-z
					(keyCode >= 65 && keyCode <= 90)  || // A-Z
					(keyCode >= 48 && keyCode <= 57)  || // 0-9
					keyCode === 32 // space
				);

				if (keyCode === KEY_DELETE || keyCode === KEY_BACKSPACE) {
					selection = getSelection($input[0]);
					if (selection.length) {
						value = value.substring(0, selection.start) + value.substring(selection.start + selection.length);
					} else if (keyCode === KEY_BACKSPACE && selection.start) {
						value = value.substring(0, selection.start - 1) + value.substring(selection.start + 1);
					} else if (keyCode === KEY_DELETE && typeof selection.start !== 'undefined') {
						value = value.substring(0, selection.start) + value.substring(selection.start + 1);
					}
				} else if (printable) {
					shift = e.shiftKey;
					character = String.fromCharCode(e.keyCode);
					if (shift) character = character.toUpperCase();
					else character = character.toLowerCase();
					value += character;
				}
			}

			placeholder = $input.attr('placeholder');
			if (!value && placeholder) {
				value = placeholder;
			}

			width = measureString(value, $input) + 4;
			if (width !== currentWidth) {
				currentWidth = width;
				$input.width(width);
				$input.triggerHandler('resize');
			}
		};

		$input.on('keydown keyup update blur', update);
		update();
	};

	var Selectize = function($input, settings) {
		var key, i, n, dir, input, self = this;
		input = $input[0];
		input.selectize = self;

		// detect rtl environment
		dir = window.getComputedStyle ? window.getComputedStyle(input, null).getPropertyValue('direction') : input.currentStyle && input.currentStyle.direction;
		dir = dir || $input.parents('[dir]:first').attr('dir') || '';

		// setup default state
		$.extend(self, {
			settings         : settings,
			$input           : $input,
			tagType          : input.tagName.toLowerCase() === 'select' ? TAG_SELECT : TAG_INPUT,
			rtl              : /rtl/i.test(dir),

			eventNS          : '.selectize' + (++Selectize.count),
			highlightedValue : null,
			isOpen           : false,
			isDisabled       : false,
			isRequired       : $input.is('[required]'),
			isInvalid        : false,
			isLocked         : false,
			isFocused        : false,
			isInputHidden    : false,
			isSetup          : false,
			isShiftDown      : false,
			isCmdDown        : false,
			isCtrlDown       : false,
			ignoreFocus      : false,
			ignoreBlur       : false,
			ignoreHover      : false,
			hasOptions       : false,
			currentResults   : null,
			lastValue        : '',
			caretPos         : 0,
			loading          : 0,
			loadedSearches   : {},

			$activeOption    : null,
			$activeItems     : [],

			optgroups        : {},
			options          : {},
			userOptions      : {},
			items            : [],
			renderCache      : {},
			onSearchChange   : settings.loadThrottle === null ? self.onSearchChange : debounce(self.onSearchChange, settings.loadThrottle)
		});

		// search system
		self.sifter = new Sifter(this.options, {diacritics: settings.diacritics});

		// build options table
		$.extend(self.options, build_hash_table(settings.valueField, settings.options));
		delete self.settings.options;

		// build optgroup table
		$.extend(self.optgroups, build_hash_table(settings.optgroupValueField, settings.optgroups));
		delete self.settings.optgroups;

		// option-dependent defaults
		self.settings.mode = self.settings.mode || (self.settings.maxItems === 1 ? 'single' : 'multi');
		if (typeof self.settings.hideSelected !== 'boolean') {
			self.settings.hideSelected = self.settings.mode === 'multi';
		}

		if (self.settings.create) {
			self.canCreate = function(input) {
				var filter = self.settings.createFilter;
				return input.length
					&& (typeof filter !== 'function' || filter.apply(self, [input]))
					&& (typeof filter !== 'string' || new RegExp(filter).test(input))
					&& (!(filter instanceof RegExp) || filter.test(input));
			};
		}

		self.initializePlugins(self.settings.plugins);
		self.setupCallbacks();
		self.setupTemplates();
		self.setup();
	};

	// mixins
	// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

	MicroEvent.mixin(Selectize);
	MicroPlugin.mixin(Selectize);

	// methods
	// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

	$.extend(Selectize.prototype, {

		/**
		 * Creates all elements and sets up event bindings.
		 */
		setup: function() {
			var self      = this;
			var settings  = self.settings;
			var eventNS   = self.eventNS;
			var $window   = $(window);
			var $document = $(document);
			var $input    = self.$input;

			var $wrapper;
			var $control;
			var $control_input;
			var $dropdown;
			var $dropdown_content;
			var $dropdown_parent;
			var inputMode;
			var timeout_blur;
			var timeout_focus;
			var tab_index;
			var classes;
			var classes_plugins;

			inputMode         = self.settings.mode;
			tab_index         = $input.attr('tabindex') || '';
			classes           = $input.attr('class') || '';

			$wrapper          = $('<div>').addClass(settings.wrapperClass).addClass(classes).addClass(inputMode);
			$control          = $('<div>').addClass(settings.inputClass).addClass('items').appendTo($wrapper);
			$control_input    = $('<input type="text" autocomplete="off" />').appendTo($control).attr('tabindex', tab_index).attr('readonly', settings.selectOnly);
			$dropdown_parent  = $(settings.dropdownParent || $wrapper);
			$dropdown         = $('<div>').addClass(settings.dropdownClass).addClass(classes).addClass(inputMode).hide().appendTo($dropdown_parent);
			$dropdown_content = $('<div>').addClass(settings.dropdownContentClass).appendTo($dropdown);

			$wrapper.css({
				width: $input[0].style.width
			});

			if (self.plugins.names.length) {
				classes_plugins = 'plugin-' + self.plugins.names.join(' plugin-');
				$wrapper.addClass(classes_plugins);
				$dropdown.addClass(classes_plugins);
			}

			if ((settings.maxItems === null || settings.maxItems > 1) && self.tagType === TAG_SELECT) {
				$input.attr('multiple', 'multiple');
			}

			if (self.settings.placeholder) {
				$control_input.attr('placeholder', settings.placeholder);
			}

			if ($input.attr('autocorrect')) {
				$control_input.attr('autocorrect', $input.attr('autocorrect'));
			}

			if ($input.attr('autocapitalize')) {
				$control_input.attr('autocapitalize', $input.attr('autocapitalize'));
			}

			self.$wrapper          = $wrapper;
			self.$control          = $control;
			self.$control_input    = $control_input;
			self.$dropdown         = $dropdown;
			self.$dropdown_content = $dropdown_content;

			$dropdown.on('mouseenter', '[data-selectable]', function() { return self.onOptionHover.apply(self, arguments); });
			$dropdown.on('mousedown', '[data-selectable]', function() { return self.onOptionSelect.apply(self, arguments); });
			watchChildEvent($control, 'mousedown', '*:not(input)', function() { return self.onItemSelect.apply(self, arguments); });
			autoGrow($control_input);

			$control.on({
				mousedown : function() { return self.onMouseDown.apply(self, arguments); },
				click     : function() { return self.onClick.apply(self, arguments); }
			});

			$control_input.on({
				mousedown : function(e) { e.stopPropagation(); },
				keydown   : function() { return self.onKeyDown.apply(self, arguments); },
				keyup     : function() { return self.onKeyUp.apply(self, arguments); },
				keypress  : function() { return self.onKeyPress.apply(self, arguments); },
				resize    : function() { self.positionDropdown.apply(self, []); },
				blur      : function() { return self.onBlur.apply(self, arguments); },
				focus     : function() { self.ignoreBlur = false; return self.onFocus.apply(self, arguments); },
				paste     : function() { return self.onPaste.apply(self, arguments); }
			});

			$document.on('keydown' + eventNS, function(e) {
				self.isCmdDown = e[IS_MAC ? 'metaKey' : 'ctrlKey'];
				self.isCtrlDown = e[IS_MAC ? 'altKey' : 'ctrlKey'];
				self.isShiftDown = e.shiftKey;
			});

			$document.on('keyup' + eventNS, function(e) {
				if (e.keyCode === KEY_CTRL) self.isCtrlDown = false;
				if (e.keyCode === KEY_SHIFT) self.isShiftDown = false;
				if (e.keyCode === KEY_CMD) self.isCmdDown = false;
			});

			$document.on('mousedown' + eventNS, function(e) {
				if (self.isFocused) {
					// prevent events on the dropdown scrollbar from causing the control to blur
					if (e.target === self.$dropdown[0] || e.target.parentNode === self.$dropdown[0]) {
						return false;
					}
					// blur on click outside
					if (!self.$control.has(e.target).length && e.target !== self.$control[0]) {
						self.blur();
					}
				}
			});

			$window.on(['scroll' + eventNS, 'resize' + eventNS].join(' '), function() {
				if (self.isOpen) {
					self.positionDropdown.apply(self, arguments);
				}
			});
			$window.on('mousemove' + eventNS, function() {
				self.ignoreHover = false;
			});

			// store original children and tab index so that they can be
			// restored when the destroy() method is called.
			this.revertSettings = {
				$children : $input.children().detach(),
				tabindex  : $input.attr('tabindex')
			};

			$input.attr('tabindex', -1);//.hide().after(self.$wrapper);//dont hide that's dumb
            $input.after(self.$wrapper);//dont hide that's dumb
            $input.prependTo($wrapper);


			if ($.isArray(settings.items)) {
				self.setValue(settings.items);
				delete settings.items;
			}

			// feature detect for the validation API
			if ($input[0].validity) {
                /*
				$input.on('invalid' + eventNS, function(e) {
					e.preventDefault();
					self.isInvalid = true;
					self.refreshState();
				});
                */
			}

			self.updateOriginalInput();
			self.refreshItems();
			self.refreshState();
			self.updatePlaceholder();
			self.isSetup = true;

			if ($input.is(':disabled')) {
				self.disable();
			}

			self.on('change', this.onChange);

			$input.data('selectize', self);
			$input.addClass('selectized');
			self.trigger('initialize');

			// preload options
			if (settings.preload === true) {
				self.onSearchChange('');
			}

		},

		/**
		 * Sets up default rendering functions.
		 */
		setupTemplates: function() {
			var self = this;
			var field_label = self.settings.labelField;
			var field_optgroup = self.settings.optgroupLabelField;

			var templates = {
				'optgroup': function(data) {
					return '<div class="optgroup">' + data.html + '</div>';
				},
				'optgroup_header': function(data, escape) {
					return '<div class="optgroup-header">' + escape(data[field_optgroup]) + '</div>';
				},
				'option': function(data, escape) {
					return '<div class="option">' + escape(data[field_label]) + '</div>';
				},
				'item': function(data, escape) {
					return '<div class="item">' + escape(data[field_label]) + '</div>';
				},
				'option_create': function(data, escape) {
					return '<div class="create">Add <strong>' + escape(data.input) + '</strong>&hellip;</div>';
				}
			};

			self.settings.render = $.extend({}, templates, self.settings.render);
		},

		/**
		 * Maps fired events to callbacks provided
		 * in the settings used when creating the control.
		 */
		setupCallbacks: function() {
			var key, fn, callbacks = {
				'initialize'     : 'onInitialize',
				'change'         : 'onChange',
				'item_add'       : 'onItemAdd',
				'item_remove'    : 'onItemRemove',
				'clear'          : 'onClear',
				'option_add'     : 'onOptionAdd',
				'option_remove'  : 'onOptionRemove',
				'option_clear'   : 'onOptionClear',
				'dropdown_open'  : 'onDropdownOpen',
				'dropdown_close' : 'onDropdownClose',
				'type'           : 'onType'
			};

			for (key in callbacks) {
				if (callbacks.hasOwnProperty(key)) {
					fn = this.settings[callbacks[key]];
					if (fn) this.on(key, fn);
				}
			}
		},

		/**
		 * Triggered when the main control element
		 * has a click event.
		 *
		 * @param {object} e
		 * @return {boolean}
		 */
		onClick: function(e) {
			var self = this;

			// necessary for mobile webkit devices (manual focus triggering
			// is ignored unless invoked within a click event)
			if (!self.isFocused) {
				self.focus();
				e.preventDefault();
			}
		},

		/**
		 * Triggered when the main control element
		 * has a mouse down event.
		 *
		 * @param {object} e
		 * @return {boolean}
		 */
		onMouseDown: function(e) {
			var self = this;
			var defaultPrevented = e.isDefaultPrevented();
			var $target = $(e.target);

			if (self.isFocused) {
				// retain focus by preventing native handling. if the
				// event target is the input it should not be modified.
				// otherwise, text selection within the input won't work.
				if (e.target !== self.$control_input[0]) {
					if (self.settings.mode === 'single') {
						// toggle dropdown
						self.isOpen ? self.close() : self.open();
					} else if (!defaultPrevented) {
						self.setActiveItem(null);
					}
					return false;
				}
			} else {
				// give control focus
				if (!defaultPrevented) {
					window.setTimeout(function() {
						self.focus();
					}, 0);
				}
			}
		},

		/**
		 * Triggered when the value of the control has been changed.
		 * This should propagate the event to the original DOM
		 * input / select element.
		 */
		onChange: function() {
			this.$input.trigger('change');
		},


		/**
		 * Triggered on <input> paste.
		 *
		 * @param {object} e
		 * @returns {boolean}
		 */
		onPaste: function(e) {
			var self = this;
			if (self.isFull() || self.isInputHidden || self.isLocked) {
				e.preventDefault();
			}
		},

		/**
		 * Triggered on <input> keypress.
		 *
		 * @param {object} e
		 * @returns {boolean}
		 */
		onKeyPress: function(e) {
			if (this.isLocked) return e && e.preventDefault();
			var character = String.fromCharCode(e.keyCode || e.which);
			if (this.settings.create && character === this.settings.delimiter) {
				this.createItem();
				e.preventDefault();
				return false;
			}
		},

		/**
		 * Triggered on <input> keydown.
		 *
		 * @param {object} e
		 * @returns {boolean}
		 */
		onKeyDown: function(e) {
			var isInput = e.target === this.$control_input[0];
			var self = this;

			if (self.isLocked) {
				if (e.keyCode !== KEY_TAB) {
					e.preventDefault();
				}
				return;
			}

			switch (e.keyCode) {
				case KEY_A:
					if (self.isCmdDown) {
						self.selectAll();
						return;
					}
					break;
				case KEY_ESC:
					self.close();
					return;
				case KEY_N:
					if (!e.ctrlKey || e.altKey) break;
				case KEY_DOWN:
					if (!self.isOpen && self.hasOptions) {
						self.open();
					} else if (self.$activeOption) {
						self.ignoreHover = true;
						var $next = self.getAdjacentOption(self.$activeOption, 1);
						if ($next.length) self.setActiveOption($next, true, true);
					}
					e.preventDefault();
					return;
				case KEY_P:
					if (!e.ctrlKey || e.altKey) break;
				case KEY_UP:
					if (self.$activeOption) {
						self.ignoreHover = true;
						var $prev = self.getAdjacentOption(self.$activeOption, -1);
						if ($prev.length) self.setActiveOption($prev, true, true);
					}
					e.preventDefault();
					return;
				case KEY_RETURN:
					if (self.isOpen && self.$activeOption) {
						self.onOptionSelect({currentTarget: self.$activeOption});
					}
					e.preventDefault();
					return;
				case KEY_LEFT:
					self.advanceSelection(-1, e);
					return;
				case KEY_RIGHT:
					self.advanceSelection(1, e);
					return;
				case KEY_TAB:
					if (self.settings.selectOnTab && self.isOpen && self.$activeOption) {
						self.onOptionSelect({currentTarget: self.$activeOption});
						e.preventDefault();
					}
					if (self.settings.create && self.createItem()) {
						e.preventDefault();
					}
					return;
				case KEY_BACKSPACE:
				case KEY_DELETE:
					self.deleteSelection(e);
					return;
			}

			if ((self.isFull() || self.isInputHidden) && !(IS_MAC ? e.metaKey : e.ctrlKey)) {
				e.preventDefault();
				return;
			}
		},

		/**
		 * Triggered on <input> keyup.
		 *
		 * @param {object} e
		 * @returns {boolean}
		 */
		onKeyUp: function(e) {
			var self = this;

			if (self.isLocked) return e && e.preventDefault();
			var value = self.$control_input.val() || '';
			if (self.lastValue !== value) {
				self.lastValue = value;
				self.onSearchChange(value);
				self.refreshOptions();
				self.trigger('type', value);
			}
		},

		/**
		 * Invokes the user-provide option provider / loader.
		 *
		 * Note: this function is debounced in the Selectize
		 * constructor (by `settings.loadDelay` milliseconds)
		 *
		 * @param {string} value
		 */
		onSearchChange: function(value) {
			var self = this;
			var fn = self.settings.load;
			if (!fn) return;
			if (self.loadedSearches.hasOwnProperty(value)) return;
			self.loadedSearches[value] = true;
			self.load(function(callback) {
				fn.apply(self, [value, callback]);
			});
		},

		/**
		 * Triggered on <input> focus.
		 *
		 * @param {object} e (optional)
		 * @returns {boolean}
		 */
		onFocus: function(e) {
			var self = this;

			self.isFocused = true;
			if (self.isDisabled) {
				self.blur();
				e && e.preventDefault();
				return false;
			}

			if (self.ignoreFocus) return;
			if (self.settings.preload === 'focus') self.onSearchChange('');

			if (!self.$activeItems.length) {
				self.showInput();
				self.setActiveItem(null);
				self.refreshOptions(!!self.settings.openOnFocus);
			}

			self.refreshState();
		},

		/**
		 * Triggered on <input> blur.
		 *
		 * @param {object} e
		 * @returns {boolean}
		 */
		onBlur: function(e) {
			var self = this;
			self.isFocused = false;
			if (self.ignoreFocus) return;

			// necessary to prevent IE closing the dropdown when the scrollbar is clicked
			if (!self.ignoreBlur && document.activeElement === self.$dropdown_content[0]) {
				self.ignoreBlur = true;
				self.onFocus(e);

				return;
			}

			if (self.settings.create && self.settings.createOnBlur) {
				self.createItem(false);
			}

			self.close();
			self.setTextboxValue('');
			self.setActiveItem(null);
			self.setActiveOption(null);
			self.setCaret(self.items.length);
			self.refreshState();
		},

		/**
		 * Triggered when the user rolls over
		 * an option in the autocomplete dropdown menu.
		 *
		 * @param {object} e
		 * @returns {boolean}
		 */
		onOptionHover: function(e) {
			if (this.ignoreHover) return;
			this.setActiveOption(e.currentTarget, false);
		},

		/**
		 * Triggered when the user clicks on an option
		 * in the autocomplete dropdown menu.
		 *
		 * @param {object} e
		 * @returns {boolean}
		 */
		onOptionSelect: function(e) {
			var value, $target, $option, self = this;

			if (e.preventDefault) {
				e.preventDefault();
				e.stopPropagation();
			}

			$target = $(e.currentTarget);
			if ($target.hasClass('create')) {
				self.createItem();
			} else {
				value = $target.attr('data-value');
				if (value) {
					self.lastQuery = null;
					self.setTextboxValue('');
					self.addItem(value);
					if (!self.settings.hideSelected && e.type && /mouse/.test(e.type)) {
						self.setActiveOption(self.getOption(value));
					}
				}
			}
		},

		/**
		 * Triggered when the user clicks on an item
		 * that has been selected.
		 *
		 * @param {object} e
		 * @returns {boolean}
		 */
		onItemSelect: function(e) {
			var self = this;

			if (self.isLocked) return;
			if (self.settings.mode === 'multi') {
				e.preventDefault();
				self.setActiveItem(e.currentTarget, e);
			}
		},

		/**
		 * Invokes the provided method that provides
		 * results to a callback---which are then added
		 * as options to the control.
		 *
		 * @param {function} fn
		 */
		load: function(fn) {
			var self = this;
			var $wrapper = self.$wrapper.addClass('loading');

			self.loading++;
			fn.apply(self, [function(results) {
				self.loading = Math.max(self.loading - 1, 0);
				if (results && results.length) {
					self.addOption(results);
					self.refreshOptions(self.isFocused && !self.isInputHidden);
				}
				if (!self.loading) {
					$wrapper.removeClass('loading');
				}
				self.trigger('load', results);
			}]);
		},

		/**
		 * Sets the input field of the control to the specified value.
		 *
		 * @param {string} value
		 */
		setTextboxValue: function(value) {
			var $input = this.$control_input;
			var changed = $input.val() !== value;
			if (changed) {
				$input.val(value).triggerHandler('update');
				this.lastValue = value;
			}
		},

		/**
		 * Returns the value of the control. If multiple items
		 * can be selected (e.g. <select multiple>), this returns
		 * an array. If only one item can be selected, this
		 * returns a string.
		 *
		 * @returns {mixed}
		 */
		getValue: function() {
			if (this.tagType === TAG_SELECT && this.$input.attr('multiple')) {
				return this.items;
			} else {
				return this.items.join(this.settings.delimiter);
			}
		},

		/**
		 * Resets the selected items to the given value.
		 *
		 * @param {mixed} value
		 */
		setValue: function(value) {
			debounce_events(this, ['change'], function() {
				this.clear();
				this.addItems(value);
			});
		},

		/**
		 * Sets the selected item.
		 *
		 * @param {object} $item
		 * @param {object} e (optional)
		 */
		setActiveItem: function($item, e) {
			var self = this;
			var eventName;
			var i, idx, begin, end, item, swap;
			var $last;

			if (self.settings.mode === 'single') return;
			$item = $($item);

			// clear the active selection
			if (!$item.length) {
				$(self.$activeItems).removeClass('active');
				self.$activeItems = [];
				if (self.isFocused) {
					self.showInput();
				}
				return;
			}

			// modify selection
			eventName = e && e.type.toLowerCase();

			if (eventName === 'mousedown' && self.isShiftDown && self.$activeItems.length) {
				$last = self.$control.children('.active:last');
				begin = Array.prototype.indexOf.apply(self.$control[0].childNodes, [$last[0]]);
				end   = Array.prototype.indexOf.apply(self.$control[0].childNodes, [$item[0]]);
				if (begin > end) {
					swap  = begin;
					begin = end;
					end   = swap;
				}
				for (i = begin; i <= end; i++) {
					item = self.$control[0].childNodes[i];
					if (self.$activeItems.indexOf(item) === -1) {
						$(item).addClass('active');
						self.$activeItems.push(item);
					}
				}
				e.preventDefault();
			} else if ((eventName === 'mousedown' && self.isCtrlDown) || (eventName === 'keydown' && this.isShiftDown)) {
				if ($item.hasClass('active')) {
					idx = self.$activeItems.indexOf($item[0]);
					self.$activeItems.splice(idx, 1);
					$item.removeClass('active');
				} else {
					self.$activeItems.push($item.addClass('active')[0]);
				}
			} else {
				$(self.$activeItems).removeClass('active');
				self.$activeItems = [$item.addClass('active')[0]];
			}

			// ensure control has focus
			self.hideInput();
			if (!this.isFocused) {
				self.focus();
			}
			if(self.settings.selectOnly){//DREW trying to rid ourselves of the keyboard on mobile
				self.$control_input.blur();//DREW trying to rid ourselves of the keyboard on mobile
				self.blur();//DREW trying to rid ourselves of the keyboard on mobile
			}
		},

		/**
		 * Sets the selected item in the dropdown menu
		 * of available options.
		 *
		 * @param {object} $object
		 * @param {boolean} scroll
		 * @param {boolean} animate
		 */
		setActiveOption: function($option, scroll, animate) {
			var height_menu, height_item, y;
			var scroll_top, scroll_bottom;
			var self = this;

			if (self.$activeOption) self.$activeOption.removeClass('active');
			self.$activeOption = null;

			$option = $($option);
			if (!$option.length) return;

			self.$activeOption = $option.addClass('active');

			if (scroll || !isset(scroll)) {

				height_menu   = self.$dropdown_content.height();
				height_item   = self.$activeOption.outerHeight(true);
				scroll        = self.$dropdown_content.scrollTop() || 0;
				y             = self.$activeOption.offset().top - self.$dropdown_content.offset().top + scroll;
				scroll_top    = y;
				scroll_bottom = y - height_menu + height_item;

				if (y + height_item > height_menu + scroll) {
					self.$dropdown_content.stop().animate({scrollTop: scroll_bottom}, animate ? self.settings.scrollDuration : 0);
				} else if (y < scroll) {
					self.$dropdown_content.stop().animate({scrollTop: scroll_top}, animate ? self.settings.scrollDuration : 0);
				}

			}
		},

		/**
		 * Selects all items (CTRL + A).
		 */
		selectAll: function() {
			var self = this;
			if (self.settings.mode === 'single') return;

			self.$activeItems = Array.prototype.slice.apply(self.$control.children(':not(input)').addClass('active'));
			if (self.$activeItems.length) {
				self.hideInput();
				self.close();
			}
			self.focus();
		},

		/**
		 * Hides the input element out of view, while
		 * retaining its focus.
		 */
		hideInput: function() {
			var self = this;

			self.setTextboxValue('');
			self.$control_input.css({opacity: 0, position: 'absolute', left: self.rtl ? 10000 : -10000});
			self.isInputHidden = true;
		},

		/**
		 * Restores input visibility.
		 */
		showInput: function() {
			this.$control_input.css({opacity: 1, position: 'relative', left: 0});
			this.isInputHidden = false;
		},

		/**
		 * Gives the control focus. If "trigger" is falsy,
		 * focus handlers won't be fired--causing the focus
		 * to happen silently in the background.
		 *
		 * @param {boolean} trigger
		 */
		focus: function() {
			var self = this;
			if (self.isDisabled) return;

			self.ignoreFocus = true;
			self.$control_input[0].focus();
			window.setTimeout(function() {
				self.ignoreFocus = false;
				self.onFocus();
			}, 0);
		},

		/**
		 * Forces the control out of focus.
		 */
		blur: function() {
			this.$control_input.trigger('blur');
		},

		/**
		 * Returns a function that scores an object
		 * to show how good of a match it is to the
		 * provided query.
		 *
		 * @param {string} query
		 * @param {object} options
		 * @return {function}
		 */
		getScoreFunction: function(query) {
			return this.sifter.getScoreFunction(query, this.getSearchOptions());
		},

		/**
		 * Returns search options for sifter (the system
		 * for scoring and sorting results).
		 *
		 * @see https://github.com/brianreavis/sifter.js
		 * @return {object}
		 */
		getSearchOptions: function() {
			var settings = this.settings;
			var sort = settings.sortField;
			if (typeof sort === 'string') {
				sort = {field: sort};
			}

			return {
				fields      : settings.searchField,
				conjunction : settings.searchConjunction,
				sort        : sort
			};
		},

		/**
		 * Searches through available options and returns
		 * a sorted array of matches.
		 *
		 * Returns an object containing:
		 *
		 *   - query {string}
		 *   - tokens {array}
		 *   - total {int}
		 *   - items {array}
		 *
		 * @param {string} query
		 * @returns {object}
		 */
		search: function(query) {
			var i, value, score, result, calculateScore;
			var self     = this;
			var settings = self.settings;
			var options  = this.getSearchOptions();

			// validate user-provided result scoring function
			if (settings.score) {
				calculateScore = self.settings.score.apply(this, [query]);
				if (typeof calculateScore !== 'function') {
					throw new Error('Selectize "score" setting must be a function that returns a function');
				}
			}

			// perform search
			if (query !== self.lastQuery) {
				self.lastQuery = query;
				result = self.sifter.search(query, $.extend(options, {score: calculateScore}));
				self.currentResults = result;
			} else {
				result = $.extend(true, {}, self.currentResults);
			}

			// filter out selected items
			if (settings.hideSelected) {
				for (i = result.items.length - 1; i >= 0; i--) {
					if (self.items.indexOf(hash_key(result.items[i].id)) !== -1) {
						result.items.splice(i, 1);
					}
				}
			}

			return result;
		},

		/**
		 * Refreshes the list of available options shown
		 * in the autocomplete dropdown menu.
		 *
		 * @param {boolean} triggerDropdown
		 */
		refreshOptions: function(triggerDropdown) {
			var i, j, k, n, groups, groups_order, option, option_html, optgroup, optgroups, html, html_children, has_create_option;
			var $active, $active_before, $create;

			if (typeof triggerDropdown === 'undefined') {
				triggerDropdown = true;
			}

			var self              = this;
			var query             = self.$control_input.val();
			var results           = self.search(query);
			var $dropdown_content = self.$dropdown_content;
			var active_before     = self.$activeOption && hash_key(self.$activeOption.attr('data-value'));

			// build markup
			n = results.items.length;
			if (typeof self.settings.maxOptions === 'number') {
				n = Math.min(n, self.settings.maxOptions);
			}

			// render and group available options individually
			groups = {};

			if (self.settings.optgroupOrder) {
				groups_order = self.settings.optgroupOrder;
				for (i = 0; i < groups_order.length; i++) {
					groups[groups_order[i]] = [];
				}
			} else {
				groups_order = [];
			}

			for (i = 0; i < n; i++) {
				option      = self.options[results.items[i].id];
				option_html = self.render('option', option);
				optgroup    = option[self.settings.optgroupField] || '';
				optgroups   = $.isArray(optgroup) ? optgroup : [optgroup];

				for (j = 0, k = optgroups && optgroups.length; j < k; j++) {
					optgroup = optgroups[j];
					if (!self.optgroups.hasOwnProperty(optgroup)) {
						optgroup = '';
					}
					if (!groups.hasOwnProperty(optgroup)) {
						groups[optgroup] = [];
						groups_order.push(optgroup);
					}
					groups[optgroup].push(option_html);
				}
			}

			// render optgroup headers & join groups
			html = [];
			for (i = 0, n = groups_order.length; i < n; i++) {
				optgroup = groups_order[i];
				if (self.optgroups.hasOwnProperty(optgroup) && groups[optgroup].length) {
					// render the optgroup header and options within it,
					// then pass it to the wrapper template
					html_children = self.render('optgroup_header', self.optgroups[optgroup]) || '';
					html_children += groups[optgroup].join('');
					html.push(self.render('optgroup', $.extend({}, self.optgroups[optgroup], {
						html: html_children
					})));
				} else {
					html.push(groups[optgroup].join(''));
				}
			}

			$dropdown_content.html(html.join(''));

			// highlight matching terms inline
			if (self.settings.highlight && results.query.length && results.tokens.length) {
				for (i = 0, n = results.tokens.length; i < n; i++) {
					highlight($dropdown_content, results.tokens[i].regex);
				}
			}

			// add "selected" class to selected options
			if (!self.settings.hideSelected) {
				for (i = 0, n = self.items.length; i < n; i++) {
					self.getOption(self.items[i]).addClass('selected');
				}
			}

			// add create option
			has_create_option = self.settings.create && self.canCreate(results.query);
			if (has_create_option) {
				$dropdown_content.prepend(self.render('option_create', {input: query}));
				$create = $($dropdown_content[0].childNodes[0]);
			}

			// activate
			self.hasOptions = results.items.length > 0 || has_create_option;
			if (self.hasOptions) {
				if (results.items.length > 0) {
					$active_before = active_before && self.getOption(active_before);
					if ($active_before && $active_before.length) {
						$active = $active_before;
					} else if (self.settings.mode === 'single' && self.items.length) {
						$active = self.getOption(self.items[0]);
					}
					if (!$active || !$active.length) {
						if ($create && !self.settings.addPrecedence) {
							$active = self.getAdjacentOption($create, 1);
						} else {
							$active = $dropdown_content.find('[data-selectable]:first');
						}
					}
				} else {
					$active = $create;
				}
				self.setActiveOption($active);
				if (triggerDropdown && !self.isOpen) { self.open(); }
			} else {
				self.setActiveOption(null);
				if (triggerDropdown && self.isOpen) { self.close(); }
			}
		},

		/**
		 * Adds an available option. If it already exists,
		 * nothing will happen. Note: this does not refresh
		 * the options list dropdown (use `refreshOptions`
		 * for that).
		 *
		 * Usage:
		 *
		 *   this.addOption(data)
		 *
		 * @param {object} data
		 */
		addOption: function(data) {
			var i, n, optgroup, value, self = this;

			if ($.isArray(data)) {
				for (i = 0, n = data.length; i < n; i++) {
					self.addOption(data[i]);
				}
				return;
			}

			value = hash_key(data[self.settings.valueField]);
			if (!value || self.options.hasOwnProperty(value)) return;

			self.userOptions[value] = true;
			self.options[value] = data;
			self.lastQuery = null;
			self.trigger('option_add', value, data);
		},

		/**
		 * Registers a new optgroup for options
		 * to be bucketed into.
		 *
		 * @param {string} id
		 * @param {object} data
		 */
		addOptionGroup: function(id, data) {
			this.optgroups[id] = data;
			this.trigger('optgroup_add', id, data);
		},

		/**
		 * Updates an option available for selection. If
		 * it is visible in the selected items or options
		 * dropdown, it will be re-rendered automatically.
		 *
		 * @param {string} value
		 * @param {object} data
		 */
		updateOption: function(value, data) {
			var self = this;
			var $item, $item_new;
			var value_new, index_item, cache_items, cache_options;

			value     = hash_key(value);
			value_new = hash_key(data[self.settings.valueField]);

			// sanity checks
			if (!self.options.hasOwnProperty(value)) return;
			if (!value_new) throw new Error('Value must be set in option data');

			// update references
			if (value_new !== value) {
				delete self.options[value];
				index_item = self.items.indexOf(value);
				if (index_item !== -1) {
					self.items.splice(index_item, 1, value_new);
				}
			}
			self.options[value_new] = data;

			// invalidate render cache
			cache_items = self.renderCache['item'];
			cache_options = self.renderCache['option'];

			if (cache_items) {
				delete cache_items[value];
				delete cache_items[value_new];
			}
			if (cache_options) {
				delete cache_options[value];
				delete cache_options[value_new];
			}

			// update the item if it's selected
			if (self.items.indexOf(value_new) !== -1) {
				$item = self.getItem(value);
				$item_new = $(self.render('item', data));
				if ($item.hasClass('active')) $item_new.addClass('active');
				$item.replaceWith($item_new);
			}

			// update dropdown contents
			if (self.isOpen) {
				self.refreshOptions(false);
			}
		},

		/**
		 * Removes a single option.
		 *
		 * @param {string} value
		 */
		removeOption: function(value) {
			var self = this;
			value = hash_key(value);

			var cache_items = self.renderCache['item'];
			var cache_options = self.renderCache['option'];
			if (cache_items) delete cache_items[value];
			if (cache_options) delete cache_options[value];

			delete self.userOptions[value];
			delete self.options[value];
			self.lastQuery = null;
			self.trigger('option_remove', value);
			self.removeItem(value);
		},

		/**
		 * Clears all options.
		 */
		clearOptions: function() {
			var self = this;

			self.loadedSearches = {};
			self.userOptions = {};
			self.renderCache = {};
			self.options = self.sifter.items = {};
			self.lastQuery = null;
			self.trigger('option_clear');
			self.clear();
		},

		/**
		 * Returns the jQuery element of the option
		 * matching the given value.
		 *
		 * @param {string} value
		 * @returns {object}
		 */
		getOption: function(value) {
			return this.getElementWithValue(value, this.$dropdown_content.find('[data-selectable]'));
		},

		/**
		 * Returns the jQuery element of the next or
		 * previous selectable option.
		 *
		 * @param {object} $option
		 * @param {int} direction  can be 1 for next or -1 for previous
		 * @return {object}
		 */
		getAdjacentOption: function($option, direction) {
			var $options = this.$dropdown.find('[data-selectable]');
			var index    = $options.index($option) + direction;

			return index >= 0 && index < $options.length ? $options.eq(index) : $();
		},

		/**
		 * Finds the first element with a "data-value" attribute
		 * that matches the given value.
		 *
		 * @param {mixed} value
		 * @param {object} $els
		 * @return {object}
		 */
		getElementWithValue: function(value, $els) {
			value = hash_key(value);

			if (value) {
				for (var i = 0, n = $els.length; i < n; i++) {
					if ($els[i].getAttribute('data-value') === value) {
						return $($els[i]);
					}
				}
			}

			return $();
		},

		/**
		 * Returns the jQuery element of the item
		 * matching the given value.
		 *
		 * @param {string} value
		 * @returns {object}
		 */
		getItem: function(value) {
			return this.getElementWithValue(value, this.$control.children());
		},

		/**
		 * "Selects" multiple items at once. Adds them to the list
		 * at the current caret position.
		 *
		 * @param {string} value
		 */
		addItems: function(values) {
			var items = $.isArray(values) ? values : [values];
			for (var i = 0, n = items.length; i < n; i++) {
				this.isPending = (i < n - 1);
				this.addItem(items[i]);
			}
		},

		/**
		 * "Selects" an item. Adds it to the list
		 * at the current caret position.
		 *
		 * @param {string} value
		 */
		addItem: function(value) {
			debounce_events(this, ['change'], function() {
				var $item, $option, $options;
				var self = this;
				var inputMode = self.settings.mode;
				var i, active, value_next, wasFull;
				value = hash_key(value);

				if (self.items.indexOf(value) !== -1) {
					if (inputMode === 'single') self.close();
					return;
				}

				if (!self.options.hasOwnProperty(value)) return;
				if (inputMode === 'single') self.clear();
				if (inputMode === 'multi' && self.isFull()) return;

				$item = $(self.render('item', self.options[value]));
				wasFull = self.isFull();
				self.items.splice(self.caretPos, 0, value);
				self.insertAtCaret($item);
				if (!self.isPending || (!wasFull && self.isFull())) {
					self.refreshState();
				}

				if (self.isSetup) {
					$options = self.$dropdown_content.find('[data-selectable]');

					// update menu / remove the option (if this is not one item being added as part of series)
					if (!self.isPending) {
						$option = self.getOption(value);
						value_next = self.getAdjacentOption($option, 1).attr('data-value');
						self.refreshOptions(self.isFocused && inputMode !== 'single');
						if (value_next) {
							self.setActiveOption(self.getOption(value_next));
						}
					}

					// hide the menu if the maximum number of items have been selected or no options are left
					if (!$options.length || self.isFull()) {
						self.close();
					} else {
						self.positionDropdown();
					}

					self.updatePlaceholder();
					self.trigger('item_add', value, $item);
					self.updateOriginalInput();
				}
			});
		},

		/**
		 * Removes the selected item matching
		 * the provided value.
		 *
		 * @param {string} value
		 */
		removeItem: function(value) {
			var self = this;
			var $item, i, idx;

			$item = (typeof value === 'object') ? value : self.getItem(value);
			value = hash_key($item.attr('data-value'));
			i = self.items.indexOf(value);

			if (i !== -1) {
				$item.remove();
				if ($item.hasClass('active')) {
					idx = self.$activeItems.indexOf($item[0]);
					self.$activeItems.splice(idx, 1);
				}

				self.items.splice(i, 1);
				self.lastQuery = null;
				if (!self.settings.persist && self.userOptions.hasOwnProperty(value)) {
					self.removeOption(value);
				}

				if (i < self.caretPos) {
					self.setCaret(self.caretPos - 1);
				}

				self.refreshState();
				self.updatePlaceholder();
				self.updateOriginalInput();
				self.positionDropdown();
				self.trigger('item_remove', value);
			}
		},

		/**
		 * Invokes the `create` method provided in the
		 * selectize options that should provide the data
		 * for the new item, given the user input.
		 *
		 * Once this completes, it will be added
		 * to the item list.
		 *
		 * @return {boolean}
		 */
		createItem: function(triggerDropdown) {
			var self  = this;
			var input = $.trim(self.$control_input.val() || '');
			var caret = self.caretPos;
			if (!self.canCreate(input)) return false;
			self.lock();

			if (typeof triggerDropdown === 'undefined') {
				triggerDropdown = true;
			}

			var setup = (typeof self.settings.create === 'function') ? this.settings.create : function(input) {
				var data = {};
				data[self.settings.labelField] = input;
				data[self.settings.valueField] = input;
				return data;
			};

			var create = once(function(data) {
				self.unlock();

				if (!data || typeof data !== 'object') return;
				var value = hash_key(data[self.settings.valueField]);
				if (!value) return;

				self.setTextboxValue('');
				self.addOption(data);
				self.setCaret(caret);
				self.addItem(value);
				self.refreshOptions(triggerDropdown && self.settings.mode !== 'single');
			});

			var output = setup.apply(this, [input, create]);
			if (typeof output !== 'undefined') {
				create(output);
			}

			return true;
		},

		/**
		 * Re-renders the selected item lists.
		 */
		refreshItems: function() {
			this.lastQuery = null;

			if (this.isSetup) {
				for (var i = 0; i < this.items.length; i++) {
					this.addItem(this.items);
				}
			}

			this.refreshState();
			this.updateOriginalInput();
		},

		/**
		 * Updates all state-dependent attributes
		 * and CSS classes.
		 */
		refreshState: function() {
			var invalid, self = this;
			if (self.isRequired) {
				if (self.items.length) self.isInvalid = false;
				self.$control_input.prop('required', self.isInvalid );
			}
			self.refreshClasses();
		},

		/**
		 * Updates all state-dependent CSS classes.
		 */
		refreshClasses: function() {
			var self     = this;
			var isFull   = self.isFull();
			var isLocked = self.isLocked;

			self.$wrapper
				.toggleClass('rtl', self.rtl);

			self.$control
				.toggleClass('focus', self.isFocused)
				.toggleClass('disabled', self.isDisabled)
				.toggleClass('required', self.isRequired)
				.toggleClass('invalid', self.isInvalid)
				.toggleClass('locked', isLocked)
				.toggleClass('full', isFull).toggleClass('not-full', !isFull)
				.toggleClass('input-active', self.isFocused && !self.isInputHidden)
				.toggleClass('dropdown-active', self.isOpen)
				.toggleClass('has-options', !$.isEmptyObject(self.options))
				.toggleClass('has-items', self.items.length > 0);

			self.$control_input.data('grow', !isFull && !isLocked);
		},

		/**
		 * Determines whether or not more items can be added
		 * to the control without exceeding the user-defined maximum.
		 *
		 * @returns {boolean}
		 */
		isFull: function() {
			return this.settings.maxItems !== null && this.items.length >= this.settings.maxItems;
		},

		/**
		 * Refreshes the original <select> or <input>
		 * element to reflect the current state.
		 */
		updateOriginalInput: function() {
			var i, n, options, self = this;

			if (self.tagType === TAG_SELECT) {
				options = [];
				for (i = 0, n = self.items.length; i < n; i++) {
					options.push('<option value="' + escape_html(self.items[i]) + '" selected="selected"></option>');
				}
				if (!options.length && !this.$input.attr('multiple')) {
					options.push('<option value="" selected="selected"></option>');
				}
				self.$input.html(options.join(''));
			} else {
				self.$input.val(self.getValue());
				self.$input.attr('value',self.$input.val());
			}
			if(self.getValue()!=="" && self.isRequired){
				self.$input.prop('required',false);
			}else if(self.getValue()!=="" && self.isRequired){
				self.$input.prop('required',true);
			}
			if (self.isSetup) {
				self.trigger('change', self.$input.val());
			}
		},

		/**
		 * Shows/hide the input placeholder depending
		 * on if there items in the list already.
		 */
		updatePlaceholder: function() {
			if (!this.settings.placeholder) return;
			var $input = this.$control_input;

			if (this.items.length) {
				$input.removeAttr('placeholder');
			} else {
				$input.attr('placeholder', this.settings.placeholder);
			}
			$input.triggerHandler('update', {force: true});
		},

		/**
		 * Shows the autocomplete dropdown containing
		 * the available options.
		 */
		open: function() {
			var self = this;

			if (self.isLocked || self.isOpen || (self.settings.mode === 'multi' && self.isFull())) return;
			self.focus();
			self.isOpen = true;
			self.refreshState();
			self.$dropdown.css({visibility: 'hidden', display: 'block'}).show();//DREW ADDED OVERRIDE TO SHOW
			self.positionDropdown();
			self.$dropdown.css({visibility: 'visible'});
			self.trigger('dropdown_open', self.$dropdown);
		},

		/**
		 * Closes the autocomplete dropdown menu.
		 */
		close: function() {
			var self = this;
			var trigger = self.isOpen;

			if (self.settings.mode === 'single' && self.items.length) {
				self.hideInput();
			}

			self.isOpen = false;
			self.$dropdown.hide();
			self.setActiveOption(null);
			self.refreshState();

			if (trigger) self.trigger('dropdown_close', self.$dropdown);
		},

		/**
		 * Calculates and applies the appropriate
		 * position of the dropdown.
		 */
		positionDropdown: function() {
			var $control = this.$control;
			var offset = this.settings.dropdownParent === 'body' ? $control.offset() : $control.position();
			offset.top += $control.outerHeight(true);

			this.$dropdown.css({
				width : $control.outerWidth(),
				top   : offset.top,
				left  : offset.left
			});
		},

		/**
		 * Resets / clears all selected items
		 * from the control.
		 */
		clear: function() {
			var self = this;

			if (!self.items.length) return;
			self.$control.children(':not(input)').remove();
			self.items = [];
			self.lastQuery = null;
			self.setCaret(0);
			self.setActiveItem(null);
			self.updatePlaceholder();
			self.updateOriginalInput();
			self.refreshState();
			self.showInput();
			self.trigger('clear');
		},

		/**
		 * A helper method for inserting an element
		 * at the current caret position.
		 *
		 * @param {object} $el
		 */
		insertAtCaret: function($el) {
			var caret = Math.min(this.caretPos, this.items.length);
			if (caret === 0) {
				this.$control.prepend($el);
			} else {
				$(this.$control[0].childNodes[caret]).before($el);
			}
			this.setCaret(caret + 1);
		},

		/**
		 * Removes the current selected item(s).
		 *
		 * @param {object} e (optional)
		 * @returns {boolean}
		 */
		deleteSelection: function(e) {
			var i, n, direction, selection, values, caret, option_select, $option_select, $tail;
			var self = this;

			direction = (e && e.keyCode === KEY_BACKSPACE) ? -1 : 1;
			selection = getSelection(self.$control_input[0]);

			if (self.$activeOption && !self.settings.hideSelected) {
				option_select = self.getAdjacentOption(self.$activeOption, -1).attr('data-value');
			}

			// determine items that will be removed
			values = [];

			if (self.$activeItems.length) {
				$tail = self.$control.children('.active:' + (direction > 0 ? 'last' : 'first'));
				caret = self.$control.children(':not(input)').index($tail);
				if (direction > 0) { caret++; }

				for (i = 0, n = self.$activeItems.length; i < n; i++) {
					values.push($(self.$activeItems[i]).attr('data-value'));
				}
				if (e) {
					e.preventDefault();
					e.stopPropagation();
				}
			} else if ((self.isFocused || self.settings.mode === 'single') && self.items.length) {
				if (direction < 0 && selection.start === 0 && selection.length === 0) {
					values.push(self.items[self.caretPos - 1]);
				} else if (direction > 0 && selection.start === self.$control_input.val().length) {
					values.push(self.items[self.caretPos]);
				}
			}

			// allow the callback to abort
			if (!values.length || (typeof self.settings.onDelete === 'function' && self.settings.onDelete.apply(self, [values]) === false)) {
				return false;
			}

			// perform removal
			if (typeof caret !== 'undefined') {
				self.setCaret(caret);
			}
			while (values.length) {
				self.removeItem(values.pop());
			}

			self.showInput();
			self.positionDropdown();
			self.refreshOptions(true);

			// select previous option
			if (option_select) {
				$option_select = self.getOption(option_select);
				if ($option_select.length) {
					self.setActiveOption($option_select);
				}
			}

			return true;
		},

		/**
		 * Selects the previous / next item (depending
		 * on the `direction` argument).
		 *
		 * > 0 - right
		 * < 0 - left
		 *
		 * @param {int} direction
		 * @param {object} e (optional)
		 */
		advanceSelection: function(direction, e) {
			var tail, selection, idx, valueLength, cursorAtEdge, $tail;
			var self = this;

			if (direction === 0) return;
			if (self.rtl) direction *= -1;

			tail = direction > 0 ? 'last' : 'first';
			selection = getSelection(self.$control_input[0]);

			if (self.isFocused && !self.isInputHidden) {
				valueLength = self.$control_input.val().length;
				cursorAtEdge = direction < 0
					? selection.start === 0 && selection.length === 0
					: selection.start === valueLength;

				if (cursorAtEdge && !valueLength) {
					self.advanceCaret(direction, e);
				}
			} else {
				$tail = self.$control.children('.active:' + tail);
				if ($tail.length) {
					idx = self.$control.children(':not(input)').index($tail);
					self.setActiveItem(null);
					self.setCaret(direction > 0 ? idx + 1 : idx);
				}
			}
		},

		/**
		 * Moves the caret left / right.
		 *
		 * @param {int} direction
		 * @param {object} e (optional)
		 */
		advanceCaret: function(direction, e) {
			var self = this, fn, $adj;

			if (direction === 0) return;

			fn = direction > 0 ? 'next' : 'prev';
			if (self.isShiftDown) {
				$adj = self.$control_input[fn]();
				if ($adj.length) {
					self.hideInput();
					self.setActiveItem($adj);
					e && e.preventDefault();
				}
			} else {
				self.setCaret(self.caretPos + direction);
			}
		},

		/**
		 * Moves the caret to the specified index.
		 *
		 * @param {int} i
		 */
		setCaret: function(i) {
			var self = this;

			if (self.settings.mode === 'single') {
				i = self.items.length;
			} else {
				i = Math.max(0, Math.min(self.items.length, i));
			}

			if(!self.isPending) {
				// the input must be moved by leaving it in place and moving the
				// siblings, due to the fact that focus cannot be restored once lost
				// on mobile webkit devices
				var j, n, fn, $children, $child;
				$children = self.$control.children(':not(input)');
				for (j = 0, n = $children.length; j < n; j++) {
					$child = $($children[j]).detach();
					if (j <  i) {
						self.$control_input.before($child);
					} else {
						self.$control.append($child);
					}
				}
			}

			self.caretPos = i;
		},

		/**
		 * Disables user input on the control. Used while
		 * items are being asynchronously created.
		 */
		lock: function() {
			this.close();
			this.isLocked = true;
			this.refreshState();
		},

		/**
		 * Re-enables user input on the control.
		 */
		unlock: function() {
			this.isLocked = false;
			this.refreshState();
		},

		/**
		 * Disables user input on the control completely.
		 * While disabled, it cannot receive focus.
		 */
		disable: function() {
			var self = this;
			self.$input.prop('disabled', true);
			self.isDisabled = true;
			self.lock();
		},

		/**
		 * Enables the control so that it can respond
		 * to focus and user input.
		 */
		enable: function() {
			var self = this;
			self.$input.prop('disabled', false);
			self.isDisabled = false;
			self.unlock();
		},

		/**
		 * Completely destroys the control and
		 * unbinds all event listeners so that it can
		 * be garbage collected.
		 */
		destroy: function() {
			var self = this;
			var eventNS = self.eventNS;
			var revertSettings = self.revertSettings;

			self.trigger('destroy');
			self.off();
            self.$input.detach().after(self.$wrapper);
			self.$wrapper.remove();
			self.$dropdown.remove();

			self.$input
				.html('')
				.append(revertSettings.$children)
				.removeAttr('tabindex')
				.removeClass('selectized')
				.attr({tabindex: revertSettings.tabindex})
				.show();

			self.$control_input.removeData('grow');
			self.$input.removeData('selectize');

			$(window).off(eventNS);
			$(document).off(eventNS);
			$(document.body).off(eventNS);

			delete self.$input[0].selectize;
		},

		/**
		 * A helper method for rendering "item" and
		 * "option" templates, given the data.
		 *
		 * @param {string} templateName
		 * @param {object} data
		 * @returns {string}
		 */
		render: function(templateName, data) {
			var value, id, label;
			var html = '';
			var cache = false;
			var self = this;
			var regex_tag = /^[\t ]*<([a-z][a-z0-9\-_]*(?:\:[a-z][a-z0-9\-_]*)?)/i;

			if (templateName === 'option' || templateName === 'item') {
				value = hash_key(data[self.settings.valueField]);
				cache = !!value;
			}

			// pull markup from cache if it exists
			if (cache) {
				if (!isset(self.renderCache[templateName])) {
					self.renderCache[templateName] = {};
				}
				if (self.renderCache[templateName].hasOwnProperty(value)) {
					return self.renderCache[templateName][value];
				}
			}

			// render markup
			html = self.settings.render[templateName].apply(this, [data, escape_html]);

			// add mandatory attributes
			if (templateName === 'option' || templateName === 'option_create') {
				html = html.replace(regex_tag, '<$1 data-selectable');
			}
			if (templateName === 'optgroup') {
				id = data[self.settings.optgroupValueField] || '';
				html = html.replace(regex_tag, '<$1 data-group="' + escape_replace(escape_html(id)) + '"');
			}
			if (templateName === 'option' || templateName === 'item') {
				html = html.replace(regex_tag, '<$1 data-value="' + escape_replace(escape_html(value || '')) + '"');
			}

			// update cache
			if (cache) {
				self.renderCache[templateName][value] = html;
			}

			return html;
		},

		/**
		 * Clears the render cache for a template. If
		 * no template is given, clears all render
		 * caches.
		 *
		 * @param {string} templateName
		 */
		clearCache: function(templateName) {
			var self = this;
			if (typeof templateName === 'undefined') {
				self.renderCache = {};
			} else {
				delete self.renderCache[templateName];
			}
		}


	});


	Selectize.count = 0;
	Selectize.defaults = {
		plugins: [],
		delimiter: ',',
		persist: true,
		diacritics: true,
		create: false,
		createOnBlur: false,
		createFilter: null,
		highlight: true,
		openOnFocus: true,
		maxOptions: 1000,
		maxItems: null,
		hideSelected: null,
		addPrecedence: false,
		selectOnTab: false,
		preload: false,

		scrollDuration: 60,
		loadThrottle: 300,

		dataAttr: 'data-data',
		optgroupField: 'optgroup',
		valueField: 'value',
		labelField: 'text',
		optgroupLabelField: 'label',
		optgroupValueField: 'value',
		optgroupOrder: null,

		sortField: '$order',
		searchField: ['text'],
		searchConjunction: 'and',

		mode: null,
		selectOnly: true,
		wrapperClass: 'selectize-control',
		inputClass: 'selectize-input',
		dropdownClass: 'selectize-dropdown',
		dropdownContentClass: 'selectize-dropdown-content',

		dropdownParent: null,

		/*
		load            : null, // function(query, callback) { ... }
		score           : null, // function(search) { ... }
		onInitialize    : null, // function() { ... }
		onChange        : null, // function(value) { ... }
		onItemAdd       : null, // function(value, $item) { ... }
		onItemRemove    : null, // function(value) { ... }
		onClear         : null, // function() { ... }
		onOptionAdd     : null, // function(value, data) { ... }
		onOptionRemove  : null, // function(value) { ... }
		onOptionClear   : null, // function() { ... }
		onDropdownOpen  : null, // function($dropdown) { ... }
		onDropdownClose : null, // function($dropdown) { ... }
		onType          : null, // function(str) { ... }
		onDelete        : null, // function(values) { ... }
		*/

		render: {
			/*
			item: null,
			optgroup: null,
			optgroup_header: null,
			option: null,
			option_create: null
			*/
		}
	};

	$.fn.selectize = function(settings_user) {
		var defaults             = $.fn.selectize.defaults;
		var settings             = $.extend({}, defaults, settings_user);
		var attr_data            = settings.dataAttr;
		var field_label          = settings.labelField;
		var field_value          = settings.valueField;
		var field_optgroup       = settings.optgroupField;
		var field_optgroup_label = settings.optgroupLabelField;
		var field_optgroup_value = settings.optgroupValueField;

		/**
		 * Initializes selectize from a <input type="text"> element.
		 *
		 * @param {object} $input
		 * @param {object} settings_element
		 */
		var init_textbox = function($input, settings_element) {
			var i, n, values, option, value = $.trim($input.val() || '');
			if (!value.length) return;

			values = value.split(settings.delimiter);
			for (i = 0, n = values.length; i < n; i++) {
				option = {};
				option[field_label] = values[i];
				option[field_value] = values[i];

				settings_element.options[values[i]] = option;
			}

			settings_element.items = values;
		};

		/**
		 * Initializes selectize from a <select> element.
		 *
		 * @param {object} $input
		 * @param {object} settings_element
		 */
		var init_select = function($input, settings_element) {
			var i, n, tagName, $children, order = 0;
			var options = settings_element.options;

			var readData = function($el) {
				var data = attr_data && $el.attr(attr_data);
				if (typeof data === 'string' && data.length) {
					return JSON.parse(data);
				}
				return null;
			};

			var addOption = function($option, group) {
				var value, option;

				$option = $($option);

				value = $option.attr('value') || '';
				if (!value.length) return;

				// if the option already exists, it's probably been
				// duplicated in another optgroup. in this case, push
				// the current group to the "optgroup" property on the
				// existing option so that it's rendered in both places.
				if (options.hasOwnProperty(value)) {
					if (group) {
						if (!options[value].optgroup) {
							options[value].optgroup = group;
						} else if (!$.isArray(options[value].optgroup)) {
							options[value].optgroup = [options[value].optgroup, group];
						} else {
							options[value].optgroup.push(group);
						}
					}
					return;
				}

				option                 = readData($option) || {};
				option[field_label]    = option[field_label] || $option.text();
				option[field_value]    = option[field_value] || value;
				option[field_optgroup] = option[field_optgroup] || group;

				option.$order = ++order;
				options[value] = option;

				if ($option.is(':selected')) {
					settings_element.items.push(value);
				}
			};

			var addGroup = function($optgroup) {
				var i, n, id, optgroup, $options;

				$optgroup = $($optgroup);
				id = $optgroup.attr('label');

				if (id) {
					optgroup = readData($optgroup) || {};
					optgroup[field_optgroup_label] = id;
					optgroup[field_optgroup_value] = id;
					settings_element.optgroups[id] = optgroup;
				}

				$options = $('option', $optgroup);
				for (i = 0, n = $options.length; i < n; i++) {
					addOption($options[i], id);
				}
			};

			settings_element.maxItems = $input.attr('multiple') ? null : 1;

			$children = $input.children();
			for (i = 0, n = $children.length; i < n; i++) {
				tagName = $children[i].tagName.toLowerCase();
				if (tagName === 'optgroup') {
					addGroup($children[i]);
				} else if (tagName === 'option') {
					addOption($children[i]);
				}
			}
		};

		return this.each(function() {
			if (this.selectize) return;

			var instance;
			var $input = $(this);
			var tag_name = this.tagName.toLowerCase();
			var settings_element = {
				'placeholder' : $input.children('option[value=""]').text() || $input.attr('placeholder'),
				'options'     : {},
				'optgroups'   : {},
				'items'       : []
			};

			if (tag_name === 'select') {
				init_select($input, settings_element);
			} else {
				init_textbox($input, settings_element);
			}

			instance = new Selectize($input, $.extend(true, {}, defaults, settings_element, settings_user));
		});
	};

	$.fn.selectize.defaults = Selectize.defaults;


	Selectize.define('drag_drop', function(options) {
		if (!$.fn.sortable) throw new Error('The "drag_drop" plugin requires jQuery UI "sortable".');
		if (this.settings.mode !== 'multi') return;
		var self = this;

		self.lock = (function() {
			var original = self.lock;
			return function() {
				var sortable = self.$control.data('sortable');
				if (sortable) sortable.disable();
				return original.apply(self, arguments);
			};
		})();

		self.unlock = (function() {
			var original = self.unlock;
			return function() {
				var sortable = self.$control.data('sortable');
				if (sortable) sortable.enable();
				return original.apply(self, arguments);
			};
		})();

		self.setup = (function() {
			var original = self.setup;
			return function() {
				original.apply(this, arguments);

				var $control = self.$control.sortable({
					items: '[data-value]',
					forcePlaceholderSize: true,
					disabled: self.isLocked,
					start: function(e, ui) {
						ui.placeholder.css('width', ui.helper.css('width'));
						$control.css({overflow: 'visible'});
					},
					stop: function() {
						$control.css({overflow: 'hidden'});
						var active = self.$activeItems ? self.$activeItems.slice() : null;
						var values = [];
						$control.children('[data-value]').each(function() {
							values.push($(this).attr('data-value'));
						});
						self.setValue(values);
						self.setActiveItem(active);
					}
				});
			};
		})();

	});

	Selectize.define('dropdown_header', function(options) {
		var self = this;

		options = $.extend({
			title         : 'Untitled',
			headerClass   : 'selectize-dropdown-header',
			titleRowClass : 'selectize-dropdown-header-title',
			labelClass    : 'selectize-dropdown-header-label',
			closeClass    : 'selectize-dropdown-header-close',

			html: function(data) {
				return (
					'<div class="' + data.headerClass + '">' +
						'<div class="' + data.titleRowClass + '">' +
							'<span class="' + data.labelClass + '">' + data.title + '</span>' +
							'<a href="javascript:void(0)" class="' + data.closeClass + '">&times;</a>' +
						'</div>' +
					'</div>'
				);
			}
		}, options);

		self.setup = (function() {
			var original = self.setup;
			return function() {
				original.apply(self, arguments);
				self.$dropdown_header = $(options.html(options));
				self.$dropdown.prepend(self.$dropdown_header);
			};
		})();

	});

	Selectize.define('optgroup_columns', function(options) {
		var self = this;

		options = $.extend({
			equalizeWidth  : true,
			equalizeHeight : true
		}, options);

		this.getAdjacentOption = function($option, direction) {
			var $options = $option.closest('[data-group]').find('[data-selectable]');
			var index    = $options.index($option) + direction;

			return index >= 0 && index < $options.length ? $options.eq(index) : $();
		};

		this.onKeyDown = (function() {
			var original = self.onKeyDown;
			return function(e) {
				var index, $option, $options, $optgroup;

				if (this.isOpen && (e.keyCode === KEY_LEFT || e.keyCode === KEY_RIGHT)) {
					self.ignoreHover = true;
					$optgroup = this.$activeOption.closest('[data-group]');
					index = $optgroup.find('[data-selectable]').index(this.$activeOption);

					if(e.keyCode === KEY_LEFT) {
						$optgroup = $optgroup.prev('[data-group]');
					} else {
						$optgroup = $optgroup.next('[data-group]');
					}

					$options = $optgroup.find('[data-selectable]');
					$option  = $options.eq(Math.min($options.length - 1, index));
					if ($option.length) {
						this.setActiveOption($option);
					}
					return;
				}

				return original.apply(this, arguments);
			};
		})();

		var getScrollbarWidth = function() {
			var div;
			var width = getScrollbarWidth.width;
			var doc = document;

			if (typeof width === 'undefined') {
				div = doc.createElement('div');
				div.innerHTML = '<div style="width:50px;height:50px;position:absolute;left:-50px;top:-50px;overflow:auto;"><div style="width:1px;height:100px;"></div></div>';
				div = div.firstChild;
				doc.body.appendChild(div);
				width = getScrollbarWidth.width = div.offsetWidth - div.clientWidth;
				doc.body.removeChild(div);
			}
			return width;
		};

		var equalizeSizes = function() {
			var i, n, height_max, width, width_last, width_parent, $optgroups;

			$optgroups = $('[data-group]', self.$dropdown_content);
			n = $optgroups.length;
			if (!n || !self.$dropdown_content.width()) return;

			if (options.equalizeHeight) {
				height_max = 0;
				for (i = 0; i < n; i++) {
					height_max = Math.max(height_max, $optgroups.eq(i).height());
				}
				$optgroups.css({height: height_max});
			}

			if (options.equalizeWidth) {
				width_parent = self.$dropdown_content.innerWidth() - getScrollbarWidth();
				width = Math.round(width_parent / n);
				$optgroups.css({width: width});
				if (n > 1) {
					width_last = width_parent - width * (n - 1);
					$optgroups.eq(n - 1).css({width: width_last});
				}
			}
		};

		if (options.equalizeHeight || options.equalizeWidth) {
			hook.after(this, 'positionDropdown', equalizeSizes);
			hook.after(this, 'refreshOptions', equalizeSizes);
		}


	});

	Selectize.define('remove_button', function(options) {
		if (this.settings.mode === 'single') return;

		options = $.extend({
			label     : '&times;',
			title     : 'Remove',
			className : 'remove',
			append    : true
		}, options);

		var self = this;
		var html = '<a href="javascript:void(0)" class="' + options.className + '" tabindex="-1" title="' + escape_html(options.title) + '">' + options.label + '</a>';

		/**
		 * Appends an element as a child (with raw HTML).
		 *
		 * @param {string} html_container
		 * @param {string} html_element
		 * @return {string}
		 */
		var append = function(html_container, html_element) {
			var pos = html_container.search(/(<\/[^>]+>\s*)$/);
			return html_container.substring(0, pos) + html_element + html_container.substring(pos);
		};

		this.setup = (function() {
			var original = self.setup;
			return function() {
				// override the item rendering method to add the button to each
				if (options.append) {
					var render_item = self.settings.render.item;
					self.settings.render.item = function(data) {
						return append(render_item.apply(this, arguments), html);
					};
				}

				original.apply(this, arguments);

				// add event listener
				this.$control.on('click', '.' + options.className, function(e) {
					e.preventDefault();
					if (self.isLocked) return;

					var $item = $(e.currentTarget).parent();
					self.setActiveItem($item);
					if (self.deleteSelection()) {
						self.setCaret(self.items.length);
					}
				});

			};
		})();

	});

	Selectize.define('restore_on_backspace', function(options) {
		var self = this;

		options.text = options.text || function(option) {
			return option[this.settings.labelField];
		};

		this.onKeyDown = (function(e) {
			var original = self.onKeyDown;
			return function(e) {
				var index, option;
				if (e.keyCode === KEY_BACKSPACE && this.$control_input.val() === '' && !this.$activeItems.length) {
					index = this.caretPos - 1;
					if (index >= 0 && index < this.items.length) {
						option = this.options[this.items[index]];
						if (this.deleteSelection(e)) {
							this.setTextboxValue(options.text.apply(this, [option]));
							this.refreshOptions(true);
						}
						e.preventDefault();
						return;
					}
				}
				return original.apply(this, arguments);
			};
		})();
	});

	return Selectize;
}));;var Swiper = function (selector, params) {
    'use strict';

    /*=========================
      A little bit dirty but required part for IE8 and old FF support
      ===========================*/
    if (!document.body.outerHTML && document.body.__defineGetter__) {
        if (HTMLElement) {
            var element = HTMLElement.prototype;
            if (element.__defineGetter__) {
                element.__defineGetter__('outerHTML', function () { return new XMLSerializer().serializeToString(this); });
            }
        }
    }

    if (!window.getComputedStyle) {
        window.getComputedStyle = function (el, pseudo) {
            this.el = el;
            this.getPropertyValue = function (prop) {
                var re = /(\-([a-z]){1})/g;
                if (prop === 'float') prop = 'styleFloat';
                if (re.test(prop)) {
                    prop = prop.replace(re, function () {
                        return arguments[2].toUpperCase();
                    });
                }
                return el.currentStyle[prop] ? el.currentStyle[prop] : null;
            };
            return this;
        };
    }
    if (!Array.prototype.indexOf) {
        Array.prototype.indexOf = function (obj, start) {
            for (var i = (start || 0), j = this.length; i < j; i++) {
                if (this[i] === obj) { return i; }
            }
            return -1;
        };
    }
    if (!document.querySelectorAll) {
        if (!window.jQuery) return;
    }
    function $$(selector, context) {
        if (document.querySelectorAll)
            return (context || document).querySelectorAll(selector);
        else
            return jQuery(selector, context);
    }

    /*=========================
      Check for correct selector
      ===========================*/
    if (typeof selector === 'undefined') return;

    if (!(selector.nodeType)) {
        if ($$(selector).length === 0) return;
    }

     /*=========================
      _this
      ===========================*/
    var _this = this;

     /*=========================
      Default Flags and vars
      ===========================*/
    _this.touches = {
        start: 0,
        startX: 0,
        startY: 0,
        current: 0,
        currentX: 0,
        currentY: 0,
        diff: 0,
        abs: 0
    };
    _this.positions = {
        start: 0,
        abs: 0,
        diff: 0,
        current: 0
    };
    _this.times = {
        start: 0,
        end: 0
    };

    _this.id = (new Date()).getTime();
    _this.container = (selector.nodeType) ? selector : $$(selector)[0];
    _this.isTouched = false;
    _this.isMoved = false;
    _this.activeIndex = 0;
    _this.centerIndex = 0;
    _this.activeLoaderIndex = 0;
    _this.activeLoopIndex = 0;
    _this.previousIndex = null;
    _this.velocity = 0;
    _this.snapGrid = [];
    _this.slidesGrid = [];
    _this.imagesToLoad = [];
    _this.imagesLoaded = 0;
    _this.wrapperLeft = 0;
    _this.wrapperRight = 0;
    _this.wrapperTop = 0;
    _this.wrapperBottom = 0;
    _this.isAndroid = navigator.userAgent.toLowerCase().indexOf('android') >= 0;
    var wrapper, slideSize, wrapperSize, direction, isScrolling, containerSize;

    /*=========================
      Default Parameters
      ===========================*/
    var defaults = {
        eventTarget: 'wrapper', // or 'container'
        mode : 'horizontal', // or 'vertical'
        touchRatio : 1,
        speed : 300,
        freeMode : false,
        freeModeFluid : false,
        momentumRatio: 1,
        momentumBounce: true,
        momentumBounceRatio: 1,
        slidesPerView : 1,
        slidesPerGroup : 1,
        slidesPerViewFit: true, //Fit to slide when spv "auto" and slides larger than container
        simulateTouch : true,
        followFinger : true,
        shortSwipes : true,
        longSwipesRatio: 0.5,
        moveStartThreshold: false,
        onlyExternal : false,
        createPagination : true,
        pagination : false,
        paginationElement: 'span',
        paginationClickable: false,
        paginationAsRange: true,
        resistance : true, // or false or 100%
        scrollContainer : false,
        preventLinks : true,
        preventLinksPropagation: false,
        noSwiping : false, // or class
        noSwipingClass : 'swiper-no-swiping', //:)
        initialSlide: 0,
        keyboardControl: false,
        mousewheelControl : false,
        mousewheelControlForceToAxis : false,
        useCSS3Transforms : true,
        // Autoplay
        autoplay: false,
        autoplayDisableOnInteraction: true,
        autoplayStopOnLast: false,
        //Loop mode
        loop: false,
        loopAdditionalSlides: 0,
        // Round length values
        roundLengths: false,
        //Auto Height
        calculateHeight: false,
        //Apply CSS for width and/or height
        cssWidthAndHeight: false, // or true or 'width' or 'height'
        //Images Preloader
        updateOnImagesReady : true,
        //Form elements
        releaseFormElements : true,
        //Watch for active slide, useful when use effects on different slide states
        watchActiveIndex: false,
        //Slides Visibility Fit
        visibilityFullFit : false,
        //Slides Offset
        offsetPxBefore : 0,
        offsetPxAfter : 0,
        offsetSlidesBefore : 0,
        offsetSlidesAfter : 0,
        centeredSlides: false,
        //Queue callbacks
        queueStartCallbacks : false,
        queueEndCallbacks : false,
        //Auto Resize
        autoResize : true,
        resizeReInit : false,
        //DOMAnimation
        DOMAnimation : true,
        //Slides Loader
        loader: {
            slides: [], //array with slides
            slidesHTMLType: 'inner', // or 'outer'
            surroundGroups: 1, //keep preloaded slides groups around view
            logic: 'reload', //or 'change'
            loadAllSlides: false
        },
        // One way swipes
        swipeToPrev: true,
        swipeToNext: true,
        //Namespace
        slideElement: 'div',
        slideClass: 'swiper-slide',
        slideActiveClass: 'swiper-slide-active',
        slideVisibleClass: 'swiper-slide-visible',
        slideDuplicateClass: 'swiper-slide-duplicate',
        wrapperClass: 'swiper-wrapper',
        paginationElementClass: 'swiper-pagination-switch',
        paginationActiveClass: 'swiper-active-switch',
        paginationVisibleClass: 'swiper-visible-switch'
    };
    params = params || {};
    for (var prop in defaults) {
        if (prop in params && typeof params[prop] === 'object') {
            for (var subProp in defaults[prop]) {
                if (! (subProp in params[prop])) {
                    params[prop][subProp] = defaults[prop][subProp];
                }
            }
        }
        else if (! (prop in params)) {
            params[prop] = defaults[prop];
        }
    }
    _this.params = params;
    if (params.scrollContainer) {
        params.freeMode = true;
        params.freeModeFluid = true;
    }
    if (params.loop) {
        params.resistance = '100%';
    }
    var isH = params.mode === 'horizontal';

    /*=========================
      Define Touch Events
      ===========================*/
    var desktopEvents = ['mousedown', 'mousemove', 'mouseup'];
    if (_this.browser.ie10) desktopEvents = ['MSPointerDown', 'MSPointerMove', 'MSPointerUp'];
    if (_this.browser.ie11) desktopEvents = ['pointerdown', 'pointermove', 'pointerup'];

    _this.touchEvents = {
        touchStart : _this.support.touch || !params.simulateTouch  ? 'touchstart' : desktopEvents[0],
        touchMove : _this.support.touch || !params.simulateTouch ? 'touchmove' : desktopEvents[1],
        touchEnd : _this.support.touch || !params.simulateTouch ? 'touchend' : desktopEvents[2]
    };

    /*=========================
      Wrapper
      ===========================*/
    for (var i = _this.container.childNodes.length - 1; i >= 0; i--) {
        if (_this.container.childNodes[i].className) {
            var _wrapperClasses = _this.container.childNodes[i].className.split(/\s+/);
            for (var j = 0; j < _wrapperClasses.length; j++) {
                if (_wrapperClasses[j] === params.wrapperClass) {
                    wrapper = _this.container.childNodes[i];
                }
            }
        }
    }

    _this.wrapper = wrapper;
    /*=========================
      Slide API
      ===========================*/
    _this._extendSwiperSlide = function  (el) {
        el.append = function () {
            if (params.loop) {
                el.insertAfter(_this.slides.length - _this.loopedSlides);
            }
            else {
                _this.wrapper.appendChild(el);
                _this.reInit();
            }

            return el;
        };
        el.prepend = function () {
            if (params.loop) {
                _this.wrapper.insertBefore(el, _this.slides[_this.loopedSlides]);
                _this.removeLoopedSlides();
                _this.calcSlides();
                _this.createLoop();
            }
            else {
                _this.wrapper.insertBefore(el, _this.wrapper.firstChild);
            }
            _this.reInit();
            return el;
        };
        el.insertAfter = function (index) {
            if (typeof index === 'undefined') return false;
            var beforeSlide;

            if (params.loop) {
                beforeSlide = _this.slides[index + 1 + _this.loopedSlides];
                if (beforeSlide) {
                    _this.wrapper.insertBefore(el, beforeSlide);
                }
                else {
                    _this.wrapper.appendChild(el);
                }
                _this.removeLoopedSlides();
                _this.calcSlides();
                _this.createLoop();
            }
            else {
                beforeSlide = _this.slides[index + 1];
                _this.wrapper.insertBefore(el, beforeSlide);
            }
            _this.reInit();
            return el;
        };
        el.clone = function () {
            return _this._extendSwiperSlide(el.cloneNode(true));
        };
        el.remove = function () {
            _this.wrapper.removeChild(el);
            _this.reInit();
        };
        el.html = function (html) {
            if (typeof html === 'undefined') {
                return el.innerHTML;
            }
            else {
                el.innerHTML = html;
                return el;
            }
        };
        el.index = function () {
            var index;
            for (var i = _this.slides.length - 1; i >= 0; i--) {
                if (el === _this.slides[i]) index = i;
            }
            return index;
        };
        el.isActive = function () {
            if (el.index() === _this.activeIndex) return true;
            else return false;
        };
        if (!el.swiperSlideDataStorage) el.swiperSlideDataStorage = {};
        el.getData = function (name) {
            return el.swiperSlideDataStorage[name];
        };
        el.setData = function (name, value) {
            el.swiperSlideDataStorage[name] = value;
            return el;
        };
        el.data = function (name, value) {
            if (typeof value === 'undefined') {
                return el.getAttribute('data-' + name);
            }
            else {
                el.setAttribute('data-' + name, value);
                return el;
            }
        };
        el.getWidth = function (outer, round) {
            return _this.h.getWidth(el, outer, round);
        };
        el.getHeight = function (outer, round) {
            return _this.h.getHeight(el, outer, round);
        };
        el.getOffset = function () {
            return _this.h.getOffset(el);
        };
        return el;
    };

    //Calculate information about number of slides
    _this.calcSlides = function (forceCalcSlides) {
        var oldNumber = _this.slides ? _this.slides.length : false;
        _this.slides = [];
        _this.displaySlides = [];
        for (var i = 0; i < _this.wrapper.childNodes.length; i++) {
            if (_this.wrapper.childNodes[i].className) {
                var _className = _this.wrapper.childNodes[i].className;
                var _slideClasses = _className.split(/\s+/);
                for (var j = 0; j < _slideClasses.length; j++) {
                    if (_slideClasses[j] === params.slideClass) {
                        _this.slides.push(_this.wrapper.childNodes[i]);
                    }
                }
            }
        }
        for (i = _this.slides.length - 1; i >= 0; i--) {
            _this._extendSwiperSlide(_this.slides[i]);
        }
        if (oldNumber === false) return;
        if (oldNumber !== _this.slides.length || forceCalcSlides) {

            // Number of slides has been changed
            removeSlideEvents();
            addSlideEvents();
            _this.updateActiveSlide();
            if (_this.params.pagination) _this.createPagination();
            _this.callPlugins('numberOfSlidesChanged');
        }
    };

    //Create Slide
    _this.createSlide = function (html, slideClassList, el) {
        slideClassList = slideClassList || _this.params.slideClass;
        el = el || params.slideElement;
        var newSlide = document.createElement(el);
        newSlide.innerHTML = html || '';
        newSlide.className = slideClassList;
        return _this._extendSwiperSlide(newSlide);
    };

    //Append Slide
    _this.appendSlide = function (html, slideClassList, el) {
        if (!html) return;
        if (html.nodeType) {
            return _this._extendSwiperSlide(html).append();
        }
        else {
            return _this.createSlide(html, slideClassList, el).append();
        }
    };
    _this.prependSlide = function (html, slideClassList, el) {
        if (!html) return;
        if (html.nodeType) {
            return _this._extendSwiperSlide(html).prepend();
        }
        else {
            return _this.createSlide(html, slideClassList, el).prepend();
        }
    };
    _this.insertSlideAfter = function (index, html, slideClassList, el) {
        if (typeof index === 'undefined') return false;
        if (html.nodeType) {
            return _this._extendSwiperSlide(html).insertAfter(index);
        }
        else {
            return _this.createSlide(html, slideClassList, el).insertAfter(index);
        }
    };
    _this.removeSlide = function (index) {
        if (_this.slides[index]) {
            if (params.loop) {
                if (!_this.slides[index + _this.loopedSlides]) return false;
                _this.slides[index + _this.loopedSlides].remove();
                _this.removeLoopedSlides();
                _this.calcSlides();
                _this.createLoop();
            }
            else _this.slides[index].remove();
            return true;
        }
        else return false;
    };
    _this.removeLastSlide = function () {
        if (_this.slides.length > 0) {
            if (params.loop) {
                _this.slides[_this.slides.length - 1 - _this.loopedSlides].remove();
                _this.removeLoopedSlides();
                _this.calcSlides();
                _this.createLoop();
            }
            else _this.slides[_this.slides.length - 1].remove();
            return true;
        }
        else {
            return false;
        }
    };
    _this.removeAllSlides = function () {
        var num = _this.slides.length;
        for (var i = _this.slides.length - 1; i >= 0; i--) {
            _this.slides[i].remove();
            if (i === num - 1) {
                _this.setWrapperTranslate(0);
            }
        }
    };
    _this.getSlide = function (index) {
        return _this.slides[index];
    };
    _this.getLastSlide = function () {
        return _this.slides[_this.slides.length - 1];
    };
    _this.getFirstSlide = function () {
        return _this.slides[0];
    };

    //Currently Active Slide
    _this.activeSlide = function () {
        return _this.slides[_this.activeIndex];
    };

    /*=========================
     Wrapper for Callbacks : Allows additive callbacks via function arrays
     ===========================*/
    _this.fireCallback = function () {
        var callback = arguments[0];
        if (Object.prototype.toString.call(callback) === '[object Array]') {
            for (var i = 0; i < callback.length; i++) {
                if (typeof callback[i] === 'function') {
                    callback[i](arguments[1], arguments[2], arguments[3], arguments[4], arguments[5]);
                }
            }
        } else if (Object.prototype.toString.call(callback) === '[object String]') {
            if (params['on' + callback]) _this.fireCallback(params['on' + callback], arguments[1], arguments[2], arguments[3], arguments[4], arguments[5]);
        } else {
            callback(arguments[1], arguments[2], arguments[3], arguments[4], arguments[5]);
        }
    };
    function isArray(obj) {
        if (Object.prototype.toString.apply(obj) === '[object Array]') return true;
        return false;
    }

    /**
     * Allows user to add callbacks, rather than replace them
     * @param callback
     * @param func
     * @return {*}
     */
    _this.addCallback = function (callback, func) {
        var _this = this, tempFunc;
        if (_this.params['on' + callback]) {
            if (isArray(this.params['on' + callback])) {
                return this.params['on' + callback].push(func);
            } else if (typeof this.params['on' + callback] === 'function') {
                tempFunc = this.params['on' + callback];
                this.params['on' + callback] = [];
                this.params['on' + callback].push(tempFunc);
                return this.params['on' + callback].push(func);
            }
        } else {
            this.params['on' + callback] = [];
            return this.params['on' + callback].push(func);
        }
    };
    _this.removeCallbacks = function (callback) {
        if (_this.params['on' + callback]) {
            _this.params['on' + callback] = null;
        }
    };

    /*=========================
      Plugins API
      ===========================*/
    var _plugins = [];
    for (var plugin in _this.plugins) {
        if (params[plugin]) {
            var p = _this.plugins[plugin](_this, params[plugin]);
            if (p) _plugins.push(p);
        }
    }
    _this.callPlugins = function (method, args) {
        if (!args) args = {};
        for (var i = 0; i < _plugins.length; i++) {
            if (method in _plugins[i]) {
                _plugins[i][method](args);
            }
        }
    };

    /*=========================
      Windows Phone 8 Fix
      ===========================*/
    if ((_this.browser.ie10 || _this.browser.ie11) && !params.onlyExternal) {
        _this.wrapper.classList.add('swiper-wp8-' + (isH ? 'horizontal' : 'vertical'));
    }

    /*=========================
      Free Mode Class
      ===========================*/
    if (params.freeMode) {
        _this.container.className += ' swiper-free-mode';
    }

    /*==================================================
        Init/Re-init/Resize Fix
    ====================================================*/
    _this.initialized = false;
    _this.init = function (force, forceCalcSlides) {
        var _width = _this.h.getWidth(_this.container, false, params.roundLengths);
        var _height = _this.h.getHeight(_this.container, false, params.roundLengths);
        if (_width === _this.width && _height === _this.height && !force) return;

        _this.width = _width;
        _this.height = _height;

        var slideWidth, slideHeight, slideMaxHeight, wrapperWidth, wrapperHeight, slideLeft;
        var i; // loop index variable to avoid JSHint W004 / W038
        containerSize = isH ? _width : _height;
        var wrapper = _this.wrapper;

        if (force) {
            _this.calcSlides(forceCalcSlides);
        }

        if (params.slidesPerView === 'auto') {
            //Auto mode
            var slidesWidth = 0;
            var slidesHeight = 0;

            //Unset Styles
            if (params.slidesOffset > 0) {
                wrapper.style.paddingLeft = '';
                wrapper.style.paddingRight = '';
                wrapper.style.paddingTop = '';
                wrapper.style.paddingBottom = '';
            }
            wrapper.style.width = '';
            wrapper.style.height = '';
            if (params.offsetPxBefore > 0) {
                if (isH) _this.wrapperLeft = params.offsetPxBefore;
                else _this.wrapperTop = params.offsetPxBefore;
            }
            if (params.offsetPxAfter > 0) {
                if (isH) _this.wrapperRight = params.offsetPxAfter;
                else _this.wrapperBottom = params.offsetPxAfter;
            }

            if (params.centeredSlides) {
                if (isH) {
                    _this.wrapperLeft = (containerSize - this.slides[0].getWidth(true, params.roundLengths)) / 2;
                    _this.wrapperRight = (containerSize - _this.slides[_this.slides.length - 1].getWidth(true, params.roundLengths)) / 2;
                }
                else {
                    _this.wrapperTop = (containerSize - _this.slides[0].getHeight(true, params.roundLengths)) / 2;
                    _this.wrapperBottom = (containerSize - _this.slides[_this.slides.length - 1].getHeight(true, params.roundLengths)) / 2;
                }
            }

            if (isH) {
                if (_this.wrapperLeft >= 0) wrapper.style.paddingLeft = _this.wrapperLeft + 'px';
                if (_this.wrapperRight >= 0) wrapper.style.paddingRight = _this.wrapperRight + 'px';
            }
            else {
                if (_this.wrapperTop >= 0) wrapper.style.paddingTop = _this.wrapperTop + 'px';
                if (_this.wrapperBottom >= 0) wrapper.style.paddingBottom = _this.wrapperBottom + 'px';
            }
            slideLeft = 0;
            var centeredSlideLeft = 0;
            _this.snapGrid = [];
            _this.slidesGrid = [];

            slideMaxHeight = 0;
            for (i = 0; i < _this.slides.length; i++) {
                slideWidth = _this.slides[i].getWidth(true, params.roundLengths);
                slideHeight = _this.slides[i].getHeight(true, params.roundLengths);
                if (params.calculateHeight) {
                    slideMaxHeight = Math.max(slideMaxHeight, slideHeight);
                }
                var _slideSize = isH ? slideWidth : slideHeight;
                if (params.centeredSlides) {
                    var nextSlideWidth = i === _this.slides.length - 1 ? 0 : _this.slides[i + 1].getWidth(true, params.roundLengths);
                    var nextSlideHeight = i === _this.slides.length - 1 ? 0 : _this.slides[i + 1].getHeight(true, params.roundLengths);
                    var nextSlideSize = isH ? nextSlideWidth : nextSlideHeight;
                    if (_slideSize > containerSize) {
                        if (params.slidesPerViewFit) {
                            _this.snapGrid.push(slideLeft + _this.wrapperLeft);
                            _this.snapGrid.push(slideLeft + _slideSize - containerSize + _this.wrapperLeft);
                        }
                        else {
                            for (var j = 0; j <= Math.floor(_slideSize / (containerSize + _this.wrapperLeft)); j++) {
                                if (j === 0) _this.snapGrid.push(slideLeft + _this.wrapperLeft);
                                else _this.snapGrid.push(slideLeft + _this.wrapperLeft + containerSize * j);
                            }
                        }
                        _this.slidesGrid.push(slideLeft + _this.wrapperLeft);
                    }
                    else {
                        _this.snapGrid.push(centeredSlideLeft);
                        _this.slidesGrid.push(centeredSlideLeft);
                    }
                    centeredSlideLeft += _slideSize / 2 + nextSlideSize / 2;
                }
                else {
                    if (_slideSize > containerSize) {
                        if (params.slidesPerViewFit) {
                            _this.snapGrid.push(slideLeft);
                            _this.snapGrid.push(slideLeft + _slideSize - containerSize);
                        }
                        else {
                            if (containerSize !== 0) {
                                for (var k = 0; k <= Math.floor(_slideSize / containerSize); k++) {
                                    _this.snapGrid.push(slideLeft + containerSize * k);
                                }
                            }
                            else {
                                _this.snapGrid.push(slideLeft);
                            }
                        }

                    }
                    else {
                        _this.snapGrid.push(slideLeft);
                    }
                    _this.slidesGrid.push(slideLeft);
                }

                slideLeft += _slideSize;

                slidesWidth += slideWidth;
                slidesHeight += slideHeight;
            }
            if (params.calculateHeight) _this.height = slideMaxHeight;
            if (isH) {
                wrapperSize = slidesWidth + _this.wrapperRight + _this.wrapperLeft;
                if (!params.cssWidthAndHeight || params.cssWidthAndHeight === 'height') {
                    wrapper.style.width = (slidesWidth) + 'px';
                }
                if (!params.cssWidthAndHeight || params.cssWidthAndHeight === 'width') {
                    wrapper.style.height = (_this.height) + 'px';
                }
            }
            else {
                if (!params.cssWidthAndHeight || params.cssWidthAndHeight === 'height') {
                    wrapper.style.width = (_this.width) + 'px';
                }
                if (!params.cssWidthAndHeight || params.cssWidthAndHeight === 'width') {
                    wrapper.style.height = (slidesHeight) + 'px';
                }
                wrapperSize = slidesHeight + _this.wrapperTop + _this.wrapperBottom;
            }

        }
        else if (params.scrollContainer) {
            //Scroll Container
            wrapper.style.width = '';
            wrapper.style.height = '';
            wrapperWidth = _this.slides[0].getWidth(true, params.roundLengths);
            wrapperHeight = _this.slides[0].getHeight(true, params.roundLengths);
            wrapperSize = isH ? wrapperWidth : wrapperHeight;
            wrapper.style.width = wrapperWidth + 'px';
            wrapper.style.height = wrapperHeight + 'px';
            slideSize = isH ? wrapperWidth : wrapperHeight;

        }
        else {
            //For usual slides
            if (params.calculateHeight) {
                slideMaxHeight = 0;
                wrapperHeight = 0;
                //ResetWrapperSize
                if (!isH) _this.container.style.height = '';
                wrapper.style.height = '';

                for (i = 0; i < _this.slides.length; i++) {
                    //ResetSlideSize
                    _this.slides[i].style.height = '';
                    slideMaxHeight = Math.max(_this.slides[i].getHeight(true), slideMaxHeight);
                    if (!isH) wrapperHeight += _this.slides[i].getHeight(true);
                }
                slideHeight = slideMaxHeight;
                _this.height = slideHeight;

                if (isH) wrapperHeight = slideHeight;
                else {
                    containerSize = slideHeight;
                    _this.container.style.height = containerSize + 'px';
                }
            }
            else {
                slideHeight = isH ? _this.height : _this.height / params.slidesPerView;
                if (params.roundLengths) slideHeight = Math.ceil(slideHeight);
                wrapperHeight = isH ? _this.height : _this.slides.length * slideHeight;
            }
            slideWidth = isH ? _this.width / params.slidesPerView : _this.width;
            if (params.roundLengths) slideWidth = Math.ceil(slideWidth);
            wrapperWidth = isH ? _this.slides.length * slideWidth : _this.width;
            slideSize = isH ? slideWidth : slideHeight;

            if (params.offsetSlidesBefore > 0) {
                if (isH) _this.wrapperLeft = slideSize * params.offsetSlidesBefore;
                else _this.wrapperTop = slideSize * params.offsetSlidesBefore;
            }
            if (params.offsetSlidesAfter > 0) {
                if (isH) _this.wrapperRight = slideSize * params.offsetSlidesAfter;
                else _this.wrapperBottom = slideSize * params.offsetSlidesAfter;
            }
            if (params.offsetPxBefore > 0) {
                if (isH) _this.wrapperLeft = params.offsetPxBefore;
                else _this.wrapperTop = params.offsetPxBefore;
            }
            if (params.offsetPxAfter > 0) {
                if (isH) _this.wrapperRight = params.offsetPxAfter;
                else _this.wrapperBottom = params.offsetPxAfter;
            }
            if (params.centeredSlides) {
                if (isH) {
                    _this.wrapperLeft = (containerSize - slideSize) / 2;
                    _this.wrapperRight = (containerSize - slideSize) / 2;
                }
                else {
                    _this.wrapperTop = (containerSize - slideSize) / 2;
                    _this.wrapperBottom = (containerSize - slideSize) / 2;
                }
            }
            if (isH) {
                if (_this.wrapperLeft > 0) wrapper.style.paddingLeft = _this.wrapperLeft + 'px';
                if (_this.wrapperRight > 0) wrapper.style.paddingRight = _this.wrapperRight + 'px';
            }
            else {
                if (_this.wrapperTop > 0) wrapper.style.paddingTop = _this.wrapperTop + 'px';
                if (_this.wrapperBottom > 0) wrapper.style.paddingBottom = _this.wrapperBottom + 'px';
            }

            wrapperSize = isH ? wrapperWidth + _this.wrapperRight + _this.wrapperLeft : wrapperHeight + _this.wrapperTop + _this.wrapperBottom;
            if (parseFloat(wrapperWidth) > 0 && (!params.cssWidthAndHeight || params.cssWidthAndHeight === 'height')) {
                wrapper.style.width = wrapperWidth + 'px';
            }
            if (parseFloat(wrapperHeight) > 0 && (!params.cssWidthAndHeight || params.cssWidthAndHeight === 'width')) {
                wrapper.style.height = wrapperHeight + 'px';
            }
            slideLeft = 0;
            _this.snapGrid = [];
            _this.slidesGrid = [];
            for (i = 0; i < _this.slides.length; i++) {
                _this.snapGrid.push(slideLeft);
                _this.slidesGrid.push(slideLeft);
                slideLeft += slideSize;
                if (parseFloat(slideWidth) > 0 && (!params.cssWidthAndHeight || params.cssWidthAndHeight === 'height')) {
                    _this.slides[i].style.width = slideWidth + 'px';
                }
                if (parseFloat(slideHeight) > 0 && (!params.cssWidthAndHeight || params.cssWidthAndHeight === 'width')) {
                    _this.slides[i].style.height = slideHeight + 'px';
                }
            }

        }

        if (!_this.initialized) {
            _this.callPlugins('onFirstInit');
            if (params.onFirstInit) _this.fireCallback(params.onFirstInit, _this);
        }
        else {
            _this.callPlugins('onInit');
            if (params.onInit) _this.fireCallback(params.onInit, _this);
        }
        _this.initialized = true;
    };

    _this.reInit = function (forceCalcSlides) {
        _this.init(true, forceCalcSlides);
    };

    _this.resizeFix = function (reInit) {
        _this.callPlugins('beforeResizeFix');

        _this.init(params.resizeReInit || reInit);

        // swipe to active slide in fixed mode
        if (!params.freeMode) {
            _this.swipeTo((params.loop ? _this.activeLoopIndex : _this.activeIndex), 0, false);
            // Fix autoplay
            if (params.autoplay) {
                if (_this.support.transitions && typeof autoplayTimeoutId !== 'undefined') {
                    if (typeof autoplayTimeoutId !== 'undefined') {
                        clearTimeout(autoplayTimeoutId);
                        autoplayTimeoutId = undefined;
                        _this.startAutoplay();
                    }
                }
                else {
                    if (typeof autoplayIntervalId !== 'undefined') {
                        clearInterval(autoplayIntervalId);
                        autoplayIntervalId = undefined;
                        _this.startAutoplay();
                    }
                }
            }
        }
        // move wrapper to the beginning in free mode
        else if (_this.getWrapperTranslate() < -maxWrapperPosition()) {
            _this.setWrapperTransition(0);
            _this.setWrapperTranslate(-maxWrapperPosition());
        }

        _this.callPlugins('afterResizeFix');
    };

    /*==========================================
        Max and Min Positions
    ============================================*/
    function maxWrapperPosition() {
        var a = (wrapperSize - containerSize);
        if (params.freeMode) {
            a = wrapperSize - containerSize;
        }
        // if (params.loop) a -= containerSize;
        if (params.slidesPerView > _this.slides.length && !params.centeredSlides) {
            a = 0;
        }
        if (a < 0) a = 0;
        return a;
    }

    /*==========================================
        Event Listeners
    ============================================*/
    function initEvents() {
        var bind = _this.h.addEventListener;
        var eventTarget = params.eventTarget === 'wrapper' ? _this.wrapper : _this.container;
        //Touch Events
        if (! (_this.browser.ie10 || _this.browser.ie11)) {
            if (_this.support.touch) {
                bind(eventTarget, 'touchstart', onTouchStart);
                bind(eventTarget, 'touchmove', onTouchMove);
                bind(eventTarget, 'touchend', onTouchEnd);
            }
            if (params.simulateTouch) {
                bind(eventTarget, 'mousedown', onTouchStart);
                bind(document, 'mousemove', onTouchMove);
                bind(document, 'mouseup', onTouchEnd);
            }
        }
        else {
            bind(eventTarget, _this.touchEvents.touchStart, onTouchStart);
            bind(document, _this.touchEvents.touchMove, onTouchMove);
            bind(document, _this.touchEvents.touchEnd, onTouchEnd);
        }

        //Resize Event
        if (params.autoResize) {
            bind(window, 'resize', _this.resizeFix);
        }
        //Slide Events
        addSlideEvents();
        //Mousewheel
        _this._wheelEvent = false;
        if (params.mousewheelControl) {
            if (document.onmousewheel !== undefined) {
                _this._wheelEvent = 'mousewheel';
            }
            if (!_this._wheelEvent) {
                try {
                    new WheelEvent('wheel');
                    _this._wheelEvent = 'wheel';
                } catch (e) {}
            }
            if (!_this._wheelEvent) {
                _this._wheelEvent = 'DOMMouseScroll';
            }
            if (_this._wheelEvent) {
                bind(_this.container, _this._wheelEvent, handleMousewheel);
            }
        }

        //Keyboard
        function _loadImage(img) {
            var image, src;
            var onReady = function () {
                if (typeof _this === 'undefined' || _this === null) return;
                if (_this.imagesLoaded !== undefined) _this.imagesLoaded++;
                if (_this.imagesLoaded === _this.imagesToLoad.length) {
                    _this.reInit();
                    if (params.onImagesReady) _this.fireCallback(params.onImagesReady, _this);
                }
            };

            if (!img.complete) {
                src = (img.currentSrc || img.getAttribute('src'));
                if (src) {
                    image = new Image();
                    image.onload = onReady;
                    image.onerror = onReady;
                    image.src = src;
                } else {
                    onReady();
                }

            } else {//image already loaded...
                onReady();
            }
        }

        if (params.keyboardControl) {
            bind(document, 'keydown', handleKeyboardKeys);
        }
        if (params.updateOnImagesReady) {
            _this.imagesToLoad = $$('img', _this.container);

            for (var i = 0; i < _this.imagesToLoad.length; i++) {
                _loadImage(_this.imagesToLoad[i]);
            }
        }
    }

    //Remove Event Listeners
    _this.destroy = function (removeStyles) {
        var unbind = _this.h.removeEventListener;
        var eventTarget = params.eventTarget === 'wrapper' ? _this.wrapper : _this.container;
        //Touch Events
        if (! (_this.browser.ie10 || _this.browser.ie11)) {
            if (_this.support.touch) {
                unbind(eventTarget, 'touchstart', onTouchStart);
                unbind(eventTarget, 'touchmove', onTouchMove);
                unbind(eventTarget, 'touchend', onTouchEnd);
            }
            if (params.simulateTouch) {
                unbind(eventTarget, 'mousedown', onTouchStart);
                unbind(document, 'mousemove', onTouchMove);
                unbind(document, 'mouseup', onTouchEnd);
            }
        }
        else {
            unbind(eventTarget, _this.touchEvents.touchStart, onTouchStart);
            unbind(document, _this.touchEvents.touchMove, onTouchMove);
            unbind(document, _this.touchEvents.touchEnd, onTouchEnd);
        }

        //Resize Event
        if (params.autoResize) {
            unbind(window, 'resize', _this.resizeFix);
        }

        //Init Slide Events
        removeSlideEvents();

        //Pagination
        if (params.paginationClickable) {
            removePaginationEvents();
        }

        //Mousewheel
        if (params.mousewheelControl && _this._wheelEvent) {
            unbind(_this.container, _this._wheelEvent, handleMousewheel);
        }

        //Keyboard
        if (params.keyboardControl) {
            unbind(document, 'keydown', handleKeyboardKeys);
        }

        //Stop autoplay
        if (params.autoplay) {
            _this.stopAutoplay();
        }
        // Remove styles
        if (removeStyles) {
            _this.wrapper.removeAttribute('style');
            for (var i = 0; i < _this.slides.length; i++) {
                _this.slides[i].removeAttribute('style');
            }
        }
        // Plugins
        _this.callPlugins('onDestroy');

        // Check jQuery/Zepto data
        if (window.jQuery && window.jQuery(_this.container).data('swiper')) {
            window.jQuery(_this.container).removeData('swiper');
        }
        if (window.Zepto && window.Zepto(_this.container).data('swiper')) {
            window.Zepto(_this.container).removeData('swiper');
        }

        //Destroy variable
        _this = null;
    };

    function addSlideEvents() {
        var bind = _this.h.addEventListener,
            i;

        //Prevent Links Events
        if (params.preventLinks) {
            var links = $$('a', _this.container);
            for (i = 0; i < links.length; i++) {
                bind(links[i], 'click', preventClick);
            }
        }
        //Release Form Elements
        if (params.releaseFormElements) {
            var formElements = $$('input, textarea, select', _this.container);
            for (i = 0; i < formElements.length; i++) {
                bind(formElements[i], _this.touchEvents.touchStart, releaseForms, true);
                if (_this.support.touch && params.simulateTouch) {
                    bind(formElements[i], 'mousedown', releaseForms, true);
                }
            }
        }

        //Slide Clicks & Touches
        if (params.onSlideClick) {
            for (i = 0; i < _this.slides.length; i++) {
                bind(_this.slides[i], 'click', slideClick);
            }
        }
        if (params.onSlideTouch) {
            for (i = 0; i < _this.slides.length; i++) {
                bind(_this.slides[i], _this.touchEvents.touchStart, slideTouch);
            }
        }
    }
    function removeSlideEvents() {
        var unbind = _this.h.removeEventListener,
            i;

        //Slide Clicks & Touches
        if (params.onSlideClick) {
            for (i = 0; i < _this.slides.length; i++) {
                unbind(_this.slides[i], 'click', slideClick);
            }
        }
        if (params.onSlideTouch) {
            for (i = 0; i < _this.slides.length; i++) {
                unbind(_this.slides[i], _this.touchEvents.touchStart, slideTouch);
            }
        }
        //Release Form Elements
        if (params.releaseFormElements) {
            var formElements = $$('input, textarea, select', _this.container);
            for (i = 0; i < formElements.length; i++) {
                unbind(formElements[i], _this.touchEvents.touchStart, releaseForms, true);
                if (_this.support.touch && params.simulateTouch) {
                    unbind(formElements[i], 'mousedown', releaseForms, true);
                }
            }
        }
        //Prevent Links Events
        if (params.preventLinks) {
            var links = $$('a', _this.container);
            for (i = 0; i < links.length; i++) {
                unbind(links[i], 'click', preventClick);
            }
        }
    }
    /*==========================================
        Keyboard Control
    ============================================*/
    function handleKeyboardKeys(e) {
        var kc = e.keyCode || e.charCode;
        if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
        if (kc === 37 || kc === 39 || kc === 38 || kc === 40) {
            var inView = false;
            //Check that swiper should be inside of visible area of window
            var swiperOffset = _this.h.getOffset(_this.container);
            var scrollLeft = _this.h.windowScroll().left;
            var scrollTop = _this.h.windowScroll().top;
            var windowWidth = _this.h.windowWidth();
            var windowHeight = _this.h.windowHeight();
            var swiperCoord = [
                [swiperOffset.left, swiperOffset.top],
                [swiperOffset.left + _this.width, swiperOffset.top],
                [swiperOffset.left, swiperOffset.top + _this.height],
                [swiperOffset.left + _this.width, swiperOffset.top + _this.height]
            ];
            for (var i = 0; i < swiperCoord.length; i++) {
                var point = swiperCoord[i];
                if (
                    point[0] >= scrollLeft && point[0] <= scrollLeft + windowWidth &&
                    point[1] >= scrollTop && point[1] <= scrollTop + windowHeight
                ) {
                    inView = true;
                }

            }
            if (!inView) return;
        }
        if (isH) {
            if (kc === 37 || kc === 39) {
                if (e.preventDefault) e.preventDefault();
                else e.returnValue = false;
            }
            if (kc === 39) _this.swipeNext();
            if (kc === 37) _this.swipePrev();
        }
        else {
            if (kc === 38 || kc === 40) {
                if (e.preventDefault) e.preventDefault();
                else e.returnValue = false;
            }
            if (kc === 40) _this.swipeNext();
            if (kc === 38) _this.swipePrev();
        }
    }

    _this.disableKeyboardControl = function () {
        params.keyboardControl = false;
        _this.h.removeEventListener(document, 'keydown', handleKeyboardKeys);
    };

    _this.enableKeyboardControl = function () {
        params.keyboardControl = true;
        _this.h.addEventListener(document, 'keydown', handleKeyboardKeys);
    };

    /*==========================================
        Mousewheel Control
    ============================================*/
    var lastScrollTime = (new Date()).getTime();
    function handleMousewheel(e) {
        var we = _this._wheelEvent;
        var delta = 0;

        //Opera & IE
        if (e.detail) delta = -e.detail;
        //WebKits
        else if (we === 'mousewheel') {
            if (params.mousewheelControlForceToAxis) {
                if (isH) {
                    if (Math.abs(e.wheelDeltaX) > Math.abs(e.wheelDeltaY)) delta = e.wheelDeltaX;
                    else return;
                }
                else {
                    if (Math.abs(e.wheelDeltaY) > Math.abs(e.wheelDeltaX)) delta = e.wheelDeltaY;
                    else return;
                }
            }
            else {
                delta = e.wheelDelta;
            }
        }
        //Old FireFox
        else if (we === 'DOMMouseScroll') delta = -e.detail;
        //New FireFox
        else if (we === 'wheel') {
            if (params.mousewheelControlForceToAxis) {
                if (isH) {
                    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) delta = -e.deltaX;
                    else return;
                }
                else {
                    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) delta = -e.deltaY;
                    else return;
                }
            }
            else {
                delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? - e.deltaX : - e.deltaY;
            }
        }

        if (!params.freeMode) {
            if ((new Date()).getTime() - lastScrollTime > 60) {
                if (delta < 0) _this.swipeNext();
                else _this.swipePrev();
            }
            lastScrollTime = (new Date()).getTime();

        }
        else {
            //Freemode or scrollContainer:
            var position = _this.getWrapperTranslate() + delta;

            if (position > 0) position = 0;
            if (position < -maxWrapperPosition()) position = -maxWrapperPosition();

            _this.setWrapperTransition(0);
            _this.setWrapperTranslate(position);
            _this.updateActiveSlide(position);

            // Return page scroll on edge positions
            if (position === 0 || position === -maxWrapperPosition()) return;
        }
        if (params.autoplay) _this.stopAutoplay(true);

        if (e.preventDefault) e.preventDefault();
        else e.returnValue = false;
        return false;
    }
    _this.disableMousewheelControl = function () {
        if (!_this._wheelEvent) return false;
        params.mousewheelControl = false;
        _this.h.removeEventListener(_this.container, _this._wheelEvent, handleMousewheel);
        return true;
    };

    _this.enableMousewheelControl = function () {
        if (!_this._wheelEvent) return false;
        params.mousewheelControl = true;
        _this.h.addEventListener(_this.container, _this._wheelEvent, handleMousewheel);
        return true;
    };

    /*=========================
      Grab Cursor
      ===========================*/
    if (params.grabCursor) {
        var containerStyle = _this.container.style;
        containerStyle.cursor = 'move';
        containerStyle.cursor = 'grab';
        containerStyle.cursor = '-moz-grab';
        containerStyle.cursor = '-webkit-grab';
    }

    /*=========================
      Slides Events Handlers
      ===========================*/

    _this.allowSlideClick = true;
    function slideClick(event) {
        if (_this.allowSlideClick) {
            setClickedSlide(event);
            _this.fireCallback(params.onSlideClick, _this, event);
        }
    }

    function slideTouch(event) {
        setClickedSlide(event);
        _this.fireCallback(params.onSlideTouch, _this, event);
    }

    function setClickedSlide(event) {

        // IE 6-8 support
        if (!event.currentTarget) {
            var element = event.srcElement;
            do {
                if (element.className.indexOf(params.slideClass) > -1) {
                    break;
                }
                element = element.parentNode;
            } while (element);
            _this.clickedSlide = element;
        }
        else {
            _this.clickedSlide = event.currentTarget;
        }

        _this.clickedSlideIndex     = _this.slides.indexOf(_this.clickedSlide);
        _this.clickedSlideLoopIndex = _this.clickedSlideIndex - (_this.loopedSlides || 0);
    }

    _this.allowLinks = true;
    function preventClick(e) {
        if (!_this.allowLinks) {
            if (e.preventDefault) e.preventDefault();
            else e.returnValue = false;
            if (params.preventLinksPropagation && 'stopPropagation' in e) {
                e.stopPropagation();
            }
            return false;
        }
    }
    function releaseForms(e) {
        if (e.stopPropagation) e.stopPropagation();
        else e.returnValue = false;
        return false;

    }

    /*==================================================
        Event Handlers
    ====================================================*/
    var isTouchEvent = false;
    var allowThresholdMove;
    var allowMomentumBounce = true;
    function onTouchStart(event) {
        if (params.preventLinks) _this.allowLinks = true;
        //Exit if slider is already was touched
        if (_this.isTouched || params.onlyExternal) {
            return false;
        }

        // Blur active elements
        var eventTarget = event.target || event.srcElement;
        if (document.activeElement && document.activeElement !== document.body) {
            if (document.activeElement !== eventTarget) document.activeElement.blur();
        }

        // Form tag names
        var formTagNames = ('input select textarea').split(' ');

        // Check for no swiping
        if (params.noSwiping && (eventTarget) && noSwipingSlide(eventTarget)) return false;
        allowMomentumBounce = false;
        //Check For Nested Swipers
        _this.isTouched = true;
        isTouchEvent = event.type === 'touchstart';

        // prevent user enter with right and the swiper move (needs isTouchEvent)
        if (!isTouchEvent && 'which' in event && event.which === 3) {
            _this.isTouched = false;
            return false;
        }

        if (!isTouchEvent || event.targetTouches.length === 1) {
            _this.callPlugins('onTouchStartBegin');
            if (!isTouchEvent && !_this.isAndroid && formTagNames.indexOf(eventTarget.tagName.toLowerCase()) < 0) {

                if (event.preventDefault) event.preventDefault();
                else event.returnValue = false;
            }

            var pageX = isTouchEvent ? event.targetTouches[0].pageX : (event.pageX || event.clientX);
            var pageY = isTouchEvent ? event.targetTouches[0].pageY : (event.pageY || event.clientY);

            //Start Touches to check the scrolling
            _this.touches.startX = _this.touches.currentX = pageX;
            _this.touches.startY = _this.touches.currentY = pageY;

            _this.touches.start = _this.touches.current = isH ? pageX : pageY;

            //Set Transition Time to 0
            _this.setWrapperTransition(0);

            //Get Start Translate Position
            _this.positions.start = _this.positions.current = _this.getWrapperTranslate();

            //Set Transform
            _this.setWrapperTranslate(_this.positions.start);

            //TouchStartTime
            _this.times.start = (new Date()).getTime();

            //Unset Scrolling
            isScrolling = undefined;

            //Set Treshold
            if (params.moveStartThreshold > 0) {
                allowThresholdMove = false;
            }

            //CallBack
            if (params.onTouchStart) _this.fireCallback(params.onTouchStart, _this, event);
            _this.callPlugins('onTouchStartEnd');

        }
    }
    var velocityPrevPosition, velocityPrevTime;
    function onTouchMove(event) {
        // If slider is not touched - exit
        if (!_this.isTouched || params.onlyExternal) return;
        if (isTouchEvent && event.type === 'mousemove') return;

        var pageX = isTouchEvent ? event.targetTouches[0].pageX : (event.pageX || event.clientX);
        var pageY = isTouchEvent ? event.targetTouches[0].pageY : (event.pageY || event.clientY);

        //check for scrolling
        if (typeof isScrolling === 'undefined' && isH) {
            isScrolling = !!(isScrolling || Math.abs(pageY - _this.touches.startY) > Math.abs(pageX - _this.touches.startX));
        }
        if (typeof isScrolling === 'undefined' && !isH) {
            isScrolling = !!(isScrolling || Math.abs(pageY - _this.touches.startY) < Math.abs(pageX - _this.touches.startX));
        }
        if (isScrolling) {
            _this.isTouched = false;
            return;
        }

        // One way swipes
        if (isH) {
            if ((!params.swipeToNext && pageX < _this.touches.startX) || ((!params.swipeToPrev && pageX > _this.touches.startX))) {
                return;
            }
        }
        else {
            if ((!params.swipeToNext && pageY < _this.touches.startY) || ((!params.swipeToPrev && pageY > _this.touches.startY))) {
                return;
            }
        }

        //Check For Nested Swipers
        if (event.assignedToSwiper) {
            _this.isTouched = false;
            return;
        }
        event.assignedToSwiper = true;

        //Block inner links
        if (params.preventLinks) {
            _this.allowLinks = false;
        }
        if (params.onSlideClick) {
            _this.allowSlideClick = false;
        }

        //Stop AutoPlay if exist
        if (params.autoplay) {
            _this.stopAutoplay(true);
        }
        if (!isTouchEvent || event.touches.length === 1) {

            //Moved Flag
            if (!_this.isMoved) {
                _this.callPlugins('onTouchMoveStart');

                if (params.loop) {
                    _this.fixLoop();
                    _this.positions.start = _this.getWrapperTranslate();
                }
                if (params.onTouchMoveStart) _this.fireCallback(params.onTouchMoveStart, _this);
            }
            _this.isMoved = true;

            // cancel event
            if (event.preventDefault) event.preventDefault();
            else event.returnValue = false;

            _this.touches.current = isH ? pageX : pageY;

            _this.positions.current = (_this.touches.current - _this.touches.start) * params.touchRatio + _this.positions.start;

            //Resistance Callbacks
            if (_this.positions.current > 0 && params.onResistanceBefore) {
                _this.fireCallback(params.onResistanceBefore, _this, _this.positions.current);
            }
            if (_this.positions.current < -maxWrapperPosition() && params.onResistanceAfter) {
                _this.fireCallback(params.onResistanceAfter, _this, Math.abs(_this.positions.current + maxWrapperPosition()));
            }
            //Resistance
            if (params.resistance && params.resistance !== '100%') {
                var resistance;
                //Resistance for Negative-Back sliding
                if (_this.positions.current > 0) {
                    resistance = 1 - _this.positions.current / containerSize / 2;
                    if (resistance < 0.5)
                        _this.positions.current = (containerSize / 2);
                    else
                        _this.positions.current = _this.positions.current * resistance;
                }
                //Resistance for After-End Sliding
                if (_this.positions.current < -maxWrapperPosition()) {

                    var diff = (_this.touches.current - _this.touches.start) * params.touchRatio + (maxWrapperPosition() + _this.positions.start);
                    resistance = (containerSize + diff) / (containerSize);
                    var newPos = _this.positions.current - diff * (1 - resistance) / 2;
                    var stopPos = -maxWrapperPosition() - containerSize / 2;

                    if (newPos < stopPos || resistance <= 0)
                        _this.positions.current = stopPos;
                    else
                        _this.positions.current = newPos;
                }
            }
            if (params.resistance && params.resistance === '100%') {
                //Resistance for Negative-Back sliding
                if (_this.positions.current > 0 && !(params.freeMode && !params.freeModeFluid)) {
                    _this.positions.current = 0;
                }
                //Resistance for After-End Sliding
                if (_this.positions.current < -maxWrapperPosition() && !(params.freeMode && !params.freeModeFluid)) {
                    _this.positions.current = -maxWrapperPosition();
                }
            }
            //Move Slides
            if (!params.followFinger) return;

            if (!params.moveStartThreshold) {
                _this.setWrapperTranslate(_this.positions.current);
            }
            else {
                if (Math.abs(_this.touches.current - _this.touches.start) > params.moveStartThreshold || allowThresholdMove) {
                    if (!allowThresholdMove) {
                        allowThresholdMove = true;
                        _this.touches.start = _this.touches.current;
                        return;
                    }
                    _this.setWrapperTranslate(_this.positions.current);
                }
                else {
                    _this.positions.current = _this.positions.start;
                }
            }

            if (params.freeMode || params.watchActiveIndex) {
                _this.updateActiveSlide(_this.positions.current);
            }

            //Grab Cursor
            if (params.grabCursor) {
                _this.container.style.cursor = 'move';
                _this.container.style.cursor = 'grabbing';
                _this.container.style.cursor = '-moz-grabbin';
                _this.container.style.cursor = '-webkit-grabbing';
            }
            //Velocity
            if (!velocityPrevPosition) velocityPrevPosition = _this.touches.current;
            if (!velocityPrevTime) velocityPrevTime = (new Date()).getTime();
            _this.velocity = (_this.touches.current - velocityPrevPosition) / ((new Date()).getTime() - velocityPrevTime) / 2;
            if (Math.abs(_this.touches.current - velocityPrevPosition) < 2) _this.velocity = 0;
            velocityPrevPosition = _this.touches.current;
            velocityPrevTime = (new Date()).getTime();
            //Callbacks
            _this.callPlugins('onTouchMoveEnd');
            if (params.onTouchMove) _this.fireCallback(params.onTouchMove, _this, event);

            return false;
        }
    }
    function onTouchEnd(event) {
        //Check For scrolling
        if (isScrolling) {
            _this.swipeReset();
        }
        // If slider is not touched exit
        if (params.onlyExternal || !_this.isTouched) return;
        _this.isTouched = false;

        //Return Grab Cursor
        if (params.grabCursor) {
            _this.container.style.cursor = 'move';
            _this.container.style.cursor = 'grab';
            _this.container.style.cursor = '-moz-grab';
            _this.container.style.cursor = '-webkit-grab';
        }

        //Check for Current Position
        if (!_this.positions.current && _this.positions.current !== 0) {
            _this.positions.current = _this.positions.start;
        }

        //For case if slider touched but not moved
        if (params.followFinger) {
            _this.setWrapperTranslate(_this.positions.current);
        }

        // TouchEndTime
        _this.times.end = (new Date()).getTime();

        //Difference
        _this.touches.diff = _this.touches.current - _this.touches.start;
        _this.touches.abs = Math.abs(_this.touches.diff);

        _this.positions.diff = _this.positions.current - _this.positions.start;
        _this.positions.abs = Math.abs(_this.positions.diff);

        var diff = _this.positions.diff;
        var diffAbs = _this.positions.abs;
        var timeDiff = _this.times.end - _this.times.start;

        if (diffAbs < 5 && (timeDiff) < 300 && _this.allowLinks === false) {
            if (!params.freeMode && diffAbs !== 0) _this.swipeReset();
            //Release inner links
            if (params.preventLinks) {
                _this.allowLinks = true;
            }
            if (params.onSlideClick) {
                _this.allowSlideClick = true;
            }
        }

        setTimeout(function () {
            //Release inner links
            if (typeof _this === 'undefined' || _this === null) return;
            if (params.preventLinks) {
                _this.allowLinks = true;
            }
            if (params.onSlideClick) {
                _this.allowSlideClick = true;
            }
        }, 100);

        var maxPosition = maxWrapperPosition();

        //Not moved or Prevent Negative Back Sliding/After-End Sliding
        if (!_this.isMoved && params.freeMode) {
            _this.isMoved = false;
            if (params.onTouchEnd) _this.fireCallback(params.onTouchEnd, _this, event);
            _this.callPlugins('onTouchEnd');
            return;
        }
        if (!_this.isMoved || _this.positions.current > 0 || _this.positions.current < -maxPosition) {
            _this.swipeReset();
            if (params.onTouchEnd) _this.fireCallback(params.onTouchEnd, _this, event);
            _this.callPlugins('onTouchEnd');
            return;
        }

        _this.isMoved = false;

        //Free Mode
        if (params.freeMode) {
            if (params.freeModeFluid) {
                var momentumDuration = 1000 * params.momentumRatio;
                var momentumDistance = _this.velocity * momentumDuration;
                var newPosition = _this.positions.current + momentumDistance;
                var doBounce = false;
                var afterBouncePosition;
                var bounceAmount = Math.abs(_this.velocity) * 20 * params.momentumBounceRatio;
                if (newPosition < -maxPosition) {
                    if (params.momentumBounce && _this.support.transitions) {
                        if (newPosition + maxPosition < -bounceAmount) newPosition = -maxPosition - bounceAmount;
                        afterBouncePosition = -maxPosition;
                        doBounce = true;
                        allowMomentumBounce = true;
                    }
                    else newPosition = -maxPosition;
                }
                if (newPosition > 0) {
                    if (params.momentumBounce && _this.support.transitions) {
                        if (newPosition > bounceAmount) newPosition = bounceAmount;
                        afterBouncePosition = 0;
                        doBounce = true;
                        allowMomentumBounce = true;
                    }
                    else newPosition = 0;
                }
                //Fix duration
                if (_this.velocity !== 0) momentumDuration = Math.abs((newPosition - _this.positions.current) / _this.velocity);

                _this.setWrapperTranslate(newPosition);

                _this.setWrapperTransition(momentumDuration);

                if (params.momentumBounce && doBounce) {
                    _this.wrapperTransitionEnd(function () {
                        if (!allowMomentumBounce) return;
                        if (params.onMomentumBounce) _this.fireCallback(params.onMomentumBounce, _this);
                        _this.callPlugins('onMomentumBounce');

                        _this.setWrapperTranslate(afterBouncePosition);
                        _this.setWrapperTransition(300);
                    });
                }

                _this.updateActiveSlide(newPosition);
            }
            if (!params.freeModeFluid || timeDiff >= 300) _this.updateActiveSlide(_this.positions.current);

            if (params.onTouchEnd) _this.fireCallback(params.onTouchEnd, _this, event);
            _this.callPlugins('onTouchEnd');
            return;
        }

        //Direction
        direction = diff < 0 ? 'toNext' : 'toPrev';

        //Short Touches
        if (direction === 'toNext' && (timeDiff <= 300)) {
            if (diffAbs < 30 || !params.shortSwipes) _this.swipeReset();
            else _this.swipeNext(true, true);
        }

        if (direction === 'toPrev' && (timeDiff <= 300)) {
            if (diffAbs < 30 || !params.shortSwipes) _this.swipeReset();
            else _this.swipePrev(true, true);
        }

        //Long Touches
        var targetSlideSize = 0;
        if (params.slidesPerView === 'auto') {
            //Define current slide's width
            var currentPosition = Math.abs(_this.getWrapperTranslate());
            var slidesOffset = 0;
            var _slideSize;
            for (var i = 0; i < _this.slides.length; i++) {
                _slideSize = isH ? _this.slides[i].getWidth(true, params.roundLengths) : _this.slides[i].getHeight(true, params.roundLengths);
                slidesOffset += _slideSize;
                if (slidesOffset > currentPosition) {
                    targetSlideSize = _slideSize;
                    break;
                }
            }
            if (targetSlideSize > containerSize) targetSlideSize = containerSize;
        }
        else {
            targetSlideSize = slideSize * params.slidesPerView;
        }
        if (direction === 'toNext' && (timeDiff > 300)) {
            if (diffAbs >= targetSlideSize * params.longSwipesRatio) {
                _this.swipeNext(true, true);
            }
            else {
                _this.swipeReset();
            }
        }
        if (direction === 'toPrev' && (timeDiff > 300)) {
            if (diffAbs >= targetSlideSize * params.longSwipesRatio) {
                _this.swipePrev(true, true);
            }
            else {
                _this.swipeReset();
            }
        }
        if (params.onTouchEnd) _this.fireCallback(params.onTouchEnd, _this, event);
        _this.callPlugins('onTouchEnd');
    }


    /*==================================================
        noSwiping Bubble Check by Isaac Strack
    ====================================================*/
    function hasClass(el, classname) {
        return el && el.getAttribute('class') && el.getAttribute('class').indexOf(classname) > -1;
    }
    function noSwipingSlide(el) {
        /*This function is specifically designed to check the parent elements for the noSwiping class, up to the wrapper.
        We need to check parents because while onTouchStart bubbles, _this.isTouched is checked in onTouchStart, which stops the bubbling.
        So, if a text box, for example, is the initial target, and the parent slide container has the noSwiping class, the _this.isTouched
        check will never find it, and what was supposed to be noSwiping is able to be swiped.
        This function will iterate up and check for the noSwiping class in parents, up through the wrapperClass.*/

        // First we create a truthy variable, which is that swiping is allowd (noSwiping = false)
        var noSwiping = false;

        // Now we iterate up (parentElements) until we reach the node with the wrapperClass.
        do {

            // Each time, we check to see if there's a 'swiper-no-swiping' class (noSwipingClass).
            if (hasClass(el, params.noSwipingClass))
            {
                noSwiping = true; // If there is, we set noSwiping = true;
            }

            el = el.parentElement;  // now we iterate up (parent node)

        } while (!noSwiping && el.parentElement && !hasClass(el, params.wrapperClass)); // also include el.parentElement truthy, just in case.

        // because we didn't check the wrapper itself, we do so now, if noSwiping is false:
        if (!noSwiping && hasClass(el, params.wrapperClass) && hasClass(el, params.noSwipingClass))
            noSwiping = true; // if the wrapper has the noSwipingClass, we set noSwiping = true;

        return noSwiping;
    }

    function addClassToHtmlString(klass, outerHtml) {
        var par = document.createElement('div');
        var child;

        par.innerHTML = outerHtml;
        child = par.firstChild;
        child.className += ' ' + klass;

        return child.outerHTML;
    }


    /*==================================================
        Swipe Functions
    ====================================================*/
    _this.swipeNext = function (runCallbacks, internal) {
        if (typeof runCallbacks === 'undefined') runCallbacks = true;
        if (!internal && params.loop) _this.fixLoop();
        if (!internal && params.autoplay) _this.stopAutoplay(true);
        _this.callPlugins('onSwipeNext');
        var currentPosition = _this.getWrapperTranslate().toFixed(2);
        var newPosition = currentPosition;
        if (params.slidesPerView === 'auto') {
            for (var i = 0; i < _this.snapGrid.length; i++) {
                if (-currentPosition >= _this.snapGrid[i].toFixed(2) && -currentPosition < _this.snapGrid[i + 1].toFixed(2)) {
                    newPosition = -_this.snapGrid[i + 1];
                    break;
                }
            }
        }
        else {
            var groupSize = slideSize * params.slidesPerGroup;
            newPosition = -(Math.floor(Math.abs(currentPosition) / Math.floor(groupSize)) * groupSize + groupSize);
        }
        if (newPosition < -maxWrapperPosition()) {
            newPosition = -maxWrapperPosition();
        }
        if (newPosition === currentPosition) return false;
        swipeToPosition(newPosition, 'next', {runCallbacks: runCallbacks});
        return true;
    };
    _this.swipePrev = function (runCallbacks, internal) {
        if (typeof runCallbacks === 'undefined') runCallbacks = true;
        if (!internal && params.loop) _this.fixLoop();
        if (!internal && params.autoplay) _this.stopAutoplay(true);
        _this.callPlugins('onSwipePrev');

        var currentPosition = Math.ceil(_this.getWrapperTranslate());
        var newPosition;
        if (params.slidesPerView === 'auto') {
            newPosition = 0;
            for (var i = 1; i < _this.snapGrid.length; i++) {
                if (-currentPosition === _this.snapGrid[i]) {
                    newPosition = -_this.snapGrid[i - 1];
                    break;
                }
                if (-currentPosition > _this.snapGrid[i] && -currentPosition < _this.snapGrid[i + 1]) {
                    newPosition = -_this.snapGrid[i];
                    break;
                }
            }
        }
        else {
            var groupSize = slideSize * params.slidesPerGroup;
            newPosition = -(Math.ceil(-currentPosition / groupSize) - 1) * groupSize;
        }

        if (newPosition > 0) newPosition = 0;

        if (newPosition === currentPosition) return false;
        swipeToPosition(newPosition, 'prev', {runCallbacks: runCallbacks});
        return true;

    };
    _this.swipeReset = function (runCallbacks) {
        if (typeof runCallbacks === 'undefined') runCallbacks = true;
        _this.callPlugins('onSwipeReset');
        var currentPosition = _this.getWrapperTranslate();
        var groupSize = slideSize * params.slidesPerGroup;
        var newPosition;
        var maxPosition = -maxWrapperPosition();
        if (params.slidesPerView === 'auto') {
            newPosition = 0;
            for (var i = 0; i < _this.snapGrid.length; i++) {
                if (-currentPosition === _this.snapGrid[i]) return;
                if (-currentPosition >= _this.snapGrid[i] && -currentPosition < _this.snapGrid[i + 1]) {
                    if (_this.positions.diff > 0) newPosition = -_this.snapGrid[i + 1];
                    else newPosition = -_this.snapGrid[i];
                    break;
                }
            }
            if (-currentPosition >= _this.snapGrid[_this.snapGrid.length - 1]) newPosition = -_this.snapGrid[_this.snapGrid.length - 1];
            if (currentPosition <= -maxWrapperPosition()) newPosition = -maxWrapperPosition();
        }
        else {
            newPosition = currentPosition < 0 ? Math.round(currentPosition / groupSize) * groupSize : 0;
            if (currentPosition <= -maxWrapperPosition()) newPosition = -maxWrapperPosition();
        }
        if (params.scrollContainer)  {
            newPosition = currentPosition < 0 ? currentPosition : 0;
        }
        if (newPosition < -maxWrapperPosition()) {
            newPosition = -maxWrapperPosition();
        }
        if (params.scrollContainer && (containerSize > slideSize)) {
            newPosition = 0;
        }

        if (newPosition === currentPosition) return false;

        swipeToPosition(newPosition, 'reset', {runCallbacks: runCallbacks});
        return true;
    };

    _this.swipeTo = function (index, speed, runCallbacks) {
        index = parseInt(index, 10);
        _this.callPlugins('onSwipeTo', {index: index, speed: speed});
        if (params.loop) index = index + _this.loopedSlides;
        var currentPosition = _this.getWrapperTranslate();
        if (index > (_this.slides.length - 1) || index < 0) return;
        var newPosition;
        if (params.slidesPerView === 'auto') {
            newPosition = -_this.slidesGrid[index];
        }
        else {
            newPosition = -index * slideSize;
        }
        if (newPosition < - maxWrapperPosition()) {
            newPosition = - maxWrapperPosition();
        }

        if (newPosition === currentPosition) return false;

        if (typeof runCallbacks === 'undefined') runCallbacks = true;
        swipeToPosition(newPosition, 'to', {index: index, speed: speed, runCallbacks: runCallbacks});
        return true;
    };

    function swipeToPosition(newPosition, action, toOptions) {
        var speed = (action === 'to' && toOptions.speed >= 0) ? toOptions.speed : params.speed;
        var timeOld = + new Date();

        function anim() {
            if (typeof _this === 'undefined' || _this === null) return;
            var timeNew = + new Date();
            var time = timeNew - timeOld;
            currentPosition += animationStep * time / (1000 / 60);
            condition = direction === 'toNext' ? currentPosition > newPosition : currentPosition < newPosition;
            if (condition) {
                _this.setWrapperTranslate(Math.ceil(currentPosition));
                _this._DOMAnimating = true;
                window.setTimeout(function () {
                    anim();
                }, 1000 / 60);
            }
            else {
                if (params.onSlideChangeEnd) {
                    if (action === 'to') {
                        if (toOptions.runCallbacks === true) _this.fireCallback(params.onSlideChangeEnd, _this, direction);
                    }
                    else {
                        _this.fireCallback(params.onSlideChangeEnd, _this, direction);
                    }

                }
                _this.setWrapperTranslate(newPosition);
                _this._DOMAnimating = false;
            }
        }

        if (_this.support.transitions || !params.DOMAnimation) {
            _this.setWrapperTranslate(newPosition);
            _this.setWrapperTransition(speed);
        }
        else {
            //Try the DOM animation
            var currentPosition = _this.getWrapperTranslate();
            var animationStep = Math.ceil((newPosition - currentPosition) / speed * (1000 / 60));
            var direction = currentPosition > newPosition ? 'toNext' : 'toPrev';
            var condition = direction === 'toNext' ? currentPosition > newPosition : currentPosition < newPosition;
            if (_this._DOMAnimating) return;

            anim();
        }

        //Update Active Slide Index
        _this.updateActiveSlide(newPosition);

        //Callbacks
        if (params.onSlideNext && action === 'next' && toOptions.runCallbacks === true) {
            _this.fireCallback(params.onSlideNext, _this, newPosition);
        }
        if (params.onSlidePrev && action === 'prev' && toOptions.runCallbacks === true) {
            _this.fireCallback(params.onSlidePrev, _this, newPosition);
        }
        //'Reset' Callback
        if (params.onSlideReset && action === 'reset' && toOptions.runCallbacks === true) {
            _this.fireCallback(params.onSlideReset, _this, newPosition);
        }

        //'Next', 'Prev' and 'To' Callbacks
        if ((action === 'next' || action === 'prev' || action === 'to') && toOptions.runCallbacks === true)
            slideChangeCallbacks(action);
    }
    /*==================================================
        Transition Callbacks
    ====================================================*/
    //Prevent Multiple Callbacks
    _this._queueStartCallbacks = false;
    _this._queueEndCallbacks = false;
    function slideChangeCallbacks(direction) {
        //Transition Start Callback
        _this.callPlugins('onSlideChangeStart');
        if (params.onSlideChangeStart) {
            if (params.queueStartCallbacks && _this.support.transitions) {
                if (_this._queueStartCallbacks) return;
                _this._queueStartCallbacks = true;
                _this.fireCallback(params.onSlideChangeStart, _this, direction);
                _this.wrapperTransitionEnd(function () {
                    _this._queueStartCallbacks = false;
                });
            }
            else _this.fireCallback(params.onSlideChangeStart, _this, direction);
        }
        //Transition End Callback
        if (params.onSlideChangeEnd) {
            if (_this.support.transitions) {
                if (params.queueEndCallbacks) {
                    if (_this._queueEndCallbacks) return;
                    _this._queueEndCallbacks = true;
                    _this.wrapperTransitionEnd(function (swiper) {
                        _this.fireCallback(params.onSlideChangeEnd, swiper, direction);
                    });
                }
                else {
                    _this.wrapperTransitionEnd(function (swiper) {
                        _this.fireCallback(params.onSlideChangeEnd, swiper, direction);
                    });
                }
            }
            else {
                if (!params.DOMAnimation) {
                    setTimeout(function () {
                        _this.fireCallback(params.onSlideChangeEnd, _this, direction);
                    }, 10);
                }
            }
        }
    }

    /*==================================================
        Update Active Slide Index
    ====================================================*/
    _this.updateActiveSlide = function (position) {
        if (!_this.initialized) return;
        if (_this.slides.length === 0) return;
        _this.previousIndex = _this.activeIndex;
        if (typeof position === 'undefined') position = _this.getWrapperTranslate();
        if (position > 0) position = 0;
        var i;
        if (params.slidesPerView === 'auto') {
            var slidesOffset = 0;
            _this.activeIndex = _this.slidesGrid.indexOf(-position);
            if (_this.activeIndex < 0) {
                for (i = 0; i < _this.slidesGrid.length - 1; i++) {
                    if (-position > _this.slidesGrid[i] && -position < _this.slidesGrid[i + 1]) {
                        break;
                    }
                }
                var leftDistance = Math.abs(_this.slidesGrid[i] + position);
                var rightDistance = Math.abs(_this.slidesGrid[i + 1] + position);
                if (leftDistance <= rightDistance) _this.activeIndex = i;
                else _this.activeIndex = i + 1;
            }
        }
        else {
            _this.activeIndex = Math[params.visibilityFullFit ? 'ceil' : 'round'](-position / slideSize);
        }

        if (_this.activeIndex === _this.slides.length) _this.activeIndex = _this.slides.length - 1;
        if (_this.activeIndex < 0) _this.activeIndex = 0;

        // Check for slide
        if (!_this.slides[_this.activeIndex]) return;

        // Calc Visible slides
        _this.calcVisibleSlides(position);

        // Mark visible and active slides with additonal classes
        if (_this.support.classList) {
            var slide;
            for (i = 0; i < _this.slides.length; i++) {
                slide = _this.slides[i];
                slide.classList.remove(params.slideActiveClass);
                if (_this.visibleSlides.indexOf(slide) >= 0) {
                    slide.classList.add(params.slideVisibleClass);
                } else {
                    slide.classList.remove(params.slideVisibleClass);
                }
            }
            _this.slides[_this.activeIndex].classList.add(params.slideActiveClass);
        } else {
            var activeClassRegexp = new RegExp('\\s*' + params.slideActiveClass),
                inViewClassRegexp = new RegExp('\\s*' + params.slideVisibleClass),
                beforeClassRegexp = new RegExp('\\s*' + params.slideBeforeClass),
                afterClassRegexp = new RegExp('\\s*' + params.slideAfterClass);


            for (i = 0; i < _this.slides.length; i++) {
                _this.slides[i].className = _this.slides[i].className.replace(activeClassRegexp, '').replace(inViewClassRegexp, '').replace(afterClassRegexp, '').replace(beforeClassRegexp, '');
                if (_this.visibleSlides.indexOf(_this.slides[i]) >= 0) {
                    _this.slides[i].className += ' ' + params.slideVisibleClass;
                }
            }
            _this.slides[_this.activeIndex].className += ' ' + params.slideActiveClass;
        }

        //Update loop index
        if (params.loop) {
            var ls = _this.loopedSlides;
            _this.activeLoopIndex = _this.activeIndex - ls;
            if (_this.activeLoopIndex >= _this.slides.length - ls * 2) {
                _this.activeLoopIndex = _this.slides.length - ls * 2 - _this.activeLoopIndex;
            }
            if (_this.activeLoopIndex < 0) {
                _this.activeLoopIndex = _this.slides.length - ls * 2 + _this.activeLoopIndex;
            }
            if (_this.activeLoopIndex < 0) _this.activeLoopIndex = 0;
        }
        else {
            _this.activeLoopIndex = _this.activeIndex;
        }
        //Update Pagination
        if (params.pagination) {
            _this.updatePagination(position);
        }
    };
    /*==================================================
        Pagination
    ====================================================*/
    _this.createPagination = function (firstInit) {
        if (params.paginationClickable && _this.paginationButtons) {
            removePaginationEvents();
        }
        _this.paginationContainer = params.pagination.nodeType ? params.pagination : $$(params.pagination)[0];
        if (params.createPagination) {
            var paginationHTML = '';
            var numOfSlides = _this.slides.length;
            var numOfButtons = numOfSlides;
            if (params.loop) numOfButtons -= _this.loopedSlides * 2;
            for (var i = 0; i < numOfButtons; i++) {
                paginationHTML += '<' + params.paginationElement + ' class="' + params.paginationElementClass + '"></' + params.paginationElement + '>';
            }
            _this.paginationContainer.innerHTML = paginationHTML;
        }
        _this.paginationButtons = $$('.' + params.paginationElementClass, _this.paginationContainer);
        if (!firstInit) _this.updatePagination();
        _this.callPlugins('onCreatePagination');
        if (params.paginationClickable) {
            addPaginationEvents();
        }
    };
    function removePaginationEvents() {
        var pagers = _this.paginationButtons;
        if (pagers) {
            for (var i = 0; i < pagers.length; i++) {
                _this.h.removeEventListener(pagers[i], 'click', paginationClick);
            }
        }
    }
    function addPaginationEvents() {
        var pagers = _this.paginationButtons;
        if (pagers) {
            for (var i = 0; i < pagers.length; i++) {
                _this.h.addEventListener(pagers[i], 'click', paginationClick);
            }
        }
    }
    function paginationClick(e) {
        var index;
        var target = e.target || e.srcElement;
        var pagers = _this.paginationButtons;
        for (var i = 0; i < pagers.length; i++) {
            if (target === pagers[i]) index = i;
        }
        if (params.autoplay) _this.stopAutoplay(true);
        _this.swipeTo(index);
    }
    _this.updatePagination = function (position) {
        if (!params.pagination) return;
        if (_this.slides.length < 1) return;
        var activePagers = $$('.' + params.paginationActiveClass, _this.paginationContainer);
        if (!activePagers) return;

        //Reset all Buttons' class to not active
        var pagers = _this.paginationButtons;
        if (pagers.length === 0) return;
        for (var i = 0; i < pagers.length; i++) {
            pagers[i].className = params.paginationElementClass;
        }

        var indexOffset = params.loop ? _this.loopedSlides : 0;
        if (params.paginationAsRange) {
            if (!_this.visibleSlides) _this.calcVisibleSlides(position);
            //Get Visible Indexes
            var visibleIndexes = [];
            var j; // lopp index - avoid JSHint W004 / W038
            for (j = 0; j < _this.visibleSlides.length; j++) {
                var visIndex = _this.slides.indexOf(_this.visibleSlides[j]) - indexOffset;

                if (params.loop && visIndex < 0) {
                    visIndex = _this.slides.length - _this.loopedSlides * 2 + visIndex;
                }
                if (params.loop && visIndex >= _this.slides.length - _this.loopedSlides * 2) {
                    visIndex = _this.slides.length - _this.loopedSlides * 2 - visIndex;
                    visIndex = Math.abs(visIndex);
                }
                visibleIndexes.push(visIndex);
            }

            for (j = 0; j < visibleIndexes.length; j++) {
                if (pagers[visibleIndexes[j]]) pagers[visibleIndexes[j]].className += ' ' + params.paginationVisibleClass;
            }

            if (params.loop) {
                if (pagers[_this.activeLoopIndex] !== undefined) {
                    pagers[_this.activeLoopIndex].className += ' ' + params.paginationActiveClass;
                }
            }
            else {
                if (pagers[_this.activeIndex]) pagers[_this.activeIndex].className += ' ' + params.paginationActiveClass;
            }
        }
        else {
            if (params.loop) {
                if (pagers[_this.activeLoopIndex]) pagers[_this.activeLoopIndex].className += ' ' + params.paginationActiveClass + ' ' + params.paginationVisibleClass;
            }
            else {
                if (pagers[_this.activeIndex]) pagers[_this.activeIndex].className += ' ' + params.paginationActiveClass + ' ' + params.paginationVisibleClass;
            }
        }
    };
    _this.calcVisibleSlides = function (position) {
        var visibleSlides = [];
        var _slideLeft = 0, _slideSize = 0, _slideRight = 0;
        if (isH && _this.wrapperLeft > 0) position = position + _this.wrapperLeft;
        if (!isH && _this.wrapperTop > 0) position = position + _this.wrapperTop;

        for (var i = 0; i < _this.slides.length; i++) {
            _slideLeft += _slideSize;
            if (params.slidesPerView === 'auto')
                _slideSize  = isH ? _this.h.getWidth(_this.slides[i], true, params.roundLengths) : _this.h.getHeight(_this.slides[i], true, params.roundLengths);
            else _slideSize = slideSize;

            _slideRight = _slideLeft + _slideSize;
            var isVisibile = false;
            if (params.visibilityFullFit) {
                if (_slideLeft >= -position && _slideRight <= -position + containerSize) isVisibile = true;
                if (_slideLeft <= -position && _slideRight >= -position + containerSize) isVisibile = true;
            }
            else {
                if (_slideRight > -position && _slideRight <= ((-position + containerSize))) isVisibile = true;
                if (_slideLeft >= -position && _slideLeft < ((-position + containerSize))) isVisibile = true;
                if (_slideLeft < -position && _slideRight > ((-position + containerSize))) isVisibile = true;
            }

            if (isVisibile) visibleSlides.push(_this.slides[i]);

        }
        if (visibleSlides.length === 0) visibleSlides = [_this.slides[_this.activeIndex]];

        _this.visibleSlides = visibleSlides;
    };

    /*==========================================
        Autoplay
    ============================================*/
    var autoplayTimeoutId, autoplayIntervalId;
    _this.startAutoplay = function () {
        if (_this.support.transitions) {
            if (typeof autoplayTimeoutId !== 'undefined') return false;
            if (!params.autoplay) return;
            _this.callPlugins('onAutoplayStart');
            if (params.onAutoplayStart) _this.fireCallback(params.onAutoplayStart, _this);
            autoplay();
        }
        else {
            if (typeof autoplayIntervalId !== 'undefined') return false;
            if (!params.autoplay) return;
            _this.callPlugins('onAutoplayStart');
            if (params.onAutoplayStart) _this.fireCallback(params.onAutoplayStart, _this);
            autoplayIntervalId = setInterval(function () {
                if (params.loop) {
                    _this.fixLoop();
                    _this.swipeNext(true, true);
                }
                else if (!_this.swipeNext(true, true)) {
                    if (!params.autoplayStopOnLast) _this.swipeTo(0);
                    else {
                        clearInterval(autoplayIntervalId);
                        autoplayIntervalId = undefined;
                    }
                }
            }, params.autoplay);
        }
    };
    _this.stopAutoplay = function (internal) {
        if (_this.support.transitions) {
            if (!autoplayTimeoutId) return;
            if (autoplayTimeoutId) clearTimeout(autoplayTimeoutId);
            autoplayTimeoutId = undefined;
            if (internal && !params.autoplayDisableOnInteraction) {
                _this.wrapperTransitionEnd(function () {
                    autoplay();
                });
            }
            _this.callPlugins('onAutoplayStop');
            if (params.onAutoplayStop) _this.fireCallback(params.onAutoplayStop, _this);
        }
        else {
            if (autoplayIntervalId) clearInterval(autoplayIntervalId);
            autoplayIntervalId = undefined;
            _this.callPlugins('onAutoplayStop');
            if (params.onAutoplayStop) _this.fireCallback(params.onAutoplayStop, _this);
        }
    };
    function autoplay() {
        autoplayTimeoutId = setTimeout(function () {
            if (params.loop) {
                _this.fixLoop();
                _this.swipeNext(true, true);
            }
            else if (!_this.swipeNext(true, true)) {
                if (!params.autoplayStopOnLast) _this.swipeTo(0);
                else {
                    clearTimeout(autoplayTimeoutId);
                    autoplayTimeoutId = undefined;
                }
            }
            _this.wrapperTransitionEnd(function () {
                if (typeof autoplayTimeoutId !== 'undefined') autoplay();
            });
        }, params.autoplay);
    }
    /*==================================================
        Loop
    ====================================================*/
    _this.loopCreated = false;
    _this.removeLoopedSlides = function () {
        if (_this.loopCreated) {
            for (var i = 0; i < _this.slides.length; i++) {
                if (_this.slides[i].getData('looped') === true) _this.wrapper.removeChild(_this.slides[i]);
            }
        }
    };

    _this.createLoop = function () {
        if (_this.slides.length === 0) return;
        if (params.slidesPerView === 'auto') {
            _this.loopedSlides = params.loopedSlides || 1;
        }
        else {
            _this.loopedSlides = params.slidesPerView + params.loopAdditionalSlides;
        }

        if (_this.loopedSlides > _this.slides.length) {
            _this.loopedSlides = _this.slides.length;
        }

        var slideFirstHTML = '',
            slideLastHTML = '',
            i;
        var slidesSetFullHTML = '';
        /**
                loopedSlides is too large if loopAdditionalSlides are set.
                Need to divide the slides by maximum number of slides existing.

                @author        Tomaz Lovrec <tomaz.lovrec@blanc-noir.at>
        */
        var numSlides = _this.slides.length;
        var fullSlideSets = Math.floor(_this.loopedSlides / numSlides);
        var remainderSlides = _this.loopedSlides % numSlides;
        // assemble full sets of slides
        for (i = 0; i < (fullSlideSets * numSlides); i++) {
            var j = i;
            if (i >= numSlides) {
                var over = Math.floor(i / numSlides);
                j = i - (numSlides * over);
            }
            slidesSetFullHTML += _this.slides[j].outerHTML;
        }
        // assemble remainder slides
        // assemble remainder appended to existing slides
        for (i = 0; i < remainderSlides;i++) {
            slideLastHTML += addClassToHtmlString(params.slideDuplicateClass, _this.slides[i].outerHTML);
        }
        // assemble slides that get preppended to existing slides
        for (i = numSlides - remainderSlides; i < numSlides;i++) {
            slideFirstHTML += addClassToHtmlString(params.slideDuplicateClass, _this.slides[i].outerHTML);
        }
        // assemble all slides
        var slides = slideFirstHTML + slidesSetFullHTML + wrapper.innerHTML + slidesSetFullHTML + slideLastHTML;
        // set the slides
        wrapper.innerHTML = slides;

        _this.loopCreated = true;
        _this.calcSlides();

        //Update Looped Slides with special class
        for (i = 0; i < _this.slides.length; i++) {
            if (i < _this.loopedSlides || i >= _this.slides.length - _this.loopedSlides) _this.slides[i].setData('looped', true);
        }
        _this.callPlugins('onCreateLoop');

    };

    _this.fixLoop = function () {
        var newIndex;
        //Fix For Negative Oversliding
        if (_this.activeIndex < _this.loopedSlides) {
            newIndex = _this.slides.length - _this.loopedSlides * 3 + _this.activeIndex;
            _this.swipeTo(newIndex, 0, false);
        }
        //Fix For Positive Oversliding
        else if ((params.slidesPerView === 'auto' && _this.activeIndex >= _this.loopedSlides * 2) || (_this.activeIndex > _this.slides.length - params.slidesPerView * 2)) {
            newIndex = -_this.slides.length + _this.activeIndex + _this.loopedSlides;
            _this.swipeTo(newIndex, 0, false);
        }
    };

    /*==================================================
        Slides Loader
    ====================================================*/
    _this.loadSlides = function () {
        var slidesHTML = '';
        _this.activeLoaderIndex = 0;
        var slides = params.loader.slides;
        var slidesToLoad = params.loader.loadAllSlides ? slides.length : params.slidesPerView * (1 + params.loader.surroundGroups);
        for (var i = 0; i < slidesToLoad; i++) {
            if (params.loader.slidesHTMLType === 'outer') slidesHTML += slides[i];
            else {
                slidesHTML += '<' + params.slideElement + ' class="' + params.slideClass + '" data-swiperindex="' + i + '">' + slides[i] + '</' + params.slideElement + '>';
            }
        }
        _this.wrapper.innerHTML = slidesHTML;
        _this.calcSlides(true);
        //Add permanent transitionEnd callback
        if (!params.loader.loadAllSlides) {
            _this.wrapperTransitionEnd(_this.reloadSlides, true);
        }
    };

    _this.reloadSlides = function () {
        var slides = params.loader.slides;
        var newActiveIndex = parseInt(_this.activeSlide().data('swiperindex'), 10);
        if (newActiveIndex < 0 || newActiveIndex > slides.length - 1) return; //<-- Exit
        _this.activeLoaderIndex = newActiveIndex;
        var firstIndex = Math.max(0, newActiveIndex - params.slidesPerView * params.loader.surroundGroups);
        var lastIndex = Math.min(newActiveIndex + params.slidesPerView * (1 + params.loader.surroundGroups) - 1, slides.length - 1);
        //Update Transforms
        if (newActiveIndex > 0) {
            var newTransform = -slideSize * (newActiveIndex - firstIndex);
            _this.setWrapperTranslate(newTransform);
            _this.setWrapperTransition(0);
        }
        var i; // loop index
        //New Slides
        if (params.loader.logic === 'reload') {
            _this.wrapper.innerHTML = '';
            var slidesHTML = '';
            for (i = firstIndex; i <= lastIndex; i++) {
                slidesHTML += params.loader.slidesHTMLType === 'outer' ? slides[i] : '<' + params.slideElement + ' class="' + params.slideClass + '" data-swiperindex="' + i + '">' + slides[i] + '</' + params.slideElement + '>';
            }
            _this.wrapper.innerHTML = slidesHTML;
        }
        else {
            var minExistIndex = 1000;
            var maxExistIndex = 0;

            for (i = 0; i < _this.slides.length; i++) {
                var index = _this.slides[i].data('swiperindex');
                if (index < firstIndex || index > lastIndex) {
                    _this.wrapper.removeChild(_this.slides[i]);
                }
                else {
                    minExistIndex = Math.min(index, minExistIndex);
                    maxExistIndex = Math.max(index, maxExistIndex);
                }
            }
            for (i = firstIndex; i <= lastIndex; i++) {
                var newSlide;
                if (i < minExistIndex) {
                    newSlide = document.createElement(params.slideElement);
                    newSlide.className = params.slideClass;
                    newSlide.setAttribute('data-swiperindex', i);
                    newSlide.innerHTML = slides[i];
                    _this.wrapper.insertBefore(newSlide, _this.wrapper.firstChild);
                }
                if (i > maxExistIndex) {
                    newSlide = document.createElement(params.slideElement);
                    newSlide.className = params.slideClass;
                    newSlide.setAttribute('data-swiperindex', i);
                    newSlide.innerHTML = slides[i];
                    _this.wrapper.appendChild(newSlide);
                }
            }
        }
        //reInit
        _this.reInit(true);
    };

    /*==================================================
        Make Swiper
    ====================================================*/
    function makeSwiper() {
        _this.calcSlides();
        if (params.loader.slides.length > 0 && _this.slides.length === 0) {
            _this.loadSlides();
        }
        if (params.loop) {
            _this.createLoop();
        }
        _this.init();
        initEvents();
        if (params.pagination) {
            _this.createPagination(true);
        }

        if (params.loop || params.initialSlide > 0) {
            _this.swipeTo(params.initialSlide, 0, false);
        }
        else {
            _this.updateActiveSlide(0);
        }
        if (params.autoplay) {
            _this.startAutoplay();
        }
        /**
         * Set center slide index.
         *
         * @author        Tomaz Lovrec <tomaz.lovrec@gmail.com>
         */
        _this.centerIndex = _this.activeIndex;

        // Callbacks
        if (params.onSwiperCreated) _this.fireCallback(params.onSwiperCreated, _this);
        _this.callPlugins('onSwiperCreated');
    }

    makeSwiper();
};

Swiper.prototype = {
    plugins : {},

    /*==================================================
        Wrapper Operations
    ====================================================*/
    wrapperTransitionEnd : function (callback, permanent) {
        'use strict';
        var a = this,
            el = a.wrapper,
            events = ['webkitTransitionEnd', 'transitionend', 'oTransitionEnd', 'MSTransitionEnd', 'msTransitionEnd'],
            i;

        function fireCallBack(e) {
            if (e.target !== el) return;
            callback(a);
            if (a.params.queueEndCallbacks) a._queueEndCallbacks = false;
            if (!permanent) {
                for (i = 0; i < events.length; i++) {
                    a.h.removeEventListener(el, events[i], fireCallBack);
                }
            }
        }

        if (callback) {
            for (i = 0; i < events.length; i++) {
                a.h.addEventListener(el, events[i], fireCallBack);
            }
        }
    },

    getWrapperTranslate : function (axis) {
        'use strict';
        var el = this.wrapper,
            matrix, curTransform, curStyle, transformMatrix;

        // automatic axis detection
        if (typeof axis === 'undefined') {
            axis = this.params.mode === 'horizontal' ? 'x' : 'y';
        }

        if (this.support.transforms && this.params.useCSS3Transforms) {
            curStyle = window.getComputedStyle(el, null);
            if (window.WebKitCSSMatrix) {
                // Some old versions of Webkit choke when 'none' is passed; pass
                // empty string instead in this case
                transformMatrix = new WebKitCSSMatrix(curStyle.webkitTransform === 'none' ? '' : curStyle.webkitTransform);
            }
            else {
                transformMatrix = curStyle.MozTransform || curStyle.OTransform || curStyle.MsTransform || curStyle.msTransform  || curStyle.transform || curStyle.getPropertyValue('transform').replace('translate(', 'matrix(1, 0, 0, 1,');
                matrix = transformMatrix.toString().split(',');
            }

            if (axis === 'x') {
                //Latest Chrome and webkits Fix
                if (window.WebKitCSSMatrix)
                    curTransform = transformMatrix.m41;
                //Crazy IE10 Matrix
                else if (matrix.length === 16)
                    curTransform = parseFloat(matrix[12]);
                //Normal Browsers
                else
                    curTransform = parseFloat(matrix[4]);
            }
            if (axis === 'y') {
                //Latest Chrome and webkits Fix
                if (window.WebKitCSSMatrix)
                    curTransform = transformMatrix.m42;
                //Crazy IE10 Matrix
                else if (matrix.length === 16)
                    curTransform = parseFloat(matrix[13]);
                //Normal Browsers
                else
                    curTransform = parseFloat(matrix[5]);
            }
        }
        else {
            if (axis === 'x') curTransform = parseFloat(el.style.left, 10) || 0;
            if (axis === 'y') curTransform = parseFloat(el.style.top, 10) || 0;
        }
        return curTransform || 0;
    },

    setWrapperTranslate : function (x, y, z) {
        'use strict';
        var es = this.wrapper.style,
            coords = {x: 0, y: 0, z: 0},
            translate;

        // passed all coordinates
        if (arguments.length === 3) {
            coords.x = x;
            coords.y = y;
            coords.z = z;
        }

        // passed one coordinate and optional axis
        else {
            if (typeof y === 'undefined') {
                y = this.params.mode === 'horizontal' ? 'x' : 'y';
            }
            coords[y] = x;
        }

        if (this.support.transforms && this.params.useCSS3Transforms) {
            translate = this.support.transforms3d ? 'translate3d(' + coords.x + 'px, ' + coords.y + 'px, ' + coords.z + 'px)' : 'translate(' + coords.x + 'px, ' + coords.y + 'px)';
            es.webkitTransform = es.MsTransform = es.msTransform = es.MozTransform = es.OTransform = es.transform = translate;
        }
        else {
            es.left = coords.x + 'px';
            es.top  = coords.y + 'px';
        }
        this.callPlugins('onSetWrapperTransform', coords);
        if (this.params.onSetWrapperTransform) this.fireCallback(this.params.onSetWrapperTransform, this, coords);
    },

    setWrapperTransition : function (duration) {
        'use strict';
        var es = this.wrapper.style;
        es.webkitTransitionDuration = es.MsTransitionDuration = es.msTransitionDuration = es.MozTransitionDuration = es.OTransitionDuration = es.transitionDuration = (duration / 1000) + 's';
        this.callPlugins('onSetWrapperTransition', {duration: duration});
        if (this.params.onSetWrapperTransition) this.fireCallback(this.params.onSetWrapperTransition, this, duration);

    },

    /*==================================================
        Helpers
    ====================================================*/
    h : {
        getWidth: function (el, outer, round) {
            'use strict';
            var width = window.getComputedStyle(el, null).getPropertyValue('width');
            var returnWidth = parseFloat(width);
            //IE Fixes
            if (isNaN(returnWidth) || width.indexOf('%') > 0 || returnWidth < 0) {
                returnWidth = el.offsetWidth - parseFloat(window.getComputedStyle(el, null).getPropertyValue('padding-left')) - parseFloat(window.getComputedStyle(el, null).getPropertyValue('padding-right'));
            }
            if (outer) returnWidth += parseFloat(window.getComputedStyle(el, null).getPropertyValue('padding-left')) + parseFloat(window.getComputedStyle(el, null).getPropertyValue('padding-right'));
            if (round) return Math.ceil(returnWidth);
            else return returnWidth;
        },
        getHeight: function (el, outer, round) {
            'use strict';
            if (outer) return el.offsetHeight;

            var height = window.getComputedStyle(el, null).getPropertyValue('height');
            var returnHeight = parseFloat(height);
            //IE Fixes
            if (isNaN(returnHeight) || height.indexOf('%') > 0 || returnHeight < 0) {
                returnHeight = el.offsetHeight - parseFloat(window.getComputedStyle(el, null).getPropertyValue('padding-top')) - parseFloat(window.getComputedStyle(el, null).getPropertyValue('padding-bottom'));
            }
            if (outer) returnHeight += parseFloat(window.getComputedStyle(el, null).getPropertyValue('padding-top')) + parseFloat(window.getComputedStyle(el, null).getPropertyValue('padding-bottom'));
            if (round) return Math.ceil(returnHeight);
            else return returnHeight;
        },
        getOffset: function (el) {
            'use strict';
            var box = el.getBoundingClientRect();
            var body = document.body;
            var clientTop  = el.clientTop  || body.clientTop  || 0;
            var clientLeft = el.clientLeft || body.clientLeft || 0;
            var scrollTop  = window.pageYOffset || el.scrollTop;
            var scrollLeft = window.pageXOffset || el.scrollLeft;
            if (document.documentElement && !window.pageYOffset) {
                //IE7-8
                scrollTop  = document.documentElement.scrollTop;
                scrollLeft = document.documentElement.scrollLeft;
            }
            return {
                top: box.top  + scrollTop  - clientTop,
                left: box.left + scrollLeft - clientLeft
            };
        },
        windowWidth : function () {
            'use strict';
            if (window.innerWidth) return window.innerWidth;
            else if (document.documentElement && document.documentElement.clientWidth) return document.documentElement.clientWidth;
        },
        windowHeight : function () {
            'use strict';
            if (window.innerHeight) return window.innerHeight;
            else if (document.documentElement && document.documentElement.clientHeight) return document.documentElement.clientHeight;
        },
        windowScroll : function () {
            'use strict';
            if (typeof pageYOffset !== 'undefined') {
                return {
                    left: window.pageXOffset,
                    top: window.pageYOffset
                };
            }
            else if (document.documentElement) {
                return {
                    left: document.documentElement.scrollLeft,
                    top: document.documentElement.scrollTop
                };
            }
        },

        addEventListener : function (el, event, listener, useCapture) {
            'use strict';
            if (typeof useCapture === 'undefined') {
                useCapture = false;
            }

            if (el.addEventListener) {
                el.addEventListener(event, listener, useCapture);
            }
            else if (el.attachEvent) {
                el.attachEvent('on' + event, listener);
            }
        },

        removeEventListener : function (el, event, listener, useCapture) {
            'use strict';
            if (typeof useCapture === 'undefined') {
                useCapture = false;
            }

            if (el.removeEventListener) {
                el.removeEventListener(event, listener, useCapture);
            }
            else if (el.detachEvent) {
                el.detachEvent('on' + event, listener);
            }
        }
    },
    setTransform : function (el, transform) {
        'use strict';
        var es = el.style;
        es.webkitTransform = es.MsTransform = es.msTransform = es.MozTransform = es.OTransform = es.transform = transform;
    },
    setTranslate : function (el, translate) {
        'use strict';
        var es = el.style;
        var pos = {
            x : translate.x || 0,
            y : translate.y || 0,
            z : translate.z || 0
        };
        var transformString = this.support.transforms3d ? 'translate3d(' + (pos.x) + 'px,' + (pos.y) + 'px,' + (pos.z) + 'px)' : 'translate(' + (pos.x) + 'px,' + (pos.y) + 'px)';
        es.webkitTransform = es.MsTransform = es.msTransform = es.MozTransform = es.OTransform = es.transform = transformString;
        if (!this.support.transforms) {
            es.left = pos.x + 'px';
            es.top = pos.y + 'px';
        }
    },
    setTransition : function (el, duration) {
        'use strict';
        var es = el.style;
        es.webkitTransitionDuration = es.MsTransitionDuration = es.msTransitionDuration = es.MozTransitionDuration = es.OTransitionDuration = es.transitionDuration = duration + 'ms';
    },
    /*==================================================
        Feature Detection
    ====================================================*/
    support: {

        touch : (window.Modernizr && Modernizr.touch === true) || (function () {
            'use strict';
            return !!(('ontouchstart' in window) || window.DocumentTouch && document instanceof DocumentTouch);
        })(),

        transforms3d : (window.Modernizr && Modernizr.csstransforms3d === true) || (function () {
            'use strict';
            var div = document.createElement('div').style;
            return ('webkitPerspective' in div || 'MozPerspective' in div || 'OPerspective' in div || 'MsPerspective' in div || 'perspective' in div);
        })(),

        transforms : (window.Modernizr && Modernizr.csstransforms === true) || (function () {
            'use strict';
            var div = document.createElement('div').style;
            return ('transform' in div || 'WebkitTransform' in div || 'MozTransform' in div || 'msTransform' in div || 'MsTransform' in div || 'OTransform' in div);
        })(),

        transitions : (window.Modernizr && Modernizr.csstransitions === true) || (function () {
            'use strict';
            var div = document.createElement('div').style;
            return ('transition' in div || 'WebkitTransition' in div || 'MozTransition' in div || 'msTransition' in div || 'MsTransition' in div || 'OTransition' in div);
        })(),

        classList : (function () {
            'use strict';
            var div = document.createElement('div');
            return 'classList' in div;
        })()
    },

    browser : {

        ie8 : (function () {
            'use strict';
            var rv = -1; // Return value assumes failure.
            if (navigator.appName === 'Microsoft Internet Explorer') {
                var ua = navigator.userAgent;
                var re = new RegExp(/MSIE ([0-9]{1,}[\.0-9]{0,})/);
                if (re.exec(ua) !== null)
                    rv = parseFloat(RegExp.$1);
            }
            return rv !== -1 && rv < 9;
        })(),

        ie10 : window.navigator.msPointerEnabled,
        ie11 : window.navigator.pointerEnabled
    }
};

/*=========================
  jQuery & Zepto Plugins
  ===========================*/
if (window.jQuery || window.Zepto) {
    (function ($) {
        'use strict';
        $.fn.swiper = function (params) {
            var firstInstance;
            this.each(function (i) {
                var that = $(this);
                var s = new Swiper(that[0], params);
                if (!i) firstInstance = s;
                that.data('swiper', s);
            });
            return firstInstance;
        };
    })(window.jQuery || window.Zepto);
}

// component
if (typeof(module) !== 'undefined')
{
    module.exports = Swiper;
}

// requirejs support
if (typeof define === 'function' && define.amd) {
    define([], function () {
        'use strict';
        return Swiper;
    });
};/*!
 * string_score.js: String Scoring Algorithm 0.1.22
 *
 * http://joshaven.com/string_score
 * https://github.com/joshaven/string_score
 *
 * Copyright (C) 2009-2014 Joshaven Potter <yourtech@gmail.com>
 * Special thanks to all of the contributors listed here https://github.com/joshaven/string_score
 * MIT License: http://opensource.org/licenses/MIT
 *
 * Date: Tue Mar 1 2011
 * Updated: Tue Mar 10 2015
*/

/*jslint nomen:true, white:true, browser:true,devel:true */

/**
 * Scores a string against another string.
 *    'Hello World'.score('he');         //=> 0.5931818181818181
 *    'Hello World'.score('Hello');    //=> 0.7318181818181818
 */
String.prototype.score = function (word, fuzziness) {
  'use strict';

  // If the string is equal to the word, perfect match.
  if (this === word) { return 1; }

  //if it's not a perfect match and is empty return 0
  if (word === "") { return 0; }

  var runningScore = 0,
      charScore,
      finalScore,
      string = this,
      lString = string.toLowerCase(),
      strLength = string.length,
      lWord = word.toLowerCase(),
      wordLength = word.length,
      idxOf,
      startAt = 0,
      fuzzies = 1,
      fuzzyFactor,
      i;

  // Cache fuzzyFactor for speed increase
  if (fuzziness) { fuzzyFactor = 1 - fuzziness; }

  // Walk through word and add up scores.
  // Code duplication occurs to prevent checking fuzziness inside for loop
  if (fuzziness) {
    for (i = 0; i < wordLength; i+=1) {

      // Find next first case-insensitive match of a character.
      idxOf = lString.indexOf(lWord[i], startAt);

      if (idxOf === -1) {
        fuzzies += fuzzyFactor;
      } else {
        if (startAt === idxOf) {
          // Consecutive letter & start-of-string Bonus
          charScore = 0.7;
        } else {
          charScore = 0.1;

          // Acronym Bonus
          // Weighing Logic: Typing the first character of an acronym is as if you
          // preceded it with two perfect character matches.
          if (string[idxOf - 1] === ' ') { charScore += 0.8; }
        }

        // Same case bonus.
        if (string[idxOf] === word[i]) { charScore += 0.1; }

        // Update scores and startAt position for next round of indexOf
        runningScore += charScore;
        startAt = idxOf + 1;
      }
    }
  } else {
    for (i = 0; i < wordLength; i+=1) {
      idxOf = lString.indexOf(lWord[i], startAt);
      if (-1 === idxOf) { return 0; }

      if (startAt === idxOf) {
        charScore = 0.7;
      } else {
        charScore = 0.1;
        if (string[idxOf - 1] === ' ') { charScore += 0.8; }
      }
      if (string[idxOf] === word[i]) { charScore += 0.1; }
      runningScore += charScore;
      startAt = idxOf + 1;
    }
  }

  // Reduce penalty for longer strings.
  finalScore = 0.5 * (runningScore / strLength    + runningScore / wordLength) / fuzzies;

  if ((lWord[0] === lString[0]) && (finalScore < 0.85)) {
    finalScore += 0.15;
  }

  return finalScore;
};;// Place any jQuery/helper plugins in here.
(function($){

    var topics = {},
        ytdomregex = new RegExp('https?://(www.|m.)?youtube|youtu.be'),
        vimeodomregex = new RegExp('https?://(player.)?vimeo.com'),
        foddomregex = new RegExp('(www.)?funnyordie.com'),
        articles = new RegExp('^(a|in|an|if|to|and|the|or|but|from)$');

window.usstates = {
    AL: "Alabama",
    AK: "Alaska",
    AS: "American Samoa",
    AZ: "Arizona",
    AR: "Arkansas",
    CA: "California",
    CO: "Colorado",
    CT: "Connecticut",
    DE: "Delaware",
    DC: "D.C.",
    FL: "Florida",
    FM: "F.S. of Micronesia",
    GA: "Georgia",
    GU: "Guam",
    HI: "Hawaii",
    ID: "Idaho",
    IL: "Illinois",
    IN: "Indiana",
    IA: "Iowa",
    KS: "Kansas",
    KY: "Kentucky",
    LA: "Louisiana",
    ME: "Maine",
    MD: "Maryland",
    MA: "Massachusetts",
    MH: "Marshall Islands",
    MI: "Michigan",
    MN: "Minnesota",
    MS: "Mississippi",
    MO: "Missouri",
    MT: "Montana",
    NE: "Nebraska",
    NV: "Nevada",
    NH: "New Hampshire",
    NJ: "New Jersey",
    NM: "New Mexico",
    NY: "New York",
    NC: "North Carolina",
    ND: "North Dakota",
    MP: "N. Mariana Islands",
    OH: "Ohio",
    OK: "Oklahoma",
    OR: "Oregon",
    PA: "Pennsylvania",
    PR: "Puerto Rico",
    PW: "Palau",
    RI: "Rhode Island",
    SC: "South Carolina",
    SD: "South Dakota",
    TN: "Tennessee",
    TX: "Texas",
    UT: "Utah",
    VT: "Vermont",
    VA: "Virginia",
    VI: "Virgin Islands",
    WA: "Washington",
    WV: "West Virginia",
    WI: "Wisconsin",
    WY: "Wyoming"
};

    //add pretty timeouts
    $.wait = function(time) {
        return $.Deferred(function(dfd){
            if(time){
                setTimeout(dfd.resolve, time);
            }else{
                dfd.resolve();
            }
        });
    };

    //add basic pub sub, for error-free behavior chains
    $.Topic = function( id ) {

        var callbacks,
            topic = id && topics[ id ];
        if ( !topic ) {
            callbacks = $.Callbacks();
            topic = {
                publish: callbacks.fire,
                subscribe: callbacks.add,
                unsubscribe: callbacks.remove
            };
            if ( id ) {
                topics[ id ] = topic;
            }
        }
        return topic;
    };

    $.fn.serializeObject = function(){

        var o = {};

        var a = this.serializeArray();

        $.each(a, function() {

            if (o[this.name] !== undefined) {

                if (!o[this.name].push) {

                    o[this.name] = [o[this.name]];

                }

                o[this.name].push(this.value || '');

            } else {

                o[this.name] = this.value || '';

            }

        });

        return o;
    };

(function($){
    var find = 'find',
        transversal = new RegExp('^(andSelf|children|closest|filter|find|first|has|last|next|nextAll|nextUntil|not|offsetParent|parent|parents|parenstUntil|prev|prevAll|prevUntil|siblings)$');
        //nah, lets not do that, lets allow any method that exists to run on the this object, yeah?


    $.extend($.fn,{

        /*because we have [hidden] defined as display none, these are more performant than the native methods*/
        hide: function() {
            return this.attr('hidden', true);
        },
        show: function() {
            return this.attr('hidden', false);
        },

        /**/
        or: function(how, selector /*[selector], or [how, selector]*/){
            var what;
            if(this.length){
                return this;//if we already have something, bypass this entire method!
            }else{
                what = this.end();
                return what[//working on this element,
                    what[how]?//does the first arg match a jQuery method?
                        how://if so, we'll use that
                        'find'//otherwise, use find
                ](selector||how);//and pipe the selector into the method
            }
        },
        orAdd: function(fallback){
            return this.length ?
                this ://if we found anything already, just return it
                $( $.apply($, $.isArray(fallback)?fallback:[fallback] || []) );//if not, use the fallback arguments to create and return it
        },
        orAddAnd: function(fallback,how,toWhat){
            return this.length?
                this:
                this.orAdd(fallback)[how||'appendTo']( toWhat||this.end() );
        },
        orAppend: function( fallback, toWhat ) {
            return this.orAddAnd(fallback,undefined, toWhat||this.end());
        },
        orPrepend: function( fallback, toWhat ) {
            return this.orAddAnd(fallback,'prependTo', toWhat||this.end());
        },
        orBefore: function( fallback, what ) {
            return this.orAddAnd(fallback,'insertBefore', what||this.end());
        },
        orAfter: function( fallback, what ) {
            return this.orAddAnd(fallback,'insertAfter', what||this.end());
        }
    });
}(jQuery));

    _.linkregex = new RegExp("(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|]","ig");
    _.mixin({
        cleanObj: function(obj){//allows us to oneline turning object keys truthy or falsy
            var nobj = {};
            _.each(obj,function(v,k){
                if(!!v){
                    nobj[k]=v;
                }
            });
            return nobj;
        },
        clamp: function clamp(num, min, max) {
            min = min||0;
            max = max?(max>0?max:0):0;
            return num < min ? min : num > max ? max : num;
        },
        chunk: function(arr,size){
            var arrays = [];
            size=size||3;
            while (arr.length > 0){
                arrays.push(arr.splice(0, size));
            }
            return arrays;
        },
        falseArr: function(arr){
            return _.map(arr,function(i){
                return (_.isArray(i) && i.length===0)?false:i;
            });
        },
        capCase: function(string) {
            return string?_.map(string.split(' '),function(word){
                return !articles.test(word)?word.charAt(0).toUpperCase() + word.substring(1):word;
            }).join(' '):'';
        },
        urlslug: function(string) {
            return string?string.replace(/\s/g,'_').replace(/[^\w-]+/g,''):'';
        },
        today: function(){
            var today = new Date();

            return today.getFullYear()+'-'+("0"+(today.getMonth()+1)).slice(-2)+'-'+("0"+today.getDate()).slice(-2);
        },
        fullencode: function(string){
            return encodeURIComponent(string)
                .replace(/!/g, '%21')
                .replace(/'/g, '%27')
                .replace(/\(/g, '%28')
                .replace(/\)/g, '%29')
                .replace(/\*/g, '%2A');
        },
        sourceLink: function(string,sourceObj){
            return (!_.isObject(sourceObj) || !string)?string:string.replace(_.linkregex,
                function(match){
                    return (match + ((match.indexOf('?')>-1) ?'&':'?')+$.param(sourceObj)).replace('www.sc.bsd.net','sc-staging.dev.bsd.net');
            });
        },
        sourceLinkEncode: function(string,sourceObj){
            return _.fullencode(_.sourceLink(string,sourceObj));
        },
        mergeTo: function(ctx, keys){
            return function(d){
                _.extend(ctx, _.pick(d,keys));
                return d;
            };
        },
        scoreByTerms: function(searchterms, target, fuzzy){
            searchterms = String(searchterms||'').split(/\s/);
            target = String(target||'');
            fuzzy = fuzzy||0.5;
            return _.reduce(searchterms, function(memo,searchterm){
                return memo + target.score(searchterm, fuzzy);
            },0);
        },
        lookup: function(obj, keystring){//search deep into an object

            if(keystring){
                if(keystring.indexOf(".") === -1) {
                    return obj && obj[keystring];
                }else{
                    var keys = keystring.split(".").reverse();
                    while(keys.length && obj) {
                        obj = obj[keys[keys.length - 1]];
                        keys.pop();
                    }
                }
            }
            return obj;
        },
        unescape: function(string){
            return $('<textarea>'+string+'</textarea>').val();
        },
        pluckDeep: function(obj, keystring) {//gets all non-falsey things from an array of objects
            if (keystring.indexOf(".") === -1) {
                obj = _.compact(_.map(obj, function(value){ return value[keystring]; }));
            }else{
                var keys = keystring.split(".").reverse();
                while(keys.length) {
                    obj = _.compact(_.pluck(obj, keys[keys.length - 1]));
                    keys.pop();
                }
            }
            return obj;
        },
        videoInfoFromUrl: function(href){
            href = (href||'')+'';
            var vid = {
                href: href,
                ops: href.indexOf('?')>-1? decodeParam(href.split('?').slice(-1)[0]):{}
            }, 
            urlParts;

            $.extend(vid.ops,{autoplay:0,hd:1,autohide:1,rel:0,wmode:'opaque'});

            if(href.indexOf('/')===-1){
                vid.slug = href;
                if(/^[0-9]+$/.test(vid.slug)){
                    vid.type = 'vimeo';
                    vid.nonyt = true;
                }else{
                    vid.type = 'youtube';
                    vid.nonyt = false;
                }
            }else{
                if (href.match(ytdomregex)) {
                    var embed = href.replace('//m.','//www.').split(/embed\//)[1],
                        shortl = href.split(/v\/|v=|youtu\.be\//)[1];
                    if(embed || shortl){
                        $.extend(vid,{
                            type: "youtube",
                            slug: href.match('embed')? href.split(/embed\//)[1].split('"')[0] : href.split(/v\/|v=|youtu\.be\//)[1].split(/[?&]/)[0]
                        });
                    }
                }
                else if (href.match(vimeodomregex)) {
                    $.extend(vid,{
                        type: 'vimeo',
                        nonyt: true,
                        slug: href.split(/[?&]/)[0].split("/").pop()
                    });
                }
                else if (href.match(foddomregex)) {
                    urlParts = href.split(/[?&]/)[0].split("/").reverse();

                    $.extend(vid,{
                        type: 'fod',
                        nonyt: true,
                        isFOD: true,
                        slug: urlParts[0].indexOf('-')>-1 ? urlParts[1] : urlParts[0]
                    });
                }
            }

            vid.embed = '<iframe class="video-iframe" src="'+(
                    (vid.nonyt) ?
                        ((vid.isFOD) ?
                            ('//www.funnyordie.com/embed/'+vid.slug) :
                            ('//player.vimeo.com/video/'+vid.slug+'?'+$.param(vid.ops))    ) :
                        ('//www.youtube.com/embed/'+vid.slug+'?'+$.param(vid.ops))
            )+'" onload="this.style.visibility=\'visible\';" frameborder="0" scrolling="0" allowfullscreen allowtransparency="true"></iframe>';
            return vid.slug?vid:{};
        }
    });

}(jQuery));
/*
May need this?
;(function (window, document, undefined) {
    // Checks to make sure Backbone, Backbone.Model and the private validate method are on the page
    if(window.Backbone && window.Backbone.Model && window.Backbone.Model.prototype._validate) {
        // Run validation against the next complete set of model attributes,
        // returning `true` if all is well. If a specific `error` callback has
        // been passed, call that instead of firing the general `"error"` event.
        window.Backbone.Model.prototype._validate = function(attrs, options) {
            if (!options.validate || !this.validate) return true;
            if (options.validateAll !== false) {
                attrs = _.extend({}, this.attributes, attrs);
            }
            var error = this.validationError = this.validate(attrs, options) || null;
            if (!error) return true;
            this.trigger('invalid', this, error, _.extend(options || {}, {validationError: error}));
            return false;
        };
    }
}(window, document));
*/



//adds before and after methods to Backbone's router
/*global Backbone:false, _: false, console: false*/

(function(Backbone, _) {

  // Save a reference to the original route method to be called
  // after we pave it over.
  var originalRoute = Backbone.Router.prototype.route;

  // Create a reusable no operation func for the case where a before
  // or after filter is not set. Backbone or Underscore should have
  // a global one of these in my opinion.
  var nop = function(){};

  // Extend the router prototype with a default before function,
  // a default after function, and a pave over of _bindRoutes.
  _.extend(Backbone.Router.prototype, {

    // Add default before filter.
    before: nop,

    // Add default after filter.
    after: nop,

    // Pave over Backbone.Router.prototype.route, the public method used
    // for adding routes to a router instance on the fly, and the
    // method which backbone uses internally for binding routes to handlers
    // on the Backbone.history singleton once it's instantiated.
    route: function(route, name, callback) {

      // If there is no callback present for this route, then set it to
      // be the name that was set in the routes property of the constructor,
      // or the name arguement of the route method invocation. This is what
      // Backbone.Router.route already does. We need to do it again,
      // because we are about to wrap the callback in a function that calls
      // the before and after filters as well as the original callback that
      // was passed in.
      if( !callback ){
        callback = this[ name ];
      }

      // Create a new callback to replace the original callback that calls
      // the before and after filters as well as the original callback
      // internally.
      var wrappedCallback = _.bind( function() {

        // Call the before filter and if it returns false, run the
        // route's original callback, and after filter. This allows
        // the user to return false from within the before filter
        // to prevent the original route callback and after
        // filter from running.
        var callbackArgs = [ route, _.toArray(arguments) ],
            beforeCallback,
            afterCallback;

        if ( _.isFunction(this.before) ) {

          // If the before filter is just a single function, then call
          // it with the arguments.
          beforeCallback = this.before;
        } else if ( typeof this.before[route] !== "undefined" ) {

          // otherwise, find the appropriate callback for the route name
          // and call that.
          beforeCallback = this.before[route];
        } else {

          // otherwise, if we have a hash of routes, but no before callback
          // for this route, just use a nop function.
          beforeCallback = nop;
        }

        // If the before callback fails during its execusion (by returning)
        // false, then do not proceed with the route triggering.
        if ( beforeCallback.apply(this, callbackArgs) === false ) {
          return;
        }

        // If the callback exists, then call it. This means that the before
        // and after filters will be called whether or not an actual
        // callback function is supplied to handle a given route.
        if( callback ) {
          callback.apply( this, arguments );
        }

        if ( _.isFunction(this.after) ) {

          // If the after filter is a single funciton, then call it with
          // the proper arguments.
          afterCallback = this.after;

        } else if ( typeof this.after[route] !== "undefined" ) {

          // otherwise if we have a hash of routes, call the appropriate
          // callback based on the route name.
          afterCallback = this.after[route];

        } else {

          // otherwise, if we have a has of routes but no after callback
          // for this route, just use the nop function.
          afterCallback = nop;
        }

        // Call the after filter.
        afterCallback.apply( this, callbackArgs );

      }, this);

      // Call our original route, replacing the callback that was originally
      // passed in when Backbone.Router.route was invoked with our wrapped
      // callback that calls the before and after callbacks as well as the
      // original callback.
      return originalRoute.call( this, route, name, wrappedCallback );
    }

  });

}(Backbone, _));


String.prototype.commafy = function () {
    return this.replace(/(^|[^\w.])(\d{4,})/g, function($0, $1, $2) {
        return $1 + $2.replace(/\d(?=(?:\d\d\d)+(?!\d))/g, "$&,");
    });
};

Number.prototype.commafy = function () {
    return String(this).commafy();
};

//allow active styles hack
//document.addEventListener("touchstart", function(){}, true);/broken in IE8 sadly

;(function($,H){

    var $body = $('body'),
        linkregex = _.linkregex,
        encodeuri = function(string){
            return encodeURIComponent(string)
                .replace(/!/g, '%21')
                .replace(/'/g, '%27')
                .replace(/\(/g, '%28')
                .replace(/\)/g, '%29')
                .replace(/\*/g, '%2A');
        },
        monthNames = [ "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December" ];

    function commafy(value){
        return (_.isString(value) || _.isNumber(value)) && value.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,").replace(/(.+?)(\.00$)/, "$1");
    }

    function daysLeft(startDate,endDate){
        return ~~(Math.abs((endDate.getTime() - startDate.getTime())/(8.64e7)));
    }

    function reformatDate(value) {
        return value?value.replace(' ', 'T'):undefined;
    }

    //H.registerHelper('int', _.partial(parseInt,_,10));
    H.registerHelper('commafy', commafy);

    Handlebars.registerHelper("inc", function(value, options) {
        return parseInt(value) + 1;
    });

    H.registerHelper('randomvictory', function(victory_campaigns) {
        if(!victory_campaigns){return '';}
        var max = victory_campaigns.length;
        var min = 0;
        var randomCampaign = Math.floor(Math.random() * (max - min)) + min;
        return victory_campaigns[randomCampaign];
    });

    H.registerHelper('truncate', function(title) {
        var titleArray = title.split(' ');
        var trunkTitle = '';
        for (var i=0; i<6; i++) {
            if (titleArray[i]) {
                trunkTitle = trunkTitle + titleArray[i] + ' ';
            }
            if ((i === 5) && (titleArray[6])) {
                trunkTitle = trunkTitle + '...';
            }
        }
        return trunkTitle;
    });

    H.registerHelper('abs', function(donationNumber) {
        var absValue = Math.abs(donationNumber);
        return absValue;
    });

    H.registerHelper('thisurl', function() {
        return window.location.href ;
    });

    H.registerHelper('recruitsremaining', function(num_recruits) {
        return num_recruits - 2;
    });

    H.registerHelper('recruitavatars', function(recruits_info) {
        var counter = 0;
        var finalarray = [];
        if (recruits_info) {
            recruits_info.forEach(function (item) {
                if ((item.avatar != null) && (counter < 4)) {
                    finalarray.push(item.avatar);
                    counter++;
                }
            });
        }
        return finalarray;
    });

    H.registerHelper('todaysDate', _.today);

    H.registerHelper('mdate', function(timestamp, method) {
        var date = _.isNumber(timestamp)?new Date(timestamp*1000):new Date(reformatDate(timestamp));
        //console.log('DATE',timestamp,method,date);
        if(!_.isDate(date)){return '';}
        return !_.isString(method)?
            monthNames[date.getMonth()] +' '+ date.getDate() :
            method ==="abvMon"?
                monthNames[date.getMonth()].slice(0,3):
                date[method]?
                    date[method]():
                    '';
    });

    H.registerHelper('timedate', function(timedate) {
        var dateArray = timedate.split(' ');
        var day = dateArray[2];
        var month = dateArray[1];
        var year = dateArray[5];
        var timeArray = dateArray[3].split(':');
        var hour = timeArray[0];
        var h = hour;
        var min = timeArray[1];
        var ampm = "AM";

        if (hour > 12) {
            h = hour - 12;
            ampm = 'PM';
        } else if (hour === 12) {
            h = 12;
            ampm = 'PM';
        } else if (hour === 0) {
            h = 12;
        }
        return h + ':' + min + ' ' + ampm + ' · ' + day + ' ' + month + ' ' + year;
    });

    H.registerHelper('displayMonth', function(eventDate, long) {

        var eventDateArray = eventDate.split('/');
        var eventMonth = parseInt(eventDateArray[0]-1, 10);
        var month = monthNames[eventMonth];

        return long===true ? month : month.slice(0,3);
    });

    H.registerHelper('displayWeekDay', function(date, long) {

        if(!date){
            return '';
        }

        var weekdays = [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday'
        ];

        var month = date.split('/')[0],
            day = date.split('/')[1],
            year = date.split('/')[2];

        var aDate = new Date(year, month-1, day);

        var weekday = weekdays[aDate.getDay()];

        return long===true ? weekday : weekday.slice(0,3);
    });

    H.registerHelper('displayDay', function(eventDate) {
        var eventDateArray = eventDate.split('/');
        var eventDay = parseInt(eventDateArray[1], 10);

        return eventDay;
    });

    H.registerHelper('displayYear', function(eventDate) {
        var eventDateArray = eventDate.split('/');
        var eventYear = parseInt(eventDateArray[2], 10);

        return eventYear;
    });

    H.registerHelper('displayTime', function(eventTime) {
        var timeArray = eventTime.split(':');
        var hour = timeArray[0];
        var h = hour;
        var min = timeArray[1];
        var ampm = "am";

        if (hour > 12) {
            h = hour - 12;
            ampm = 'pm';
        } else if (hour === 12) {
            h = 12;
            ampm = 'pm';
        } else if (hour === 0) {
            h = 12;
        }

        if (h < 10) {
            var hArray = h.toString().split('');
            var newH = h;
            if (hArray[0] === '0') {
                newH = hArray[1];
            }
            return newH + ' ' + ampm;
        } else {
            return h + ' ' + ampm;
        }
    });

    H.registerHelper('mapFriendly', function(location) {
        return (location||'').replace(/ /g,"+");
    });

    H.registerHelper('hashtag', function(hashtag) {
        var hashtagArray = hashtag.split('');
        var newHashtag = hashtag;
        if (hashtagArray[0] === hashtagArray[1]) {
            newHashtag = hashtag.substr(1);
        }
        return newHashtag;
    });

    //turn 45677 into 45k or 45.6k. the decimal setting is mandatory, but leave it at 0 for no decimals
    H.registerHelper('numabbrv', function(number, decPlaces) {
        var p=Math.pow,
            i=4,
            size;
        decPlaces=p(10,(_.isNumber(decPlaces)?decPlaces:1));
        number = parseFloat(number);
        while(i) {
            size=p(10,i--*3);
            if(size<=number){
                number=((~~(number*decPlaces/size))/decPlaces)+"kMBT"[i]+"+";
            }
        }
        return number.toString();
    });

    H.registerHelper('s', function(value) {
        return (value===1 || value && value.length===1)?'':'s';//undefined is 0, implied plural
    });

    H.registerHelper('list', function(list) {
        var string = list.join(', ').replace(/(.*),/, '$1 and');
        return list.length < 2? string.replace(' and',', and'):string;
    });

    H.registerHelper('escape', _.escape );
    H.registerHelper('unescape', _.unescape );


    H.registerHelper('capCase', _.capCase );
    H.registerHelper('urlslug', _.urlslug );
    H.registerHelper('paragraphify', function(str, noclose) {
        str = str||'';

        var p = noclose===true ? '<p>' : '</p><p>';
        return new H.SafeString('<p>' + str
            .replace(/\n([ \t]*\n)+/g, p).replace(/\r\n/g, p)
            .replace(/\n/g, p) + (noclose===true?'':'</p>'));
    });

    H.registerHelper('videofromurl', function(str) {
        str = str||'';
        return new H.SafeString(_.videoInfoFromUrl(str).embed||'');
    });

    /*a comprehensive helper here is around 10k: this will only be used on key terms known in advance.
    This super hacky and limited, lets think of something better soon*/
    H.registerHelper('articlize', function(value) {
        var article='',s = ' ';
        $.each({"us":"the","district":"the"},function(k,v){
            if (value.toString().toLowerCase().indexOf(k)===0){ article = v+s; return false; }
        });
        return article + value;
    });

    H.registerHelper('selectoptions', function(items, selected, options) {
        var optionsHtml = '',
            preselect = false;

        if (items==="states"){
            items = _.map(window.usstates,function(k,v){
                return {name:k,value:v};
            });
        }else if (items==="exp_month"){
            var monthString = _.range(1,13).join(',');
            var months = monthString.split(',');
            items = [];
            months.forEach(function(val) {
                items.push({name:val,value:val});
            });
        }else if (items==="exp_year"){
            var thisYear = new Date().getFullYear();
            var yearsString = _.range(thisYear, thisYear+10).join(',');
            var years = yearsString.split(',');
            items = [];
            years.forEach(function(val) {
                items.push({name:val,value:val});
            });
        }else if (_.isString(items)){
            items = _.map(items.split(','),function(val,i){
                val = val.split('=');
                if (val.length===1){
                    val.push((val+''));
                }
                return {name:val[1],value:val[0]};
            });
        }
        _.each(items,function(item){
            optionsHtml +='<option';
            if (selected && (selected.toLowerCase() === item.value.toLowerCase())) {
                optionsHtml += ' selected="selected"';
                preselect = true;
            }
            optionsHtml +=' value="'+item.value+'">'+item.name+'</option>';
        });
        if(!preselect && !options.hash.noempty){
            optionsHtml = '<option></option>' + optionsHtml;
        }
        return new H.SafeString(optionsHtml);
    });

    H.registerHelper('req', function(fieldname) {
        if (fieldname && this.required_params && _.contains(this.required_params,fieldname) ){
            return new H.SafeString('required="required"');
        }
    });

    H.registerHelper('prettylink', function(string) {
        return _.isString(string)?
            new H.SafeString(
                string.split("?")[0].replace(/https?:\/\/(www\.)/,'')
            ):
            '';
    });

    //more robust? /\s([^\s<]{0,10})\s*$/,'&nbsp;$1'
    H.registerHelper('titleize', function(string) {
        var trimstring = $.trim(string);
        var stringArray = trimstring.split(' ');
        return _.isString(string)?
            new H.SafeString(
                (trimstring.split(' ').length>3) ?
                    trimstring.replace(/ (?=[^ ]*$)/,'&nbsp;') :
                    trimstring
            ):
            '';
    });

    //more robust? /\s([^\s<]{0,10})\s*$/,'&nbsp;$1'
    H.registerHelper('tiletitleize', function(string) {
        string = string ||'';
        var trimstring = $.trim(string);
        var stringArray = trimstring.split(' ');
        var stringArrayln = stringArray.length;
        var last = stringArray[stringArrayln - 1]||'';
        var secondToLast = stringArray[stringArrayln - 2]||'';

        if (last.length + secondToLast.length < 15) {
            return _.isString(string)?
                new H.SafeString(
                    (trimstring.split(' ').length>3) ?
                        trimstring.replace(/ (?=[^ ]*$)/,'&nbsp;') :
                        trimstring
                ):
                '';
        } else {
            return _.isString(string)?
                new H.SafeString(
                    trimstring
                ):
                '';
        }
    });

    H.registerHelper('encodeuri', function(string) {
        return new H.SafeString(
            _.fullencode(string)
        );
    });

    H.registerHelper('safetitle', function(string) {
        var safestring = string.replace(/\'/,'&#039;').replace(/\)/, '&#41;');
        return safestring;
    });

    /*reduce list of needs done by a list of haves to reveal what's missing, return as csv*/
    H.registerHelper('matchmissing', function(need,have){
        //lug('permreq',need,have);
        var missing = _.difference(need, have);
        //lug('perms missing',missing);
        if (missing.length){
            return missing.join(',');
        }else {
            return false;
        }
    });

    H.registerHelper('twitterlinkify', function(tweet) {
        return _.isString(tweet) && tweet.indexOf('u2026')===-1?
            new H.SafeString(
                tweet.replace(linkregex,"<a href=\"$&\" class=\"tw-link\" target=\"_blank\">$&</a>").replace(/(^|\s)@(\w+)/g, "$1<a target=\"_blank\" class=\"tw-link\" href=\"http://www.twitter.com/$2\">@$2</a>").replace(/(^|\s)#(\w+)/g, "$1<a target=\"_blank\" class=\"tw-link\" href=\"https://twitter.com/search?q=%23$2\">#$2</a>")
            ):'';
    });

    H.registerHelper('twittercustom', function(param, obj, fallback) {
        return H.helpers.twitterlinkify(
            (obj[param])? obj[param] : fallback
        );
    });

    /*limits to number and also pads it out to number*/
    H.registerHelper('pad', function(arr,to) {
        var padded = [].concat(arr);
        if(_.isArray(padded) && padded.length<to){
            _(to - padded.length).times(function(){ padded.push({}); });//pad the number of friends
        }
        padded.length = to;
        return padded;
    });

    //negates
    H.registerHelper('not', function(thing) {
        return !thing;
    });


    //use the first truthy value that was passed
    H.registerHelper('find', function() {
        return _.find(_.initial(arguments),_.identity);
    });


    //block helper like an if that takes any # of values, if any are true, display
    H.registerHelper('any', function() {
        var options =  _.last(arguments);
        return _.any(_.initial(arguments))? options.fn(this) : options.inverse(this);
    });
    //if none are true, display
    H.registerHelper('none', function() {
        var options =  _.last(arguments);
        lug('none helper',_.initial(arguments));
        return !_.any(_.initial(arguments))? options.fn(this) : options.inverse(this);
    });
    //if all are true, display
    H.registerHelper('all', function() {
        var options =  _.last(arguments);
        //lug('all',_.falseArr( _.initial(arguments) ) );
        return _.every(_.falseArr( _.initial(arguments) ) )? options.fn(this) : options.inverse(this);
    });

    //unless all are true, display
    H.registerHelper('unall', function() {
        var options =  _.last(arguments);
        //lug('unall',_.initial(arguments),'result',!_.every(_.initial(arguments)));
        return !_.every(_.initial(arguments))? options.fn(this) : options.inverse(this);
    });



H.registerHelper('compare', function(lvalue, operator, rvalue, options) {

    var operators, result;

    if (typeof lvalue === 'number') {
        rvalue = Number(rvalue);
    }

    lvalue = lvalue===0?0:(lvalue||'');

    if (options === undefined) {
        options = rvalue;
        rvalue = operator;
        operator = "===";
    }

    lug('compare args',arguments, lvalue, operator, rvalue);

    function unarr(v){
        return _.isArray(v)?v.length:v;
    }
    operators = {
        'f': function(l,r){//test against a boolean
            return r === Boolean(unarr(l));
        },
        'isInvalidOr': function(l,r){ return !l || l===r; },
        '===':  function(l,r) { return l === r; },
        '#==':  function(l,r) { return Number(l) === Number(r); },
        '[]':  function(l,r) { return _.contains(lvalue,rvalue); },
        '[!]':  function(l,r) { return lvalue.length && !_.contains(lvalue,(rvalue||'')); },
        '[arraylength]':  function(l,r) { return lvalue.length === rvalue; },
        '!=':       function(l,r) { return l !== r; },
        '!=-1':       function(l,r) { return r && l !== r-1; },
        '>=2':       function(l,r) { return l - r >= 2; },
        '==2':       function(l,r) { return l - r === 2; },
        '==1':       function(l,r) { return l - r === 1; },
        '==0':       function(l,r) { return l - r === 0; },
        '!==0':       function(l,r) { return l - r !== 0; },
        '==-1':       function(l,r) { return l - r === -1; },
        '==-2':       function(l,r) { return l - r === -2; },
        '<=-2':       function(l,r) { return l - r <= -2; },
        '!=0':       function(l,r) { return r && l !== 0; },
        '<':        function(l,r) { return unarr(l) < r; },
        '>':        function(l,r) { return unarr(l) > r; },
        '<=':       function(l,r) { return unarr(l) <= r; },
        '&&':       function(l,r) { return l && r; },
        '&!':       function(l,r) { return l && !r; },
        '!||':      function(l,r) { return !l || r; },
        '>=':       function(l,r) { return unarr(l) >= r; },
        'typeof':   function(l,r) { return typeof l === r; }
    };

    result = operators[operator](lvalue,rvalue);
    //console.log('NEW RESULT',lvalue,rvalue,operator,result);
    if (options.fn){
        if( result) {
            return options.fn(this);
        } else {
            return options.inverse(this);
        }
    }
    else{
        return result;
    }

});


    H.registerHelper('strslice', function(str, limit, start) {
        return _.isString(str) ? str.slice(start||0, limit) : '';
    });

    H.registerHelper('strslicea', function(str, limit, start) {
        return _.isString(str) ?
            ($.trim(str.slice(start||0, limit)) + (str.length>limit?'...':'')) :
            '';
    });

    H.registerHelper('switch', function(conditional) {
        return (['odd','even'][conditional %2] +' '+ ['first','second','third'][conditional % 3]);
    });

    H.registerHelper('gridsection', function(context, options) {
        //lug('section',context,options,arguments);
        if(typeof options ==="undefined"){ options = context; context = ''; }
        return '<section class="'+context+'"><div class="grid-row">'+options.fn(this)+'</div></section>';
    });

    H.registerHelper('ww', function(width,tmpl) {
            var num = (tmpl==='resist-campaign-tiles')?
            (width>1100) ? 'auto' : ((width>746) ? 2 : 1) :
                (tmpl==='campaign-tiles') ?
                    (width>1100) ? 3 : ((width>746) ? 2 : 1) :
                    (width>1170) ? 4 : ((width>940) ? 3 : ((width>635)? 2 : 1));
        return num;
    });

    /*use a different partial depending on the "template" variable in the context object*/
    H.registerHelper('dynamicpartial', function(opts) {
        var template = opts.template,
            f = H.partials[template];
        if (!f) {
            lug('missing partial: ',template);
        }
        return new H.SafeString(f(this));//the this context is in a loop
    });

    /*override a particular value in a partial: latest handlebars may incorporate this!*/
    H.registerHelper('$', function(partial){//override a value {{$ 'template-name' title="different title from the data"}}
        var ops = _.last(arguments),
            context = _.extend({}, ops.context||this, ops.hash);
            //console.log(partial);
        return new H.SafeString( H.partials[partial]? H.partials[partial](context) : '' );
    });

    H.registerHelper('countdown', function(end, start) {
        var startDate = (typeof start==="string")?new Date(reformatDate(start)):new Date(),
            endDate = new Date(reformatDate(end));
        return daysLeft(startDate,endDate);
    });

    H.registerHelper('progress', function(value, goal, type) {
        var calc, now, start, factor = parseFloat(type);
        //lug('progress date args',arguments);
        if(type==="days"){
            now = new Date();
            start = new Date(reformatDate(value));
            start = start>now?now:start;
            goal = daysLeft(start,new Date(reformatDate(goal)));
            value = daysLeft(start,now);
        }
        //lug('progress dates',value,goal);
        if ( isNaN(parseFloat(value)) || isNaN(parseFloat(goal)) ){
            return 0;
        }
        calc = Math.ceil((parseFloat(value)/parseFloat(goal))*100 );
        return ((calc>100)?100:calc) * (!isNaN(factor)?factor:1);
    });


    H.registerHelper('each_upto', function(ary, max, options){
        if(!ary || ary.length === 0){
            return options.inverse(this);
        }
        var result = [];
        for(var i = 0; i < max && i < ary.length; ++i){
            result.push(options.fn(ary[i]));
        }
        return result.join('');
    });

    /*if variable exists return it, or else return what's in the block*/
    H.registerHelper('ifor', function(what) {
        var options =  _.last(arguments);
        return what? what : options.fn(this);
    });


    H.registerHelper('iflonger', function(what,ln) {
        var options =  _.last(arguments);
        return _.isString(what) && what.length > ln ? options.fn(this) : options.inverse(this);
    });

    H.registerHelper('source', _.sourceLink);
    H.registerHelper('sourceencode', _.sourceLinkEncode);

    H.registerHelper('limit', function(ary, max, start, options){
        return _.isArray(ary) || _.isString(ary)?
            ary.slice(_.isNumber(start)?start:0, max):
            ary;
    });


    H.registerHelper('concat', function(delim){
        var hash = _.last(arguments);
        return _.initial(arguments).join(hash.delim||'');
    });

    H.registerHelper('indexunshift', function(index) {
        return parseInt(index);
    });

    H.registerHelper('indexshift', function(index) {
        return index+1;
    });

    // H.registerHelper("debug", function(optionalValue, context) {
    //   lug("Current Context");
    //   lug("====================");
    //   lug(this,{args:arguments});

    //   if (optionalValue) {
    //     lug("Value");
    //     lug("====================");
    //     lug(optionalValue,context);
    //   }
    // });

    /**
    * returns boolean if one of our domains or not
    *
    * @param {String} url
    */
  
    H.registerHelper('isExternalUrl', function(url) {
        try {
            var domain;
            const ourHosts = ['addup', 'sierraclub', 'bsd', 'localhost', '127.0.0.1', '0.0.0.0'];
            const hostname = new URL(url).hostname;
            if (hostname.includes('.')) {
                const splitHostname = hostname.split('.');
                domain = splitHostname[splitHostname.length - 2] !== '0' ? splitHostname[splitHostname.length - 2] : splitHostname.join('.');
            } else {
                domain = hostname;
            }
            return !ourHosts.includes(domain);
        }
        catch(err) {
            return false;
        }
    });
    
    /**
     * returns url param value
     *
     * @param {String} name of param key
     */
    H.registerHelper('getUrlParameter', function(name) {
        name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
        var regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
        var results = regex.exec(location.search);
        return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
    });

    H.registerHelper("checkIf", function (v1,o1,v2,mainOperator,v3,o2,v4,options) {
        var operators = {
            '===': function(a, b){ return a===b; },
            '!==': function(a, b){ return a!==b; },
            '<': function(a, b){ return a<b; },
            '<=': function(a, b){ return a<=b; },
            '>': function(a, b){ return a>b; },
            '>=': function(a, b){ return a>=b; },
            '&&': function(a, b){ return a&&b; },
            '||': function(a, b){ return a||b; },
        };
        var a1 = operators[o1](v1,v2);
        var a2 = operators[o2](v3,v4);
        var isTrue = operators[mainOperator](a1, a2);
        return isTrue ? options.fn(this) : options.inverse(this);
    });

    H.registerHelper('toString', function(value) {console.log('value',value);
        value = value ? value + '' : '';
        if (value) {
            return value.replace(/[<*]/gm, '').replace(/[>*]/gm, '');
        }
        return value;
    });

}(jQuery,Handlebars));
;this["SC"] = this["SC"] || {};

Handlebars.registerPartial("action-header", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"rec-reason"),depth0,{"name":"rec-reason","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "<header><h2 class=\"h2 black\">"
    + container.escapeExpression(lookupProperty(helpers,"titleize").call(alias1,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"titleize","hash":{},"data":data,"loc":{"start":{"line":3,"column":29},"end":{"line":3,"column":47}}}))
    + "</h2></header>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"belongs_to_umbrella_event") : depth0),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":8,"column":7}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"action-content__umbrella-description\">\n<p>"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"belongs_to_umbrella_event") : depth0)) != null ? lookupProperty(stack1,"description") : stack1), depth0))
    + "</p>\n</div>\n";
},"4":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"marquee-header",{"name":"$","hash":{"mleft":true,"arrow":true},"data":data,"loc":{"start":{"line":10,"column":0},"end":{"line":10,"column":44}}}))
    + "\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"single_action") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(4, data, 0),"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":11,"column":7}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("action-complete-next-action", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"post-action-join\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"is_registered") : stack1),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.program(4, data, 0),"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":16,"column":7}}})) != null ? stack1 : "")
    + "</div>\n</div>\n";
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h4 class=\"h4\">You're already registered!</h4>\n<p>Log in or connect with Facebook now to see your&nbsp;progress!</p>\n<div class=\"row center join-buttons\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"sso-button"),depth0,{"name":"sso-button","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"connect-to-facebook-button",{"name":"$","hash":{"post_action":"/supporter-home"},"data":data,"loc":{"start":{"line":9,"column":0},"end":{"line":9,"column":64}}}))
    + "\n";
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.escapeExpression, alias2=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h4 class=\"h4\">Track your impact by joining&nbsp;AddUp</h4>\n<p>We'll show you the progress you're making as you continue taking action to protect the environment.</p>\n<div class=\"row center join-buttons\">\n<button data-event=\"registration-modal\" data-nofb=\"true\" data-post_action=\"supporter-home/"
    + alias1(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"id") : stack1), depth0))
    + "\" data-skipclose=\"true\" class=\"button\">Sign Up</button>\n"
    + alias1(lookupProperty(helpers,"$").call(alias2,"connect-to-facebook-button",{"name":"$","hash":{"post_action":lookupProperty(helpers,"concat").call(alias2,"supporter-home/",((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"id") : stack1),{"name":"concat","hash":{"delim":"/"},"data":data,"loc":{"start":{"line":15,"column":45},"end":{"line":15,"column":90}}})},"data":data,"loc":{"start":{"line":15,"column":0},"end":{"line":15,"column":92}}}))
    + "\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"other-action\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),false,{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":19,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = container.invokePartial(lookupProperty(partials,"next-action-link"),depth0,{"name":"next-action-link","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("action-complete-section", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "sent this message";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"what") : depth0), depth0)) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p class=\"action-complete\"><strong>You've already "
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","Contact Officials - All info required",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":1,"column":50},"end":{"line":1,"column":175}}})) != null ? stack1 : "")
    + "!</strong>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"next-action-link"),depth0,{"name":"next-action-link","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("action-post-action-section", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"action-progress-section",{"name":"$","hash":{},"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":31}}}))
    + "\n";
},"useData":true}));

Handlebars.registerPartial("action-progress-section", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"action-progress-section\">\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">=",768,{"name":"compare","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":84}}})) != null ? stack1 : "")
    + "\n<p>"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),"<",768,{"name":"compare","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":3},"end":{"line":4,"column":84}}})) != null ? stack1 : "")
    + "<strong>"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","Contact Officials - All info required",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.program(8, data, 0),"data":data,"loc":{"start":{"line":4,"column":92},"end":{"line":4,"column":205}}})) != null ? stack1 : "")
    + alias2(lookupProperty(helpers,"s").call(alias1,(depth0 != null ? lookupProperty(depth0,"current_amount") : depth0),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":4,"column":205},"end":{"line":4,"column":225}}}))
    + "</strong> of "
    + alias2(lookupProperty(helpers,"commafy").call(alias1,(depth0 != null ? lookupProperty(depth0,"goal_amount") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":4,"column":238},"end":{"line":4,"column":261}}}))
    + " goal</p>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"progress-meter"),depth0,{"name":"progress-meter","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"2":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h4 class=\"h1\">"
    + container.escapeExpression(lookupProperty(helpers,"commafy").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"current_amount") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":3,"column":41},"end":{"line":3,"column":67}}}))
    + "</h4>";
},"4":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<strong>"
    + container.escapeExpression(lookupProperty(helpers,"commafy").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"current_amount") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":4,"column":36},"end":{"line":4,"column":62}}}))
    + "</strong> ";
},"6":function(container,depth0,helpers,partials,data) {
    return "message";
},"8":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"type") : depth0), depth0));
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"goal_amount") : depth0),"!=",0,{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":7,"column":12}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("action-tree", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"single-action"),depth0,{"name":"single-action","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"actions") : depth0),{"name":"each","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":3,"column":9}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("reason-category", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "you took action for";
},"3":function(container,depth0,helpers,partials,data) {
    return "you're interested in";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<i class=\"category-icon "
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"reason") : depth0)) != null ? lookupProperty(stack1,"category") : stack1)) != null ? lookupProperty(stack1,"slug") : stack1), depth0))
    + "-icon\"></i><span>Because "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"reason") : depth0)) != null ? lookupProperty(stack1,"c_taken") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":1,"column":73},"end":{"line":1,"column":149}}})) != null ? stack1 : "")
    + " <strong>"
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"reason") : depth0)) != null ? lookupProperty(stack1,"category") : stack1)) != null ? lookupProperty(stack1,"name") : stack1), depth0))
    + "</strong></span>\n";
},"useData":true}));

Handlebars.registerPartial("reason-state", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<i class=\"category-icon\"><img src=\"/img/yourarea-icon.png\"></i><span>This campaign affects&nbsp;<strong>"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"reason") : depth0)) != null ? lookupProperty(stack1,"state") : stack1), depth0))
    + "</strong>. Take action now:</span>\n";
},"useData":true}));

Handlebars.registerPartial("reason-type", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<i class=\"category-icon "
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"reason") : depth0)) != null ? lookupProperty(stack1,"a_type") : stack1), depth0))
    + "-icon\"></i><span>Why not <strong>"
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"reason") : depth0)) != null ? lookupProperty(stack1,"a_type_verb") : stack1), depth0))
    + "</strong> for this campaign too?</span>\n";
},"useData":true}));

Handlebars.registerPartial("rec-reason", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"rec-reason\">\n"
    + container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"reason") : depth0)) != null ? lookupProperty(stack1,"template") : stack1),{"name":"$","hash":{},"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":21}}}))
    + "\n</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"reason") : depth0)) != null ? lookupProperty(stack1,"template") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":5,"column":7}}})) != null ? stack1 : "");
},"useData":true}));

Handlebars.registerPartial("recommended-action", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"actions") : depth0)) != null ? lookupProperty(stack1,"0") : stack1),{"name":"with","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":4,"column":9}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"single-action",{"name":"$","hash":{"single_action":true},"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":40}}}))
    + "\n";
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"gridsection","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":6,"column":0},"end":{"line":6,"column":83}}})) != null ? stack1 : "")
    + "\n";
},"5":function(container,depth0,helpers,partials,data) {
    return "<br><br><h2 class=\"h2 center\">No actions left!</h2>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"actions") : depth0)) != null ? lookupProperty(stack1,"0") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(4, data, 0),"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":7,"column":7}}})) != null ? stack1 : "");
},"useData":true}));

Handlebars.registerPartial("single-action", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<section class=\""
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","Contact Officials - All info required",{"name":"compare","hash":{},"fn":container.program(2, data, 0),"inverse":container.program(4, data, 0),"data":data,"loc":{"start":{"line":2,"column":16},"end":{"line":2,"column":137}}})) != null ? stack1 : "")
    + "-action-section action-section"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_took") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.program(10, data, 0),"data":data,"loc":{"start":{"line":2,"column":167},"end":{"line":2,"column":358}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"actionable_type") : depth0),"===","UmbrellaEvent",{"name":"compare","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":359},"end":{"line":2,"column":438}}})) != null ? stack1 : "")
    + "\">\n<div class=\"grid-row\">\n<div data-template=\"action-"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"===","message",{"name":"compare","hash":{},"fn":container.program(2, data, 0),"inverse":container.program(4, data, 0),"data":data,"loc":{"start":{"line":4,"column":27},"end":{"line":4,"column":110}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"===","event",{"name":"compare","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":110},"end":{"line":4,"column":186}}})) != null ? stack1 : "")
    + "\" data-single_action=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"single_action") : depth0), depth0))
    + "\" data-actionmodel=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" data-series=\""
    + alias3(lookupProperty(helpers,"indexshift").call(alias1,(data && lookupProperty(data,"index")),{"name":"indexshift","hash":{},"data":data,"loc":{"start":{"line":4,"column":266},"end":{"line":4,"column":287}}}))
    + "\" data-last=\""
    + alias3(alias2((data && lookupProperty(data,"last")), depth0))
    + "\" id=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"action_type") : depth0), depth0))
    + "\" data-mixins=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"action_type") : depth0), depth0))
    + "\" data-source=\"action:"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" class=\"action-template\"></div>\n</div>\n</section>\n";
},"2":function(container,depth0,helpers,partials,data) {
    return "petition";
},"4":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"action_type") : depth0), depth0));
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"!=","donate",{"name":"compare","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":186},"end":{"line":2,"column":316}}})) != null ? stack1 : "");
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " action-complete "
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"!=","event",{"name":"compare","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":241},"end":{"line":2,"column":304}}})) != null ? stack1 : "");
},"8":function(container,depth0,helpers,partials,data) {
    return "closed-section";
},"10":function(container,depth0,helpers,partials,data) {
    return " action-undone open-section";
},"12":function(container,depth0,helpers,partials,data) {
    return "is-umbrella-event";
},"14":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"is_rec") : depth0),{"name":"if","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":148},"end":{"line":4,"column":174}}})) != null ? stack1 : "");
},"15":function(container,depth0,helpers,partials,data) {
    return "-recs";
},"17":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"active") : depth0),"===",1,{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":0},"end":{"line":15,"column":12}}})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"!=","petition",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":7,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"checkIf").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"===","petition","||",(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"===","message",{"name":"checkIf","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":16,"column":12}}})) != null ? stack1 : "");
},"useData":true}));

Handlebars.registerPartial("try-another-action-btn", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<button class=\"try-another\">Try another action</button>\n";
},"useData":true}));

Handlebars.registerPartial("alt-login-link", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<a data-ahref=\"/saml/login\" class=\"popup saml\">Already signed up? Login</a>\n";
},"useData":true}));

Handlebars.registerPartial("avatar-caption", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "Drag an image here or c";
},"3":function(container,depth0,helpers,partials,data) {
    return "C";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"all").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"t_filer") : depth0),(depth0 != null ? lookupProperty(depth0,"d_nomobos") : depth0),{"name":"all","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":66}}})) != null ? stack1 : "")
    + "lick to choose a file &amp; change your profile photo.\n";
},"useData":true}));

Handlebars.registerPartial("action-next-campaign-no-results", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"first_name") : depth0), depth0))
    + ", a";
},"3":function(container,depth0,helpers,partials,data) {
    return "A";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"no-results-section\">\n<h4 class=\"h4 center no-results\">"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":2,"column":33},"end":{"line":2,"column":84}}})) != null ? stack1 : "")
    + "ll nature lovers sometimes wander off the beaten path...<br/>but we can help you find your&nbsp;way!</h4>\n<div class=\"nr-next-campaign\">\n<header>\n<h2 class=\"h2 center\">Try this trending campaign to find a new way&nbsp;forward</h2>\n</header>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"next-campaign-content"),depth0,{"name":"next-campaign-content","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("campaign-action-bar-item", Handlebars.template({"1":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li data-active=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"active") : depth0), depth0))
    + "\" data-action_type=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"action_type") : depth0), depth0))
    + "\" id=\"a_id"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" class=\"action-bar-item action-bar-type smooth-scroll action-"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","Contact Officials - All info required",{"name":"compare","hash":{},"fn":container.program(2, data, 0, blockParams, depths),"inverse":container.program(4, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":2,"column":140},"end":{"line":2,"column":261}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"!=","donate",{"name":"compare","hash":{},"fn":container.program(6, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":261},"end":{"line":2,"column":373}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"===","donate",{"name":"compare","hash":{},"fn":container.program(9, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":373},"end":{"line":2,"column":439}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depths[1] != null ? lookupProperty(depths[1],"t_lastaction") : depths[1]),"===",(depth0 != null ? lookupProperty(depth0,"id") : depth0),{"name":"compare","hash":{},"fn":container.program(11, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":439},"end":{"line":2,"column":500}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"is_new") : depth0),{"name":"if","hash":{},"fn":container.program(13, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":500},"end":{"line":2,"column":532}}})) != null ? stack1 : "")
    + "\" data-href=\"#"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"action_type") : depth0), depth0))
    + "\">\n<a><span>"
    + alias2(lookupProperty(helpers,"indexshift").call(alias3,(data && lookupProperty(data,"index")),{"name":"indexshift","hash":{},"data":data,"loc":{"start":{"line":3,"column":9},"end":{"line":3,"column":30}}}))
    + "</span> &nbsp;"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","Contact Officials - All info required",{"name":"compare","hash":{},"fn":container.program(15, data, 0, blockParams, depths),"inverse":container.program(17, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":3,"column":44},"end":{"line":3,"column":161}}})) != null ? stack1 : "")
    + "</a>\n</li>\n";
},"2":function(container,depth0,helpers,partials,data) {
    return "petition";
},"4":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"action_type") : depth0), depth0));
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"action_took") : depth0),{"name":"if","hash":{},"fn":container.program(7, data, 0),"inverse":container.program(9, data, 0),"data":data,"loc":{"start":{"line":2,"column":299},"end":{"line":2,"column":361}}})) != null ? stack1 : "");
},"7":function(container,depth0,helpers,partials,data) {
    return " action-taken";
},"9":function(container,depth0,helpers,partials,data) {
    return " action-untaken";
},"11":function(container,depth0,helpers,partials,data) {
    return " mostraction";
},"13":function(container,depth0,helpers,partials,data) {
    return " new-action";
},"15":function(container,depth0,helpers,partials,data) {
    return "Take Action";
},"17":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"verb") : depth0), depth0));
},"19":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"active") : depth0),"===",1,{"name":"compare","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":11,"column":12}}})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"!=","petition",{"name":"compare","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":5,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"checkIf").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"===","petition","||",(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"===","message",{"name":"checkIf","hash":{},"fn":container.program(19, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":6,"column":0},"end":{"line":12,"column":12}}})) != null ? stack1 : "");
},"useData":true,"useDepths":true}));

Handlebars.registerPartial("campaign-large-image", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return " has-video";
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"videoWrapper\">"
    + container.escapeExpression(lookupProperty(helpers,"videofromurl").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"yt_video") : depth0),{"name":"videofromurl","hash":{},"data":data,"loc":{"start":{"line":2,"column":42},"end":{"line":2,"column":67}}}))
    + "</div>";
},"5":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"image") : depth0), depth0))
    + "/@mx_900\" onerror=\"this.onerror=null;this.src='/img/default_image_large.jpg';\" alt=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "\" class=\"campaign-large-image\">";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"campaign-image-container"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"yt_video") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":39},"end":{"line":1,"column":72}}})) != null ? stack1 : "")
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"yt_video") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.program(5, data, 0),"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":2,"column":230}}})) != null ? stack1 : "")
    + "\n</figure>\n";
},"useData":true}));

Handlebars.registerPartial("campaign-tiles", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "/"
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"top_action") : depth0)) != null ? lookupProperty(stack1,"featured_action_type") : stack1), depth0))
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"top_action") : depth0)) != null ? lookupProperty(stack1,"featured_action_type") : stack1),"!=","event",{"name":"compare","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":82},"end":{"line":4,"column":187}}})) != null ? stack1 : "")
    + "\"><span class=\"campaign-top-action icon\"><i class=\"action-"
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"top_action") : depth0)) != null ? lookupProperty(stack1,"featured_action_type") : stack1), depth0))
    + "\"></i> "
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"top_action") : depth0)) != null ? lookupProperty(stack1,"featured_action_phrase") : stack1), depth0))
    + "</span></a>";
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "/"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"top_action") : depth0)) != null ? lookupProperty(stack1,"featured_action_slug") : stack1), depth0));
},"4":function(container,depth0,helpers,partials,data) {
    return " long-title";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"campaign-tile\">\n<a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\"><span class=\"campaign-image\"><img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"image") : depth0), depth0))
    + "/@zx_175@zy_175\" alt=\""
    + alias2(lookupProperty(helpers,"capCase").call(alias3,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"capCase","hash":{},"data":data,"loc":{"start":{"line":2,"column":100},"end":{"line":2,"column":117}}}))
    + "\"></span></a>\n<figcaption>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"top_action") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":4,"column":342}}})) != null ? stack1 : "")
    + "\n<a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" class=\"block-link\">\n<span class=\"campaign-name"
    + ((stack1 = lookupProperty(helpers,"iflonger").call(alias3,(depth0 != null ? lookupProperty(depth0,"title") : depth0),55,{"name":"iflonger","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":6,"column":26},"end":{"line":6,"column":72}}})) != null ? stack1 : "")
    + "\">"
    + alias2(lookupProperty(helpers,"tiletitleize").call(alias3,lookupProperty(helpers,"capCase").call(alias3,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"capCase","hash":{},"data":data,"loc":{"start":{"line":6,"column":89},"end":{"line":6,"column":104}}}),{"name":"tiletitleize","hash":{},"data":data,"loc":{"start":{"line":6,"column":74},"end":{"line":6,"column":106}}}))
    + "</span>\n<span class=\"supporter-count\"><strong>"
    + alias2(lookupProperty(helpers,"commafy").call(alias3,(depth0 != null ? lookupProperty(depth0,"num_supporters_current") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":7,"column":38},"end":{"line":7,"column":72}}}))
    + "</strong> Supporter"
    + alias2(lookupProperty(helpers,"s").call(alias3,(depth0 != null ? lookupProperty(depth0,"num_supporters_current") : depth0),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":7,"column":91},"end":{"line":7,"column":119}}}))
    + "</span>\n</a>\n</figcaption>\n</figure>\n";
},"useData":true}));

Handlebars.registerPartial("campaigns-grid", Handlebars.template({"1":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\""
    + alias2(lookupProperty(helpers,"switch").call(alias1,(data && lookupProperty(data,"index")),(data && lookupProperty(data,"last")),{"name":"switch","hash":{},"data":data,"loc":{"start":{"line":2,"column":31},"end":{"line":2,"column":54}}}))
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(data && lookupProperty(data,"last")),{"name":"if","hash":{},"fn":container.program(2, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":54},"end":{"line":2,"column":84}}})) != null ? stack1 : "")
    + " tile-item\">"
    + alias2(lookupProperty(helpers,"dynamicpartial").call(alias1,depths[1],{"name":"dynamicpartial","hash":{},"data":data,"loc":{"start":{"line":2,"column":96},"end":{"line":2,"column":117}}}))
    + "</div>";
},"2":function(container,depth0,helpers,partials,data) {
    return " last-item";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"campaigns-grid\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"data") : depth0),{"name":"each","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":2,"column":132}}})) != null ? stack1 : "")
    + "\n</div>\n";
},"useData":true,"useDepths":true}));

Handlebars.registerPartial("campaigns-search", Handlebars.template({"1":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=container.escapeExpression, alias2=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<option value=\""
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\""
    + ((stack1 = lookupProperty(helpers,"compare").call(alias2,(depths[1] != null ? lookupProperty(depths[1],"category") : depths[1]),"#==",(depth0 != null ? lookupProperty(depth0,"id") : depth0),{"name":"compare","hash":{},"fn":container.program(2, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":42},"end":{"line":8,"column":96}}})) != null ? stack1 : "")
    + ">"
    + alias1(lookupProperty(helpers,"capCase").call(alias2,(depth0 != null ? lookupProperty(depth0,"name") : depth0),{"name":"capCase","hash":{},"data":data,"loc":{"start":{"line":8,"column":97},"end":{"line":8,"column":113}}}))
    + "</option>";
},"2":function(container,depth0,helpers,partials,data) {
    return " selected";
},"4":function(container,depth0,helpers,partials,data) {
    return "selected";
},"6":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "value=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0))
    + "\"";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"campaigns-search search-bar\">\n<form id=\"search-form\" action=\"#\">\n<ul class=\"search-types\">\n<li class=\"select-field\">\n<label for=\"issues-field\">Issues:</label>\n<select name=\"category\" id=\"issues-field\">\n<option value=\"none\">All</option>\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"categories") : depth0),{"name":"each","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":8,"column":131}}})) != null ? stack1 : "")
    + "\n</select>\n</li>\n<li class=\"select-field\">\n<label for=\"location-field\" id=\"location-field\">Location:</label>\n<select name=\"state\">\n<option value=\"nearby\">Campaigns Near You</option>\n<option value=\"national\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"national") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":15,"column":25},"end":{"line":15,"column":56}}})) != null ? stack1 : "")
    + ">National</option>\n"
    + container.escapeExpression(lookupProperty(helpers,"$").call(alias1,"state_options",{"name":"$","hash":{"state":(depth0 != null ? lookupProperty(depth0,"state") : depth0),"noempty":true},"data":data,"loc":{"start":{"line":16,"column":0},"end":{"line":16,"column":46}}}))
    + "\n</select>\n</li>\n<li class=\"search-field\">\n<label for=\"campaign-search\">Search:</label>\n<input type=\"search\" name=\"title\" placeholder=\"Search Campaigns\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":21,"column":65},"end":{"line":21,"column":102}}})) != null ? stack1 : "")
    + " id=\"campaign-search\" class=\"search-input\">\n<button class=\"button blue-button\" title=\"search\"></button>\n</li>\n</ul>\n</form>\n</div>\n";
},"useData":true,"useDepths":true}));

Handlebars.registerPartial("next-campaign-content", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row\">\n<a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\">\n<span class=\"title-and-image\">\n<span class=\"campaign-image\">\n<img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"image_url") : depth0), depth0))
    + "/@zx_175@zy_175\" alt=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0))
    + "\">\n</span>\n<span class=\"title\">"
    + alias2(lookupProperty(helpers,"titleize").call(alias3,lookupProperty(helpers,"capCase").call(alias3,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"capCase","hash":{},"data":data,"loc":{"start":{"line":7,"column":31},"end":{"line":7,"column":46}}}),{"name":"titleize","hash":{},"data":data,"loc":{"start":{"line":7,"column":20},"end":{"line":7,"column":48}}}))
    + "</span>\n</span>\n<button class=\"button action-button\">See This Action</button>\n</a>\n</div>\n";
},"useData":true}));

Handlebars.registerPartial("resist-campaign-tiles", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "/"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"top_action") : depth0)) != null ? lookupProperty(stack1,"featured_action_slug") : stack1), depth0));
},"3":function(container,depth0,helpers,partials,data) {
    return "Take Action";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a class=\"resist-campaign-tile\" href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "/"
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"top_action") : depth0)) != null ? lookupProperty(stack1,"featured_action_type") : stack1), depth0))
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,((stack1 = (depth0 != null ? lookupProperty(depth0,"top_action") : depth0)) != null ? lookupProperty(stack1,"featured_action_type") : stack1),"!=","event",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":93},"end":{"line":1,"column":198}}})) != null ? stack1 : "")
    + "\">\n<span class=\"slide-content\">\n<span class=\"supporter-count\"><strong>"
    + alias2(lookupProperty(helpers,"commafy").call(alias3,(depth0 != null ? lookupProperty(depth0,"num_supporters_current") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":3,"column":38},"end":{"line":3,"column":72}}}))
    + "</strong> Supporters</span>\n<span class=\"campaign-name\">"
    + alias2(lookupProperty(helpers,"titleize").call(alias3,lookupProperty(helpers,"capCase").call(alias3,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"capCase","hash":{},"data":data,"loc":{"start":{"line":4,"column":39},"end":{"line":4,"column":54}}}),{"name":"titleize","hash":{},"data":data,"loc":{"start":{"line":4,"column":28},"end":{"line":4,"column":56}}}))
    + "</span>\n<span class=\"campaign-description\">"
    + alias2(lookupProperty(helpers,"strslicea").call(alias3,(depth0 != null ? lookupProperty(depth0,"goal") : depth0),120,{"name":"strslicea","hash":{},"data":data,"loc":{"start":{"line":5,"column":35},"end":{"line":5,"column":57}}}))
    + "</span>\n</span>\n<span class=\"image-section\">\n<img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"image") : depth0), depth0))
    + "/@zx_387@zy_235\" alt=\""
    + alias2(lookupProperty(helpers,"capCase").call(alias3,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"capCase","hash":{},"data":data,"loc":{"start":{"line":8,"column":41},"end":{"line":8,"column":58}}}))
    + "\">\n<button class=\"button action-button\">"
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias3,((stack1 = (depth0 != null ? lookupProperty(depth0,"top_action") : depth0)) != null ? lookupProperty(stack1,"featured_action_phrase") : stack1),{"name":"ifor","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":37},"end":{"line":9,"column":100}}})) != null ? stack1 : "")
    + "</button>\n</span>\n</a>\n";
},"useData":true}));

Handlebars.registerPartial("donate-link", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"source").call(depth0 != null ? depth0 : (container.nullContext || {}),"https://sierra.secure.force.com/donate/rc_connect__campaign_designform?id=70131000001ReZA#!form=00P3100000T5ER6EAN",(depth0 != null ? lookupProperty(depth0,"t_sources") : depth0),{"name":"source","hash":{},"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":137}}}))
    + "\n";
},"useData":true}));

Handlebars.registerPartial("donation-btn", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "checked=\"checked\"";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"radio\" name=\"amount\" id=\"d-btn-"
    + alias2(lookupProperty(helpers,"abs").call(alias1,depth0,{"name":"abs","hash":{},"data":data,"loc":{"start":{"line":1,"column":44},"end":{"line":1,"column":56}}}))
    + "\" value=\""
    + alias2(lookupProperty(helpers,"abs").call(alias1,depth0,{"name":"abs","hash":{},"data":data,"loc":{"start":{"line":1,"column":65},"end":{"line":1,"column":77}}}))
    + "\" class=\"donate-radio\" title=\"Please select an amount\" required "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"checked") : depth0),"===",lookupProperty(helpers,"abs").call(alias1,depth0,{"name":"abs","hash":{},"data":data,"loc":{"start":{"line":1,"column":166},"end":{"line":1,"column":176}}}),{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":141},"end":{"line":1,"column":207}}})) != null ? stack1 : "")
    + " /><label for=\"d-btn-"
    + alias2(lookupProperty(helpers,"abs").call(alias1,depth0,{"name":"abs","hash":{},"data":data,"loc":{"start":{"line":1,"column":228},"end":{"line":1,"column":240}}}))
    + "\" class=\"button donate-radio-label\">$"
    + alias2(lookupProperty(helpers,"commafy").call(alias1,lookupProperty(helpers,"abs").call(alias1,depth0,{"name":"abs","hash":{},"data":data,"loc":{"start":{"line":1,"column":287},"end":{"line":1,"column":297}}}),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":1,"column":277},"end":{"line":1,"column":299}}}))
    + "</label>\n";
},"useData":true}));

Handlebars.registerPartial("hidden-fields", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"hidden\" name=\""
    + alias2(alias1((data && lookupProperty(data,"key")), depth0))
    + "\" value=\""
    + alias2(alias1(depth0, depth0))
    + "\">\n";
},"useData":true}));

Handlebars.registerPartial("action-events-no-results", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<div class=\"no-results-section\">\n<h4 class=\"h4 center no-results\">Darn, there don't appear to be any nearby events that match your search. Try changing your search filters or searching in a different area.</h4>\n</div>\n";
},"useData":true}));

Handlebars.registerPartial("calendars", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var alias1=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "href=\""
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"ics") : depth0), depth0))
    + "\" download=\""
    + alias1(lookupProperty(helpers,"urlslug").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"urlslug","hash":{},"data":data,"loc":{"start":{"line":1,"column":45},"end":{"line":1,"column":62}}}))
    + "\"";
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " href=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"gcal") : depth0), depth0))
    + "\" target=\"_blank\"";
},"5":function(container,depth0,helpers,partials,data) {
    var alias1=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\""
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"ics") : depth0), depth0))
    + "\" download=\""
    + alias1(lookupProperty(helpers,"urlslug").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"urlslug","hash":{},"data":data,"loc":{"start":{"line":3,"column":49},"end":{"line":3,"column":66}}}))
    + "\"><img src=\"/img/ical-icon.png\"> iCal</a>";
},"7":function(container,depth0,helpers,partials,data) {
    var alias1=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\""
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"ics") : depth0), depth0))
    + "\" download=\""
    + alias1(lookupProperty(helpers,"urlslug").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"urlslug","hash":{},"data":data,"loc":{"start":{"line":4,"column":45},"end":{"line":4,"column":62}}}))
    + "\"><img src=\"/img/outlook-cal-icon.png\"> Outlook</a>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_android") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":1,"column":3},"end":{"line":1,"column":110}}})) != null ? stack1 : "")
    + "><img src=\"/img/google-cal-icon.png\"> Google Calendar</a>\n<a href=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"ycal") : depth0), depth0))
    + "\" target=\"_blank\"><img src=\"/img/yahoo-cal-icon.png\"> Yahoo! Calendar</a>\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_android") : depth0),{"name":"unless","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":118}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_iOS") : depth0),{"name":"unless","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":4,"column":124}}})) != null ? stack1 : "")
    + "\n<a href=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"oocal") : depth0), depth0))
    + "\" target=\"_blank\"><img src=\"/img/outlook-cal-icon.png\"> Outlook Online</a>\n";
},"useData":true}));

Handlebars.registerPartial("display-event", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row inset-content\">\n<div class=\"col-9\">\n<div class=\"row featured-event\">"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"featured-event"),depth0,{"name":"featured-event","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n</div>\n<div class=\"action-area col-3 center\">\n"
    + container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"action-progress-section",{"name":"$","hash":{"type":"RSVP"},"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":7,"column":43}}}))
    + "\n</div>\n</div>\n";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"action-confirmation\">\n<div class=\"bubble-pale\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"events-confirmation"),depth0,{"name":"events-confirmation","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"action-complete-next-action"),depth0,{"name":"action-complete-next-action","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"action_taken") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":17,"column":11}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("event-info-link", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"info_url") : depth0), depth0))
    + "\" target=\"_blank\" rel=\"noreferrer noopener\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"content") : depth0), depth0))
    + "</a>";
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"content") : depth0), depth0));
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"info_url") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":122}}})) != null ? stack1 : "")
    + "\n";
},"useData":true}));

Handlebars.registerPartial("event-slide", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.escapeExpression, alias2=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p class=\"info-location clickable\">\n<a href=\"https://www.google.com/maps/place/"
    + alias1(lookupProperty(helpers,"mapFriendly").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"location") : depth0),{"name":"mapFriendly","hash":{},"data":data,"loc":{"start":{"line":18,"column":43},"end":{"line":18,"column":67}}}))
    + "/@"
    + alias1(alias2((depth0 != null ? lookupProperty(depth0,"lat") : depth0), depth0))
    + ","
    + alias1(alias2((depth0 != null ? lookupProperty(depth0,"lng") : depth0), depth0))
    + ",17z/\" target=\"_blank\">"
    + ((stack1 = alias2((depth0 != null ? lookupProperty(depth0,"location") : depth0), depth0)) != null ? stack1 : "")
    + "</a>\n</p>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"event-date clickable\">\n<div class=\"date-inner\">\n<p class=\"start-day\">\n"
    + alias2(lookupProperty(helpers,"displayDay").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayDay","hash":{},"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":4,"column":25}}}))
    + "\n</p>\n<p class=\"start-monthyear\">\n"
    + alias2(lookupProperty(helpers,"displayMonth").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayMonth","hash":{},"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":7,"column":27}}}))
    + "<br/>\n"
    + alias2(lookupProperty(helpers,"displayYear").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayYear","hash":{},"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":8,"column":26}}}))
    + "\n</p>\n</div>\n</div>\n<div class=\"event-data\">\n<p class=\"info-list clickable\">\n<a href=\""
    + alias2(container.lambda((depth0 != null ? lookupProperty(depth0,"info_url") : depth0), depth0))
    + "\">"
    + ((stack1 = lookupProperty(helpers,"truncate").call(alias1,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"truncate","hash":{},"data":data,"loc":{"start":{"line":14,"column":23},"end":{"line":14,"column":43}}})) != null ? stack1 : "")
    + "</a>\n</p>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"location") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":0},"end":{"line":20,"column":7}}})) != null ? stack1 : "")
    + "</div>\n";
},"useData":true}));

Handlebars.registerPartial("event-tile", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"start_time") : depth0), depth0));
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p class=\"info-location clickable\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"location") : depth0), depth0)) != null ? stack1 : "")
    + "</p>\n";
},"5":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"category-tag "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "-icon\" data=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\">\n"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "\n</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"rsvpinline-container\">\n<!-- Normally we could never call a view from inside a partial. We will do some JS magic to make this happen -->\n<div class=\"rsvpinline\" data-template=\"rsvpinline\" data-source=\"action:"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" data-mixins=\"rsvpinline\" data-actionmodel=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" data-watch=\"false\" data-slug=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\">\n</div>\n</div>\n<div class=\"event-tile-details col-8\">\n<div class=\"event-title\">\n"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0)) != null ? stack1 : "")
    + "\n</div>\n<div class=\"umbrella-event-listing__date event-date-section\">\n"
    + alias2(lookupProperty(helpers,"displayMonth").call(alias3,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayMonth","hash":{},"data":data,"loc":{"start":{"line":11,"column":0},"end":{"line":11,"column":27}}}))
    + " "
    + alias2(lookupProperty(helpers,"displayDay").call(alias3,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayDay","hash":{},"data":data,"loc":{"start":{"line":11,"column":28},"end":{"line":11,"column":53}}}))
    + ", "
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"start_time") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":11,"column":55},"end":{"line":11,"column":94}}})) != null ? stack1 : "")
    + "\n</div>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"location") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":13,"column":0},"end":{"line":15,"column":7}}})) != null ? stack1 : "")
    + "<div class=\"category-tags\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias3,(depth0 != null ? lookupProperty(depth0,"categories") : depth0),{"name":"each","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":17,"column":0},"end":{"line":21,"column":9}}})) != null ? stack1 : "")
    + "</div>\n\n<div class=\"inline-event-rsvp-placement\"></div>\n</div>\n";
},"useData":true}));

Handlebars.registerPartial("events-confirmation", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h2 class=\"confirm-title\">See you there"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"first_name") : stack1),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":39},"end":{"line":2,"column":94}}})) != null ? stack1 : "")
    + "!</h2>\n<p class=\"subhead confirm-message\">\nKeep an eye on your inbox for an email with everything you need to know about this event.\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"signup_instructions") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":7,"column":7}}})) != null ? stack1 : "")
    + "</p>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"signup_instructions") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":0},"end":{"line":13,"column":7}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ", "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"first_name") : stack1), depth0));
},"4":function(container,depth0,helpers,partials,data) {
    return "Here's an additional message from the event host:\n";
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p class=\"event-instructions\">\n&#8220;"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"signup_instructions") : depth0), depth0)) != null ? stack1 : "")
    + "&#8221;\n</p>\n";
},"8":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h2 class=\"confirm-title\">Thanks for your interest!</h2>\n<p class=\"subhead confirm-message\">\nSome events require one more step to register &mdash; be sure to check out the "
    + container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"event-info-link",{"name":"$","hash":{"content":"event info"},"data":data,"loc":{"start":{"line":17,"column":79},"end":{"line":17,"column":123}}}))
    + " for more details.\n</p>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"sf_campaign_id") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(8, data, 0),"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":19,"column":7}}})) != null ? stack1 : "");
},"useData":true}));

Handlebars.registerPartial("events-grid", Handlebars.template({"1":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"data") : depth0),{"name":"each","hash":{},"fn":container.program(2, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":20,"column":9}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, alias3=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\""
    + alias2(lookupProperty(helpers,"switch").call(alias1,(data && lookupProperty(data,"index")),(data && lookupProperty(data,"last")),{"name":"switch","hash":{},"data":data,"loc":{"start":{"line":5,"column":12},"end":{"line":5,"column":35}}}))
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(data && lookupProperty(data,"last")),{"name":"if","hash":{},"fn":container.program(3, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":35},"end":{"line":5,"column":65}}})) != null ? stack1 : "")
    + " tile-item action-section tile-"
    + alias2(alias3((data && lookupProperty(data,"index")), depth0))
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"umbrella") : depth0)) != null ? lookupProperty(stack1,"0") : stack1)) != null ? lookupProperty(stack1,"name") : stack1),{"name":"if","hash":{},"fn":container.program(5, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":106},"end":{"line":5,"column":152}}})) != null ? stack1 : "")
    + "\">\n<div class=\"event-tile row\" data-eventslug=\""
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" data-eventid=\""
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" data-date=\""
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"start_date") : depth0), depth0))
    + "\">\n<a href=\"/events/"
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" class=\"event-tile-container\" target=\"_blank\">\n</a>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"umbrella") : depth0)) != null ? lookupProperty(stack1,"0") : stack1)) != null ? lookupProperty(stack1,"name") : stack1),{"name":"if","hash":{},"fn":container.program(7, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":0},"end":{"line":13,"column":7}}})) != null ? stack1 : "")
    + "<div class=\"event-tile-detail\">\n<div class=\"col-2 tile-number\">"
    + alias2(lookupProperty(helpers,"inc").call(alias1,(data && lookupProperty(data,"index")),{"name":"inc","hash":{},"data":data,"loc":{"start":{"line":15,"column":31},"end":{"line":15,"column":45}}}))
    + "</div>\n"
    + alias2(lookupProperty(helpers,"dynamicpartial").call(alias1,depths[1],{"name":"dynamicpartial","hash":{},"data":data,"loc":{"start":{"line":16,"column":0},"end":{"line":16,"column":21}}}))
    + "\n</div>\n</div>\n</div>\n";
},"3":function(container,depth0,helpers,partials,data) {
    return " last-item";
},"5":function(container,depth0,helpers,partials,data) {
    return " tile-umbrella";
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"/events?umbrella_event_id="
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"umbrella") : depth0)) != null ? lookupProperty(stack1,"0") : stack1)) != null ? lookupProperty(stack1,"id") : stack1), depth0))
    + "\" class=\"umbrella-event-listing__partof\" target=\"_blank\">\nPart of "
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"umbrella") : depth0)) != null ? lookupProperty(stack1,"0") : stack1)) != null ? lookupProperty(stack1,"name") : stack1), depth0))
    + "\n</a>\n";
},"9":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"action-events-no-results"),depth0,{"name":"action-events-no-results","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"events-grid active\" data-total=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"total") : depth0), depth0))
    + "\">\n\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"total") : depth0),">",0,{"name":"compare","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.program(9, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":23,"column":12}}})) != null ? stack1 : "")
    + "\n<script>\n/**\n*  We have to run this after each time the events grid loads.\n*  This is because the events-grid reloads each time data/filter changes, and wipes out the state of everything here each time.\n*  So if someone is on mobile view and is viewing the map, and then changes the search filter or clicks the redo-search button, this partial would reset its state, and replace the map view with the list view.\n*  This JS below ensures maintaining the integrity of the user's currently chosen view.\n*/\njQuery(function() {\nif (jQuery('.map').hasClass('disabled')) {\njQuery('.events-grid').removeClass('active');\n}\n});\n</script>\n</div>\n";
},"usePartial":true,"useData":true,"useDepths":true}));

Handlebars.registerPartial("events-header", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<header class=\"your-campaigns-header umbrella-events-header\">\n<h3>Coordinated Event</h3>\n<h1>"
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"name") : stack1), depth0))
    + "</h1>\n<h3><span class=\"umbrella-events-header__date\">"
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"start_date") : stack1), depth0))
    + "</span></h3>\n<p class=\"umbrella-events-header__description\">"
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"umbrella_description") : stack1), depth0))
    + "</p>\n<p class=\"umbrella-events-header__makenew\">Find an event near you, or <a href=\"/cms/event/new/edit?umbrella_id="
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"id") : stack1), depth0))
    + "\" target=\"_blank\">create your own</a></p>\n</header>\n";
},"3":function(container,depth0,helpers,partials,data) {
    return "<header class=\"your-campaigns-header\">\n<h1>Events</h1>\n</header>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<!-- This file is no longer used. See public/source/react/Events/index.jsx for current -->\n<div class=\"events-header\" id=\"events-header\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"umbrellaChosen") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":15,"column":7}}})) != null ? stack1 : "")
    + "</div>\n";
},"useData":true}));

Handlebars.registerPartial("events-map-data", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "bounds  = new google.maps.LatLngBounds();\n\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"data") : depth0),{"name":"each","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":51,"column":0},"end":{"line":61,"column":9}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "loc = new google.maps.LatLng("
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lat") : depth0), depth0))
    + ", "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lng") : depth0), depth0))
    + ");\nbounds.extend(loc);\n\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(data && lookupProperty(data,"last")),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":55,"column":0},"end":{"line":60,"column":7}}})) != null ? stack1 : "");
},"3":function(container,depth0,helpers,partials,data) {
    return "/** Once all the map marker locations are set, make the map fit to the boundaries of the current markers, so the area seen on the map isn't too huge */\nmap = new google.maps.Map(document.getElementById('map'), controlConfig);\nmap.fitBounds(bounds);\nmap.panToBounds(bounds);\n";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"data") : depth0),{"name":"each","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":73,"column":0},"end":{"line":175,"column":9}}})) != null ? stack1 : "");
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "var newIndex = "
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + " + 1;\nvar stringIndex = newIndex.toString() + '\\n';\nvar defaultIcon = {\nurl: '/img/icons/map-marker.svg',\nscaledSize: new google.maps.Size(50, 50),\norigin: new google.maps.Point(0, 0),\nlabelOrigin: new google.maps.Point(25, 20),\nanchor: new google.maps.Point(0, 40)\n};\nvar selectedIcon = {\nurl: '/img/icons/map-marker-hover.svg',\nscaledSize: new google.maps.Size(50, 50),\norigin: new google.maps.Point(0, 0),\nlabelOrigin: new google.maps.Point(25, 20),\nzIndex: 100,\nanchor: new google.maps.Point(0, 40)\n};\nvar rsvpdIcon = {\nurl: '/img/icons/map-marker-rsvpd.svg',\nscaledSize: new google.maps.Size(50, 50),\norigin: new google.maps.Point(0, 0),\nlabelOrigin: new google.maps.Point(25, 20),\nanchor: new google.maps.Point(0, 40)\n};\nvar marker"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + " = new google.maps.Marker({\nposition: new google.maps.LatLng("
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lat") : depth0), depth0))
    + ","
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lng") : depth0), depth0))
    + "),\nlabel: {text: stringIndex, color: \"white\", fontSize: '20px', fontFamily: \"Oswald\"},\nsize: new google.maps.Size(20, 20),\norigin: new google.maps.Point(0, 0),\nanchor: new google.maps.Point(0, 40),\nicon: "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"rsvp_required") : depth0),"===",1,{"name":"compare","hash":{},"fn":container.program(7, data, 0),"inverse":container.program(9, data, 0),"data":data,"loc":{"start":{"line":104,"column":6},"end":{"line":104,"column":80}}})) != null ? stack1 : "")
    + ",\nzIndex: "
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ",\nmap: map\n});\nmarkerList["
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "] = marker"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ";\n\n\n\n/**\n*  This popup is modified from the Google Maps API page: https://developers.google.com/maps/documentation/javascript/examples/overlay-popup\n*\n*  First, the jQuery creates the popup DOM for each event's data, and appends it to .events-map-popup-content\n*\n*  Then, each event's custom popup DOM is then saved into a new popup variable associated with the lat/lng of its marker.\n*\n*  This uses definePopupClass() to create and draw the popup, but that function is defined after initMap()\n*\n*/\n\njQuery('<div id=\"popup-content"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "\"><div class=\"infowindow\"><a href=\"/events/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" target=\"_blank\"><div class=\"infowindow-data\"><p class=\"event-title\">"
    + alias2(lookupProperty(helpers,"safetitle").call(alias3,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"safetitle","hash":{},"data":data,"loc":{"start":{"line":123,"column":161},"end":{"line":123,"column":180}}}))
    + "</p><div class=\"events-section\"><p class=\"info-time\">"
    + alias2(lookupProperty(helpers,"displayMonth").call(alias3,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayMonth","hash":{},"data":data,"loc":{"start":{"line":123,"column":233},"end":{"line":123,"column":260}}}))
    + " "
    + alias2(lookupProperty(helpers,"displayDay").call(alias3,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayDay","hash":{},"data":data,"loc":{"start":{"line":123,"column":261},"end":{"line":123,"column":286}}}))
    + ", "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"start_time") : depth0), depth0))
    + "</p></div></div>"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"image_url") : depth0),{"name":"if","hash":{},"fn":container.program(11, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":123,"column":318},"end":{"line":123,"column":422}}})) != null ? stack1 : "")
    + "</a><div class=\"popup-bubble-close\" id=\"close-poppup-"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "\" onclick=\"closePoppup("
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ");\"></div></div></div>').appendTo('.events-map-popup-content');\n\ndefinePopupClass();\npopup"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + " = new Popup(\nnew google.maps.LatLng("
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lat") : depth0), depth0))
    + ","
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lng") : depth0), depth0))
    + "),\ndocument.getElementById('popup-content"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "')\n);\npopupList["
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "] = popup"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ";\n\n/**\n*\n*  These below are the map Events (listeners, not to be confused by SC Events).\n*\n*  Map Events are described in:\n*  https://developers.google.com/maps/documentation/javascript/events\n*\n*  Map Triggers are described in:\n*  https://developers.google.com/maps/documentation/javascript/reference/event#event.trigger\n*\n*/\nmarker"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ".addListener('click', function() {\ncloseAllPoppups();\npopup"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ".setMap(map);\njQuery('.tile-"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "').children('.event-tile').addClass('hover');\n});\n\nmarker"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ".addListener('mouseover', function() {\nmarker"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ".setIcon(selectedIcon);\nmarker"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ".setZIndex(100);\njQuery('.tile-"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "').children('.event-tile').addClass('hover');\n});\n\nmarker"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ".addListener('mouseout', function() {\nif (!jQuery('#popup-content"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "').length) {\nmarker"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ".setIcon("
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"rsvp_required") : depth0),"===",1,{"name":"compare","hash":{},"fn":container.program(7, data, 0),"inverse":container.program(9, data, 0),"data":data,"loc":{"start":{"line":157,"column":25},"end":{"line":157,"column":99}}})) != null ? stack1 : "")
    + ");\nmarker"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ".setZIndex("
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ");\njQuery('.tile-"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "').children('.event-tile').removeClass('hover');\n}\n});\n\njQuery('.tile-"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "').on('mouseover', function() {\ngoogle.maps.event.trigger(marker"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ", \"mouseover\");\n});\n\njQuery('.tile-"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "').on('mouseout', function() {\ngoogle.maps.event.trigger(marker"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ", \"mouseout\");\n});\n\npopup"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + ".addListener('click', function() {\nconsole.log('listener triggered for popup"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "');\n});\n\n";
},"7":function(container,depth0,helpers,partials,data) {
    return "rsvpdIcon";
},"9":function(container,depth0,helpers,partials,data) {
    return "defaultIcon";
},"11":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"info-image\"><img src=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"image_url") : depth0), depth0))
    + "\" class=\"info-image-src\" /></div>";
},"13":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"data") : depth0),"===",0,{"name":"compare","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":177,"column":0},"end":{"line":198,"column":12}}})) != null ? stack1 : "");
},"14":function(container,depth0,helpers,partials,data) {
    return "/**\n* If there is no data, then we need to redraw the map to get rid of existing map pins.\n* Since we're redoing the map with no data, we lose the previous map's boundaries.\n* So, we will just show the Continental US\n*/\nmap = new google.maps.Map(document.getElementById('map'));\n\nvar latNE = '45.2538';\nvar lngNE = '-69.4455';\nvar latSW = '30.8406';\nvar lngSW = '-115.2838';\n\nvar emptybounds  = new google.maps.LatLngBounds();\nloc1 = new google.maps.LatLng(latNE, lngNE);\nloc2 = new google.maps.LatLng(latSW, lngSW);\nemptybounds.extend(loc2);\nemptybounds.extend(loc1);\nmap.fitBounds(emptybounds);\nmap.panToBounds(emptybounds);\n\n";
},"16":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"data") : depth0),{"name":"each","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":387,"column":0},"end":{"line":389,"column":9}}})) != null ? stack1 : "");
},"17":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "closePoppup("
    + container.escapeExpression(container.lambda((data && lookupProperty(data,"index")), depth0))
    + ");\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"events-map-data\">\n<!-- **\n*  When the data loads, the map JS will load each event's data into DOM that will serve as the popup, when you click on each map marker.\n*  Each event's popup DOM will load below into .events-map-popup-content.\n*  When populating the map with markers, and putting the popups over each marker, the maps JS will pull and remove each marker's popup DOM from here.\n* -->\n<div class=\"events-map-popup-content\"></div>\n<script>\n/**\n*  a lot of these values are initialized in initMap() below.\n*  But we also need a lot of these values in subsequent functions, like when making the map marker popups and when re-drawing the map. So we make them global here.\n*/\nvar map;\nvar bounds;\nvar geocoder;\nvar markerList = [];\nvar popupList = [];\n\nvar lat0;\nvar lng0;\nvar lat1;\nvar lng1;\n\nvar latSearch;\nvar lngSearch;\nvar rlatlng = {};\nvar rdegrees = 90;\n\nvar controlConfig = {\nzoomControl: true,\nmapTypeControl: true,\nscaleControl: true,\nstreetViewControl: false,\nrotateControl: true,\nfullscreenControl: true\n};\n\n/**\n* Initializes the events map\n*\n* @var geocoder    {object} is a google maps Geocoder instance\n* @var bounds      {object} is a lat/lng size object that limits the size of the map to the content we're showing\n* @var markerList  {object}is an object that holds the data for each event marker. It's set up the way it is in order to most easily accommodate a lot more complex data if we need it. But it's a style preference.\n*/\nfunction initMap() {\n\ngeocoder = new google.maps.Geocoder();\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"data") : depth0),">",0,{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":48,"column":0},"end":{"line":62,"column":12}}})) != null ? stack1 : "")
    + "\n/**\n* This handlebars loop walks the events results array and assigns to each:\n*\n* - its marker icon\n* - marker number label\n* - popup info window with the event data and link to the event page\n* - hover behavior, linking the map view marker's hover state to its list-view counterpart.\n*/\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"data") : depth0),">",0,{"name":"compare","hash":{},"fn":container.program(5, data, 0),"inverse":container.program(13, data, 0),"data":data,"loc":{"start":{"line":72,"column":0},"end":{"line":199,"column":12}}})) != null ? stack1 : "")
    + "}\n\n\n/** Defines the Popup class. */\nfunction definePopupClass() {\n/**\n* A customized popup on the map.\n* @param {!google.maps.LatLng} position\n* @param {!Element} content\n* @constructor\n* @extends {google.maps.OverlayView}\n*/\nPopup = function(position, content) {\nthis.position = position;\n\ncontent.classList.add('popup-bubble-content');\n\nvar pixelOffset = document.createElement('div');\npixelOffset.classList.add('popup-bubble-anchor');\npixelOffset.appendChild(content);\n\nthis.anchor = document.createElement('div');\nthis.anchor.classList.add('popup-tip-anchor');\nthis.anchor.appendChild(pixelOffset);\n\n// Optionally stop clicks, etc., from bubbling up to the map.\nthis.stopEventPropagation();\n};\n// NOTE: google.maps.OverlayView is only defined once the Maps API has\n// loaded. That is why Popup is defined inside initMap().\nPopup.prototype = Object.create(google.maps.OverlayView.prototype);\n\n/** Called when the popup is added to the map. */\nPopup.prototype.onAdd = function() {\nthis.getPanes().floatPane.appendChild(this.anchor);\n};\n\n/** Called when the popup is removed from the map. */\nPopup.prototype.onRemove = function() {\nif (this.anchor.parentElement) {\nthis.anchor.parentElement.removeChild(this.anchor);\n}\n};\n\n/** Called when the popup needs to draw itself. */\nPopup.prototype.draw = function() {\nvar divPosition = this.getProjection().fromLatLngToDivPixel(this.position);\n// Hide the popup when it is far out of view.\nvar display =\nMath.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000 ?\n'block' :\n'none';\n\n/**\n*\n*  Here we're finding out if the popup is on the left 61% of the map area.\n*\n*  If it is, then we add the class .lefthand, and floats the popup off-center by 70px\n*\n*/\nlat0 = map.getBounds().getNorthEast().lat();\nlat1 = map.getBounds().getSouthWest().lat();\nvar sixtyPercent = ((lat1 - lat0) * 0.61) + lat0;\n\nif (display === 'block') {\nif (divPosition.x < sixtyPercent) {\nthis.anchor.classList.add('lefthand');\nvar divPositionSixty = divPosition.x + 70;\nthis.anchor.style.left = divPositionSixty + 'px';\n} else {\nthis.anchor.classList.remove('lefthand');\nthis.anchor.style.left = divPosition.x + 'px';\n}\nvar anchorMarkerOffset = divPosition.y - 40;\nthis.anchor.style.top = anchorMarkerOffset + 'px';\n}\nif (this.anchor.style.display !== display) {\nthis.anchor.style.display = display;\n}\n};\n\n/** Stops clicks/drags from bubbling up to the map. */\nPopup.prototype.stopEventPropagation = function() {\nvar anchor = this.anchor;\nanchor.style.cursor = 'auto';\n\n['click', 'dblclick', 'contextmenu', 'wheel', 'mousedown', 'touchstart',\n'pointerdown']\n.forEach(function(event) {\nanchor.addEventListener(event, function(e) {\ne.stopPropagation();\n});\n});\n};\n}\n\n\n/**\n* This function takes the current map boundaries, and finds the zip code of the center point\n*\n* @param geocoder {object} the google maps geocoder instance that looks for lat/lng boundaries, defined earlier\n*/\nfunction searchCurrentMap(geocoder, currentRadius, latlng) {\n/** this calculates the closest radius to search in */\nvar radiusArray = [5, 10, 50, 100, 200, 500, 3000];\nfunction radiusOption() {\nfor (let i=0; i<radiusArray.length; i++) {\nif (radiusArray[i] > currentRadius) {\nreturn radiusArray[i];\n}\n}\n}\n\n\n/**\n* This function looks at the center point of the map, and if there is something there (eg not the middle of the ocean) it will get the zip code of that point.\n*\n* @param location {object} the google maps geocoder instance that looks for lat/lng boundaries, defined earlier\n* @param results {array} the address at the center of the map\n*/\n\nnoZip = false;\njQuery('[name=\"radius\"] option').removeAttr('selected');\njQuery('[name=\"radius\"] option[value=\"' + radiusOption() + '\"]').attr('selected','selected');\njQuery('#search-form').find('input[name=\"lat\"]').val(latlng.lat);\njQuery('#search-form').find('input[name=\"lng\"]').val(latlng.lng);\njQuery('#lat').change();\n}\n\n/**\n* This function calculates the center point of the map, and the radius of the current map view. Then it runs the function to see if there is a zip code. If there is no zip code, it will change the center point in an expanding radius until it finds a valid zip code.\n*\n* @var {object} rlatlng is the coordinates of the centerpoint.\n* @var {number} rcurrentRadius is the radius of the current map view.\n*/\njQuery('.reload-map').click(function() {\n\nlat0 = map.getBounds().getNorthEast().lat();\nlng0 = map.getBounds().getNorthEast().lng();\nlat1 = map.getBounds().getSouthWest().lat();\nlng1 = map.getBounds().getSouthWest().lng();\n\nlatSearch = (lat0 + lat1) / 2;\nlngSearch = (lng0 + lng1) / 2;\nrlatlng = {lat: latSearch, lng: lngSearch};\n\n/**\n* this calculates the number of miles in half the height of the map\n* there are approx 69.2 miles per degree latitude\n*/\nvar rcurrentRadius = ((lat0 - lat1) / 2) * 69.2;\nsearchCurrentMap(geocoder, rcurrentRadius, rlatlng);\n});\n\n/** toggling between map and list view: */\njQuery('.map-toggle__option').click(function() {\njQuery('.map-toggle__option').removeClass('disabled');\njQuery(this).addClass('disabled');\nif (jQuery(this).hasClass('map')) {\njQuery('.events-map').addClass('active');\njQuery('.events-grid').removeClass('active');\nmap.fitBounds(bounds);\nmap.panToBounds(bounds);\n} else {\njQuery('.events-grid').addClass('active');\njQuery('.events-map').removeClass('active');\n}\n});\n\nif (jQuery('.list').hasClass('disabled')) {\njQuery('.events-grid').addClass('active');\njQuery('.events-map').removeClass('active');\n} else {\njQuery('.events-map').addClass('active');\njQuery('.events-grid').removeClass('active');\n}\n\n\n\nfunction closePoppup(indexedPopup) {\npopupList[indexedPopup].setMap(null);\njQuery('.tile-' + indexedPopup).children('.event-tile').removeClass('hover');\ngoogle.maps.event.trigger(markerList[indexedPopup], \"mouseout\");\n};\n\nfunction closeAllPoppups() {\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"data") : depth0),">",0,{"name":"compare","hash":{},"fn":container.program(16, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":386,"column":0},"end":{"line":390,"column":12}}})) != null ? stack1 : "")
    + "}\n\n</script>\n<script src=\"https://maps.googleapis.com/maps/api/js?key=AIzaSyD1LqGvj-IryzE6EqCVWaCYMME6CEzxZuc&callback=initMap\"></script>\n\n</div>\n";
},"useData":true}));

Handlebars.registerPartial("events-map", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"events-map spinnermap\" data-total=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"total") : depth0), depth0))
    + "\">\n\n<div id=\"map\"></div>\n<button class=\"button blue-button reload-map\">Redo search in this area</button>\n\n</div>\n";
},"useData":true}));

Handlebars.registerPartial("events-rsvp-inline", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"inline-rsvp-placeholder-button\">\n<div class=\"button\">Attending</div>\n<!-- div class=\"inline-cancel-rsvp button\">Cancel RSVP</div -->\n</div>\n<div class=\"inline-rsvp-popout-form\">\n<form data-formaction=\"moleform\" method=\"POST\" data-busytemplate=\"true\" action=\"/api/v1/events/cancel_rsvp/\" class=\"clearfix row\">\n<ul class=\"no-placeholders "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">=","940",{"name":"compare","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":11,"column":27},"end":{"line":11,"column":82}}})) != null ? stack1 : "")
    + "\">\n<li class=\"error-message\"></li>\n<li><button type=\"submit\" class=\"event-un-rsvp button\">Cancel RSVP</button></li>\n</ul>\n</form>\n</div>\n<div class=\"inline-rsvp-popout-form active\">\n<div class=\"inline-rsvp-modal\">\n<div class=\"close-popup\">X</div>\n<header class=\"your-campaigns-header\">\n<h2>See You There</h2>\n</header>\n<div class=\"event-tile-details\">\n<p><strong>You replied: Going</strong></p>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"umbrella") : depth0)) != null ? lookupProperty(stack1,"0") : stack1)) != null ? lookupProperty(stack1,"name") : stack1),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":25,"column":0},"end":{"line":25,"column":111}}})) != null ? stack1 : "")
    + "\n<div class=\"event-title\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0)) != null ? stack1 : "")
    + "</div>\n<div class=\"umbrella-event-listing__date event-date-section\">"
    + container.escapeExpression(lookupProperty(helpers,"displayWeekDay").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayWeekDay","hash":{},"data":data,"loc":{"start":{"line":27,"column":61},"end":{"line":27,"column":90}}}))
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_time") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":27,"column":91},"end":{"line":27,"column":130}}})) != null ? stack1 : "")
    + "</div>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"location") : depth0),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":28,"column":0},"end":{"line":30,"column":7}}})) != null ? stack1 : "")
    + "<div class=\"category-tags\">"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"categories") : depth0),{"name":"each","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":31,"column":27},"end":{"line":31,"column":124}}})) != null ? stack1 : "")
    + "</div>\n</div>\n</div>\n</div>\n";
},"2":function(container,depth0,helpers,partials,data) {
    return "two-column-form";
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"umbrella-event-listing__partof\">Part of "
    + container.escapeExpression(container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"umbrella") : depth0)) != null ? lookupProperty(stack1,"0") : stack1)) != null ? lookupProperty(stack1,"name") : stack1), depth0))
    + "</div>";
},"6":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"start_time") : depth0), depth0));
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p class=\"info-location clickable\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"location") : depth0), depth0)) != null ? stack1 : "")
    + "</p>\n";
},"10":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"category-tag "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "-icon\" data=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "</div>";
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"action_took") : depth0),{"name":"unless","hash":{},"fn":container.program(13, data, 0),"inverse":container.program(25, data, 0),"data":data,"loc":{"start":{"line":36,"column":0},"end":{"line":85,"column":11}}})) != null ? stack1 : "");
},"13":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"no_inline_rsvp") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.program(19, data, 0),"data":data,"loc":{"start":{"line":37,"column":0},"end":{"line":69,"column":7}}})) != null ? stack1 : "");
},"14":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row\"><div class=\"no-inline\"><a href=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"info_url") : depth0), depth0))
    + "\" target=\"_blank\" class=\"action-button-rsvp\">"
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"reservations_accepted") : depth0),"===","1",{"name":"compare","hash":{},"fn":container.program(15, data, 0),"inverse":container.program(17, data, 0),"data":data,"loc":{"start":{"line":38,"column":106},"end":{"line":38,"column":185}}})) != null ? stack1 : "")
    + "</a></div></div>\n";
},"15":function(container,depth0,helpers,partials,data) {
    return "RSVP Now";
},"17":function(container,depth0,helpers,partials,data) {
    return "Details";
},"19":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"reservations_accepted") : depth0),"===","1",{"name":"compare","hash":{},"fn":container.program(20, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":40,"column":0},"end":{"line":68,"column":12}}})) != null ? stack1 : "");
},"20":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"all").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"email") : depth0),(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),(depth0 != null ? lookupProperty(depth0,"last_name") : depth0),(depth0 != null ? lookupProperty(depth0,"zipcode") : depth0),(depth0 != null ? lookupProperty(depth0,"phone") : depth0),{"name":"all","hash":{},"fn":container.program(21, data, 0),"inverse":container.program(23, data, 0),"data":data,"loc":{"start":{"line":41,"column":0},"end":{"line":67,"column":8}}})) != null ? stack1 : "");
},"21":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<form data-formaction=\"moleform\" method=\"POST\" data-busytemplate=\"true\" action=\"/u/actions/"
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" class=\"clearfix row\">\n<ul class=\"no-placeholders\">\n<input type=\"hidden\" name=\"sourceURL\" value=\""
    + alias1(lookupProperty(helpers,"thisurl").call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"thisurl","hash":{},"data":data,"loc":{"start":{"line":44,"column":45},"end":{"line":44,"column":56}}}))
    + "\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"event-collection"),depth0,{"name":"event-collection","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</ul>\n</form>\n";
},"23":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.escapeExpression, alias2=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"inline-rsvp-placeholder-button\"><div class=\"inline-rsvp-show-form button\">RSVP</div></div>\n<div class=\"inline-rsvp-popout-form\">\n<div class=\"inline-rsvp-modal\">\n<div class=\"close-popup\">Close X</div>\n<header class=\"your-campaigns-header\">\n<h2>Complete Your RSVP</h2>\n</header>\n<form data-formaction=\"moleform\" method=\"POST\" data-busytemplate=\"true\" action=\"/u/actions/"
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" class=\"clearfix row\">\n<ul class=\"no-placeholders "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias2,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">=","940",{"name":"compare","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":57,"column":27},"end":{"line":57,"column":82}}})) != null ? stack1 : "")
    + "\">\n<li class=\"error-message\"></li>\n<li class=\"two-column-form\">\n<input type=\"hidden\" name=\"sourceURL\" value=\""
    + alias1(lookupProperty(helpers,"thisurl").call(alias2,{"name":"thisurl","hash":{},"data":data,"loc":{"start":{"line":60,"column":45},"end":{"line":60,"column":56}}}))
    + "\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"event-collection"),depth0,{"name":"event-collection","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n</ul>\n</form>\n</div>\n</div>\n";
},"25":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"no_inline_rsvp") : depth0),{"name":"unless","hash":{},"fn":container.program(26, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":71,"column":0},"end":{"line":84,"column":11}}})) != null ? stack1 : "");
},"26":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"inline-rsvp-placeholder-button\">\n<div class=\"button\">Attending</div>\n<!-- div class=\"inline-cancel-rsvp button\">Cancel RSVP</div -->\n</div>\n<div class=\"inline-rsvp-popout-form\">\n<form data-formaction=\"moleform\" method=\"POST\" data-busytemplate=\"true\" action=\"/api/v1/events/cancel_rsvp/\" class=\"clearfix row\">\n<ul class=\"no-placeholders "
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">=","940",{"name":"compare","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":78,"column":27},"end":{"line":78,"column":82}}})) != null ? stack1 : "")
    + "\">\n<li class=\"error-message\"></li>\n<li><button type=\"submit\" class=\"event-un-rsvp button\">Cancel RSVP</button><li>\n</ul>\n</form>\n</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<!-- This page is much like the events-rsvp partial, with some notable exceptions. -->\n<!-- - we have to have an inline RSVP modal. Both for rsvp process as well as for canceling RSVP. -->\n<!-- - We don't have to do anything for the \"unless no_inline_rsvp\" condition if the type isn't ACT. -->\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_taken") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(12, data, 0),"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":86,"column":7}}})) != null ? stack1 : "")
    + "<div class=\"cancel-popout-container\">\n<div class=\"inline-rsvp-popout-form inline-rsvp-cancel-popout\">\n<div class=\"inline-rsvp-modal\">\n<div class=\"close-popup\">X</div>\n<header class=\"your-campaigns-header\">\n<h2>You'll Be Missed</h2>\n</header>\n<div class=\"event-tile-details\">\n<p><strong>You replied: <span class=\"event-going-status\">Not Going</span></strong></p>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"umbrella") : depth0)) != null ? lookupProperty(stack1,"0") : stack1)) != null ? lookupProperty(stack1,"name") : stack1),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":96,"column":0},"end":{"line":96,"column":111}}})) != null ? stack1 : "")
    + "\n<div class=\"event-title\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0)) != null ? stack1 : "")
    + "</div>\n<div class=\"umbrella-event-listing__date event-date-section\">"
    + container.escapeExpression(lookupProperty(helpers,"displayWeekDay").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayWeekDay","hash":{},"data":data,"loc":{"start":{"line":98,"column":61},"end":{"line":98,"column":90}}}))
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_time") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":98,"column":91},"end":{"line":98,"column":130}}})) != null ? stack1 : "")
    + "</div>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"location") : depth0),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":99,"column":0},"end":{"line":101,"column":7}}})) != null ? stack1 : "")
    + "<div class=\"category-tags\">"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"categories") : depth0),{"name":"each","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":102,"column":27},"end":{"line":102,"column":124}}})) != null ? stack1 : "")
    + "</div>\n</div>\n</div>\n</div>\n</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("events-rsvp", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p class=\"action-complete\"><strong>Thanks for your interest! Some events require one more step to register&mdash;be sure to check out the "
    + container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"event-info-link",{"name":"$","hash":{"content":"event info"},"data":data,"loc":{"start":{"line":2,"column":138},"end":{"line":2,"column":182}}}))
    + " for more details.</strong>\n";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"action_took") : depth0),{"name":"unless","hash":{},"fn":container.program(4, data, 0),"inverse":container.program(18, data, 0),"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":45,"column":11}}})) != null ? stack1 : "");
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"no_inline_rsvp") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.program(12, data, 0),"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":28,"column":7}}})) != null ? stack1 : "");
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"event_status") : depth0),"===","ACT",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":6,"column":0},"end":{"line":8,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"event_status") : depth0),"===","CAP",{"name":"compare","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":0},"end":{"line":12,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"event_status") : depth0),"===","CANC",{"name":"compare","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":13,"column":0},"end":{"line":16,"column":12}}})) != null ? stack1 : "");
},"6":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row\"><div class=\"no-inline\"><a href=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"info_url") : depth0), depth0))
    + "\" target=\"_blank\" class=\"action-button-rsvp\">RSVP Now</a></div></div>\n";
},"8":function(container,depth0,helpers,partials,data) {
    return "<div class=\"row\"><div class=\"disabled-rsvp\">RSVP Now</div></div>\n<p class=\"action-complete\"><strong>Sorry, this event has reached capacity.</strong>\n";
},"10":function(container,depth0,helpers,partials,data) {
    return "<div class=\"row\"><div class=\"disabled-rsvp\">RSVP Now</div></div>\n<p class=\"action-complete\"><strong>Sorry, this event has been canceled.</strong>\n";
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"requires_external_signup") : depth0),"===","0",{"name":"compare","hash":{},"fn":container.program(13, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":18,"column":0},"end":{"line":24,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"requires_external_signup") : depth0),"===","1",{"name":"compare","hash":{},"fn":container.program(16, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":25,"column":0},"end":{"line":27,"column":12}}})) != null ? stack1 : "");
},"13":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<form data-formaction=\"moleform\" method=\"POST\" data-busytemplate=\"true\" action=\"/u/actions/"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" class=\"clearfix row\">\n<ul class=\"no-placeholders "
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">=","940",{"name":"compare","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":20,"column":27},"end":{"line":20,"column":82}}})) != null ? stack1 : "")
    + "\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"event-collection"),depth0,{"name":"event-collection","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</ul>\n</form>\n";
},"14":function(container,depth0,helpers,partials,data) {
    return "two-column-form";
},"16":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"info_url") : depth0), depth0))
    + "\" target=\"_blank\" class=\"action-button-rsvp\">RSVP Now</a>\n";
},"18":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"no_inline_rsvp") : depth0),{"name":"if","hash":{},"fn":container.program(19, data, 0),"inverse":container.program(26, data, 0),"data":data,"loc":{"start":{"line":30,"column":0},"end":{"line":44,"column":7}}})) != null ? stack1 : "");
},"19":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"event_status") : depth0),"===","ACT",{"name":"compare","hash":{},"fn":container.program(20, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":31,"column":0},"end":{"line":33,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"event_status") : depth0),"===","CAP",{"name":"compare","hash":{},"fn":container.program(22, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":34,"column":0},"end":{"line":37,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"event_status") : depth0),"===","CANC",{"name":"compare","hash":{},"fn":container.program(24, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":38,"column":0},"end":{"line":41,"column":12}}})) != null ? stack1 : "");
},"20":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row\"><a href=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"info_url") : depth0), depth0))
    + "\" class=\"button\" target=\"_blank\">Event Details</a></div>\n";
},"22":function(container,depth0,helpers,partials,data) {
    return "<div class=\"row\"><div class=\"disabled-rsvp\">Event Details</div></div>\n<p class=\"action-complete\"><strong>Sorry, this event has reached capacity.</strong>\n";
},"24":function(container,depth0,helpers,partials,data) {
    return "<div class=\"row\"><div class=\"disabled-rsvp\">Event Details</div></div>\n<p class=\"action-complete\"><strong>Sorry, this event has been canceled.</strong>\n";
},"26":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"action-complete-section",{"name":"$","hash":{"what":"said you're&nbsp;in"},"data":data,"loc":{"start":{"line":43,"column":0},"end":{"line":43,"column":58}}}))
    + "\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"action_taken") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":46,"column":7}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("events-search-large", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return " selected ";
},"3":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<option value=\""
    + alias2(alias1((data && lookupProperty(data,"key")), depth0))
    + "\""
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depths[1] != null ? lookupProperty(depths[1],"category") : depths[1]),"===",(data && lookupProperty(data,"key")),{"name":"compare","hash":{},"fn":container.program(4, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":44},"end":{"line":9,"column":100}}})) != null ? stack1 : "")
    + ">"
    + alias2(alias1(depth0, depth0))
    + "</option>";
},"4":function(container,depth0,helpers,partials,data) {
    return " selected";
},"6":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<option value=\""
    + alias2(alias1((data && lookupProperty(data,"key")), depth0))
    + "\""
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depths[1] != null ? lookupProperty(depths[1],"type") : depths[1]),"===",(data && lookupProperty(data,"key")),{"name":"compare","hash":{},"fn":container.program(4, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":39},"end":{"line":16,"column":91}}})) != null ? stack1 : "")
    + ">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "</option>";
},"8":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<option value=\""
    + alias2(alias1((data && lookupProperty(data,"key")), depth0))
    + "\""
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depths[1] != null ? lookupProperty(depths[1],"umbrella_event_id") : depths[1]),"===",(data && lookupProperty(data,"key")),{"name":"compare","hash":{},"fn":container.program(4, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":23,"column":43},"end":{"line":23,"column":108}}})) != null ? stack1 : "")
    + ">"
    + alias2(alias1(depth0, depth0))
    + "</option>";
},"10":function(container,depth0,helpers,partials,data) {
    return "selected=\"selected\"";
},"12":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "value=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"zip") : depth0), depth0))
    + "\"";
},"14":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"zipcode") : stack1),{"name":"if","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":37,"column":86},"end":{"line":37,"column":149}}})) != null ? stack1 : "");
},"15":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "value=\""
    + container.escapeExpression(lookupProperty(helpers,"limit").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"zipcode") : stack1),5,{"name":"limit","hash":{},"data":data,"loc":{"start":{"line":37,"column":115},"end":{"line":37,"column":141}}}))
    + "\"";
},"17":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " min=\""
    + container.escapeExpression(lookupProperty(helpers,"todaysDate").call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"todaysDate","hash":{},"data":data,"loc":{"start":{"line":42,"column":182},"end":{"line":42,"column":196}}}))
    + "\" max=\"2026-12-31\"";
},"19":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "value=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"start_date") : depth0), depth0))
    + "\"";
},"21":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "min=\""
    + container.escapeExpression(lookupProperty(helpers,"todaysDate").call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"todaysDate","hash":{},"data":data,"loc":{"start":{"line":42,"column":410},"end":{"line":42,"column":424}}}))
    + "\" max=\"2026-12-31\"";
},"23":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "value=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"end_date") : depth0), depth0))
    + "\"";
},"25":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "value=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"keywords") : depth0), depth0))
    + "\"";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"search-bar events-search-large\">\n<form id=\"search-form\" action=\"#\">\n<input type=\"hidden\" name=\"limit\" value=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"apilimit") : depth0), depth0))
    + "\">\n<ul class=\"search-types\">\n<li class=\"select-field categories-search\">\n<label for=\"issues-field\">Category:</label>\n<select name=\"category\" id=\"issues-field\" tabindex=\"1\">\n<option "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depths[1] != null ? lookupProperty(depths[1],"category") : depths[1]),"===","none",{"name":"compare","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":8},"end":{"line":8,"column":67}}})) != null ? stack1 : "")
    + " value=\"none\">All</option>\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"categories") : depth0),{"name":"each","hash":{},"fn":container.program(3, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":0},"end":{"line":9,"column":127}}})) != null ? stack1 : "")
    + "\n</select>\n</li>\n<li class=\"select-field type-search\">\n<label for=\"issues-field\">Type:</label>\n<select name=\"type_id\" id=\"issues-field\" tabindex=\"1\">\n<option value=\"none\">All</option>\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"types") : depth0),{"name":"each","hash":{},"fn":container.program(6, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":0},"end":{"line":16,"column":123}}})) != null ? stack1 : "")
    + "\n</select>\n</li>\n<li class=\"select-field umbrella-search\">\n<label for=\"issues-field\">Coordinated Event:</label>\n<select name=\"umbrella_event_id\" id=\"issues-field\" tabindex=\"1\">\n<option value=\"none\">All</option>\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"umbrellas") : depth0),{"name":"each","hash":{},"fn":container.program(8, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":23,"column":0},"end":{"line":23,"column":135}}})) != null ? stack1 : "")
    + "\n</select>\n</li>\n<li class=\"select-field zip-search\" >\n<label for=\"zipF\" id=\"location-field\">Near Zip Code:</label>\n<select id=\"radius\" name=\"radius\" tabindex=\"2\" class=\"noselectize\">\n<option value=\"5\">5 miles</option>\n<option value=\"10\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"radius") : depth0),"#==",10,{"name":"compare","hash":{},"fn":container.program(10, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":30,"column":19},"end":{"line":30,"column":78}}})) != null ? stack1 : "")
    + ">10 miles</option>\n<option value=\"50\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"radius") : depth0),"#==",50,{"name":"compare","hash":{},"fn":container.program(10, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":31,"column":19},"end":{"line":31,"column":78}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"radius") : depth0),{"name":"unless","hash":{},"fn":container.program(10, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":31,"column":78},"end":{"line":31,"column":126}}})) != null ? stack1 : "")
    + ">50 miles</option>\n<option value=\"100\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"radius") : depth0),"#==",100,{"name":"compare","hash":{},"fn":container.program(10, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":32,"column":20},"end":{"line":32,"column":80}}})) != null ? stack1 : "")
    + ">100 miles</option>\n<option value=\"200\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"radius") : depth0),"#==",200,{"name":"compare","hash":{},"fn":container.program(10, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":33,"column":20},"end":{"line":33,"column":80}}})) != null ? stack1 : "")
    + ">200 miles</option>\n<option value=\"500\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"radius") : depth0),"#==",500,{"name":"compare","hash":{},"fn":container.program(10, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":34,"column":20},"end":{"line":34,"column":80}}})) != null ? stack1 : "")
    + ">500 miles</option>\n<option value=\"3000\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"radius") : depth0),"#==",3000,{"name":"compare","hash":{},"fn":container.program(10, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":35,"column":21},"end":{"line":35,"column":82}}})) != null ? stack1 : "")
    + ">Anywhere</option>\n</select>\n<input type=\"tel\" id=\"zipF\" tabindex=\"3\" name=\"zip\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"zip") : depth0),{"name":"if","hash":{},"fn":container.program(12, data, 0, blockParams, depths),"inverse":container.program(14, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":37,"column":52},"end":{"line":37,"column":156}}})) != null ? stack1 : "")
    + ">\n</li>\n<li class=\"date-fields date-search\">\n<label for=\"start-date-field\" id=\"date-field\">Dates:</label>\n<div class=\"date-combo\">\n<span><label for=\"start-date-field\"></label><input tabindex=\"4\" title=\"Start Date\" placeholder=\"mm/dd/yyy\" name=\"start_date\" id=\"start-date-field\" type=\"date\" "
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_iOS") : depth0),{"name":"unless","hash":{},"fn":container.program(17, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":42,"column":159},"end":{"line":42,"column":225}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"if","hash":{},"fn":container.program(19, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":42,"column":226},"end":{"line":42,"column":273}}})) != null ? stack1 : "")
    + "></span><i>⟶</i><span><label for=\"end-date-field\"></label><input tabindex=\"5\" id=\"end-date-field\" title=\"End Date\" "
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_iOS") : depth0),{"name":"unless","hash":{},"fn":container.program(21, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":42,"column":388},"end":{"line":42,"column":453}}})) != null ? stack1 : "")
    + " placeholder=\"mm/dd/yyy\" name=\"end_date\" type=\"date\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"end_date") : depth0),{"name":"if","hash":{},"fn":container.program(23, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":42,"column":506},"end":{"line":42,"column":549}}})) != null ? stack1 : "")
    + "></span>\n</div>\n</li>\n<li class=\"search-field\">\n<label for=\"event-search\">Search:</label>\n<input type=\"search\" tabindex=\"6\" name=\"keywords\" placeholder=\"Search Events\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"keywords") : depth0),{"name":"if","hash":{},"fn":container.program(25, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":47,"column":78},"end":{"line":47,"column":121}}})) != null ? stack1 : "")
    + " id=\"event-search\" class=\"search-input\">\n<button class=\"button blue-button\" title=\"search\"></button>\n</li>\n</ul>\n<input type=\"hidden\" name=\"lat\" id=\"lat\" />\n<input type=\"hidden\" name=\"lng\" id=\"lng\" />\n</form>\n</div>\n";
},"useData":true,"useDepths":true}));

Handlebars.registerPartial("events-search", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "value=\""
    + container.escapeExpression(lookupProperty(helpers,"limit").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"zipcode") : depth0),5,{"name":"limit","hash":{},"data":data,"loc":{"start":{"line":6,"column":141},"end":{"line":6,"column":160}}}))
    + "\"";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"events-search search-bar\">\n<form id=\"events-search-form\" action=\"#\">\n<ul>\n<li><h2 class=\"h2\">More Events</h2></li>\n<li class=\"search-field\">\n<input type=\"search\" name=\"title\" maxlength=\"5\" title=\"5-Digit Zip Code\" pattern=\"^\\d{5}$\" placeholder=\"Search by zip\" "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"zipcode") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":6,"column":119},"end":{"line":6,"column":168}}})) != null ? stack1 : "")
    + " id=\"events-search\" class=\"search-input\">\n<button class=\"button search-button blue-button\" title=\"search\"></button>\n</li>\n</ul>\n</form>\n</div>\n";
},"useData":true}));

Handlebars.registerPartial("events-slider", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<button id=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "-prev\" class=\"swiper-prev\" title=\"Previous\"></button>\n";
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"numslide") : depth0), depth0));
},"5":function(container,depth0,helpers,partials,data) {
    return "3";
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"private_event") : depth0),"!=","1",{"name":"compare","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":12,"column":12}}})) != null ? stack1 : "");
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"slide "
    + alias2(lookupProperty(helpers,"switch").call(alias1,(data && lookupProperty(data,"index")),{"name":"switch","hash":{},"data":data,"loc":{"start":{"line":9,"column":18},"end":{"line":9,"column":35}}}))
    + " swiper-slide"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"prime_ev") : depth0),{"name":"if","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":48},"end":{"line":9,"column":84}}})) != null ? stack1 : "")
    + "\" data-index=\""
    + alias2(container.lambda((data && lookupProperty(data,"index")), depth0))
    + "\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"event-slide"),depth0,{"name":"event-slide","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"9":function(container,depth0,helpers,partials,data) {
    return " currentEvent";
},"11":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<button id=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "-next\" class=\"swiper-next\" title=\"Next\"></button>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row slider events-slider\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"events") : depth0)) != null ? lookupProperty(stack1,"2") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":4,"column":7}}})) != null ? stack1 : "")
    + "<div class=\"swiper-container\" data-slidenum=\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"numslide") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.program(5, data, 0),"data":data,"loc":{"start":{"line":5,"column":45},"end":{"line":5,"column":89}}})) != null ? stack1 : "")
    + "\">\n<div class=\"swiper-wrapper\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"events") : depth0),{"name":"each","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":13,"column":9}}})) != null ? stack1 : "")
    + "</div>\n</div>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"events") : depth0)) != null ? lookupProperty(stack1,"2") : stack1),{"name":"if","hash":{},"fn":container.program(11, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":0},"end":{"line":18,"column":7}}})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("featured-event", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "col-8";
},"3":function(container,depth0,helpers,partials,data) {
    return "has-featured";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"is_featured") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":66}}})) != null ? stack1 : "")
    + "\n<div class=\"event-title-block\">\n<div class=\"event-image-container\">\n<div class=\"event-image-container-inner\">"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"map-image"),depth0,{"name":"map-image","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n</div>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"belongs_to_umbrella_event") : depth0),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.program(10, data, 0),"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":17,"column":7}}})) != null ? stack1 : "")
    + "\n</div>\n\n<ul class=\"event-info\">\n<li class=\"event-date "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"location") : depth0),{"name":"if","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":22,"column":22},"end":{"line":22,"column":53}}})) != null ? stack1 : "")
    + "\">"
    + alias2(lookupProperty(helpers,"displayMonth").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayMonth","hash":{},"data":data,"loc":{"start":{"line":22,"column":55},"end":{"line":22,"column":82}}}))
    + " "
    + alias2(lookupProperty(helpers,"displayDay").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayDay","hash":{},"data":data,"loc":{"start":{"line":22,"column":83},"end":{"line":22,"column":108}}}))
    + ", "
    + alias2(lookupProperty(helpers,"displayYear").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayYear","hash":{},"data":data,"loc":{"start":{"line":22,"column":110},"end":{"line":22,"column":136}}}))
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_time") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":23,"column":0},"end":{"line":41,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"location") : depth0),{"name":"if","hash":{},"fn":container.program(29, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":42,"column":0},"end":{"line":42,"column":63}}})) != null ? stack1 : "")
    + "\n</li>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"location") : depth0),{"name":"if","hash":{},"fn":container.program(31, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":44,"column":0},"end":{"line":49,"column":7}}})) != null ? stack1 : "")
    + "</ul>\n<div class=\"event-details\">\n<div class=\"columns event-image-container\">\n<div class=\"event-image-container-inner\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"image_url") : depth0),{"name":"if","hash":{},"fn":container.program(34, data, 0),"inverse":container.program(35, data, 0),"data":data,"loc":{"start":{"line":54,"column":0},"end":{"line":62,"column":7}}})) != null ? stack1 : "")
    + "</div>\n</div>\n<div class=\"columns event-text\">\n<p class=\"event-instructions\">\n"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"long_description") : depth0), depth0)) != null ? stack1 : "")
    + "\n</p>\n\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">=","768",{"name":"compare","hash":{},"fn":container.program(43, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":70,"column":0},"end":{"line":84,"column":12}}})) != null ? stack1 : "")
    + "\n</div>\n\n</div>\n\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),"<","768",{"name":"compare","hash":{},"fn":container.program(51, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":90,"column":0},"end":{"line":98,"column":12}}})) != null ? stack1 : "")
    + "\n";
},"6":function(container,depth0,helpers,partials,data) {
    return "<p class=\"main-event\">Featured Event</p>";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"event-title-inner\">\n<div><span class=\"umbrella-event-listing__partof\">Part of "
    + container.escapeExpression(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"belongs_to_umbrella_event") : depth0)) != null ? lookupProperty(stack1,"title") : stack1), depth0))
    + "</span></div>\n<h3>"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0)) != null ? stack1 : "")
    + "</h3>\n</div>\n";
},"10":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"event-title-inner\">\n<h3>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0)) != null ? stack1 : "")
    + "</h3>\n</div>\n";
},"12":function(container,depth0,helpers,partials,data) {
    return "has-addr";
},"14":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"event-time\">\n"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"start_time") : depth0), depth0))
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","eastern",{"name":"compare","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":26,"column":0},"end":{"line":26,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","central",{"name":"compare","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":27,"column":0},"end":{"line":27,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","mountain",{"name":"compare","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":28,"column":0},"end":{"line":28,"column":53}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","pacific",{"name":"compare","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":29,"column":0},"end":{"line":29,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","alaska",{"name":"compare","hash":{},"fn":container.program(23, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":30,"column":0},"end":{"line":30,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","hawaii",{"name":"compare","hash":{},"fn":container.program(25, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":31,"column":0},"end":{"line":31,"column":51}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"end_time") : depth0),{"name":"if","hash":{},"fn":container.program(27, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":32,"column":0},"end":{"line":39,"column":7}}})) != null ? stack1 : "")
    + "</span>\n";
},"15":function(container,depth0,helpers,partials,data) {
    return " ET";
},"17":function(container,depth0,helpers,partials,data) {
    return " CT";
},"19":function(container,depth0,helpers,partials,data) {
    return " MT";
},"21":function(container,depth0,helpers,partials,data) {
    return " PT";
},"23":function(container,depth0,helpers,partials,data) {
    return " AKT";
},"25":function(container,depth0,helpers,partials,data) {
    return " HT";
},"27":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " - "
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"end_time") : depth0), depth0))
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","eastern",{"name":"compare","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":33,"column":0},"end":{"line":33,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","central",{"name":"compare","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":34,"column":0},"end":{"line":34,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","mountain",{"name":"compare","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":35,"column":0},"end":{"line":35,"column":53}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","pacific",{"name":"compare","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":36,"column":0},"end":{"line":36,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","alaska",{"name":"compare","hash":{},"fn":container.program(23, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":37,"column":0},"end":{"line":37,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","hawaii",{"name":"compare","hash":{},"fn":container.program(25, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":38,"column":0},"end":{"line":38,"column":51}}})) != null ? stack1 : "")
    + "\n";
},"29":function(container,depth0,helpers,partials,data) {
    return "<span class=\"event-border\">&nbsp;</span>";
},"31":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"event-addr\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"location") : depth0), depth0)) != null ? stack1 : "")
    + "</li>\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"lat") : depth0),"!=0",(depth0 != null ? lookupProperty(depth0,"lng") : depth0),{"name":"compare","hash":{},"fn":container.program(32, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":46,"column":0},"end":{"line":48,"column":12}}})) != null ? stack1 : "");
},"32":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"event-map\">(<a href=\"https://maps.google.com/maps?daddr="
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lat") : depth0), depth0))
    + "%2C"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lng") : depth0), depth0))
    + "&saddr=\" target=\"_blank\">Map</a>)</li>\n";
},"34":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,lookupProperty(helpers,"isExternalUrl").call(alias1,(depth0 != null ? lookupProperty(depth0,"image_url") : depth0),{"name":"isExternalUrl","hash":{},"data":data,"loc":{"start":{"line":55,"column":6},"end":{"line":55,"column":31}}}),{"name":"if","hash":{},"fn":container.program(35, data, 0),"inverse":container.program(37, data, 0),"data":data,"loc":{"start":{"line":55,"column":0},"end":{"line":59,"column":7}}})) != null ? stack1 : "");
},"35":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"map-image"),depth0,{"name":"map-image","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"37":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"image_url") : depth0), depth0))
    + "/@zx_175@zy_175\" alt=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0))
    + "\" class=\"event-image\" onerror=\"this.onerror=null;this.src='"
    + ((stack1 = lookupProperty(helpers,"ifor").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"image") : depth0),{"name":"ifor","hash":{},"fn":container.program(38, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":58,"column":113},"end":{"line":58,"column":385}}})) != null ? stack1 : "")
    + "';\">\n";
},"38":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"any").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"lat") : depth0),(depth0 != null ? lookupProperty(depth0,"lng") : depth0),{"name":"any","hash":{},"fn":container.program(39, data, 0),"inverse":container.program(41, data, 0),"data":data,"loc":{"start":{"line":58,"column":128},"end":{"line":58,"column":376}}})) != null ? stack1 : "");
},"39":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "https://maps.googleapis.com/maps/api/staticmap?center="
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lat") : depth0), depth0))
    + ","
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lng") : depth0), depth0))
    + "&size=400x400&maptype=roadmap&markers=color:red%7Clabel:C%7C"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lat") : depth0), depth0))
    + ","
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lng") : depth0), depth0))
    + "&key=AIzaSyD1LqGvj-IryzE6EqCVWaCYMME6CEzxZuc";
},"41":function(container,depth0,helpers,partials,data) {
    return "/img/default_image_small.jpg";
},"43":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"reservations_accepted") : depth0),"===","1",{"name":"compare","hash":{},"fn":container.program(44, data, 0),"inverse":container.program(49, data, 0),"data":data,"loc":{"start":{"line":71,"column":0},"end":{"line":83,"column":12}}})) != null ? stack1 : "");
},"44":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"email") : depth0),{"name":"if","hash":{},"fn":container.program(45, data, 0),"inverse":container.program(47, data, 0),"data":data,"loc":{"start":{"line":72,"column":0},"end":{"line":80,"column":7}}})) != null ? stack1 : "");
},"45":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row rsvp-block\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"events-rsvp"),depth0,{"name":"events-rsvp","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"47":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row rsvp-block rsvp-above show-rsvp\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"events-rsvp"),depth0,{"name":"events-rsvp","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"49":function(container,depth0,helpers,partials,data) {
    return "<div class=\"row rsvp-block\"><p>RSVP not required for this event</p></div>\n";
},"51":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"reservations_accepted") : depth0),"===","1",{"name":"compare","hash":{},"fn":container.program(52, data, 0),"inverse":container.program(49, data, 0),"data":data,"loc":{"start":{"line":91,"column":0},"end":{"line":97,"column":12}}})) != null ? stack1 : "");
},"52":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row rsvp-block rsvp-below show-rsvp\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"events-rsvp"),depth0,{"name":"events-rsvp","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"54":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"action-confirmation\">\n<div class=\"bubble-pale\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"events-confirmation"),depth0,{"name":"events-confirmation","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\""
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias1,(depth0 != null ? lookupProperty(depth0,"col") : depth0),{"name":"ifor","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":12},"end":{"line":1,"column":39}}})) != null ? stack1 : "")
    + " featured-event-inner "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"is_featured") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":61},"end":{"line":1,"column":99}}})) != null ? stack1 : "")
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_taken") : depth0),{"name":"unless","hash":{},"fn":container.program(5, data, 0),"inverse":container.program(54, data, 0),"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":106,"column":11}}})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("map-image", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"any").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"lat") : depth0),(depth0 != null ? lookupProperty(depth0,"lng") : depth0),{"name":"any","hash":{},"fn":container.program(2, data, 0),"inverse":container.program(4, data, 0),"data":data,"loc":{"start":{"line":1,"column":25},"end":{"line":1,"column":273}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "https://maps.googleapis.com/maps/api/staticmap?center="
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lat") : depth0), depth0))
    + ","
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lng") : depth0), depth0))
    + "&size=400x400&maptype=roadmap&markers=color:red%7Clabel:C%7C"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lat") : depth0), depth0))
    + ","
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lng") : depth0), depth0))
    + "&key=AIzaSyD1LqGvj-IryzE6EqCVWaCYMME6CEzxZuc";
},"4":function(container,depth0,helpers,partials,data) {
    return "/img/default_image_small.jpg";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<img src=\""
    + ((stack1 = lookupProperty(helpers,"ifor").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"image") : depth0),{"name":"ifor","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":10},"end":{"line":1,"column":282}}})) != null ? stack1 : "")
    + "\" class=\"event-image\" />\n";
},"useData":true}));

Handlebars.registerPartial("mobile-more-events", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var alias1=container.escapeExpression, alias2=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p class=\"event-map\"><a href=\"https://www.google.com/maps/place/"
    + alias1(lookupProperty(helpers,"mapFriendly").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"location") : depth0),{"name":"mapFriendly","hash":{},"data":data,"loc":{"start":{"line":11,"column":64},"end":{"line":11,"column":88}}}))
    + "/@"
    + alias1(alias2((depth0 != null ? lookupProperty(depth0,"lat") : depth0), depth0))
    + ","
    + alias1(alias2((depth0 != null ? lookupProperty(depth0,"lng") : depth0), depth0))
    + ",17z/\" target=\"_blank\">"
    + alias1(alias2((depth0 != null ? lookupProperty(depth0,"location") : depth0), depth0))
    + "</a></p>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row more-events-item\" data-index=\""
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "\">\n<div class=\"action-block\">\n<div class=\"event-date-block\">"
    + alias2(lookupProperty(helpers,"displayMonth").call(alias3,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayMonth","hash":{},"data":data,"loc":{"start":{"line":3,"column":30},"end":{"line":3,"column":57}}}))
    + " "
    + alias2(lookupProperty(helpers,"displayDay").call(alias3,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayDay","hash":{},"data":data,"loc":{"start":{"line":3,"column":58},"end":{"line":3,"column":83}}}))
    + ", "
    + alias2(lookupProperty(helpers,"displayYear").call(alias3,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayYear","hash":{},"data":data,"loc":{"start":{"line":3,"column":85},"end":{"line":3,"column":111}}}))
    + "</div>\n<div class=\"rsvp-block\"><button class=\"button rsvp-button clickable\">RSVP</button></div>\n</div>\n<div class=\"info-block clickable\">\n<p class=\"event-more info-list\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"activity_id") : depth0), depth0))
    + "\">\n<span><a href=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"info_url") : depth0), depth0))
    + "\" >"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0))
    + "</a></span>\n</p>\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"location") : depth0),"!=","",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":10,"column":0},"end":{"line":12,"column":12}}})) != null ? stack1 : "")
    + "</div>\n</div>\n";
},"useData":true}));

Handlebars.registerPartial("more-events-stacked", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"mobile-more-events"),depth0,{"name":"mobile-more-events","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"fullwidth\">\n<a  href=\"#\" class=\"more-button-after hide-when-expanded\" data-expand=\"events-more\">More Events</a>\n</div>\n<div data-expandable=\"events-more\" class=\"events-more expandable closed\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,lookupProperty(helpers,"limit").call(alias1,(depth0 != null ? lookupProperty(depth0,"events") : depth0),7,4,{"name":"limit","hash":{},"data":data,"loc":{"start":{"line":10,"column":8},"end":{"line":10,"column":26}}}),{"name":"each","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":10,"column":0},"end":{"line":12,"column":9}}})) != null ? stack1 : "")
    + "<div class=\"fullwidth bottom-of-more\">\n<button class=\"less-button\" data-expand=\"events-more\">Less Events</button>\n</div>\n</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"more-events-stacked\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,lookupProperty(helpers,"limit").call(alias1,(depth0 != null ? lookupProperty(depth0,"events") : depth0),3,{"name":"limit","hash":{},"data":data,"loc":{"start":{"line":2,"column":8},"end":{"line":2,"column":24}}}),{"name":"each","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":4,"column":9}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"events") : depth0)) != null ? lookupProperty(stack1,"4") : stack1),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":17,"column":7}}})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("related-campaign", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"campaign-tiles"),depth0,{"name":"campaign-tiles","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"related-campaign\">\n<h4 class=\"h4\">This Event is Part of a Campaign</h4>\n<div class=\"related-campaign-link\">\n"
    + ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"admin_campaign") : depth0),{"name":"with","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":4,"column":53}}})) != null ? stack1 : "")
    + "\n</div>\n</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("eventspagination", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<!-- button class=\"prev page\">"
    + container.escapeExpression(lookupProperty(helpers,"indexunshift").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"currentpage") : depth0),{"name":"indexunshift","hash":{},"data":data,"loc":{"start":{"line":1,"column":73},"end":{"line":1,"column":101}}}))
    + "</button -->";
},"3":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<button class=\"page"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depths[1] != null ? lookupProperty(depths[1],"currentpage") : depths[1]),"===",(data && lookupProperty(data,"index")),{"name":"compare","hash":{},"fn":container.program(4, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":19},"end":{"line":3,"column":79}}})) != null ? stack1 : "")
    + "\">"
    + container.escapeExpression(lookupProperty(helpers,"indexshift").call(alias1,(data && lookupProperty(data,"index")),{"name":"indexshift","hash":{},"data":data,"loc":{"start":{"line":3,"column":81},"end":{"line":3,"column":102}}}))
    + "</button>\n";
},"4":function(container,depth0,helpers,partials,data) {
    return " current";
},"6":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<!-- button class=\"next page\">"
    + container.escapeExpression(lookupProperty(helpers,"indexunshift").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"currentpage") : depth0),{"name":"indexunshift","hash":{},"data":data,"loc":{"start":{"line":5,"column":69},"end":{"line":5,"column":97}}}))
    + "</button -->";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"pagination\">"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"currentpage") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":24},"end":{"line":1,"column":120}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"pages") : depth0),{"name":"each","hash":{},"fn":container.program(3, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":4,"column":9}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"currentpage") : depth0),"!=-1",(depth0 != null ? lookupProperty(depth0,"pagenum") : depth0),{"name":"compare","hash":{},"fn":container.program(6, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":5,"column":121}}})) != null ? stack1 : "")
    + "</div>\n";
},"useData":true,"useDepths":true}));

Handlebars.registerPartial("disambiguation", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<tr><td style=\"text-align: center\"><input type=\"radio\" name=\"contact_id\" value=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\"></td><td>"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"first_name") : depth0), depth0))
    + "</td><td>"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"zipcode") : depth0), depth0))
    + "</td></tr>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<label style=\"padding-top: 0\">Choose an existing contact:</label>\n<table><tbody>\n<tr><th width=\"30%\">Choose</th><th width=\"35%\">Name</th><th width=\"35%\">Zip Code</th></tr>\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"contacts") : depth0),{"name":"each","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":4,"column":168}}})) != null ? stack1 : "")
    + "\n</tbody></table>\n";
},"useData":true}));

Handlebars.registerPartial("email_pref_fields", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return " class=\"inactive\"";
},"3":function(container,depth0,helpers,partials,data) {
    return " checked";
},"5":function(container,depth0,helpers,partials,data) {
    return "checked";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<ul class=\"radio\">\n<li"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":3},"end":{"line":2,"column":55}}})) != null ? stack1 : "")
    + ">\n<p>\nSend me updates on the campaigns I've supported.\n</p>\n</li>\n<li"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":3},"end":{"line":7,"column":55}}})) != null ? stack1 : "")
    + ">\n<input type=\"radio\" id=\"email_opt_in_campaign_updates_no\" name=\"email_opt_in_campaign_updates\" value=\"0\""
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in_campaign_updates") : depth0),{"name":"unless","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":104},"end":{"line":8,"column":164}}})) != null ? stack1 : "")
    + ">\n<label for=\"email_opt_in_campaign_updates_no\" class=\""
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in_campaign_updates") : depth0),{"name":"unless","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":53},"end":{"line":9,"column":112}}})) != null ? stack1 : "")
    + "\">\n<span><i></i></span>\nNo\n</label>\n\n<input type=\"radio\" id=\"email_opt_in_campaign_updates_yes\" name=\"email_opt_in_campaign_updates\" value=\"1\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in_campaign_updates") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":105},"end":{"line":14,"column":157}}})) != null ? stack1 : "")
    + ">\n<label for=\"email_opt_in_campaign_updates_yes\" class=\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in_campaign_updates") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":15,"column":54},"end":{"line":15,"column":106}}})) != null ? stack1 : "")
    + "\">\n<span><i></i></span>\nYes\n</label>\n</li>\n<li"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":20,"column":3},"end":{"line":20,"column":55}}})) != null ? stack1 : "")
    + ">\n<p>\nSend me recommendations for actions I may be interested in taking.\n</p>\n</li>\n<li"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":25,"column":3},"end":{"line":25,"column":55}}})) != null ? stack1 : "")
    + ">\n<input type=\"radio\" id=\"email_opt_in_user_recommendations_no\" name=\"email_opt_in_user_recommendations\" value=\"0\""
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in_user_recommendations") : depth0),{"name":"unless","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":26,"column":112},"end":{"line":26,"column":176}}})) != null ? stack1 : "")
    + ">\n<label for=\"email_opt_in_user_recommendations_no\" class=\""
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in_user_recommendations") : depth0),{"name":"unless","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":27,"column":57},"end":{"line":27,"column":120}}})) != null ? stack1 : "")
    + "\">\n<span><i></i></span>\nNo\n</label>\n\n<input type=\"radio\" id=\"email_opt_in_user_recommendations_yes\" name=\"email_opt_in_user_recommendations\" value=\"1\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in_user_recommendations") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":32,"column":113},"end":{"line":32,"column":169}}})) != null ? stack1 : "")
    + ">\n<label for=\"email_opt_in_user_recommendations_yes\" class=\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in_user_recommendations") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":33,"column":58},"end":{"line":33,"column":114}}})) != null ? stack1 : "")
    + "\">\n<span><i></i></span>\nYes\n</label>\n</li>\n<li"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":38,"column":3},"end":{"line":38,"column":55}}})) != null ? stack1 : "")
    + ">\n<p>\nSend me recommendations based on what my friends are doing on AddUp.\n</p>\n</li>\n<li"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":43,"column":3},"end":{"line":43,"column":55}}})) != null ? stack1 : "")
    + ">\n<input type=\"radio\" id=\"email_opt_in_fb_friend_recommendations_no\" name=\"email_opt_in_fb_friend_recommendations\" value=\"0\""
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in_fb_friend_recommendations") : depth0),{"name":"unless","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":44,"column":122},"end":{"line":44,"column":191}}})) != null ? stack1 : "")
    + ">\n<label for=\"email_opt_in_fb_friend_recommendations_no\" class=\""
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in_fb_friend_recommendations") : depth0),{"name":"unless","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":45,"column":62},"end":{"line":45,"column":130}}})) != null ? stack1 : "")
    + "\">\n<span><i></i></span>\nNo\n</label>\n\n<input type=\"radio\" id=\"email_opt_in_fb_friend_recommendations_yes\" name=\"email_opt_in_fb_friend_recommendations\" value=\"1\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in_fb_friend_recommendations") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":50,"column":123},"end":{"line":50,"column":184}}})) != null ? stack1 : "")
    + ">\n<label for=\"email_opt_in_fb_friend_recommendations_yes\" class=\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in_fb_friend_recommendations") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":51,"column":63},"end":{"line":51,"column":123}}})) != null ? stack1 : "")
    + "\">\n<span><i></i></span>\nYes\n</label>\n</li>\n<li>\n<p>\nReceive AddUp Emails <br /> (Note: Selecting \"NO\" will also unsubscribe you from all of the above emails.)\n</p>\n</li>\n<li class=\"opt-out-all\">\n<input type=\"radio\" id=\"email_opt_in_no\" name=\"email_opt_in\" value=\"0\""
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in") : depth0),{"name":"unless","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":62,"column":70},"end":{"line":62,"column":113}}})) != null ? stack1 : "")
    + ">\n<label for=\"email_opt_in_no\" class=\"email_opt_in_no "
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in") : depth0),{"name":"unless","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":63,"column":52},"end":{"line":63,"column":94}}})) != null ? stack1 : "")
    + "\">\n<span><i></i></span>\nNo\n</label>\n\n<input type=\"radio\" id=\"email_opt_in_yes\" name=\"email_opt_in\" value=\"1\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":68,"column":71},"end":{"line":68,"column":106}}})) != null ? stack1 : "")
    + ">\n<label for=\"email_opt_in_yes\" class=\"email_opt_in_yes "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_in") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":69,"column":54},"end":{"line":69,"column":88}}})) != null ? stack1 : "")
    + "\">\n<span><i></i></span>\nYes\n</label>\n</li>\n<li><hr></li>\n<!--li>\n<input type=\"checkbox\" id=\"all-e\" name=\"email_opt_out\" value=\"1\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"email_opt_out") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":76,"column":65},"end":{"line":76,"column":101}}})) != null ? stack1 : "")
    + ">\n<label for=\"all-e\" class=\"like-radio\">\n<span><i></i></span>\nUnsubscribe me from all AddUp emails.\n</label>\n</li-->\n</ul>\n";
},"useData":true}));

Handlebars.registerPartial("event-collection", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"reservations_accepted") : depth0),"===","1",{"name":"compare","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":56,"column":12}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),{"name":"unless","hash":{},"fn":container.program(3, data, 0),"inverse":container.program(5, data, 0),"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":15,"column":11}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"zipcode") : depth0),{"name":"unless","hash":{},"fn":container.program(7, data, 0),"inverse":container.program(10, data, 0),"data":data,"loc":{"start":{"line":17,"column":0},"end":{"line":24,"column":11}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"phone") : depth0),{"name":"unless","hash":{},"fn":container.program(12, data, 0),"inverse":container.program(15, data, 0),"data":data,"loc":{"start":{"line":26,"column":0},"end":{"line":33,"column":11}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"email") : depth0),{"name":"unless","hash":{},"fn":container.program(17, data, 0),"inverse":container.program(19, data, 0),"data":data,"loc":{"start":{"line":35,"column":0},"end":{"line":41,"column":11}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"unall").call(alias1,(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),(depth0 != null ? lookupProperty(depth0,"last_name") : depth0),(depth0 != null ? lookupProperty(depth0,"email") : depth0),(depth0 != null ? lookupProperty(depth0,"zipcode") : depth0),(depth0 != null ? lookupProperty(depth0,"phone") : depth0),{"name":"unall","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":43,"column":0},"end":{"line":52,"column":10}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"existing-info"),depth0,{"name":"existing-info","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "\n";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"firstname\">\n<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-firstname-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">First Name</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"first_name_field"),depth0,{"name":"first_name_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"lastname\">\n<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-lastname-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Last Name</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"last_name_field"),depth0,{"name":"last_name_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"5":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"hidden\" name=\"first_name\" value=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"first_name") : depth0), depth0))
    + "\"><input type=\"hidden\" name=\"last_name\" value=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"last_name") : depth0), depth0))
    + "\">\n";
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"zipcode"
    + ((stack1 = lookupProperty(helpers,"all").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),(depth0 != null ? lookupProperty(depth0,"last_name") : depth0),(depth0 != null ? lookupProperty(depth0,"phone") : depth0),(depth0 != null ? lookupProperty(depth0,"email") : depth0),{"name":"all","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":18,"column":18},"end":{"line":18,"column":76}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-zip-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">ZIP Code</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"zip_field"),depth0,{"name":"zip_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"8":function(container,depth0,helpers,partials,data) {
    return " zip-only";
},"10":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"hidden\" name=\"zipcode\" value=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"zipcode") : depth0), depth0))
    + "\">\n";
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"phone"
    + ((stack1 = lookupProperty(helpers,"all").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),(depth0 != null ? lookupProperty(depth0,"last_name") : depth0),(depth0 != null ? lookupProperty(depth0,"zipcode") : depth0),(depth0 != null ? lookupProperty(depth0,"email") : depth0),{"name":"all","hash":{},"fn":container.program(13, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":27,"column":16},"end":{"line":27,"column":78}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-phone-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Phone - e.g. 1(123)456-7890</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"phone_field"),depth0,{"name":"phone_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"13":function(container,depth0,helpers,partials,data) {
    return " phone-only";
},"15":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"hidden\" name=\"phone\" value=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"phone") : depth0), depth0))
    + "\">\n";
},"17":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"email\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"email_label_field"),depth0,{"name":"email_label_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"19":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"hidden\" name=\"email\" value=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"email") : depth0), depth0))
    + "\">\n";
},"21":function(container,depth0,helpers,partials,data) {
    return "<li class=\"disclaimer full-width\"><small>All fields required unless noted otherwise.</small>\n<span class=\"tooltip\">?\n<span class=\"tooltip-bubble\">\n<span class=\"closebutton\"></span>\n<p>Recipients often require this information in order to accept your message, so we have to require it too.</p>\n</span>\n</span>\n</li>\n";
},"23":function(container,depth0,helpers,partials,data) {
    return "<button type=\"submit\" class=\"button event-rsvp\">RSVP</button>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"event_status") : depth0),"===","ACT",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":57,"column":12}}})) != null ? stack1 : "")
    + "<li class=\"submit full-width\">\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"reservations_accepted") : depth0),"===","1",{"name":"compare","hash":{},"fn":container.program(23, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":59,"column":0},"end":{"line":61,"column":12}}})) != null ? stack1 : "")
    + "</li>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("existing-info", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"full-width\">\n<div class=\"existing-info-summary\">\n<a class=\"resetForm\">Not you? Reset Form</a>\n<div class=\"existing-info\">\n<span class=\"prefix bold\">"
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"prefix") : depth0),{"name":"toString","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":6,"column":26},"end":{"line":6,"column":59}}})) != null ? stack1 : "")
    + "</span>\n<span class=\"firstname bold\">\n"
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),{"name":"toString","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":8,"column":37}}})) != null ? stack1 : "")
    + "\n</span>\n<span class=\"lastname bold\">\n"
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"last_name") : depth0),{"name":"toString","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":11,"column":0},"end":{"line":11,"column":36}}})) != null ? stack1 : "")
    + "\n</span>\n<br/>\n<span class=\"street_address\">"
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"street_address") : depth0),{"name":"toString","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":29},"end":{"line":14,"column":70}}})) != null ? stack1 : "")
    + "</span>"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"additional_address") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":77},"end":{"line":14,"column":111}}})) != null ? stack1 : "")
    + "<span class=\"additional_address\">"
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"additional_address") : depth0),{"name":"toString","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":144},"end":{"line":14,"column":189}}})) != null ? stack1 : "")
    + "</span>"
    + ((stack1 = lookupProperty(helpers,"any").call(alias1,(depth0 != null ? lookupProperty(depth0,"street_address") : depth0),(depth0 != null ? lookupProperty(depth0,"additional_address") : depth0),{"name":"any","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":196},"end":{"line":14,"column":247}}})) != null ? stack1 : "")
    + "\n<span class=\"city\">"
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"city") : depth0),{"name":"toString","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":15,"column":19},"end":{"line":15,"column":50}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"city") : depth0),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":15,"column":50},"end":{"line":15,"column":70}}})) != null ? stack1 : "")
    + "</span>\n<span class=\"state\">"
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"state") : depth0),{"name":"toString","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":20},"end":{"line":16,"column":52}}})) != null ? stack1 : "")
    + "</span>\n<span class=\"zipcode\">"
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"zipcode") : depth0),{"name":"toString","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":17,"column":22},"end":{"line":17,"column":56}}})) != null ? stack1 : "")
    + "</span>\n<br/>\n<span class=\"phone\">"
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"phone") : depth0),{"name":"toString","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":19,"column":20},"end":{"line":19,"column":52}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"all").call(alias1,(depth0 != null ? lookupProperty(depth0,"phone") : depth0),(depth0 != null ? lookupProperty(depth0,"email") : depth0),{"name":"all","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":19,"column":52},"end":{"line":19,"column":83}}})) != null ? stack1 : "")
    + "</span>\n<span class=\"email\">"
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"email") : depth0),{"name":"toString","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":20,"column":20},"end":{"line":20,"column":52}}})) != null ? stack1 : "")
    + "</span>\n</div>\n</div>\n</li>\n";
},"2":function(container,depth0,helpers,partials,data) {
    return "";
},"4":function(container,depth0,helpers,partials,data) {
    return " ";
},"6":function(container,depth0,helpers,partials,data) {
    return ";";
},"8":function(container,depth0,helpers,partials,data) {
    return ",";
},"10":function(container,depth0,helpers,partials,data) {
    return " | ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"any").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"prefix") : depth0),(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),(depth0 != null ? lookupProperty(depth0,"last_name") : depth0),(depth0 != null ? lookupProperty(depth0,"street_address") : depth0),(depth0 != null ? lookupProperty(depth0,"zip") : depth0),(depth0 != null ? lookupProperty(depth0,"phone") : depth0),(depth0 != null ? lookupProperty(depth0,"email") : depth0),{"name":"any","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":24,"column":8}}})) != null ? stack1 : "");
},"useData":true}));

Handlebars.registerPartial("login-form", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"error-message\">"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"error") : depth0)) != null ? lookupProperty(stack1,"message") : stack1), depth0))
    + "</div>\n";
},"3":function(container,depth0,helpers,partials,data) {
    return "Go!";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<form action=\"#\" class=\"page-form\" data-formaction=\"login\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"error") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":4,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = container.invokePartial(lookupProperty(partials,"email_label_field"),depth0,{"name":"email_label_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "<label class=\"password-label\" for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-password-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Password</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"password_field"),depth0,{"name":"password_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "<a data-event=\"forgot-password\" class=\"forgot-password\" tabindex=\"-1\">Forgot Password?</a>\n<button class=\"button submit-button\">"
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias1,(depth0 != null ? lookupProperty(depth0,"login-title") : depth0),{"name":"ifor","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":37},"end":{"line":9,"column":70}}})) != null ? stack1 : "")
    + "</button>\n</form>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("personal-n-address", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"error-message\">"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"message") : stack1), depth0))
    + "</li>\n";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"prefix"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"prefix") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":6,"column":17},"end":{"line":6,"column":53}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-prefix-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Title <span class=\"tooltip\">?<span class=\"tooltip-bubble\"><span class=\"closebutton\"></span><p>Currently the systems of nearly all federal, state, and local elected officials require this information, so we must ask for it to make sure your message is received.</p></span></span> "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","prefix",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":323},"end":{"line":7,"column":413}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"prefix_field"),depth0,{"name":"prefix_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"4":function(container,depth0,helpers,partials,data) {
    return " inactive-field";
},"6":function(container,depth0,helpers,partials,data) {
    return "<span class=\"tiny\">(optional)</span>";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"firstname"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":13,"column":20},"end":{"line":13,"column":60}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-firstname-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">First Name "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","first_name",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":58},"end":{"line":14,"column":152}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"first_name_field"),depth0,{"name":"first_name_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"10":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"lastname"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"last_name") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":20,"column":19},"end":{"line":20,"column":58}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-lastname-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Last Name "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","last_name",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":21,"column":56},"end":{"line":21,"column":149}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"last_name_field"),depth0,{"name":"last_name_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"street_address"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"street_address") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":27,"column":25},"end":{"line":27,"column":69}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-a1-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Address Line 1 "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","street_address",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":28,"column":55},"end":{"line":28,"column":153}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"street_address_field"),depth0,{"name":"street_address_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"additional_address"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"street_address") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":31,"column":29},"end":{"line":31,"column":73}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-a2-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Address Line 2 <span class=\"tiny\">(optional)</span></label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"additional_address_field"),depth0,{"name":"additional_address_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"14":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"city"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"city") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":38,"column":15},"end":{"line":38,"column":49}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-city-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">City "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","city",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":39,"column":47},"end":{"line":39,"column":135}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"city_field"),depth0,{"name":"city_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"16":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"state"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"state") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":45,"column":16},"end":{"line":45,"column":51}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-state-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">State "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","state",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":46,"column":49},"end":{"line":46,"column":138}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"state_field"),depth0,{"name":"state_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"18":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"zipcode"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"zipcode") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":52,"column":18},"end":{"line":52,"column":55}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-zip-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">ZIP Code "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","zipcode",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":53,"column":50},"end":{"line":53,"column":141}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"zip_field"),depth0,{"name":"zip_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"20":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"phone"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"phone") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":59,"column":16},"end":{"line":59,"column":51}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-phone-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Phone (123)456-7890 "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","phone",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":60,"column":63},"end":{"line":60,"column":152}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"phone_field"),depth0,{"name":"phone_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"message") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":3,"column":7}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","prefix",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":10,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","first_name",{"name":"compare","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":12,"column":0},"end":{"line":17,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","last_name",{"name":"compare","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":19,"column":0},"end":{"line":24,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","street_address",{"name":"compare","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":26,"column":0},"end":{"line":35,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","city",{"name":"compare","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":37,"column":0},"end":{"line":42,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","state",{"name":"compare","hash":{},"fn":container.program(16, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":44,"column":0},"end":{"line":49,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","zipcode",{"name":"compare","hash":{},"fn":container.program(18, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":51,"column":0},"end":{"line":56,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","phone",{"name":"compare","hash":{},"fn":container.program(20, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":58,"column":0},"end":{"line":63,"column":12}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("petition-fields", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"error-message\">"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"message") : stack1), depth0))
    + "</li>\n";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"prefix"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"prefix") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":17},"end":{"line":8,"column":60}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-prefix-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Title <span class=\"tooltip\">?<span class=\"tooltip-bubble\"><span class=\"closebutton\"></span><p>Currently the systems of nearly all federal, state, and local elected officials require this information, so we must ask for it to make sure your message is received.</p></span></span> "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","prefix",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":323},"end":{"line":9,"column":413}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"prefix_field"),depth0,{"name":"prefix_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"4":function(container,depth0,helpers,partials,data) {
    return " inactive-field hidden";
},"6":function(container,depth0,helpers,partials,data) {
    return "<span class=\"tiny\">(optional)</span>";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"firstname"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":15,"column":20},"end":{"line":15,"column":67}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-firstname-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">First Name "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","first_name",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":58},"end":{"line":16,"column":152}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"first_name_field"),depth0,{"name":"first_name_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"10":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"lastname"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"last_name") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":22,"column":19},"end":{"line":22,"column":65}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-lastname-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Last Name "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","last_name",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":23,"column":56},"end":{"line":23,"column":149}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"last_name_field"),depth0,{"name":"last_name_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"street_address"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"street_address") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":29,"column":25},"end":{"line":29,"column":76}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-a1-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Address Line 1 "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","street_address",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":30,"column":55},"end":{"line":30,"column":153}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"street_address_field"),depth0,{"name":"street_address_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"additional_address"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"street_address") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":33,"column":29},"end":{"line":33,"column":80}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-a2-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Address Line 2 <span class=\"tiny\">(optional)</span></label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"additional_address_field"),depth0,{"name":"additional_address_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"14":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"city"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"city") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":40,"column":15},"end":{"line":40,"column":56}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-city-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">City "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","city",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":41,"column":47},"end":{"line":41,"column":135}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"city_field"),depth0,{"name":"city_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"16":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"state"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"state") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":47,"column":16},"end":{"line":47,"column":58}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-state-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">State "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","state",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":48,"column":49},"end":{"line":48,"column":138}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"state_field"),depth0,{"name":"state_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"18":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"zipcode"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"zipcode") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":54,"column":18},"end":{"line":54,"column":62}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-zip-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">ZIP Code "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","zipcode",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":55,"column":50},"end":{"line":55,"column":141}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"zip_field"),depth0,{"name":"zip_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"20":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"phone"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"phone") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":61,"column":16},"end":{"line":61,"column":58}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-phone-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Phone (123)456-7890 "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","phone",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":62,"column":63},"end":{"line":62,"column":152}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"phone_field"),depth0,{"name":"phone_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"22":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"t_loggedin") : depth0),{"name":"unless","hash":{},"fn":container.program(23, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":68,"column":0},"end":{"line":70,"column":11}}})) != null ? stack1 : "");
},"23":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"email"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"email") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":69,"column":16},"end":{"line":69,"column":58}}})) != null ? stack1 : "")
    + "\">"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"email_label_field"),depth0,{"name":"email_label_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"existing-info"),depth0,{"name":"existing-info","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"message") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":5,"column":7}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","prefix",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":12,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","first_name",{"name":"compare","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":0},"end":{"line":19,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","last_name",{"name":"compare","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":21,"column":0},"end":{"line":26,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","street_address",{"name":"compare","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":28,"column":0},"end":{"line":37,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","city",{"name":"compare","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":39,"column":0},"end":{"line":44,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","state",{"name":"compare","hash":{},"fn":container.program(16, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":46,"column":0},"end":{"line":51,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","zipcode",{"name":"compare","hash":{},"fn":container.program(18, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":53,"column":0},"end":{"line":58,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","phone",{"name":"compare","hash":{},"fn":container.program(20, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":60,"column":0},"end":{"line":65,"column":12}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","email",{"name":"compare","hash":{},"fn":container.program(22, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":67,"column":0},"end":{"line":71,"column":12}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("signup-form", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"error-message\">"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"message") : stack1), depth0))
    + "</li>\n";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"email\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"email_label_field"),depth0,{"name":"email_label_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"firstname\">\n<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-firstname-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">First name:</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"first_name_field"),depth0,{"name":"first_name_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"7":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"hidden\" name=\"first_name\" value=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"first_name") : depth0), depth0))
    + "\">\n";
},"9":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"lastname\">\n<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-lastname-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Last name: </label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"last_name_field"),depth0,{"name":"last_name_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"11":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"hidden\" name=\"last_name\" value=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"last_name") : depth0), depth0))
    + "\">\n";
},"13":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"two-passwords"),depth0,{"name":"two-passwords","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"15":function(container,depth0,helpers,partials,data) {
    return "See My Impact";
},"17":function(container,depth0,helpers,partials,data) {
    return "Go!";
},"19":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"skip-step\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"alt-login-link"),depth0,{"name":"alt-login-link","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n";
},"21":function(container,depth0,helpers,partials,data) {
    return "<li class=\"skip-step\">\n<a class=\"modal-close\">No thanks, I'll do this later</a>\n</li>\n";
},"23":function(container,depth0,helpers,partials,data) {
    return "<li class=\"skip-step\">\n<a class=\"not-you\" data-event=\"logout\">Not You?</a>\n</li>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"message") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":3,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"fbloggedin") : depth0),{"name":"unless","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":8,"column":11}}})) != null ? stack1 : "")
    + "<li class=\"zipcode\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"zip_label_field"),depth0,{"name":"zip_label_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),{"name":"unless","hash":{},"fn":container.program(5, data, 0),"inverse":container.program(7, data, 0),"data":data,"loc":{"start":{"line":12,"column":0},"end":{"line":19,"column":11}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"last_name") : depth0),{"name":"unless","hash":{},"fn":container.program(9, data, 0),"inverse":container.program(11, data, 0),"data":data,"loc":{"start":{"line":20,"column":0},"end":{"line":27,"column":11}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"fbloggedin") : depth0),{"name":"unless","hash":{},"fn":container.program(13, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":28,"column":0},"end":{"line":30,"column":11}}})) != null ? stack1 : "")
    + "<li class=\"submit-button\">\n<button class=\"button action-button\" type=\"submit\">"
    + ((stack1 = lookupProperty(helpers,"any").call(alias1,(depth0 != null ? lookupProperty(depth0,"prospective") : depth0),(depth0 != null ? lookupProperty(depth0,"post_action") : depth0),{"name":"any","hash":{},"fn":container.program(15, data, 0),"inverse":container.program(17, data, 0),"data":data,"loc":{"start":{"line":32,"column":51},"end":{"line":32,"column":115}}})) != null ? stack1 : "")
    + "</button>\n</li>\n"
    + ((stack1 = lookupProperty(helpers,"none").call(alias1,(depth0 != null ? lookupProperty(depth0,"loggedin") : depth0),(depth0 != null ? lookupProperty(depth0,"post_action") : depth0),{"name":"none","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":34,"column":0},"end":{"line":38,"column":9}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"post_action") : depth0),{"name":"if","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":39,"column":0},"end":{"line":43,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"any").call(alias1,(depth0 != null ? lookupProperty(depth0,"email") : depth0),(depth0 != null ? lookupProperty(depth0,"zipcode") : depth0),{"name":"any","hash":{},"fn":container.program(23, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":44,"column":0},"end":{"line":48,"column":8}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("two-passwords", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"password1\">\n<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-password-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"new") : depth0), depth0))
    + "Password:</label>\n<input title=\"Enter a "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"new") : depth0), depth0))
    + "password at least 8 characters in length, containing at least one number and one letter.\" placeholder=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"new") : depth0), depth0))
    + "Password\" name=\"password\" value=\""
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"password") : stack1), depth0))
    + "\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-password-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"r-pswd"),depth0,{"name":"r-pswd","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + ">\n</li>\n<li class=\"password2\">\n<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-password2-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Confirm "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"new") : depth0), depth0))
    + "password:</label>\n<input title=\"Confirm your "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"new") : depth0), depth0))
    + "password.\" name=\"password_confirmation\" placeholder=\"Confirm "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"new") : depth0), depth0))
    + "password\" value=\""
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"password") : stack1), depth0))
    + "\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-password2-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"r-pswd"),depth0,{"name":"r-pswd","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + ">\n</li>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("additional_address_field", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"req"),depth0,{"name":"req","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"text\" name=\"additional_address\" title=\"Enter your street address\" value=\""
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"additional_address") : depth0),{"name":"toString","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":86},"end":{"line":1,"column":131}}})) != null ? stack1 : "")
    + alias3(alias2(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"additional_address") : stack1), depth0))
    + "\" placeholder=\"Street Address 2\" title=\"Address 2\" maxlength=\"120\" id=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-a2-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"required_params") : depth0),"[]","additional_address",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":267},"end":{"line":1,"column":342}}})) != null ? stack1 : "")
    + ">\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("card_cvv_field", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"card_cvv_number") : depth0), depth0));
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"card\" value=\""
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"card_cvv_number") : depth0),"!=","false",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":26},"end":{"line":1,"column":98}}})) != null ? stack1 : "")
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"card_cvv_number") : stack1), depth0))
    + "\" placeholder=\"CVV\" title=\"CVV\" maxlength=\"4\" pattern=\"^\\d{3,4}$\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-card-cvv-number-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" class=\"card-cvv-field text-field\" data-stripe=\"cvc\" required=\"required\">\n";
},"useData":true}));

Handlebars.registerPartial("card_field", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"card_number") : depth0), depth0));
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"card\" value=\""
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"card_number") : depth0),"!=","false",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":26},"end":{"line":1,"column":90}}})) != null ? stack1 : "")
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"card_number") : stack1), depth0))
    + "\" placeholder=\"Credit Card\" title=\"Credit Card\" pattern=\"^(\\d{12,19})$\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-card-number-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" data-stripe=\"number\" class=\"card-field text-field\" required=\"required\">\n";
},"useData":true}));

Handlebars.registerPartial("city_field", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"req"),depth0,{"name":"req","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"text\" name=\"city\" title=\"Enter your city\" pattern=\"^[a-zA-Z0-9]{2,}.*\" value=\""
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"city") : depth0),{"name":"toString","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":91},"end":{"line":1,"column":122}}})) != null ? stack1 : "")
    + alias3(alias2(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"sity") : stack1), depth0))
    + "\" placeholder=\"City\" title=\"City\" maxlength=\"52\" id=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-city-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"required_params") : depth0),"[]","city",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":228},"end":{"line":1,"column":289}}})) != null ? stack1 : "")
    + ">\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("connect-to-facebook-button", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "data-post_action=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"post_action") : depth0), depth0))
    + "\"";
},"3":function(container,depth0,helpers,partials,data) {
    return "Connect";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<button class=\"button fb-button\" data-event=\"request-fbconnect\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"post_action") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":64},"end":{"line":1,"column":124}}})) != null ? stack1 : "")
    + " data-addperms=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"missing_permissions") : depth0), depth0))
    + "\"><i></i><span>"
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias1,(depth0 != null ? lookupProperty(depth0,"btn_copy") : depth0),{"name":"ifor","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":178},"end":{"line":1,"column":212}}})) != null ? stack1 : "")
    + "</span></button>\n";
},"useData":true}));

Handlebars.registerPartial("country_field", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<select name=\"country\" name=\"country\" title=\"Country\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-country-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" required=\"required\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"country_options"),depth0,{"name":"country_options","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</select>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("donate_other", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "checked=\"checked\"";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"text\" name=\"donate-other\" title=\"Dollar Amount\" value=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"donate-other") : depth0), depth0))
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"donate-other") : stack1), depth0))
    + "\" placeholder=\"Other\" maxlength=\"52\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-donate-other-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" pattern=\"^\\$?(([1-9]\\d{0,2}(,\\d{3})*)|(([1-9]\\d*)?\\d))(\\.\\d\\d)?$\" class=\"donate-other\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"donate-other") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":281},"end":{"line":1,"column":325}}})) != null ? stack1 : "")
    + " />\n<input type=\"radio\" name=\"amount\" id=\"d-btn-other\" class=\"donate-radio donate-other-radio\" value=\"\" required "
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,lookupProperty(helpers,"abs").call(alias3,(depth0 != null ? lookupProperty(depth0,"donate-other") : depth0),{"name":"abs","hash":{},"data":data,"loc":{"start":{"line":2,"column":115},"end":{"line":2,"column":133}}}),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":109},"end":{"line":2,"column":159}}})) != null ? stack1 : "")
    + " />\n";
},"useData":true}));

Handlebars.registerPartial("email_field", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"email") : depth0),"!=","false",{"name":"compare","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":82},"end":{"line":1,"column":157}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"toString").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"email") : depth0),{"name":"toString","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":113},"end":{"line":1,"column":145}}})) != null ? stack1 : "");
},"3":function(container,depth0,helpers,partials,data) {
    return "";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"req"),depth0,{"name":"req","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"email\" name=\"email\" value=\""
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_template") : depth0),"!=","hero-signup",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":40},"end":{"line":1,"column":169}}})) != null ? stack1 : "")
    + alias3(alias2(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"email") : stack1), depth0))
    + "\" placeholder=\"Email\" title=\"Email Address\" maxlength=\"254\" id=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-email-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" class=\"email-field text-field\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"required_params") : depth0),"[]","email",{"name":"compare","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":319},"end":{"line":1,"column":381}}})) != null ? stack1 : "")
    + ">\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("exp_month_field", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<select title=\"Exp Month\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-exp-month-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" data-stripe=\"exp_month\" required=\"required\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"exp_month_options"),depth0,{"name":"exp_month_options","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</select>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("exp_year_field", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<select title=\"Exp Year\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-exp-year-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" data-stripe=\"exp_year\" required=\"required\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"exp_year_options"),depth0,{"name":"exp_year_options","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</select>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("country_options", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"selectoptions").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"countries") : depth0),(depth0 != null ? lookupProperty(depth0,"country") : depth0),{"name":"selectoptions","hash":{"noempty":(depth0 != null ? lookupProperty(depth0,"noempty") : depth0),"error":((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"country") : stack1)},"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":80}}}))
    + "\n";
},"useData":true}));

Handlebars.registerPartial("exp_month_options", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"selectoptions").call(depth0 != null ? depth0 : (container.nullContext || {}),"exp_month",(depth0 != null ? lookupProperty(depth0,"exp_month") : depth0),{"name":"selectoptions","hash":{"noempty":(depth0 != null ? lookupProperty(depth0,"noempty") : depth0),"error":((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"exp_month") : stack1)},"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":86}}}))
    + "\n";
},"useData":true}));

Handlebars.registerPartial("exp_year_options", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"selectoptions").call(depth0 != null ? depth0 : (container.nullContext || {}),"exp_year",(depth0 != null ? lookupProperty(depth0,"exp_year") : depth0),{"name":"selectoptions","hash":{"noempty":(depth0 != null ? lookupProperty(depth0,"noempty") : depth0),"error":((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"exp_year") : stack1)},"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":83}}}))
    + "\n";
},"useData":true}));

Handlebars.registerPartial("state_options", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"selectoptions").call(depth0 != null ? depth0 : (container.nullContext || {}),"states",(depth0 != null ? lookupProperty(depth0,"state") : depth0),{"name":"selectoptions","hash":{"noempty":(depth0 != null ? lookupProperty(depth0,"noempty") : depth0),"error":((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"state") : stack1)},"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":75}}}))
    + "\n";
},"useData":true}));

Handlebars.registerPartial("first_name_field", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"req"),depth0,{"name":"req","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"text\" name=\"first_name\" title=\"Enter your first name\" value=\""
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),{"name":"toString","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":74},"end":{"line":1,"column":111}}})) != null ? stack1 : "")
    + alias3(alias2(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"first_name") : stack1), depth0))
    + "\" title=\"First name\" placeholder=\"First Name\" pattern=\"^.{1,20}$\" maxlength=\"20\" id=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-firstname-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"required_params") : depth0),"[]","first_name",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":260},"end":{"line":1,"column":327}}})) != null ? stack1 : "")
    + ">\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("issue-checkbox", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return " checked";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"checkbox\" id=\"cx"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" name=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" value=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" class=\"checkbox\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"picked") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":84},"end":{"line":1,"column":113}}})) != null ? stack1 : "")
    + "><label for=\"cx"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" class=\""
    + alias2(lookupProperty(helpers,"switch").call(alias3,(data && lookupProperty(data,"index")),{"name":"switch","hash":{},"data":data,"loc":{"start":{"line":1,"column":143},"end":{"line":1,"column":160}}}))
    + " cat_"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "-icon"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"picked") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":176},"end":{"line":1,"column":205}}})) != null ? stack1 : "")
    + "\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "</label>\n";
},"useData":true}));

Handlebars.registerPartial("email_label_field", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "<span class=\"tiny\">(optional)</span>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-email-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" class=\"email-label\">Email "
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"optional_params") : depth0),"[]","email",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":69},"end":{"line":1,"column":158}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"email_field"),depth0,{"name":"email_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("zip_label_field", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "<span class=\"tooltip\">?<span class=\"tooltip-bubble\"><span class=\"closebutton\"></span><p>This information helps AddUp identify campaigns that need your support locally.</p></span></span>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-zip-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">ZIP Code "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"tooltip") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":50},"end":{"line":1,"column":257}}})) != null ? stack1 : "")
    + "</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"zip_field"),depth0,{"name":"zip_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("last_name_field", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"req"),depth0,{"name":"req","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"text\" name=\"last_name\" title=\"Enter your lastname.\" value=\""
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"last_name") : depth0),{"name":"toString","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":72},"end":{"line":1,"column":108}}})) != null ? stack1 : "")
    + alias3(alias2(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"last_name") : stack1), depth0))
    + "\" title=\"Last Name\" placeholder=\"Last Name\" pattern=\"^.{1,22}$\" maxlength=\"22\" id=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-lastname-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"required_params") : depth0),"[]","last_name",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":253},"end":{"line":1,"column":319}}})) != null ? stack1 : "")
    + ">\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("password_field", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input title=\"Enter your password.\" name=\"password\" value=\""
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"password") : stack1), depth0))
    + "\" placeholder=\"Password\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-password-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"r-pswd"),depth0,{"name":"r-pswd","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + ">\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("phone_field", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"req"),depth0,{"name":"req","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"tel\" title=\"Enter your telephone number.\" value=\""
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"phone") : depth0),{"name":"toString","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":62},"end":{"line":1,"column":94}}})) != null ? stack1 : "")
    + alias3(alias2(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"phone") : stack1), depth0))
    + "\" name=\"phone\" title=\"Phone\" pattern=\"^(1-?)?(\\([0-9]\\d{2}\\)(\\s)?|[0-9]\\d{2})-?[0-9]\\d{2}-?\\d{4}$\" placeholder=\"Phone\" id=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-phone-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" maxlength=\"14\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"required_params") : depth0),"[]","phone",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":287},"end":{"line":1,"column":349}}})) != null ? stack1 : "")
    + " class=\"phone-field text-field\">\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("prefix_field", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"req"),depth0,{"name":"req","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<select name=\"prefix\" title=\"Title/Prefix\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-prefix-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" class=\"sc-select\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"required_params") : depth0),"[]","prefix",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":97},"end":{"line":1,"column":160}}})) != null ? stack1 : "")
    + ">\n<option value=\"\" selected=\"selected\"> </option>\n"
    + alias2(lookupProperty(helpers,"selectoptions").call(alias3,"Mr,Mrs,Ms,Miss,Hon,Dr,Rev,Prof,Dean,Pres,Rabbi,Imam",(depth0 != null ? lookupProperty(depth0,"prefix") : depth0),{"name":"selectoptions","hash":{"error":((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"prefix") : stack1)},"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":98}}}))
    + "\n</select>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("state_field", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"req"),depth0,{"name":"req","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<select name=\"state\" name=\"state\" title=\"State\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-state-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"required_params") : depth0),"[]","state",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":83},"end":{"line":1,"column":145}}})) != null ? stack1 : "")
    + ">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"state_options"),depth0,{"name":"state_options","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</select>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("street_address_field", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"req"),depth0,{"name":"req","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"text\" name=\"street_address\" title=\"Enter your street address\" value=\""
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"street_address") : depth0),{"name":"toString","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":82},"end":{"line":1,"column":123}}})) != null ? stack1 : "")
    + alias3(alias2(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"street_address") : stack1), depth0))
    + "\" placeholder=\"Street Address\" title=\"Address 1\" maxlength=\"120\" id=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-a1-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"required_params") : depth0),"[]","street_address",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":253},"end":{"line":1,"column":324}}})) != null ? stack1 : "")
    + ">\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("update_button", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<button class=\"button update-button\" type=\"submit\"></button>\n";
},"useData":true}));

Handlebars.registerPartial("zip_field", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"req"),depth0,{"name":"req","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"tel\" title=\"Enter your 5 digit zip code.\" value=\""
    + ((stack1 = lookupProperty(helpers,"toString").call(alias1,(depth0 != null ? lookupProperty(depth0,"zipcode") : depth0),{"name":"toString","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":62},"end":{"line":1,"column":96}}})) != null ? stack1 : "")
    + alias3(alias2(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"zipcode") : stack1), depth0))
    + "\" name=\"zipcode\" title=\"Zip\" pattern=\"^\\d{5}(-)?(\\d{4})?$\" placeholder=\"Zip\" id=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-zip-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" maxlength=\"5\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"required_params") : depth0),"[]","zipcode",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":246},"end":{"line":1,"column":310}}})) != null ? stack1 : "")
    + " class=\"zip-field text-field\">\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("choose-contacts", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<label><input type=\"checkbox\" value=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"email") : depth0), depth0))
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"fname") : depth0),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":51},"end":{"line":3,"column":111}}})) != null ? stack1 : "")
    + "\"> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"name") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.program(7, data, 0),"data":data,"loc":{"start":{"line":3,"column":114},"end":{"line":3,"column":195}}})) != null ? stack1 : "")
    + "</label>\n";
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "|"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"fname") : depth0), depth0))
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"lname") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":74},"end":{"line":3,"column":104}}})) != null ? stack1 : "");
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "|"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"lname") : depth0), depth0));
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0)) != null ? stack1 : "")
    + " <span>"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0)) != null ? stack1 : "")
    + "</span>";
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0)) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"contact-chooser\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"contacts") : depth0),{"name":"each","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":4,"column":9}}})) != null ? stack1 : "")
    + "</div>\n";
},"useData":true}));

Handlebars.registerPartial("editable-petition-form", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return " has-prefix-field";
},"3":function(container,depth0,helpers,partials,data) {
    return "<small>By providing your mobile number you consent to receive cell phone and text communications from Sierra Club and its affiliated entities concerning news, action opportunities, and other important information about the environment.</small>\n";
},"5":function(container,depth0,helpers,partials,data) {
    return "Send Your Message";
},"7":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"phrase") : depth0), depth0));
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<ul class=\"user-input-form petition-form"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","prefix",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":40},"end":{"line":1,"column":107}}})) != null ? stack1 : "")
    + "\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"petition-fields"),depth0,{"name":"petition-fields","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "<li class=\"disclaimer full-width\"><small>All fields required unless noted otherwise.</small>\n<span class=\"tooltip\">?\n<span class=\"tooltip-bubble\">\n<span class=\"closebutton\"></span>\n<p>Recipients often require this information in order to accept your message, so we have to require it too.</p>\n</span>\n</span>\n</li>\n\n<li class=\"disclaimer full-width\">\n<p class=\"legal\">\n<small>By taking action, you will also receive periodic communications from the Sierra Club. You can unsubscribe at any time. </small>\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","phone",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":15,"column":0},"end":{"line":17,"column":12}}})) != null ? stack1 : "")
    + "</p>\n</li>\n\n<li class=\"submit base\">\n<label>&nbsp;</label>\n<button class=\"button sign-button\" type=\"submit\">"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","Contact Officials - All info required",{"name":"compare","hash":{},"fn":container.program(5, data, 0),"inverse":container.program(7, data, 0),"data":data,"loc":{"start":{"line":23,"column":49},"end":{"line":23,"column":174}}})) != null ? stack1 : "")
    + "</button>\n<p>"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"next-action-link"),depth0,{"name":"next-action-link","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</p>\n</li>\n</ul>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("petition-form", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return " has-prefix-field";
},"3":function(container,depth0,helpers,partials,data) {
    return "<small>By providing your mobile number you consent to receive cell phone and text communications from Sierra Club and its affiliated entities concerning news, action opportunities, and other important information about the environment.</small>";
},"5":function(container,depth0,helpers,partials,data) {
    return "Send Your Message";
},"7":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"phrase") : depth0), depth0));
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<form data-formaction=\"moleform\" method=\"POST\" action=\"/u/actions/"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" class=\"clearfix\">\n<ul class=\"user-input-form petition-form"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","prefix",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":40},"end":{"line":2,"column":107}}})) != null ? stack1 : "")
    + "\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"petition-fields"),depth0,{"name":"petition-fields","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "<li class=\"disclaimer full-width\"><small>All fields required unless noted otherwise.</small>\n<span class=\"tooltip\">?\n<span class=\"tooltip-bubble\">\n<span class=\"closebutton\"></span>\n<p>Recipients often require this information in order to accept your message, so we have to require it too.</p>\n</span>\n</span>\n</li>\n\n<li class=\"disclaimer full-width\">\n<p class=\"legal\">\n<small>By taking action, you will also receive periodic communications from the Sierra Club. You can unsubscribe at any time. </small>\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"show_fields") : depth0),"[]","phone",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":0},"end":{"line":16,"column":292}}})) != null ? stack1 : "")
    + "\n</p>\n</li>\n\n<li class=\"submit base\">\n<label>&nbsp;</label>\n<button class=\"button sign-button\" type=\"submit\">"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","Contact Officials - All info required",{"name":"compare","hash":{},"fn":container.program(5, data, 0),"inverse":container.program(7, data, 0),"data":data,"loc":{"start":{"line":22,"column":49},"end":{"line":22,"column":174}}})) != null ? stack1 : "")
    + "</button>\n<p>"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"next-action-link"),depth0,{"name":"next-action-link","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</p>\n</li>\n</ul>\n</form>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("send-email", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"hidden\" name=\"user_id\" value=\""
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"moleid") : stack1), depth0))
    + "\">";
},"3":function(container,depth0,helpers,partials,data) {
    return "site";
},"5":function(container,depth0,helpers,partials,data) {
    return "campaign";
},"7":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"campaign_id") : depth0), depth0));
},"9":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<input type=\"hidden\" name=\"sharer_email\" value=\""
    + ((stack1 = lookupProperty(helpers,"ifor").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"email") : stack1),{"name":"ifor","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":10,"column":81},"end":{"line":10,"column":127}}})) != null ? stack1 : "")
    + "\">\n";
},"10":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"owner-email") : depth0), depth0));
},"12":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li>\n<label class=\"h4\" for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-email-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">From:</label>\n<input type=\"email\" name=\"sharer_email\" value=\"\" placeholder=\"Your Email Address\" title=\"Your Email Address\" maxlength=\"254\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-email-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" class=\"email-field text-field\" required=\"required\">\n</li>\n";
},"14":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<textarea name=\"recipients\" required=\"required\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-to-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" placeholder=\"Enter a comma separated list of email addresses\"></textarea>\n";
},"16":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<select name=\"recipients\" required=\"required\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-to-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" class=\"email-select\"></select>\n";
},"18":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "Check out this campaign from AddUp.Org: \""
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"title") : stack1), depth0)) != null ? stack1 : "")
    + "\"\nJoin me in taking action now: "
    + container.escapeExpression(lookupProperty(helpers,"source").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"url") : stack1),{"name":"source","hash":{},"data":data,"loc":{"start":{"line":31,"column":30},"end":{"line":31,"column":50}}}))
    + "promoid=70131000001LjgmAAC&utm_medium=recruit&utm_source=email&utm_campaign=addup\n";
},"20":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "Dear friend of the environment,\n\nPlease join me at addup.org, an online community protecting the environment through exciting campaigns and events. We're mobilizing to take real actions in our communities on issues like climate change, clean energy and preserving wildlife.\n\nCheck it out: "
    + container.escapeExpression(lookupProperty(helpers,"source").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"url") : stack1),(depth0 != null ? lookupProperty(depth0,"t_sources") : depth0),{"name":"source","hash":{},"data":data,"loc":{"start":{"line":36,"column":14},"end":{"line":36,"column":44}}}))
    + "\n";
},"22":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"first_name") : stack1), depth0))
    + " "
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"last_name") : stack1), depth0));
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<form data-formaction=\"moleform\" method=\"POST\" action=\"/shares\" data-vcb=\"sendemail\" data-vtransform=\"convertemails\" class=\"pick-only send-email-form\">\n<p class=\"pick-step\">Type or select up to 50 friends to share with.</p>\n<p class=\"compose-step\">Personalize and send your message.</p>\n<ul class=\"user-input-form send-email\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"moleid") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":5,"column":90}}})) != null ? stack1 : "")
    + "\n<input type=\"hidden\" name=\"share_code\" value=\""
    + alias3(alias2(((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"share_code") : stack1), depth0))
    + "\">\n<input type=\"hidden\" name=\"type\" value=\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"default_share") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.program(5, data, 0),"data":data,"loc":{"start":{"line":7,"column":40},"end":{"line":7,"column":88}}})) != null ? stack1 : "")
    + "\">\n<input type=\"hidden\" name=\"campaign_id\" value=\""
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"id") : stack1),{"name":"ifor","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":47},"end":{"line":8,"column":89}}})) != null ? stack1 : "")
    + "\">\n<input type=\"hidden\" name=\"action_id\" value=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"any").call(alias1,(depth0 != null ? lookupProperty(depth0,"owner-email") : depth0),((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"email") : stack1),{"name":"any","hash":{},"fn":container.program(9, data, 0),"inverse":container.program(12, data, 0),"data":data,"loc":{"start":{"line":10,"column":0},"end":{"line":16,"column":8}}})) != null ? stack1 : "")
    + "<li>\n<label class=\"h4\" for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-to-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">To:</label>\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"gotcontacts") : depth0),{"name":"unless","hash":{},"fn":container.program(14, data, 0),"inverse":container.program(16, data, 0),"data":data,"loc":{"start":{"line":19,"column":0},"end":{"line":23,"column":11}}})) != null ? stack1 : "")
    + "</li>\n<li class=\"email-contacts pick-step\"></li>\n<li class=\"compose-step\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-state-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\"></label>\n<textarea required=\"required\" class=\"email-compose\" name=\"custom_msg\">\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"default_share") : depth0),{"name":"unless","hash":{},"fn":container.program(18, data, 0),"inverse":container.program(20, data, 0),"data":data,"loc":{"start":{"line":29,"column":0},"end":{"line":37,"column":11}}})) != null ? stack1 : "")
    + "Sincerely,\n"
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias1,(depth0 != null ? lookupProperty(depth0,"owner-name") : depth0),{"name":"ifor","hash":{},"fn":container.program(22, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":39,"column":0},"end":{"line":39,"column":71}}})) != null ? stack1 : "")
    + "</textarea>\n</li>\n<li class=\"compose-step\">\n<label><input type=\"checkbox\" name=\"copy_sharer\" value=\"1\"> Send a copy to myself</label>\n</li>\n<li class=\"submit pick-step\">\n<a href=\"#next\" class=\"button next-button\">Next</a>\n</li>\n\n<li class=\"submit compose-step\">\n<button class=\"button sign-button\" type=\"submit\">Send</button>\n</li>\n<li class=\"back-li\">\n<button class=\"button back-button\" type=\"reset\">Back</button>\n</li>\n</ul>\n\n</form>\n";
},"useData":true}));

Handlebars.registerPartial("volunteer-form", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "volunteer-has-overlay";
},"3":function(container,depth0,helpers,partials,data) {
    return "<input type=\"checkbox\" name=\"volunteer_options\" id=\"canvassing\" value=\"canvassing\">\n<label class=\"volunteer-block\" for=\"canvassing\">\n<div class=\"volunteer-option\">\n<h4>Canvassing</h4>\n<p>Talk to people in your community (door-to-door, at events, etc)</p>\n</div>\n</label>\n<input type=\"checkbox\" name=\"volunteer_options\" id=\"event_support\" value=\"event_support\">\n<label class=\"volunteer-block\" for=\"event_support\">\n<div class=\"volunteer-option\">\n<h4>Supporting Events</h4>\n<p>Lend a hand to make an event successful</p>\n</div>\n</label>\n<input type=\"checkbox\" name=\"volunteer_options\" id=\"oped\" value=\"oped\">\n<label class=\"volunteer-block\" for=\"oped\">\n<div class=\"volunteer-option\">\n<h4>Writing</h4>\n<p>Submit a letter to the editor or op-ed to newspapers</p>\n</div>\n</label>\n<input type=\"checkbox\" name=\"volunteer_options\" id=\"phonebanking\" value=\"phonebanking\">\n<label class=\"volunteer-block\" for=\"phonebanking\">\n<div class=\"volunteer-option\">\n<h4>Phonebanking</h4>\n<p>Make phone calls to get people involved</p>\n</div>\n</label>\n<input type=\"checkbox\" name=\"volunteer_options\" id=\"speaking\" value=\"speaking\">\n<label class=\"volunteer-block\" for=\"speaking\">\n<div class=\"volunteer-option\">\n<h4>Speaking Out</h4>\n<p>Testify at a public hearing or speak at an event</p>\n</div>\n</label>\n<input type=\"checkbox\" name=\"volunteer_options\" id=\"fundraising\" value=\"fundraising\">\n<label class=\"volunteer-block\" for=\"fundraising\">\n<div class=\"volunteer-option\">\n<h4>Fundraising</h4>\n<p>Help raise money to support Sierra Club's work</p>\n</div>\n</label>\n";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"available_actions") : depth0),{"name":"each","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":52,"column":0},"end":{"line":62,"column":9}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"available_actions") : depth0),{"name":"each","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":64,"column":0},"end":{"line":74,"column":9}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"available_actions") : depth0),{"name":"each","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":76,"column":0},"end":{"line":86,"column":9}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"available_actions") : depth0),{"name":"each","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":88,"column":0},"end":{"line":98,"column":9}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"available_actions") : depth0),{"name":"each","hash":{},"fn":container.program(18, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":100,"column":0},"end":{"line":110,"column":9}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"available_actions") : depth0),{"name":"each","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":112,"column":0},"end":{"line":122,"column":9}}})) != null ? stack1 : "")
    + "\n";
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,"===","canvassing",{"name":"compare","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":53,"column":0},"end":{"line":61,"column":12}}})) != null ? stack1 : "");
},"7":function(container,depth0,helpers,partials,data) {
    return "<input type=\"checkbox\" name=\"volunteer_options\" id=\"canvassing\" value=\"canvassing\">\n<label class=\"volunteer-block\" for=\"canvassing\">\n<div class=\"volunteer-option\">\n<h4>Canvassing</h4>\n<p>Talk to people in your community (door-to-door, at events, etc)</p>\n</div>\n</label>\n";
},"9":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,"===","event_support",{"name":"compare","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":65,"column":0},"end":{"line":73,"column":12}}})) != null ? stack1 : "");
},"10":function(container,depth0,helpers,partials,data) {
    return "<input type=\"checkbox\" name=\"volunteer_options\" id=\"event_support\" value=\"event_support\">\n<label class=\"volunteer-block\" for=\"event_support\">\n<div class=\"volunteer-option\">\n<h4>Supporting Events</h4>\n<p>Lend a hand to make an event successful</p>\n</div>\n</label>\n";
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,"===","oped",{"name":"compare","hash":{},"fn":container.program(13, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":77,"column":0},"end":{"line":85,"column":12}}})) != null ? stack1 : "");
},"13":function(container,depth0,helpers,partials,data) {
    return "<input type=\"checkbox\" name=\"volunteer_options\" id=\"oped\" value=\"oped\">\n<label class=\"volunteer-block\" for=\"oped\">\n<div class=\"volunteer-option\">\n<h4>Writing</h4>\n<p>Submit a letter to the editor or op-ed to newspapers</p>\n</div>\n</label>\n";
},"15":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,"===","phonebanking",{"name":"compare","hash":{},"fn":container.program(16, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":89,"column":0},"end":{"line":97,"column":12}}})) != null ? stack1 : "");
},"16":function(container,depth0,helpers,partials,data) {
    return "<input type=\"checkbox\" name=\"volunteer_options\" id=\"phonebanking\" value=\"phonebanking\">\n<label class=\"volunteer-block\" for=\"phonebanking\">\n<div class=\"volunteer-option\">\n<h4>Phonebanking</h4>\n<p>Make phone calls to get people involved</p>\n</div>\n</label>\n";
},"18":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,"===","speaking",{"name":"compare","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":101,"column":0},"end":{"line":109,"column":12}}})) != null ? stack1 : "");
},"19":function(container,depth0,helpers,partials,data) {
    return "<input type=\"checkbox\" name=\"volunteer_options\" id=\"speaking\" value=\"speaking\">\n<label class=\"volunteer-block\" for=\"speaking\">\n<div class=\"volunteer-option\">\n<h4>Speaking Out</h4>\n<p>Testify at a public hearing or speak at an event</p>\n</div>\n</label>\n";
},"21":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,"===","fundraising",{"name":"compare","hash":{},"fn":container.program(22, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":113,"column":0},"end":{"line":121,"column":12}}})) != null ? stack1 : "");
},"22":function(container,depth0,helpers,partials,data) {
    return "<input type=\"checkbox\" name=\"volunteer_options\" id=\"fundraising\" value=\"fundraising\">\n<label class=\"volunteer-block\" for=\"fundraising\">\n<div class=\"volunteer-option\">\n<h4>Fundraising</h4>\n<p>Help raise money to support Sierra Club's work</p>\n</div>\n</label>\n";
},"24":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"email") : depth0), depth0));
},"26":function(container,depth0,helpers,partials,data) {
    return "<div>You've already chosen a volunteer action, but you can choose more!</div>";
},"28":function(container,depth0,helpers,partials,data) {
    return "<div class=\"signup-overlay\">\n<h3>You need an ADDUP account to volunteer for this campaign</h3>\n<p>(It's quick and free!)</p>\n<a href=\"/register\" onClick=\"javascript:location.href='/register'\" class=\"button action-button\">Register</a>\n<p>Already have an account?</p>\n<p><a href=\"/saml/login\" class=\"clickthrough popup saml loginlink\">Log in with email</a>\n<br /><a class=\"loginlink\" data-event=\"request-fbconnect\" data-addperms=\"\">Log in with Facebook</a></p>\n\n</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<form data-formaction=\"moleform\" method=\"POST\" action=\"/u/actions/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" class=\"clearfix\">\n\n<div class=\"volunteer-options "
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"t_loggedout") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":30},"end":{"line":3,"column":77}}})) != null ? stack1 : "")
    + "\">\n\n<div class=\"options-container volunteer-options-container-all\">\n\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"available_actions") : depth0),"[arraylength]",0,{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.program(5, data, 0),"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":124,"column":12}}})) != null ? stack1 : "")
    + "</div>\n\n<input type=\"hidden\" name=\"email\" value=\"asdf@asdf.com"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"email") : depth0),"!=","false",{"name":"compare","hash":{},"fn":container.program(24, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":127,"column":54},"end":{"line":127,"column":106}}})) != null ? stack1 : "")
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"email") : stack1), depth0))
    + "\" placeholder=\"Email\" title=\"Email Address\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-email-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" class=\"email-field text-field\" required=\"required\">\n\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"action_took") : depth0),{"name":"if","hash":{},"fn":container.program(26, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":129,"column":0},"end":{"line":129,"column":103}}})) != null ? stack1 : "")
    + "\n\n<p class=\"volunteer-options-all error\">Sorry, you must pick at least one option.</p>\n\n<p class=\"volunteer-email-all error\">Sorry, you must have a real email.</p>\n\n<div class=\"volunteer-submit\">\n<button class=\"button action-button\">I'm ready to help!</button>\n</div>\n\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias3,(depth0 != null ? lookupProperty(depth0,"moleid") : depth0),{"name":"unless","hash":{},"fn":container.program(28, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":139,"column":0},"end":{"line":149,"column":11}}})) != null ? stack1 : "")
    + "</div> <!-- volunteer options -->\n</form>\n";
},"useData":true}));

Handlebars.registerPartial("impact-stats", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"tile-item impact-stat-item\">\n<h1 class=\"stat-figure\">"
    + container.escapeExpression(lookupProperty(helpers,"numabbrv").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"stat") : depth0),1,{"name":"numabbrv","hash":{},"data":data,"loc":{"start":{"line":2,"column":24},"end":{"line":2,"column":43}}}))
    + "</h1>\n<figcaption class=\"stat-label\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"description") : depth0), depth0)) != null ? stack1 : "")
    + "</figcaption>\n</figure>\n";
},"useData":true}));

Handlebars.registerPartial("latest-update", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var alias1=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\""
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"link") : depth0), depth0))
    + "\" target=\"_external\" class=\"more-link\">"
    + alias1(lookupProperty(helpers,"prettylink").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"link") : depth0),{"name":"prettylink","hash":{},"data":data,"loc":{"start":{"line":16,"column":56},"end":{"line":16,"column":75}}}))
    + "</a><br/>\n";
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"#\" class=\"more-button-after hide-when-expanded hide-expander\" data-expand=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "-reportback\">Read&nbsp;More</a>\n";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div data-expandable=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "-reportback\" class=\"expandable open report-expanded-content base\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"yt_video") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.program(8, data, 0),"data":data,"loc":{"start":{"line":24,"column":0},"end":{"line":24,"column":188}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"content") : depth0), depth0)) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"url") : depth0),{"name":"if","hash":{},"fn":container.program(11, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":26,"column":0},"end":{"line":28,"column":7}}})) != null ? stack1 : "")
    + "<p><button class=\"less-button\" data-expand=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "-reportback\">Read Less</button></p>\n</div>\n";
},"6":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"videoWrapper\">"
    + container.escapeExpression(lookupProperty(helpers,"videofromurl").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"yt_video") : depth0),{"name":"videofromurl","hash":{},"data":data,"loc":{"start":{"line":24,"column":42},"end":{"line":24,"column":67}}}))
    + "</div>";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"image") : depth0),{"name":"if","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":24,"column":81},"end":{"line":24,"column":181}}})) != null ? stack1 : "");
},"9":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"image") : depth0), depth0))
    + "/@my_300\" alt=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "\" class=\"report-image image-or-video\">";
},"11":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p class=\"reportback-external-url\"><a href=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"url") : depth0), depth0))
    + "\" target=\"_blank\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"url") : depth0), depth0))
    + "</a></p>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, alias3=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<section class=\"reportback-update\" id=\"reportbackemail\">\n<ul class=\"reportback-date\">\n<li class=\"update-day\">"
    + alias2(lookupProperty(helpers,"mdate").call(alias1,(depth0 != null ? lookupProperty(depth0,"updated_at") : depth0),"getDate",{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":23},"end":{"line":3,"column":53}}}))
    + "</li>\n<li class=\"update-month\"><small>"
    + alias2(lookupProperty(helpers,"mdate").call(alias1,(depth0 != null ? lookupProperty(depth0,"updated_at") : depth0),"abvMon",{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":4,"column":32},"end":{"line":4,"column":61}}}))
    + "</small><small>"
    + alias2(lookupProperty(helpers,"mdate").call(alias1,(depth0 != null ? lookupProperty(depth0,"updated_at") : depth0),"getFullYear",{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":4,"column":76},"end":{"line":4,"column":110}}}))
    + "</small></li>\n</ul>\n<div class=\"report-title\">\n<p>\nLatest Update\n</p>\n</div>\n<div class=\"report-content clearfix\">\n<p class=\"report-slug\">\n<b class=\"reporttitle\">"
    + ((stack1 = alias3((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0)) != null ? stack1 : "")
    + "</b><br/>\n"
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"description") : depth0), depth0))
    + "<br/>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"link") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":15,"column":0},"end":{"line":17,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"content") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":18,"column":0},"end":{"line":20,"column":7}}})) != null ? stack1 : "")
    + "</p>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"content") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":22,"column":0},"end":{"line":31,"column":7}}})) != null ? stack1 : "")
    + "</div>\n</section>\n";
},"useData":true}));

Handlebars.registerPartial("marquee-header", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "left";
},"3":function(container,depth0,helpers,partials,data) {
    return "center";
},"5":function(container,depth0,helpers,partials,data) {
    return " downward-arrow-icon";
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"alt-title\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"phrase") : depth0), depth0)) != null ? stack1 : "")
    + "</span>";
},"9":function(container,depth0,helpers,partials,data) {
    return "id=\"event-title\"";
},"11":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"titleize").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"display_title") : depth0),{"name":"titleize","hash":{},"data":data,"loc":{"start":{"line":4,"column":177},"end":{"line":4,"column":203}}}));
},"13":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.program(16, data, 0),"data":data,"loc":{"start":{"line":4,"column":211},"end":{"line":4,"column":344}}})) != null ? stack1 : "");
},"14":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"titleize").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"titleize","hash":{},"data":data,"loc":{"start":{"line":4,"column":224},"end":{"line":4,"column":242}}}));
},"16":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"===","volunteer",{"name":"compare","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":250},"end":{"line":4,"column":337}}})) != null ? stack1 : "");
},"17":function(container,depth0,helpers,partials,data) {
    return "Volunteer To Increase Your Impact";
},"19":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<!-- // action-content__umbrella-description required for 1 instance in dev.main.js (5148) -->\n<div class=\"action-content action-content__umbrella-description\">\n<p>"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"belongs_to_umbrella_event") : depth0)) != null ? lookupProperty(stack1,"description") : stack1), depth0))
    + "</p>\n</div>\n";
},"21":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"action-content\">\n<p>"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"action_description") : depth0), depth0))
    + "</p>\n</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<header class=\"marquee-header-"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"mleft") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":1,"column":30},"end":{"line":1,"column":68}}})) != null ? stack1 : "")
    + "\">\n<h2 class=\"marquee-header-text\">\n<span class=\"marquee-inner-text"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"arrow") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":31},"end":{"line":3,"column":71}}})) != null ? stack1 : "")
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"phrase") : depth0),{"name":"if","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":4,"column":64}}})) != null ? stack1 : "")
    + "<span class=\"main-title\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"===","event",{"name":"compare","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":89},"end":{"line":4,"column":155}}})) != null ? stack1 : "")
    + ">"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"display_title") : depth0),{"name":"if","hash":{},"fn":container.program(11, data, 0),"inverse":container.program(13, data, 0),"data":data,"loc":{"start":{"line":4,"column":156},"end":{"line":4,"column":351}}})) != null ? stack1 : "")
    + "</span>\n</span>\n</h2>\n</header>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"belongs_to_umbrella_event") : depth0),{"name":"if","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":13,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_description") : depth0),{"name":"if","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":0},"end":{"line":18,"column":7}}})) != null ? stack1 : "");
},"useData":true}));

Handlebars.registerPartial("next-action-link", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"t_last") : depth0),{"name":"unless","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":2,"column":107}}})) != null ? stack1 : "")
    + "\n";
},"2":function(container,depth0,helpers,partials,data) {
    return "<p class=\"skip-step\"><a href=\"#next\" class=\"smooth-scroll\">NEXT ACTION</a></p>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"all").call(alias1,(depth0 != null ? lookupProperty(depth0,"outer") : depth0),lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_pagetitle") : depth0),"!=","share",{"name":"compare","hash":{},"data":data,"loc":{"start":{"line":1,"column":13},"end":{"line":1,"column":47}}}),lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_pagetitle") : depth0),"!=","supporter-home",{"name":"compare","hash":{},"data":data,"loc":{"start":{"line":1,"column":48},"end":{"line":1,"column":91}}}),{"name":"all","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":3,"column":8}}})) != null ? stack1 : "");
},"useData":true}));

Handlebars.registerPartial("act_total_actions_taken", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"sm-figure campaign-image\"><img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"campaign_img") : depth0), depth0))
    + "/@zx_175@zy_175\"></figure>\n<div class=\"notification-content with-figure\">\n<header>"
    + alias2(lookupProperty(helpers,"mdate").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":22}}}))
    + "</header>\n<p>"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"notice") : depth0), depth0)) != null ? stack1 : "")
    + "</p>\n</div>\n";
},"useData":true}));

Handlebars.registerPartial("cat_trending_state", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"sm-figure category-icon "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"category_slug") : depth0), depth0))
    + "-icon\"></figure>\n<div class=\"notification-content with-figure\">\n<header>"
    + alias2(lookupProperty(helpers,"mdate").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":22}}}))
    + "</header>\n<p>"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"notice") : depth0), depth0)) != null ? stack1 : "")
    + "</p>\n<p><a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"category_slug") : depth0), depth0))
    + "\" class=\"notification-link\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"cta") : depth0), depth0))
    + "</a></p>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"share-cat"),depth0,{"name":"share-cat","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("cat_trending", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.escapeExpression, alias2=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"sm-figure\"><img src=\"/img/trending-icon.png\"></figure>\n<div class=\"notification-content with-figure\">\n<header>"
    + alias1(lookupProperty(helpers,"mdate").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":22}}}))
    + "</header>\n<p>"
    + ((stack1 = alias2((depth0 != null ? lookupProperty(depth0,"notice") : depth0), depth0)) != null ? stack1 : "")
    + "</p>\n<p><a href=\"/campaigns/"
    + alias1(alias2((depth0 != null ? lookupProperty(depth0,"category_slug") : depth0), depth0))
    + "\" class=\"notification-link\">"
    + alias1(alias2((depth0 != null ? lookupProperty(depth0,"cta") : depth0), depth0))
    + "</a></p>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"share-cat"),depth0,{"name":"share-cat","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("cpn_total_supporters_by_state", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"sm-figure campaign-image\"><img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"campaign_img") : depth0), depth0))
    + "/@zx_175@zy_175\"></figure>\n<div class=\"notification-content with-figure\">\n<header>"
    + alias2(lookupProperty(helpers,"mdate").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":22}}}))
    + "</header>\n<p>"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"notice") : depth0), depth0)) != null ? stack1 : "")
    + " "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"action-cta"),depth0,{"name":"action-cta","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</p>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"share-camp"),depth0,{"name":"share-camp","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("cpn_total_supporters", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"sm-figure campaign-image\"><img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"campaign_img") : depth0), depth0))
    + "/@zx_175@zy_175\"></figure>\n<div class=\"notification-content with-figure\">\n<header>"
    + alias2(lookupProperty(helpers,"mdate").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":22}}}))
    + "</header>\n<p>"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"notice") : depth0), depth0)) != null ? stack1 : "")
    + " "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"action-cta"),depth0,{"name":"action-cta","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</p>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"share-camp"),depth0,{"name":"share-camp","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("cpn_trending_by_state", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"sm-figure\"><img src=\"/img/trending-icon.png\"></figure>\n<div class=\"notification-content with-figure\">\n<header>"
    + container.escapeExpression(lookupProperty(helpers,"mdate").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":22}}}))
    + "</header>\n<p>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"notice") : depth0), depth0)) != null ? stack1 : "")
    + " "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"action-cta"),depth0,{"name":"action-cta","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</p>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"share-camp"),depth0,{"name":"share-camp","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("cpn_trending", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"sm-figure\"><img src=\"/img/trending-icon.png\"></figure>\n<div class=\"notification-content with-figure\">\n<header>"
    + container.escapeExpression(lookupProperty(helpers,"mdate").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":22}}}))
    + "</header>\n<p>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"notice") : depth0), depth0)) != null ? stack1 : "")
    + "</p>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"action-cta"),depth0,{"name":"action-cta","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + ((stack1 = container.invokePartial(lookupProperty(partials,"share-camp"),depth0,{"name":"share-camp","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("ending_soon", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"sm-figure campaign-image\"><img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"campaign_img") : depth0), depth0))
    + "/@zx_175@zy_175\"></figure>\n<div class=\"notification-content with-figure\">\n<header>"
    + alias2(lookupProperty(helpers,"mdate").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":22}}}))
    + "</header>\n<p>"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"notice") : depth0), depth0)) != null ? stack1 : "")
    + " "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"action-cta"),depth0,{"name":"action-cta","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</p>\n</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("featured_campaign_total_supporters_by_state", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"large-figure\"><img src=\"/img/yourarea-icon.png\"></figure>\n<div class=\"notification-content with-large-figure\">\n<header>"
    + container.escapeExpression(lookupProperty(helpers,"mdate").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":22}}}))
    + " | <span>In Your Area</span></header>\n<p>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"notice") : depth0), depth0)) != null ? stack1 : "")
    + "</p>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"share-camp"),depth0,{"name":"share-camp","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("featured_campaign_total_supporters", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"large-figure featured-figure campaign-image\"><img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"campaign_img") : depth0), depth0))
    + "/@zx_175@zy_175\"></figure>\n<div class=\"notification-content with-large-figure\">\n<header>"
    + alias2(lookupProperty(helpers,"mdate").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":22}}}))
    + " | <span>Featured Campaign</span></header>\n<p>"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"notice") : depth0), depth0)) != null ? stack1 : "")
    + "</p>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"share-camp"),depth0,{"name":"share-camp","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("friend_action", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"large-figure featured-figure campaign-image\"><img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"campaign_img") : depth0), depth0))
    + "/@zx_175@zy_175\"></figure>\n<div class=\"notification-content with-large-figure\">\n<header>"
    + alias2(lookupProperty(helpers,"mdate").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":22}}}))
    + "</header>\n<p>"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"notice") : depth0), depth0)) != null ? stack1 : "")
    + " "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"action-cta"),depth0,{"name":"action-cta","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</p>\n</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("friends_joined", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "and "
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"num_recruits") : depth0), depth0))
    + " other "
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"two_recruits") : depth0),{"name":"unless","hash":{},"fn":container.program(2, data, 0),"inverse":container.program(4, data, 0),"data":data,"loc":{"start":{"line":4,"column":78},"end":{"line":4,"column":143}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    return "friends have";
},"4":function(container,depth0,helpers,partials,data) {
    return "friend has";
},"6":function(container,depth0,helpers,partials,data) {
    return "has";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"tiny-avatar\"><img src=\""
    + ((stack1 = lookupProperty(helpers,"ifor").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"avatar_url") : depth0),{"name":"ifor","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":54},"end":{"line":5,"column":206}}})) != null ? stack1 : "")
    + "\" title=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"first_name") : depth0), depth0))
    + " "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"last_name") : depth0), depth0))
    + "\" alt=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"first_name") : depth0), depth0))
    + "\"></span>";
},"9":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"oauth_uid") : depth0),{"name":"if","hash":{},"fn":container.program(10, data, 0),"inverse":container.program(12, data, 0),"data":data,"loc":{"start":{"line":5,"column":74},"end":{"line":5,"column":197}}})) != null ? stack1 : "");
},"10":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "https://graph.facebook.com/"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"oauth_uid") : depth0), depth0))
    + "/picture?width=40&height=40";
},"12":function(container,depth0,helpers,partials,data) {
    return "/img/user_no_image.png?1";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"sm-figure round-icon recruit-icon\"></figure>\n<div class=\"notification-content with-figure\">\n<header>"
    + alias2(lookupProperty(helpers,"mdate").call(alias1,(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":22}}}))
    + "</header>\n<p>"
    + alias2(container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"recruits") : depth0)) != null ? lookupProperty(stack1,"0") : stack1)) != null ? lookupProperty(stack1,"first_name") : stack1), depth0))
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_recruits") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(6, data, 0),"data":data,"loc":{"start":{"line":4,"column":31},"end":{"line":4,"column":161}}})) != null ? stack1 : "")
    + " joined you on AddUp.</p>\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"recruits") : depth0),{"name":"each","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":5,"column":282}}})) != null ? stack1 : "")
    + "\n</div>\n";
},"useData":true}));

Handlebars.registerPartial("just_launched_local", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"sm-figure campaign-image\"><img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"campaign_img") : depth0), depth0))
    + "/@zx_175@zy_175\"></figure>\n<div class=\"notification-content with-figure\">\n<header>"
    + alias2(lookupProperty(helpers,"mdate").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":22}}}))
    + "</header>\n<p>"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"notice") : depth0), depth0)) != null ? stack1 : "")
    + " "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"action-cta"),depth0,{"name":"action-cta","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</p>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"share-camp"),depth0,{"name":"share-camp","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("milestone", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"category-icon "
    + container.escapeExpression(lookupProperty(helpers,"find").call(alias1,(depth0 != null ? lookupProperty(depth0,"category_slug") : depth0),(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),"checkmark",{"name":"find","hash":{},"data":data,"loc":{"start":{"line":2,"column":29},"end":{"line":2,"column":75}}}))
    + "-icon"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":80},"end":{"line":2,"column":125}}})) != null ? stack1 : "")
    + "\"></figure>\n<div class=\"inner-notice\">\n<p>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"notice") : depth0), depth0)) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"cta") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":16},"end":{"line":4,"column":67}}})) != null ? stack1 : "")
    + "</p>\n</div>\n<div class=\"close-btn\"></div>\n";
},"2":function(container,depth0,helpers,partials,data) {
    return " action-notice-icon";
},"4":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"link_to") : depth0), depth0))
    + "\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"cta") : depth0), depth0))
    + "</a>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"gridsection","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":7,"column":16}}})) != null ? stack1 : "");
},"useData":true}));

Handlebars.registerPartial("new_action_added", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"sm-figure campaign-image\"><img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"campaign_img") : depth0), depth0))
    + "/@zx_175@zy_175\"></figure>\n<div class=\"notification-content with-figure\">\n<header>"
    + alias2(lookupProperty(helpers,"mdate").call(alias3,(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":22}}}))
    + "</header>\n<p>"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"notice") : depth0), depth0)) != null ? stack1 : "")
    + "</p>\n"
    + alias2(lookupProperty(helpers,"$").call(alias3,"share-camp",{"name":"$","hash":{"share_action":true},"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":5,"column":36}}}))
    + "\n</div>\n";
},"useData":true}));

Handlebars.registerPartial("action-cta", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"campaign_slug") : depth0), depth0))
    + ((stack1 = lookupProperty(helpers,"all").call(alias3,(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),(depth0 != null ? lookupProperty(depth0,"action_slug") : depth0),{"name":"all","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":48},"end":{"line":1,"column":120}}})) != null ? stack1 : "")
    + "\" class=\"notification-link"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"action_type") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":146},"end":{"line":1,"column":205}}})) != null ? stack1 : "")
    + "\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"cta") : depth0), depth0))
    + "</a>";
},"2":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"action_type") : depth0), depth0))
    + "/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"action_slug") : depth0), depth0));
},"4":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " with-action "
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"action_type") : depth0), depth0))
    + "-icon";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"cta") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":225}}})) != null ? stack1 : "")
    + "\n";
},"useData":true}));

Handlebars.registerPartial("cat-cta", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p><a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"category_slug") : depth0), depth0))
    + "\" class=\"notification-link\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"cta") : depth0), depth0))
    + "</a></p>\n";
},"useData":true}));

Handlebars.registerPartial("share-camp", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " data-action-type=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"action_type") : depth0), depth0))
    + "\" data-action-slug=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"action_slug") : depth0), depth0))
    + "\"";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"n-share\" data-campaign=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"campaign_slug") : depth0), depth0))
    + "\" data-campaign-name=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"campaign_name") : depth0), depth0))
    + "\""
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"share_action") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":94},"end":{"line":1,"column":191}}})) != null ? stack1 : "")
    + ">Share</span>\n";
},"useData":true}));

Handlebars.registerPartial("share-cat", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"n-share\" data-category=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"category_slug") : depth0), depth0))
    + "\">Share</div>\n";
},"useData":true}));

Handlebars.registerPartial("reportback", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\"large-image\"><img src=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"reportback_image") : depth0), depth0))
    + "\"></figure>";
},"3":function(container,depth0,helpers,partials,data) {
    return "<br>";
},"5":function(container,depth0,helpers,partials,data) {
    var alias1=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\""
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"link") : depth0), depth0))
    + "\" target=\"_external\" class=\"more-link\">"
    + alias1(lookupProperty(helpers,"prettylink").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"link") : depth0),{"name":"prettylink","hash":{},"data":data,"loc":{"start":{"line":10,"column":128},"end":{"line":10,"column":147}}}))
    + "</a>";
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div data-expandable=\"r-"
    + container.escapeExpression(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" class=\"expandable closed base\">\n"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"reportback_content") : depth0), depth0)) != null ? stack1 : "")
    + "\n</div>\n";
},"9":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"#\" class=\"more-button-after hide-when-expanded\" data-expand=\"r-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\">Read&nbsp;More</a> <button class=\"less-button hide-when-closed hide-expander\" data-expand=\"r-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\">Read&nbsp;Less</button>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"notification-content\">\n<header class=\"reportback-header\">\n<a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"reportback_campaign_slug") : depth0), depth0))
    + "\"><figure class=\"campaign-image\"><img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"campaign_img") : depth0), depth0))
    + "/@zx_175@zy_175\" onerror=\"this.onerror=null;this.src='/img/twitter-egg.png';\"></figure></a>\n"
    + alias2(lookupProperty(helpers,"mdate").call(alias3,(depth0 != null ? lookupProperty(depth0,"date") : depth0),{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":4,"column":14}}}))
    + "\n<h3 class=\"title\">"
    + ((stack1 = lookupProperty(helpers,"capCase").call(alias3,(depth0 != null ? lookupProperty(depth0,"campaign_title") : depth0),{"name":"capCase","hash":{},"data":data,"loc":{"start":{"line":5,"column":18},"end":{"line":5,"column":46}}})) != null ? stack1 : "")
    + "</h3>\n</header>\n</div>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"reportback_image") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":8,"column":112}}})) != null ? stack1 : "")
    + "\n<div class=\"notification-content\">\n<p><strong>"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0)) != null ? stack1 : "")
    + "</strong> "
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"reportback_description") : depth0), depth0)) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"link") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":10,"column":60},"end":{"line":10,"column":158}}})) != null ? stack1 : "")
    + "</p>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"reportback_content") : depth0),{"name":"if","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":11,"column":0},"end":{"line":15,"column":7}}})) != null ? stack1 : "")
    + "<p>"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"reportback_content") : depth0),{"name":"if","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":3},"end":{"line":16,"column":244}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"share-camp"),depth0,{"name":"share-camp","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</p>\n</div>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("single-not-template", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var alias1=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"feed-notification type-"
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"notice_type") : depth0), depth0))
    + "\">"
    + alias1(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"notice_type") : depth0),{"name":"$","hash":{},"data":data,"loc":{"start":{"line":1,"column":52},"end":{"line":1,"column":69}}}))
    + "</div>\n";
},"useData":true}));

Handlebars.registerPartial("pagination", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "<button class=\"prev\"></button>";
},"3":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"pagenum") : depth0),">",6,{"name":"compare","hash":{},"fn":container.program(4, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":36,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"pagenum") : depth0),"<=",6,{"name":"compare","hash":{},"fn":container.program(22, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":37,"column":0},"end":{"line":41,"column":12}}})) != null ? stack1 : "");
},"4":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"pages") : depth0),{"name":"each","hash":{},"fn":container.program(5, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":35,"column":9}}})) != null ? stack1 : "");
},"5":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(data && lookupProperty(data,"last")),{"name":"if","hash":{},"fn":container.program(6, data, 0, blockParams, depths),"inverse":container.program(11, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":6,"column":0},"end":{"line":34,"column":7}}})) != null ? stack1 : "");
},"6":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(alias1,(data && lookupProperty(data,"index")),">=2",(depths[1] != null ? lookupProperty(depths[1],"currentpage") : depths[1]),{"name":"compare","hash":{},"fn":container.program(7, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":7,"column":55}}})) != null ? stack1 : "")
    + "\n<button class=\"page"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depths[1] != null ? lookupProperty(depths[1],"currentpage") : depths[1]),"==0",(data && lookupProperty(data,"index")),{"name":"compare","hash":{},"fn":container.program(9, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":19},"end":{"line":8,"column":79}}})) != null ? stack1 : "")
    + "\">"
    + container.escapeExpression(lookupProperty(helpers,"indexshift").call(alias1,(data && lookupProperty(data,"index")),{"name":"indexshift","hash":{},"data":data,"loc":{"start":{"line":8,"column":81},"end":{"line":8,"column":102}}}))
    + "</button>\n";
},"7":function(container,depth0,helpers,partials,data) {
    return "...";
},"9":function(container,depth0,helpers,partials,data) {
    return " current";
},"11":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(data && lookupProperty(data,"first")),{"name":"if","hash":{},"fn":container.program(12, data, 0, blockParams, depths),"inverse":container.program(14, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":10,"column":0},"end":{"line":33,"column":7}}})) != null ? stack1 : "");
},"12":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<button class=\"page"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depths[1] != null ? lookupProperty(depths[1],"currentpage") : depths[1]),"==0",(data && lookupProperty(data,"index")),{"name":"compare","hash":{},"fn":container.program(9, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":11,"column":19},"end":{"line":11,"column":79}}})) != null ? stack1 : "")
    + "\">"
    + container.escapeExpression(lookupProperty(helpers,"indexshift").call(alias1,(data && lookupProperty(data,"index")),{"name":"indexshift","hash":{},"data":data,"loc":{"start":{"line":11,"column":81},"end":{"line":11,"column":102}}}))
    + "</button>\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(data && lookupProperty(data,"index")),"<=-2",(depths[1] != null ? lookupProperty(depths[1],"currentpage") : depths[1]),{"name":"compare","hash":{},"fn":container.program(7, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":12,"column":0},"end":{"line":12,"column":56}}})) != null ? stack1 : "")
    + "\n";
},"14":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depths[1] != null ? lookupProperty(depths[1],"currentpage") : depths[1]),"==2",(data && lookupProperty(data,"index")),{"name":"compare","hash":{},"fn":container.program(15, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":0},"end":{"line":18,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depths[1] != null ? lookupProperty(depths[1],"currentpage") : depths[1]),"==1",(data && lookupProperty(data,"index")),{"name":"compare","hash":{},"fn":container.program(16, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":19,"column":0},"end":{"line":21,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depths[1] != null ? lookupProperty(depths[1],"currentpage") : depths[1]),"==0",(data && lookupProperty(data,"index")),{"name":"compare","hash":{},"fn":container.program(18, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":22,"column":0},"end":{"line":24,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depths[1] != null ? lookupProperty(depths[1],"currentpage") : depths[1]),"==-1",(data && lookupProperty(data,"index")),{"name":"compare","hash":{},"fn":container.program(16, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":25,"column":0},"end":{"line":27,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depths[1] != null ? lookupProperty(depths[1],"currentpage") : depths[1]),"==-2",(data && lookupProperty(data,"index")),{"name":"compare","hash":{},"fn":container.program(20, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":28,"column":0},"end":{"line":32,"column":12}}})) != null ? stack1 : "");
},"15":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depths[1] != null ? lookupProperty(depths[1],"currentpage") : depths[1]),"==-1",(depth0 != null ? lookupProperty(depth0,"pagenum") : depth0),{"name":"compare","hash":{},"fn":container.program(16, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":15,"column":0},"end":{"line":17,"column":12}}})) != null ? stack1 : "");
},"16":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<button class=\"page\">"
    + container.escapeExpression(lookupProperty(helpers,"indexshift").call(depth0 != null ? depth0 : (container.nullContext || {}),(data && lookupProperty(data,"index")),{"name":"indexshift","hash":{},"data":data,"loc":{"start":{"line":16,"column":21},"end":{"line":16,"column":42}}}))
    + "</button>\n";
},"18":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<button class=\"page"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depths[1] != null ? lookupProperty(depths[1],"currentpage") : depths[1]),"==0",(data && lookupProperty(data,"index")),{"name":"compare","hash":{},"fn":container.program(9, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":23,"column":19},"end":{"line":23,"column":79}}})) != null ? stack1 : "")
    + "\">"
    + container.escapeExpression(lookupProperty(helpers,"indexshift").call(alias1,(data && lookupProperty(data,"index")),{"name":"indexshift","hash":{},"data":data,"loc":{"start":{"line":23,"column":81},"end":{"line":23,"column":102}}}))
    + "</button>\n";
},"20":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depths[1] != null ? lookupProperty(depths[1],"currentpage") : depths[1]),"===",0,{"name":"compare","hash":{},"fn":container.program(16, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":29,"column":0},"end":{"line":31,"column":12}}})) != null ? stack1 : "");
},"22":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"pages") : depth0),{"name":"each","hash":{},"fn":container.program(23, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":38,"column":0},"end":{"line":40,"column":9}}})) != null ? stack1 : "");
},"23":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<button class=\"page"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depths[1] != null ? lookupProperty(depths[1],"currentpage") : depths[1]),"===",(data && lookupProperty(data,"index")),{"name":"compare","hash":{},"fn":container.program(9, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":39,"column":19},"end":{"line":39,"column":79}}})) != null ? stack1 : "")
    + "\">"
    + container.escapeExpression(lookupProperty(helpers,"indexshift").call(alias1,(data && lookupProperty(data,"index")),{"name":"indexshift","hash":{},"data":data,"loc":{"start":{"line":39,"column":81},"end":{"line":39,"column":102}}}))
    + "</button>\n";
},"25":function(container,depth0,helpers,partials,data) {
    return "<button class=\"next\"></button>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"pagination\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"currentpage") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":2,"column":56}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"pagenum") : depth0),">",1,{"name":"compare","hash":{},"fn":container.program(3, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":42,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"currentpage") : depth0),"!=-1",(depth0 != null ? lookupProperty(depth0,"pagenum") : depth0),{"name":"compare","hash":{},"fn":container.program(25, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":43,"column":0},"end":{"line":43,"column":81}}})) != null ? stack1 : "")
    + "\n</div>\n";
},"useData":true,"useDepths":true}));

Handlebars.registerPartial("progress-meter", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"progress-meter\">\n<span class=\"progress-bar\" style=\"width:"
    + container.escapeExpression(lookupProperty(helpers,"progress").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"current_amount") : depth0),(depth0 != null ? lookupProperty(depth0,"goal_amount") : depth0),{"name":"progress","hash":{},"data":data,"loc":{"start":{"line":2,"column":40},"end":{"line":2,"column":79}}}))
    + "%\"></span>\n</div>\n";
},"useData":true}));

Handlebars.registerPartial("rando-friend", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<img src=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"avatar") : depth0), depth0))
    + "\">";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure id=\"shuffler_"
    + container.escapeExpression(container.lambda((data && lookupProperty(data,"index")), depth0))
    + "\" class=\"shuffler\">"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"avatar") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":50},"end":{"line":1,"column":93}}})) != null ? stack1 : "")
    + "</figure>\n";
},"useData":true}));

Handlebars.registerPartial("recommended-campaigns-small", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"rec_action_type") : depth0), depth0))
    + "/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"rec_action_slug") : depth0), depth0))
    + "\" class=\"campaign-top-action icon\"><i class=\"action-"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","Contact Officials - All info required",{"name":"compare","hash":{},"fn":container.program(2, data, 0),"inverse":container.program(4, data, 0),"data":data,"loc":{"start":{"line":2,"column":268},"end":{"line":2,"column":465}}})) != null ? stack1 : "")
    + "\"></i> "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","Contact Officials - All info required",{"name":"compare","hash":{},"fn":container.program(7, data, 0),"inverse":container.program(9, data, 0),"data":data,"loc":{"start":{"line":2,"column":472},"end":{"line":2,"column":600}}})) != null ? stack1 : "")
    + "</a>";
},"2":function(container,depth0,helpers,partials,data) {
    return "petition";
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"rec_action_type") : depth0),"===","message",{"name":"compare","hash":{},"fn":container.program(2, data, 0),"inverse":container.program(5, data, 0),"data":data,"loc":{"start":{"line":2,"column":362},"end":{"line":2,"column":453}}})) != null ? stack1 : "");
},"5":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"rec_action_type") : depth0), depth0));
},"7":function(container,depth0,helpers,partials,data) {
    return "Take Action";
},"9":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"rec_action_verb") : depth0), depth0));
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"swiper_class") : depth0), depth0))
    + "recommended-campaign\">\n<span class=\"rec-campaign-title\"><a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" class=\"campaign-name\">"
    + alias2(lookupProperty(helpers,"titleize").call(alias3,lookupProperty(helpers,"capCase").call(alias3,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"capCase","hash":{},"data":data,"loc":{"start":{"line":2,"column":96},"end":{"line":2,"column":111}}}),{"name":"titleize","hash":{},"data":data,"loc":{"start":{"line":2,"column":85},"end":{"line":2,"column":113}}}))
    + "</a></span> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"rec_action_type") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":125},"end":{"line":2,"column":611}}})) != null ? stack1 : "")
    + "\n</figure>\n";
},"useData":true}));

Handlebars.registerPartial("recommended-campaigns", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"rec_action_type") : depth0), depth0))
    + "/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"rec_action_slug") : depth0), depth0))
    + "\" class=\"button blue-button\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"rec_action_verb") : depth0), depth0))
    + "</a>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<figure class=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"swiper_class") : depth0), depth0))
    + "recommended-campaign\">\n<a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" class=\"campaign-image\">\n<img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"image") : depth0), depth0))
    + "/@zx_140@zy_140\" alt=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0))
    + "\">\n</a>\n<figcaption>\n<a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" class=\"campaign-name\">"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0)) != null ? stack1 : "")
    + "</a>\n<span class=\"supporter-count\"><strong>"
    + alias2(lookupProperty(helpers,"commafy").call(alias3,(depth0 != null ? lookupProperty(depth0,"num_supporters_current") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":7,"column":38},"end":{"line":7,"column":72}}}))
    + "</strong> Supporters</span>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"rec_action_type") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":8,"column":150}}})) != null ? stack1 : "")
    + "\n</figcaption>\n</figure>\n";
},"useData":true}));

Handlebars.registerPartial("r-pswd", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "type=\"password\" pattern=\"^.*(?=.{8,40})(?=.*\\d)(?=.*[a-zA-Z]).*$\" autocomplete=\"off\" class=\"password-field text-field\" maxlength=\"40\" required=\"required\"\n";
},"useData":true}));

Handlebars.registerPartial("req", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "required=\"required\"";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"required_params") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":49}}})) != null ? stack1 : "")
    + "\n";
},"useData":true}));

Handlebars.registerPartial("related-news-item", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"cc-related-item\">\n<div class=\"title\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0))
    + "</div>\n<p>"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"blurb") : depth0), depth0))
    + " <a href=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"url") : depth0), depth0))
    + "\" target=\"_blank\" class=\"read-more-link\">Read&nbsp;More</a></p>\n<div class=\"citation\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"cite") : depth0), depth0))
    + "</div>\n</div>\n";
},"useData":true}));

Handlebars.registerPartial("email-share", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"encodeuri").call(alias1,lookupProperty(helpers,"unescape").call(alias1,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"unescape","hash":{},"data":data,"loc":{"start":{"line":1,"column":45},"end":{"line":1,"column":61}}}),{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":1,"column":33},"end":{"line":1,"column":63}}}));
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return alias2(lookupProperty(helpers,"encodeuri").call(alias1,"Hello, I'd like you to check out this campaign from addup.org: ",{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":1,"column":92},"end":{"line":1,"column":171}}}))
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,lookupProperty(helpers,"source").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"extradata") : depth0)) != null ? lookupProperty(stack1,"outer") : stack1)) != null ? lookupProperty(stack1,"url") : stack1),(depth0 != null ? lookupProperty(depth0,"t_sources") : depth0),{"name":"source","hash":{},"data":data,"loc":{"start":{"line":1,"column":183},"end":{"line":1,"column":221}}}),{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":1,"column":171},"end":{"line":1,"column":223}}}))
    + "%0A%0A"
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,"Please consider helping us out!",{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":1,"column":229},"end":{"line":1,"column":276}}}));
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "mailto:?subject="
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias1,(depth0 != null ? lookupProperty(depth0,"subject") : depth0),{"name":"ifor","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":16},"end":{"line":1,"column":72}}})) != null ? stack1 : "")
    + "&body="
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias1,(depth0 != null ? lookupProperty(depth0,"body") : depth0),{"name":"ifor","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":78},"end":{"line":1,"column":285}}})) != null ? stack1 : "")
    + "\n";
},"useData":true}));

Handlebars.registerPartial("fb-ui-btn", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<button class=\"button action-button fb-share-btn\" data-fbaction-id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" data-url=\""
    + alias2(lookupProperty(helpers,"find").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"url") : depth0),((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"url") : stack1),{"name":"find","hash":{},"data":data,"loc":{"start":{"line":1,"column":86},"end":{"line":1,"column":108}}}))
    + "?promoid=70131000001LjgSAAS&utm_medium=recruit&utm_source=facebook&utm_campaign=addup&tc="
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"share_code") : stack1), depth0))
    + "\">Share</button>\n";
},"useData":true}));

Handlebars.registerPartial("fbsharer", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "https://www.facebook.com/sharer/sharer.php?u="
    + ((stack1 = container.invokePartial(lookupProperty(partials,"share-url"),depth0,{"name":"share-url","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("share-block", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "href=\"#recruit\" class=\"email-share-campaign\" ";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "href=\"mailto:?subject="
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,lookupProperty(helpers,"unescape").call(alias1,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"unescape","hash":{},"data":data,"loc":{"start":{"line":4,"column":148},"end":{"line":4,"column":164}}}),{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":4,"column":136},"end":{"line":4,"column":166}}}))
    + "&body="
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,"Hello, I'd like you to check out this campaign from AddUp.org: ",{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":4,"column":172},"end":{"line":4,"column":251}}}))
    + ((stack1 = container.invokePartial(lookupProperty(partials,"share-url"),depth0,{"name":"share-url","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "%0A%0A"
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,"Please consider helping us out!",{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":4,"column":272},"end":{"line":4,"column":319}}}))
    + "\"";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<ul class=\"share-this\">\n<li class=\"share facebook\"><a href=\"#fb\" class=\"fb-share-btn\" data-url=\""
    + alias2(lookupProperty(helpers,"source").call(alias1,lookupProperty(helpers,"find").call(alias1,(depth0 != null ? lookupProperty(depth0,"sharelink") : depth0),(depth0 != null ? lookupProperty(depth0,"url") : depth0),((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"url") : stack1),{"name":"find","hash":{},"data":data,"loc":{"start":{"line":2,"column":81},"end":{"line":2,"column":111}}}),(depth0 != null ? lookupProperty(depth0,"t_sources") : depth0),{"name":"source","hash":{},"data":data,"loc":{"start":{"line":2,"column":72},"end":{"line":2,"column":123}}}))
    + "\" title=\"Share on Facebook\"></a></li>\n<li class=\"share twitter\"><a href=\"https://twitter.com/intent/tweet?text="
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,"Help me support this campaign from http://addup.org",{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":3,"column":73},"end":{"line":3,"column":140}}}))
    + "&url="
    + ((stack1 = container.invokePartial(lookupProperty(partials,"share-url"),depth0,{"name":"share-url","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "\" title=\"Share on Twitter\"></a></li>\n<li class=\"share email\"><a target=\"_blank\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"camp-share") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":4,"column":43},"end":{"line":4,"column":327}}})) != null ? stack1 : "")
    + " title=\"Share by Email\"></a></li>\n</ul>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("share-modal", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"encodeuri").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"campaignName") : depth0),{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":4,"column":67},"end":{"line":4,"column":93}}}));
},"3":function(container,depth0,helpers,partials,data) {
    var alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return alias2(lookupProperty(helpers,"encodeuri").call(alias1,"Check out campaigns for ",{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":4,"column":101},"end":{"line":4,"column":141}}}))
    + alias2(container.lambda((depth0 != null ? lookupProperty(depth0,"categoryName") : depth0), depth0))
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1," at Addup.org",{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":4,"column":157},"end":{"line":4,"column":186}}}));
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"share-modal-content\">\n<h2 class=\"h2\">Share</h2>\n<hr>\n<a href=\"https://twitter.com/intent/tweet?text="
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"campaignName") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":4,"column":47},"end":{"line":4,"column":193}}})) != null ? stack1 : "")
    + "&url="
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"share_url") : depth0), depth0))
    + "\" title=\"Tweet\" target=\"_blank\" class=\"tw\"></a>\n<a class=\"fb-share-btn fb\" data-url=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"share_url") : depth0), depth0))
    + "\" title=\"Share on Facebook\"></a>\n<hr>\n</div>\n";
},"useData":true}));

Handlebars.registerPartial("share-url", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"sourceencode").call(alias1,lookupProperty(helpers,"find").call(alias1,(depth0 != null ? lookupProperty(depth0,"url") : depth0),((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"url") : stack1),{"name":"find","hash":{},"data":data,"loc":{"start":{"line":1,"column":15},"end":{"line":1,"column":35}}}),(depth0 != null ? lookupProperty(depth0,"t_sources") : depth0),{"name":"sourceencode","hash":{},"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":47}}}))
    + "\n";
},"useData":true}));

Handlebars.registerPartial("twitter-share", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return alias2(lookupProperty(helpers,"encodeuri").call(alias1,"Help me support this campaign from http://addup.org",{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":1,"column":53},"end":{"line":1,"column":120}}}))
    + "&url="
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,lookupProperty(helpers,"source").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"extradata") : depth0)) != null ? lookupProperty(stack1,"outer") : stack1)) != null ? lookupProperty(stack1,"url") : stack1),(depth0 != null ? lookupProperty(depth0,"t_sources") : depth0),{"name":"source","hash":{},"data":data,"loc":{"start":{"line":1,"column":137},"end":{"line":1,"column":175}}}),{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":1,"column":125},"end":{"line":1,"column":177}}}));
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "https://twitter.com/intent/tweet?text="
    + ((stack1 = lookupProperty(helpers,"ifor").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"tweet") : depth0),{"name":"ifor","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":38},"end":{"line":1,"column":186}}})) != null ? stack1 : "")
    + "\n";
},"useData":true}));

Handlebars.registerPartial("signup-disclaimer", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<i class=\"signup-disclaimer\">Take action and get email updates on the issues that matter to you.</i>\n";
},"useData":true}));

Handlebars.registerPartial("sso-button", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<button href=\"/saml/login\" class=\"clickthrough popup saml button\">Log In</button>\n";
},"useData":true}));

Handlebars.registerPartial("standalone-recruit", Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div data-template=\"action-recruit\" data-actionmodel=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"pageactionmodel") : depth0), depth0))
    + "\" data-series=\""
    + alias2(lookupProperty(helpers,"indexshift").call(depth0 != null ? depth0 : (container.nullContext || {}),(data && lookupProperty(data,"index")),{"name":"indexshift","hash":{},"data":data,"loc":{"start":{"line":1,"column":88},"end":{"line":1,"column":109}}}))
    + "\" data-last=\""
    + alias2(alias1((data && lookupProperty(data,"last")), depth0))
    + "\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"type") : depth0), depth0))
    + "/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" data-source=\"action:"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"pageactionmodel") : depth0), depth0))
    + "\" data-mixins=\"recruit\" class=\"icon-section action-template actions\"></div>\n";
},"useData":true}));

Handlebars.registerPartial("stat-area", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"impact-stats"),depth0,{"name":"impact-stats","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<footer class=\"stat-row\" role=\"banner\">\n<div></div>\n<div class=\"grid-row\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,lookupProperty(helpers,"limit").call(alias1,(depth0 != null ? lookupProperty(depth0,"stats") : depth0),5,{"name":"limit","hash":{},"data":data,"loc":{"start":{"line":4,"column":8},"end":{"line":4,"column":23}}}),{"name":"each","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":6,"column":9}}})) != null ? stack1 : "")
    + "</div>\n</footer>\n";
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("rec-campaigns-slider", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"campaigns-happening-now gray-background",{"name":"gridsection","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":5,"column":16}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"marquee-header",{"name":"$","hash":{"title":"We picked these for you"},"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":54}}}))
    + "\n<div data-template=\"swiper-slider\" data-mixins=\"swiper\" data-listen=\"user:recs\" data-limit=\"6\" data-mobile-resize=\"true\" data-source=\"recommended-campaigns-list\" data-watch=\"false\" class=\"row slider campaign-slider\"></div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"actions") : depth0),">",0,{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":6,"column":12}}})) != null ? stack1 : "");
},"useData":true}));

Handlebars.registerPartial("top-action-section", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"single-action-section",{"name":"gridsection","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":10,"column":16}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return alias2(lookupProperty(helpers,"$").call(alias1,"marquee-header",{"name":"$","hash":{"title":(depth0 != null ? lookupProperty(depth0,"sa_title") : depth0)},"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":37}}}))
    + "\n<div class=\"actions actions-slider\">\n<div id=\"single-action\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"actions_left") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":6,"column":0},"end":{"line":6,"column":55}}})) != null ? stack1 : "")
    + "\n"
    + alias2(lookupProperty(helpers,"$").call(alias1,"recommended-action",{"name":"$","hash":{},"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":7,"column":26}}}))
    + "\n</div>\n</div>\n";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"try-another-action-btn"),depth0,{"name":"try-another-action-btn","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"actions") : depth0),">",0,{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":11,"column":12}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true}));

Handlebars.registerPartial("target-share-item", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "selected";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "https://graph.facebook.com/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"fbid") : depth0), depth0))
    + "/picture?width=300&height=300&access_token="
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"t_token") : depth0)) != null ? lookupProperty(stack1,"authResponse") : stack1)) != null ? lookupProperty(stack1,"accessToken") : stack1), depth0));
},"5":function(container,depth0,helpers,partials,data) {
    return "checked";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li data-proximity=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"score") : depth0), depth0))
    + "\" data-fbid=\""
    + alias2(lookupProperty(helpers,"find").call(alias3,(depth0 != null ? lookupProperty(depth0,"fbid") : depth0),(depth0 != null ? lookupProperty(depth0,"id") : depth0),{"name":"find","hash":{},"data":data,"loc":{"start":{"line":1,"column":42},"end":{"line":1,"column":58}}}))
    + "\" data-fbname=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "\" class=\"friend-item "
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"selected") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":102},"end":{"line":1,"column":133}}})) != null ? stack1 : "")
    + " "
    + alias2(lookupProperty(helpers,"switch").call(alias3,(data && lookupProperty(data,"index")),{"name":"switch","hash":{},"data":data,"loc":{"start":{"line":1,"column":134},"end":{"line":1,"column":151}}}))
    + " item-"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "\">\n<label for=\"f"
    + alias2(lookupProperty(helpers,"find").call(alias3,(depth0 != null ? lookupProperty(depth0,"fbid") : depth0),(depth0 != null ? lookupProperty(depth0,"id") : depth0),{"name":"find","hash":{},"data":data,"loc":{"start":{"line":2,"column":13},"end":{"line":2,"column":29}}}))
    + "_"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "\">\n<figure>\n<div class=\"friend-photo\"><img src=\""
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias3,(depth0 != null ? lookupProperty(depth0,"avatar") : depth0),{"name":"ifor","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":36},"end":{"line":4,"column":175}}})) != null ? stack1 : "")
    + "\"  alt=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "\" title=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "\" draggable=\"false\"></div>\n<figcaption>"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "</figcaption>\n</figure>\n<input type=\"checkbox\" id=\"f"
    + alias2(lookupProperty(helpers,"find").call(alias3,(depth0 != null ? lookupProperty(depth0,"fbid") : depth0),(depth0 != null ? lookupProperty(depth0,"id") : depth0),{"name":"find","hash":{},"data":data,"loc":{"start":{"line":7,"column":28},"end":{"line":7,"column":44}}}))
    + "_"
    + alias2(alias1((data && lookupProperty(data,"index")), depth0))
    + "\" value=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"fbid") : depth0), depth0))
    + "\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"selected") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":74},"end":{"line":7,"column":104}}})) != null ? stack1 : "")
    + ">\n</label>\n</li>\n";
},"useData":true}));

Handlebars.registerPartial("ts-friends-list", Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"target-share-item"),depth0,{"name":"target-share-item","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"ts-recommendation-section\">\n<ol class=\"bsdts-friend-list friend-list-results row\" data-session=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"session") : depth0), depth0))
    + "\" data-model=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"model") : depth0), depth0))
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias3,lookupProperty(helpers,"limit").call(alias3,(depth0 != null ? lookupProperty(depth0,"friends") : depth0),36,{"name":"limit","hash":{},"data":data,"loc":{"start":{"line":3,"column":8},"end":{"line":3,"column":26}}}),{"name":"each","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":5,"column":9}}})) != null ? stack1 : "")
    + "</ol>\n</div>\n";
},"usePartial":true,"useData":true}));

this["SC"]["404"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"marquee-header",{"name":"$","hash":{"title":"404 Error"},"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":40}}}))
    + "\n<div class=\"row\">\n<h1 class=\"h0\">Ah, darn!</h1>\n<p>You've tried visiting a campaign that is no longer active, or a page that doesn't exist. But don't worry – there are plenty of other actions you can take to protect our planet and fight for environmental justice, like these:</p>\n<br><br>\n<iframe src=\"https://vault.sierraclub.org/addup?featured=1&branded=false\" width=\"100%\" height=\"350\" border=\"0\" frameborder=\"0\" scrolling=\"no\" id=\"iframe\"></iframe>\n<br><br>\n</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<br><br>\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"gridsection","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":11,"column":16}}})) != null ? stack1 : "");
},"useData":true});

this["SC"]["account-settings"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"marquee-header",{"name":"$","hash":{"title":"Account Settings"},"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":2,"column":47}}}))
    + "\n";
},"3":function(container,depth0,helpers,partials,data) {
    return "<div data-template=\"account-personal-info\" data-watch=\"false\" data-mixins=\"chosen\" class=\"row inset-content\"></div>\n";
},"5":function(container,depth0,helpers,partials,data) {
    return "<div id=\"email-prefs\" data-template=\"account-email-prefs\" data-watch=\"false\" class=\"row inset-content\"></div>\n";
},"7":function(container,depth0,helpers,partials,data) {
    return "<div data-template=\"account-privacy\" data-watch=\"false\" class=\"row inset-content\"></div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"account-section settings-heading",{"name":"gridsection","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":3,"column":16}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"account-section personal-info",{"name":"gridsection","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":7,"column":16}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"account-section account-email account-radio-group",{"name":"gridsection","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":0},"end":{"line":11,"column":16}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"account-section account-privacy account-radio-group",{"name":"gridsection","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":13,"column":0},"end":{"line":15,"column":16}}})) != null ? stack1 : "");
},"useData":true});

this["SC"]["campaign-detail"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"no_campaign_recs") : depth0),{"name":"unless","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":35},"end":{"line":1,"column":262}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    return "<div data-template=\"recommended-campaigns-bar\" data-mixins=\"swiper\" data-mobile-resize=\"true\" data-listen=\"user:recs\" data-source=\"recommended-actions-list\" data-removeonfail=\"true\"></div>";
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"full-campaign",{"name":"gridsection","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":14,"column":16}}})) != null ? stack1 : "")
    + "\n<div id=\"take-action\" class=\"actions banded-sections\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"action-tree"),depth0,{"name":"action-tree","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"funding_type_id") : depth0),"===","3",{"name":"compare","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":20,"column":0},"end":{"line":20,"column":309}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"funding_type_id") : depth0),"===","4",{"name":"compare","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":21,"column":0},"end":{"line":21,"column":290}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"funding_type_id") : depth0),"===","5",{"name":"compare","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":22,"column":0},"end":{"line":22,"column":347}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"funding_type_id") : depth0),"===","6",{"name":"compare","hash":{},"fn":container.program(18, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":23,"column":0},"end":{"line":23,"column":304}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"funding_type_id") : depth0),"===","7",{"name":"compare","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":24,"column":0},"end":{"line":24,"column":139}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"funding_type_id") : depth0),"===","8",{"name":"compare","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":25,"column":0},"end":{"line":25,"column":139}}})) != null ? stack1 : "")
    + "\n";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div data-template=\"full-campaign\" data-source=\"campaign\" data-watch=\"false\" data-mobile-resize=\"true\" class=\"row\"></div>\n<div data-template=\"campaign-actions-bar\" data-source=\"campaign\" data-listen=\"user:historyactions\" class=\"row campaign-action-bar\" data-mobile-resize=\"true\"></div>\n\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"reportbacks") : depth0)) != null ? lookupProperty(stack1,"0") : stack1),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":12,"column":7}}})) != null ? stack1 : "")
    + "\n";
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"reportbacks") : depth0)) != null ? lookupProperty(stack1,"0") : stack1),{"name":"with","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":0},"end":{"line":11,"column":9}}})) != null ? stack1 : "");
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"latest-update"),depth0,{"name":"latest-update","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"9":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"gridsection","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":20,"column":38},"end":{"line":20,"column":297}}})) != null ? stack1 : "");
},"10":function(container,depth0,helpers,partials,data) {
    return "<div class=\"pac-disclaimer\">Paid for by the Sierra Club Political Committee (<a href=\"http://www.sierraclub.org\" target=\"_blank\">http://www.sierraclub.org</a>) and not authorized by any candidate or candidate's committee.</div>";
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"gridsection","hash":{},"fn":container.program(13, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":21,"column":38},"end":{"line":21,"column":278}}})) != null ? stack1 : "");
},"13":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"pac-disclaimer\">Paid for by the Sierra Club Political Committee (<a href=\"http://www.sierraclub.org\" target=\"_blank\">http://www.sierraclub.org</a>) and authorized by "
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"candidate_committee") : depth0), depth0))
    + ".</div>";
},"15":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"gridsection","hash":{},"fn":container.program(16, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":22,"column":38},"end":{"line":22,"column":335}}})) != null ? stack1 : "");
},"16":function(container,depth0,helpers,partials,data) {
    return "<div class=\"pac-disclaimer\">Paid for by the Sierra Club Voter Education Fund, which seeks to raise key environmental issues in the discussions around elections, and encourage the public to find out more about candidates' positions on key environmental issues.</div>";
},"18":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"gridsection","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":23,"column":38},"end":{"line":23,"column":292}}})) != null ? stack1 : "");
},"19":function(container,depth0,helpers,partials,data) {
    return "<div class=\"pac-disclaimer\">Paid for by Sierra Club Independent Action (<a href=\"http://www.sierraclub.org\" target=\"_blank\">http://www.sierraclub.org</a>) and not authorized by any candidate or candidate’s committee.</div>";
},"21":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"gridsection","hash":{},"fn":container.program(22, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":24,"column":38},"end":{"line":24,"column":127}}})) != null ? stack1 : "");
},"22":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"pac-disclaimer\">"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"candidate_committee") : depth0), depth0))
    + "</div>";
},"24":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"updates",{"name":"gridsection","hash":{},"fn":container.program(25, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":27,"column":0},"end":{"line":27,"column":42}}})) != null ? stack1 : "")
    + "\n";
},"25":function(container,depth0,helpers,partials,data) {
    return "";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"is_official") : depth0),"!=","pac",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":274}}})) != null ? stack1 : "")
    + "\n\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"updatepage") : depth0),{"name":"unless","hash":{},"fn":container.program(4, data, 0),"inverse":container.program(24, data, 0),"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":28,"column":11}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true});

this["SC"]["campaigns"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"center",{"name":"gridsection","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":6,"column":16}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"featured-campaigns-section dotted-background icon-section",{"name":"gridsection","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":10,"column":16}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    return "<header class=\"your-campaigns-header\">\n<h1>Campaigns</h1>\n</header>\n";
},"4":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"marquee-header",{"name":"$","hash":{"title":"Sierra Club Campaigns","mleft":true},"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":8,"column":63}}}))
    + "\n<div data-template=\"swiper-slider\" data-mixins=\"swiper\" data-mobile-resize=\"true\" data-source=\"featured-campaigns\" data-watch=\"false\" class=\"row slider campaign-slider\"></div>\n";
},"6":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_category") : depth0),{"name":"if","hash":{},"fn":container.program(7, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":0},"end":{"line":18,"column":7}}})) != null ? stack1 : "")
    + "\n<div class=\"tabs row"
    + ((stack1 = lookupProperty(helpers,"none").call(alias1,lookupProperty(helpers,"not").call(alias1,(depth0 != null ? lookupProperty(depth0,"loggedin") : depth0),{"name":"not","hash":{},"data":data,"loc":{"start":{"line":20,"column":28},"end":{"line":20,"column":42}}}),(depth0 != null ? lookupProperty(depth0,"start_category") : depth0),{"name":"none","hash":{},"fn":container.program(9, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":20,"column":20},"end":{"line":20,"column":77}}})) != null ? stack1 : "")
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"none").call(alias1,lookupProperty(helpers,"not").call(alias1,(depth0 != null ? lookupProperty(depth0,"loggedin") : depth0),{"name":"not","hash":{},"data":data,"loc":{"start":{"line":21,"column":8},"end":{"line":21,"column":22}}}),(depth0 != null ? lookupProperty(depth0,"start_category") : depth0),{"name":"none","hash":{},"fn":container.program(11, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":21,"column":0},"end":{"line":26,"column":9}}})) != null ? stack1 : "")
    + "</div>\n\n<div id=\"all-campaigns\" data-template=\"campaigns-search\" data-pageid=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"pageid") : depth0), depth0))
    + "\" data-start_category=\""
    + alias3(alias2(((stack1 = (depth0 != null ? lookupProperty(depth0,"start_category") : depth0)) != null ? lookupProperty(stack1,"id") : stack1), depth0))
    + "\" data-perpage=\"12\" data-source=\"campaigns-nearby\" data-mixins=\"gallery\" data-watch=\"false\" class=\"tab row campaigns-grid "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"urlhash") : depth0),"===","your-campaigns",{"name":"compare","hash":{},"fn":container.program(14, data, 0, blockParams, depths),"inverse":container.program(16, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":29,"column":246},"end":{"line":29,"column":363}}})) != null ? stack1 : "")
    + "></div>\n"
    + ((stack1 = lookupProperty(helpers,"none").call(alias1,lookupProperty(helpers,"not").call(alias1,(depth0 != null ? lookupProperty(depth0,"loggedin") : depth0),{"name":"not","hash":{},"data":data,"loc":{"start":{"line":30,"column":8},"end":{"line":30,"column":22}}}),(depth0 != null ? lookupProperty(depth0,"start_category") : depth0),{"name":"none","hash":{},"fn":container.program(18, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":30,"column":0},"end":{"line":34,"column":9}}})) != null ? stack1 : "");
},"7":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"start-category\">\n"
    + container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"marquee-header",{"name":"$","hash":{"title":((stack1 = (depths[1] != null ? lookupProperty(depths[1],"start_category") : depths[1])) != null ? lookupProperty(stack1,"title") : stack1),"mleft":true},"data":data,"loc":{"start":{"line":16,"column":0},"end":{"line":16,"column":63}}}))
    + "\n</div>\n";
},"9":function(container,depth0,helpers,partials,data) {
    return " has-tabs";
},"11":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<ol>\n<li"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"urlhash") : depth0),"!=","your-campaigns",{"name":"compare","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":23,"column":3},"end":{"line":23,"column":74}}})) != null ? stack1 : "")
    + " data-route=\"campaigns\">All</li>\n<li data-tab=\"your-campaigns\""
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"urlhash") : depth0),"===","your-campaigns",{"name":"compare","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":24,"column":29},"end":{"line":24,"column":101}}})) != null ? stack1 : "")
    + " data-route=\"campaigns/your-campaigns\">Campaigns You Support</li>\n</ol>\n";
},"12":function(container,depth0,helpers,partials,data) {
    return " class=\"selected\"";
},"14":function(container,depth0,helpers,partials,data) {
    return "hidden\"";
},"16":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\" data-pageid=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"pageid") : depth0), depth0))
    + "\" data-hash=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"hash") : depth0), depth0))
    + "\"";
},"18":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div id=\"your-campaigns\" class=\"section your-campaigns-section tab "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"urlhash") : depth0),"!=","your-campaigns",{"name":"compare","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":31,"column":67},"end":{"line":31,"column":127}}})) != null ? stack1 : "")
    + "\">\n<div data-template=\"your-campaigns-list\" data-source=\"your-campaigns\" data-perpage=\"9\" data-mixins=\"gallery\" data-watch=\"false\" "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"urlhash") : depth0),"===","your-campaigns",{"name":"compare","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":32,"column":128},"end":{"line":32,"column":229}}})) != null ? stack1 : "")
    + " class=\"row campaigns-grid\"></div>\n</div>\n";
},"19":function(container,depth0,helpers,partials,data) {
    return "hidden";
},"21":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " data-pageid=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"pageid") : depth0), depth0))
    + "\" data-hash=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"hash") : depth0), depth0))
    + "\"";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_category") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":11,"column":11}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"search-section",{"name":"gridsection","hash":{},"fn":container.program(6, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":13,"column":0},"end":{"line":35,"column":16}}})) != null ? stack1 : "");
},"useData":true,"useDepths":true});

this["SC"]["event-detail"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div data-template=\"event-fullpage\" data-mixins=\"event\" data-source=\"action:"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" data-actionmodel=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" data-watch=\"false\" class=\"row\"></div>\n";
},"useData":true});

this["SC"]["events"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "<div id=\"events-header\" data-template=\"events-header\" data-source=\"events\" data-watch=\"false\" class=\"events-header-section\"></div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"center",{"name":"gridsection","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":3,"column":16}}})) != null ? stack1 : "")
    + "<section class=\"events-section dotted-background icon-section\" id=\"events-react-container\">\n\n</section>\n";
},"useData":true});

this["SC"]["faq"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"base faq-container\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"faqcontent") : depth0), depth0)) != null ? stack1 : "")
    + "</div>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"gridsection","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":86}}})) != null ? stack1 : "")
    + "\n";
},"useData":true});

this["SC"]["issues-you-care-about"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "<header>\n<h2>There&rsquo;s a lot to do, but first&hellip;</h2>\n<p class=\"subhead\">Tell us which issues you care about most.</p>\n</header>\n<div data-template=\"category-chooser\" data-source=\"my-cats\" data-watch=\"false\"></div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"issues-you-care-about",{"name":"gridsection","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":7,"column":16}}})) != null ? stack1 : "");
},"useData":true});

this["SC"]["lte"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "<div id=\"lte-react-container\"></div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"gridsection","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":3,"column":16}}})) != null ? stack1 : "");
},"useData":true});

this["SC"]["opt-out"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(alias1,"marquee-header",{"name":"$","hash":{"title":"Email Preferences"},"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":2,"column":48}}}))
    + "\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"anon") : depth0),{"name":"unless","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":87}}})) != null ? stack1 : "")
    + "\n<form data-formaction=\"unsubscribe\" method=\"POST\" data-method=\"POST\" action=\"/unsubscribe\" class=\"check-labels page-form\" "
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"anon") : depth0),{"name":"unless","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":122},"end":{"line":4,"column":169}}})) != null ? stack1 : "")
    + ">\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"anon") : depth0),{"name":"unless","hash":{},"fn":container.program(6, data, 0),"inverse":container.program(8, data, 0),"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":5,"column":434}}})) != null ? stack1 : "")
    + "\n<br><div class=\"h3\">Email Preferences</div>\n<ul class=\"row background-pale section-contents\">\n<li class=\"email\">\n<label for=\"-email-\">Email</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"email_field"),depth0,{"name":"email_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"email_pref_fields"),depth0,{"name":"email_pref_fields","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"update_button"),depth0,{"name":"update_button","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</ul>\n</form>\n<div class=\"unsub-success\">\n<div class=\"bubble-pale\">\n<h1 class=\"h2\">Email Preferences Updated!</h1>\n</div>\n<br>\n<a href=\""
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"anon") : depth0),{"name":"unless","hash":{},"fn":container.program(10, data, 0),"inverse":container.program(12, data, 0),"data":data,"loc":{"start":{"line":23,"column":9},"end":{"line":23,"column":62}}})) != null ? stack1 : "")
    + "\" class=\"button block h4\">"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"anon") : depth0),{"name":"unless","hash":{},"fn":container.program(14, data, 0),"inverse":container.program(16, data, 0),"data":data,"loc":{"start":{"line":23,"column":88},"end":{"line":23,"column":168}}})) != null ? stack1 : "")
    + "</a>\n</div>\n";
},"2":function(container,depth0,helpers,partials,data) {
    return "<h3 class=\"slate-gray-text\">Sierra Club Member Services</h3>";
},"4":function(container,depth0,helpers,partials,data) {
    return "data-internal=\"true\"";
},"6":function(container,depth0,helpers,partials,data) {
    return "<p>Check the boxes for the email the account wants to receive, or unsubscribe them from all email communications.</p>";
},"8":function(container,depth0,helpers,partials,data) {
    return "<p><strong>Before you leave, explore your options.</strong></p><p>We want to keep you up to date with the campaigns you've supported and keep your actions adding up.</p><p>Check the boxes for the email you DO want to receive, or unsubscribe from all of our email communications.</p>";
},"10":function(container,depth0,helpers,partials,data) {
    return "/refresh";
},"12":function(container,depth0,helpers,partials,data) {
    return "/campaigns";
},"14":function(container,depth0,helpers,partials,data) {
    return "Update Another Account";
},"16":function(container,depth0,helpers,partials,data) {
    return "Explore Addup Campaigns";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"account-section account-email settings-heading account-radio-group account-unsubscribe",{"name":"gridsection","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":25,"column":16}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true});

this["SC"]["preview-updates"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"latest-update"),depth0,{"name":"latest-update","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"full-campaign",{"name":"gridsection","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":3,"column":16}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true});

this["SC"]["resist"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<style>.cc-hero{background-image:url("
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"cc_headline_image") : depth0), depth0))
    + "/@mx_2800);}\n@media screen and (max-width: 1000px){.cc-hero{background-image:url("
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"cc_headline_image") : depth0), depth0))
    + "/@mx_1800);}}\n@media screen and (max-width: 767px){.cc-hero{background-image:url("
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"cc_headline_image") : depth0), depth0))
    + "/@mx_900);}}\n@media screen and (max-width: 480px){.cc-hero{background-image:url("
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"cc_headline_image") : depth0), depth0))
    + "/@mx_600);}}</style>\n\n<section class=\"hero-signup welcome-section cc-hero\">\n<div class=\"cc-hero-area\">\n<h1 class=\"hero-text\">"
    + alias2(lookupProperty(helpers,"titleize").call(alias3,(depth0 != null ? lookupProperty(depth0,"cc_headline_h1") : depth0),{"name":"titleize","hash":{},"data":data,"loc":{"start":{"line":9,"column":22},"end":{"line":9,"column":49}}}))
    + "</h1>\n<p class=\"hero-description\">"
    + alias2(lookupProperty(helpers,"titleize").call(alias3,(depth0 != null ? lookupProperty(depth0,"cc_headline_p") : depth0),{"name":"titleize","hash":{},"data":data,"loc":{"start":{"line":10,"column":28},"end":{"line":10,"column":54}}}))
    + "</p>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"cc_headline_photocredit") : depth0),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":11,"column":0},"end":{"line":11,"column":99}}})) != null ? stack1 : "")
    + "\n</div>\n</section>\n\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias3,"cc-feature",{"name":"gridsection","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":15,"column":0},"end":{"line":66,"column":16}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias3,"cc-full-story",{"name":"gridsection","hash":{},"fn":container.program(24, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":68,"column":0},"end":{"line":79,"column":16}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"cc_featured_event") : depth0),{"name":"if","hash":{},"fn":container.program(27, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":81,"column":0},"end":{"line":107,"column":7}}})) != null ? stack1 : "")
    + "\n\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias3,(depth0 != null ? lookupProperty(depth0,"loggedin") : depth0),{"name":"unless","hash":{},"fn":container.program(36, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":110,"column":0},"end":{"line":140,"column":11}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias3,"cc-domore",{"name":"gridsection","hash":{},"fn":container.program(40, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":142,"column":0},"end":{"line":145,"column":16}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"cc_infographic_image") : depth0),{"name":"if","hash":{},"fn":container.program(42, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":147,"column":0},"end":{"line":156,"column":7}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias3,"sc-info-section",{"name":"gridsection","hash":{},"fn":container.program(45, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":158,"column":0},"end":{"line":164,"column":16}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"photo-credit\">"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"cc_headline_photocredit") : depth0), depth0))
    + "</span>";
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"cc_featured_campaign") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":0},"end":{"line":38,"column":7}}})) != null ? stack1 : "")
    + "<div class=\"cc-related-news\">\n<div class=\"heading\">Related News</div>\n<div class=\"cc-related-items "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"cc_related_tweet") : depth0),{"name":"if","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":41,"column":29},"end":{"line":41,"column":67}}})) != null ? stack1 : "")
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"cc_related_tweet") : depth0),{"name":"if","hash":{},"fn":container.program(19, data, 0),"inverse":container.program(22, data, 0),"data":data,"loc":{"start":{"line":42,"column":0},"end":{"line":63,"column":7}}})) != null ? stack1 : "")
    + "</div>\n</div>\n";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"cc-main-feature\">\n"
    + ((stack1 = lookupProperty(helpers,"with").call(alias1,(depth0 != null ? lookupProperty(depth0,"cc_featured_campaign") : depth0),{"name":"with","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":18,"column":0},"end":{"line":23,"column":9}}})) != null ? stack1 : "")
    + "<div class=\"top-action\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"cc_featured_campaign") : depth0)) != null ? lookupProperty(stack1,"top_action") : stack1),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.program(13, data, 0),"data":data,"loc":{"start":{"line":25,"column":0},"end":{"line":33,"column":7}}})) != null ? stack1 : "")
    + "</div>\n</div>\n<figure><img src=\""
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias1,(depth0 != null ? lookupProperty(depth0,"cc_fc_overrideimage") : depth0),{"name":"ifor","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":36,"column":18},"end":{"line":36,"column":86}}})) != null ? stack1 : "")
    + "\"></figure>\n</div>\n";
},"6":function(container,depth0,helpers,partials,data) {
    var alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"cc-feature-content\">\n<div class=\"supporter-count\"><strong>"
    + alias2(lookupProperty(helpers,"commafy").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_supporters_current") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":20,"column":37},"end":{"line":20,"column":71}}}))
    + "</strong> Supporter"
    + alias2(lookupProperty(helpers,"s").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_supporters_current") : depth0),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":20,"column":90},"end":{"line":20,"column":118}}}))
    + "</div>\n<h1 class=\"h2\">"
    + alias2(lookupProperty(helpers,"titleize").call(alias1,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"titleize","hash":{},"data":data,"loc":{"start":{"line":21,"column":15},"end":{"line":21,"column":33}}}))
    + "</h1>\n<p>"
    + alias2(container.lambda((depth0 != null ? lookupProperty(depth0,"goal") : depth0), depth0))
    + "</p>\n";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"historyactions") : depth0),"[]",((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"cc_featured_campaign") : depth0)) != null ? lookupProperty(stack1,"top_action") : stack1)) != null ? lookupProperty(stack1,"featured_action_id") : stack1),{"name":"compare","hash":{},"fn":container.program(9, data, 0),"inverse":container.program(11, data, 0),"data":data,"loc":{"start":{"line":26,"column":0},"end":{"line":30,"column":12}}})) != null ? stack1 : "");
},"9":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"/campaigns/"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"cc_featured_campaign") : depth0)) != null ? lookupProperty(stack1,"slug") : stack1), depth0))
    + "\" class=\"button action-button\">Take Action</a>\n";
},"11":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"/campaigns/"
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"cc_featured_campaign") : depth0)) != null ? lookupProperty(stack1,"slug") : stack1), depth0))
    + "/"
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"cc_featured_campaign") : depth0)) != null ? lookupProperty(stack1,"top_action") : stack1)) != null ? lookupProperty(stack1,"featured_action_type") : stack1), depth0))
    + "/"
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"cc_featured_campaign") : depth0)) != null ? lookupProperty(stack1,"top_action") : stack1)) != null ? lookupProperty(stack1,"featured_action_slug") : stack1), depth0))
    + "\" class=\"button action-button\">"
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"cc_featured_campaign") : depth0)) != null ? lookupProperty(stack1,"top_action") : stack1)) != null ? lookupProperty(stack1,"featured_action_phrase") : stack1), depth0))
    + "</a>\n";
},"13":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"/campaigns/"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" class=\"button action-button\">Take Action</a>\n";
},"15":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"cc_featured_campaign") : depth0)) != null ? lookupProperty(stack1,"image") : stack1), depth0));
},"17":function(container,depth0,helpers,partials,data) {
    return "w-tweet";
},"19":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"item-group\">\n<div class=\"cc-related-item r-tweet\">\n"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"cc_related_tweet") : depth0), depth0)) != null ? stack1 : "")
    + "\n</div>\n"
    + ((stack1 = lookupProperty(helpers,"with").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"cc_related_articles") : depth0)) != null ? lookupProperty(stack1,"0") : stack1),{"name":"with","hash":{},"fn":container.program(20, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":47,"column":0},"end":{"line":49,"column":9}}})) != null ? stack1 : "")
    + "</div>\n<div class=\"item-group group2\">\n"
    + ((stack1 = lookupProperty(helpers,"with").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"cc_related_articles") : depth0)) != null ? lookupProperty(stack1,"1") : stack1),{"name":"with","hash":{},"fn":container.program(20, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":52,"column":0},"end":{"line":54,"column":9}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"with").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"cc_related_articles") : depth0)) != null ? lookupProperty(stack1,"2") : stack1),{"name":"with","hash":{},"fn":container.program(20, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":55,"column":0},"end":{"line":57,"column":9}}})) != null ? stack1 : "")
    + "</div>\n";
},"20":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"related-news-item"),depth0,{"name":"related-news-item","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"22":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"cc_related_articles") : depth0),{"name":"each","hash":{},"fn":container.program(20, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":60,"column":0},"end":{"line":62,"column":9}}})) != null ? stack1 : "");
},"24":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h3 class=\"h1\">More ways to RESIST</h3>\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"full_story_articles") : depth0),{"name":"each","hash":{},"fn":container.program(25, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":70,"column":0},"end":{"line":78,"column":9}}})) != null ? stack1 : "");
},"25":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"url") : depth0), depth0))
    + "\" target=\"_blank\" class=\"cc-full-story-article\">\n<span class=\"right-arrow\"></span>\n<span class=\"cc-fs-content\">\n<span class=\"cc-fs-title\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0))
    + "</span>\n<span class=\"cc-fs-blurb\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"blurb") : depth0), depth0))
    + "</span>\n</span>\n</a>\n";
},"27":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"cc-upcoming",{"name":"gridsection","hash":{},"fn":container.program(28, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":82,"column":0},"end":{"line":106,"column":16}}})) != null ? stack1 : "");
},"28":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h3 class=\"h3\">Upcoming</h3>\n<div>Resist Trump by getting involved in your community. Find <a href=\"/events/\">Events near you</a>.</div>\n"
    + ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"cc_featured_event") : depth0),{"name":"with","hash":{},"fn":container.program(29, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":85,"column":0},"end":{"line":105,"column":9}}})) != null ? stack1 : "");
},"29":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"event-tile\">\n<a href=\"/events/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\"></a>\n<div class=\"event-date-section col-2\">\n\n<div class=\"event-weekday\">"
    + alias2(lookupProperty(helpers,"displayWeekDay").call(alias3,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayWeekDay","hash":{},"data":data,"loc":{"start":{"line":90,"column":27},"end":{"line":90,"column":56}}}))
    + "</div>\n<div class=\"event-monthdate\">"
    + alias2(lookupProperty(helpers,"displayMonth").call(alias3,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayMonth","hash":{},"data":data,"loc":{"start":{"line":91,"column":29},"end":{"line":91,"column":56}}}))
    + " "
    + alias2(lookupProperty(helpers,"displayDay").call(alias3,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayDay","hash":{},"data":data,"loc":{"start":{"line":91,"column":57},"end":{"line":91,"column":82}}}))
    + "</div>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"start_time") : depth0),{"name":"if","hash":{},"fn":container.program(30, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":92,"column":0},"end":{"line":92,"column":154}}})) != null ? stack1 : "")
    + "\n\n</div>\n<div class=\"event-tile-details col-8\">\n<div class=\"event-title\">"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0)) != null ? stack1 : "")
    + "</div>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"location") : depth0),{"name":"if","hash":{},"fn":container.program(34, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":97,"column":0},"end":{"line":99,"column":7}}})) != null ? stack1 : "")
    + "</div>\n<div class=\"event-tile-map col-2\">\n<div class=\"button\">Sign Up</div>\n</div>\n</div>\n";
},"30":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"event-time\">"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"start_time") : depth0), depth0))
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"end_time") : depth0),{"name":"if","hash":{},"fn":container.program(31, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":92,"column":56},"end":{"line":92,"column":141}}})) != null ? stack1 : "")
    + "</div>";
},"31":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"start_time") : depth0),"!=",(depth0 != null ? lookupProperty(depth0,"end_time") : depth0),{"name":"compare","hash":{},"fn":container.program(32, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":92,"column":72},"end":{"line":92,"column":134}}})) != null ? stack1 : "");
},"32":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "-"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"end_time") : depth0), depth0));
},"34":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p class=\"info-location clickable\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"location") : depth0), depth0)) != null ? stack1 : "")
    + "</p>\n";
},"36":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"cc-signup-section form-anchor",{"name":"gridsection","hash":{},"fn":container.program(37, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":111,"column":0},"end":{"line":139,"column":16}}})) != null ? stack1 : "");
},"37":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),false,{"name":"if","hash":{},"fn":container.program(38, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":112,"column":0},"end":{"line":128,"column":7}}})) != null ? stack1 : "")
    + "<div class=\"cc-next\">\n<h4>Now, take the next step and become a member of our community of activists.</h4>\n<div class=\"button-row\"><button data-event=\"registration-modal\" data-skipclose=\"true\" action=\"#issues-you-care-about\" class=\"button member-btn\">Join AddUp</button><span>or</span> "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"connect-to-facebook-button"),depth0,{"name":"connect-to-facebook-button","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n<div>Already a member? <a href=\"/saml/login\" class=\"clickthrough popup saml\">Log with your credentials</a> or click Connect.</div>\n</div>\n<div class=\"cc-thanks\">\n<h4>Thank you for being a part of our community.</h4>\n<blockquote>&rdquo;The battle we have fought, and are still fighting, for the forests is a part of the eternal conflict between right and wrong, and we cannot expect to see the end of it. ...So we must count on watching and striving for these trees, and should always be glad to find anything so surely good and noble to strive for.&ldquo;\n<br><br>&mdash;John Muir</blockquote>\n</div>\n";
},"38":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"cc-signup\">\n<blockquote>&rdquo;It is horrifying that we have to fight our own government to save the environment&ldquo;<br>&mdash;Ansel Adams</blockquote>\n\n<h4>Don't miss a single opportunity to fight with us.</h4>\n<form class=\"row cc-signup\" data-formsuccessclass=\"signedup\" data-formaction=\"moleform\" method=\"POST\" action=\"/register/prospective\">\n<ul>\n<li class=\"email\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"email_label_field"),depth0,{"name":"email_label_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"zipcode\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"zip_label_field"),depth0,{"name":"zip_label_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n</ul>\n<button type=\"submit\" class=\"button submit-button right-arrow\"></button>\n</form>\n</div>\n";
},"40":function(container,depth0,helpers,partials,data) {
    return "<h3 class=\"h1\">Happening Now</h3>\n<div data-template=\"swiper-slider\" data-mixins=\"swiper\" data-mobile-resize=\"true\" data-limit=\"10\" data-source=\"resist-campaigns\" data-watch=\"false\" class=\"row slider resist-slider\"></div>\n";
},"42":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"dotted-background cc-infographic",{"name":"gridsection","hash":{},"fn":container.program(43, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":148,"column":0},"end":{"line":155,"column":16}}})) != null ? stack1 : "");
},"43":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h3 class=\"h1\">Our Progress</h3>\n<figure>\n<img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"cc_infographic_image") : depth0), depth0))
    + "\">\n<figcaption>"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"cc_infographic_citation") : depth0), depth0))
    + "</figcaption>\n</figure>\n\n";
},"45":function(container,depth0,helpers,partials,data) {
    return "<div>\n<img src=\"/img/sc-logo-white.svg\" alt=\"Sierra Club\">\n<p>Founded by legendary conservationist John Muir in 1892, the Sierra Club is the nation’s largest and most influential grassroots environmental organization—with more than two million members and supporters. Our mission is to explore, enjoy, and protect the wild pieces of the earth; to practice and promote the responsible use of the earth’s ecosystems and resources; to educate and enlist humanity to protect and restore the quality of the natural and human environment; and to use all lawful means to carry out these objectives.\n</p>\n</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"tseed") : depth0),{"name":"with","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":165,"column":9}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true});

this["SC"]["share"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<header>\n<h1>Recruit Your Friends</h1>\n</header>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"standalone-recruit"),depth0,{"name":"standalone-recruit","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"share-page\">\n\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"recruit-your-friends recruit-action-section",{"name":"gridsection","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":8,"column":16}}})) != null ? stack1 : "")
    + "\n</div>\n";
},"usePartial":true,"useData":true});

this["SC"]["supporter-home"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"supporter-home-header",{"name":"gridsection","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":11,"column":16}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<header>\n<h2>Hello"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":9},"end":{"line":4,"column":50}}})) != null ? stack1 : "")
    + "!</h2>\n<p class=\"subhead\">Thanks for joining Addup and welcome to your impact page. Here, you'll see <strong>the direct impact you have</strong> as we track your actions and recruits across the site. We'll recommend actions especially for you based on the issues you care about most.</p>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"postsignupcamp") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":6,"column":0},"end":{"line":8,"column":190}}})) != null ? stack1 : "")
    + "\n<p><a href=\"/campaigns\">Explore other campaigns &raquo;</a></p>\n</header>\n";
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ", "
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"first_name") : depth0), depth0));
},"5":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<hr>\n<h4 class=\"h4\">Continue Taking Action On:</h4>\n<div data-template=\"actions-remaining\" data-listen=\"user:historyactions\" data-source=\"campaign:"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"postsignupcamp") : depth0), depth0))
    + "\" data-mixins=\"loader\" class=\"campaign-tile row continue-block\"></div>";
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"supporter-welcome-intro\">\n<strong>Hey "
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias1,(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),{"name":"ifor","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":12},"end":{"line":16,"column":46}}})) != null ? stack1 : "")
    + "!</strong>\n"
    + ((stack1 = lookupProperty(helpers,"all").call(alias1,(depth0 != null ? lookupProperty(depth0,"city") : depth0),(depth0 != null ? lookupProperty(depth0,"state") : depth0),{"name":"all","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":17,"column":0},"end":{"line":17,"column":148}}})) != null ? stack1 : "")
    + "\n</div>\n";
},"8":function(container,depth0,helpers,partials,data) {
    return "There";
},"10":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"https://www.google.com/maps/place/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"city") : depth0), depth0))
    + "+"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"state") : depth0), depth0))
    + "/\"><p class=\"supporter-location\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"city") : depth0), depth0))
    + ", "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"state") : depth0), depth0))
    + "</p></a>";
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\n<div class=\"what-youve-done\">\n"
    + alias2(lookupProperty(helpers,"$").call(alias1,"marquee-header",{"name":"$","hash":{"title":"What you've done so far"},"data":data,"loc":{"start":{"line":24,"column":0},"end":{"line":24,"column":55}}}))
    + "\n\n<div class=\"accomplishments actions\">\n<figure class=\"supporter-stat one\">\n<span class=\"ss-number\">"
    + alias2(lookupProperty(helpers,"commafy").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_actions") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":28,"column":24},"end":{"line":28,"column":47}}}))
    + "</span><figcaption>\n<strong>action"
    + alias2(lookupProperty(helpers,"s").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_actions") : depth0),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":29,"column":14},"end":{"line":29,"column":31}}}))
    + "</strong> taken\n</figcaption>\n</figure>\n<div data-template=\"impact-icons\" data-listen=\"user:category_completion\" data-mixins=\"impact-graph\" data-source=\"impact-stats\" class=\"stat-template impact-graph-section\"></div>\n<p class=\"cta-block\"><a href=\"/campaigns\">Take more action ></a></p>\n</div>\n\n<div class=\"accomplishments events\">\n<figure class=\"supporter-stat one\">\n<span class=\"ss-number\">"
    + alias2(lookupProperty(helpers,"commafy").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_events") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":38,"column":24},"end":{"line":38,"column":46}}}))
    + "</span>\n<figcaption>\n<strong>event</strong> rsvp"
    + alias2(lookupProperty(helpers,"s").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_events") : depth0),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":40,"column":27},"end":{"line":40,"column":43}}}))
    + "\n</figcaption>\n</figure>\n<div class=\"stat-template events-stats\"></div>\n<p class=\"cta-block\"><a href=\"/events\">Explore events ></a></p>\n</div>\n\n<div class=\"accomplishments campaigns-supported mobile-only\">\n<figure class=\"supporter-stat one\">\n<span class=\"ss-number\">"
    + alias2(lookupProperty(helpers,"commafy").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_campaigns") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":49,"column":24},"end":{"line":49,"column":49}}}))
    + "</span>\n<figcaption>\n<strong>campaign"
    + alias2(lookupProperty(helpers,"s").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_campaigns") : depth0),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":51,"column":16},"end":{"line":51,"column":35}}}))
    + "</strong> supported\n</figcaption>\n</figure>\n<div class=\"stat-template row campaigns-your-impact\"></div>\n<p class=\"cta-block\"><a href=\"/campaigns/your-campaigns\">My campaigns ></a></p>\n</div>\n\n<div class=\"accomplishments recruits\">\n<figure class=\"supporter-stat one\">\n<span class=\"ss-number\">\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"recruits_info") : depth0)) != null ? lookupProperty(stack1,"0") : stack1),{"name":"unless","hash":{},"fn":container.program(13, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":61,"column":0},"end":{"line":63,"column":11}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"recruits_info") : depth0)) != null ? lookupProperty(stack1,"0") : stack1),{"name":"if","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":64,"column":0},"end":{"line":66,"column":7}}})) != null ? stack1 : "")
    + "</span>\n<figcaption>\n<strong> Friend"
    + alias2(lookupProperty(helpers,"s").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_recruits") : depth0),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":69,"column":15},"end":{"line":69,"column":33}}}))
    + "</strong> recruited\n</figcaption>\n</figure>\n<div class=\"stat-template recruits-section\">\n<div class=\"recruit-icons-block\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,lookupProperty(helpers,"recruitavatars").call(alias1,(depth0 != null ? lookupProperty(depth0,"recruits_info") : depth0),{"name":"recruitavatars","hash":{},"data":data,"loc":{"start":{"line":74,"column":8},"end":{"line":74,"column":38}}}),{"name":"each","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":74,"column":0},"end":{"line":76,"column":9}}})) != null ? stack1 : "")
    + "</div>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"recruits_info") : depth0)) != null ? lookupProperty(stack1,"0") : stack1),{"name":"if","hash":{},"fn":container.program(19, data, 0),"inverse":container.program(31, data, 0),"data":data,"loc":{"start":{"line":78,"column":0},"end":{"line":101,"column":7}}})) != null ? stack1 : "")
    + "</div>\n<p class=\"cta-block\"><a href=\"/share\">Recruit more ></a></p>\n</div>\n\n</div>\n<div class=\"impact-single\">\n"
    + alias2(lookupProperty(helpers,"$").call(alias1,"marquee-header",{"name":"$","hash":{"title":"our impact"},"data":data,"loc":{"start":{"line":108,"column":0},"end":{"line":108,"column":41}}}))
    + "\n\n<div class=\"our-impact-block\">\n"
    + ((stack1 = lookupProperty(helpers,"with").call(alias1,lookupProperty(helpers,"randomvictory").call(alias1,(depth0 != null ? lookupProperty(depth0,"victory_campaigns") : depth0),{"name":"randomvictory","hash":{},"data":data,"loc":{"start":{"line":111,"column":8},"end":{"line":111,"column":41}}}),{"name":"with","hash":{},"fn":container.program(33, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":111,"column":0},"end":{"line":116,"column":9}}})) != null ? stack1 : "")
    + "</div>\n</div>\n\n";
},"13":function(container,depth0,helpers,partials,data) {
    return "0\n";
},"15":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"commafy").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"num_recruits") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":65,"column":0},"end":{"line":65,"column":24}}}))
    + "\n";
},"17":function(container,depth0,helpers,partials,data) {
    return "<img src=\""
    + container.escapeExpression(container.lambda(depth0, depth0))
    + "\" class=\"recruit-avatar\" alt=\"avatar\" />\n";
},"19":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p class=\"recruit-names\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"recruits_info") : depth0)) != null ? lookupProperty(stack1,"2") : stack1),{"name":"if","hash":{},"fn":container.program(20, data, 0),"inverse":container.program(24, data, 0),"data":data,"loc":{"start":{"line":80,"column":0},"end":{"line":96,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"recruits_info") : depth0)) != null ? lookupProperty(stack1,"2") : stack1),{"name":"if","hash":{},"fn":container.program(29, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":97,"column":0},"end":{"line":97,"column":115}}})) != null ? stack1 : "")
    + " recruited.\n</p>\n";
},"20":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"recruits_info") : depth0),{"name":"each","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":81,"column":0},"end":{"line":85,"column":9}}})) != null ? stack1 : "");
},"21":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(data && lookupProperty(data,"index")),"<",2,{"name":"compare","hash":{},"fn":container.program(22, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":82,"column":0},"end":{"line":84,"column":12}}})) != null ? stack1 : "");
},"22":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + ",\n";
},"24":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"with").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"recruits_info") : depth0)) != null ? lookupProperty(stack1,"0") : stack1),{"name":"with","hash":{},"fn":container.program(25, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":87,"column":0},"end":{"line":89,"column":9}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"recruits_info") : depth0)) != null ? lookupProperty(stack1,"1") : stack1),{"name":"if","hash":{},"fn":container.program(27, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":90,"column":0},"end":{"line":95,"column":7}}})) != null ? stack1 : "");
},"25":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "\n";
},"27":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "and\n"
    + ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"recruits_info") : depth0)) != null ? lookupProperty(stack1,"1") : stack1),{"name":"with","hash":{},"fn":container.program(25, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":92,"column":0},"end":{"line":94,"column":9}}})) != null ? stack1 : "");
},"29":function(container,depth0,helpers,partials,data) {
    var alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "and "
    + alias2(lookupProperty(helpers,"recruitsremaining").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_recruits") : depth0),{"name":"recruitsremaining","hash":{},"data":data,"loc":{"start":{"line":97,"column":29},"end":{"line":97,"column":63}}}))
    + " other"
    + alias2(lookupProperty(helpers,"s").call(alias1,lookupProperty(helpers,"recruitsremaining").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_recruits") : depth0),{"name":"recruitsremaining","hash":{},"data":data,"loc":{"start":{"line":97,"column":73},"end":{"line":97,"column":105}}}),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":97,"column":69},"end":{"line":97,"column":107}}}))
    + " ";
},"31":function(container,depth0,helpers,partials,data) {
    return "<p class=\"recruit-names none\">Looks like you haven't recruited anyone yet. </p>\n";
},"33":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"impact-background\"></div>\n<div class=\"impact-underlay\" style=\"background:url("
    + container.escapeExpression(alias1((depth0 != null ? lookupProperty(depth0,"our_impact_image") : depth0), depth0))
    + ");background-size: cover;\"></div>\n<div class=\"impact-overlay\"></div>\n<div class=\"impact-text\">"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"our_impact_text") : depth0), depth0)) != null ? stack1 : "")
    + "</div>\n";
},"35":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,{"name":"gridsection","hash":{},"fn":container.program(36, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":123,"column":0},"end":{"line":125,"column":16}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"victory-block",{"name":"gridsection","hash":{},"fn":container.program(38, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":127,"column":0},"end":{"line":129,"column":16}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"notifications-section",{"name":"gridsection","hash":{},"fn":container.program(40, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":131,"column":0},"end":{"line":133,"column":16}}})) != null ? stack1 : "");
},"36":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"marquee-header",{"name":"$","hash":{"title":"What's Happening Now"},"data":data,"loc":{"start":{"line":124,"column":0},"end":{"line":124,"column":52}}}))
    + "\n";
},"38":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div data-template=\"victory-campaign\" data-source=\"campaign:"
    + container.escapeExpression(container.lambda(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"victory_campaigns") : depth0)) != null ? lookupProperty(stack1,"0") : stack1)) != null ? lookupProperty(stack1,"victory_campaign") : stack1), depth0))
    + "\" class=\"row\"></div>\n";
},"40":function(container,depth0,helpers,partials,data) {
    return "<div data-template=\"notifications-feed\" data-mixins=\"more\" data-source=\"notifications\" data-watch=\"false\" data-mobile-resize=\"true\" id=\"notifications-feed\"></div>\n";
},"42":function(container,depth0,helpers,partials,data) {
    var alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\n<div class=\"accomplishments campaigns-supported\">\n<figure class=\"supporter-stat one\">\n<span class=\"ss-number\">"
    + alias2(lookupProperty(helpers,"commafy").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_campaigns") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":140,"column":24},"end":{"line":140,"column":49}}}))
    + "</span><figcaption>\n<strong>campaign"
    + alias2(lookupProperty(helpers,"s").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_campaigns") : depth0),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":141,"column":16},"end":{"line":141,"column":35}}}))
    + "</strong> supported\n</figure>\n<div data-template=\"campaigns-your-impact\" data-source=\"your-campaigns\" data-limit=\"3\" data-perpage=\"3\" data-mixins=\"limit\" data-watch=\"false\" class=\"stat-template row campaigns-your-impact\"></div>\n<p class=\"cta-block\"><a href=\"campaigns/your-campaigns\">My campaigns ></a></p>\n</div>\n\n";
},"44":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"marquee-header",{"name":"$","hash":{"title":"AppUp is more fun with friends"},"data":data,"loc":{"start":{"line":150,"column":0},"end":{"line":150,"column":61}}}))
    + "\n<div class=\"recruit-your-friends recruit-action-section\">"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"standalone-recruit"),depth0,{"name":"standalone-recruit","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"postsignupcamp") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":12,"column":7}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"supporter-welcome",{"name":"gridsection","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":0},"end":{"line":19,"column":16}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"supporter-intro-section one",{"name":"gridsection","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":21,"column":0},"end":{"line":120,"column":16}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"loggedin") : depth0),{"name":"if","hash":{},"fn":container.program(35, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":122,"column":0},"end":{"line":134,"column":7}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"supporter-intro-section two desktop-only",{"name":"gridsection","hash":{},"fn":container.program(42, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":136,"column":0},"end":{"line":147,"column":16}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"impact-recruit",{"name":"gridsection","hash":{},"fn":container.program(44, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":149,"column":0},"end":{"line":152,"column":16}}})) != null ? stack1 : "")
    + "\n<div id=\"rec-action-section\"></div>\n";
},"usePartial":true,"useData":true});

this["SC"]["welcome"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<style>.hero-signup{background-image:url("
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"hero_image_override") : depth0), depth0)) != null ? stack1 : "")
    + "/@mx_2800);}@media screen and (max-width: 1000px){.hero-signup{background-image:url("
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"hero_image_override") : depth0), depth0)) != null ? stack1 : "")
    + "/@mx_1800);}}@media screen and (max-width: 767px){.hero-signup{background-image:url("
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"hero_image_override") : depth0), depth0)) != null ? stack1 : "")
    + "/@mx_900);}}@media screen and (max-width: 480px){.hero-signup{background-image:url("
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"hero_image_override") : depth0), depth0)) != null ? stack1 : "")
    + "/@mx_600);}}</style>";
},"3":function(container,depth0,helpers,partials,data) {
    var alias1=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return alias1(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"marquee-header",{"name":"$","hash":{"title":"Campaigns That Need Your Help Now"},"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":7,"column":64}}}))
    + "\n<div data-template=\"swiper-slider\" data-mixins=\"swiper\" data-mobile-resize=\"true\" data-limit=\""
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"welcome_page_campaign_limit") : depth0), depth0))
    + "\" data-source=\"welcome-campaigns\" data-watch=\"false\" class=\"row slider campaign-slider\"></div>\n";
},"5":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"marquee-header",{"name":"$","hash":{"title":"Whose Actions Are Adding Up Right Now..."},"data":data,"loc":{"start":{"line":12,"column":0},"end":{"line":12,"column":71}}}))
    + "\n<div data-template=\"happening-now\" data-mobile-resize=\"true\" data-source=\"happening-now\" data-listen=\"happening-now\" class=\"row\"></div>\n";
},"7":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"marquee-header",{"name":"$","hash":{"title":"Our Impact"},"data":data,"loc":{"start":{"line":17,"column":0},"end":{"line":17,"column":41}}}))
    + "\n<div data-template=\"swiper-slider\" data-mixins=\"swiper\" data-mobile-resize=\"true\" data-model=\"stats\" data-source=\"stats\" data-listen=\"stats\" class=\"row slider impact-stat-slider\"></div>\n";
},"9":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"marquee-header",{"name":"$","hash":{"title":"Ways to Get Involved"},"data":data,"loc":{"start":{"line":21,"column":0},"end":{"line":21,"column":51}}}))
    + "\n<div data-template=\"involvement-promos\" data-mixins=\"swiper\" data-noloop=\"true\" data-centered=\"true\" data-mobile-resize=\"true\"></div>\n<a href=\"/campaigns\" class=\"button\">Explore</a>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"hero_image_override") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":446}}})) != null ? stack1 : "")
    + "\n<section class=\"hero-signup welcome-section\">\n<div data-template=\"welcome-campaign-hero\" data-noactions=\"true\" data-source=\"campaign:"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"hero_campaign_id") : depth0), depth0))
    + "\"></div>\n</section>\n<div class=\"welcome-page banded-sections\">\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"campaigns-happening-now",{"name":"gridsection","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":6,"column":0},"end":{"line":9,"column":16}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"happening-now",{"name":"gridsection","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":11,"column":0},"end":{"line":14,"column":16}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"our-impact",{"name":"gridsection","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":0},"end":{"line":19,"column":16}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"promo-4x center",{"name":"gridsection","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":20,"column":0},"end":{"line":24,"column":16}}})) != null ? stack1 : "")
    + "</div>\n";
},"useData":true});

this["SC"]["account-change-password"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h2 class=\"h3\">Change Password</h2>\n\n<div class=\"row background-pale section-contents\">\n\n<form data-formaction=\"moleform\" method=\"POST\" data-listerrors=\"true\" data-method=\"PUT\" action=\"/u/passwords\" class=\"inactive reset-when-done\">\n<ul class=\"update-password-form\">\n<li class=\"current-password\">\n<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-password-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Enter old password</label>\n<input title=\"Enter your old password\" name=\"old_password\" value=\""
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"password") : stack1), depth0))
    + "\" placeholder=\"Old Password\" id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-oldpassword-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"r-pswd"),depth0,{"name":"r-pswd","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + ">\n</li>\n"
    + alias2(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"two-passwords",{"name":"$","hash":{"new":"new "},"data":data,"loc":{"start":{"line":11,"column":0},"end":{"line":11,"column":32}}}))
    + "\n</ul>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"update_button"),depth0,{"name":"update_button","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</form>\n\n</div>\n";
},"usePartial":true,"useData":true});

this["SC"]["account-email-prefs"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h2 class=\"h3\">Email Preferences</h2>\n\n<div class=\"row background-pale section-contents\">\n\n<form data-formaction=\"moleform\" method=\"POST\" data-method=\"POST\" action=\"/u/email_prefs_global\" class=\"inactive check-labels\">\n\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"email_pref_fields"),depth0,{"name":"email_pref_fields","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + ((stack1 = container.invokePartial(lookupProperty(partials,"update_button"),depth0,{"name":"update_button","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "\n</form>\n\n</div>\n";
},"usePartial":true,"useData":true});

this["SC"]["account-personal-info"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"avatar") : depth0), depth0));
},"3":function(container,depth0,helpers,partials,data) {
    return "/img/user_no_image_lg.png";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p>Signed in as "
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"email") : stack1), depth0))
    + ".</p>\n\n<h2 class=\"h3\">Personal Info</h2>\n<div class=\"row background-pale section-contents\">\n<div class=\"update-user-photo\">\n<figure>\n<div id=\"avatar_"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" class=\"dropable avatar-upload-container\" data-avatarget=\"avatar_"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">\n<img src=\""
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"avatar") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":8,"column":10},"end":{"line":8,"column":74}}})) != null ? stack1 : "")
    + "\" alt=\"\">\n<input type=\"file\" value=\"Upload\" name=\"avatar\" title=\""
    + ((stack1 = container.invokePartial(lookupProperty(partials,"avatar-caption"),depth0,{"name":"avatar-caption","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "\" accept=\"image/*;capture=camera\" data-avatarget=\"avatar_"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" class=\"avatar-upload\">\n</div>\n<figcaption class=\"photo-options\">\n<small>Drag an image here or click to choose a file and change your profile photo. Photo is saved automatically. (Hint: This will look best if the picture is centered.)</small>\n</figcaption>\n</figure>\n</div>\n<form data-formaction=\"moleform\" method=\"POST\" data-method=\"PUT\" data-silent=\"true\" action=\"/u/\" class=\"inactive clearfix pform\">\n<p>To view or update your personal contact information (e.g. email, phone number, address), please visit your <a href=\"https://myaccount.sierraclub.org/MyAccountDashboard\">MyAccount page</a>.</p>\n</form>\n</div>\n";
},"usePartial":true,"useData":true});

this["SC"]["account-privacy"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return " checked";
},"3":function(container,depth0,helpers,partials,data) {
    return "checked";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h2 class=\"h3\">Privacy</h2>\n\n<div class=\"row background-pale section-contents\">\n\n<form data-formaction=\"moleform\" method=\"POST\" data-method=\"PUT\" action=\"/u/privacy\" class=\"inactive check-labels\">\n\n<ul class=\"radio\">\n<li>\n<input type=\"radio\" name=\"anonymous\" id=\"privacy-yes\" value=\"0\""
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"anonymous") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":63},"end":{"line":9,"column":103}}})) != null ? stack1 : "")
    + ">\n<label for=\"privacy-yes\" class=\""
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"anonymous") : depth0),{"name":"unless","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":10,"column":32},"end":{"line":10,"column":71}}})) != null ? stack1 : "")
    + "\">\n<span><i></i></span>\nYes, I would like others to see my participation on campaigns!\n</label>\n<input type=\"radio\" name=\"anonymous\" id=\"privacy-no\" value=\"1\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"anonymous") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":62},"end":{"line":14,"column":94}}})) != null ? stack1 : "")
    + ">\n<label for=\"privacy-no\" class=\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"anonymous") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":15,"column":31},"end":{"line":15,"column":63}}})) != null ? stack1 : "")
    + "\">\n<span><i></i></span>\nNo, please hide my participation from others.\n</label>\n</li>\n</ul>\n\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"update_button"),depth0,{"name":"update_button","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "\n</form>\n\n</div>\n";
},"usePartial":true,"useData":true});

this["SC"]["account-social-connection"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p>Connect to Facebook! "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"connect-to-facebook-button"),depth0,{"name":"connect-to-facebook-button","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</p>\n\n";
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\n<p>You are connected to Facebook as "
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"email") : depth0), depth0))
    + ". <a href=\"#\" data-event=\"revoke-fb\">Unlink Account</a></p>\n\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h2 class=\"h3\">Social Connection</h2>\n\n<div class=\"row background-pale section-contents\">\n\n"
    + ((stack1 = lookupProperty(helpers,"unall").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"fbconnected") : depth0),(depth0 != null ? lookupProperty(depth0,"fbloggedin") : depth0),{"name":"unall","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":12,"column":10}}})) != null ? stack1 : "")
    + "\n</div>\n";
},"usePartial":true,"useData":true});

this["SC"]["action-donate"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"action-header",{"name":"$","hash":{"title":"Donate to the Sierra Club Foundation"},"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":2,"column":66}}}))
    + "\n";
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"action-header",{"name":"$","hash":{"title":"Donate to the Sierra Club"},"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":5,"column":55}}}))
    + "\n";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\n<div class=\"row donation-description\">\n<div class=\"col-9\">\n<p>"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"body_copy") : depth0), depth0))
    + "</p>\n</div>\n<div class=\"col-3\">\n</div>\n</div>\n\n<div class=\"row inset-content\">\n<div class=\"col-9\">\n<ol class=\"donation-steps-actions clearfix\">\n<div class=\"donation-line\"></div>\n<li class=\"donation-step-action to-0"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"step") : depth0),"===",0,{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":24,"column":36},"end":{"line":24,"column":80}}})) != null ? stack1 : "")
    + "\" data-stepnum=\"0\">\n<div class=\"step-block\">\n<p class=\"step-num to-0\">1</p>\n<p class=\"step-name to-0\">Amount</p>\n</div>\n</li>\n<li class=\"donation-step-action to-1"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"step") : depth0),"===",1,{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":30,"column":36},"end":{"line":30,"column":80}}})) != null ? stack1 : "")
    + "\" data-stepnum=\"1\">\n<div class=\"step-block\">\n<p class=\"step-num to-1\">2</p>\n<p class=\"step-name to-1\">Information</p>\n</div>\n</li>\n<li class=\"donation-step-action to-2"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"step") : depth0),"===",2,{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":36,"column":36},"end":{"line":36,"column":80}}})) != null ? stack1 : "")
    + "\" data-stepnum=\"2\">\n<div class=\"step-block\">\n<p class=\"step-num to-2\">3</p>\n<p class=\"step-name to-2\">Payment</p>\n</div>\n</li>\n</ol>\n\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"message") : stack1),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":44,"column":0},"end":{"line":46,"column":7}}})) != null ? stack1 : "")
    + "\n<ol class=\"donation-steps\">\n<li class=\"donation-step active\" data-stepnum=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"step") : depth0), depth0))
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"step") : depth0),"===",0,{"name":"compare","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":50,"column":0},"end":{"line":61,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"step") : depth0),"===",1,{"name":"compare","hash":{},"fn":container.program(13, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":62,"column":0},"end":{"line":105,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"step") : depth0),"===",2,{"name":"compare","hash":{},"fn":container.program(16, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":106,"column":0},"end":{"line":145,"column":12}}})) != null ? stack1 : "")
    + "</li>\n</ol>\n</div>\n<div class=\"action-area col-3 center\">\n<div class=\"action-progress-section\">\n<h4 class=\"h1\">It Adds Up</h4>\n<p>Help <strong>Support</strong> the cause</p>\n</div>\n</div>\n</div>\n<div class=\"row inset-content\">\n<div class=\"col-9\">\n<div class=\"donations-legal\">\n<p>\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"c_code") : depth0),"===","C3",{"name":"compare","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":160,"column":0},"end":{"line":163,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"c_code") : depth0),"===","C4",{"name":"compare","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":164,"column":0},"end":{"line":168,"column":12}}})) != null ? stack1 : "")
    + "</p>\n</div>\n</div>\n</div>\n\n";
},"6":function(container,depth0,helpers,partials,data) {
    return " active";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<ul><li class=\"error-message\">"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"message") : stack1), depth0))
    + "</li></ul>\n";
},"10":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<form method=\"post\" class=\"form1 donation-form\">\n<ol class=\"donation-buttons clearfix row\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"amounts") : depth0),{"name":"each","hash":{},"fn":container.program(11, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":53,"column":0},"end":{"line":53,"column":87}}})) != null ? stack1 : "")
    + "\n<li class=\"amount-label col-4\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"donate_other"),depth0,{"name":"donate_other","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-amounts-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">dollar amount</label>\n</li>\n<li class=\"donate-next\"><button class=\"button next-button to-1\" type=\"submit\">Next</button></li>\n</ol>\n</form>\n";
},"11":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"donate-radio-button col-4\">"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"donation-btn"),depth0,{"name":"donation-btn","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>";
},"13":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<form method=\"post\" class=\"form2 donation-form\">\n<ul class=\"two-column-form\">\n<li class=\"firstname"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":65,"column":20},"end":{"line":65,"column":60}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-firstname-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">First Name</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"first_name_field"),depth0,{"name":"first_name_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"lastname"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":69,"column":19},"end":{"line":69,"column":59}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-lastname-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Last Name</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"last_name_field"),depth0,{"name":"last_name_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"street_address"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"street_address") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":73,"column":25},"end":{"line":73,"column":69}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-a1-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Street Line 1</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"street_address_field"),depth0,{"name":"street_address_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"additional_address"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"additional_address") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":77,"column":29},"end":{"line":77,"column":77}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-a2-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Street Line 2 <span class=\"tiny\">(optional)</span></label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"additional_address_field"),depth0,{"name":"additional_address_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"country"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"country") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":81,"column":18},"end":{"line":81,"column":55}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-country-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Country</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"country_field"),depth0,{"name":"country_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"city"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"city") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":85,"column":15},"end":{"line":85,"column":49}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-city-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">City</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"city_field"),depth0,{"name":"city_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"state"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"state") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":89,"column":16},"end":{"line":89,"column":51}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-state-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">State/Province</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"state_field"),depth0,{"name":"state_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"zipcode"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"zipcode") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":93,"column":18},"end":{"line":93,"column":55}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-zip-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Postal Code</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"zip_field"),depth0,{"name":"zip_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"email"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"email") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":97,"column":16},"end":{"line":97,"column":51}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-email-"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Email</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"email_field"),depth0,{"name":"email_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"disclaimer\"><small>All fields required.</small></li>\n<li><button type=\"submit\" class=\"button next-button to-2\">Next</button></li>\n</ul>\n</form>\n";
},"14":function(container,depth0,helpers,partials,data) {
    return " inactive-field";
},"16":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<form data-formaction=\"moleform\" class=\"donate-form donation-form form3 clearfix\" method=\"POST\" action=\"/u/actions/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias3,(depth0 != null ? lookupProperty(depth0,"savedData") : depth0),{"name":"each","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":108,"column":0},"end":{"line":110,"column":9}}})) != null ? stack1 : "")
    + "<ul class=\"two-column-form\">\n<div class=\"card-outer\">\n<div class=\"card-info\">\n<li class=\"card"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"card_number") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":114,"column":15},"end":{"line":114,"column":56}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-card-number-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Card Number</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"card_field"),depth0,{"name":"card_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"exp-month"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"exp_month") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":118,"column":20},"end":{"line":118,"column":59}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-exp-month-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Expiration Date</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"exp_month_field"),depth0,{"name":"exp_month_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + ((stack1 = container.invokePartial(lookupProperty(partials,"exp_year_field"),depth0,{"name":"exp_year_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n<li class=\"card-cvv"
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"card_cvv") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":123,"column":19},"end":{"line":123,"column":57}}})) != null ? stack1 : "")
    + "\">\n<label for=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_template") : depth0), depth0))
    + "-card-cvv-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">CVV\n<span class=\"tooltip\">?\n<span class=\"tooltip-bubble\">\n<span class=\"closebutton\"></span>\n<p>The last block of digits on the back of your card</p>\n</span>\n</span>\n</label>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"card_cvv_field"),depth0,{"name":"card_cvv_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n</div>\n</div>\n<li class=\"card-types\">\n<img src=\"/img/donate-visa.png\" class=\"card-type visa\" />\n<img src=\"/img/donate-mc.png\" class=\"card-type mc\" />\n<img src=\"/img/donate-amex.png\" class=\"card-type amex\" />\n<img src=\"/img/donate-disc.png\" class=\"card-type disc\" />\n</li>\n<li class=\"donate-row\"><a class=\"button donate-button submit\">Donate</a></li>\n</ul>\n</form>\n";
},"17":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"hidden-fields"),depth0,{"name":"hidden-fields","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"19":function(container,depth0,helpers,partials,data) {
    return "The Sierra Club Foundation is tax exempt under Section 501(c)(3) of the Internal Revenue Code, and gifts to The Foundation are tax-deductible under Section 170(b)(1)(A)(vi) of the Code. IRS regulations require that you retain a written acknowledgement from The Foundation to support your claim of a tax deduction. A receipt of your gift will be mailed to you shortly. The Foundation tax ID (EIN) is 94-6069890. The Foundation confirms that you received no goods or services in return for your gift.</p>\n<p>By law, The Sierra Club Foundation retains control and discretion/variance power over all charitable funds received, including how they are disbursed within the purposes for which they were contributed; the Foundation reserves the unilateral right to select those recipients or beneficiaries it believes will best accomplish those purposes.\n";
},"21":function(container,depth0,helpers,partials,data) {
    return "Your gift will help support our many efforts, including protecting our wildlands and wildlife, keeping our air and water free from pollution, and promoting a clean energy future.</p>\n<p>Note: Contributions, gifts, and dues to the Sierra Club are not tax deductible. They support our effective, citizen-based advocacy and lobbying efforts. (<a href=\"http://www.sierraclub.org/financial/financial-regulatory-info\" target=\"_blank\">Additional financial and regulatory information</a>.)</p>\n<p>Please note that free premiums and membership benefits including newsletters and SIERRA magazine will not be shipped outside the U.S. due to shipping costs, allowing us to more effectively use your contribution.\n";
},"23":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"action-confirmation\">\n<div class=\"bubble-pale\">\n<h2 class=\"confirm-title\">"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"thank_you_copy") : depth0), depth0))
    + "</h2>\n</div>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"next-action-link"),depth0,{"name":"next-action-link","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"c_code") : depth0),"===","C3",{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":3,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"c_code") : depth0),"===","C4",{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":6,"column":12}}})) != null ? stack1 : "")
    + "\n<div class=\"action-content\">\n\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_taken") : depth0),{"name":"unless","hash":{},"fn":container.program(5, data, 0),"inverse":container.program(23, data, 0),"data":data,"loc":{"start":{"line":10,"column":0},"end":{"line":181,"column":11}}})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true});

this["SC"]["action-event-recs"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "<button class=\"btn action-reset\">Toggle</button>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return alias2(lookupProperty(helpers,"$").call(alias1,"action-header",{"name":"$","hash":{"title":"Attend an Event"},"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":45}}}))
    + "\n<div class=\"action-content\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_debug") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":70}}})) != null ? stack1 : "")
    + "\n<div class=\"row event-display-row\">\n"
    + alias2(lookupProperty(helpers,"$").call(alias1,"display-event",{"name":"$","hash":{"col":"col-12"},"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":5,"column":34}}}))
    + "\n</div>\n</div>\n";
},"useData":true});

this["SC"]["action-event"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "<button class=\"btn action-reset\">Toggle</button>";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"action_taken") : depth0),{"name":"unless","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":17,"column":11}}})) != null ? stack1 : "");
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"$").call(alias1,"events-search",{"name":"$","hash":{"alt_title":"Related Events"},"data":data,"loc":{"start":{"line":9,"column":0},"end":{"line":9,"column":51}}})) != null ? stack1 : "")
    + "\n<div class=\"event-list "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">=","768",{"name":"compare","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":10,"column":23},"end":{"line":10,"column":71}}})) != null ? stack1 : "")
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">=","768",{"name":"compare","hash":{},"fn":container.program(7, data, 0),"inverse":container.program(9, data, 0),"data":data,"loc":{"start":{"line":11,"column":0},"end":{"line":15,"column":12}}})) != null ? stack1 : "")
    + "</div>\n";
},"5":function(container,depth0,helpers,partials,data) {
    return "w-swiper";
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"events-slider"),depth0,{"name":"events-slider","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"9":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"more-events-stacked"),depth0,{"name":"more-events-stacked","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"$").call(alias1,"action-header",{"name":"$","hash":{},"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":23}}})) != null ? stack1 : "")
    + "\n<div class=\"action-content\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_debug") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":70}}})) != null ? stack1 : "")
    + "\n<div class=\"row event-display-row\">\n"
    + ((stack1 = lookupProperty(helpers,"$").call(alias1,"display-event",{"name":"$","hash":{"col":"col-12"},"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":5,"column":36}}})) != null ? stack1 : "")
    + "\n</div>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"events") : depth0)) != null ? lookupProperty(stack1,"1") : stack1),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":18,"column":7}}})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true});

this["SC"]["action-next-campaign"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"icon-section action-template\">\n"
    + container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"marquee-header",{"name":"$","hash":{"title":(depths[1] != null ? lookupProperty(depths[1],"head_title") : depths[1]),"mleft":true},"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":53}}}))
    + "\n<hr>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"next-campaign-content"),depth0,{"name":"next-campaign-content","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"next-campaign-section action-section",{"name":"gridsection","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":7,"column":16}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true,"useDepths":true});

this["SC"]["action-petition"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "<button class=\"btn action-reset\">Toggle</button><button class=\"btn action-open\">Open</button>";
},"3":function(container,depth0,helpers,partials,data) {
    return " dimmed";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"col-9 background-pale-section\">\n"
    + ((stack1 = lookupProperty(helpers,"any").call(alias1,(depth0 != null ? lookupProperty(depth0,"can_edit_subject") : depth0),(depth0 != null ? lookupProperty(depth0,"can_edit_message") : depth0),(depth0 != null ? lookupProperty(depth0,"display_personal_message") : depth0),{"name":"any","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":9,"column":8}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"state_restriction") : depth0),"[!]",(depth0 != null ? lookupProperty(depth0,"state") : depth0),{"name":"compare","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":10,"column":0},"end":{"line":10,"column":289}}})) != null ? stack1 : "")
    + "\n<p class=\"to-section\"><span class=\"h3 oswald\">To: </span><span class=\"italic\">"
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias1,(depth0 != null ? lookupProperty(depth0,"custom_targets") : depth0),{"name":"ifor","hash":{},"fn":container.program(13, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":11,"column":78},"end":{"line":11,"column":131}}})) != null ? stack1 : "")
    + "</span></p>\n<div class=\"petition-text bubble base\">\n<div class=\"content clearfix\">\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_took") : depth0),{"name":"unless","hash":{},"fn":container.program(15, data, 0),"inverse":container.program(39, data, 0),"data":data,"loc":{"start":{"line":14,"column":0},"end":{"line":122,"column":11}}})) != null ? stack1 : "")
    + "</div>\n</div>\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_took") : depth0),{"name":"unless","hash":{},"fn":container.program(41, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":125,"column":0},"end":{"line":135,"column":11}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"any").call(alias1,(depth0 != null ? lookupProperty(depth0,"can_edit_subject") : depth0),(depth0 != null ? lookupProperty(depth0,"can_edit_message") : depth0),(depth0 != null ? lookupProperty(depth0,"display_personal_message") : depth0),{"name":"any","hash":{},"fn":container.program(47, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":136,"column":0},"end":{"line":138,"column":8}}})) != null ? stack1 : "")
    + "</div>\n<div class=\"action-area col-3 center\">\n"
    + container.escapeExpression(lookupProperty(helpers,"$").call(alias1,"action-progress-section",{"name":"$","hash":{"type":"Signature"},"data":data,"loc":{"start":{"line":141,"column":0},"end":{"line":141,"column":48}}}))
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_took") : depth0),{"name":"if","hash":{},"fn":container.program(49, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":142,"column":0},"end":{"line":144,"column":7}}})) != null ? stack1 : "")
    + "</div>\n";
},"6":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<form data-formaction=\"moleform\" method=\"POST\" action=\"/u/actions/"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" class=\"clearfix\">\n";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"error-message\">This "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","Contact Officials - All info required",{"name":"compare","hash":{},"fn":container.program(9, data, 0),"inverse":container.program(11, data, 0),"data":data,"loc":{"start":{"line":10,"column":74},"end":{"line":10,"column":186}}})) != null ? stack1 : "")
    + " is only available for residents of the following states: "
    + container.escapeExpression(lookupProperty(helpers,"list").call(alias1,(depth0 != null ? lookupProperty(depth0,"state_restriction") : depth0),{"name":"list","hash":{},"data":data,"loc":{"start":{"line":10,"column":244},"end":{"line":10,"column":270}}}))
    + ".</div>";
},"9":function(container,depth0,helpers,partials,data) {
    return "action";
},"11":function(container,depth0,helpers,partials,data) {
    return "petition";
},"13":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"petition_targets") : depth0), depth0));
},"15":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","Contact Officials - All info required",{"name":"compare","hash":{},"fn":container.program(16, data, 0),"inverse":container.program(34, data, 0),"data":data,"loc":{"start":{"line":15,"column":0},"end":{"line":114,"column":12}}})) != null ? stack1 : "");
},"16":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"display_personal_message") : depth0),{"name":"if","hash":{},"fn":container.program(17, data, 0),"inverse":container.program(32, data, 0),"data":data,"loc":{"start":{"line":16,"column":0},"end":{"line":74,"column":7}}})) != null ? stack1 : "");
},"17":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"petition-text-container\">\n<label for=\"pmessage\" class=\"pmessage\">\n<img src=\"//content.sierraclub.org/sites/all/libraries/sc-static/roundcorner-dev/roundcorner/images/gift-arrow.png\"/>\nPersonal messages make a big impact on decision makers. Please add a note about why this issue matters to you!\n</label>\n<textarea class=\"petition-personal\" name=\"pmessage\" id=\"pmessage\" placeholder=\"Why this matters to me (optional)\" maxlength=\"1000\"></textarea>\n<div class=\"petition-counter petition-personal-container\"><span class=\"petition-charcount-personal\" id=\"petition-charcount-personal\">0</span>/1000</div>\n</div>\n<p style=\"margin-bottom:0;padding-top:15px;\"><a href=\"#\" class=\"more-button-after hide-when-expanded\" data-expand=\"petition-body\" style=\"margin-bottom:0\">Read entire "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","Contact Officials - All info required",{"name":"compare","hash":{},"fn":container.program(18, data, 0),"inverse":container.program(20, data, 0),"data":data,"loc":{"start":{"line":25,"column":166},"end":{"line":25,"column":286}}})) != null ? stack1 : "")
    + "</a></p>\n<div data-expandable=\"petition-body\" class=\"petition-body expandable closed\" style=\"margin:0\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"description") : depth0),{"name":"if","hash":{},"fn":container.program(22, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":27,"column":0},"end":{"line":34,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"body") : depth0),{"name":"if","hash":{},"fn":container.program(27, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":35,"column":0},"end":{"line":47,"column":7}}})) != null ? stack1 : "")
    + "<p style=\"margin-bottom:0;padding-top:15px;\">\n<button class=\"less-button\" data-expand=\"petition-body\">Read&nbsp;Less</button>\n</p>\n</div>\n";
},"18":function(container,depth0,helpers,partials,data) {
    return "message";
},"20":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"action_type") : depth0), depth0));
},"22":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"can_edit_subject") : depth0),{"name":"if","hash":{},"fn":container.program(23, data, 0),"inverse":container.program(25, data, 0),"data":data,"loc":{"start":{"line":28,"column":0},"end":{"line":33,"column":7}}})) != null ? stack1 : "");
},"23":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<label>Subject <span class=\"tooltip\">?<span class=\"tooltip-bubble\"><span class=\"closebutton\"></span><p>Personalizing the subject line of your message will help it stand out.</p></span></span></label>\n<input type=\"text\" name=\"subject\" title=\"Subject\" value=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"description") : depth0), depth0))
    + "\" required=\"required\" class=\"petition-subject\" maxlength=\"255\">\n";
},"25":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"paragraphify").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"description") : depth0),{"name":"paragraphify","hash":{},"data":data,"loc":{"start":{"line":32,"column":0},"end":{"line":32,"column":28}}}))
    + "\n";
},"27":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"can_edit_message") : depth0),{"name":"if","hash":{},"fn":container.program(28, data, 0),"inverse":container.program(30, data, 0),"data":data,"loc":{"start":{"line":36,"column":0},"end":{"line":46,"column":7}}})) != null ? stack1 : "");
},"28":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<label>Message <span class=\"tooltip\">?<span class=\"tooltip-bubble\"><span class=\"closebutton\"></span><p>Personalized messages carry more weight with decision-makers. Talk about why you care about this issue and how it will impact you personally.</p></span></span></label>\n<div class=\"petition-text-container\">\n<textarea class=\"petition-longtext\" id=\"description-body\" name=\"message\" maxlength=\"5000\">\n"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"body") : depth0), depth0))
    + "\n</textarea>\n<div class=\"petition-counter petition-charcount-container\"><span class=\"petition-charcount\" id=\"petition-charcount\"></span>/5000</div>\n</div>\n";
},"30":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"paragraphify").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"body") : depth0),{"name":"paragraphify","hash":{},"data":data,"loc":{"start":{"line":45,"column":0},"end":{"line":45,"column":21}}}))
    + "\n";
},"32":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"description") : depth0),{"name":"if","hash":{},"fn":container.program(22, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":53,"column":0},"end":{"line":60,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"body") : depth0),{"name":"if","hash":{},"fn":container.program(27, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":61,"column":0},"end":{"line":73,"column":7}}})) != null ? stack1 : "");
},"34":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"description") : depth0),{"name":"if","hash":{},"fn":container.program(25, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":76,"column":0},"end":{"line":78,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","No Delivery - Location required",{"name":"compare","hash":{},"fn":container.program(35, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":79,"column":0},"end":{"line":92,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","No Delivery - Minimal info required",{"name":"compare","hash":{},"fn":container.program(35, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":93,"column":0},"end":{"line":106,"column":12}}})) != null ? stack1 : "")
    + "<p style=\"margin-bottom:0;padding-top:15px;\"><a href=\"#\" class=\"more-button-after hide-when-expanded\" data-expand=\"petition-body\" style=\"margin-bottom:0\">Read entire "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","Contact Officials - All info required",{"name":"compare","hash":{},"fn":container.program(18, data, 0),"inverse":container.program(20, data, 0),"data":data,"loc":{"start":{"line":107,"column":166},"end":{"line":107,"column":286}}})) != null ? stack1 : "")
    + "</a></p>\n<div data-expandable=\"petition-body\" class=\"petition-body expandable closed\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"body") : depth0),{"name":"if","hash":{},"fn":container.program(30, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":109,"column":0},"end":{"line":111,"column":7}}})) != null ? stack1 : "")
    + "<button class=\"less-button\" data-expand=\"petition-body\">Read&nbsp;Less</button>\n</div>\n";
},"35":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"display_personal_message") : depth0),{"name":"if","hash":{},"fn":container.program(36, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":80,"column":0},"end":{"line":91,"column":7}}})) != null ? stack1 : "");
},"36":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"action_took") : depth0),{"name":"unless","hash":{},"fn":container.program(37, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":81,"column":0},"end":{"line":90,"column":11}}})) != null ? stack1 : "");
},"37":function(container,depth0,helpers,partials,data) {
    return "<div class=\"petition-text-container\">\n<label for=\"pmessage\" class=\"pmessage\">\n<img class=\"arrow\" src=\"//content.sierraclub.org/sites/all/libraries/sc-static/roundcorner-dev/roundcorner/images/gift-arrow.png\"/>\nPersonal messages make a big impact on decision makers. Please add a note about why this issue matters to you!\n</label>\n<textarea class=\"petition-personal\" name=\"pmessage\" id=\"pmessage\" placeholder=\"Why this matters to me (optional)\" maxlength=\"1000\"></textarea>\n<div class=\"petition-counter petition-personal-container\"><span class=\"petition-charcount-personal\" id=\"petition-charcount-personal\">0</span>/1000</div>\n</div>\n";
},"39":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"description") : depth0),{"name":"if","hash":{},"fn":container.program(25, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":116,"column":0},"end":{"line":118,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"body") : depth0),{"name":"if","hash":{},"fn":container.program(30, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":119,"column":0},"end":{"line":121,"column":7}}})) != null ? stack1 : "");
},"41":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"can_edit_subject") : depth0),{"name":"if","hash":{},"fn":container.program(42, data, 0),"inverse":container.program(44, data, 0),"data":data,"loc":{"start":{"line":126,"column":0},"end":{"line":134,"column":7}}})) != null ? stack1 : "");
},"42":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"editable-petition-form"),depth0,{"name":"editable-petition-form","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"44":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"can_edit_message") : depth0),{"name":"if","hash":{},"fn":container.program(42, data, 0),"inverse":container.program(45, data, 0),"data":data,"loc":{"start":{"line":129,"column":0},"end":{"line":133,"column":7}}})) != null ? stack1 : "");
},"45":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"petition-form"),depth0,{"name":"petition-form","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"47":function(container,depth0,helpers,partials,data) {
    return "</form>\n";
},"49":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"action-complete-section",{"name":"$","hash":{"what":"signed this&nbsp;petition"},"data":data,"loc":{"start":{"line":143,"column":0},"end":{"line":143,"column":64}}}))
    + "\n";
},"51":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.escapeExpression, alias2=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"action-confirmation\">\n<div class=\"bubble-pale\">\n<h2 class=\"confirm-title\">Nice work, "
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"first_name") : depth0), depth0))
    + "!</h2>\n<p class=\"subhead confirm-message\">We are one "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias2,(depth0 != null ? lookupProperty(depth0,"knowwho_action_type") : depth0),"===","Contact Officials - All info required",{"name":"compare","hash":{},"fn":container.program(18, data, 0),"inverse":container.program(52, data, 0),"data":data,"loc":{"start":{"line":150,"column":46},"end":{"line":150,"column":160}}})) != null ? stack1 : "")
    + " closer to our goal!</p>\n"
    + alias1(lookupProperty(helpers,"$").call(alias2,"action-post-action-section",{"name":"$","hash":{"type":"Signature"},"data":data,"loc":{"start":{"line":151,"column":0},"end":{"line":151,"column":51}}}))
    + "\n</div>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"action-complete-next-action"),depth0,{"name":"action-complete-next-action","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"52":function(container,depth0,helpers,partials,data) {
    return "signature";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(alias1,"action-header",{"name":"$","hash":{"mleft":true},"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":32}}}))
    + "\n<div class=\"action-content\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_debug") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":115}}})) != null ? stack1 : "")
    + "\n<div class=\"row inset-content"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"state_restriction") : depth0),"[!]",(depth0 != null ? lookupProperty(depth0,"state") : depth0),{"name":"compare","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":29},"end":{"line":4,"column":90}}})) != null ? stack1 : "")
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_taken") : depth0),{"name":"unless","hash":{},"fn":container.program(5, data, 0),"inverse":container.program(51, data, 0),"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":155,"column":11}}})) != null ? stack1 : "")
    + "</div>\n</div>\n";
},"usePartial":true,"useData":true});

this["SC"]["action-recruit"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "<button class=\"btn action-reset\">Toggle</button>";
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"action-header",{"name":"$","hash":{},"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":21}}}))
    + "\n";
},"5":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"action-header",{"name":"$","hash":{"title":"Recruit Your Friends"},"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":5,"column":50}}}))
    + "\n";
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, alias3=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"col-9 friends-photos-caption\">\n\n<div class=\"row email-row"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"email-step") : depth0),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":13,"column":25},"end":{"line":13,"column":59}}})) != null ? stack1 : "")
    + "\">\n<p>Share this campaign using one of these social channels:</p>\n<div class=\"share-button-row\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"fb-ui-btn"),depth0,{"name":"fb-ui-btn","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "<div class=\"fb-share-button\" data-href=\""
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,lookupProperty(helpers,"find").call(alias1,(depth0 != null ? lookupProperty(depth0,"url") : depth0),((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"url") : stack1),{"name":"find","hash":{},"data":data,"loc":{"start":{"line":17,"column":52},"end":{"line":17,"column":72}}}),{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":17,"column":40},"end":{"line":17,"column":74}}}))
    + "\" data-layout=\"button\" data-size=\"large\"></div>\n<a href=\"https://twitter.com/intent/tweet?utm_medium=recruit&utm_source=twitter&utm_campaign=addup&tc="
    + alias2(alias3(((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"share_code") : stack1), depth0))
    + "&text="
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,"I just took action on \"",{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":18,"column":129},"end":{"line":18,"column":168}}}))
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,lookupProperty(helpers,"source").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"title") : stack1),{"name":"source","hash":{},"data":data,"loc":{"start":{"line":18,"column":180},"end":{"line":18,"column":200}}}),{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":18,"column":168},"end":{"line":18,"column":202}}}))
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,"\" -- will you join me?",{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":18,"column":202},"end":{"line":18,"column":240}}}))
    + "&url="
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,lookupProperty(helpers,"find").call(alias1,(depth0 != null ? lookupProperty(depth0,"url") : depth0),((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"url") : stack1),{"name":"find","hash":{},"data":data,"loc":{"start":{"line":18,"column":257},"end":{"line":18,"column":277}}}),{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":18,"column":245},"end":{"line":18,"column":279}}}))
    + "?utm_medium%3Drecruit%26utm_source%3Dtwitter%26utm_campaign%3Daddup%26tc%3D"
    + alias2(alias3(((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"share_code") : stack1), depth0))
    + "\" data-action-id=\""
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" target=\"_blank\" class=\"button action-button tw\">Tweet</a>\n<a href=\"mailto:?to=&subject="
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,"I just spoke out, will you join me?",{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":19,"column":29},"end":{"line":19,"column":80}}}))
    + "&body="
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,"I thought you might be interested in this as well: ",{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":19,"column":86},"end":{"line":19,"column":153}}}))
    + "%0D%0A"
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,lookupProperty(helpers,"find").call(alias1,(depth0 != null ? lookupProperty(depth0,"url") : depth0),((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"url") : stack1),{"name":"find","hash":{},"data":data,"loc":{"start":{"line":19,"column":171},"end":{"line":19,"column":191}}}),{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":19,"column":159},"end":{"line":19,"column":193}}}))
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,"?promoid=70131000001LjgmAAC&utm_medium=recruit&utm_source=email&utm_campaign=addup",{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":19,"column":193},"end":{"line":19,"column":291}}}))
    + "\" data-email-id=\""
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" class=\"button action-button\">Email</a>\n</div>\n</div>\n</div>\n<div class=\"action-area"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_fbloggedin") : depth0),{"name":"if","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":23,"column":23},"end":{"line":23,"column":62}}})) != null ? stack1 : "")
    + " col-3 center\">\n<div class=\"action-progress-section\">\n<h4 class=\"h1\">"
    + alias2(lookupProperty(helpers,"commafy").call(alias1,(depth0 != null ? lookupProperty(depth0,"current_amount") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":25,"column":15},"end":{"line":25,"column":41}}}))
    + "</h4>\n<p><strong>"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"current_amount") : depth0),"===",1,{"name":"compare","hash":{},"fn":container.program(12, data, 0),"inverse":container.program(14, data, 0),"data":data,"loc":{"start":{"line":26,"column":11},"end":{"line":26,"column":78}}})) != null ? stack1 : "")
    + "</strong> Shared This Campaign</p>\n</div>\n<div class=\"action-button-section\" data-action-id-remove=\""
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"actionid") : depth0), depth0))
    + "\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"next-action-link"),depth0,{"name":"next-action-link","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n</div>\n";
},"8":function(container,depth0,helpers,partials,data) {
    return " emhidden";
},"10":function(container,depth0,helpers,partials,data) {
    return " fb-loggedin";
},"12":function(container,depth0,helpers,partials,data) {
    return "Person";
},"14":function(container,depth0,helpers,partials,data) {
    return "People";
},"16":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"action-confirmation\">\n<div class=\"bubble-pale\">\n<h2 class=\"confirm-title\">WAY TO SPREAD THE WORD"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"first_name") : stack1),{"name":"if","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":35,"column":48},"end":{"line":35,"column":103}}})) != null ? stack1 : "")
    + "!</h2>\n<p class=\"subhead confirm-message\">See the numbers on \"Your Impact\" page increase, each time a friend you recruited takes their first action on AddUp.</p>\n<div class=\"action-progress-section\">\n<div class=\"col-"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"num_secondary_actions") : stack1),{"name":"if","hash":{},"fn":container.program(19, data, 0),"inverse":container.program(21, data, 0),"data":data,"loc":{"start":{"line":38,"column":16},"end":{"line":38,"column":72}}})) != null ? stack1 : "")
    + "\">\n<h4 class=\"h1\">"
    + alias2(lookupProperty(helpers,"commafy").call(alias1,(depth0 != null ? lookupProperty(depth0,"current_amount") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":39,"column":15},"end":{"line":39,"column":41}}}))
    + "</h4>\n<p><strong>"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"current_amount") : depth0),"===",1,{"name":"compare","hash":{},"fn":container.program(12, data, 0),"inverse":container.program(14, data, 0),"data":data,"loc":{"start":{"line":40,"column":11},"end":{"line":40,"column":78}}})) != null ? stack1 : "")
    + "</strong> of "
    + alias2(container.lambda((depth0 != null ? lookupProperty(depth0,"goal_amount") : depth0), depth0))
    + " Goal</p>\n</div>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"num_secondary_actions") : stack1),{"name":"if","hash":{},"fn":container.program(23, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":42,"column":0},"end":{"line":45,"column":13}}})) != null ? stack1 : "")
    + "\n</div>\n</div>\n<button class=\"button back-button\" type=\"reset\">Recruit More Friends</button>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"action-complete-next-action"),depth0,{"name":"action-complete-next-action","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"17":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ", "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"first_name") : stack1), depth0));
},"19":function(container,depth0,helpers,partials,data) {
    return "6";
},"21":function(container,depth0,helpers,partials,data) {
    return "full";
},"23":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"col-6\">\n<h4 class=\"h1\">"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"num_secondary_actions") : stack1), depth0))
    + "</h4>\n<p><strong>Actions</strong> by your friends</p>\n</div>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_debug") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":70}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"subshare") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.program(5, data, 0),"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":6,"column":7}}})) != null ? stack1 : "")
    + "<div class=\"action-content\">\n\n<div class=\"target-share-section row inset-content\">\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_taken") : depth0),{"name":"unless","hash":{},"fn":container.program(7, data, 0),"inverse":container.program(16, data, 0),"data":data,"loc":{"start":{"line":10,"column":0},"end":{"line":51,"column":11}}})) != null ? stack1 : "")
    + "</div>\n</div>\n";
},"usePartial":true,"useData":true});

this["SC"]["action-tweet"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "<button class=\"btn action-reset\">Toggle</button>";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, alias3=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row inset-content\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"tweets") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.program(10, data, 0),"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":45,"column":7}}})) != null ? stack1 : "")
    + "<div class=\"action-area col-3 center\">\n"
    + alias2(lookupProperty(helpers,"$").call(alias1,"action-progress-section",{"name":"$","hash":{"type":"Tweet"},"data":data,"loc":{"start":{"line":47,"column":0},"end":{"line":47,"column":44}}}))
    + "\n<div class=\"action-button-section\" data-action-id-remove=\""
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"actionid") : depth0), depth0))
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_took") : depth0),{"name":"if","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":49,"column":0},"end":{"line":49,"column":85}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"tweets") : depth0),{"name":"if","hash":{},"fn":container.program(23, data, 0),"inverse":container.program(25, data, 0),"data":data,"loc":{"start":{"line":50,"column":0},"end":{"line":53,"column":7}}})) != null ? stack1 : "")
    + "</div>\n</div>\n<div class=\"action-area-mobile center\">\n<div class=\"action-button-section\" data-action-id-remove=\""
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"actionid") : depth0), depth0))
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_took") : depth0),{"name":"if","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":58,"column":0},"end":{"line":58,"column":85}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"tweets") : depth0),{"name":"if","hash":{},"fn":container.program(23, data, 0),"inverse":container.program(25, data, 0),"data":data,"loc":{"start":{"line":59,"column":0},"end":{"line":62,"column":7}}})) != null ? stack1 : "")
    + "</div>\n</div>\n</div>\n";
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"col-9 background-pale-section iblockrow hashtag-block\">\n<div class=\"h3\">#"
    + alias2(lookupProperty(helpers,"hashtag").call(alias1,(depth0 != null ? lookupProperty(depth0,"hashtag") : depth0),{"name":"hashtag","hash":{},"data":data,"loc":{"start":{"line":9,"column":17},"end":{"line":9,"column":36}}}))
    + "</div>\n<div class=\"tweet-content bubble col-12\">\n\n<div class=\"swiper-container tweet-action-outer\" data-slidenum=\"3\">\n<div class=\"swiper-wrapper\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"tweets") : depth0),{"name":"each","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":0},"end":{"line":28,"column":9}}})) != null ? stack1 : "")
    + "</div>\n</div>\n\n</div>\n<div class=\"tweet-content-submit\">\n<a href=\"https://twitter.com/intent/tweet?text="
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,lookupProperty(helpers,"source").call(alias1,(depth0 != null ? lookupProperty(depth0,"description") : depth0),{"name":"source","hash":{},"data":data,"loc":{"start":{"line":34,"column":59},"end":{"line":34,"column":79}}}),{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":34,"column":47},"end":{"line":34,"column":81}}}))
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"append_link") : depth0),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":34,"column":81},"end":{"line":34,"column":155}}})) != null ? stack1 : "")
    + "\" data-action-id=\""
    + alias2(container.lambda((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" target=\"_blank\" class=\"button action-button\">Add My Voice</a>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"next-action-link"),depth0,{"name":"next-action-link","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n</div>\n";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"swiper-slide tweet-action-content",{"name":"gridsection","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":15,"column":0},"end":{"line":27,"column":16}}})) != null ? stack1 : "");
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"col-2\">\n<img class=\"tweet-action-img\" src=\""
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"user") : depth0)) != null ? lookupProperty(stack1,"profile_image_url_https") : stack1), depth0))
    + "\" />\n</div>\n<div class=\"col-10\">\n<p>\n"
    + alias2(lookupProperty(helpers,"twitterlinkify").call(alias3,(depth0 != null ? lookupProperty(depth0,"text") : depth0),{"name":"twitterlinkify","hash":{},"data":data,"loc":{"start":{"line":21,"column":0},"end":{"line":21,"column":23}}}))
    + "\n</p>\n<p class=\"tweet-user\">\n<a href=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"link") : depth0), depth0))
    + "\" target=\"_blank\" class=\"hashtag-tweet-icon\">@"
    + alias2(lookupProperty(helpers,"twitterlinkify").call(alias3,((stack1 = (depth0 != null ? lookupProperty(depth0,"user") : depth0)) != null ? lookupProperty(stack1,"screen_name") : stack1),{"name":"twitterlinkify","hash":{},"data":data,"loc":{"start":{"line":24,"column":63},"end":{"line":24,"column":98}}}))
    + "</a> &middot; "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"created_at") : depth0), depth0))
    + "\n</p>\n</div>\n";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "&url="
    + container.escapeExpression(lookupProperty(helpers,"encodeuri").call(alias1,lookupProperty(helpers,"source").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"url") : stack1),(depth0 != null ? lookupProperty(depth0,"t_sources") : depth0),{"name":"source","hash":{},"data":data,"loc":{"start":{"line":34,"column":118},"end":{"line":34,"column":146}}}),{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":34,"column":105},"end":{"line":34,"column":148}}}));
},"10":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"col-9 background-pale-section iblockrow\">\n<img src=\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"custom_avatar") : depth0),{"name":"if","hash":{},"fn":container.program(11, data, 0),"inverse":container.program(13, data, 0),"data":data,"loc":{"start":{"line":40,"column":10},"end":{"line":40,"column":110}}})) != null ? stack1 : "")
    + "\" onerror=\"this.onerror=null;this.src='/img/default_twitter_avatar.gif';\" class=\"twitter-avatar col-3\">\n<div class=\"tweet-content bubble col-9\">\n<p>"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"twitter_campaign_custom_tweet") : depth0),{"name":"if","hash":{},"fn":container.program(15, data, 0),"inverse":container.program(17, data, 0),"data":data,"loc":{"start":{"line":42,"column":3},"end":{"line":42,"column":159}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"append_link") : depth0),{"name":"if","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":42,"column":159},"end":{"line":42,"column":216}}})) != null ? stack1 : "")
    + "</p>\n</div>\n</div>\n";
},"11":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"custom_avatar") : depth0), depth0))
    + "/@zx_160@zy_160";
},"13":function(container,depth0,helpers,partials,data) {
    return "/img/default_twitter_avatar.gif\"";
},"15":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"twittercustom").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"t_user") : depth0)) != null ? lookupProperty(stack1,"state") : stack1),(depth0 != null ? lookupProperty(depth0,"twitter_campaign_custom_tweet") : depth0),(depth0 != null ? lookupProperty(depth0,"description") : depth0),{"name":"twittercustom","hash":{},"data":data,"loc":{"start":{"line":42,"column":40},"end":{"line":42,"column":112}}}));
},"17":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"twitterlinkify").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"description") : depth0),{"name":"twitterlinkify","hash":{},"data":data,"loc":{"start":{"line":42,"column":120},"end":{"line":42,"column":152}}})) != null ? stack1 : "");
},"19":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"twitterlinkify").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"outer") : depth0)) != null ? lookupProperty(stack1,"url") : stack1),{"name":"twitterlinkify","hash":{},"data":data,"loc":{"start":{"line":42,"column":179},"end":{"line":42,"column":209}}})) != null ? stack1 : "");
},"21":function(container,depth0,helpers,partials,data) {
    return "<div>You've already tweeted, but you can tweet again!</div>";
},"23":function(container,depth0,helpers,partials,data) {
    return "";
},"25":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\n<a href=\"https://twitter.com/intent/tweet?text="
    + alias2(lookupProperty(helpers,"encodeuri").call(alias1,lookupProperty(helpers,"source").call(alias1,(depth0 != null ? lookupProperty(depth0,"description") : depth0),{"name":"source","hash":{},"data":data,"loc":{"start":{"line":51,"column":59},"end":{"line":51,"column":79}}}),{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":51,"column":47},"end":{"line":51,"column":81}}}))
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"append_link") : depth0),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":51,"column":81},"end":{"line":51,"column":155}}})) != null ? stack1 : "")
    + "\" data-action-id=\""
    + alias2(container.lambda((depth0 != null ? lookupProperty(depth0,"id") : depth0), depth0))
    + "\" target=\"_blank\" class=\"button action-button\">Tweet Now</a>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"next-action-link"),depth0,{"name":"next-action-link","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"27":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"action-confirmation\">\n<div class=\"bubble-pale\">\n<h2 class=\"confirm-title\">Way to tell 'em"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),{"name":"if","hash":{},"fn":container.program(28, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":69,"column":41},"end":{"line":69,"column":83}}})) != null ? stack1 : "")
    + "</h2>\n<p class=\"subhead confirm-message\">We are one tweet closer to our goal!</p>\n"
    + container.escapeExpression(lookupProperty(helpers,"$").call(alias1,"action-post-action-section",{"name":"$","hash":{"type":"Tweet"},"data":data,"loc":{"start":{"line":71,"column":0},"end":{"line":71,"column":47}}}))
    + "\n</div>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"action-complete-next-action"),depth0,{"name":"action-complete-next-action","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n\n";
},"28":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ", "
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"first_name") : depth0), depth0))
    + "!";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(alias1,"action-header",{"name":"$","hash":{"mleft":true},"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":32}}}))
    + "\n<div class=\"action-content\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_debug") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":70}}})) != null ? stack1 : "")
    + "\n\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_taken") : depth0),{"name":"unless","hash":{},"fn":container.program(3, data, 0),"inverse":container.program(27, data, 0),"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":76,"column":11}}})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true});

this["SC"]["action-volunteer"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "<button class=\"btn action-reset\">Toggle</button>";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row\">\n\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"description") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.program(6, data, 0),"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":12,"column":7}}})) != null ? stack1 : "")
    + "\n<p class=\"options-directions\"><b>Choose as many of the options as you are interested in:</b></p>\n\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"volunteer-form"),depth0,{"name":"volunteer-form","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "\n</div>\n";
},"4":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p>"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"description") : depth0), depth0))
    + "</p>\n";
},"6":function(container,depth0,helpers,partials,data) {
    return "<p>Want to do even more to make this campaign a success? Let the creator of this campaign know how you'd like to help out, and they'll get in touch with you with more information!</p>\n";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"action-confirmation\">\n<div class=\"bubble-pale\">\n\n<h2 class=\"confirm-title\">Thanks!</h2>\n<p class=\"subhead confirm-message\">You're awesome! An organizer will get in touch with you soon with more information on how you can volunteer and make a bigger impact on this campaign.</p>\n\n</div>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"action-complete-next-action"),depth0,{"name":"action-complete-next-action","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(alias1,"action-header",{"name":"$","hash":{"mleft":true},"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":1,"column":32}}}))
    + "\n<div class=\"action-content\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_debug") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":70}}})) != null ? stack1 : "")
    + "\n\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_taken") : depth0),{"name":"unless","hash":{},"fn":container.program(3, data, 0),"inverse":container.program(8, data, 0),"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":29,"column":11}}})) != null ? stack1 : "")
    + "\n</div>\n";
},"usePartial":true,"useData":true});

this["SC"]["campaign-actions-bar"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "Take The New Action"
    + container.escapeExpression(lookupProperty(helpers,"s").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"newactions") : depth0),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":2,"column":118},"end":{"line":2,"column":134}}}));
},"3":function(container,depth0,helpers,partials,data) {
    return "Get Started";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"campaign-action-bar-item"),depth0,{"name":"campaign-action-bar-item","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<ul class=\"action-links-bar\" data-ab-length=\""
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"actions") : depth0)) != null ? lookupProperty(stack1,"length") : stack1), depth0))
    + "\">\n<li class=\"action-bar-item smooth-scroll first-item\" data-href=\"#take-action\"><a>"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"newactions") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":2,"column":81},"end":{"line":2,"column":160}}})) != null ? stack1 : "")
    + "</a></li>\n"
    + ((stack1 = lookupProperty(helpers,"each_upto").call(alias1,(depth0 != null ? lookupProperty(depth0,"actions") : depth0),5,{"name":"each_upto","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":5,"column":14}}})) != null ? stack1 : "")
    + "</ul>\n";
},"usePartial":true,"useData":true});

this["SC"]["event-fullpage"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\""
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_prevpaged") : depth0),{"name":"ifor","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":9},"end":{"line":2,"column":46}}})) != null ? stack1 : "")
    + "\" class=\"event-page-link\">&lsaquo; Back to All Events</a>\n<div class=\"row event-details-intro\">\n<div class=\""
    + ((stack1 = lookupProperty(helpers,"any").call(alias1,(depth0 != null ? lookupProperty(depth0,"video_url") : depth0),(depth0 != null ? lookupProperty(depth0,"image_url") : depth0),{"name":"any","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":12},"end":{"line":4,"column":53}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"none").call(alias1,(depth0 != null ? lookupProperty(depth0,"video_url") : depth0),(depth0 != null ? lookupProperty(depth0,"image_url") : depth0),{"name":"none","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":53},"end":{"line":4,"column":96}}})) != null ? stack1 : "")
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"club_name") : depth0),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.program(11, data, 0),"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":19,"column":7}}})) != null ? stack1 : "")
    + "<div class=\"event-title-block\">\n<h1 class=\"h2\">"
    + ((stack1 = alias2((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0)) != null ? stack1 : "")
    + "</h1>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"umbrella") : depth0)) != null ? lookupProperty(stack1,"0") : stack1)) != null ? lookupProperty(stack1,"name") : stack1),{"name":"if","hash":{},"fn":container.program(16, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":22,"column":0},"end":{"line":28,"column":7}}})) != null ? stack1 : "")
    + "</div>\n<ul class=\"event-info\">\n<li class=\"event-date\">\n<span class=\"h4 bold\">"
    + alias3(lookupProperty(helpers,"displayWeekDay").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayWeekDay","hash":{},"data":data,"loc":{"start":{"line":32,"column":22},"end":{"line":32,"column":52}}}))
    + ", "
    + alias3(lookupProperty(helpers,"displayMonth").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayMonth","hash":{},"data":data,"loc":{"start":{"line":32,"column":54},"end":{"line":32,"column":81}}}))
    + " "
    + alias3(lookupProperty(helpers,"displayDay").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),{"name":"displayDay","hash":{},"data":data,"loc":{"start":{"line":32,"column":82},"end":{"line":32,"column":107}}}))
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"end_date") : depth0),{"name":"if","hash":{},"fn":container.program(18, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":32,"column":107},"end":{"line":32,"column":266}}})) != null ? stack1 : "")
    + "</span>\n<span class=\"add-to-cal\"> [ <u>Add to calendar</u> ] <span class=\"calendars\">"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"calendars"),depth0,{"name":"calendars","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</span></span>\n</li>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"start_time") : depth0),{"name":"if","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":35,"column":0},"end":{"line":55,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"location") : depth0),{"name":"if","hash":{},"fn":container.program(37, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":56,"column":0},"end":{"line":60,"column":7}}})) != null ? stack1 : "")
    + "</ul>\n</div>\n<div class=\""
    + ((stack1 = lookupProperty(helpers,"any").call(alias1,(depth0 != null ? lookupProperty(depth0,"video_url") : depth0),(depth0 != null ? lookupProperty(depth0,"image_url") : depth0),{"name":"any","hash":{},"fn":container.program(39, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":63,"column":12},"end":{"line":63,"column":143}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"none").call(alias1,(depth0 != null ? lookupProperty(depth0,"video_url") : depth0),(depth0 != null ? lookupProperty(depth0,"image_url") : depth0),{"name":"none","hash":{},"fn":container.program(41, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":63,"column":143},"end":{"line":63,"column":186}}})) != null ? stack1 : "")
    + "\">\n<figure class=\"event-image-container"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"video_url") : depth0),{"name":"if","hash":{},"fn":container.program(43, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":64,"column":36},"end":{"line":64,"column":70}}})) != null ? stack1 : "")
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"video_url") : depth0),{"name":"if","hash":{},"fn":container.program(45, data, 0),"inverse":container.program(47, data, 0),"data":data,"loc":{"start":{"line":65,"column":0},"end":{"line":78,"column":7}}})) != null ? stack1 : "")
    + "</figure>\n</div>\n</div>\n<div class=\"db-div\"><div class=\"dotted-background\"></div></div>\n<div class=\"row event-details-body\">\n<div class=\"col-8 featured-event\">\n<div class=\"featured-event-inner "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"phone") : depth0),{"name":"if","hash":{},"fn":container.program(59, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":85,"column":33},"end":{"line":85,"column":62}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"all").call(alias1,(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),(depth0 != null ? lookupProperty(depth0,"last_name") : depth0),(depth0 != null ? lookupProperty(depth0,"zipcode") : depth0),(depth0 != null ? lookupProperty(depth0,"email") : depth0),{"name":"all","hash":{},"fn":container.program(61, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":85,"column":63},"end":{"line":85,"column":125}}})) != null ? stack1 : "")
    + "\">\n<div class=\"event-details\">\n<div class=\"event-text\">\n<div class=\"events-body \">\n"
    + ((stack1 = lookupProperty(helpers,"paragraphify").call(alias1,(depth0 != null ? lookupProperty(depth0,"description") : depth0),true,{"name":"paragraphify","hash":{},"data":data,"loc":{"start":{"line":89,"column":0},"end":{"line":89,"column":35}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"sf_campaign_id") : depth0),{"name":"unless","hash":{},"fn":container.program(63, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":89,"column":36},"end":{"line":89,"column":156}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"leader") : depth0),{"name":"if","hash":{},"fn":container.program(65, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":90,"column":0},"end":{"line":95,"column":7}}})) != null ? stack1 : "")
    + "</div>\n</div>\n</div>\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"action_taken") : depth0),{"name":"unless","hash":{},"fn":container.program(70, data, 0),"inverse":container.program(78, data, 0),"data":data,"loc":{"start":{"line":99,"column":0},"end":{"line":130,"column":11}}})) != null ? stack1 : "")
    + "</div>\n</div>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"admin_campaign") : depth0),{"name":"if","hash":{},"fn":container.program(84, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":133,"column":0},"end":{"line":137,"column":7}}})) != null ? stack1 : "")
    + "</div>\n<div class=\"row invite-friends\">\n<div class=\"col-8\">\n<div class=\"invite-buttons\">\nInvite Your Friends: &nbsp;\n<span class=\"button-block\">\n<a class=\"fb-share-btn\" data-url=\""
    + alias3(lookupProperty(helpers,"thisurl").call(alias1,{"name":"thisurl","hash":{},"data":data,"loc":{"start":{"line":144,"column":34},"end":{"line":144,"column":45}}}))
    + "\" title=\"Share on Facebook\"><span class=\"invite fb white-facebook-icon\"> </span></a>\n<a href=\"https://twitter.com/intent/tweet?text="
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0))
    + "&url="
    + alias3(lookupProperty(helpers,"thisurl").call(alias1,{"name":"thisurl","hash":{},"data":data,"loc":{"start":{"line":145,"column":61},"end":{"line":145,"column":72}}}))
    + "\" title=\"Share on Twitter\"><span class=\"invite tw white-twitter-icon\"> </span></a>\n<a href=\"mailto:?subject="
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0))
    + "&body="
    + alias3(lookupProperty(helpers,"thisurl").call(alias1,{"name":"thisurl","hash":{},"data":data,"loc":{"start":{"line":146,"column":40},"end":{"line":146,"column":51}}}))
    + "\"><span class=\"invite em white-envelope-icon\"> </span></a>\n</span>\n</div>\n</div>\n</div>\n";
},"2":function(container,depth0,helpers,partials,data) {
    return "/events";
},"4":function(container,depth0,helpers,partials,data) {
    return "col-6";
},"6":function(container,depth0,helpers,partials,data) {
    return "col-8";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<header class=\"event-header\">\n<h6 class=\"h5 official\"><img src=\"/img/SC-logo-gray.svg\" alt=\"Sierra Club\">Official"
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"sponsor_description") : depth0),"!=","National",{"name":"compare","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":83},"end":{"line":7,"column":177}}})) != null ? stack1 : "")
    + " Event</h6>\n</header>\n";
},"9":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"concat").call(depth0 != null ? depth0 : (container.nullContext || {})," ",(depth0 != null ? lookupProperty(depth0,"sponsor_description") : depth0),{"name":"concat","hash":{},"data":data,"loc":{"start":{"line":7,"column":131},"end":{"line":7,"column":165}}}));
},"11":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<header class=\"user-header\">\n<div class=\"clearfix\">\n<img class=\"user-avatar\" src=\""
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"created_by") : depth0)) != null ? lookupProperty(stack1,"avatar_url") : stack1),{"name":"if","hash":{},"fn":container.program(12, data, 0),"inverse":container.program(14, data, 0),"data":data,"loc":{"start":{"line":12,"column":30},"end":{"line":12,"column":123}}})) != null ? stack1 : "")
    + "\">\n<p>\nCreated By:<br/>\n<span>"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"created_by") : depth0)) != null ? lookupProperty(stack1,"name") : stack1), depth0))
    + "</span>\n</p>\n</div>\n</header>\n";
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"created_by") : depth0)) != null ? lookupProperty(stack1,"avatar_url") : stack1), depth0));
},"14":function(container,depth0,helpers,partials,data) {
    return "/img/user_no_image.png?1";
},"16":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p>\n<a href=\"/events?umbrella_event_id="
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"umbrella") : depth0)) != null ? lookupProperty(stack1,"0") : stack1)) != null ? lookupProperty(stack1,"id") : stack1), depth0))
    + "\" class=\"umbrella-event-listing__partof\" target=\"_blank\">\nPart of "
    + alias2(alias1(((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"umbrella") : depth0)) != null ? lookupProperty(stack1,"0") : stack1)) != null ? lookupProperty(stack1,"name") : stack1), depth0))
    + "\n</a>\n</p>\n";
},"18":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"start_date") : depth0),"!=",(depth0 != null ? lookupProperty(depth0,"end_date") : depth0),{"name":"compare","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":32,"column":123},"end":{"line":32,"column":259}}})) != null ? stack1 : "");
},"19":function(container,depth0,helpers,partials,data) {
    var alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " &mdash; "
    + alias2(lookupProperty(helpers,"displayWeekDay").call(alias1,(depth0 != null ? lookupProperty(depth0,"end_date") : depth0),{"name":"displayWeekDay","hash":{},"data":data,"loc":{"start":{"line":32,"column":169},"end":{"line":32,"column":196}}}))
    + ", "
    + alias2(lookupProperty(helpers,"displayMonth").call(alias1,(depth0 != null ? lookupProperty(depth0,"end_date") : depth0),{"name":"displayMonth","hash":{},"data":data,"loc":{"start":{"line":32,"column":198},"end":{"line":32,"column":223}}}))
    + " "
    + alias2(lookupProperty(helpers,"displayDay").call(alias1,(depth0 != null ? lookupProperty(depth0,"end_date") : depth0),{"name":"displayDay","hash":{},"data":data,"loc":{"start":{"line":32,"column":224},"end":{"line":32,"column":247}}}));
},"21":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"event-time opensans\">\n"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"start_time") : depth0), depth0))
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","eastern",{"name":"compare","hash":{},"fn":container.program(22, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":38,"column":0},"end":{"line":38,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","central",{"name":"compare","hash":{},"fn":container.program(24, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":39,"column":0},"end":{"line":39,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","mountain",{"name":"compare","hash":{},"fn":container.program(26, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":40,"column":0},"end":{"line":40,"column":53}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","pacific",{"name":"compare","hash":{},"fn":container.program(28, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":41,"column":0},"end":{"line":41,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","alaska",{"name":"compare","hash":{},"fn":container.program(30, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":42,"column":0},"end":{"line":42,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","hawaii",{"name":"compare","hash":{},"fn":container.program(32, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":43,"column":0},"end":{"line":43,"column":51}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"end_time") : depth0),{"name":"if","hash":{},"fn":container.program(34, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":44,"column":0},"end":{"line":53,"column":7}}})) != null ? stack1 : "")
    + "</li>\n";
},"22":function(container,depth0,helpers,partials,data) {
    return " ET";
},"24":function(container,depth0,helpers,partials,data) {
    return " CT";
},"26":function(container,depth0,helpers,partials,data) {
    return " MT";
},"28":function(container,depth0,helpers,partials,data) {
    return " PT";
},"30":function(container,depth0,helpers,partials,data) {
    return " AKT";
},"32":function(container,depth0,helpers,partials,data) {
    return " HT";
},"34":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"start_time") : depth0),"!=",(depth0 != null ? lookupProperty(depth0,"end_time") : depth0),{"name":"compare","hash":{},"fn":container.program(35, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":45,"column":0},"end":{"line":52,"column":12}}})) != null ? stack1 : "");
},"35":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "-"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"end_time") : depth0), depth0))
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","eastern",{"name":"compare","hash":{},"fn":container.program(22, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":46,"column":0},"end":{"line":46,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","central",{"name":"compare","hash":{},"fn":container.program(24, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":47,"column":0},"end":{"line":47,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","mountain",{"name":"compare","hash":{},"fn":container.program(26, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":48,"column":0},"end":{"line":48,"column":53}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","pacific",{"name":"compare","hash":{},"fn":container.program(28, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":49,"column":0},"end":{"line":49,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","alaska",{"name":"compare","hash":{},"fn":container.program(30, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":50,"column":0},"end":{"line":50,"column":52}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"timezone") : depth0),"===","hawaii",{"name":"compare","hash":{},"fn":container.program(32, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":51,"column":0},"end":{"line":51,"column":51}}})) != null ? stack1 : "")
    + "\n";
},"37":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"event-location\">\n<div class=\"event-addr\"><a href=\"https://maps.google.com/maps?daddr="
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lat") : depth0), depth0))
    + "%2C"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lng") : depth0), depth0))
    + "&saddr=\" target=\"_blank\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"location") : depth0), depth0))
    + "</a></div>\n</li>\n";
},"39":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"image_url") : depth0),{"name":"if","hash":{},"fn":container.program(40, data, 0),"inverse":container.program(4, data, 0),"data":data,"loc":{"start":{"line":63,"column":40},"end":{"line":63,"column":135}}})) != null ? stack1 : "");
},"40":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,lookupProperty(helpers,"isExternalUrl").call(alias1,(depth0 != null ? lookupProperty(depth0,"image_url") : depth0),{"name":"isExternalUrl","hash":{},"data":data,"loc":{"start":{"line":63,"column":63},"end":{"line":63,"column":88}}}),{"name":"if","hash":{},"fn":container.program(41, data, 0),"inverse":container.program(4, data, 0),"data":data,"loc":{"start":{"line":63,"column":57},"end":{"line":63,"column":115}}})) != null ? stack1 : "");
},"41":function(container,depth0,helpers,partials,data) {
    return "col-3";
},"43":function(container,depth0,helpers,partials,data) {
    return " has-video";
},"45":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"videoWrapper\">"
    + container.escapeExpression(lookupProperty(helpers,"videofromurl").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"video_url") : depth0),{"name":"videofromurl","hash":{},"data":data,"loc":{"start":{"line":65,"column":43},"end":{"line":65,"column":69}}}))
    + "</div>\n";
},"47":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"image_url") : depth0),{"name":"if","hash":{},"fn":container.program(48, data, 0),"inverse":container.program(57, data, 0),"data":data,"loc":{"start":{"line":67,"column":0},"end":{"line":77,"column":7}}})) != null ? stack1 : "");
},"48":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,lookupProperty(helpers,"isExternalUrl").call(alias1,(depth0 != null ? lookupProperty(depth0,"image_url") : depth0),{"name":"isExternalUrl","hash":{},"data":data,"loc":{"start":{"line":68,"column":6},"end":{"line":68,"column":31}}}),{"name":"if","hash":{},"fn":container.program(49, data, 0),"inverse":container.program(51, data, 0),"data":data,"loc":{"start":{"line":68,"column":0},"end":{"line":72,"column":7}}})) != null ? stack1 : "");
},"49":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"map-image"),depth0,{"name":"map-image","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"51":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"image_url") : depth0), depth0))
    + "/@zx_640@zy_360\" alt=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0))
    + "\" class=\"campaign-large-image\" onerror=\"this.onerror=null;this.src='"
    + ((stack1 = lookupProperty(helpers,"ifor").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"image") : depth0),{"name":"ifor","hash":{},"fn":container.program(52, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":71,"column":122},"end":{"line":71,"column":394}}})) != null ? stack1 : "")
    + "';\">\n";
},"52":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"any").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"lat") : depth0),(depth0 != null ? lookupProperty(depth0,"lng") : depth0),{"name":"any","hash":{},"fn":container.program(53, data, 0),"inverse":container.program(55, data, 0),"data":data,"loc":{"start":{"line":71,"column":137},"end":{"line":71,"column":385}}})) != null ? stack1 : "");
},"53":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "https://maps.googleapis.com/maps/api/staticmap?center="
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lat") : depth0), depth0))
    + ","
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lng") : depth0), depth0))
    + "&size=400x400&maptype=roadmap&markers=color:red%7Clabel:C%7C"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lat") : depth0), depth0))
    + ","
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"lng") : depth0), depth0))
    + "&key=AIzaSyD1LqGvj-IryzE6EqCVWaCYMME6CEzxZuc";
},"55":function(container,depth0,helpers,partials,data) {
    return "/img/default_image_small.jpg";
},"57":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"event-image-container-inner\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"map-image"),depth0,{"name":"map-image","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"59":function(container,depth0,helpers,partials,data) {
    return "has-phone";
},"61":function(container,depth0,helpers,partials,data) {
    return " phone-only";
},"63":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"event-info-link\"> "
    + ((stack1 = lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"event-info-link",{"name":"$","hash":{"content":"More Info"},"data":data,"loc":{"start":{"line":89,"column":93},"end":{"line":89,"column":138}}})) != null ? stack1 : "")
    + "</span>";
},"65":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"event-leader\">\n<h5>Event Leader:</h5>\n"
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"leader") : depth0)) != null ? lookupProperty(stack1,"name") : stack1), depth0))
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"leader") : depth0)) != null ? lookupProperty(stack1,"email") : stack1),{"name":"if","hash":{},"fn":container.program(66, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":93,"column":16},"end":{"line":93,"column":127}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"leader") : depth0)) != null ? lookupProperty(stack1,"contact_info") : stack1),{"name":"if","hash":{},"fn":container.program(68, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":93,"column":127},"end":{"line":93,"column":187}}})) != null ? stack1 : "")
    + "\n</div>\n";
},"66":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "| <a href=\"mailto:"
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"leader") : depth0)) != null ? lookupProperty(stack1,"email") : stack1), depth0))
    + "?subject="
    + alias2(lookupProperty(helpers,"encodeuri").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"encodeuri","hash":{},"data":data,"loc":{"start":{"line":93,"column":79},"end":{"line":93,"column":98}}}))
    + "\">"
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"leader") : depth0)) != null ? lookupProperty(stack1,"email") : stack1), depth0))
    + "</a>";
},"68":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " | "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"leader") : depth0)) != null ? lookupProperty(stack1,"contact_info") : stack1), depth0));
},"70":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"reservations_accepted") : depth0),"===","1",{"name":"compare","hash":{},"fn":container.program(71, data, 0),"inverse":container.program(76, data, 0),"data":data,"loc":{"start":{"line":100,"column":0},"end":{"line":112,"column":12}}})) != null ? stack1 : "");
},"71":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"email") : depth0),{"name":"if","hash":{},"fn":container.program(72, data, 0),"inverse":container.program(74, data, 0),"data":data,"loc":{"start":{"line":101,"column":0},"end":{"line":109,"column":7}}})) != null ? stack1 : "");
},"72":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row rsvp-block\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"events-rsvp"),depth0,{"name":"events-rsvp","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"74":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row rsvp-block rsvp-above show-rsvp\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"events-rsvp"),depth0,{"name":"events-rsvp","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"76":function(container,depth0,helpers,partials,data) {
    return "<div class=\"row rsvp-block\"><p>RSVP not required for this event</p></div>\n";
},"78":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"rsvp-block action-confirmation"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"sf_campaign_id") : depth0),{"name":"if","hash":{},"fn":container.program(79, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":114,"column":42},"end":{"line":114,"column":83}}})) != null ? stack1 : "")
    + "\">\n<div class=\"bubble-pale\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"events-confirmation"),depth0,{"name":"events-confirmation","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"admin_campaign") : depth0),{"name":"if","hash":{},"fn":container.program(81, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":117,"column":0},"end":{"line":127,"column":7}}})) != null ? stack1 : "")
    + "</div>\n</div>\n";
},"79":function(container,depth0,helpers,partials,data) {
    return " sf-campaign";
},"81":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<hr>\n<p>\nIn the meantime, here's even more you can do on this issue right now:\n</p>\n<div class=\"related-campaign\">\n<div class=\"related-campaign-link\">\n"
    + ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"admin_campaign") : depth0),{"name":"with","hash":{},"fn":container.program(82, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":124,"column":0},"end":{"line":124,"column":53}}})) != null ? stack1 : "")
    + "\n</div>\n</div>\n";
},"82":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"campaign-tiles"),depth0,{"name":"campaign-tiles","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"84":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"col-4\">\n"
    + ((stack1 = lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"related-campaign",{"name":"$","hash":{"col":"3"},"data":data,"loc":{"start":{"line":135,"column":0},"end":{"line":135,"column":34}}})) != null ? stack1 : "")
    + "\n</div>\n";
},"86":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"gridsection","hash":{},"fn":container.program(87, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":153,"column":0},"end":{"line":177,"column":16}}})) != null ? stack1 : "");
},"87":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"pac-disclaimer\">\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"funding_type") : depth0)) != null ? lookupProperty(stack1,"name") : stack1),"===","SCPC IE",{"name":"compare","hash":{},"fn":container.program(88, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":155,"column":0},"end":{"line":157,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"funding_type") : depth0)) != null ? lookupProperty(stack1,"name") : stack1),"===","SCVEF 527 - Voter Education",{"name":"compare","hash":{},"fn":container.program(90, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":158,"column":0},"end":{"line":160,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"funding_type") : depth0)) != null ? lookupProperty(stack1,"name") : stack1),"===","SCIA - Federal Super PAC",{"name":"compare","hash":{},"fn":container.program(92, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":161,"column":0},"end":{"line":163,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"funding_type") : depth0)) != null ? lookupProperty(stack1,"name") : stack1),"===","SCPC Coordinated",{"name":"compare","hash":{},"fn":container.program(94, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":164,"column":0},"end":{"line":166,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"funding_type") : depth0)) != null ? lookupProperty(stack1,"name") : stack1),"===","State PAC IE",{"name":"compare","hash":{},"fn":container.program(96, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":167,"column":0},"end":{"line":169,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"funding_type") : depth0)) != null ? lookupProperty(stack1,"name") : stack1),"===","State PAC Coordinated",{"name":"compare","hash":{},"fn":container.program(96, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":170,"column":0},"end":{"line":172,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"club_name") : depth0),{"name":"unless","hash":{},"fn":container.program(98, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":173,"column":0},"end":{"line":175,"column":11}}})) != null ? stack1 : "")
    + "</div>\n";
},"88":function(container,depth0,helpers,partials,data) {
    return "<p>Paid for by the Sierra Club Political Committee (www.sierraclub.org) and not authorized by any candidate or candidate's committee.</p>\n";
},"90":function(container,depth0,helpers,partials,data) {
    return "<p>Paid for by the Sierra Club Voter Education Fund, which seeks to raise key environmental issues in the discussions around elections, and encourage the public to find out more about candidates' positions on key environmental issues.</p>\n";
},"92":function(container,depth0,helpers,partials,data) {
    return "<p>Paid for by Sierra Club Independent Action (www.sierraclub.org) and not authorized by any candidate or candidate's committee.</p>\n";
},"94":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p>Paid for by the Sierra Club Political Committee (www.sierraclub.org) and authorized by "
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"candidate_committee") : depth0), depth0))
    + ".</p>\n";
},"96":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p>"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"candidate_committee") : depth0), depth0))
    + "</p>\n";
},"98":function(container,depth0,helpers,partials,data) {
    return "<p>This event is not affiliated with, sponsored, or endorsed by the Sierra Club. The event and all its activities are the sole responsibility of the event owner. Participants attend at their own risk.</p>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(alias1,"event-details-page",{"name":"gridsection","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":151,"column":16}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"any").call(alias1,lookupProperty(helpers,"compare").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"funding_type") : depth0)) != null ? lookupProperty(stack1,"name") : stack1),"===","SCPC IE",{"name":"compare","hash":{},"data":data,"loc":{"start":{"line":152,"column":7},"end":{"line":152,"column":50}}}),lookupProperty(helpers,"compare").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"funding_type") : depth0)) != null ? lookupProperty(stack1,"name") : stack1),"===","SCVEF 527 - Voter Education",{"name":"compare","hash":{},"data":data,"loc":{"start":{"line":152,"column":51},"end":{"line":152,"column":114}}}),lookupProperty(helpers,"compare").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"funding_type") : depth0)) != null ? lookupProperty(stack1,"name") : stack1),"===","SCIA - Federal Super PAC",{"name":"compare","hash":{},"data":data,"loc":{"start":{"line":152,"column":115},"end":{"line":152,"column":175}}}),lookupProperty(helpers,"compare").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"funding_type") : depth0)) != null ? lookupProperty(stack1,"name") : stack1),"===","SCPC Coordinated",{"name":"compare","hash":{},"data":data,"loc":{"start":{"line":152,"column":176},"end":{"line":152,"column":228}}}),lookupProperty(helpers,"compare").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"funding_type") : depth0)) != null ? lookupProperty(stack1,"name") : stack1),"===","State PAC IE",{"name":"compare","hash":{},"data":data,"loc":{"start":{"line":152,"column":229},"end":{"line":152,"column":277}}}),lookupProperty(helpers,"compare").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"funding_type") : depth0)) != null ? lookupProperty(stack1,"name") : stack1),"===","State PAC Coordinated",{"name":"compare","hash":{},"data":data,"loc":{"start":{"line":152,"column":278},"end":{"line":152,"column":335}}}),lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"club_name") : depth0),"===","",{"name":"compare","hash":{},"data":data,"loc":{"start":{"line":152,"column":336},"end":{"line":152,"column":364}}}),{"name":"any","hash":{},"fn":container.program(86, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":152,"column":0},"end":{"line":178,"column":8}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true});

this["SC"]["campaigns-list"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"campaigns-grid"),depth0,{"name":"campaigns-grid","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"usePartial":true,"useData":true});

this["SC"]["campaigns-search"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"campaigns-search"),depth0,{"name":"campaigns-search","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + ((stack1 = container.invokePartial(lookupProperty(partials,"campaigns-grid"),depth0,{"name":"campaigns-grid","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + ((stack1 = container.invokePartial(lookupProperty(partials,"pagination"),depth0,{"name":"pagination","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"usePartial":true,"useData":true});

this["SC"]["campaigns-your-impact"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"campaigns-grid\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"data") : depth0),{"name":"each","hash":{},"fn":container.program(2, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":132}}})) != null ? stack1 : "")
    + "\n</div>\n";
},"2":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\""
    + alias2(lookupProperty(helpers,"switch").call(alias1,(data && lookupProperty(data,"index")),(data && lookupProperty(data,"last")),{"name":"switch","hash":{},"data":data,"loc":{"start":{"line":3,"column":31},"end":{"line":3,"column":54}}}))
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(data && lookupProperty(data,"last")),{"name":"if","hash":{},"fn":container.program(3, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":54},"end":{"line":3,"column":84}}})) != null ? stack1 : "")
    + " tile-item\">"
    + alias2(lookupProperty(helpers,"dynamicpartial").call(alias1,depths[1],{"name":"dynamicpartial","hash":{},"data":data,"loc":{"start":{"line":3,"column":96},"end":{"line":3,"column":117}}}))
    + "</div>";
},"3":function(container,depth0,helpers,partials,data) {
    return " last-item";
},"5":function(container,depth0,helpers,partials,data) {
    return "<div class=\"sadtrees\">\n<span class=\"sadtree\"></span>\n<h1>You have not supported any campaigns yet.</h1>\n<h6>Don't worry&mdash;it's an easy fix.</h6>\n<p>The best way to get started is to just get started!</p>\n\n<a href=\"/campaigns\" class=\"button\">Find Campaigns</a>\n</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"data") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.program(5, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":14,"column":7}}})) != null ? stack1 : "");
},"useData":true,"useDepths":true});

this["SC"]["category-chooser"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li>"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"issue-checkbox"),depth0,{"name":"issue-checkbox","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<form action=\"/supporter-home\" data-formaction=\"issue-selection\" method=\"POST\" class=\"check-labels page-form\" role=\"form\">\n<ol class=\"checkbox-group category-icon-labels row\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"categories") : depth0),{"name":"each","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":3,"column":58}}})) != null ? stack1 : "")
    + "\n<li class=\"surprise-me\"><label class=\"odd third\">Surprise me!</label></li>\n</ol>\n<button type=\"submit\" class=\"button\">Submit</button>\n<input type=\"hidden\" name=\"redirect_on_success\" value=\"/supporter-home\">\n</form>\n<p class=\"skip-step\"><a href=\"/supporter-home\">Skip this</a></p>\n";
},"usePartial":true,"useData":true});

this["SC"]["actions-remaining"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"action_took") : depth0),{"name":"unless","hash":{},"fn":container.program(2, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":13,"column":27},"end":{"line":13,"column":155}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"/campaigns/"
    + alias2(alias1((depths[2] != null ? lookupProperty(depths[2],"slug") : depths[2]), depth0))
    + "/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"action_type") : depth0), depth0))
    + "/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" class=\"button\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"phrase") : depth0), depth0))
    + "</a><br>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"col-8\">\n<figure class=\"recommended-campaign\">\n<a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" class=\"campaign-image\">\n<img src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"image") : depth0), depth0))
    + "/@zx_140@zy_140\" alt=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0))
    + "\">\n</a>\n<figcaption>\n<a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" class=\"campaign-name\">"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0)) != null ? stack1 : "")
    + "</a>\n<span class=\"supporter-count\"><strong>"
    + alias2(lookupProperty(helpers,"commafy").call(alias3,(depth0 != null ? lookupProperty(depth0,"num_supporters_current") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":8,"column":38},"end":{"line":8,"column":72}}}))
    + "</strong> Supporters</span>\n</figcaption>\n</figure>\n</div>\n<div class=\"col-4 continue-campaign\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias3,lookupProperty(helpers,"limit").call(alias3,(depth0 != null ? lookupProperty(depth0,"actions") : depth0),3,{"name":"limit","hash":{},"data":data,"loc":{"start":{"line":13,"column":8},"end":{"line":13,"column":25}}}),{"name":"each","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":13,"column":0},"end":{"line":13,"column":164}}})) != null ? stack1 : "")
    + "\n</div>\n";
},"useData":true,"useDepths":true});

this["SC"]["impact-graph"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return " cat-empty";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, alias3=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"campaign prog-"
    + alias2(lookupProperty(helpers,"progress").call(alias1,(depth0 != null ? lookupProperty(depth0,"taken") : depth0),(depth0 != null ? lookupProperty(depth0,"available") : depth0),{"name":"progress","hash":{},"data":data,"loc":{"start":{"line":3,"column":26},"end":{"line":3,"column":54}}}))
    + "\">\n<a href=\"/campaigns/"
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(data && lookupProperty(data,"first")),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":30},"end":{"line":4,"column":69}}})) != null ? stack1 : "")
    + ">\n<span class=\"spaceleft\" style=\"width:"
    + alias2(lookupProperty(helpers,"progress").call(alias1,(depth0 != null ? lookupProperty(depth0,"taken") : depth0),(depth0 != null ? lookupProperty(depth0,"available") : depth0),"0.4",{"name":"progress","hash":{},"data":data,"loc":{"start":{"line":5,"column":37},"end":{"line":5,"column":71}}}))
    + "%\"></span>\n<span class=\"progress-bar\" style=\"width:"
    + alias2(lookupProperty(helpers,"progress").call(alias1,(depth0 != null ? lookupProperty(depth0,"taken") : depth0),(depth0 != null ? lookupProperty(depth0,"available") : depth0),{"name":"progress","hash":{},"data":data,"loc":{"start":{"line":6,"column":40},"end":{"line":6,"column":68}}}))
    + "%\">\n<p class=\"cname\">"
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "</p>\n<p class=\"actionstaken\"><span class=\"first-block\"><span>"
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"taken") : depth0), depth0))
    + "</span> of <span>"
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"available") : depth0), depth0))
    + "</span></span> actions <span class=\"bottom\">taken</span></p>\n</span>\n<i class=\"category-icon "
    + alias2(alias3((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "-icon\"></i>\n</a>\n</div>\n";
},"4":function(container,depth0,helpers,partials,data) {
    return "class=\"mobile-bar\"";
},"6":function(container,depth0,helpers,partials,data) {
    return "<div class=\"emptycats\">\n<p><b>Hey there!</b></p>\n<p>Looks like you haven't gotten started yet.</p>\n<p>Pick a topic from the left and then check back to see your impact.</p>\n</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"catsofar"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"no_actions") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":20},"end":{"line":1,"column":55}}})) != null ? stack1 : "")
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"categories") : depth0),{"name":"each","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":13,"column":9}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"no_actions") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":0},"end":{"line":20,"column":7}}})) != null ? stack1 : "")
    + "</div>\n";
},"useData":true});

this["SC"]["impact-icons"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return " cat-empty";
},"3":function(container,depth0,helpers,partials,data) {
    var alias1=container.escapeExpression, alias2=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"campaign prog-"
    + alias1(lookupProperty(helpers,"progress").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"taken") : depth0),(depth0 != null ? lookupProperty(depth0,"available") : depth0),{"name":"progress","hash":{},"data":data,"loc":{"start":{"line":5,"column":26},"end":{"line":5,"column":54}}}))
    + " campaign-cat\">\n<a href=\"/campaigns/"
    + alias1(alias2((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\">\n<i class=\"category-icon "
    + alias1(alias2((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "-icon\"></i>\n<div class=\"hover-stats\">\n<p class=\"cname\">"
    + alias1(alias2((depth0 != null ? lookupProperty(depth0,"taken") : depth0), depth0))
    + " "
    + alias1(alias2((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "</p>\n</div>\n</a>\n</div>\n";
},"5":function(container,depth0,helpers,partials,data) {
    return "<div class=\"emptycats\">\n<p>Pick a topic above and then check back to see your impact.</p>\n</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"impact-graph\">\n<div class=\"impact-icons catsofar"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"no_actions") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":33},"end":{"line":2,"column":68}}})) != null ? stack1 : "")
    + "\">\n\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"categories") : depth0),{"name":"each","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":13,"column":9}}})) != null ? stack1 : "")
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"no_actions") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":15,"column":0},"end":{"line":19,"column":7}}})) != null ? stack1 : "")
    + "\n</div>\n</div>\n";
},"useData":true});

this["SC"]["your-impact"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"avatar") : depth0), depth0));
},"3":function(container,depth0,helpers,partials,data) {
    return "/img/user_no_image.png?1";
},"5":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<br>"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"city") : depth0), depth0))
    + ", "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"state") : depth0), depth0));
},"7":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ": <i>"
    + container.escapeExpression(lookupProperty(helpers,"titleize").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"recruit_names") : depth0),{"name":"titleize","hash":{},"data":data,"loc":{"start":{"line":43,"column":26},"end":{"line":43,"column":52}}}))
    + "</i>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"supporter-welcome clearfix\">\n<div class=\"avatar-image\">\n<img class=\"user-avatar\" src=\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"avatar") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":3,"column":30},"end":{"line":3,"column":93}}})) != null ? stack1 : "")
    + "\">\n</div>\n<div class=\"supporter-welcome-intro\">\n<strong>Hey "
    + alias2(container.lambda((depth0 != null ? lookupProperty(depth0,"first_name") : depth0), depth0))
    + "!</strong>\n"
    + ((stack1 = lookupProperty(helpers,"all").call(alias1,(depth0 != null ? lookupProperty(depth0,"city") : depth0),(depth0 != null ? lookupProperty(depth0,"state") : depth0),{"name":"all","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":7,"column":50}}})) != null ? stack1 : "")
    + "\n</div>\n</div>\n<div class=\"supporter-stats\">\n\n<figure class=\"supporter-stat one\">\n<span class=\"ss-number\">"
    + alias2(lookupProperty(helpers,"commafy").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_actions") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":13,"column":24},"end":{"line":13,"column":47}}}))
    + "</span><figcaption>\n<strong>action"
    + alias2(lookupProperty(helpers,"s").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_actions") : depth0),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":14,"column":14},"end":{"line":14,"column":31}}}))
    + "</strong> taken\n</figcaption>\n</figure>\n\n<figure class=\"supporter-stat two\">\n<span class=\"ss-number\">"
    + alias2(lookupProperty(helpers,"commafy").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_campaigns") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":19,"column":24},"end":{"line":19,"column":49}}}))
    + "</span><figcaption>\n<span>\n<strong>campaign"
    + alias2(lookupProperty(helpers,"s").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_campaigns") : depth0),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":21,"column":16},"end":{"line":21,"column":35}}}))
    + "</strong>\n<span class=\"tt-anchor\">supported<span class=\"tooltip\">?\n<span class=\"tooltip-bubble\">\n<span class=\"closebutton\"></span>\n<p>Based on your email address, we can see if you've taken action with Sierra Club before. If you have, we've included the campaigns you supported in this total to give you credit for your pre-AddUp advocacy action history. The actions you took through these campaigns are reflected in the \"Actions Taken\" total above.</p>\n</span>\n</span>\n</span>\n</span>\n</figcaption>\n</figure>\n\n<figure class=\"supporter-stat three\">\n<span class=\"ss-number\">"
    + alias2(lookupProperty(helpers,"commafy").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_recruits") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":34,"column":24},"end":{"line":34,"column":48}}}))
    + "</span><figcaption>\n<strong>Friend"
    + alias2(lookupProperty(helpers,"s").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_recruits") : depth0),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":35,"column":14},"end":{"line":35,"column":32}}}))
    + "</strong> Successfully\n<span class=\"tt-anchor\">Recruited<span class=\"tooltip\">?\n<span class=\"tooltip-bubble\">\n<span class=\"closebutton\"></span>\n<p>Your numbers will add up here each time a friend you recruited takes their first action on AddUp!</p>\n</span>\n</span>\n</span>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"recruit_names") : depth0),{"name":"if","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":43,"column":0},"end":{"line":43,"column":63}}})) != null ? stack1 : "")
    + "\n<a href=\"/share\" class=\"recruit-more\"><b class=\"recruit-icon\"></b>recruit more</a>\n</figcaption>\n</figure>\n</div>\n";
},"useData":true});

this["SC"]["events-feed"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"marquee-header",{"name":"$","hash":{"title":"Sierra Club Events and Outings"},"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":2,"column":61}}}))
    + "\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"events") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":3,"column":7}}})) != null ? stack1 : "");
},"useData":true});

this["SC"]["events-header"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"events-header"),depth0,{"name":"events-header","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"usePartial":true,"useData":true});

this["SC"]["events-search"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"events-search-large"),depth0,{"name":"events-search-large","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "<div class=\"events-grid-and-map\">\n<div class=\"events-cover\"></div><!-- adding this to hide the repeatedly reloading map and grid -->\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"events-map-data"),depth0,{"name":"events-map-data","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + ((stack1 = container.invokePartial(lookupProperty(partials,"events-grid"),depth0,{"name":"events-grid","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + ((stack1 = container.invokePartial(lookupProperty(partials,"events-map"),depth0,{"name":"events-map","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"pagination"),depth0,{"name":"pagination","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"usePartial":true,"useData":true});

this["SC"]["featured-and-more-events"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"events-search"),depth0,{"name":"events-search","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "\n<div class=\"event-list "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">=","768",{"name":"compare","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":6,"column":23},"end":{"line":6,"column":71}}})) != null ? stack1 : "")
    + "\">\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">=","768",{"name":"compare","hash":{},"fn":container.program(4, data, 0),"inverse":container.program(6, data, 0),"data":data,"loc":{"start":{"line":7,"column":0},"end":{"line":11,"column":12}}})) != null ? stack1 : "")
    + "</div>\n";
},"2":function(container,depth0,helpers,partials,data) {
    return "w-swiper";
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"events-slider"),depth0,{"name":"events-slider","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"more-events-stacked"),depth0,{"name":"more-events-stacked","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row\"><div class=\"col-2\"></div><div class=\"featured-event event-display-row\">"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"featured-event"),depth0,{"name":"featured-event","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div><div class=\"col-2\"></div></div>\n\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"action_taken") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":13,"column":11}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true});

this["SC"]["forgot-password-form"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "class=\"inactive\"";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<section class=\"registration-form forgot-pwd-form\">\n\n<form data-formaction=\"moleform\" method=\"POST\" action=\"/passwords\" data-message=\"hard\" "
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"errors") : depth0)) != null ? lookupProperty(stack1,"oldvals") : stack1)) != null ? lookupProperty(stack1,"email") : stack1),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":87},"end":{"line":3,"column":146}}})) != null ? stack1 : "")
    + ">\n<header class=\"registration-header\">\n<p>Forgot your password?</p>\n<h2>Enter Your Email Address</h2>\n</header>\n<ul class=\"two-column-form\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"email_field"),depth0,{"name":"email_field","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "<li class=\"submit-button\">\n<button class=\"button action-button\" type=\"submit\">Go!</button>\n</li>\n</ul>\n</form>\n</section>\n";
},"usePartial":true,"useData":true});

this["SC"]["full-campaign"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "col-6";
},"3":function(container,depth0,helpers,partials,data) {
    return "no-image";
},"5":function(container,depth0,helpers,partials,data) {
    return " official\"><img src=\"/img/SC-logo-gray.svg\" alt=\"Sierra Club";
},"7":function(container,depth0,helpers,partials,data) {
    return " star-sides";
},"9":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),"<",768,{"name":"compare","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":50},"end":{"line":5,"column":113}}})) != null ? stack1 : "");
},"10":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"campaign-large-image"),depth0,{"name":"campaign-large-image","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"#\" class=\"more-button-after hide-when-expanded\" data-expand=\"why-this-matters\">Read&nbsp;More</a></p>\n</div><div data-expandable=\"why-this-matters\" class=\"expandable closed why-this-matters base\">\n<h2>Why This Matters</h2>\n"
    + container.escapeExpression(lookupProperty(helpers,"paragraphify").call(alias1,(depth0 != null ? lookupProperty(depth0,"why_it_matters") : depth0),{"name":"paragraphify","hash":{},"data":data,"loc":{"start":{"line":9,"column":0},"end":{"line":9,"column":31}}}))
    + "\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"learn_more_url") : depth0),{"name":"if","hash":{},"fn":container.program(13, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":10,"column":0},"end":{"line":10,"column":161}}})) != null ? stack1 : "")
    + "\n<button class=\"less-button\" data-expand=\"why-this-matters\">Read&nbsp;Less</button>\n";
},"13":function(container,depth0,helpers,partials,data) {
    var alias1=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p><strong>Learn More: </strong><a target=\"_blank\" href=\""
    + alias1(container.lambda((depth0 != null ? lookupProperty(depth0,"learn_more_url") : depth0), depth0))
    + "\" class=\"more-link\">"
    + alias1(lookupProperty(helpers,"prettylink").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"learn_more_url") : depth0),{"name":"prettylink","hash":{},"data":data,"loc":{"start":{"line":10,"column":117},"end":{"line":10,"column":146}}}))
    + "</a></p>";
},"15":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"h3 block\">"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"actions_this_week") : depth0), depth0))
    + "</div>\n<div class=\"h6 block opensans\"><strong>Actions</strong>this week</div>\n";
},"17":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"h3 block\">"
    + container.escapeExpression(lookupProperty(helpers,"countdown").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"end_date") : depth0),{"name":"countdown","hash":{},"data":data,"loc":{"start":{"line":19,"column":22},"end":{"line":19,"column":44}}}))
    + "</div>\n<div class=\"h6 block opensans\"><strong>Days</strong>to Go</div>\n";
},"19":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"h3 block\">"
    + alias2(lookupProperty(helpers,"commafy").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_supporters_friends") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":29,"column":22},"end":{"line":29,"column":56}}}))
    + "</div>\n<div class=\"h6 block opensans\"><strong>Friend"
    + alias2(lookupProperty(helpers,"s").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_supporters_friends") : depth0),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":30,"column":45},"end":{"line":30,"column":73}}}))
    + "</strong>"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_supporters_friends") : depth0),"===",1,{"name":"compare","hash":{},"fn":container.program(20, data, 0),"inverse":container.program(22, data, 0),"data":data,"loc":{"start":{"line":30,"column":82},"end":{"line":30,"column":152}}})) != null ? stack1 : "")
    + "&nbsp;taken&nbsp;action</div>\n";
},"20":function(container,depth0,helpers,partials,data) {
    return "has";
},"22":function(container,depth0,helpers,partials,data) {
    return "have";
},"24":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"h3 block\">"
    + container.escapeExpression(lookupProperty(helpers,"numabbrv").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"stat_supporters") : depth0),{"name":"numabbrv","hash":{},"data":data,"loc":{"start":{"line":32,"column":22},"end":{"line":32,"column":50}}}))
    + "</div>\n<div class=\"h6 block opensans\"><strong>AddUp</strong>Supporters</div>\n";
},"26":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">",767,{"name":"compare","hash":{},"fn":container.program(27, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":36,"column":25},"end":{"line":36,"column":128}}})) != null ? stack1 : "");
},"27":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"col-6 campaign-media\">"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"campaign-large-image"),depth0,{"name":"campaign-large-image","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row campaign-intro\"><div class=\"campaign-summary "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"image") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":1,"column":61},"end":{"line":1,"column":102}}})) != null ? stack1 : "")
    + "\">\n<header class=\"campaign-header center\">\n<h6 class=\"h5"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"club_id_name") : depth0),{"name":"unless","hash":{},"fn":container.program(5, data, 0),"inverse":container.program(7, data, 0),"data":data,"loc":{"start":{"line":3,"column":13},"end":{"line":3,"column":127}}})) != null ? stack1 : "")
    + "\">Official"
    + alias2(lookupProperty(helpers,"concat").call(alias1," ",(depth0 != null ? lookupProperty(depth0,"club_id_name") : depth0),{"name":"concat","hash":{},"data":data,"loc":{"start":{"line":3,"column":137},"end":{"line":3,"column":164}}}))
    + "&nbsp;Campaign</h6>\n</header>\n<h1>"
    + alias2(lookupProperty(helpers,"titleize").call(alias1,lookupProperty(helpers,"capCase").call(alias1,(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"capCase","hash":{},"data":data,"loc":{"start":{"line":5,"column":15},"end":{"line":5,"column":30}}}),{"name":"titleize","hash":{},"data":data,"loc":{"start":{"line":5,"column":4},"end":{"line":5,"column":32}}}))
    + "</h1>"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"image") : depth0),{"name":"if","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":37},"end":{"line":5,"column":120}}})) != null ? stack1 : "")
    + "<div class=\"our-goal base\">\n<p>"
    + alias2(container.lambda((depth0 != null ? lookupProperty(depth0,"goal") : depth0), depth0))
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"why_it_matters") : depth0),{"name":"if","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":6,"column":12},"end":{"line":12,"column":7}}})) != null ? stack1 : "")
    + "</div><div class=\"campaign-status-boxes\">\n<figure class=\"col col-4\">\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"end_date") : depth0),"isInvalidOr","2100-01-01 00:00:00",{"name":"compare","hash":{},"fn":container.program(15, data, 0),"inverse":container.program(17, data, 0),"data":data,"loc":{"start":{"line":15,"column":0},"end":{"line":21,"column":12}}})) != null ? stack1 : "")
    + "</figure>\n<figure class=\"col col-4\">\n<div class=\"h3 block\">"
    + alias2(lookupProperty(helpers,"commafy").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_supporters_current") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":24,"column":22},"end":{"line":24,"column":56}}}))
    + "</div>\n<div class=\"h6 block opensans\"><strong>Supporter"
    + alias2(lookupProperty(helpers,"s").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_supporters_current") : depth0),{"name":"s","hash":{},"data":data,"loc":{"start":{"line":25,"column":48},"end":{"line":25,"column":76}}}))
    + "</strong> of "
    + alias2(lookupProperty(helpers,"commafy").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_supporters_goal") : depth0),{"name":"commafy","hash":{},"data":data,"loc":{"start":{"line":25,"column":89},"end":{"line":25,"column":120}}}))
    + " Goal</div>\n</figure>\n<figure class=\"col col-4\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"num_supporters_friends") : depth0),{"name":"if","hash":{},"fn":container.program(19, data, 0),"inverse":container.program(24, data, 0),"data":data,"loc":{"start":{"line":28,"column":0},"end":{"line":34,"column":7}}})) != null ? stack1 : "")
    + "</figure>\n</div></div>"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"image") : depth0),{"name":"if","hash":{},"fn":container.program(26, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":36,"column":12},"end":{"line":36,"column":135}}})) != null ? stack1 : "")
    + "</div>\n";
},"usePartial":true,"useData":true});

this["SC"]["hashtags"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"statuses") : depth0)) != null ? lookupProperty(stack1,"0") : stack1),{"name":"with","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":9,"column":9}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"twitter-hashtags",{"name":"gridsection","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":8,"column":16}}})) != null ? stack1 : "");
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"hashtag-tweet\">\n"
    + ((stack1 = lookupProperty(helpers,"twitterlinkify").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"text") : depth0),{"name":"twitterlinkify","hash":{},"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":5,"column":25}}})) != null ? stack1 : "")
    + "\n<a href=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"link") : depth0), depth0))
    + "\" target=\"_blank\" class=\"hashtag-tweet-icon\"></a>\n</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"statuses") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":10,"column":7}}})) != null ? stack1 : "");
},"useData":true});

this["SC"]["impact-single"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"slide "
    + alias2(lookupProperty(helpers,"switch").call(alias1,(data && lookupProperty(data,"index")),{"name":"switch","hash":{},"data":data,"loc":{"start":{"line":4,"column":18},"end":{"line":4,"column":35}}}))
    + " swiper-slide\">"
    + alias2(lookupProperty(helpers,"dynamicpartial").call(alias1,depths[1],{"name":"dynamicpartial","hash":{},"data":data,"loc":{"start":{"line":4,"column":50},"end":{"line":4,"column":71}}}))
    + "</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"swiper-container single-impact\" data-slidenum=\""
    + container.escapeExpression(lookupProperty(helpers,"ww").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),(depth0 != null ? lookupProperty(depth0,"template") : depth0),{"name":"ww","hash":{},"data":data,"loc":{"start":{"line":1,"column":59},"end":{"line":1,"column":79}}}))
    + "\">\n<div class=\"swiper-wrapper\">\n"
    + ((stack1 = lookupProperty(helpers,"with").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"data") : depth0)) != null ? lookupProperty(stack1,"0") : stack1),{"name":"with","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":5,"column":9}}})) != null ? stack1 : "")
    + "</.[0]div>\n</div>\n";
},"useData":true,"useDepths":true});

this["SC"]["modal-contact"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "value=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"first_name") : depth0), depth0))
    + " "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"last_name") : depth0), depth0))
    + "\"";
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "value=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"email") : depth0), depth0))
    + "\"";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<section class=\"registration-form\">\n<button class=\"modal-close\">+</button>\n<form name=\"contact-us-form\" method=\"post\" action=\"https://webto.salesforce.com/servlet/servlet.WebToCase\" target=\"temporaryiframe\" class=\"modal-contact\">\n<header class=\"registration-header\">\n<h2>Send us your feedback</h2>\n<p>Need help? Start here to get support</p>\n</header>\n<input type=\"hidden\" name=\"recordType\" value=\"012i0000000xmgt\">\n<input type=\"hidden\" name=\"orgid\" value=\"00Di0000000b7eb\">\n<input type=\"hidden\" name=\"00Ni000000E2CQh\" value=\"AddUP\">\n<input type=\"hidden\" name=\"00Ni000000FNQLx\" id=\"page_url\" value=\""
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"userEnv") : depth0)) != null ? lookupProperty(stack1,"url") : stack1), depth0))
    + "\">\n<input type=\"hidden\" name=\"00Ni000000FNQM7\" id=\"browser_type\" value=\""
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"userEnv") : depth0)) != null ? lookupProperty(stack1,"browser") : stack1), depth0))
    + "\">\n<input type=\"hidden\" name=\"00Ni000000FNQMg\" id=\"browser_version\" value=\""
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"userEnv") : depth0)) != null ? lookupProperty(stack1,"screen") : stack1), depth0))
    + "\">\n<input type=\"hidden\" name=\"00Ni000000FpfVG\" id=\"device_type\" value=\""
    + alias2(alias1(((stack1 = (depth0 != null ? lookupProperty(depth0,"userEnv") : depth0)) != null ? lookupProperty(stack1,"device") : stack1), depth0))
    + "\">\n<ul class=\"one-column-form\">\n<li>\n<label for=\"name-field"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Name</label>\n<input type=\"text\" name=\"name\" "
    + ((stack1 = lookupProperty(helpers,"any").call(alias3,(depth0 != null ? lookupProperty(depth0,"first_name") : depth0),(depth0 != null ? lookupProperty(depth0,"last_name") : depth0),{"name":"any","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":18,"column":31},"end":{"line":18,"column":104}}})) != null ? stack1 : "")
    + " id=\"name-field"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" maxlength=\"80\" required=\"required\" tabindex=\"1\"></li>\n<li>\n<label for=\"email-feild"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\">Email address</label>\n<input type=\"email\" name=\"email\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"email") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":21,"column":33},"end":{"line":21,"column":70}}})) != null ? stack1 : "")
    + " id=\"email-field"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "\" maxlength=\"80\" required=\"required\" tabindex=\"2\"></li>\n<li class=\"radio-buttons\">\n<label>Issue type</label>\n<span><input type=\"radio\" name=\"type\" value=\"Question\" id=\"topic-selection1\" tabindex=\"3\">\n<label for=\"topic-selection1\">Question</label></span>\n<span><input type=\"radio\" name=\"type\" value=\"Problem\" id=\"topic-selection2\" tabindex=\"4\">\n<label for=\"topic-selection2\">Problem</label></span>\n<span><input type=\"radio\" name=\"type\" value=\"Feature Request\" id=\"topic-selection3\" tabindex=\"5\">\n<label for=\"topic-selection3\">Feature request</label></span>\n</li>\n<li>\n<label for=\"subject-field\">Subject</label>\n<input type=\"text\" name=\"subject\" id=\"subject-field\" size=\"20\" required=\"required\" autocomplete=\"off\" tabindex=\"6\">\n</li>\n<li>\n<label for=\"description-field\">Detailed description</label>\n<textarea name=\"description\" id=\"description-field\" required=\"required\" rows=\"4\" tabindex=\"7\"></textarea>\n</li>\n<li class=\"recaptcha-row\"><div class=\"g-recaptcha\"></div></li>\n<li class=\"submit\"><button type=\"submit\" class=\"button action-button\">Tell Us</button></li>\n</ul>\n</form>\n<iframe border=\"0\" scrolling=\"0\" id=\"temporaryiframe\" name=\"temporaryiframe\" class=\"hiddenIF\"></iframe>\n</section>\n";
},"useData":true});

this["SC"]["nav-main"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_debug") : depth0),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":7,"column":7}}})) != null ? stack1 : "")
    + "<ul id=\"nav-desktop\">\n<li class=\"nav-item nav-donate divider\">\n<a href=\""
    + ((stack1 = container.invokePartial(lookupProperty(partials,"donate-link"),depth0,{"name":"donate-link","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "\" target=\"_blank\">Donate</a>\n</li>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"loggedin") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.program(9, data, 0),"data":data,"loc":{"start":{"line":12,"column":0},"end":{"line":45,"column":7}}})) != null ? stack1 : "")
    + "</ul>\n";
},"2":function(container,depth0,helpers,partials,data) {
    return "<ul id=\"debug-list\" class=\"vertical-list opensans\">\n<li><button class=\"logout\" data-event=\"logout\" style=\"display:block;width:100%;\">Clear Data</button></li>\n<li><button class=\"logout\" data-event=\"clearactions\" style=\"display:block;width:100%;\">Clear Action History</button></li>\n</ul>\n";
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"nav-item nav-campaigns divider\">\n<a href=\"/campaigns\">Browse Campaigns</a>\n</li>\n<li class=\"nav-item nav-campaigns divider\">\n<a href=\"/events\">Browse Events</a>\n</li>\n<li class=\"nav-item cms-link divider\">\n<a class=\"cms-url\" href=\"#\">My Campaigns/Events</a>\n</li>\n<li class=\"nav-item profile-avatar\">\n<a data-toggle-dropdown=\"user-options\" class=\"avatar-image arrow-down\">\n<img class=\"user-avatar\" src=\""
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"avatar") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.program(7, data, 0),"data":data,"loc":{"start":{"line":24,"column":30},"end":{"line":24,"column":93}}})) != null ? stack1 : "")
    + "\">\n</a>\n<ul class=\"nav-dropdown user-options\">\n<li class=\"supporter-impact\"><a href=\"/supporter-home\">My Impact</a></li>\n<li><a href=\"/account-settings\">Account Settings</a></li>\n<li><a data-event=\"logout\" class=\"logout\">Log Out</a></li>\n</ul>\n</li>\n";
},"5":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"avatar") : depth0), depth0));
},"7":function(container,depth0,helpers,partials,data) {
    return "/img/user_no_image.png?1";
},"9":function(container,depth0,helpers,partials,data) {
    return "<li class=\"nav-item nav-cgal divider\">\n<a href=\"/campaigns\">Browse Campaigns</a>\n</li>\n<li class=\"nav-item nav-cgal divider\">\n<a href=\"/events\">Browse Events</a>\n</li>\n<li class=\"nav-item nav-register divider\">\n<a class=\"static-page\" href=\"/register\">Register</a>\n</li>\n<li class=\"nav-item nav-signup\">\n<a class=\"static-page\" href=\"/saml/login\">Login</a>\n</li>\n";
},"11":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a class=\"mobile-menu-toggle\" data-expand-section=\"mobile-nav-menu\">Menu</a>\n<ul id=\"nav-mobile\" class=\"mobile-nav-menu\">\n<li class=\"nav-item donate\"><a href=\""
    + ((stack1 = container.invokePartial(lookupProperty(partials,"donate-link"),depth0,{"name":"donate-link","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "\" target=\"_blank\">Donate</a></li>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"loggedin") : depth0),{"name":"if","hash":{},"fn":container.program(12, data, 0),"inverse":container.program(14, data, 0),"data":data,"loc":{"start":{"line":52,"column":0},"end":{"line":69,"column":7}}})) != null ? stack1 : "")
    + "</ul>\n";
},"12":function(container,depth0,helpers,partials,data) {
    return "<li class=\"nav-item\"><a href=\"/campaigns\">Browse Campaigns</a></li>\n<li class=\"nav-item\"><a href=\"/events\">Browse Events</a></li>\n<li class=\"nav-item\"><a href=\"/supporter-home\">My Impact</a></li>\n<li class=\"nav-item cms-link\"><a class=\"cms-url\" href=\"#\" target=\"_blank\">My Campaigns/Events</a></li>\n<li class=\"nav-item\"><a href=\"/account-settings\">Account Settings</a></li>\n<li class=\"nav-item\"><a data-event=\"logout\" class=\"logout\">Log Out</a></li>\n";
},"14":function(container,depth0,helpers,partials,data) {
    return "<li class=\"nav-item log-in\">\n<a class=\"static-page\" href=\"/saml/login\">Login</a>\n</li>\n<li class=\"nav-item\">\n<a href=\"/campaigns\">Browse Campaigns</a>\n</li>\n<li class=\"nav-item\">\n<a href=\"/events\">Browse Events</a>\n</li>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">",767,{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(11, data, 0),"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":71,"column":12}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true});

this["SC"]["notifications-feed"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),"<",768,{"name":"compare","hash":{},"fn":container.program(2, data, 0),"inverse":container.program(5, data, 0),"data":data,"loc":{"start":{"line":2,"column":0},"end":{"line":19,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"readmore") : depth0),{"name":"if","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":20,"column":0},"end":{"line":22,"column":7}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"notifications") : depth0),{"name":"each","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":5,"column":9}}})) != null ? stack1 : "");
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"single-not-template"),depth0,{"name":"single-not-template","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"n-timeline\">\n<div class=\"n-stack-left\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"left") : depth0),{"name":"each","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":0},"end":{"line":11,"column":9}}})) != null ? stack1 : "")
    + "</div>\n<div class=\"n-stack-right\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias1,(depth0 != null ? lookupProperty(depth0,"right") : depth0),{"name":"each","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":0},"end":{"line":16,"column":9}}})) != null ? stack1 : "")
    + "</div>\n</div>\n";
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<button class=\"more-button-after n-more\">"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),"<",768,{"name":"compare","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":21,"column":41},"end":{"line":21,"column":81}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">=",768,{"name":"compare","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":21,"column":81},"end":{"line":21,"column":123}}})) != null ? stack1 : "")
    + " More</button>\n";
},"8":function(container,depth0,helpers,partials,data) {
    return "See";
},"10":function(container,depth0,helpers,partials,data) {
    return "Read";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"notifications") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":23,"column":7}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true});

this["SC"]["login-error"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<h2 class=\"h3 bubble\">\nThere was a problem logging in, please contact <a href=\"mailto:Member.Care@sierraclub.org\">Member.Care@sierraclub.org</a> for assistance.</h2>\n";
},"useData":true});

this["SC"]["login"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"connect-to-facebook-button"),depth0,{"name":"connect-to-facebook-button","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "<span class=\"login-divider\"><em>or</em></span>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h2 class=\"h3 bubble-pale\">Welcome Back!</h2>\n<br>\n<ul class=\"login-options\">\n<li class=\"signin-facebook\">\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"fbloggedin") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":8,"column":11}}})) != null ? stack1 : "")
    + "</li>\n<li class=\"signin-account\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"sso-button"),depth0,{"name":"sso-button","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</li>\n</ul>\n";
},"usePartial":true,"useData":true});

this["SC"]["not-verified"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<h2 class=\"h3 bubble-pale\">Unable to verify your account</h2>\n<p>If you continue to experience difficulty, please email <a href=\"mailto:support@addup.org\">support@addup.org</a></p>\n";
},"useData":true});

this["SC"]["splash"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<div class=\"splash-msg\">\n<h2 class=\"h3 bubble-pale\">Welcome to AddUp!<br>Ready to get&nbsp;started? </a></h2>\n\n<p>AddUp is all about you&mdash;providing the tools you need to protect our air, water, and natural landscapes, as well as the precious wildlife that inhabits them.\n\n<p>This site is in <strong>BETA</strong>, but every campaign you see is real. So take action, share with your friends, and <a href=\"mailto:addup@sierraclub.org\">tell us what you think!</a>\n\n<p>Get in on the ground floor and help us AddUp to a greener, more sustainable world.\n\n<div><br><button class=\"modal-close button\">Get Started!</button></div>\n</div>\n";
},"useData":true});

this["SC"]["verified"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"sso-button"),depth0,{"name":"sso-button","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h2 class=\"h3 bubble-pale\">Your Account is now Verified! Please login</h2>\n<br>\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"loggedin") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":5,"column":11}}})) != null ? stack1 : "");
},"usePartial":true,"useData":true});

this["SC"]["recommended-campaigns-bar"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"gridsection").call(depth0 != null ? depth0 : (container.nullContext || {}),"recommended-campaigns-bar",{"name":"gridsection","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":28},"end":{"line":15,"column":22}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\n<h4 class=\"h4 rec-campaigns-header\">Recommended<br>for You</h4>\n<div class=\"rec-campaigns-slider\">\n<div class=\"rec-campaigns-outer\">\n<button id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "-prev\" class=\"swiper-prev\" title=\"Previous\"></button>\n<div class=\"swiper-container recommended-swiper\" data-slidenum=\"3\">\n<div class=\"swiper-wrapper\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias3,lookupProperty(helpers,"limit").call(alias3,(depth0 != null ? lookupProperty(depth0,"data") : depth0),10,{"name":"limit","hash":{},"data":data,"loc":{"start":{"line":8,"column":8},"end":{"line":8,"column":28}}}),{"name":"each","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":10,"column":9}}})) != null ? stack1 : "")
    + "</div>\n</div>\n<button id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "-next\" class=\"swiper-next\" title=\"Next\"></button>\n</div>\n</div>";
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"$").call(depth0 != null ? depth0 : (container.nullContext || {}),"recommended-campaigns-small",{"name":"$","hash":{"swiper_class":"swiper-slide "},"data":data,"loc":{"start":{"line":9,"column":0},"end":{"line":9,"column":64}}}))
    + "\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"data") : depth0),">",2,{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":15,"column":34}}})) != null ? stack1 : "")
    + "\n";
},"useData":true});

this["SC"]["registration-modal"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h2>"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"post_action") : depth0),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.program(4, data, 0),"data":data,"loc":{"start":{"line":5,"column":4},"end":{"line":5,"column":102}}})) != null ? stack1 : "")
    + "</h2>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"altintro") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.program(8, data, 0),"data":data,"loc":{"start":{"line":6,"column":0},"end":{"line":6,"column":164}}})) != null ? stack1 : "")
    + "\n";
},"2":function(container,depth0,helpers,partials,data) {
    return "TRACK YOUR IMPACT BY&nbsp;SIGNING&nbsp;UP";
},"4":function(container,depth0,helpers,partials,data) {
    return "Almost ready to&nbsp;go";
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"altintro") : depth0), depth0)) != null ? stack1 : "")
    + "</p>";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p>"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"firstname") : depth0),{"name":"if","hash":{},"fn":container.program(9, data, 0),"inverse":container.program(11, data, 0),"data":data,"loc":{"start":{"line":6,"column":48},"end":{"line":6,"column":97}}})) != null ? stack1 : "")
    + "e just need a bit more information to create an account.</p>";
},"9":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"firstname") : depth0), depth0))
    + ", w";
},"11":function(container,depth0,helpers,partials,data) {
    return "W";
},"13":function(container,depth0,helpers,partials,data) {
    return "<h2>Welcome Back!</h2>\n<p>You're just a click away from your personal impact page.  Create a password and start tracking your progress.</p>\n";
},"15":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<p>"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"alt-login-link"),depth0,{"name":"alt-login-link","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</p>";
},"17":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<br>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"connect-to-facebook-button"),depth0,{"name":"connect-to-facebook-button","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "<span class=\"login-divider\"><em>or</em></span>\n";
},"19":function(container,depth0,helpers,partials,data) {
    return "/issues-you-care-about";
},"21":function(container,depth0,helpers,partials,data) {
    return " zip-only";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<section class=\"registration-form\">\n\n<header class=\"registration-header\">\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(alias1,(depth0 != null ? lookupProperty(depth0,"prospective") : depth0),{"name":"unless","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(13, data, 0),"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":10,"column":11}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"post_action") : depth0),{"name":"if","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":11,"column":0},"end":{"line":11,"column":53}}})) != null ? stack1 : "")
    + "\n</header>\n\n"
    + ((stack1 = lookupProperty(helpers,"none").call(alias1,(depth0 != null ? lookupProperty(depth0,"fbloggedin") : depth0),(depth0 != null ? lookupProperty(depth0,"nofb") : depth0),{"name":"none","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":0},"end":{"line":18,"column":9}}})) != null ? stack1 : "")
    + "\n<form data-formaction=\"registration\" data-listerrors=\"true\" action=\""
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias1,(depth0 != null ? lookupProperty(depth0,"post_action") : depth0),{"name":"ifor","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":20,"column":68},"end":{"line":20,"column":120}}})) != null ? stack1 : "")
    + "\" class=\"page-form"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"fbloggedin") : depth0),{"name":"if","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":20,"column":138},"end":{"line":20,"column":172}}})) != null ? stack1 : "")
    + "\">\n<ul class=\"two-column-form\">\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"signup-form"),depth0,{"name":"signup-form","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</ul>\n</form>\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"signup-disclaimer"),depth0,{"name":"signup-disclaimer","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</section>\n";
},"usePartial":true,"useData":true});

this["SC"]["rsvpinline"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"events-rsvp-inline"),depth0,{"name":"events-rsvp-inline","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"usePartial":true,"useData":true});

this["SC"]["slider"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"slide "
    + alias2(lookupProperty(helpers,"switch").call(alias1,(data && lookupProperty(data,"index")),{"name":"switch","hash":{},"data":data,"loc":{"start":{"line":4,"column":18},"end":{"line":4,"column":35}}}))
    + "\">"
    + alias2(lookupProperty(helpers,"dynamicpartial").call(alias1,depths[1],{"name":"dynamicpartial","hash":{},"data":data,"loc":{"start":{"line":4,"column":37},"end":{"line":4,"column":58}}}))
    + "</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<button id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "-prev\" class=\"cycle-prev\">Prev</button>\n<div class=\"slideshow campaign-tiles\" data-cycle-next=\"#"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "-next\" data-cycle-carousel-fluid=\"true\" data-cycle-carousel-visible=\""
    + alias2(lookupProperty(helpers,"ww").call(alias3,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),(depth0 != null ? lookupProperty(depth0,"template") : depth0),{"name":"ww","hash":{},"data":data,"loc":{"start":{"line":2,"column":133},"end":{"line":2,"column":153}}}))
    + "\" data-pause-on-hover=\"true\" data-manual-trump=\"true\" data-manual-speed=\"400\" data-cycle-swipe=\"true\" data-cycle-log=\"false\" data-cycle-prev=\"#"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "-prev\" data-slides=\".slide\" data-swipe=\"true\" data-cycle-fx=\"carousel\" data-cycle-timeout=\"555000\" data-speed=\"600\" data-allow-wrap=\"true\">\n"
    + ((stack1 = lookupProperty(helpers,"each").call(alias3,(depth0 != null ? lookupProperty(depth0,"data") : depth0),{"name":"each","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":5,"column":9}}})) != null ? stack1 : "")
    + "<div class=\"cycle-pager\"></div>\n</div>\n<button id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "-next\" class=\"cycle-next\">Next</button>\n";
},"useData":true,"useDepths":true});

this["SC"]["swiper-slider"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"data") : depth0),{"name":"each","hash":{},"fn":container.program(2, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":7,"column":9}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"slide "
    + alias2(lookupProperty(helpers,"switch").call(alias1,(data && lookupProperty(data,"index")),{"name":"switch","hash":{},"data":data,"loc":{"start":{"line":6,"column":18},"end":{"line":6,"column":35}}}))
    + " swiper-slide\">"
    + alias2(lookupProperty(helpers,"dynamicpartial").call(alias1,depths[1],{"name":"dynamicpartial","hash":{},"data":data,"loc":{"start":{"line":6,"column":50},"end":{"line":6,"column":71}}}))
    + "</div>\n";
},"4":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(alias1,lookupProperty(helpers,"limit").call(alias1,(depth0 != null ? lookupProperty(depth0,"data") : depth0),5,{"name":"limit","hash":{},"data":data,"loc":{"start":{"line":10,"column":8},"end":{"line":10,"column":27}}}),{"name":"each","hash":{},"fn":container.program(2, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":10,"column":0},"end":{"line":12,"column":9}}})) != null ? stack1 : "");
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<button id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "-prev\" class=\"swiper-prev\" title=\"Previous\"></button>\n<div class=\"swiper-container\" data-slidenum=\""
    + alias2(lookupProperty(helpers,"ww").call(alias3,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),(depth0 != null ? lookupProperty(depth0,"template") : depth0),{"name":"ww","hash":{},"data":data,"loc":{"start":{"line":2,"column":45},"end":{"line":2,"column":65}}}))
    + "\">\n<div class=\"swiper-wrapper\">\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">=","768",{"name":"compare","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":8,"column":12}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),"<","768",{"name":"compare","hash":{},"fn":container.program(4, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":9,"column":0},"end":{"line":13,"column":12}}})) != null ? stack1 : "")
    + "</div>\n</div>\n<button id=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"t_id") : depth0), depth0))
    + "-next\" class=\"swiper-next\" title=\"Next\"></button>\n<div class=\"swiper-pagination\"></div>\n";
},"useData":true,"useDepths":true});

this["SC"]["victory-campaign"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"victory-campaign\">\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"reportbacks") : depth0)) != null ? lookupProperty(stack1,"0") : stack1)) != null ? lookupProperty(stack1,"image") : stack1),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":9,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"with").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"reportbacks") : depth0)) != null ? lookupProperty(stack1,"0") : stack1),{"name":"with","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":10,"column":0},"end":{"line":14,"column":9}}})) != null ? stack1 : "")
    + "<div class=\"victory-info victory-blurb\">"
    + ((stack1 = alias2((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0)) != null ? stack1 : "")
    + "</div> <div class=\"victory-info victory-more\"><a href=\"/campaigns/"
    + container.escapeExpression(alias2((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\">Read the story ></a></div>\n</div>\n</div>\n";
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"reportbacks") : depth0)) != null ? lookupProperty(stack1,"0") : stack1),{"name":"with","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":6,"column":9}}})) != null ? stack1 : "");
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"victory-icon\" style=\"background:url("
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"image") : depth0), depth0))
    + ");background-size:cover;\"></div>\n";
},"5":function(container,depth0,helpers,partials,data) {
    var alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"victory-cup\"><p class=\"victory-title\">Victory!</p></div>\n<div class=\"victory-info victory-date\">"
    + alias2(lookupProperty(helpers,"mdate").call(alias1,(depth0 != null ? lookupProperty(depth0,"updated_at") : depth0),"abvMon",{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":12,"column":39},"end":{"line":12,"column":68}}}))
    + " "
    + alias2(lookupProperty(helpers,"mdate").call(alias1,(depth0 != null ? lookupProperty(depth0,"updated_at") : depth0),"getDate",{"name":"mdate","hash":{},"data":data,"loc":{"start":{"line":12,"column":69},"end":{"line":12,"column":99}}}))
    + "</div>\n<div class=\"victory-text\">\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"reportbacks") : depth0)) != null ? lookupProperty(stack1,"0") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":18,"column":7}}})) != null ? stack1 : "");
},"useData":true});

this["SC"]["happening-now"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<li class=\"item\">\n<img src=\""
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"avatar_url") : depth0),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.program(4, data, 0),"data":data,"loc":{"start":{"line":6,"column":10},"end":{"line":6,"column":79}}})) != null ? stack1 : "")
    + "\" alt=\"\"/>&nbsp;\n<b>"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"first_name") : depth0), depth0))
    + " "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"last_name") : depth0), depth0))
    + "</b> "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"action_type") : depth0), depth0))
    + "\n<a href=\"/campaigns/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"campaign_slug") : depth0), depth0))
    + "\">\n<b>&ldquo;"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"campaign_name") : depth0), depth0))
    + "&rdquo;</b>\n</a>\n- "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"action_timestamp") : depth0), depth0))
    + "\n</li>\n";
},"2":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"avatar_url") : depth0), depth0));
},"4":function(container,depth0,helpers,partials,data) {
    return "/img/user_no_image.png";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div id=\"scrollContainer\">\n<ul id=\"scroller\">\n<li class=\"mask-top\"></li>\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"data") : depth0),{"name":"each","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":0},"end":{"line":13,"column":9}}})) != null ? stack1 : "")
    + "<li class=\"mask-bottom\"></li>\n</ul>\n</div>\n";
},"useData":true});

this["SC"]["involvement-promos"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "<div class=\"calls-to-action row center flex-row hide-mobile\">\n";
},"3":function(container,depth0,helpers,partials,data) {
    return "<div class=\"calls-to-action row center flex-row hide-desktop\">\n<div class=\"swiper-container\" data-slidenum=\"auto\">\n<div class=\"swiper-wrapper\">\n";
},"5":function(container,depth0,helpers,partials,data) {
    return "</div>\n</div>\n<div class=\"swiper-pagination\"></div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),">",767,{"name":"compare","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":7,"column":12}}})) != null ? stack1 : "")
    + "\n<div class=\"swiper-slide col-3 background-pale white-sign-icon\">\n<div class=\"inside\">\n<h3 class=\"h1\">Act</h3>\n<p><strong>Take action</strong> to stand up for what's right\n</div>\n</div>\n<div class=\"swiper-slide col-3 background-beige white-twitter-icon\">\n<div class=\"inside\">\n<h3 class=\"h1\">Tweet</h3>\n<p>for <strong>change</strong>\n</div>\n</div>\n<div class=\"swiper-slide col-3 background-pale white-recruit-icon\">\n<div class=\"inside\">\n<h3 class=\"h1\">Recruit</h3>\n<p>&amp; find <strong>supporters</strong> in your area\n</div>\n</div>\n<div class=\"swiper-slide col-3 background-beige white-event-icon\">\n<div class=\"inside\">\n<h3 class=\"h1\">Attend</h3>\n<p>&amp; find <strong>events</strong> in your area\n</div>\n</div>\n\n"
    + ((stack1 = lookupProperty(helpers,"compare").call(alias1,(depth0 != null ? lookupProperty(depth0,"t_ww") : depth0),"<=",767,{"name":"compare","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":34,"column":0},"end":{"line":38,"column":12}}})) != null ? stack1 : "")
    + "\n</div> <!-- /.calls-to-action -->\n";
},"useData":true});

this["SC"]["welcome-campaign-hero"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"titleize").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"t_seed") : depth0)) != null ? lookupProperty(stack1,"hero_title_override") : stack1),{"name":"titleize","hash":{},"data":data,"loc":{"start":{"line":3,"column":62},"end":{"line":3,"column":101}}}));
},"3":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(lookupProperty(helpers,"titleize").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"title") : depth0),{"name":"titleize","hash":{},"data":data,"loc":{"start":{"line":3,"column":109},"end":{"line":3,"column":127}}}));
},"5":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"goal") : depth0), depth0));
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"/campaigns/"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "/"
    + ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"top_action") : depth0),{"name":"with","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":47},"end":{"line":5,"column":205}}})) != null ? stack1 : "");
},"8":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return alias2(alias1((depth0 != null ? lookupProperty(depth0,"featured_action_type") : depth0), depth0))
    + "/"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"featured_action_slug") : depth0), depth0))
    + "\" class=\"button action-button hero-action-button\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"featured_action_phrase") : depth0), depth0))
    + "</a>";
},"10":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"/campaigns/"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"slug") : depth0), depth0))
    + "\" class=\"button action-button hero-action-button\">Take Action</a>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"grid-row\">\n<div class=\"row\">\n<h1 class=\"hero-text col-8\">"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"t_seed") : depth0)) != null ? lookupProperty(stack1,"hero_title_override") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":3,"column":28},"end":{"line":3,"column":134}}})) != null ? stack1 : "")
    + "</h1>\n<p class=\"hero-description col-6\">"
    + ((stack1 = lookupProperty(helpers,"ifor").call(alias1,((stack1 = (depth0 != null ? lookupProperty(depth0,"t_seed") : depth0)) != null ? lookupProperty(stack1,"hero_text_override") : stack1),{"name":"ifor","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":34},"end":{"line":4,"column":86}}})) != null ? stack1 : "")
    + "</p>\n"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"top_action") : depth0),{"name":"if","hash":{},"fn":container.program(7, data, 0),"inverse":container.program(10, data, 0),"data":data,"loc":{"start":{"line":5,"column":0},"end":{"line":5,"column":313}}})) != null ? stack1 : "")
    + "\n</div>\n</div>\n";
},"useData":true});

this["SC"]["your-campaigns-list"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"events-search-large"),depth0,{"name":"events-search-large","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "<div class=\"events-grid-and-map\">\n<div class=\"events-cover\"></div><!-- adding this to hide the repeatedly reloading map and grid -->\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"events-grid"),depth0,{"name":"events-grid","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</div>\n";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"data") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.program(6, data, 0),"data":data,"loc":{"start":{"line":8,"column":0},"end":{"line":18,"column":7}}})) != null ? stack1 : "");
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"campaigns-search"),depth0,{"name":"campaigns-search","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + ((stack1 = container.invokePartial(lookupProperty(partials,"campaigns-grid"),depth0,{"name":"campaigns-grid","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"6":function(container,depth0,helpers,partials,data) {
    return "<div class=\"sadtrees\">\n<span class=\"sadtree\"></span>\n<h1>You have not supported any campaigns yet.</h1>\n<h6>Don't worry&mdash;it's an easy fix.</h6>\n<p>The best way to get started is to just get started!</p>\n</div>\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"events") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":19,"column":7}}})) != null ? stack1 : "")
    + ((stack1 = container.invokePartial(lookupProperty(partials,"pagination"),depth0,{"name":"pagination","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"usePartial":true,"useData":true});;//@codekit-prepend "underscore.js"
/*global _ */

window.bsdts = {}; //our one global exposure

//prepare to write to these optional libraries whether they exist or not
window.optimizely = window.optimizely || [];

(function($, bsdts){

    var endpointdomain = '',//window.seed.ts_endpoint,//'https://client-addup.ts.bsd.net/',//http://client-addup.ts.bsd.net/ //https://elb-ts-001.dev.bsd.net/
        debug = (gup('debug')==="true"||(c('debug')==="true"&&gup('debug')!=="false")),
        version = 'v3',
        taggabledfd = $.Deferred();

    $.extend(bsdts,{s:{
        tsappid: 2, //our id code for the particular use case. Eventually should deifnitely be read out of the page
        approute_new: version+'/new/',
        approute_guid: version+'/guid/',
        approute_link: version+'/newlink/',
        approute_share: version+'/share/',
        approute_view: version+'/view/',
        appendpoint: endpointdomain,//'http://secure-fortress-7239.herokuapp.com/',
        fqlroute: 'https://graph.facebook.com/fql',
        action: 'me/sc_addup:share'
    }});

    function sortByKey(array, key) {
        return array.sort(function(a, b) {
            var x = parseFloat(a[key]),
                y = parseFloat(b[key]);
            return ((x < y) ? -1 : ((x > y) ? 1 : 0));
        });
    }

    //turns a response object status or code into either an integer or undefined
    function filterStatus(error){
        if(error && error.error) {
            error = error.error;
        }
        if(error && error.code){
            error.status = error.code;
        }
        return (error && !isNaN( parseFloat(error.status) )) ? parseFloat(error.status):'5XX';
    }

    function fbapi(route,options){
        var dfd = $.Deferred();
        FB.api(route,function(response){
            //console.log('!! fbapi',response);
            if(!response.error){
                dfd.resolve(response);
            }else{
                dfd.reject(response);
            }
        });
        return dfd;
    }

    //convert the share message into the FB mentions format
    function translateMentions(msg,friends){
        msg = msg||"";
        lug('translating message',msg, friends);
        return msg + ' ' + _.map(friends, function(friend){
            return "@["+friend.fbid+"]";
        }).join(' ');
    }

    //add the bsdts as a plugin to jQuery
    $.fn.extend({
        bsdts: function(options) {


        } //end bsdts call
    }); //end jQuery extend


    bsdts.getlink = function(ops){

        return $.ajax({
            "url":endpointdomain + 'v3/newlink/'+c('tsguid'),
            "dataType":"JSON",
            data:{
                link: ops.link,
                session: ops.session
            },
            timeout: 5000
        }).then(function(d){
            return (d && d.status===200)?d.link:$.Deferred().reject(d);//anything with a message should be treated as a fail. Can also break it manually
        }).done(function(link){
            FB.api('https://graph.facebook.com/', 'post', {
                id: link,
                scrape: true
            }, function(response) {
                //console.log('rescrape!',response);
            });
        });
    };

    bsdts.sessiontracker = {};//stored object to keep track of what sessions have shown what to whom

    function fbPaginate(route,ops){
        var masterdfd = $.Deferred(),//create a promise external to the fb call
            results = [];//store any results, even partial

        (function poll(nextroute){
            fbapi(nextroute||route)
                .then(function(response){ //filter responses into success, wait/progress, or error
                    var next = response.paging && response.paging.next,//if there's a next page
                        data = response.data||[];//simplify data
                    if(ops.mapfunc){
                        data = _.map(data,ops.mapfunc);//if we have data, and want to reshape it, do so, so that even notify results fit the format
                    }

                    results = results.concat(
                        _.filter(data,function(item){
                            return _.isObject(item) && item.name;//ensure that each result is valid and sufficient
                        })
                    );//add this round of results to the set

                    if(next && ops.limit > results.length){
                        return $.Deferred().notify(next);
                    }
                }).done(function(){
                    if(results.length > ops.limit){
                        results.length = ops.limit;//shorten things to what was requested
                    }
                    masterdfd.resolve(results);//resolve that dfd
                }).progress(function(next){
                    if(ops.notifyat){
                        masterdfd.notify(results);//pass along any early results
                    }
                    poll(next);
                }).fail(function(error){

                    return (results.length)?
                        masterdfd.resolve(results) ://we still have some results to return, so let's do that
                        masterdfd.reject(error);//otherwise we failed, return the error
                });
        }());
        return masterdfd;
    }

    bsdts.getFBPaginatedRoute = function(ops){

        ops = _.extend({
            version: 'v2.0',
            route: '/me/taggable_friends',
            route_template: "<%= version %><%= route %>"
        },_.isNumber(ops)?{limit:ops}:(ops||{}));

        var route_template= _.template(ops.route_template);

        return fbPaginate(route_template(ops),ops).progress(function(n){
            //console.log('fbapi tf progress',n);
        });//creates the route from the template using the data parameters in ops
    };

    /*bsdts.getTaggableFriends = function(ops){

        ops = _.extend({
                route_template: "<%= version %><%= route %>?fields=name,picture.width(<%= width %>).height(<%= height %>)",
                mapfunc: function(item){
                    return item ?
                        {
                            id: item.id,
                            fbid: item.id||item.uid||false,//will never happen in v2.0 but...
                            'score': 0,
                            "name": item.name,
                            avatar: item.picture && item.picture.data.url
                        } :
                        false;
                },
                limit:7e5,//effectively infinite, fb friends are capped at 5000, so probably nobody is gonna have 50000
                notifyat: false,
                height:200,
                width:200
            },
            _.isNumber(ops)?{limit:ops}:(ops||{})
        );
        taggabledfd = $.Deferred();
        return bsdts.getFBPaginatedRoute(ops).done(function(results){
            taggabledfd.resolve(results);
        });
    };*/

    // bsdts.trackviews = function(ops){
    //     var fl = _.isArray(ops.friends) && ops.friends.length;

    //     if(!fl){
    //         return false;
    //     }

    //     if (bsdts.sessiontracker[ops.session]){
    //         ops.offset = bsdts.sessiontracker[ops.session];
    //         bsdts.sessiontracker[ops.session] = ops.offset + fl;
    //     }else{
    //         bsdts.sessiontracker[ops.session] = fl;
    //     }
    //     ops.friends = ops.friends.join(',');

    //     return $.ajax({
    //         "url":endpointdomain + version +'/view/'+c('tsguid'),
    //         "dataType":"JSON",
    //         data:ops,
    //         timeout: 5000
    //     }).then(function(d){
    //         lug('viewtracked',d);
    //         return (d && d.status===200)?d.link:$.Deferred().reject(d);//anything with a message should be treated as a fail. Can also break it manually
    //     });
    // };

    // bsdts.post = function(friends,msg,item_type,url,sessionObj,settings,respmodel){
    //         var privateurl = gup('private'),
    //             notag = gup('notag');
    //         lug('TS POST',arguments);

    //         // create a custom post object
    //         function postObject(url, item_type, msg, settings){
    //             var obj = {};
    //             msg = msg||"";
    //             settings = settings||{};
    //             lug('post arguments',arguments);

    //             if(item_type.indexOf(':')>-1){
    //                 item_type = item_type.split(":")[1];
    //             }else{
    //                 item_type = 'website';
    //             }

    //             if (settings.privacy){
    //                 obj.privacy = settings.privacy;//settings.privacy;
    //             }
    //             if (!!privateurl){
    //                 obj.privacy = {value:'SELF'};//settings.privacy;
    //             }

    //             if(settings.no_feed_story){
    //                 obj.no_feed_story = settings.no_feed_story;
    //             }
    //             if(settings['fb:explicitly_shared']){
    //                 obj['fb:explicitly_shared'] = settings['fb:explicitly_shared'];
    //             }
    //             obj.message = translateMentions(msg, !notag ? friends : []);//strip friends from share if that testmode is on

    //             if(settings.custom && settings.custom.title){
    //                 obj[item_type] = {
    //                     title: settings.custom.title,
    //                     description: settings.custom.description,
    //                     image: settings.custom.image,
    //                     url: url
    //                };
    //             }
    //             else if(url && item_type){
    //                 obj[item_type] = url;
    //             }
    //             lug('final post object',obj);
    //             return obj;
    //         }

    //         // post something to facebook using the api but turn it into a standard deferred
    //         function fbPost(url, item_type, msg, sessionObj, settings, dfd){
    //             var masterdfd = dfd||$.Deferred();
    //             lug('posting to fb', 'me/sc_addup:share', url, translateMentions(msg,friends), item_type, postObject(url, item_type, msg, settings));
    //             FB.api(
    //                 'me/sc_addup:share',
    //                 'post',
    //                 postObject(url, item_type, msg, settings),
    //                 function(response) {
    //                     // handle the response
    //                     lug('fb share error:',response);
    //                     if(!response || response.error){

    //                         if(response && response.error && response.error.code===100 && !dfd){
    //                             lug('FIRST SHARE FAILED, TRYING A SECOND');
    //                             $.wait(700).done(function(){
    //                                 fbPost(url, item_type, msg, sessionObj, settings, masterdfd);
    //                             });
    //                         }else{
    //                             masterdfd.reject(response);
    //                             report(['TargetShare','failed_share',filterStatus(
    //                                 (response && response.error?response.error:'500')
    //                                 )
    //                             ]);
    //                         }
    //                     }else{
    //                         lug('the message: '+ translateMentions(msg,friends) + ' ... was sent');
    //                         lug(response);
    //                         report(['TargetShare','share',url,friends.length]);
    //                         report(['TargetShare','model shared',respmodel,friends.length,{'nonInteraction': 1}]);
    //                         masterdfd.resolve(response);
    //                     }
    //                 }
    //             );
    //             return masterdfd;
    //         }

    //         // report any shares to our tracking api
    //         function trackShare(response){
    //             lug('sending share values to API');
    //             $.ajax({
    //                 "url":bsdts.s.appendpoint + bsdts.s.approute_share+c('tsguid'),
    //                 "dataType":"JSON",
    //                 data:{
    //                     session: sessionObj.source,
    //                     friends: _.pluck(friends,'fbid').join(','),
    //                     pid:response.id,
    //                     link: url
    //                 },
    //                 timeout: bsdts.s.apitimeout
    //             }).then(function(d){
    //                 lug(d);
    //                 return (d && d.status===200)?d:$.Deferred().reject(d);//anything with a message should be treated as a fail. Can also break it manually
    //             }).fail(function(error){
    //                 lug('API tracking called failed',error);
    //                 report(['TargetShare','api_fail_report',filterStatus(error),{'nonInteraction': 1}]);
    //             });
    //         }


    //         return fbPost(url, item_type, msg, sessionObj, settings).done(trackShare);
    // };


    bsdts.getFriends = function(options){

            var masterdfd = $.Deferred(), //resolves when we have results
                itemurl = options.link, //figure out what is being shared, ultimately, this is the BASE link
                itemurlshare = options.link,//starts out as just the share url for now, may get "upgraded" by the api
                item_type = 'website',
                source = gup('source'), //read source
                testusers = !!gup('testusers'),
                breakourapi = window.location.hash === '#break', //check for dev command to break the api
                firstloginreq = false, //was this the first request for a login
                loginnoauth = false, //was the user logged in but not authorized to use the app
                firstauth = false, //was the user not authed before, then became so
                firstshareloaded = false, //was this the first time a friends list loaded
                apiattempts = 0, //how many times did we try the guid poll
                auth = options.auth, //will contain the .access_token necessary to make quality api responses
                currentfriends = [],
                currentfriendnames = {},
                mentions = {}, //map of selected mentions, with fb ids as the keys and names the values
                mentionnames = {}, //object of current available names for fast detecting
                toLine = [],
                fbinit = $.Deferred(), //resolves when fb library is loaded
                messageln = 0, //share message length, used to measure if it's recently gotten longer or shorter
                sessionid = 0, //session id that will be assigned by the api
                model = options.model, //default friend model
                sharelink = options.link,
                guid = c('tsguid'), //any previous guid
                newguid = !guid, //is this session fresh, without a stored guid?
                apitime, //save the time we started calling the api
                perms = '',//string of fb permissions once we know we have some
                fbrandomquery = 'SELECT uid, name FROM user WHERE uid IN ( SELECT uid2 FROM friend WHERE uid1 = me() ) ORDER BY rand()',
                defaults = {
                    fbappid : "203478199839346",//"290836427720965" //default appid Eventually should be read out of the page... maybe?
                    tsappid: 2, //our id code for the particular use case. Eventually should deifnitely be read out of the page
                    approute_new: version +'/new/',
                    approute_guid: version +'/guid/',
                    approute_user: version +'/user/',
                    approute_fb: version +'/fb/',
                    approute_share: version +'/share/',
                    nopost: false,
                    fqlonly: false,
                    privacy: false, //{'value':'SELF'},
                    fbroute_share: 'me/sc_addup:share', //app specific post method.  Will change.
                    feedstory: true, //false if we don't want it to post anywhere
                    explicitly_shared: false, //true if we want it poste to timeline - requires extra permissions
                    pollingwait: 900, //how long to wait between polling attempts
                    maxapiattemtps: 16, //how many times to poll the api before falling back to FB
                    guidskip: false, //true to skip the /new step if there is a tsguid already. Currently, the API isn't returning detailed enough results for this
                    appendpoint: endpointdomain,//'http://secure-fortress-7239.herokuapp.com/',
                    fqlroute: 'https://graph.facebook.com/fql',
                    devmode: true,
                    apitimeout: (breakourapi || window.location.href.indexOf('local')>-1)?20000:20000, //how long to wait on the api before declaring failure
                    fbbackups: [],
                    displaylimit: 36, //how many results to show at a time
                    selectlimit: 10 //total you can select. Will likely not be seen by user since it will be set to 10, and api will return only 9 at a time
                };

            if(item_type.indexOf(':')>-1){
                item_type = item_type.split(":")[1];
            }else{
                item_type = 'website';
            }
            //lug('options, defaults',defaults,options);
            //consolidate both user defined and default settings
            defaults.fbbackups.push(fbrandomquery);
            bsdts.s = $.extend({},defaults, options);
            //lug('options',bsdts.s);
            function errorThen(e){
                return e.responseJSON||e;
            }

            //initiate an API session or recover an already stored one
            function newAPISession(){
                lug('initial request to our api');
                //if we have an active guid, skip the /new step and just grab results directly
                if(guid && guid.length>10 && bsdts.s.guidskip){
                    lug('/new step skipped, already have a guid cookie',guid);
                    return $.Deferred().resolve([{'path':bsdts.s.approute_guid+guid}]);
                }
                else {
                    return $.ajax({
                        "url":(bsdts.s.appendpoint + bsdts.s.approute_new +bsdts.s.tsappid+'/' + auth.authResponse.accessToken+((options.loc)?('?loc='+options.loc+'&name='+options.name):'')),
                        //data: params,
                        timeout: bsdts.s.apitimeout
                    }).then(function(d){
                        var status = (d && !!d.status)?d.status.toString():'500';
                        //lug('original /new request returned!',d);
                        //anything with a message should be treated as a fail. Can also break it manually
                        return (status.indexOf(2)===0 && !breakourapi)? d.data : $.Deferred().reject(d);
                    });
                }
            }

            //poll until the api is prepared to return results
            function pollForFriends(response, num){
                num = num || bsdts.s.displaylimit;
                var calldfd = $.Deferred(), //master deferred
                    params = { 'limit': num, 'model': (model+',sc_fb,random') , 'link': sharelink};
                if(options.name){
                    params.name = options.name;
                }

                if (sessionid){
                    _.extend(params,{'session':sessionid});
                }

                //this function fires immediately, and if it has not hit the limit of tries or gotten a success or fail, calls itself again, with optional progress updates
                (function poll(){
                    //lug('checking to see if we have '+(options.name||model)+' results, attempt #'+ apiattempts);
                    var req = $.ajax({
                        "url":bsdts.s.appendpoint + bsdts.s.approute_guid + guid,
                        "data":params,
                        "dataType":"JSON"
                    }).then(function(d){ //filter responses into success, wait/progress, or error
                        //d = filterGuid(d);
                        lug('FINAL RESPONSE',d);
                        var status = (d && !!d.status)?d.status.toString():'500';

                        if (status.indexOf(5)!==0 &&//no 500s
                            (status.indexOf(4)!==0 || (status.indexOf(4)===0 && (d.data && d.data[0] && !!d.data[0].name))  ) &&
                            !breakourapi &&
                            apiattempts<bsdts.s.maxapiattemtps){
                            /*
                            if(d.data) {
                                //lug('need num '+num+' got '+(d && d.data)?d.data.length:0,d);
                            }
                            */
                            if (status==="200" ||
                                (status==="203" && apiattempts>1 && d.data && d.data[0].name!=="") ||
                                status==="304"||
                                (status.indexOf(4)===0 && d.data[0].name!=="") ||//nothing better
                                (status==="206" && apiattempts>1 && d.data && d.data[0].name!=="")//give up waiting early
                                ){
                                return d;
                            }else{
                                return $.Deferred().notify(d);
                            }
                        }else {
                            lug('apifail', (!breakourapi?d.status:'deliberate breakage')  );
                            return $.Deferred().reject(d);
                        }
                    });
                    req.done(function(response){
                        if(response && response.data){
                            response.data = _.map(response.data,function(friend){
                                friend.avatar= friend.picture && friend.picture.data.url;
                                friend.id = friend.id;
                                friend.fbid = friend.id;
                                return friend;
                            });
                            //console.log('FINAL FRIENDS',response.data);
                        }
                        calldfd.resolve(response);
                        $(window).trigger('scroll');//just in case the page is scrolled to the view, start tracking now
                    }).progress(function(d){
                        //lug('api said to wait a bit('+apiattempts+') and try again',d);
                        apiattempts++;
                        $.wait(bsdts.s.pollingwait).done(function(){
                            poll();
                        });
                    }).fail(function(e){
                        calldfd.reject(e);
                    });
                }());
                return calldfd;
            }
            //master deferred: the logic of asking for a token or not, then polling for friends, and maybe falling back to fb
            //is all collapsed into this deferred, which simply returns friends any way it can
            //if the entire deferred fails, both our api and fb were both unresponsive, which suggests loss of internet
            function getFriends(num){
                num = num || bsdts.s.displaylimit;
                apiattempts = 0; //reset apiattempts
                var dfd = $.Deferred(),//outer, master Deferred
                    apidfd = $.Deferred(); //inner Deferred
                apitime = new Date();
                if(bsdts.s.fqlonly){
                    apidfd.reject();
                }else{
                    newAPISession(num).done(function(response){
                        //guid for this user/app/usage
                        if(response.guid){
                            guid = response.guid;
                            document.cookie  = "tsguid="+guid;
                        }
                        if(response.session){
                            sessionid = response.session;
                        }
                        pollForFriends(response, num).done(function(response){
                            /* better for front end to use a fresh token
                            if(response.result[0].long_token){
                                auth.authResponse.accessToken = response.result[0].long_token;
                            }
                            */
                            //session_id
                            if(response.session){
                                sessionid = response.session;
                            }
                            //model used
                            if(response.model){
                                model = response.model;
                            }
                            if(response.link){
                                itemurlshare = response.link;//upgrade the link to an api endpoint
                            }
                            //if we have the key user-specific datapoints, use them in the share url
                            itemurlshare = (sessionid && guid)? (itemurlshare + "?" + $.param({'sessionid':sessionid, 'guid': guid})) : itemurlshare;
                            //fbLinkCheck(itemurlshare); //interesting, but not working
                            apidfd.resolve(response);
                        }).fail(function(error){
                            lug('primary api wasn\'t ready on guid, showing friends',((error && error.message)?error.message:''),error);
                            apidfd.reject();
                            report(['TargetShare','api_fail_guid',filterStatus(error),{'nonInteraction': 1}]);
                        });
                    }).fail(function(error){
                        lug(error);
                        lug('primary api wasn\'t ready on new, showing friends',((error && error.message)?error.message:''),error);
                        apidfd.reject();
                        report(['TargetShare','api_fail_new',filterStatus(error),{'nonInteraction': 1}]);
                    });
                }
                apidfd.done(function(response){
                    reportTime('new_to_guid', (new Date() - apitime ),((guid && bsdts.s.guidskip && !newguid)?'existing':'fresh'),'TargetShare');
                    report(['TargetShare','model results',response.model,{'nonInteraction': 1}]);
                    lug('api time from /new to valid /guid: '+ (((new Date()) - apitime)/ 1000) +'s', response, sessionid);
                    dfd.resolve({friends: _.sortBy(response.data,'score').reverse(), session: response.session, model: response.model});
                }).fail(function(){
                    bsdts.getTaggableFriends().done(function(friends){
                        var model = "fb_backup",
                            fakesession = Math.round((Math.pow(36, length + 1) - Math.random() * Math.pow(36, 10))).toString(36).slice(1);
                        reportTime('new_to_fbbackup', (new Date() - apitime ),'apifailed','TargetShare');
                        dfd.resolve({friends: friends, sessionid:fakesession, fbfallback:true, model:model});
                    }).fail(function(error){
                        lug('Can\'t even get FB friends: FB potentially down');
                        report(['TargetShare','fb_fail',filterStatus(error),{'nonInteraction': 1}]);
                        dfd.reject(error);
                    });
                });
                return dfd;
            }


            //FBQL call to get random friends
            function getFBFriends(num, query,fakeprox){
                var realquery = query||fbrandomquery;
                fakeprox = fakeprox||0;
                num = num || bsdts.s.displaylimit;
                lug('fbquery with fe-set prox',fakeprox+'-'+query.length);
                return fbapi({'method':'fql.query','query':(realquery + ' limit '+num)}).then(function(response){
                    lug(fakeprox+'-'+query.length+ ' got '+response.length+ ' facebook friends: ', $.map(response, function(o) { return o.name; }).join(','));
                    return $.map(response,function(v,i){ //reeeeeemap!
                        return  {'fbid':parseFloat(v.uid),'score':fakeprox,"name": v.name};
                    });
                });
            }


        lug('GETTING NUM',options.limit);
        getFriends(options.limit).done(function(friends){
            masterdfd.resolve(friends);
        });
        return masterdfd.promise();

    }; //end main func

}(jQuery, bsdts));
;function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  }

  return _assertThisInitialized(self);
}

function _createSuper(Derived) {
  var hasNativeReflectConstruct = _isNativeReflectConstruct();

  return function _createSuperInternal() {
    var Super = _getPrototypeOf(Derived),
        result;

    if (hasNativeReflectConstruct) {
      var NewTarget = _getPrototypeOf(this).constructor;

      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }

    return _possibleConstructorReturn(this, result);
  };
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]);

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var activeActionModelViews = function activeActionModelViews(currentViews, reject_lookup, reject_type) {
  return _.chain(currentViews).filter(_.iteratee('actionmodel')).reject(_.isString(reject_lookup) ? function (item) {
    return !!_.lookup(item, reject_lookup);
  } : _.iteratee(false)).reject(function (v) {
    return v.mixins === reject_type;
  }).value();
};

var dateAsISOString = function dateAsISOString(date, allDay) {
  var baseDate = date.toISOString().replace(/:|-|\.\d+/g, '');
  return allDay ? baseDate.split('T')[0] : baseDate;
};

var toText = function toText(str) {
  return $('<div/>').html(str).html();
};

var gcal = function gcal(_ref) {
  var title = _ref.title,
      startShort = _ref.startShort,
      endShort = _ref.endShort,
      description = _ref.description,
      url = _ref.url,
      location = _ref.location;
  return 'https://calendar.google.com/calendar/render?action=TEMPLATE&' + $.param({
    text: title,
    //sprop:'website:addup.sierraclub.org',
    dates: (startShort || '') + '/' + (endShort || ''),
    details: description + url,
    location: location
  }) + '&sprop=&sprop=name:';
};

var outlook = function outlook(_ref2) {
  var title = _ref2.title,
      startShort = _ref2.startShort,
      endShort = _ref2.endShort,
      description = _ref2.description,
      url = _ref2.url,
      location = _ref2.location,
      allDay = _ref2.allDay;
  return 'https://calendar.live.com/calendar/calendar.aspx?rru=addevent&' + $.param({
    summary: title,
    dtstart: startShort,
    dtend: endShort,
    allDay: allDay,
    description: (description.length > 800 ? description.slice(0, 800) + '...' : description) + url,
    location: location
  });
};

var ycal = function ycal(_ref3) {
  var title = _ref3.title;
      _ref3.start;
      var startShort = _ref3.startShort,
      startDate = _ref3.startDate;
      _ref3.end;
      var endDate = _ref3.endDate,
      endShort = _ref3.endShort,
      endLocalized = _ref3.endLocalized,
      multiDay = _ref3.multiDay,
      description = _ref3.description,
      url = _ref3.url,
      location = _ref3.location,
      allDay = _ref3.allDay;
  var eventDuration = (endDate.getTime() - startDate.getTime()) / 6e4; //6e4 = 60 * 1000
  //minutes -> HH:MM
  //may need to handle this when it's over 99...

  var yahooHourDuration = eventDuration < 600 ? '0' + Math.floor(eventDuration / 60) : Math.floor(eventDuration / 60) + '';
  var yahooMinuteDuration = eventDuration % 60 < 10 ? '0' + eventDuration % 60 : eventDuration % 60 + '';
  var yahooEventDuration = yahooHourDuration + yahooMinuteDuration;
  return 'https://calendar.yahoo.com/?v=60&view=d&type=20&' + $.param({
    title: title,
    allDay: allDay,
    st: startShort,
    dtend: endShort,
    desc: description + url,
    in_loc: location
  }) + (multiDay ? '&et=' + endLocalized : '&dur=' + (allDay ? '2359' : yahooEventDuration || ''));
};

var ics = function ics(_ref4) {
  var title = _ref4.title,
      start = _ref4.start,
      startShort = _ref4.startShort;
      _ref4.end;
      var endShort = _ref4.endShort,
      description = _ref4.description,
      location = _ref4.location,
      allDay = _ref4.allDay;
      _ref4.multiDay;
  var now = dateAsISOString(new Date());
  return encodeURI('data:text/calendar;charset=utf8,' + ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Sierra Club/AddUp//EN', 'BEGIN:VEVENT', 'DTSTAMP:' + now, 'URL:' + window.location.href, "UID:".concat(now, "@").concat(document.domain), 'DTSTART' + (allDay ? ";VALUE=DATE:".concat(startShort) : ":".concat(start)), 'DTEND' + (allDay ? ";VALUE=DATE:".concat(endShort) : ":".concat(start)), 'SUMMARY:' + title.replace(/(,|;|\\)/g, '\\$1').replace(/\n/g, '\\n'), 'DESCRIPTION:' + description.replace(/(,|;|\\)/g, '\\$1').replace(/\n/g, '\\n'), 'LOCATION:' + location.replace(/(,|;|\\)/g, '\\$1').replace(/\n/g, '\\n'), 'END:VEVENT', 'END:VCALENDAR'].join('\r\n'));
};

var parseDateTime = function parseDateTime(date, time) {
  time = time || '';
  var isPM = time.toLowerCase().indexOf('pm') > -1;

  var _date$split = date.split('/'),
      _date$split2 = _slicedToArray(_date$split, 3),
      _date$split2$ = _date$split2[0],
      month = _date$split2$ === void 0 ? 1 : _date$split2$,
      _date$split2$2 = _date$split2[1],
      day = _date$split2$2 === void 0 ? 1 : _date$split2$2,
      _date$split2$3 = _date$split2[2],
      year = _date$split2$3 === void 0 ? 2000 : _date$split2$3;

  var _time$replace$split = time.replace(/am|pm/i, '').split(':'),
      _time$replace$split2 = _slicedToArray(_time$replace$split, 2),
      _time$replace$split2$ = _time$replace$split2[0],
      hours = _time$replace$split2$ === void 0 ? 0 : _time$replace$split2$,
      _time$replace$split2$2 = _time$replace$split2[1],
      minutes = _time$replace$split2$2 === void 0 ? 0 : _time$replace$split2$2;

  var hoursInt = Number(hours);
  var fixedHours = isPM && hoursInt < 12 ? hoursInt + 12 : !isPM && hoursInt === 12 ? hoursInt - 12 : hoursInt;
  return new Date(year, Number(month) - 1, day, fixedHours, minutes);
};

var calendarize = function calendarize(params) {
  var start_date = params.start_date,
      start_time = params.start_time,
      end_date = params.end_date,
      end_time = params.end_time,
      title = params.title,
      description = params.description,
      location = params.location;
  var allDay = !start_time;
  var startDate = parseDateTime(start_date, start_time);
  var start = dateAsISOString(startDate);
  var startShort = dateAsISOString(startDate, allDay);
  var endDate = end_date && end_time ? parseDateTime(end_date, end_time) : startDate;
  var end = dateAsISOString(endDate);
  var endShort = dateAsISOString(endDate, allDay);
  var endLocalized = dateAsISOString(new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000));
  var multiDay = endDate.toDateString() !== startDate.toDateString();
  var fixedDescription = toText(description || '').replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"');
  var event = {
    start: start,
    startDate: startDate,
    startShort: startShort,
    end: end,
    endDate: endDate,
    endShort: endShort,
    endLocalized: endLocalized,
    multiDay: multiDay,
    allDay: allDay,
    location: location || '',
    title: title,
    description: fixedDescription,
    url: '\n\n' + window.location.href
  };
  return {
    gcal: gcal(event),
    ycal: ycal(event),
    ics: ics(event),
    oocal: outlook(event)
  };
};

var onceMap = {},
    fireFBPixel = function fireFBPixel(id) {
  //generic FB ad pixel
  window._fbq.push(['track', id, {
    'value': '0.01',
    'currency': 'USD'
  }]);
},
    fireTWPixel = function fireTWPixel(id) {
  if (window.twttr && window.twttr.conversion) {
    window.twttr.conversion.trackPid(id, {
      tw_sale_amount: 0,
      tw_order_quantity: 0
    });
  }
},
    fireOnce = function fireOnce(fn) {
  return function (id) {
    if (!onceMap[id]) {
      onceMap[id] = true;
      fn(id);
    }
  };
};

var fireFBPixelOnce = fireOnce(fireFBPixel); //create a pixel that can only be added once ever

var fireTWPixelOnce = fireOnce(fireTWPixel); //create a twitter pixel that can only be added once ever

var d = document,
    w = window,
    hostname = w.location.hostname,
    gnow = _.now();

var setcookie = function setcookie(n, v, ex) {
  return d.cookie = n + "=" + v + "; Path=/; domain=" + hostname + "; " + (ex ? "expires=" + new Date(gnow + ex * 864e5).toGMTString() : '');
};
var deletecookie = function deletecookie(name) {
  return d.cookie = name + '=; Path=/; domain=' + hostname + '; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
};
var encryptcookie = {
  setItem: function setItem(k, v) {
    setcookie(k, v);
  },
  getItem: function getItem(k) {
    return (c(k) || '').replace(/\\/g, '').replace(/^\"|\"$/g, "") || '{}';
  },
  clear: function clear(k) {
    deletecookie(k);
  }
}; //alternate functions for saving session cookies if we get to it: would enable some stored functionality even in private mode browsers.

var $w = $(window),
    $html = $(document.documentElement);
function isVisible($el) {
  if (!$el || !$el.length) {
    return true;
  } //if the el is invalid, don't trigger anything that would try to fix it's view


  $el = $el[0];
  var rect = $el.getBoundingClientRect();
  return rect.top >= 0 && rect.bottom <= ($w.height() || $html[0].clientHeight);
}

function gacreate() {
  ga('create', 'UA-48632079-1', {
    'siteSpeedSampleRate': 100,
    'userId': c('mid')
  });
  ga('set', 'dimension1', gup('promoid'));
  ga('set', 'dimension2', gup('s_subsrc'));
}

var resolve = function resolve() {
  var _$;

  return (_$ = $).when.apply(_$, arguments);
};
var reject = function reject() {
  var d = $.Deferred();
  d.reject.apply($, arguments || []);
  return d;
};

/*
        saveicon(dfd, $whereToAddClasses, [$whereToAddEffect]) - chains behavior off existing deferred
        saveicon($whereToAddClasses, [$whereToAddEffect]) - creates new deferred you'll have to resolve or reject yourself
        using [$whereToAddEffect] allows you to prepend the effect to a different element than the top-level classes
        returns a dfd (new or proxied)
    */
function saveicon() {
  var args = _.toArray(arguments),
      resolver = args[0] && args[0].state ? //if first argument was a deferred
  args.shift() : //then we'll use that
  $.Deferred(),
      //otherwise, create a new Deferred
  $outer = args[0],
      //either way, this is now $whereToAddClasses
  $saveicon = $outer.find('.saveicon') //find existing div
  .orAdd('<div class="saveicon"/>') //or create it
  .prependTo(args[1] || $outer).show(); //prepend it to $whereToAddEffect or $whereToAddClasses as fallback and reveal it if it was hidden


  return resolver.done($outer.use('addClass', 'successmsg')) //add class that changes icon to success
  .fail($outer.use('addClass', 'failmsg')) //add class that changes icon to fail
  .progress($saveicon.use('hide')) //hide without special classes
  .always($saveicon.use('fadeOut', 1000, $outer.use('removeClass', 'successmsg failmsg')));
}

/*used in cases where we know the view might blow away other forms on the page that are half completed*/

/*this creates a promise that can then be resolved, resulting in the form fields being re-applied*/
function saveForms($el) {
  var $forms = $el.find('form'),
      formfields = $forms.serializeArray();
  return $.Deferred(function (dfd) {
    dfd.done(function () {
      //lug('formcache resolving');
      var $inputs = $el.find('input,select,textarea');
      $.each(formfields, function (i, v) {
        if (v && v.value) {
          //lug('formcache RESTORING',v.name,v.value);
          $inputs.filter('[name="' + v.name + '"]').val(v.value);
        }
      });
    });
  });
}

/*we do this so much that we might as well make it easier to minify*/
var stopevent = (function (e, prop, imm) {
  if (_.isObject(e) && e.preventDefault) {
    e.preventDefault();

    if (prop) {
      e.stopPropagation();
    }

    if (imm) {
      e.stopImmediatePropagation();
    }
  }
});

var translateMoleLoginByUser = function translateMoleLoginByUser(user) {
  return function (data) {
    lug('server response', data);
    var obj = {};

    if (data.verified) {
      obj.verified = true;
    }

    if (data.registered) {
      obj.is_registered = true;
    }

    if (data.profile) {
      obj.activated = true;

      if (data.profile.avatar_url) {
        obj.avatar = data.profile.avatar_url;
      }

      if (data.profile.email_opt_in) {
        data.profile.email_opt_in = parseFloat(data.profile.email_opt_in);
      }

      $.extend(obj, _.pick(data.profile, user.keys));
    }

    if (data.last_login) {
      obj.last_login = data.last_login;
    }

    if (data.passwd_flag) {
      obj.passwd = true;
    }

    if (data.anonymous) {
      obj.anonymous = data.anonymous;
    }

    if (data.recruits) {
      $.extend(obj, _.pick(data.recruits, user.keys));
    }

    if (data.history) {
      if (data.action_id && !_.contains(user.get('historyactions'), data.action_id)) {
        //console.log('added action',data.action_id,'to historyactions');
        data.history.historyactions = user.get('historyactions').concat(data.action_id);
      } else {
        data.history.historyactions = data.history.actions;
      }

      $.extend(obj, _.omit(data.history, 'action_id', 'actions'));
    }

    if (data.user) {
      if (data.user.activated) {
        obj.activated = true;
      }

      if (!data.user.oauth_provider) {
        obj.activated = true;
      }

      if (data.user.id) {
        obj.moleid = data.user.id;
      }

      if (data.user_type_id) {
        obj.user_type_id = data.user.user_type_id;
      }

      if (data.user.email) {
        obj.email = data.user.email;
      }
    } else {
      if (data.user_type_id) {
        obj.user_type_id = data.user_type_id;
      }
    }

    if (data.email) {
      obj.email = data.email;
    }

    if (data.share_code) {
      obj.share_code = data.share_code;
    }

    if (data.address) {
      _.extend(obj, _.pick(data.address, user.serverwhitelist));
    }

    if (data.id) {
      obj.moleid = data.id;
    }

    if (data.preference_ids && data.preference_ids.length) {
      obj.preference_ids = data.preference_ids;
      obj.preferences = true;
    }

    lug('translating mole: final translated', obj);

    if (obj.prefix && typeof obj.prefix === 'string') {
      obj.prefix = obj.prefix.replace(/\./g, '');
    }

    if (obj.phone && typeof obj.phone === 'string') {
      var cleaned = obj.phone.replace(/\D/g, '');
      var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

      if (match) {
        var intlCode = match[1] ? '+1 ' : '';
        obj.phone = [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
      } else {
        obj.phone = '';
      }
    }

    return obj;
  };
};

/*similar syntax allows creating callback-friendly functions from jQuery collections without the .on() syntax*/
$.fn.use = function (method
/* args */
) {
  var args = _.rest(arguments),
      func = $.proxy(function () {
    return this[method].apply(this, args);
  }, this);

  func.isUseable = true; //flag function as preusable by use

  return func;
};
/* create a function call that can be used as a callback but is guarded against any contextual arguments (or can accept them if you want) */


function use(method
/* args */
) {
  var args = _.rest(arguments); //outer arguments to apply


  return {
    on: function on(what, argln) {
      var func = function func() {
        if (_.isFunction(what) && what.isUseable) {
          what = what(); //if what is itself a function, run it first
        } //lug('use',what,method,args);


        return what[method].apply(what, args.concat(_.first(arguments, argln || 0))); //then run the method on it with the original arguments, plus accepting however many others
      };

      func.isUseable = true; //flag function as preusable by use

      return func;
    }
  };
}

var action_verbs = {
  tweet: 'tweet',
  petition: 'sign a petition',
  'event': 'attend an event',
  recruit: 'recruit friends',
  donate: 'donate'
};

var approutes = {
  "": "welcome",
  "_=_": "welcome",
  "issues-you-care-about": "issues-you-care-about",
  "supporter-home(/:campid)": "supporter-home",
  "join-today": "join-today",
  "resist": "resist",
  "campaigns/your-campaigns(/)(:pid)(/)(:hash)(/)": "campaigns/your-campaigns",
  "campaigns(/)(:id)(/)(:actiontype)(/)(:action)(/)": "campaigns",
  "events/your-events(/)(:pid)(/)(:hash)(/)": "events/your-events",
  "events(/)(:id)(/)(:page)(/)(:hash)(/)(:yhash)(/)": "events",
  "account-settings": "account-settings",
  "faq": "faq",
  "lte": "lte",
  share: "share",
  "opt-out(/:internal)": "opt-out",
  // spanish
  "legado(/)(:id)(/)(:actiontype)(/)(:action)(/)": "campaigns",
  "legacy1(/)(:id)(/)(:actiontype)(/)(:action)(/)": "campaigns",
  "justicia(/)(:id)(/)(:actiontype)(/)(:action)(/)": "campaigns",
  "justice(/)(:id)(/)(:actiontype)(/)(:action)(/)": "campaigns",
  "*path": "404"
};

var user_defaults = {
  avatar: false,
  //we can store avatar references
  activated: false,
  //has the user entered enough key bits of information we need (could include login, might not)
  verified: 0,
  anonymous: 0,
  first_name: false,
  //only one of these we'll save in the local object
  last_name: false,
  share_code: false,
  phone: false,
  prefix: false,
  street_address: false,
  additional_address: false,
  city: false,
  state: false,
  zipcode: false,
  //only save if true or not for the rest
  email: false,
  email_opt_in: false,
  email_opt_in_campaign_updates: false,
  email_opt_in_user_recommendations: false,
  email_opt_in_fb_friend_recommendations: false,
  gender: false,
  preferences: false,
  //issue categories, but not called issues
  passwd: false,
  //do they have a password/i.e. the ability to log in
  loggedin: false,
  //currently logged in
  fbloggedin: false,
  //currently logged in via facebook
  fbconnected: false,
  //most recent flag about whether they have authorized out app/fbconnect or not
  fbperms: '',
  moleid: false,
  user_type_id: false,
  //1 is for logged in
  //moleaddrid: false,
  num_actions: 0,
  prospective: false,
  num_campaigns: 0,
  num_recruits: 0,
  category_completion: {},
  historyactions: [],
  yourcampaigns: [],
  preference_ids: [],
  num_secondary_recruits: 0,
  recruit_names: [],
  num_secondary_actions: 0,
  recs: [],
  last_login: 0,
  randfb_friends: [],
  num_events: 0,
  recruits_info: [],
  lastloaded: null,
  //last time we pulled user information from MOLE important to know because someone might leave a browser tab open for a long time, make changes elsewhere, and we'd want to know to recheck
  lastsync: 0 //last time we synced data with MOLE

};

/*simple plugin factory for attaching plugins to views. View, what to do when added/redrawn, what to when it needs to be removed*/
var plugin = function plugin(view, _add, _remove, name) {
  view.loaded.add(function () {
    _add(view); //when the view loads, run the plugin add function on the view

  });

  function removed() {
    _remove(view); //create a function that runs the plugin remove function the view

  }

  view.firstload.done(function () {
    view.loading.add(removed);
  }); //once view has loaded once, add the plugin teardown code to any subsequent reloads of the view

  view.removed.always(removed); //and always remove the plugin on view teardown

  if (name) {
    view[name] = {
      remove: function remove() {
        _remove(view);
      },
      add: function add() {
        _add(view);
      }
    };
  }
};
/*adds swiper to a view*/

var addSwiper = function addSwiper(view) {
  plugin(view, function () {
    var $outer = view.$el.find('.swiper-container'),
        viewops = view.$el.data(); //lug('swiper viewops',viewops);

    if ($outer.length) {
      view.$swiper = $outer.swiper({
        pagination: view.$el.find('.swiper-pagination')[0],
        paginationClickable: true,
        queueEndCallbacks: true,
        preventLinksPropagation: true,
        calculateHeight: true,
        centeredSlides: !!viewops.centered,
        loopedSlides: 2,
        loopAdditionalSlides: 2,
        loop: !viewops.noloop,
        //simulateTouch: false,//bit wonky on the desktop
        grabCursor: true,
        slidesPerView: $outer.data('slidenum')
      });
    }
  }, function remove() {
    view.$el.find('.swiper-container').each(function () {
      var $swiper = $(this),
          swiper = $swiper.data('swiper');

      if (swiper) {
        $swiper.data('swiper').destroy();
        $swiper.data('swiper', null);
        $swiper.empty();
      }
    });
    view.$swiper = undefined;
  }, 'p_swiper');
};
/*adds swiper to a view*/

var addSwiperV = function addSwiperV(view) {
  plugin(view, function () {
    var $outer = view.$el.find('.swiper-container');
        view.$el.data();
    var numSlides = $outer.children('.swiper-wrapper').children('.swiper-slide').length;
    var heightSlides = numSlides * $outer.parent().height(); //lug('swiper viewops',viewops);

    view.$swiper = $outer.swiper({
      pagination: view.$el.find('.swiper-pagination')[0],
      paginationClickable: true,
      mode: 'vertical',
      autoplay: 3000,
      loop: true,
      speed: 1000,
      height: heightSlides,
      calculateHeight: true,
      //simulateTouch: false,//bit wonky on the desktop
      grabCursor: true,
      slidesPerView: 1
    });
  }, function remove() {
    view.$el.find('.swiper-container').each(function () {
      var $swiper = $(this),
          swiper = $swiper.data('swiper');

      if (swiper) {
        $swiper.data('swiper').destroy();
        $swiper.data('swiper', null);
        $swiper.empty();
      }
    });
  });
};

var SimpleCollection = Backbone.Collection.extend({
  byIds: function byIds(arrayIds) {
    //get items from an array of ids and return as an array of their JSON
    return _.chain(arrayIds).map(_.bind(this.get, this)) //create a function that will run the ids through the get method of the collection
    .compact() //no missing ones allowed
    .invoke('toJSON') //jsonize
    .value(); //return unwrapped
  },
  random: function random(exclude_id) {
    //returns dfd with json of a random campaign
    var modelids = _.without(this.pluck('id'), exclude_id || null);

    return this.get(modelids[~~(Math.random() * (modelids.length - 1))]).toJSON();
  },
  randomdfd: function randomdfd(exclude_id) {
    return $.Deferred().resolve(this.random(exclude_id));
  }
});

var EventsTabs = /*#__PURE__*/function (_React$Component) {
  _inherits(EventsTabs, _React$Component);

  var _super = _createSuper(EventsTabs);

  function EventsTabs() {
    _classCallCheck(this, EventsTabs);

    return _super.apply(this, arguments);
  }

  _createClass(EventsTabs, [{
    key: "render",
    value: function render() {
      var _this$props = this.props,
          route = _this$props.route,
          path = _this$props.path,
          loggedin = _this$props.loggedin;
      return /*#__PURE__*/React.createElement("div", {
        className: "tabs row has-tabs"
      }, loggedin && /*#__PURE__*/React.createElement("ol", null, /*#__PURE__*/React.createElement("li", {
        className: "".concat(path === '/events' && 'selected'),
        id: "eventstab",
        onClick: function onClick() {
          return route('/events');
        }
      }, "All"), /*#__PURE__*/React.createElement("li", {
        className: "".concat(path === '/your-events' && 'selected'),
        onClick: function onClick() {
          return route('/your-events');
        },
        id: "youreventstab"
      }, "Events You're Attending")));
    }
  }]);

  return EventsTabs;
}(React.Component);

var EventsSearch = /*#__PURE__*/function (_React$Component) {
  _inherits(EventsSearch, _React$Component);

  var _super = _createSuper(EventsSearch);

  function EventsSearch() {
    _classCallCheck(this, EventsSearch);

    return _super.apply(this, arguments);
  }

  _createClass(EventsSearch, [{
    key: "render",
    value: function render() {
      var SelectComponents = this.props.SelectComponents;
      return /*#__PURE__*/React.createElement("div", {
        className: "search-bar events-search-large"
      }, /*#__PURE__*/React.createElement("form", {
        onSubmit: function onSubmit(e) {
          return e.preventDefault();
        }
      }, /*#__PURE__*/React.createElement("ul", {
        className: "search-types"
      }, SelectComponents.map(function (column, idx) {
        return /*#__PURE__*/React.createElement("div", {
          className: "search-column",
          key: idx
        }, column.map(function (SelectComponent) {
          return SelectComponent;
        }));
      }))));
    }
  }]);

  return EventsSearch;
}(React.Component); // visibility: visible; display: block; width: 230px; top: 40px; left: 0px;

var Select = function Select(props) {
  var options = props.options,
      _onClick = props.onClick,
      searchParam = props.searchParam,
      label = props.label,
      selectMenuVisible = props.selectMenuVisible,
      selectedValueName = props.selectedValueName;
  var isVisible = searchParam === selectMenuVisible;
  var fieldName = 'event-' + label.split(' ')[0].toLowerCase();
  return /*#__PURE__*/React.createElement("li", {
    className: "select-field categories-search ".concat(fieldName),
    onClick: function onClick(e) {
      var target = e.target;
      var dataset = target.dataset;
      var name = dataset.name,
          value = dataset.value;

      _onClick({
        searchValue: value,
        searchParam: searchParam,
        selectedValueName: name
      });
    }
  }, /*#__PURE__*/React.createElement("label", {
    htmlFor: fieldName
  }, label, ":"), /*#__PURE__*/React.createElement("div", {
    className: "selectize-control single"
  }, /*#__PURE__*/React.createElement("select", {
    name: fieldName,
    id: fieldName,
    tabIndex: "-1",
    className: "selectized"
  }), /*#__PURE__*/React.createElement("div", {
    className: "selectize-input items full has-options has-items focus input-active ".concat(isVisible ? 'dropdown-active' : '')
  }, /*#__PURE__*/React.createElement("div", {
    className: "item"
  }, selectedValueName)), /*#__PURE__*/React.createElement("div", {
    className: "selectize-dropdown single",
    style: {
      visibility: isVisible ? 'visible' : 'hidden',
      display: 'block',
      width: '230px',
      top: '40px',
      left: 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "selectize-dropdown-content"
  }, options.map(function (option) {
    var id = option.id,
        name = option.name;
    return /*#__PURE__*/React.createElement("div", {
      key: id,
      "data-selectable": "",
      className: "option",
      "data-value": id,
      "data-name": name
    }, name);
  })))));
};

var ZipInput = function ZipInput(props) {
  var value = props.value,
      label = props.label,
      _onChange = props.onChange;
  return /*#__PURE__*/React.createElement("li", {
    className: "select-field zip-search"
  }, /*#__PURE__*/React.createElement("label", {
    htmlFor: "zip",
    id: "location-field"
  }, label, ":"), /*#__PURE__*/React.createElement("input", {
    type: "tel",
    id: "zip",
    name: "zip",
    value: value,
    onChange: function onChange(e) {
      return _onChange({
        inputName: 'zip',
        e: e
      });
    }
  }));
};

var DateInput = function DateInput(props) {
  var onDateChange = props.onDateChange,
      dateInputValues = props.dateInputValues,
      min = props.min;
  var start_date = dateInputValues.start_date,
      end_date = dateInputValues.end_date;
  return /*#__PURE__*/React.createElement("li", {
    className: "date-fields date-search"
  }, /*#__PURE__*/React.createElement("label", {
    htmlFor: "start-date-field",
    id: "date-field"
  }, "Dates:"), /*#__PURE__*/React.createElement("div", {
    className: "date-combo"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("input", {
    tabIndex: "4",
    title: "Start Date",
    placeholder: "mm/dd/yyy",
    name: "start_date",
    id: "start-date-field",
    type: "date",
    max: "2026-12-31",
    min: min,
    value: start_date,
    onChange: function onChange(e) {
      return onDateChange({
        inputName: 'start_date',
        e: e
      });
    }
  }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("i", null, "\u27F6")), /*#__PURE__*/React.createElement("input", {
    tabIndex: "5",
    id: "end-date-field",
    title: "End Date",
    max: "2026-12-31",
    placeholder: "mm/dd/yyy",
    name: "end_date",
    min: min,
    type: "date",
    value: end_date,
    onChange: function onChange(e) {
      return onDateChange({
        inputName: 'end_date',
        e: e
      });
    }
  }))));
};

var SearchInput = function SearchInput(props) {
  var _onChange = props.onChange,
      _onKeyPress = props.onKeyPress,
      onSearchButtonClick = props.onSearchButtonClick,
      value = props.value;
  return /*#__PURE__*/React.createElement("li", {
    className: "search-field"
  }, /*#__PURE__*/React.createElement("label", {
    htmlFor: "event-search"
  }, "Search:"), /*#__PURE__*/React.createElement("input", {
    type: "search",
    tabIndex: "6",
    name: "keywords",
    placeholder: "Search Events",
    id: "event-search",
    className: "search-input",
    onChange: function onChange(e) {
      return _onChange({
        e: e,
        inputName: 'keywords'
      });
    },
    onKeyPress: function onKeyPress(e) {
      return _onKeyPress(e);
    },
    value: value
  }), /*#__PURE__*/React.createElement("button", {
    onClick: function onClick() {
      return onSearchButtonClick();
    },
    className: "button blue-button",
    title: "search"
  }));
};

var Pagination = function Pagination(props) {
  var paginationData = props.paginationData,
      onPageClick = props.onPageClick;
  var pages = paginationData.pages,
      cur_page = paginationData.cur_page;
  return /*#__PURE__*/React.createElement("div", {
    className: "pagination"
  }, pages.map(function (page, idx) {
    var pageNumber = page.pageNumber;
        page.events;
    var isCurPage = cur_page === idx;
    return /*#__PURE__*/React.createElement("button", {
      key: pageNumber,
      className: "page ".concat(isCurPage && 'current'),
      onClick: function onClick() {
        return onPageClick(page);
      }
    }, pageNumber);
  }));
};

var safestring = function safestring(string) {
  return string.replace(/\'/, '&#039;').replace(/\)/, '&#41;');
};

var currentPopup = null;

function Popup(_ref) {
  var position = _ref.position,
      contentElement = _ref.contentElement;
  this.position = position;
  this.contentElement = contentElement;
  var pixelOffset = document.createElement('div');
  pixelOffset.classList.add('popup-bubble-anchor');
  pixelOffset.appendChild(contentElement);
  this.anchor = document.createElement('div');
  this.anchor.classList.add('popup-tip-anchor');
  this.anchor.appendChild(pixelOffset);
}

var addMethodsToPopupPrototype = function addMethodsToPopupPrototype(_ref2) {
  var map = _ref2.map;
  Popup.prototype = Object.create(window.google.maps.OverlayView.prototype);

  Popup.prototype.onAdd = function () {
    this.getPanes().floatPane.appendChild(this.anchor);
    window.google.maps.event.addDomListener(this.anchor, 'click', function () {
      if (currentPopup) {
        currentPopup.setMap(null);
      }
    });
  };
  /** Called when the popup is removed from the map. */


  Popup.prototype.onRemove = function () {
    if (this.anchor.parentElement) {
      this.anchor.parentElement.removeChild(this.anchor);
    }
  };
  /** Called when the popup needs to draw itself. */


  Popup.prototype.draw = function () {
    var divPosition = this.getProjection().fromLatLngToDivPixel(this.position); // Hide the popup when it is far out of view.

    var display = Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000 ? 'block' : 'none';
    /**
     *
     *  Here we're finding out if the popup is on the left 61% of the map area.
     *
     *  If it is, then we add the class .lefthand, and floats the popup off-center by 70px
     *
     */

    lat0 = map.getBounds().getNorthEast().lat();
    lat1 = map.getBounds().getSouthWest().lat();
    var sixtyPercent = (lat1 - lat0) * 0.61 + lat0;

    if (display === 'block') {
      if (divPosition.x < sixtyPercent) {
        this.anchor.classList.add('lefthand');
        var divPositionSixty = divPosition.x + 70;
        this.anchor.style.left = divPositionSixty + 'px';
      } else {
        this.anchor.classList.remove('lefthand');
        this.anchor.style.left = divPosition.x + 'px';
      }

      var anchorMarkerOffset = divPosition.y - 40;
      this.anchor.style.top = anchorMarkerOffset + 'px';
    }

    if (this.anchor.style.display !== display) {
      this.anchor.style.display = display;
    }
  };
};

var POPUP_CONTENT_ELEMENT = function POPUP_CONTENT_ELEMENT(_ref3) {
  var event = _ref3.event;
  var slug = event.slug,
      title = event.title,
      start_date = event.start_date,
      start_time = event.start_time,
      img_url = event.img_url;
  var safeTitle = safestring(title);
  var dateToDisplay = displayDate(start_date);
  var eventDay = dateToDisplay.eventDay,
      month = dateToDisplay.month;
  var divContainer = document.createElement('div');
  var _event$timezone = event.timezone,
      timezone = _event$timezone === void 0 ? '' : _event$timezone;

  switch (timezone) {
    case 'eastern':
      timezone = 'ET';
      break;

    case 'central':
      timezone = 'CT';
      break;

    case 'mountain':
      timezone = 'MT';
      break;

    case 'pacific':
      timezone = 'PT';
      break;

    case 'alaska':
      timezone = 'AKT';
      break;

    case 'hawaii':
      timezone = 'HT';
      break;

    default:
      timezone = '';
  }

  divContainer.classList.add('popup-bubble-content');
  divContainer.innerHTML = "\n      <div class=\"infowindow\">\n        <a href=\"/events/".concat(slug, "\" target=\"_blank\">\n          <div class=\"infowindow-data\">\n            <p class=\"event-title\">").concat(safeTitle, "</p>\n            <div class=\"events-section\">\n              <p class=\"info-time\">").concat(month, " ").concat(eventDay, ", ").concat(start_time, " ").concat(timezone, "</p>\n            </div>\n            ").concat(img_url ? "<div class=\"info-image\"><img src=".concat(img_url, " class=\"info-image-src\" alt=\"popup marker image\"/></div>") : '', "\n          </div>\n        </a>\n        <div class=\"popup-bubble-close\" id=\"popup-close\"></div>\n      </div>\n  ");
  return divContainer;
};

var showPopup = function showPopup(_ref4) {
  var event = _ref4.event,
      position = _ref4.position,
      map = _ref4.map;
  var contentElement = POPUP_CONTENT_ELEMENT({
    event: event
  });
  addMethodsToPopupPrototype({
    map: map
  });
  currentPopup = new Popup({
    position: position,
    contentElement: contentElement
  });
  currentPopup.setMap(map);
};

var MapMarker = /*#__PURE__*/function (_React$Component) {
  _inherits(MapMarker, _React$Component);

  var _super = _createSuper(MapMarker);

  function MapMarker() {
    _classCallCheck(this, MapMarker);

    return _super.apply(this, arguments);
  }

  _createClass(MapMarker, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      var _this$props = this.props,
          maps = _this$props.maps,
          lat = _this$props.lat,
          lng = _this$props.lng,
          index = _this$props.index,
          map = _this$props.map,
          onEventMouseover = _this$props.onEventMouseover;
          _this$props.hoveredEvent;
          var zIndex = _this$props.zIndex,
          event = _this$props.event,
          eventRefs = _this$props.eventRefs,
          eventsGridRef = _this$props.eventsGridRef,
          id = _this$props.id;
      var position = new maps.LatLng(lat, lng);
      this.DEFAULT_ICON = {
        url: '/img/icons/map-marker.svg',
        scaledSize: new maps.Size(50, 50),
        origin: new maps.Point(0, 0),
        labelOrigin: new maps.Point(25, 20),
        anchor: new maps.Point(25, 50)
      };
      this.SELECTED_ICON = {
        url: '/img/icons/map-marker-hover.svg',
        scaledSize: new maps.Size(50, 50),
        origin: new maps.Point(0, 0),
        labelOrigin: new maps.Point(25, 20),
        anchor: new maps.Point(25, 50)
      };
      this.marker = new maps.Marker({
        position: position,
        label: {
          text: index.toString(),
          color: "white",
          fontSize: '20px',
          fontFamily: "Ringside Regular A"
        },
        origin: new maps.Point(0, 0),
        anchor: new maps.Point(25, 50),
        icon: this.DEFAULT_ICON,
        zIndex: zIndex,
        map: map
      });
      this.marker.addListener('mouseover', function () {
        var eventRef = eventRefs[id];
        var offsetTop = eventRef.offsetTop;
        eventsGridRef.current.scrollTo(0, offsetTop);
        onEventMouseover(index);
      });
      this.marker.addListener('click', function () {
        if (currentPopup) {
          currentPopup.setMap(null);
        }

        showPopup({
          event: event,
          position: position,
          map: map
        });
      });
      this.marker.addListener('mouseout', function () {
        onEventMouseover(0);
      });
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.marker.setMap(null);
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props2 = this.props,
          isHoveredEvent = _this$props2.isHoveredEvent,
          zIndex = _this$props2.zIndex;

      if (isHoveredEvent && this.marker) {
        this.marker.setIcon(this.SELECTED_ICON);
        this.marker.setZIndex(100);
      } else if (this.marker) {
        this.marker.setIcon(this.DEFAULT_ICON);
        this.marker.setZIndex(zIndex);
      }

      return null;
    }
  }]);

  return MapMarker;
}(React.Component);

var EventDetails = /*#__PURE__*/function (_React$Component) {
  _inherits(EventDetails, _React$Component);

  var _super = _createSuper(EventDetails);

  function EventDetails() {
    _classCallCheck(this, EventDetails);

    return _super.apply(this, arguments);
  }

  _createClass(EventDetails, [{
    key: "render",
    value: function render() {
      var _this$props = this.props,
          apipath = _this$props.apipath,
          eventDetailsData = _this$props.eventDetailsData;
      console.log('eventDetailsData ', eventDetailsData);
      var title = eventDetailsData.title,
          start_date = eventDetailsData.start_date,
          start_time = eventDetailsData.start_time,
          end_time = eventDetailsData.end_time;
          eventDetailsData.end_date;
          var admin_campaign = eventDetailsData.admin_campaign;
      var eventDates = displayDate(start_date);
      var eventDay = eventDates.eventDay,
          month = eventDates.month;
      console.log('eventDates ', eventDates);
      return /*#__PURE__*/React.createElement("section", {
        className: "event-details-page"
      }, /*#__PURE__*/React.createElement("div", {
        className: "grid-row"
      }, /*#__PURE__*/React.createElement("a", {
        href: "".concat(apipath, "/events"),
        className: "event-page-link"
      }, "\u2039 Back to All Events"), /*#__PURE__*/React.createElement("div", {
        className: "row event-details-intro"
      }, /*#__PURE__*/React.createElement("div", {
        className: "col-8"
      }, /*#__PURE__*/React.createElement("div", {
        className: "event-title-block"
      }, /*#__PURE__*/React.createElement("h1", {
        className: "h2"
      }, title)), /*#__PURE__*/React.createElement("ul", {
        className: "event-info"
      }, /*#__PURE__*/React.createElement("li", {
        className: "event-date"
      }, /*#__PURE__*/React.createElement("span", {
        className: "h4 bold"
      }, eventDay, ", ", month), /*#__PURE__*/React.createElement("span", {
        className: "add-to-cal"
      }, "[ ", /*#__PURE__*/React.createElement("u", null, "Add to calendar"), " ]")), start_time && /*#__PURE__*/React.createElement("li", {
        className: "event-time opensans"
      }, start_time, " ", end_time && start_time !== end_time && "-".concat(end_time)))), admin_campaign && /*#__PURE__*/React.createElement("div", {
        className: "related-campaign col-3"
      }, /*#__PURE__*/React.createElement("h4", {
        className: "h4"
      }, "This Event is Part of a Campaign"), /*#__PURE__*/React.createElement("div", {
        className: "related-campaign-link"
      }, /*#__PURE__*/React.createElement("figure", {
        "class": "campaign-tile"
      }, /*#__PURE__*/React.createElement("a", {
        href: "/campaigns/".concat(admin_campaign.slug)
      }, /*#__PURE__*/React.createElement("span", {
        "class": "campaign-image"
      }, /*#__PURE__*/React.createElement("img", {
        src: "".concat(admin_campaign.image, "/@zx_175@zy_175"),
        alt: "".concat(admin_campaign.title)
      }))), /*#__PURE__*/React.createElement("figcaption", null, admin_campaign.top_action ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("a", {
        href: "/campaigns/".concat(admin_campaign.slug, "/").concat(top_action.featured_action_type !== "event" ? "/".concat(admin_campaign.top_action.featured_action_slug) : '')
      }, /*#__PURE__*/React.createElement("span", {
        className: "campaign-top-action icon"
      }, /*#__PURE__*/React.createElement("i", {
        className: "action-".concat(top_action.featured_action_type)
      }), " ", admin_campaign.top_action.featured_action_phrase)), /*#__PURE__*/React.createElement("a", {
        href: "/campaigns/{{slug}}",
        className: "block-link"
      }), /*#__PURE__*/React.createElement("span", {
        className: "campaign-name{{#iflonger title 55}} long-title{{/iflonger}}"
      }, admin_campaign.title), /*#__PURE__*/React.createElement("span", {
        className: "supporter-count"
      }, /*#__PURE__*/React.createElement("strong", null, admin_campaign.num_supporters_current), " Supporters ", admin_campaign.num_supporters_current)) : null)))))));
    }
  }]);

  return EventDetails;
}(React.Component);

var apipath = '/api/v1';
var LEGACY_URL_REGEX = /\/events\/\d\/(.+)/; //const EVENTS_PER_PAGE = 20;

var MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
var CATEGORY_MAP = {
  1: 'Sierra Club Outing',
  2: 'Social Event',
  3: 'Activist Event',
  4: 'Club Support Event'
};
var LOAD_GOOGLE_MAPS = true;
var categoryOptions = [{
  id: 'none',
  name: 'All'
}, {
  id: '1',
  name: 'Sierra Club Outing'
}, {
  id: '2',
  name: 'Social Event'
}, {
  id: '3',
  name: 'Activist Event'
}, {
  id: '4',
  name: 'Club Support Event'
}];
var GOOGLE_MAP_CONTROL_CONFIG = {
  zoomControl: true,
  mapTypeControl: true,
  scaleControl: true,
  streetViewControl: false,
  rotateControl: true,
  fullscreenControl: true
};

var createScript = function createScript(src, onLoadCb) {
  var script = document.createElement('script');
  var body = document.body;
  script.setAttribute('async', true);
  script.setAttribute('src', src);
  script.addEventListener('load', function () {
    onLoadCb();
  });
  body.appendChild(script);
};

var eventTypeOptions = [{
  id: 'none',
  name: 'All'
}];
var umbrellaEventOptions = [{
  id: 'none',
  name: 'All'
}];
var zipValidationRegex = /^\d{5}$/;
var weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
var displayDate = function displayDate(date, _long) {
  var eventDateArray = date.split('/');
  var eventMonth = parseInt(eventDateArray[0] - 1, 10);
  var month = MONTH_NAMES[eventMonth];
  var eventDay = parseInt(eventDateArray[1], 10);
  var aDate = new Date(eventDateArray[2], eventMonth, eventDay);
  var weekday = weekdays[aDate.getDay()];
  weekday = _long === true ? weekday : weekday.slice(0, 3);
  return {
    eventDay: eventDay,
    month: _long ? month : month.slice(0, 3),
    weekday: weekday
  };
};

var createStartDateSearchParam = function createStartDateSearchParam() {
  var todaysDate = new Date();
  return todaysDate.getFullYear() + '-' + ("0" + (todaysDate.getMonth() + 1)).slice(-2) + '-' + ("0" + todaysDate.getDate()).slice(-2);
};

var getSearchedEventsPromise = function getSearchedEventsPromise(_ref) {
  var searchParams = _ref.searchParams;
  var hasStartDate = searchParams.indexOf('start_date') > -1;
  var hasZip = searchParams.indexOf('zip') > -1;
  var hasRadius = searchParams.indexOf('radius') > -1;
  searchParams.indexOf('umbrella_event_id') > -1;
  var curriedSearchParams = '';

  if (!hasStartDate) {
    var startDate = createStartDateSearchParam();
    curriedSearchParams = "".concat(searchParams, "&start_date=").concat(startDate);
  } else {
    curriedSearchParams = "".concat(searchParams);
  }

  if (!hasRadius) {
    curriedSearchParams = "".concat(curriedSearchParams, "&").concat(hasZip ? 'radius=500' : 'radius=3000');
  }

  return jQuery.getJSON("".concat(apipath, "/events/search?").concat(curriedSearchParams, "&limit=100"));
};

var createSearchString = function createSearchString(_ref2) {
  var searchValue = _ref2.searchValue,
      searchParam = _ref2.searchParam,
      isCategorySearch = _ref2.isCategorySearch,
      isZipSearch = _ref2.isZipSearch;
  var _window = window,
      location = _window.location;
  var search = location.search;
  var searchString = search.substring(1);
  var isNone = searchValue === 'none';
  var newSearchString = '';

  if (searchString) {
    var searchObject = JSON.parse('{"' + decodeURI(searchString).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') + '"}');

    if (isNone || !searchValue) {
      delete searchObject[searchParam];
    } else {
      searchObject[searchParam] = searchValue;
    }

    if (isCategorySearch) {
      delete searchObject['type_id'];
    }

    if (isZipSearch) {
      delete searchObject['lat'];
      delete searchObject['lng'];
      delete searchObject['radius'];
    }

    newSearchString = "".concat(Object.keys(searchObject).map(function (key) {
      return key + '=' + searchObject[key];
    }).join('&'));
  } else if (searchValue && searchParam && !isNone) {
    newSearchString = "".concat(searchParam, "=").concat(searchValue);
  }

  return newSearchString;
};

var fetchEventTypes = function fetchEventTypes(searchValue) {
  //const eventTypesFetch = fetch(`${apipath}/event_types?category_id=${searchValue}`);
  //return eventTypesFetch.then((response) => {
  //  return response.json().then((json) => {
  //    return json;
  //  });
  //});
  return jQuery.getJSON("".concat(apipath, "/event_types?category_id=").concat(searchValue));
};

var mapSearchValuesToState = function mapSearchValuesToState(_ref3) {
  var searchObject = _ref3.searchObject,
      eventTypeName = _ref3.eventTypeName,
      umbrellaEventName = _ref3.umbrellaEventName;
  var category = searchObject.category,
      zip = searchObject.zip,
      keywords = searchObject.keywords,
      start_date = searchObject.start_date,
      end_date = searchObject.end_date;
      searchObject.umbrella_event_id;
  var categoryName = CATEGORY_MAP[parseInt(category)];
  return {
    category: categoryName || 'All',
    type_id: eventTypeName || 'All',
    zip: zip || '',
    keywords: keywords || '',
    start_date: start_date || '',
    end_date: end_date || '',
    umbrella_event_id: umbrellaEventName || 'All'
  };
};

function searchEvents(_ref4) {
  var _this = this;

  var searchValue = _ref4.searchValue,
      searchParam = _ref4.searchParam,
      isCategorySearch = _ref4.isCategorySearch,
      queryString = _ref4.queryString,
      isZipSearch = _ref4.isZipSearch;
  var searchParams = queryString || createSearchString({
    searchValue: searchValue,
    searchParam: searchParam,
    isCategorySearch: isCategorySearch,
    isZipSearch: isZipSearch
  });
  var getEventsPromise = getSearchedEventsPromise({
    searchParams: searchParams
  });
  var _window2 = window,
      location = _window2.location,
      history = _window2.history;
  var pathname = location.pathname;
  history.pushState(null, null, "".concat(pathname).concat(searchParams ? "?".concat(searchParams) : ''));
  this.setState({
    loading: true
  });
  return getEventsPromise.then(function (json) {
    _this.setState({
      displayEvents: []
    });

    var data = json.data,
        metadata = json.metadata;
    var filteredData = data.filter(function (event) {
      return !Number(event.private_event);
    });
    var numberOfEvents = filteredData.length;
    var EVENTS_PER_PAGE = metadata ? 100 : 20;
    var totalPages = Math.ceil(numberOfEvents / EVENTS_PER_PAGE);
    var totalPagesArray = [];
    var startIndex = 0;

    for (var i = 0; i < totalPages; i += 1) {
      var endIndex = EVENTS_PER_PAGE + startIndex;
      var pageData = {
        pageNumber: i + 1,
        events: filteredData.slice(startIndex, endIndex),
        index: i
      };
      startIndex = endIndex;
      totalPagesArray.push(pageData);
    }

    var paginationData = {
      pages: totalPagesArray,
      cur_page: 0
    };
    var firstPage = totalPagesArray[0];
    var displayEvents = firstPage ? firstPage['events'] : [];

    _this.setState({
      events: filteredData,
      paginationData: paginationData,
      displayEvents: displayEvents,
      umbrellaEventMetaData: metadata || null,
      loading: false,
      hasNoResults: !numberOfEvents
    });

    return displayEvents;
  });
}

function getRegisteredEvents(_ref5) {
  var moleid = _ref5.moleid,
      guid = _ref5.guid;
  //return fetch(`${apipath}/users/${moleid}/registered-events?api_token=${guid}`).then((response) => {
  //  return response.json((registeredEventsJson) => {
  //    return registeredEventsJson;
  //  });
  //});
  return jQuery.getJSON("".concat(apipath, "/users/").concat(moleid, "/registered-events?api_token=").concat(guid));
}

var Events = /*#__PURE__*/function (_React$Component) {
  _inherits(Events, _React$Component);

  var _super = _createSuper(Events);

  function Events(props) {
    var _this2;

    _classCallCheck(this, Events);

    _this2 = _super.call(this, props);

    _defineProperty(_assertThisInitialized(_this2), "state", {
      events: [],
      displayEvents: [],
      path: '/events',
      options: {
        categoryOptions: categoryOptions,
        eventTypeOptions: eventTypeOptions,
        umbrellaEventOptions: umbrellaEventOptions
      },
      searchValues: {
        category: 'All',
        type_id: 'All',
        umbrella_event_id: 'All',
        zip: '',
        keywords: '',
        start_date: '',
        end_date: '',
        lat: '',
        lng: ''
      },
      selectMenuVisible: '',
      paginationData: {
        cur_page: 0,
        pages: []
      },
      googleMapsLoaded: false,
      hoveredEvent: 0,
      umbrellaEventMetaData: null,
      loading: true,
      hasNoResults: false,
      showEventDetail: false,
      eventDetailsData: {},
      showMapOrList: 'list'
    });

    _defineProperty(_assertThisInitialized(_this2), "formatDate", function (dateString) {
      var date = new Date(dateString);
      var month = date.getMonth();

      if (isNaN(month)) {
        return 'Date is invalid';
      }

      var theMonth = MONTH_NAMES[month];
      var day = date.getUTCDate();
      var year = date.getFullYear();
      return "".concat(theMonth, " ").concat(day, ", ").concat(year);
    });

    _defineProperty(_assertThisInitialized(_this2), "formatTime", function (timeString) {
      var time = timeString.split(':');
      var h = parseInt(time[0]),
          m = time[1],
          ampm = time[2] && time[2].match(/pm/i) || time[3] && time[3].match(/pm/i) ? 'pm' : 'am';

      if (h > 12) {
        h -= 12;
        ampm = 'pm';
      }

      return "".concat(h, ":").concat(m).concat(ampm);
    });

    _this2.mapRef = React.createRef();
    _this2.eventsGridRef = React.createRef();
    _this2.seed = window.seed;
    _this2.eventsTileRefs = {};
    return _this2;
  }

  _createClass(Events, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      var _this3 = this;

      this.searchEvents = searchEvents.bind(this);
      this.getRegisteredEvents = getRegisteredEvents.bind(this);
      var user = this.props.user;
      user.on('change', function () {
        if (user.hasChanged('loggedin')) {
          _this3.forceUpdate();
        }
      }); // create umbrella event options array from seed

      var options = this.state.options;
      var seedUmbrellaEvents = window.seed.umbrella_events || {};
      var umbrellaEventSeedKeys = Object.keys(seedUmbrellaEvents);
      var umbrellaEventsArray = umbrellaEventSeedKeys.map(function (umbrellaEventId) {
        return {
          id: umbrellaEventId,
          name: seedUmbrellaEvents[umbrellaEventId]
        };
      });
      options.umbrellaEventOptions = [].concat(umbrellaEventOptions, _toConsumableArray(umbrellaEventsArray)); // search events and plug values into state based on query string

      var queryString = window.location.search;
      var searchString = queryString.substr(1);
      var pathname = window.location.pathname;
      var isLegacyUrl = LEGACY_URL_REGEX.test(pathname);

      if (isLegacyUrl) {
        var legacySearchString = pathname.match(LEGACY_URL_REGEX)[1];
        window.location = "".concat(window.location.origin, "/events?").concat(legacySearchString);
      } else {
        this.initSearch({
          searchString: searchString,
          options: options,
          seedUmbrellaEvents: seedUmbrellaEvents
        });
      }
    }
  }, {
    key: "initSearch",
    value: function initSearch(_ref6) {
      var _this4 = this;

      var searchString = _ref6.searchString,
          options = _ref6.options,
          seedUmbrellaEvents = _ref6.seedUmbrellaEvents;

      if (searchString) {
        var searchObject = JSON.parse('{"' + decodeURI(searchString).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') + '"}');
        this.state.searchValues;
        var category = searchObject.category,
            type_id = searchObject.type_id,
            umbrella_event_id = searchObject.umbrella_event_id;
        var umbrellaEventName = 'All';

        if (umbrella_event_id) {
          umbrellaEventName = seedUmbrellaEvents[parseInt(umbrella_event_id)];
        }

        if (category) {
          fetchEventTypes(category).then(function (json) {
            var eventTypesArray = json.data;
            options.eventTypeOptions = [].concat(eventTypeOptions, _toConsumableArray(eventTypesArray));
            var eventTypeName = 'All';

            if (type_id) {
              eventTypeName = eventTypesArray.filter(function (eventType) {
                return eventType.id === type_id;
              })[0].name;
            }

            var stateFromSearchObject = mapSearchValuesToState({
              searchObject: searchObject,
              eventTypeName: eventTypeName,
              umbrellaEventName: umbrellaEventName
            });

            _this4.setState({
              searchValues: stateFromSearchObject,
              options: options
            });
          });
        } else {
          var stateFromSearchObject = mapSearchValuesToState({
            searchObject: searchObject,
            umbrellaEventName: umbrellaEventName
          });
          this.setState({
            searchValues: stateFromSearchObject,
            options: options
          });
        }
      }

      this.searchEvents({}).then(function (eventsArray) {
        // create script to load Google map
        if (LOAD_GOOGLE_MAPS) {
          createScript('https://maps.googleapis.com/maps/api/js?key=AIzaSyD1LqGvj-IryzE6EqCVWaCYMME6CEzxZuc', function () {
            var maps = window.google.maps;
            var mapNode = _this4.mapRef.current;
            _this4.map = new maps.Map(mapNode, GOOGLE_MAP_CONTROL_CONFIG);
            _this4.maps = maps;

            _this4.setMapBoundsAndMarker(eventsArray);

            _this4.setState({
              googleMapsLoaded: true
            });

            LOAD_GOOGLE_MAPS = false;
          });
        }
      });
    }
  }, {
    key: "route",
    value: function route(path) {
      var _this5 = this;

      window.history.pushState(null, null, path);
      this.setState({
        path: path
      });

      if (path === '/your-events') {
        var _this$props = this.props,
            user = _this$props.user,
            getGuid = _this$props.getGuid;
        var moleid = user.get('moleid');
        var guid = getGuid();
        this.getRegisteredEvents({
          guid: guid,
          moleid: moleid
        }).then(function (registeredEventsJson) {
          var _registeredEventsJson = registeredEventsJson.data,
              data = _registeredEventsJson === void 0 ? [] : _registeredEventsJson;

          _this5.setState({
            displayEvents: data,
            hasNoResults: !data.length,
            paginationData: {
              cur_page: 0,
              pages: []
            }
          });
        });
      } else {
        this.searchEvents({
          queryString: ''
        }).then(function (eventsArray) {
          _this5.setMapBoundsAndMarker(eventsArray);
        });
      }
    }
  }, {
    key: "setMapBoundsAndMarker",
    value: function setMapBoundsAndMarker(eventsArray) {
      var _this6 = this;

      var bounds = new this.maps.LatLngBounds();
      eventsArray = this.state.umbrellaEventMetaData ? this.state.events : eventsArray;

      if (eventsArray.length > 0) {
        // set our bounds to our events group
        eventsArray.forEach(function (event, idx) {
          var lat = event.lat,
              lng = event.lng;
          var loc = new _this6.maps.LatLng(lat, lng);
          bounds.extend(loc);
        }); // Don't zoom in too far if only one location

        if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
          var extendPoint1 = new google.maps.LatLng(bounds.getNorthEast().lat() + 0.01, bounds.getNorthEast().lng() + 0.01);
          var extendPoint2 = new google.maps.LatLng(bounds.getNorthEast().lat() - 0.01, bounds.getNorthEast().lng() - 0.01);
          bounds.extend(extendPoint1);
          bounds.extend(extendPoint2);
        }

        this.setState({
          bounds: bounds
        });
        this.map.fitBounds(bounds);
        this.map.panToBounds(bounds);
      } else if (this.state.bounds) {
        // use previous bounds if we have it
        bounds = this.state.bounds;
        this.map.fitBounds(bounds);
      } else {
        // set our bounds to center of U.S.
        bounds.extend(new this.maps.LatLng(24.891752, -98.4375));
        bounds.extend(new this.maps.LatLng(40.351289, -124.244385));
        bounds.extend(new this.maps.LatLng(44.488196, -70.290656));
        bounds.extend(new this.maps.LatLng(49.000282, -101.37085));
        this.map.setCenter(bounds.getCenter());
        this.map.fitBounds(bounds);
      }
    }
  }, {
    key: "searchRoute",
    value: function searchRoute(_ref7) {
      var _this7 = this;

      var searchValue = _ref7.searchValue,
          searchParam = _ref7.searchParam,
          selectedValueName = _ref7.selectedValueName;
      var isCategorySearch = searchParam === 'category';

      if (searchValue) {
        // change query string in url
        this.searchEvents({
          searchParam: searchParam,
          searchValue: searchValue,
          isCategorySearch: isCategorySearch
        }).then(function (eventsArray) {
          _this7.setMapBoundsAndMarker(eventsArray);
        });
        var searchValues = this.state.searchValues;
        searchValues[searchParam] = selectedValueName;

        if (isCategorySearch) {
          searchValues['type_id'] = 'All';
        }

        this.setState({
          selectMenuVisible: '',
          searchValues: searchValues
        }); // make call to get event types associated with category

        if (isCategorySearch) {
          fetchEventTypes(searchValue).then(function (json) {
            var options = _this7.state.options;
            options.eventTypeOptions = [].concat(eventTypeOptions, _toConsumableArray(json.data));

            _this7.setState({
              options: options
            });
          });
        }
      } else {
        var selectMenuVisible = this.state.selectMenuVisible;
        var showMenu = selectMenuVisible === searchParam ? '' : searchParam;
        this.setState({
          selectMenuVisible: showMenu
        });
      }
    }
  }, {
    key: "onChange",
    value: function onChange(_ref8) {
      var _this8 = this;

      var inputName = _ref8.inputName,
          e = _ref8.e;
      var searchValues = this.state.searchValues;
      var isZip = inputName === 'zip';
      var searchValue = e.target.value;
      searchValues[inputName] = searchValue;

      if (isZip) {
        var isValidZip = zipValidationRegex.test(searchValue);

        if (isValidZip) {
          this.searchEvents({
            searchParam: inputName,
            searchValue: searchValue,
            isZipSearch: true
          }).then(function (eventsArray) {
            _this8.setMapBoundsAndMarker(eventsArray);
          });
        } else if (!searchValue) {
          this.searchEvents({
            searchParam: inputName,
            searchValue: 'none'
          }).then(function (eventsArray) {
            _this8.setMapBoundsAndMarker(eventsArray);
          });
        }
      } else {
        if (!searchValue) {
          this.searchEvents({
            searchParam: inputName,
            searchValue: 'none'
          }).then(function (eventsArray) {
            _this8.setMapBoundsAndMarker(eventsArray);
          });
        }
      }

      this.setState({
        searchValues: searchValues
      });
    }
  }, {
    key: "onDateChange",
    value: function onDateChange(_ref9) {
      var inputName = _ref9.inputName,
          e = _ref9.e;
      var searchValue = e.target.value;
      var searchValues = this.state.searchValues;
      searchValues[inputName] = searchValue;
      this.setState({
        searchValues: searchValues
      });
      this.searchEvents({
        searchParam: inputName,
        searchValue: searchValue
      });
    }
  }, {
    key: "keywordsSearch",
    value: function keywordsSearch() {
      var _this9 = this;

      var searchValues = this.state.searchValues;
      var keywords = searchValues.keywords;
      this.searchEvents({
        searchParam: 'keywords',
        searchValue: keywords
      }).then(function (eventsArray) {
        _this9.setMapBoundsAndMarker(eventsArray);
      });
    }
  }, {
    key: "onKeyPress",
    value: function onKeyPress(e) {
      var isEnter = e.key === 'Enter';

      if (isEnter) {
        this.keywordsSearch();
      }
    }
  }, {
    key: "onSearchButtonClick",
    value: function onSearchButtonClick(e) {
      this.keywordsSearch();
    }
  }, {
    key: "onPageClick",
    value: function onPageClick(page) {
      var events = page.events,
          index = page.index;
      var paginationData = this.state.paginationData;
      paginationData.cur_page = index;
      this.setMapBoundsAndMarker(events);
      this.setState({
        displayEvents: events,
        paginationData: paginationData
      });
    }
  }, {
    key: "onEventMouseover",
    value: function onEventMouseover(eventIdx) {
      this.setState({
        hoveredEvent: eventIdx
      });
    }
  }, {
    key: "onEventMouseLeave",
    value: function onEventMouseLeave() {
      this.setState({
        hoveredEvent: 0
      });
    }
    /**
     * Formats date string in form mm/dd/yyyy to Mon, Day Year
     *
     * @param {String} dateString date in slash separated formate
     */

  }, {
    key: "renderUmbrellaHeader",
    value: function renderUmbrellaHeader(metadata) {
      var eventsHeaderEl = document.getElementById('events-header');

      if (metadata) {
        var name = metadata.name,
            start_date = metadata.start_date,
            end_date = metadata.end_date,
            start_time = metadata.start_time,
            end_time = metadata.end_time,
            umbrella_description = metadata.umbrella_description,
            id = metadata.id; // gotta love inconsistency, let's fix that

        var startDateSplit = start_date ? start_date.split('-') : null;
        var endDateSplit = end_date ? end_date.split('-') : null; // create a display date range:

        var startDate = startDateSplit ? this.formatDate("".concat(startDateSplit[1], "/").concat(startDateSplit[2], "/").concat(startDateSplit[0])) : '';
        var endDate = endDateSplit && end_date !== start_date ? this.formatDate("".concat(endDateSplit[1], "/").concat(endDateSplit[2], "/").concat(endDateSplit[0])) : '';
        var startTime = start_time ? this.formatTime(start_time) : '';
        var endTime = end_time && end_time !== start_time ? this.formatTime(end_time) : '';
        var _displayDate = startDate;
        _displayDate += startTime ? ", ".concat(startTime) : '';
        _displayDate += endDate || endTime ? " - ".concat(endDate) : '';
        _displayDate += endDate && endTime ? ", ".concat(endTime) : "".concat(endTime);
        eventsHeaderEl.innerHTML = "\n      <header class=\"your-campaigns-header umbrella-events-header\">\n        <h3>Coordinated Event</h3>\n        <h1>".concat(name, "</h1>\n        <h3><span class=\"umbrella-events-header__date\">").concat(_displayDate, "</span></h3>\n        <p class=\"umbrella-events-header__description\">").concat(umbrella_description, "</p>\n        <p class=\"umbrella-events-header__makenew\">Find an event near you, or <a href=\"/cms/event/new/edit?umbrella_id=").concat(id, "\" target=\"_blank\">create your own</a></p>\n      </header>");
      } else {
        eventsHeaderEl.innerHTML = "\n      <header class=\"your-campaigns-header\">\n        <h1>Events</h1>\n      </header>";
      }
    }
  }, {
    key: "redoSearchInArea",
    value: function redoSearchInArea() {
      var lat0 = this.map.getBounds().getNorthEast().lat();
      var lng0 = this.map.getBounds().getNorthEast().lng();
      var lat1 = this.map.getBounds().getSouthWest().lat();
      var lng1 = this.map.getBounds().getSouthWest().lng();
      var latSearch = (lat0 + lat1) / 2;
      var lngSearch = (lng0 + lng1) / 2;
      /**
       * this calculates the number of miles in half the height of the map
       * there are approx 69.2 miles per degree latitude
       */

      var rcurrentRadius = (lat0 - lat1) / 2 * 69.2;
      var searchString = location.search.substring(1);
      var newSearchString = '';
      var searchObject = {
        lat: latSearch,
        lng: lngSearch,
        radius: rcurrentRadius
      };

      if (searchString) {
        var urlStringSearchObject = JSON.parse('{"' + decodeURI(searchString).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') + '"}');
        delete urlStringSearchObject['zip'];
        var mergedSearchObject = Object.assign(urlStringSearchObject, searchObject);
        newSearchString = "".concat(Object.keys(mergedSearchObject).map(function (key) {
          return key + '=' + mergedSearchObject[key];
        }).join('&'));
      } else {
        newSearchString = "".concat(Object.keys(searchObject).map(function (key) {
          return key + '=' + searchObject[key];
        }).join('&'));
      }

      this.searchEvents({
        queryString: newSearchString
      });
    }
  }, {
    key: "toggleMapOrListView",
    value: function toggleMapOrListView(mapOrList) {
      var _this10 = this;

      this.setState({
        showMapOrList: mapOrList
      });

      if (mapOrList === 'map') {
        var displayEvents = this.state.displayEvents;
        window.setTimeout(function () {
          _this10.setMapBoundsAndMarker(displayEvents);
        });
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _this11 = this;

      var _this$state = this.state,
          path = _this$state.path,
          options = _this$state.options,
          displayEvents = _this$state.displayEvents,
          selectMenuVisible = _this$state.selectMenuVisible,
          searchValues = _this$state.searchValues,
          paginationData = _this$state.paginationData,
          googleMapsLoaded = _this$state.googleMapsLoaded,
          hoveredEvent = _this$state.hoveredEvent,
          umbrellaEventMetaData = _this$state.umbrellaEventMetaData,
          loading = _this$state.loading,
          hasNoResults = _this$state.hasNoResults,
          showEventDetail = _this$state.showEventDetail,
          eventDetailsData = _this$state.eventDetailsData,
          showMapOrList = _this$state.showMapOrList;

      if (showEventDetail) {
        return /*#__PURE__*/React.createElement(EventDetails, {
          apipath: apipath,
          eventDetailsData: eventDetailsData
        });
      }

      var categoryOptions = options.categoryOptions,
          eventTypeOptions = options.eventTypeOptions,
          umbrellaEventOptions = options.umbrellaEventOptions;
      var zip = searchValues.zip,
          keywords = searchValues.keywords;
      var user = this.props.user; // relying on Backbone model :(

      var loggedin = user.get('loggedin');
      this.renderUmbrellaHeader(umbrellaEventMetaData);
      var SelectComponents = [[/*#__PURE__*/React.createElement(Select, {
        options: categoryOptions,
        onClick: function onClick(selectData) {
          return _this11.searchRoute(selectData);
        },
        searchParam: "category",
        key: "category",
        label: "Category",
        selectMenuVisible: selectMenuVisible,
        selectedValueName: searchValues.category
      }), /*#__PURE__*/React.createElement(ZipInput, {
        label: "Near Zip Code",
        value: zip,
        key: "zip",
        onChange: function onChange(inputData) {
          return _this11.onChange(inputData);
        }
      })], [/*#__PURE__*/React.createElement(Select, {
        options: eventTypeOptions,
        onClick: function onClick(selectData) {
          return _this11.searchRoute(selectData);
        },
        searchParam: "type_id",
        key: "type_id",
        label: "Type",
        selectMenuVisible: selectMenuVisible,
        selectedValueName: searchValues.type_id
      }), /*#__PURE__*/React.createElement(DateInput, {
        key: "date",
        onDateChange: function onDateChange(dateChangeData) {
          return _this11.onDateChange(dateChangeData);
        },
        dateInputValues: searchValues,
        min: createStartDateSearchParam()
      })], [/*#__PURE__*/React.createElement(Select, {
        options: umbrellaEventOptions,
        onClick: function onClick(selectData) {
          return _this11.searchRoute(selectData);
        },
        searchParam: "umbrella_event_id",
        key: "umbrella_event_id",
        label: "Coordinated Event",
        selectMenuVisible: selectMenuVisible,
        selectedValueName: searchValues.umbrella_event_id
      }), /*#__PURE__*/React.createElement(SearchInput, {
        key: "search",
        value: keywords,
        onChange: function onChange(inputData) {
          return _this11.onChange(inputData);
        },
        onKeyPress: function onKeyPress(e) {
          return _this11.onKeyPress(e);
        },
        onSearchButtonClick: function onSearchButtonClick(e) {
          return _this11.onSearchButtonClick(e);
        }
      })]];
      var EVENTS_PER_PAGE = umbrellaEventMetaData ? 100 : 20;
      return /*#__PURE__*/React.createElement("div", {
        className: "grid-row"
      }, /*#__PURE__*/React.createElement(EventsTabs, {
        route: function route(path) {
          return _this11.route(path);
        },
        path: path,
        loggedin: loggedin
      }), loading ? /*#__PURE__*/React.createElement("div", {
        className: "saveicon"
      }) : null, /*#__PURE__*/React.createElement("div", {
        id: "all-events",
        className: "tab row events-search-section"
      }, /*#__PURE__*/React.createElement("div", {
        className: "map-toggle"
      }, /*#__PURE__*/React.createElement("div", {
        className: "button map-toggle__option list ".concat(showMapOrList === 'map' ? 'disabled' : ''),
        onClick: function onClick() {
          return _this11.toggleMapOrListView('list');
        }
      }, "List"), /*#__PURE__*/React.createElement("div", {
        className: "button map-toggle__option map ".concat(showMapOrList === 'list' ? 'disabled' : ''),
        onClick: function onClick() {
          return _this11.toggleMapOrListView('map');
        }
      }, "Map")), /*#__PURE__*/React.createElement(EventsSearch, {
        SelectComponents: SelectComponents
      }), /*#__PURE__*/React.createElement("div", {
        className: "events-grid-and-map"
      }, /*#__PURE__*/React.createElement("div", {
        className: "events-grid ".concat(showMapOrList === 'list' ? 'active' : ''),
        ref: this.eventsGridRef
      }, hasNoResults ? /*#__PURE__*/React.createElement("div", {
        className: "no-results-section"
      }, /*#__PURE__*/React.createElement("h4", {
        className: "h4 center no-results"
      }, "Darn, there don't appear to be any nearby events that match your search.", /*#__PURE__*/React.createElement("br", null), "Try changing your search filters or searching in a different area.")) : null, displayEvents.map(function (event, idx, eventsArray) {
        var id = event.id,
            umbrella = event.umbrella,
            slug = event.slug,
            reservations_accepted = event.reservations_accepted,
            title = event.title,
            start_date = event.start_date,
            start_time = event.start_time,
            location = event.location,
            lat = event.lat,
            lng = event.lng;
        var _event$timezone = event.timezone,
            timezone = _event$timezone === void 0 ? '' : _event$timezone;

        switch (timezone) {
          case 'eastern':
            timezone = 'ET';
            break;

          case 'central':
            timezone = 'CT';
            break;

          case 'mountain':
            timezone = 'MT';
            break;

          case 'pacific':
            timezone = 'PT';
            break;

          case 'alaska':
            timezone = 'AKT';
            break;

          case 'hawaii':
            timezone = 'HT';
            break;

          default:
            timezone = '';
        }

        var eventsArrayLength = eventsArray.length;
        var umbrellaEvent = umbrella[0];
        var umbrellaName = umbrellaEvent && umbrellaEvent.name;
        var umbrellaId = umbrellaEvent && umbrellaEvent.id;
        var dateDisplayObject = displayDate(start_date);
        var month = dateDisplayObject.month,
            eventDay = dateDisplayObject.eventDay;
        var displayNumber = paginationData.cur_page > 0 ? idx + 1 + paginationData.cur_page * EVENTS_PER_PAGE : idx + 1;
        var isHoveredEvent = hoveredEvent === displayNumber;
        return /*#__PURE__*/React.createElement("div", {
          key: id,
          className: "tile-item action-section ".concat(umbrellaName ? 'tile-umbrella' : ''),
          onMouseEnter: function onMouseEnter() {
            return _this11.onEventMouseover(displayNumber);
          },
          onMouseLeave: function onMouseLeave() {
            return _this11.onEventMouseLeave();
          },
          ref: function ref(node) {
            return _this11.eventsTileRefs[id] = node;
          }
        }, /*#__PURE__*/React.createElement("div", {
          className: "event-tile row ".concat(isHoveredEvent ? 'hover' : '')
        }, /*#__PURE__*/React.createElement("a", {
          className: "event-tile-container",
          href: "/events/".concat(slug),
          target: "_blank"
        }), umbrellaName && /*#__PURE__*/React.createElement("a", {
          href: "/events?umbrella_event_id=".concat(umbrellaId),
          className: "umbrella-event-listing__partof",
          target: "_blank"
        }, "Part of ", umbrellaName), /*#__PURE__*/React.createElement("div", {
          className: "event-tile-detail"
        }, /*#__PURE__*/React.createElement("div", {
          className: "col-2 tile-number"
        }, displayNumber), /*#__PURE__*/React.createElement("div", {
          className: "rsvpinline-container"
        }, parseInt(reservations_accepted) ? /*#__PURE__*/React.createElement("div", {
          className: "rsvpinline"
        }, /*#__PURE__*/React.createElement("div", {
          className: "inline-rsvp-placeholder-button"
        }, /*#__PURE__*/React.createElement("div", {
          className: "inline-rsvp-show-form button"
        }, "RSVP"))) : null), /*#__PURE__*/React.createElement("div", {
          className: "event-tile-details col-8"
        }, /*#__PURE__*/React.createElement("div", {
          className: "event-title"
        }, title), /*#__PURE__*/React.createElement("div", {
          className: "umbrella-event-listing__date event-date-section"
        }, month, " ", eventDay, " ", start_time && ", ".concat(start_time), " ", timezone), location && /*#__PURE__*/React.createElement("p", {
          className: "info-location clickable"
        }, location), /*#__PURE__*/React.createElement("div", {
          className: "inline-event-rsvp-placement"
        })))), googleMapsLoaded && /*#__PURE__*/React.createElement(MapMarker, {
          lat: lat,
          lng: lng,
          maps: _this11.maps,
          map: _this11.map,
          index: displayNumber,
          zIndex: eventsArrayLength - idx,
          isHoveredEvent: isHoveredEvent,
          event: event,
          id: id,
          eventRefs: _this11.eventsTileRefs,
          eventsGridRef: _this11.eventsGridRef,
          onEventMouseover: function onEventMouseover(eventIdx) {
            return _this11.onEventMouseover(eventIdx);
          }
        }));
      })), /*#__PURE__*/React.createElement("div", {
        className: "events-map ".concat(showMapOrList === 'map' ? 'active' : '')
      }, /*#__PURE__*/React.createElement("div", {
        id: "map",
        ref: this.mapRef
      }), /*#__PURE__*/React.createElement("button", {
        className: "button blue-button reload-map",
        onClick: function onClick() {
          return _this11.redoSearchInArea();
        }
      }, "Redo search in this area"))), /*#__PURE__*/React.createElement(Pagination, {
        paginationData: paginationData,
        onPageClick: function onPageClick(page) {
          return _this11.onPageClick(page);
        }
      })));
    }
  }]);

  return Events;
}(React.Component);

var uri = '/api/v1/lte';
var Lte = /*#__PURE__*/function (_React$Component) {
  _inherits(Lte, _React$Component);

  var _super = _createSuper(Lte);

  function Lte(props) {
    var _this;

    _classCallCheck(this, Lte);

    _this = _super.call(this, props);
    _this.state = {
      template: {},
      publications: [],
      filteredPublications: [],
      publication: {},
      submitted: false
    };
    return _this;
  }

  _createClass(Lte, [{
    key: "fetchData",
    value: function fetchData(url, method, body) {
      if (url) {
        var headers = {
          'content-type': 'application/json'
        };
        return fetch(url, {
          method: method || 'GET',
          headers: headers,
          body: body
        }).then(function (response) {
          if (response.ok) {
            return response.json();
          }

          throw new Error('Request failed!');
        }, function (networkError) {
          return console.log(networkError.message);
        });
      }
    }
  }, {
    key: "filterPublications",
    value: function filterPublications(e) {
      var publications = this.state.publications;
      var filteredPublications = publications.filter(function (publication) {
        return publication.state === e.target.value;
      });
      this.setState({
        filteredPublications: filteredPublications
      });
    }
  }, {
    key: "filterTalkingPoints",
    value: function filterTalkingPoints() {
      var talkingPoints = this.state.talkingPoints;
      var cleanedTalkingPoints = talkingPoints.map(function (talkingPoint) {
        return talkingPoint.template;
      });
      this.setState({
        talkingPoints: cleanedTalkingPoints
      });
    }
  }, {
    key: "setPublication",
    value: function setPublication(e) {
      var filteredPublications = this.state.filteredPublications;
      var publication = filteredPublications.filter(function (publication) {
        return publication.id === Number(e.target.value);
      });
      this.setState({
        publication: publication[0],
        submitted: false
      });
    }
  }, {
    key: "getUrlParam",
    value: function getUrlParam(parameter_name) {
      var query = window.location.search.substring(1);
      var params = query.split('&');

      for (var i = 0; i < params.length; i++) {
        var pair = params[i].split('=');

        if (pair[0] === parameter_name) {
          return pair[1];
        }
      }

      return false;
    }
  }, {
    key: "wordCount",
    value: function wordCount() {
      var e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
      var count = e.target ? e.target.value.match(/\w+/g) : e.match(/\w+/g);
      count = count ? count.length : 0;
      this.setState({
        wordCount: count
      });
    }
  }, {
    key: "recordFieldChange",
    value: function recordFieldChange(e) {
      this.setState(_defineProperty({}, e.target.name, e.target.value));
    }
  }, {
    key: "showNext",
    value: function showNext(section) {
      this.setState(_defineProperty({}, 'show' + section, true));
    }
  }, {
    key: "copyToClipboard",
    value: function copyToClipboard(id) {
      if (id) {
        var copyText = document.getElementById(id);
        copyText.focus();
        copyText.select();
        copyText.setSelectionRange(0, 99999);
        document.execCommand('copy');
      }
    }
  }, {
    key: "compileObject",
    value: function compileObject() {
      var _this$state = this.state,
          firstName = _this$state.firstName,
          lastName = _this$state.lastName,
          email = _this$state.email,
          phone = _this$state.phone,
          streetAddress = _this$state.streetAddress,
          city = _this$state.city,
          state = _this$state.state,
          postalCode = _this$state.postalCode,
          template = _this$state.template,
          publication = _this$state.publication,
          letterSubject = _this$state.letterSubject,
          letterBody = _this$state.letterBody;
      return {
        first_name: firstName,
        last_name: lastName,
        email: email,
        street: streetAddress,
        city: city,
        state: state,
        zipcode: postalCode,
        phone_number: phone,
        subject_line: letterSubject,
        body: letterBody,
        template_id: template.id,
        newspaper_id: publication.id
      };
    }
  }, {
    key: "recordSubmission",
    value: function recordSubmission() {
      var _this2 = this;

      var submitted = this.state.submitted;

      if (!submitted) {
        this.fetchData("".concat(uri, "/submissions"), 'POST', JSON.stringify(this.compileObject())).then(function (response) {
          _this2.setState({
            submitted: true
          });
        });
      }
    }
  }, {
    key: "componentDidMount",
    value: function componentDidMount() {
      var _this3 = this;

      var templateId = this.getUrlParam('id') || 1;
      this.fetchData("".concat(uri, "/newspapers")).then(function (response) {
        _this3.setState({
          'publications': response.data
        });

        _this3.fetchData("".concat(uri, "/templates/").concat(templateId, "?with_talking_points=true")).then(function (response) {
          var template = response.data;

          _this3.setState({
            templateId: templateId,
            template: template
          }); // this.wordCount(template.body);

        });
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _this4 = this;

      var _this$state2 = this.state,
          template = _this$state2.template,
          filteredPublications = _this$state2.filteredPublications,
          publication = _this$state2.publication,
          _this$state2$wordCoun = _this$state2.wordCount,
          wordCount = _this$state2$wordCoun === void 0 ? 0 : _this$state2$wordCoun;
      template.talking_points = template.talking_points || [];
      var wordLimit = publication.word_limit || 500;
      var countColor = wordCount > wordLimit ? 'red-ish' : wordCount > parseInt(wordLimit * .8) ? 'orange-ish' : 'green-ish';
      var _this$state3 = this.state,
          firstName = _this$state3.firstName,
          lastName = _this$state3.lastName,
          email = _this$state3.email,
          phone = _this$state3.phone,
          streetAddress = _this$state3.streetAddress,
          city = _this$state3.city,
          state = _this$state3.state,
          postalCode = _this$state3.postalCode,
          letterSubject = _this$state3.letterSubject,
          letterBody = _this$state3.letterBody;
      var contactInfo = "\n\n".concat(firstName, " ").concat(lastName, "\n").concat(streetAddress, "\n").concat(city, ", ").concat(state, " ").concat(postalCode, "\n\n").concat(phone, "\n").concat(email, "\n");
      var stepOneComplete = firstName && lastName && email && phone && streetAddress && city && state && postalCode ? 'display' : null;
      var stepTwoComplete = stepOneComplete && publication.name ? 'display' : null;
      var stepThreeComplete = stepTwoComplete && letterSubject && letterBody ? 'display' : null;
      return /*#__PURE__*/React.createElement("main", {
        id: "lte"
      }, /*#__PURE__*/React.createElement("h1", null, template.name), /*#__PURE__*/React.createElement("p", null, template.description), /*#__PURE__*/React.createElement("form", null, /*#__PURE__*/React.createElement("fieldset", {
        className: "display"
      }, /*#__PURE__*/React.createElement("legend", null, "STEP ONE: Tell us who you are (most newspapers require this info)."), /*#__PURE__*/React.createElement("section", {
        className: "form-group col-sm-6"
      }, /*#__PURE__*/React.createElement("label", {
        htmlFor: "firstName"
      }, "First Name"), /*#__PURE__*/React.createElement("input", {
        type: "text",
        name: "firstName",
        id: "firstName",
        className: "form-control",
        placeholder: "first name",
        required: "required",
        onChange: function onChange(e) {
          return _this4.recordFieldChange(e);
        }
      })), /*#__PURE__*/React.createElement("section", {
        className: "form-group col-sm-6"
      }, /*#__PURE__*/React.createElement("label", {
        htmlFor: "lastName"
      }, "Last Name"), /*#__PURE__*/React.createElement("input", {
        type: "text",
        name: "lastName",
        id: "lastName",
        className: "form-control",
        placeholder: "last name",
        required: "required",
        onChange: function onChange(e) {
          return _this4.recordFieldChange(e);
        }
      })), /*#__PURE__*/React.createElement("section", {
        className: "form-group col-sm-6"
      }, /*#__PURE__*/React.createElement("label", {
        htmlFor: "email"
      }, "Email"), /*#__PURE__*/React.createElement("input", {
        type: "email",
        name: "email",
        id: "email",
        className: "form-control",
        placeholder: "email",
        required: "required",
        onChange: function onChange(e) {
          return _this4.recordFieldChange(e);
        }
      })), /*#__PURE__*/React.createElement("section", {
        className: "form-group col-sm-6"
      }, /*#__PURE__*/React.createElement("label", {
        htmlFor: "phone"
      }, "Phone"), /*#__PURE__*/React.createElement("input", {
        type: "tel",
        name: "phone",
        id: "phone",
        className: "form-control",
        placeholder: "phone",
        required: "required",
        onChange: function onChange(e) {
          return _this4.recordFieldChange(e);
        }
      })), /*#__PURE__*/React.createElement("section", {
        className: "form-group col-sm-12"
      }, /*#__PURE__*/React.createElement("label", {
        htmlFor: "streetAddress"
      }, "Street Address"), /*#__PURE__*/React.createElement("input", {
        type: "text",
        name: "streetAddress",
        id: "streetAddress",
        className: "form-control",
        placeholder: "street address",
        required: "required",
        onChange: function onChange(e) {
          return _this4.recordFieldChange(e);
        }
      })), /*#__PURE__*/React.createElement("section", {
        className: "form-group col-sm-6"
      }, /*#__PURE__*/React.createElement("label", {
        htmlFor: "city"
      }, "City"), /*#__PURE__*/React.createElement("input", {
        type: "text",
        name: "city",
        id: "city",
        className: "form-control",
        placeholder: "city",
        required: "required",
        onChange: function onChange(e) {
          return _this4.recordFieldChange(e);
        }
      })), /*#__PURE__*/React.createElement("section", {
        className: "form-group col-sm-3"
      }, /*#__PURE__*/React.createElement("label", {
        htmlFor: "state"
      }, "State"), /*#__PURE__*/React.createElement("select", {
        name: "state",
        id: "state",
        className: "form-control",
        required: "required",
        onChange: function onChange(e) {
          _this4.recordFieldChange(e);

          _this4.filterPublications(e);
        }
      }, /*#__PURE__*/React.createElement("option", null, "state"), /*#__PURE__*/React.createElement("option", {
        value: "AK"
      }, "AK"), /*#__PURE__*/React.createElement("option", {
        value: "AL"
      }, "AL"), /*#__PURE__*/React.createElement("option", {
        value: "AR"
      }, "AR"), /*#__PURE__*/React.createElement("option", {
        value: "AZ"
      }, "AZ"), /*#__PURE__*/React.createElement("option", {
        value: "CA"
      }, "CA"), /*#__PURE__*/React.createElement("option", {
        value: "CO"
      }, "CO"), /*#__PURE__*/React.createElement("option", {
        value: "CT"
      }, "CT"), /*#__PURE__*/React.createElement("option", {
        value: "DC"
      }, "DC"), /*#__PURE__*/React.createElement("option", {
        value: "DE"
      }, "DE"), /*#__PURE__*/React.createElement("option", {
        value: "FL"
      }, "FL"), /*#__PURE__*/React.createElement("option", {
        value: "GA"
      }, "GA"), /*#__PURE__*/React.createElement("option", {
        value: "HI"
      }, "HI"), /*#__PURE__*/React.createElement("option", {
        value: "IA"
      }, "IA"), /*#__PURE__*/React.createElement("option", {
        value: "ID"
      }, "ID"), /*#__PURE__*/React.createElement("option", {
        value: "IL"
      }, "IL"), /*#__PURE__*/React.createElement("option", {
        value: "IN"
      }, "IN"), /*#__PURE__*/React.createElement("option", {
        value: "KS"
      }, "KS"), /*#__PURE__*/React.createElement("option", {
        value: "KY"
      }, "KY"), /*#__PURE__*/React.createElement("option", {
        value: "LA"
      }, "LA"), /*#__PURE__*/React.createElement("option", {
        value: "MA"
      }, "MA"), /*#__PURE__*/React.createElement("option", {
        value: "MD"
      }, "MD"), /*#__PURE__*/React.createElement("option", {
        value: "ME"
      }, "ME"), /*#__PURE__*/React.createElement("option", {
        value: "MI"
      }, "MI"), /*#__PURE__*/React.createElement("option", {
        value: "MN"
      }, "MN"), /*#__PURE__*/React.createElement("option", {
        value: "MO"
      }, "MO"), /*#__PURE__*/React.createElement("option", {
        value: "MS"
      }, "MS"), /*#__PURE__*/React.createElement("option", {
        value: "MT"
      }, "MT"), /*#__PURE__*/React.createElement("option", {
        value: "NC"
      }, "NC"), /*#__PURE__*/React.createElement("option", {
        value: "ND"
      }, "ND"), /*#__PURE__*/React.createElement("option", {
        value: "NE"
      }, "NE"), /*#__PURE__*/React.createElement("option", {
        value: "NH"
      }, "NH"), /*#__PURE__*/React.createElement("option", {
        value: "NJ"
      }, "NJ"), /*#__PURE__*/React.createElement("option", {
        value: "NM"
      }, "NM"), /*#__PURE__*/React.createElement("option", {
        value: "NV"
      }, "NV"), /*#__PURE__*/React.createElement("option", {
        value: "NY"
      }, "NY"), /*#__PURE__*/React.createElement("option", {
        value: "OH"
      }, "OH"), /*#__PURE__*/React.createElement("option", {
        value: "OK"
      }, "OK"), /*#__PURE__*/React.createElement("option", {
        value: "OR"
      }, "OR"), /*#__PURE__*/React.createElement("option", {
        value: "PA"
      }, "PA"), /*#__PURE__*/React.createElement("option", {
        value: "RI"
      }, "RI"), /*#__PURE__*/React.createElement("option", {
        value: "SC"
      }, "SC"), /*#__PURE__*/React.createElement("option", {
        value: "SD"
      }, "SD"), /*#__PURE__*/React.createElement("option", {
        value: "TN"
      }, "TN"), /*#__PURE__*/React.createElement("option", {
        value: "TX"
      }, "TX"), /*#__PURE__*/React.createElement("option", {
        value: "UT"
      }, "UT"), /*#__PURE__*/React.createElement("option", {
        value: "VA"
      }, "VA"), /*#__PURE__*/React.createElement("option", {
        value: "VT"
      }, "VT"), /*#__PURE__*/React.createElement("option", {
        value: "WA"
      }, "WA"), /*#__PURE__*/React.createElement("option", {
        value: "WI"
      }, "WI"), /*#__PURE__*/React.createElement("option", {
        value: "WV"
      }, "WV"), /*#__PURE__*/React.createElement("option", {
        value: "WY"
      }, "WY"))), /*#__PURE__*/React.createElement("section", {
        className: "form-group col-sm-3"
      }, /*#__PURE__*/React.createElement("label", {
        htmlFor: "zip"
      }, "Zip Code"), /*#__PURE__*/React.createElement("input", {
        type: "tel",
        name: "postalCode",
        id: "zip",
        className: "form-control",
        placeholder: "zip code",
        required: "required",
        onChange: function onChange(e) {
          return _this4.recordFieldChange(e);
        }
      })), /*#__PURE__*/React.createElement("div", {
        className: "form-group col-sm-12"
      }, !stepOneComplete && /*#__PURE__*/React.createElement("button", {
        disabled: "disabled"
      }, "Next"))), /*#__PURE__*/React.createElement("fieldset", {
        className: stepOneComplete
      }, /*#__PURE__*/React.createElement("legend", null, "STEP TWO: Pick a newspaper you'd like to write to."), /*#__PURE__*/React.createElement("section", {
        className: "form-group col-sm-6"
      }, /*#__PURE__*/React.createElement("label", {
        htmlFor: "publication"
      }, "Newspaper"), /*#__PURE__*/React.createElement("select", {
        name: "publication",
        id: "publication",
        className: "form-control",
        required: "required",
        onChange: function onChange(e) {
          return _this4.setPublication(e);
        }
      }, filteredPublications.length > 1 && /*#__PURE__*/React.createElement("option", null, "Choose a publication"), filteredPublications.map(function (publication) {
        return /*#__PURE__*/React.createElement("option", {
          value: publication.id,
          key: publication.id
        }, publication.name);
      }))), /*#__PURE__*/React.createElement("div", {
        className: "form-group col-sm-12"
      }, !stepTwoComplete && /*#__PURE__*/React.createElement("button", {
        disabled: "disabled"
      }, "Next"))), /*#__PURE__*/React.createElement("fieldset", {
        className: stepTwoComplete
      }, /*#__PURE__*/React.createElement("legend", null, "STEP THREE: Start writing your letter-to-the-editor -- we'll help you get going!"), /*#__PURE__*/React.createElement("section", {
        className: "form-group col-sm-6"
      }, template.talking_points.length > 0 && /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("b", null, "Here are some talking points to help you write your letter:"), /*#__PURE__*/React.createElement("ul", null, template.talking_points.map(function (point, index) {
        return /*#__PURE__*/React.createElement("li", {
          key: index
        }, point.body);
      }))), /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("b", null, "Here are some general tips for writing a letter-to-the-editor:"), /*#__PURE__*/React.createElement("ul", null, /*#__PURE__*/React.createElement("li", null, "Assume Nothing \u2014 Do not assume that your readers are informed on your topic. Give a concise but informative background before plunging into the main issue. Refer to any newspaper article or editorial by date and title."), /*#__PURE__*/React.createElement("li", null, "Find a Local Angle \u2014 Readers are more interested in an issue when they see how it affects their lives and communities. Find a way to show how budget cuts or environmental policies will affect this particular readership."), /*#__PURE__*/React.createElement("li", null, "Make it Personal \u2014 Personal stories and anecdotes are particularly compelling to editors and readers than facts and figures alone. The less impersonal a letter is the better."), /*#__PURE__*/React.createElement("li", null, "Still not sure what to write? Find more tips and guidance by ", /*#__PURE__*/React.createElement("a", {
        href: "https://www.sierraclub.org/sierrarise/writing-letter-editor",
        target: "_blank"
      }, "clicking here!"))))), /*#__PURE__*/React.createElement("section", {
        className: "form-group col-sm-6"
      }, /*#__PURE__*/React.createElement("section", {
        className: "form-group"
      }, /*#__PURE__*/React.createElement("label", {
        htmlFor: "letterSubject"
      }, "Letter Title", /*#__PURE__*/React.createElement("span", {
        className: "clipboard",
        title: "copy to clipboard",
        onMouseUp: function onMouseUp(e) {
          return _this4.copyToClipboard('letterSubject');
        }
      }, /*#__PURE__*/React.createElement("svg", {
        xmlns: "http://www.w3.org/2000/svg",
        width: "16",
        height: "16",
        fill: "currentColor",
        viewBox: "0 0 16 16"
      }, /*#__PURE__*/React.createElement("path", {
        d: "M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"
      })))), /*#__PURE__*/React.createElement("input", {
        type: "text",
        name: "letterSubject",
        id: "letterSubject",
        className: "form-control",
        placeholder: "Letter Subject",
        required: "required",
        onChange: function onChange(e) {
          return _this4.recordFieldChange(e);
        }
      })), /*#__PURE__*/React.createElement("section", {
        className: "form-group"
      }, /*#__PURE__*/React.createElement("label", {
        htmlFor: "letterBody"
      }, "Letter Body", /*#__PURE__*/React.createElement("span", {
        className: "clipboard",
        title: "copy to clipboard",
        onMouseUp: function onMouseUp(e) {
          return _this4.copyToClipboard('letterBody');
        }
      }, /*#__PURE__*/React.createElement("svg", {
        xmlns: "http://www.w3.org/2000/svg",
        width: "16",
        height: "16",
        fill: "currentColor",
        viewBox: "0 0 16 16"
      }, /*#__PURE__*/React.createElement("path", {
        d: "M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"
      }), /*#__PURE__*/React.createElement("path", {
        d: "M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"
      })))), /*#__PURE__*/React.createElement("textarea", {
        name: "letterBody",
        id: "letterBody",
        className: "form-control",
        placeholder: "Letter Body",
        rows: "10",
        cols: "80",
        required: "required",
        onChange: function onChange(e) {
          _this4.wordCount(e);

          _this4.recordFieldChange(e);
        }
      }), /*#__PURE__*/React.createElement("p", null, wordLimit, " word limit ", /*#__PURE__*/React.createElement("mark", {
        className: countColor
      }, wordCount)))), !stepThreeComplete && /*#__PURE__*/React.createElement("button", {
        disabled: "disabled"
      }, "Next")), /*#__PURE__*/React.createElement("fieldset", {
        className: stepThreeComplete
      }, /*#__PURE__*/React.createElement("legend", null, "STEP FOUR: Submit your LTE to your newspaper."), /*#__PURE__*/React.createElement("section", {
        className: "form-group col-sm-12"
      }, publication.submission_instructions && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("b", null, "Additional submission instructions:"), /*#__PURE__*/React.createElement("blockquote", null, publication.submission_instructions))), publication.submission_email && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
        className: "left"
      }, "For ", publication.name, ", letters-to-the-editor should be submitted via email.\xA0", /*#__PURE__*/React.createElement("a", {
        href: 'mailto:' + publication.submission_email + '?subject=' + encodeURI(letterSubject) + '&body=' + encodeURI(letterBody + contactInfo),
        onMouseUp: function onMouseUp(e) {
          return _this4.recordSubmission(e);
        }
      }, "Click here"), "\xA0 to open in your default email client, or if that doesn't work, ", /*#__PURE__*/React.createElement("a", {
        title: "copy to clipboard",
        onMouseUp: function onMouseUp(e) {
          return _this4.copyToClipboard('letterBody');
        }
      }, "click here"), " to copy your letter so you can paste it into an email yourself."), /*#__PURE__*/React.createElement("a", {
        href: 'mailto:' + publication.submission_email + '?subject=' + encodeURI(letterSubject) + '&body=' + encodeURI(letterBody + contactInfo),
        className: "btn",
        onMouseUp: function onMouseUp(e) {
          return _this4.recordSubmission(e);
        }
      }, /*#__PURE__*/React.createElement("b", null, /*#__PURE__*/React.createElement("u", null, "Click here")))), publication.submission_url && !publication.submission_email && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
        className: "left"
      }, "For ", publication.name, ", letters-to-the-editor must be submitted via an online form. Please copy your completed letter text above and paste it into the appropriate field in the form that will open in a new browser window when you click the \"Continue\" button on this page."), /*#__PURE__*/React.createElement("a", {
        href: publication.submission_url,
        className: "btn",
        target: "_blank",
        rel: "noreferrer",
        onMouseUp: function onMouseUp(e) {
          return _this4.recordSubmission(e);
        }
      }, "Continue"))))));
    }
  }]);

  return Lte;
}(React.Component);

function getWhichPages(page, perpage) {
  var offset = page * perpage - (page === 0 ? 0 : perpage); //if not 0, get the previous page too

  return {
    offset: offset
  };
}

function getPage(page) {
  return this.pages[page] ? resolve(this.pages[page]) : this.downloadPage(page);
}
function downloadPage(page) {
  var searchObj = JSON.parse(this.searchhash) || {};

  var data = _.extend(searchObj, getWhichPages(page, this.perpage)),
      search = this.searchapi(data);

  this.spinner(search);
  return search.then(_.mergeTo(this, ['offset', 'total', 'total_pages', 'cur_page'])).then(function (response) {
    return response.data;
  }).then(_.bind(this.setPages, this));
}
/* Groups events into sets based on the perpage param */

function setPages(events) {
  var self = this,
      pages = _.chain(events).groupBy(function (element, index) {
    return Math.floor(index / self.perpage);
  }).toArray().value(),
      boundedOffset = this.offset > 0 ? self.offset / self.perpage : 0;

  _.each(pages, function (page, i) {
    return self.pages[boundedOffset + i] = page;
  });

  return self.pages[self.extradata.currentpage] || [];
}
/* Calculates page count of event listings for pagination based on items/perpage */

function recalc(d) {
  this.extradata.items = this.total || d.data.length;
  this.extradata.pagenum = Math.floor(this.extradata.items / (this.extradata.perpage || 3)) || 0; // this.extradata.pages = new Array(this.extradata.pagenum);

  var pages = [];

  for (var i = 0; i < this.extradata.pagenum; i++) {
    pages.push(null);
  }

  this.extradata.pages = pages;
  var place = this.extradata.currentpage * this.extradata.perpage;

  if (this.total) {
    //total is a crude proxy for whether it's api based or Backbone model based
    d.data = this.pages[this.extradata.currentpage];
  } else {
    d.data = d.data.slice(place, place + this.extradata.perpage);
  }

  return d;
}
function spinner(dfd) {
  var $grid = this.$el.find('.events-grid').empty();
      this.$el.find('.pagination').empty();
  saveicon(dfd, this.$el, $grid);
} //these 3 are the major ones that trigger actual page show events

function page(e) {
  stopevent(e);
  var newpage = parseFloat($(e.currentTarget).text()) - 1;
  return this.byPage(newpage) && this.reroute();
}
function next(e) {
  stopevent(e);

  if (this.extradata.currentpage < this.extradata.pagenum - 1) {
    this.byPage(this.extradata.currentpage + 1);
  }
}
function prev(e) {
  stopevent(e);

  if (this.extradata.currentpage !== 0) {
    this.byPage(this.extradata.currentpage - 1);
  }
}
function byPage(newpage, hash) {
  var self = this;

  if (newpage !== this.extradata.currentpage) {
    if (this.yours || this.campaigns) {
      self.extradata.currentpage = newpage;
      self.rerender();
      self.reroute();
    } else {
      this.getPage(newpage).then(function (page) {
        self.sorteddata = page;
        self.extradata.currentpage = newpage;
        self.rerender();
        self.reroute();
      });
    }

    return true;
  } else {
    return false;
  }
}
var rerouter = function rerouter(router) {
  return function () {
    var searchObj = JSON.parse(this.searchhash) || null;
    var currentpage = this.extradata.currentpage + 1;
    var newroute = router.lastroute + (currentpage > 1 || this.searchhash ? '/' + currentpage + '/' + (this.searchhash ? $.param(searchObj) : '') : ''); //console.log('%creroute','color:red;font-size:20px;',this.searchhash, router.lastroute, newroute, window.location.pathname);

    router.navigate(newroute, {
      trigger: false
    });
    router.prevpaged = window.location.pathname; //have to do this after the navigation as we're saving it for the NEXT step
  };
};
function eventFilter(keeppage) {
  var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var startValue = this.$el.find('#search-form input[name="start_date"]').val().replace(/-/g, "");
  var endValue = this.$el.find('#search-form input[name="end_date"]').val().replace(/-/g, "");
  var umbrellaValue = this.hash.indexOf('umbrella_event_id') > -1;

  if (this.$el.find('#search-form input[name="end_date"]').val() !== '' && startValue > endValue) {
    document.getElementById('end-date-field').value = $('input[name="start_date"]').val();
  } // if we have not chosen an umbrella event via filters, let's also confirm that there isn't one in the query string


  if (!this.umbrellaChosen) {
    this.umbrellaChosen = umbrellaValue;
  }

  var $form = this.$el.find('#search-form'),
      data,
      thishash;
  data = _.cleanObj(_.pick($form.serializeObject(), function (d) {
    d = $.trim(d);
    return d && d !== 'none';
  }));

  if (!data.zip && !$('#lat').val() && typeof user !== 'undefined') {
    data.zip = user.get('zipcode');
  }

  if (data.radius) {
    if (data.radius > 500) {
      this.$el.addClass('wide-search');
    } else {
      this.$el.removeClass('wide-search');
    }
  }

  this.lastsearch = _.pairs(data); //update to the latest search

  this.previoussearch = data; // an object of same
  // here the hash is being stringified and then later it`s being parsed. Why? ¯\_(ツ)_/¯
  // but leaving for now so as not to create a bunch of regressions

  if (!_.keys(data).length) {
    this.searchhash = thishash = null;
  } else {
    var params = '?';
    var dataKeys = Object.keys(data);
    var queryString = dataKeys.reduce(function (acc, key, idx) {
      var value = data[key];
      return "".concat(acc).concat(idx ? '&' : '').concat(key, "=").concat(value);
    }, params);
    /** the existing query data won't work in a new tab, so we have to look manually if this is an umbrella event page: */

    if (!window.location.href.indexOf("umbrella_event_id")) {
      history.replaceState({}, '', "/events".concat(queryString));
    } else {
      /** on a new page, the umbrella event isn't passed via a search object, so we have to get it old school style: */
      var urlvars = {};
      var urlcount = 0;
      window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, function (m, key, value) {
        urlvars[key] = value;
        urlcount++;
      });

      if (urlcount === 1 && !!urlvars['umbrella_event_id']) {
        data.umbrella_event_id = urlvars['umbrella_event_id'];
      } else if (!urlvars['umbrella_event_id']) {
        this.umbrellaChosen = false;
        data.umbrella_event_id = 0;
      }
      /** otherwise radius on new page defaults to 10: */


      if (!!$('[name="lat"]').val() || !!$('#zipF').val()) {
        data.radius = 500;
      } else {
        data.radius = 3000;
      }

      history.replaceState({}, '', "/events".concat(queryString));
    }

    this.searchhash = thishash = JSON.stringify(data);
  }

  if (!this.yours) {
    // /** so if this is a new page, and you clear filters, it will do the reradiusing thing, so hide the repeating searches: */
    // this.reradiusing = true;
    $('.events-cover').addClass('spinnerfade');
    this.$el.find('.events-map').addClass('spinnermap'); // $('.events-grid-and-map').addClass('saveicon');

    this.searchevents(_.extend(data, {
      offset: offset
    }), thishash, keeppage);
  } else {
    this.filterYours(data, keeppage);
  }
}

function mmddyyToYYMMDD(str) {
  if (typeof str === "string" && str.indexOf('/') > -1) {
    var dateArray = str.split('/');
    return "".concat(dateArray[2], "-").concat(dateArray[0], "-").concat(dateArray[1]);
  } else {
    return null;
  }
}

function filterYours(filters, keeppage) {
  new RegExp(filters.keyword, "gi");
  var type = filters.type,
      end_date = filters.end_date,
      start_date = filters.start_date,
      keyword = filters.keyword;
  var avg_score = 0;
  this.myevents = this.myevents || (this.myevents = this.sorteddata);

  if (keyword) {
    avg_score = _.reduce(this.myevents, function (acc, ev) {
      return acc + _.scoreByTerms(keyword, ev.title);
    }, 0) / this.myevents.length * 0.9;
  }

  var data = _.reject(this.myevents, function (event) {
    if (type && event.type !== type) {
      return true;
    }

    if (end_date || start_date) {
      var cutoffendStr = mmddyyToYYMMDD(event.end_date || event.start_date); //when the event ends, which could be on the same day it starts

      var cutoffstartStr = mmddyyToYYMMDD(event.start_date); //when the event starts

      if (end_date && cutoffstartStr) {
        if (end_date < cutoffstartStr) {
          return true;
        }
      }

      if (start_date && cutoffendStr) {
        if (start_date > cutoffendStr) {
          return true;
        }
      }
    }

    if (keyword) {
      event.score = _.scoreByTerms(keyword, event.title);

      if (event.score < avg_score || event.score < 0.1) {
        return true;
      }
    }
  });

  if (keyword) {
    data.sort(function (a, b) {
      return a.score - b.score;
    }).reverse();
  }

  this.resort(data, keeppage);
}

function selectli(e, ovfbid) {
  stopevent(e);

  var $el = $(e.currentTarget),
      selectize = this.$el.find('select')[0].selectize,
      fbid = $el.data('fbid'),
      friends = this.tsmodel.get('friends'),
      who = _.findWhere(friends, {
    fbid: fbid
  });

  if (!who || !who.fbid) {
    who = {
      fbid: fbid,
      name: $el.data('fbname')
    };
  }

  if (!$el.hasClass('selected')) {
    selectize.addOption(who);
    selectize.addItem(who.fbid);
  } else {
    selectize.removeItem(who.fbid);
  }

  this.tsmodel.set('friends', friends, {
    silent: true
  });
}
function toggleFriend(fbid, on) {
  var friends = this.tsmodel.get('friends'),
      who = _.findWhere(friends, {
    fbid: fbid
  }),
      $el; //console.log('toggle',fbid);


  if (who && who.fbid) {
    $el = this.$el.find('.friend-item').filter('[data-fbid="' + who.fbid + '"]');

    if (on && !who.selected) {
      who.selected = true;
      $el.addClass('selected');
    } else if (who.selected) {
      who.selected = false;
      $el.removeClass('selected');
    }
  }
}
var recruitSelectConfig = {
  plugins: ['remove_button'],
  persist: false,
  selectOnly: false,
  hideSelected: true,
  maxItems: 9,
  openOnFocus: false,
  selectOnTab: true,
  valueField: 'fbid',
  sortField: 'name',
  labelField: 'name',
  searchField: 'name',
  onInitialize: function onInitialize() {
    this.init = false;
    var view = this.$input.closest('[data-template]').data('view');
    this.setValue(view.who);
    this.init = true;
  },
  onItemRemove: function onItemRemove(fbid) {
    if (this.init) {
      this.$input.closest('[data-template]').data('view').toggle(fbid, false);
    }
  },
  onItemAdd: function onItemAdd(fbid) {
    if (this.init) {
      this.$input.closest('[data-template]').data('view').toggle(fbid, true);
    }

    this.$control_input.blur();
  }
};

/*set seed defaults just in case */

var seed = _.defaults(window.seed || {}, {
  initial_signup_prompt: 1,
  secondary_signup_prompt: 10,
  secondary_signup_prompt_by_days: 14
});

var tseed = window.tseed || {};

if (seed.config) {
  if (!_.has(seed.config, 'no_campaign_recs')) {
    seed.config.no_campaign_recs = false;
  }
}

seed.categories = _.filter(seed.categories, function (cat) {
  return cat.id !== 257;
}); //get rid of "suprise me"

(function ($, w, document, Mod, M, undefined$1) {

  var apipath = '/api/v1';
      seed.sfdomain || 'sierraclub.force.com';
      var //jquery caching, though won't need as much as usual
  $w = $(w),
      $d = $(document),
      $html = $(document.documentElement),
      $body = $(document.body),
      $container = $('#container'),
      $nav = $body.find('.main-nav'),
      $notify = $body.find('.notification'),
      $modalouter = $body.find('.modal-outer'),
      $modal = $modalouter.find('.modal-content'),
      $pagecontent = $('#page-body'),
      uniqueid = 1,
      /*shortcut strings*/
  sp = ' ',
      //hehe
  truestring = 'true',
      dataTemplateSelector = '[data-template]',
      neededTxt = 'Here&rsquo;s where you&rsquo;re needed',
      reloadBtnHTML = '<br><br><a href="javascript:window.location.reload()" class="white">Reload</a>',
      retina = Mod.highresdisplay,
      apptitle = 'Help Wildlife, Protect the Environment, Support Nature Conservation, Save the Planet',
      tablet = 768,
      //lock width at 980 for old browsers
  mq = typeof w.matchMedia !== "undefined" || typeof w.msMatchMedia !== "undefined",
      getWidth = function getWidth(_) {
    return M.max(mq ? document.documentElement.clientWidth : 980, w.innerWidth || 0);
  },
      getHeight = function getHeight(_) {
    return $w.height();
  },
      wwidth = getWidth(),
      defsource = '701i00000015KrcAAE';
      // //was 714ZSCAU00, pre knowwho
  gup('promoid') === defsource ? '' : gup('promoid');
      var sourceObj = _.cleanObj({
    source: gup('source'),
    promoid: gup('promoid'),
    s_subsrc: gup('s_subsrc')
  }),
      tmpsrc = {},
      tmpsign = {},
      notify = gup('notify'),
      //one time landing notifications
  debug = gup('debug') === truestring || c('debug') === truestring && gup('debug') !== "false",
      firstvisit = c('splashed') !== truestring || gup('showsplash') === truestring,
      //let's hold off on using it
  post_login_url = gup('post_login'),
      althomepage_url = gup('alt_homepage'),
      prompt_login = gup('prompt_login') === truestring,
      join_login = althomepage_url === "join-today",
      hash = window.location.hash.replace("#", '').replace("_=_", '').split('?')[0],
      gnow = _.now(),
      //just ot make sure local environments talk to a real api
  hostname = w.location.hostname,
      domain = w.location.protocol + "//" + hostname,
      //will eventually be addup
  nua = navigator.userAgent.toLowerCase(),
      is_crios = /crios/.test('nua'),
      directlogin = is_crios || /(iphone|ipod|ipad).*os (8_|10_)/.test(nua) || /simulator/.test(nua),
      oldie = $html.hasClass('oldie'),
      is_anyIE = ("ActiveXObject" in window),
      is_android = nua.indexOf("android") > -1,
      is_iOS = window.ios,
      no_twitter_api = is_anyIE || is_crios || is_android,
      startApp = $.Deferred(),
      guid = c('scguid') || '',
      getGuid = function getGuid(_) {
    return guid;
  },
      mid = c('mid'),
      sentFriends = c('fbf'),
      //whether fb friends were sent recently
  pinfokeys = ['moleid', 'email', 'prefix', 'first_name', 'last_name', 'city', 'state', 'zipcode', 'gender', 'street_address', 'additional_address', 'phone'],
      pinfokeyslogin = pinfokeys.concat('loggedin'),
      urlpinfo = _.pick(_.object(_.map(pinfokeys, function (k) {
    return [k, gup(k)];
  })), _.identity),
      //reduce falsy values
  moleinprogress = false,
      setMIP = function setMIP(_boolean) {
    return moleinprogress = _boolean;
  },
      //models and temporary object till we get collections
  user,
      models = {},
      SyncItem,
      //viewtypes
  mixins,
      //special view setup behaviors as requested by the subviews
  SimpleView,
      //router and router to view handler
  MainRouter,
      router,
      currentViews = [],
      activeCampaign = {},
      modaldfd = $.Deferred().resolve(),
      //set modal to "done" to start
  categories = seed.categories,
      catsObject = _.reduce(categories, function (acc, item) {
    var _$extend;

    return _.extend(acc, (_$extend = {}, _defineProperty(_$extend, item.slug, item), _defineProperty(_$extend, item.id, item), _$extend));
  }, {}),
      //object of slugs keys
  addCats = function addCats(items) {
    return _.map(items, function (item) {
      //if a list has items with a categories array of ids, transform those into real categories
      if (item.categories && item.categories.length) {
        item.categories = item.categories.map(function (catid) {
          return catsObject[catid];
        });
      }

      return item;
    });
  },
      seen_reasons = {},
      //create a function that takes data and returns a particular sort of wrapping template
  subtemplate = function subtemplate(tname) {
    return function (data) {
      return data ? {
        template: tname,
        data: data
      } : reject();
    };
  },
      cc_do_more_campaigns_array = Object.keys(window.tseed)[0] ? $.map(window.tseed.cc_do_more_campaigns, function (value, index) {
    return [value];
  }) : [],
      datasources = {
    'welcome-campaigns': function welcomeCampaigns(_) {
      return subtemplate('campaign-tiles')(Campaigns.byIds(seed.welcome));
    },
    'featured-campaigns': function featuredCampaigns(_) {
      return subtemplate('campaign-tiles')(Campaigns.byIds(seed.is_featured));
    },
    'resist-campaigns': function resistCampaigns(_) {
      return subtemplate('resist-campaign-tiles')(Campaigns.byIds(cc_do_more_campaigns_array.map(function (x) {
        return x.id;
      })));
    },
    'your-campaigns': function yourCampaigns(limit) {
      return moleyourcampaigns().then(subtemplate('campaign-tiles'));
    },
    'recommended-campaigns-list': function recommendedCampaignsList(_) {
      return Campaigns.best(activeCampaign.id, true).then(subtemplate('campaign-tiles'));
    },
    'campaigns-nearby': function campaignsNearby(limit) {
      return molecampaignsnearby().then(subtemplate('campaign-tiles'));
    },
    'campaigns': function campaigns(catid) {
      return (catid ? searchcamps({
        category: catid
      }) : molecampaigns()).then(subtemplate('campaign-tiles'));
    },
    'campaign': function campaign(idorslug, view) {
      var dfd = $.Deferred(),
          //outer dfd
      saved = Campaigns.idOrSlug(idorslug); //could be either, get a campaign that works

      /*if we already have it and either we have the full campaign or don't need it, return that*/

      if (saved && _.lookup(saved, 'attributes.full') || saved && view && view.noactions) {
        //lug('found campaign without lookup',saved);
        return dfd.resolve(saved.toJSON());
      } else {
        return molecampaign(idorslug).done(dfd.resolve).fail(dfd.reject);
      } // unreachable code
      // return dfd.then(function(d){//we got some json, lets get the real actions then merge in stats
      //         //we need this stat as a full campaign fallback
      //         //console.log(`%cdata: campaign/${d.slug}`,'font-size:9px;color:purple',d);
      //         var supporters = Stats.findWhere({id:'supporters'})?
      //             Stats.findWhere({id:'supporters'}).get('stat'):
      //             2400000;
      //
      //     return $.extend(
      //         {},
      //         getRealActions(d),
      //         {stat_supporters:supporters}
      //     );
      // });

    },
    'your-events': function yourEvents(limit) {
      return moleyourevents().then(subtemplate('event-tile'));
    },
    'eventnoop': function eventnoop(_) {
      return resolve([]).then(subtemplate('event-tile'));
    },
    'event': function (_event) {
      function event(_x) {
        return _event.apply(this, arguments);
      }

      event.toString = function () {
        return _event.toString();
      };

      return event;
    }(function (event) {
      return moleapi("/events/".concat(event), 'get', {}).then(function (event) {
        return Actions.add(event, {
          merge: true
        }) && event;
      });
    }),
    'my-cats': function myCats(params) {
      var usercats = user.get('preference_ids');
      return {
        categories: _.map(categories, function (c) {
          if (_.contains(usercats, c.id)) {
            c.picked = true;
          }

          return c;
        })
      };
    },
    'happening-now': function happeningNow() {
      $('.happening-now').hide();
      pollForScroller();
      return _happeningNow();
    },
    'search-events': function searchEvents(params) {
      return searchevents(params);
    },
    'stats': function stats(_) {
      var impactStats = Stats.toJSON();
      impactStats = impactStats.filter(function (obj) {
        return obj.id !== "supporters";
      });
      return subtemplate('impact-stats')(impactStats);
    },
    notifications: function notifications(undefined$1, view) {
      var dfd = view.storednotifs || moleapi('/u/notifications');
      return dfd.then(function (n) {
        var bigside = null,
            friends = n.friends,
            notifications = _.compact(n.notifications.slice(0)),
            //get a true copy, kick out nulls
        groups,
            readmore = false;

        view.storednotifs = dfd;

        if (friends && friends.length) {
          notifications.unshift({
            date: ~~(_.now() / 1000),
            first_name: user.get('first_name'),
            recruits: friends,
            num_recruits: friends.length - 1,
            two_recruits: friends.length === 2,
            notice_type: "friends_joined"
          });
        }

        if (notifications.length > view.limit) {
          readmore = true;
          notifications = _.first(notifications, view.limit);
        } //lug('pre bigside',notifications.length);

        /*now we're going to split things out and try to balance them*/


        groups = _.partition(notifications, function (el, i) {
          var which = i % 2; //default
          //console.log(el.notice_type,which,i);

          if (el.notice_type === "reportback") {
            if (bigside !== null) {
              //lug('fixing bigside',bigside,which);
              if (bigside === which) {
                which = which ? 0 : 1;
              }

              bigside = null;
            } else {
              bigside = which; //lug('setting bigside',bigside);
            }
          }

          return which;
        }).reverse(); //partition treats 0 as false, so it's flipped, but we use 0 and 1 directly below, so need to reverse it
        //lug('bigside swap?',{bigsideleft:(bigside!==null && groups.length>1), which:bigside, groups:groups, left:groups[0].length, right:groups[1] && groups[1].length});

        if (groups && groups.length > 1) {
          //if we have groups
          if (M.abs(groups[0].length - groups[1].length) > 1) {
            //if the sides aren't equal
            //lug('bigside sides off by more than 1',groups[0].length,groups[1].length);
            bigside = groups[0].length > groups[1].length ? 0 : 1; //then flag one as being bigger
          }

          if (bigside !== null && groups[bigside].length < groups[bigside ? 0 : 1].length) {
            //lug('but sides are already balanced or bigside is already smaller');
            bigside = null; //but don't do this if big side is less than the other
          } //and, when all is said and done...


          if (bigside !== null) {
            //lug('bigside pre swap',bigside, groups[0].length, groups[1].length);
            groups[bigside ? 0 : 1].push(groups[bigside].pop()); //swap a last one
            //lug('bigside post swap',bigside, groups[0].length, groups[1].length);
          }
        }

        if (notifications.length === 0) {
          $body.find('.impact-recruit').show();
        }

        return {
          notifications: notifications,
          ln: notifications.length,
          left: groups[0],
          right: groups[1],
          readmore: readmore
        };
      });
    },
    'impact-stats': function impactStats() {
      var cats = user.get('category_completion') || {},
          //get what the user has done if anything
      none = !_.filter(cats, function (cat) {
        return cat && cat.taken && cat.taken > 0;
      }).length; //if cats is empty or the sum is 0, flag that no cat actions have been taken

      /* extend known categories with any user data, or random data */

      cats = _.map(categories, function (cat, key) {
        var match = cats[cat.id];
        return _.extend(cat, none && !match ? {
          taken: 0,
          available: ~~(M.random() * 30) + 1
        } : match || {});
      });
      return {
        categories: cats,
        no_actions: none
      };
    },
    'countries': function getCountries() {
      return getCountries.countries || (getCountries.countries = moleapi("/countries").then(function (c) {
        return {
          countries: _.map(c, function (v) {
            return {
              name: v,
              value: v
            };
          })
        };
      }));
    },
    action: function action(id, view) {
      var a = Actions.get(id);
      a = a ? a.toJSON() : {
        nodisplay: true
      }; //if we can't get the action flag to not show it
      //console.log('action by id', id, a);

      if (view && view.single_action) {
        //if the view flags for single actions, make sure the action includes that flag
        a.single_action = true;
      }

      if (a.action_type === "donate") {
        return datasources.countries().then(function (countries) {
          return $.extend(a, countries);
        });
      }

      if (a.action_type === "event") {
        a = $.extend(a, calendarize(a));
      }

      return a;
    }
  },
      //FB
  fb_init = $.Deferred(),
      fb_connect = $.Deferred(),
      fbmaybe = false,
      fbscope = 'publish_actions,email,user_location,user_friends',
      permissionscope = {
    scope: fbscope //main, original login scope

  },
      fbnoauth = false,
      auth = false,
      getauth = function getauth(_) {
    return auth;
  },
      ts_friendnum = 9,
      fburlstate = gup('state') || gup('#state'),
      default_share_id = 1;
      $.Deferred();
      var gotContacts = $.Callbacks('unique'),
      csclients = ['gmail', 'yahoo', 'outlook', 'other'],
      REGEX_EMAIL = "^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$",
      emregex = new RegExp(REGEX_EMAIL, "i"),
      silent = {
    silent: true
  },
      xhrPool = [],
      online = true,
      //_.isUndefined(navigator.onLine)?true:navigator.onLine,//assume we're online unless we have an api report saying otherwise
  isAppOnline = function isAppOnline(_) {
    return online;
  },
      cachedReqs = [],
      //store for requests made while offline
  cachedNav = $.Deferred(),
      timeout = 40000,
      //in ms
  cancler = reject(); //lug('starting guids',{guid:guid,cookieguid:c('scguid'),hash:hash},'starting sources',sourceObj);


  var _happeningNow = function _happeningNow() {
    return moleapi('/users/recent_actions?limit=35', 'get', {}).then(function (fetched_recent_actions) {
      return {
        "data": fetched_recent_actions
      };
    }).then(function (recent_actions) {
      recent_actions.data.map(function (item) {
        item.action_type = item.action_type === 'Petition' ? 'signed on' : item.action_type;
        item.action_type = item.action_type === 'Donation' ? 'donated to support' : item.action_type;
        item.action_type = item.action_type === 'TweetCampaign' ? 'tweeted on' : item.action_type;
        item.action_type = item.action_type === 'SierraEvent' ? 'is attending' : item.action_type;
        item.action_type = item.action_type === 'VolunteerAction' ? 'is volunteering on' : item.action_type;
        item.action_type = item.action_type === 'TargetShare' ? 'shared' : item.action_type;

        if (item.last_name) {
          item.last_name = item.last_name.charAt(0);
        }

        var now = new Date();
        var offset = now.getTimezoneOffset() * 60;
        var timeUnits = [['second', 1], ['minute', 60], ['hour', 3600], ['day', 86400], ['week', 604800], ['month', 2592000], ['year', 31536000]];
        var timeDiff = Math.round((now - new Date(item.action_timestamp)) / 1000) + offset;

        for (var i = 0; i < timeUnits.length; i++) {
          if (timeDiff >= timeUnits[i][1]) {
            var newTime = Math.round(timeDiff / timeUnits[i][1]);
            item.action_timestamp = "".concat(newTime, " ").concat(timeUnits[i][0]) + (newTime > 1 ? 's ' : ' ') + 'ago';
          }
        }
      });
      return recent_actions;
    });
  };

  var happeningNowFunction = function happeningNowFunction() {
    var parent = document.getElementById('scroller');
    var scrollItems = parent.querySelectorAll('.item');

    if (scrollItems.length > 4) {
      $('.happening-now').show();
      setInterval(function () {
        var scrollItem = parent.querySelectorAll('.item');

        for (var i = 0; i < scrollItem.length; i++) {
          scrollItem[i].classList.toggle('sliding-now');
        }

        setTimeout(function () {
          parent.appendChild(scrollItem[0]);
        }, 1000);
      }, 1000);
    } else {
      $('.happening-now').hide();
    }
  };

  var pollForScroller = function pollForScroller() {
    if ($('#scroller').length > 0) {
      happeningNowFunction();
    } else {
      setTimeout(pollForScroller, 500);
    }
  };

  user_defaults.lastloaded = gnow; //last time we pulled user information from MOLE important to know because someone might leave a browser tab open for a long time, make changes elsewhere, and we'd want to know to recheck

  setcookie('debug', debug, 30);
  setcookie('lv', gnow, 364);

  xhrPool.abortAll = function () {
    lug('ABORTING ALL REQUESTS', this, xhrPool);

    _.each(this, function (el) {
      return el && el.abort ? el.abort() : null;
    });

    this.length = 0;
  };

  $d.ajaxSend(function (e, jqXHR, options) {
    xhrPool.push(jqXHR); //lug('adding',jqXHR,xhrPool.length,xhrPool.abortAll);
  });
  $d.ajaxComplete(function (e, jqXHR, options) {
    var index = _.indexOf(xhrPool, jqXHR);

    if (index > -1) {
      xhrPool.splice(index, 1);
    } //lug('removing',jqXHR,xhrPool.length,xhrPool.abortAll);

  });
  $.ajaxSetup({
    timeout: timeout
  });
  window.xxx = xhrPool; //list of ongoign xhr-ish requests that we track so that we can abort them all if the user loses login status

  /*because Safari and iOS basically break local and session storage in private browsing node, we need to make sure we avoid that error and don't waste time on that functionality*/

  var ls = Mod.localstorage ? localStorage : {
    setItem: $.noop,
    getItem: $.noop,
    clear: $.noop
  },
      ss = Mod.sessionstorage ? sessionStorage : encryptcookie;
  /*methods for saving and restoring both long-term and short-term data*/

  var lstore = function lstore(k, v) {
    return ls.setItem(k, JSON.stringify(v));
  };

  function sstore(k, v) {
    if (guid === (c('scguid') || "")) {
      //this is proving unworkable in incognito mode when logged out, as of course there's no way to persist
      ss.setItem(k, JSON.stringify(v));
    } else {
      lug('session data loss', {
        guid: guid,
        scguidcookie: c('scguid')
      });
      clearAuth();
    }
  } //make sure what's stored is safe to parse


  var safeJ = function safeJ(v) {
    return typeof v === "string" && v.indexOf('{') === 0 ? v : '{}';
  };

  var retrieve = function retrieve(k) {
    return JSON.parse(safeJ(ls.getItem(k)));
  };

  var sretrieve = function sretrieve(k) {
    return JSON.parse(safeJ(ss.getItem(k)));
  };

  SyncItem = Backbone.Model.extend({
    initialize: function initialize(attr, ops) {
      //lug("init sync item",this,user_defaults);
      $.extend(this, ops || {});
      this.keys = _.keys(user_defaults);
      models[this.name] = this;
      this.on('change', this.store); //this needs to be waaaaaay more complicated! Needs to handle cases where we shouldn't store, when to sync with backend, etc.  Basically, it's VIEWS that should decide this, not the model

      this.restore();
    },
    defaults: user_defaults,
    store: function store() {
      var attr = _.pick(this.attributes, this.keys); //store only whitelisted things


      lstore( //locally store
      '-user', //against this key
      _.omit(attr, this.sessiononly, this.nosave)); ////lug('STORING SESSION',attr);

      sstore('-user', _.omit(attr, this.localonly, this.nosave)); //$html.addClass(obj_to_str(this.attributes));
    },
    restore: function restore() {
      var sdata = sretrieve('-user'),
          ldata = retrieve('-user');
      /*"temporary" attempt to catch any old object formats*/

      if (sdata.avatar && _.isObject(sdata.avatar) && sdata.avatar.url) {
        sdata.avatar = sdata.avatar.url;
      } //silently restore the model, favoring the more detailed values that could be stored in the session
      //lug({'RESTORING local':ldata,'session log state':sdata.loggedin,'local log state':ldata.loggedin});


      this.set($.extend({}, sdata, _.pick(ldata, ['num_actions', 'num_campaigns', 'num_recruits', 'historyactions', 'num_secondary_recruits', 'num_secondary_actions'])), silent);

      if (!ldata.loggedin && sdata.loggedin) {
        //lug('no domain level login even though session thinks we have one');
        this.set(user_defaults); //ahhhhhh

        clearAuth();
      } else if (ldata.loggedin && !sdata.loggedin) {
        //lug('user is logged in, sorta');
        fbmaybe = ldata.fbloggedin;
      } //lug({fburlstate:fburlstate,fbcookiestate:c('fbstate'),guptoken:gup('code')});


      if (gup('code')) {
        if (fburlstate === c('fbstate')) {
          fbmaybe = true; //user is explicitly trying to log in with facebook
        }

        deletecookie('fbstate');
      }
    }
  });
  /*need to think about how we access these various properties: might be better to get at them in a unified way*/

  user = new SyncItem({}, {
    name: 'user',
    classlist: ['activated', 'zipcode', 'email'],
    nosave: ['recs'],
    serverwhitelist: _.without(pinfokeys, 'email'),
    sessiononly: ['moleid', 'avatar', 'fbperms', 'prefix', 'randfb_friends', 'last_name', 'email', 'preferences', 'city', 'state', 'gender', 'street_address', 'additional_address', 'phone'],
    localonly: []
  });

  if (user.attributes.phone) {
    var oldphone = user.attributes.phone.toString();
    user.attributes.phone = oldphone.replace(/^1/, '');
  }

  var Campaign = Backbone.Model.extend({
    initialize: function initialize(attr, ops) {
      ////lug("init campaign model",this,user_defaults);
      $.extend(this, ops || {});
    }
  });
  var Action = Backbone.Model.extend({
    initialize: function initialize(attr, ops) {
      ////lug("init action model",this,user_defaults);
      $.extend(this, ops || {});
    },
    getCampJSON: function getCampJSON() {
      var id = this.get('campaign_id'),
          camp = Campaigns.get(id);
      return camp ? camp.toJSON() : {
        none: true,
        id: id
      };
    }
  });
  var TSmodel = Backbone.Model.extend({
    defaults: {
      pointer: ts_friendnum
    },
    resetSelected: function resetSelected() {
      var friends = this.get('friends');

      if (friends && _.isArray(friends)) {
        _.each(friends, function (f, i) {
          f.seen = false;

          if (i < ts_friendnum - 1) {
            f.seen = true;
          }

          f.selected = false;
        });

        this.set('friends', friends);
        this.trigger('reset');
      }
    },
    more: function more() {
      var friends = this.get('friends'),
          keep = _.where(friends, {
        selected: true
      }),
          unseen = _.where(friends, {
        seen: false
      }),
          need = ts_friendnum - keep.length,
          temp;

      if (keep.length !== ts_friendnum) {
        if (unseen.length >= need) {
          _(need).times(function (n) {
            unseen[n].seen = true;
          }); //lug('stillunseen',_.pluck(_.where(unseen,{seen:false}),'name'));

        } else {
          temp = _.filter(friends, function (friend) {
            return !friend.selected;
          });

          _.each(temp, function (friend) {
            return friend.seen = false;
          });

          _(need - unseen.length).times(function (n) {
            temp[n].seen = true;
            unseen.push(temp[n]);
          });
        }

        this.set({
          'friends': friends
        }, silent);
        return keep.concat(unseen);
      } else {
        return false;
      }
    }
  });

  var Campaigns = _.extend(new SimpleCollection([], {
    model: Campaign
  }), {
    comparator: function comparator(camp) {
      return -camp.get('id');
    },
    best: function best(current_camp_id) {
      return Actions.bestRecs().then(function (recs) {
        return _.chain(recs).filter(function (rec) {
          return rec.campaign_id !== current_camp_id && !!Campaigns.get(rec.campaign_id);
        }).uniq(_.iteratee(!user.get('email') ? 'campaign_id' : undefined$1)).map(function (action) {
          var rec_a_type = action.action_type;

          if (action.action_type === 'petition' && action.knowwho_action_type === 'Contact Officials - All info required') {
            rec_a_type = 'message';
          }

          return $.extend({
            rec_action_type: rec_a_type,
            rec_action_slug: action.slug,
            rec_action_verb: action.verb
          }, Campaigns.get(action.campaign_id).toJSON());
        }).value();
      });
    },
    goodOne: function goodOne(current_camp_id) {
      return this.best(current_camp_id).then(function (recommendations) {
        var camp = recommendations.length && _.shuffle(_.first(recommendations, 4))[0];

        return camp || reject(); //if we got one, return it, otherwise reject
      }).then(undefined$1, _.bind(this.randomdfd, this, current_camp_id)); //and if rejected, get a random one
    },
    idOrSlug: function idOrSlug(idorslug) {
      return this.findWhere({
        slug: idorslug
      }) || this.findWhere({
        id: parseFloat(idorslug)
      });
    },
    filtertitle: function filtertitle(query) {
      var pattern = new RegExp($.trim(query).replace(/[/\\*]/g, "").replace(/ /gi, '|'), "i");
      return function (model) {
        return pattern.test(model.get('title')) || pattern.test(model.get('goal'));
      };
    },
    filteryours: function filteryours() {
      return function (model) {
        var yours = user.get('yourcampaigns') || []; //console.log(yours.join(','),_.contains(yours, model.get('id')) );

        return _.contains(yours, model.get('id'));
      };
    },
    filterstate: function filterstate(state) {
      return function (model) {
        return _(model.attributes.campaign_state_restriction).contains(state);
      };
    },
    filtercategory: function filtercategory(category) {
      return function (model) {
        return _(model.attributes.categories).contains(parseFloat(category));
      };
    },
    filternational: function filternational(_) {
      return function (model) {
        return !model.attributes.campaign_state_restriction.length;
      };
    },
    multifilter: function multifilter(methodargs) {
      lug('meta methodargs', methodargs);
      var self = this;
      return _.reduce(methodargs, function (memo, methodarg) {
        return _.filter(memo, self['filter' + methodarg[0]](methodarg[1]));
      }, this.models);
    }
  });

  var Stats = new SimpleCollection(_.reject(seed.stats || [], function (obj) {
    return obj.stat === 0;
  }));
  models.stats = Stats;
  /*define some models*/

  var Actions = _.extend(new SimpleCollection([], {
    model: Action
  }), {
    lastaction: null,
    bestRecs: function bestRecs() {
      if (this.recs && this.recs.state && this.recs.state() === "pending") {
        return this.recs;
      } else {
        var recs = user.get('recs');
        this.recs = (recs.length > 3 ? resolve() : molerecs_partial()).then(function () {
          return [];
        });
      }

      return this.recs;
    },
    bestOne: function bestOne(offset) {
      offset = offset || 0;
      return this.bestRecs().then(function (d) {
        var ln = d.length,
            which_action = ln ? d[offset % ln] : undefined$1;

        if (which_action) {
          Actions.get(which_action.id).set('reason', recReason(which_action), silent);
        }

        return which_action; //offset can keep going up but it'll always fall into the array
      });
    }
  });

  var TSModels = new SimpleCollection([], {
    model: TSmodel
  });
  /*run behavior on any new TSmodels*/

  TSModels.on('add', function (model) {
    var realperms = fbscope; //hardcode all models to use this for now

    model.set('extra_permissions', realperms, silent);
    permsReduce(model);
    model.listenTo(user, 'change:fbperms', function (_) {
      return permsReduce(model);
    });
    getTS(model);
  }).on("change:friends", function (model) {
    return model.resetSelected();
  });
  /*set up events on actions to enhance them when added*/

  Actions.on('add', function (model, f, ops) {
    var action_type = model.get('action_type'),
        has = user.get('historyactions'),
        assocs = model.get('assoc_actions');

    if (assocs && assocs.length) {
      if (_.intersection(has, assocs).length) {
        //user.set('historyactions', has.push(model.get('assoc_actions')));
        model.set('action_took', true, silent);
      }
    } else if (_.contains(has, model.get('id'))) {
      //lug('setting to true',model.get('id'));
      model.set('action_took', true, silent);
    } //action models should always be slugs at this point and we only care that the top-level model has been added, the first time


    if (!model.get('title')) {
      model.set('title', model.get('verb'), silent);
    }

    if (action_type === 'recruit') {
      if (model.id % 1 === 0) {
        //if it's not a dot clone, don't do this
        model.set({
          'email-step': false
        }, silent);

        if (!TSModels.findWhere({
          model: model.get('model')
        })) {
          model.set('model', newTS(model), silent); //turns the model into a reference
        }
      }
    }

    if (action_type === 'petition') {
      if ($.trim(model.get('description')) === "") {
        model.set('description', false, silent);
      }

      if (model.get('body') === "--- Personalize your message here ---" || model.get('body') === "-- Add a personal message here --") {
        model.set('body', false, silent);
      }
    } //lug('action added',model.get('title'));

  }); //add in campaigns from seed

  if (seed.campaigns && seed.campaigns.length) {
    Campaigns.add(seed.campaigns); //can do directly since they don't have actions
  } //add in campaigns from seed


  if (tseed.cc_do_more_campaigns && tseed.cc_do_more_campaigns.length) {
    Campaigns.add(tseed.cc_do_more_campaigns); //can do directly since they don't have actions
  }
  /*we have a default share for sharing addup itself*/


  if (seed.default_share) {
    $.extend(seed.default_share[0], {
      default_share: true,
      csclients: csclients,
      outer: {
        url: domain,
        //it's just the standard share now
        title: apptitle,
        image: '/img/addup.png?1&',
        goal: 'Join campaigns to protect the environment.',
        ogtype: 'website'
      }
    });
    default_share_id = seed.default_share[0].id;
    Actions.add(seed.default_share[0]);
  }

  function newTS(oldmodel, additions) {
    var attr = oldmodel.attributes,
        perms = fbscope,
        //attr.extra_permissions,
    clone = {
      model: attr.model,
      'extra_permissions': perms,
      'missing_permissions': checkPerms(perms)
    };
    return TSModels.add(_.extend(clone, additions || {}));
  }

  function permsReduce(model) {
    var perms = checkPerms(model.get('extra_permissions')); //lug('perms here',perms);

    model.set('missing_permissions', perms === "publish_actions" ? false : perms);
  }

  user.on('change:fbconnected', function (e) {
    //getTS(e);

    /*try to rerun all models for this new user*/
    //lug('TS DETECT fblogin status changed, userfb is',user.get('fbloggedin'));
    if (user.get('fbconnected')) {
      //if there are any models hanging around that haven't been tried, try them now as if they'd just been added
      //lug('TSModels remaining', TSModels.pluck('ts_tried'), TSModels.where({ts_tried:undefined}) );
      _(TSModels.where({
        ts_tried: undefined$1
      })).each(function (m) {
        //lug('TStriggering add');
        TSModels.trigger('add', m);
      });
    } else {
      TSModels.invoke('unset', 'friends', silent);
      TSModels.invoke('unset', 'session', silent);
      TSModels.invoke('unset', 'ts_tried', silent);
    }
  });

  function getTS(tsmodel) {
    //lug('TS CALLED friends request',{data: JSON.parse(JSON.stringify(tsmodel.attributes))});
    fb_init.done(function () {
      //wait for FB to actually be ready
      if (getauth() && user.get('fbconnected') && !tsmodel.get('ts_tried')) {
        //lug('TS actually attempting model',tsmodel);
        tsmodel.set({
          ts_tried: true
        }, silent);
        var options = {
          model: M.round(M.random()) ? tsmodel.get('model') : 'interaction',
          limit: 7e5
        };

        if (tsmodel.get('loc')) {
          options.loc = tsmodel.get('loc');
          options.name = tsmodel.get('model') + '_' + tsmodel.get('loc').replace(/[^0-9]/g, '');
        }
      }
    });
  } //this helper function allows views to get data either directly (like from a model) or from a simple non-modeled source as outlined in the datasources array
  //it works by returning a deferred that can be used to chain either immediately or by collapsing in other asynchronous options/deferreds


  function findData(datasource, opid, view) {
    var dfd = $.Deferred(),
        id = opid || undefined$1; //if we already have data, send it on through

    if (_.isObject(datasource)) {
      dfd.resolve(datasource);
    } else {
      //otherwise, lets parse the string and see if it's a known method
      if (datasource.indexOf(':') > -1) {
        id = datasource.split(':')[1];
        datasource = datasource.split(':')[0];
      }

      if (!datasources[datasource]) {
        dfd.reject();
        lug('%cFAILED TO FIND to find ', 'color:red', arguments);
      } else {
        $.when(datasources[datasource](id, view)).done(dfd.resolve).fail(dfd.reject);
      }
    }

    return dfd;
  } //revoke fb permissions. Likely not used in production


  var revoke = function revoke(_) {
    return fbapi('/me/permissions', 'delete').done(disconnectFB);
  };
  /*dealing with actions*/
  //get all views with actionmodels with an optional reject filter


  var getCurrentActionViews = function getCurrentActionViews() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return activeActionModelViews.apply(void 0, [currentViews].concat(args));
  };

  function recommendCampaignSectionifDone() {
    var apiToken = c('scguid');
    var moleid = user.get('moleid') || 0;
    var user_id = moleid ? "&user_id=".concat(moleid, "&api_token=").concat(apiToken) : '';
    var endpoint = "/api/v1/recommendations/campaigns?limit=10&exclude_campaign_id=".concat(activeCampaign.id).concat(user_id);
    $.ajax(endpoint).then(function (response) {
      if (response.data) {
        var filteredRecommendations = response.data.map(function (campaign) {
          return {
            sort: Math.random(),
            value: campaign
          };
        }).sort(function (campaign, index) {
          return campaign.sort - index.sort;
        }).map(function (campaign) {
          return campaign.value;
        });

        if (filteredRecommendations[0]) {
          var recommendation = filteredRecommendations[0];
          var $action_section = $body.find('#take-action');

          if ($action_section.length) {
            var name = user.get('first_name');
            var title = 'Here\'s where you\'re needed next' + (name ? ' ' + name : '') + ':';
            $action_section.append(SC['action-next-campaign']($.extend({}, recommendation, {
              head_title: title,
              phrase: title
            })));
            exitIntentModal(filteredRecommendations[0]);
          }
        } else {
          exitIntentModal();
        }
      }
    });
  }

  function exitIntentModal(recommendation) {
    $('#exitintent').remove();
    $(document).off('mouseleave');

    if (recommendation && getWidth() >= tablet) {
      var exit_modal = '' + '<section class="exit-modal modal--show" id="exitintent" tabindex="-1" role="dialog" aria-labelledby="modal-label" aria-hidden="true">' + '   <div class="modal-inner">' + '       <div class="modal-content">' + '           <div class="top">' + '               <div class="penIcon"></div>' + '               <h2>Before you go</h2>' + '               </div>' + '           <div class="middle">' + '               <div class="imgFrame">' + '                   <a href="' + $(".next-campaign-section .grid-row .icon-section .row a").attr("href") + '" id="exit-modal-next-image"><img src="' + $(".title-and-image .campaign-image img").attr("src") + '" /></a>' + '               </div>' + '               <a href="' + $(".next-campaign-section .grid-row .icon-section .row a").attr("href") + '" id="exit-modal-next-title"><h2 class="exit-modal-header">' + $(".title-and-image .title").text() + '</h2></a>' + '           </div>' + '           <div class="bottom">' + '               <a href="' + $(".next-campaign-section .grid-row .icon-section .row a").attr("href") + '" class="button" id="exit-modal-redirect">Take Action</a>' + '               <br />' + '           </div>' + '       </div>' + '   </div>' + '   <a href="javascript:void(0)" class="modal-close" title="Close this modal" id="exit-modal-close" data-close="Close" data-dismiss="modal">&nbsp;</a>' + '</section>';
      $('body').append(exit_modal).addClass('exitintent');
      $(document).on('mouseleave', leaveFromTop);
      $('#exit-modal-redirect').click(function () {
        $('#exitintent').removeClass('active').addClass('inactive');
      });
      $('#exit-modal-close').click(function () {
        $('#exitintent').removeClass('active').addClass('inactive');
      });
      $('#exit-modal-next-image').click(function () {
        $('#exitintent').removeClass('active').addClass('inactive');
      });
      $('#exit-modal-next-title').click(function () {
        $('#exitintent').removeClass('active').addClass('inactive');
      });
    }
  }

  var leaveFromTop = function leaveFromTop(e) {
    setTimeout(function () {
      showExit(e);
    }, 1500);

    function showExit(e) {
      if ($('#exitintent') && e.clientY <= 1) {
        if (e.clientY <= 1) {
          if (!sessionStorage.exitSeen) {
            $('#exitintent').addClass('active'); //                        window['optimizely'].push({
            //                            type: "event",
            //                            eventName: "exit_intent_displayed"
            //                        });

            sessionStorage.exitSeen = 1;
          }
        }
      }
    }
  };

  function updateActionSections() {
    _.each(getCurrentActionViews(), function (view) {
      var $section = view.$el.closest('section');
      $section.toggleClass('action-complete closed-section', !!view.actionmodel.get('action_took')); //what should we do about state? maybe nothing, it's harmless?
    });
  } //trigger a change event on all actionmodels that aren't already listening


  function actionViewRefresh() {
    var ams = _.pluck(getCurrentActionViews('listen'), 'actionmodel');

    _.invoke(ams, 'trigger', 'change');
    /*now reset the toggle states, ugh*/


    updateActionSections();
  } //check history actions and all currently stored actions and update them to their new states


  function actionsUpdate() {
    var hisas = user.get('historyactions'),
        actions = _.compact(_.map(hisas, function (id) {
      return Actions.get(id);
    }));

    if (actions.length) {
      /*not all actions shold be visually refreshed by action_took*/
      actions = _.groupBy(actions, function (a) {
        return a.attributes.action_type !== "recruit";
      }); //lug('refreshable actions ',actions);

      if (actions['true'] && actions['true'].length) {
        _.invoke(actions['true'], 'set', {
          action_took: true
        });
      }

      if (actions['false'] && actions['false']) {
        _.invoke(actions['false'], 'set', {
          action_took: true
        }, silent);
      }
    }

    if (hisas.length) {
      user.trigger('change:historyactions');
    }

    updateActionSections();
  }
  /*Logins and sessions*/


  function clearAuth() {
    auth = false;
    deletecookie('scguid');
    deletecookie('complete');
    deletecookie('mid');
    sessionStorage.clear();
    localStorage.clear(); //rather drastic but ok

    guid = '';
    mid = '';

    if (Actions) {
      Actions.invoke('set', {
        "action_took": undefined$1,
        "action_taken": undefined$1,
        'email-step': false
      });
    } //need to also clear any tags like featured or recommended that may have been user specific


    if (user) {
      user.set(user_defaults);
    } //ahhhhhh


    $html.removeClass('logged-in');
    actionViewRefresh();

    if (router) {
      routeCheck();
    } //make sure user can actually stay on this page

  }

  function refreshSession(scguid, mid) {
    setcookie('scguid', scguid, 182); //6 months

    setcookie('mid', mid, 182); //6 months
  } //login(d,false,false);//do nag preferences, do create a new ga session


  function login(mole_user_obj, nonag, implicit, quiet, post_login) {
    //nonag: need to figure this out
    mole_user_obj = $.extend({}, mole_user_obj || {});
    guid = mole_user_obj.api_token || guid;
    post_login = post_login_url || post_login; //let url add  or override

    if (!guid || !mole_user_obj.id) {
      logout();
      meerkatMsg($(SC['login-error']()).text());
      report(['Login Failure', !guid ? 'no token' : 'invalid login object', mole_user_obj.id]);
      return false;
    } //lug('logging in with',mole_user_obj,nonag,implicit,quiet,{post_login:post_login});


    $body.find('.sfif').remove();
    refreshSession(guid, mole_user_obj.id); //6 months

    user.set('recs', [], silent).set(translateMoleLogin(mole_user_obj), silent).store(); //silent until we know if we're navigating away or not

    actionsUpdate();
    /*ga funnery*/

    if (!implicit) {
      gacreate();
    } //since the new session is truly being created anew, rather than being a carryover, inform ga


    user.set({
      activated: true,
      'loggedin': true
    });
    $html.addClass('logged-in');
    var zipRegex = /^(\d{5}){1}(-?\d{4})?/;

    if (zipRegex.test(user.attributes.zipcode)) {
      user.attributes.zipcode = user.attributes.zipcode.match(zipRegex)[1];
      user.set('zipcode', user.attributes.zipcode, silent);
    }

    clearNav();

    if (!quiet) {
      //if we're told explicitly we oughta be quiet
      report(['Accounts', 'Login']);
    }

    if (user.get('fbconnected') && user.get('fbloggedin')) {
      if (!sentFriends) {
        fb_connect.done(function () {
          appFriendIds().then(function (ids) {
            return moleapi('/u/fbfriends', 'POST', {
              fbids: ids
            });
          });
        });
        setcookie('fbf', true, 20);
      }

      if (!user.get('avatar')) {
        lug('no pic, getting FB pic');
        getFBPicture();
      }
    }

    if (user.attributes.user_type_id > 1) {
      $('.main-nav, .mobile-nav-menu').addClass('cms-login'); // $nav.find('.footer-navigation').append('&nbsp;<li><a href="/cms/login">CMS Login</a></li>');
    } else {
      $('.main-nav, .mobile-nav-menu').removeClass('cms-login');
    }

    post_login_url = undefined$1; //we should only allow this one once: if they were ever logged in then logging out and logging in again should NOT trigger this behavior

    if (post_login) {
      router.navigate(post_login, {
        trigger: true
      }); //nope, need a auth non-auth hash for pages
    } else if (!nonag && window.location.pathname.indexOf('campaigns') === -1 && window.location.pathname.indexOf('share') === -1 && !user.get('preferences')) {
      router.navigate('issues-you-care-about', {
        trigger: true
      }); //nope, need a auth non-auth hash for pages
    } else {
      if (routeCheck()) {
        //if we're staying on the page, just refresh the actions and stop being silent
        //user.trigger('change');
        actionViewRefresh();
      } else {
        if (!nonag && !!user.get('loggedin') && !user.get('zipcode')) {
          nextload(false, false).done(function () {
            modalView(); //prompt the user to enter their zipcode
          });
        }
      }
    }
  }

  window.loginExternal = function (jsonstring) {
    login(JSON.parse(jsonstring));
  };

  window.triggerNavError = function (jsonstring) {
    user.trigger('error:nav-main', {
      error: JSON.parse(jsonstring)
    });
  };

  function logout(e) {
    lug('logout: cookies', {
      guid: guid,
      cookieguid: c('scguid')
    });

    if (e && e.preventDefault) {
      stopevent(e);
      $body.click(); //should close the nav
    } else if (e !== 'loginfailed') {
      report(['Accounts', 'Logout', e ? 'initiated by user' : 'forced']);
    } else {
      report(['Accounts', 'Logout', 'forced']);
    }

    lug('aborting these outstanding requests: ', window.xxx);
    xhrPool.abortAll(); //cancel all ajax requests

    lug('authonly?', router.authOnly, router.current().route);

    if (!!user.get('moleid') || e === 'loginfailed') {
      //          the below iframe is currently blocked on SF side. commenting out for now.
      //          $body.find('.sfif').orPrepend('<iframe class="sfif hiddenif" width="1" height="1" tabindex="-1"/>').attr('src','https://'+sfdomain+'/secur/logout.jsp?'+_.uniqueId() );//full-sierraclub.cs15.force.com
      moleapi('/u/logout', 'DELETE').always(clearAuth); //            moleapi('/saml/logout','GET').always(clearAuth);
    }

    clearAuth();
    pollForHeader();
  }

  function meerkatMsg(html, type, speed) {
    cancler.reject(); //immediately reject anything else

    cancler = $.Deferred(); //start it over

    speed = speed || 40000;
    type = type || 'server-error';
    var cancelDfd = cancler.reject,
        //shorthand
    $servermsg = $body.find('.' + type).orPrepend('<div class="' + type + '"/>') //find or add a msg
    .finish() //stop an animation
    .removeClass('show').show() //unhide it
    .html(html) //replace its content
    .on('click', '.close-btn', cancelDfd) //if there's a close button, all it to close things
    .delay(speed).fadeOut(1200, 'swing', cancelDfd); //fade it out after a delay, then cancel it

    $.wait(2).done($servermsg.use('addClass', 'show'));
    cancler.always($servermsg.use('remove')); //always complete by removing it from the dom
  }

  function giveNotice(json, duration) {
    if (json && _.isString(json.notice)) {
      if (router.current().route === "supporter-home") {
        json = _.omit(json, 'cta'); //don't need to link anywhere, already here
      }

      if (json.link_to && json.link_to.indexOf('supporter-home') > -1 && !user.get('loggedin')) {
        json.link_to = '/join-today'; //unregistered user, should be prompted to create account
      }

      meerkatMsg(Handlebars.partials.milestone(json), 'notice', duration || debug ? 900000 : 22000);
    }
  }

  function sourceUpdate(obj) {
    obj = obj || {};
    tmpsrc = {};
    tmpsign = {};

    if (obj.source_action) {
      tmpsrc.promoid = obj.source_action;
    }

    if (obj.subsource_action) {
      tmpsrc.s_subsrc = obj.subsource_action;
    }

    if (obj.source_signup) {
      tmpsign.promoid = obj.source_signup;
    }

    if (obj.subsource_signup) {
      tmpsign.s_subsrc = obj.subsource_signup;
    }

    tmpsrc = _.extend({
      promoid: defsource
    }, tmpsrc, sourceObj);
  }

  var gettmpsrc = function gettmpsrc(_) {
    return tmpsrc;
  };

  var getrouter = function getrouter(_) {
    return router;
  };

  function userroute(guid) {
    var userType = guid ? user.get('moleid') : 0;
    return "/users/".concat(userType, "/"); // fix for below, where we have a user id, but no api key from a legit login, aka shadow users
    // return '/users/'+(user.get('moleid')||0)+'/';
  }

  function moleapi(route) {
    var method = arguments.length > 1 && arguments[1] !== undefined$1 ? arguments[1] : 'GET';
    var data = arguments.length > 2 && arguments[2] !== undefined$1 ? arguments[2] : {};
    var config = arguments.length > 3 && arguments[3] !== undefined$1 ? arguments[3] : {};
    data = $.extend(true, {}, data); //don't want to bork data we sent

    var guid = getGuid(),
        router = getrouter(),
        tmpsrc = gettmpsrc();

    if (guid) {
      data.api_token = guid;
    }

    var db_token = getUrlParam('db_token');

    if (db_token) {
      data.db_token = db_token;
      var url = location.href;
      url = decodeURI(url).split('?');
      var params = url.length === 1 ? '' : url[1];

      if (params) {
        params = params.replace(new RegExp("&?" + 'db_token' + "=[\\w]+", "g"), "");
        params = params.replace('&', '');
        url = url[0] + '?' + params;
      } else {
        url = url[0];
      }

      history.pushState({
        page: url
      }, '', url);
    }

    _.extend(data, tmpsrc);
    /*jshint validthis:true */


    var self = this,
        routestring = route.replace('/u/', userroute(guid)).replace(/\/$/, ""),
        ops = {
      url: apipath + routestring,
      type: method,
      data: data,
      dataType: 'json'
    },
        args = arguments; //original arguments, in case we need to save them
    //lug('trying api '+routestring+' with guid api_token ',guid);

    function requeue() {
      var queuedfd = $.Deferred(),
          queuedfd2 = $.Deferred();
      queuedfd.done(function () {
        moleapi.apply(self, args).done(function () {
          queuedfd2.resolveWith(self, arguments);
        }).fail(function () {
          queuedfd2.rejectWith(self, arguments);
        });
      }); //lug('added',queuedfd);

      cachedReqs.push(queuedfd);
      return queuedfd2;
    } //  This could live somewhere more globally accessible.


    function getUrlParam(parameter_name) {
      var query = window.location.search.substring(1);
      var vars = query.split("&");

      for (var i = 0; i < vars.length; i++) {
        var pair = vars[i].split("=");

        if (pair[0] === parameter_name) {
          return pair[1];
        }
      }

      return false;
    }

    if (config.lock) {
      setMIP(true);
    }

    if (isAppOnline()) {
      _.now();
      return $.ajax(ops).then(function (d, g, h) {
        var r = d;
        lug("%cApi: ".concat(routestring), 'color:green', d && {
          data: d.data
        });

        if (h && h.readyState === 0 && false) {
          //artifical offline trigger, do we dare?, off for now
          isOffline();
          return requeue();
        } else if (d.error || d.status === "error" || !d.status || debug && (gup('apifail') === "true" || config.apifail)) {
          lug('response looks like an error');
          return reject(d, h);
        } else {
          if (d.data && !_.isString(d.data)) {
            if (!config.ext) {
              r = d.data;
            }
          } else if (d.message) {
            r = {
              message: d.message
            };
          } else {
            r = {};
          }

          return resolve(r, d, g, h);
        }
      }, function (e, h) {
        var ejson = {};

        if (h === "abort") {
          lug('request aborted');
        } else {
          ejson = e && e.responseJSON ? e.responseJSON : null;

          if (ejson && ejson.status === "error" || ejson && ejson.error) {
            ejson.message = ejson.message || ejson.error || '';

            if (_.isString(ejson.message) && ejson.message.indexOf("A valid API key is required") > -1) {
              lug('api rejected token', guid);
              logout();
              ga('send', 'exception', {
                'exDescription': 'API rejected token on : ' + route + ' while on page ' + (router && router.lastroute),
                'exFatal': false
              });
              report(['Token Rejection', route, user.get('moleid')]);
            }


            if (ejson.data) {
              ejson.errors = ejson.data;
            }

            if (!ejson.errors) {
              ejson.errors = [];
            }

            if (ejson.errors.result && !ejson.errors.message) {
              ejson.message = ejson.errors.result;
            }
          } else {
            e.status = e.status || '5XX';

            if (debug || config.critical) {
              meerkatMsg((h === "timeout" ? "A request for ".concat(routestring, " timed out after ").concat(timeout / 1000, " seconds") : 'Unparseable ' + routestring + ' response: status:' + e.status + ', text:' + e.statusText + ', type:' + h + (ejson ? "<br>json: " + JSON.stringify(ejson) : '')) + reloadBtnHTML);
            }

            ejson = {
              status: 'error',
              message: 'Server Error: ' + e.statusText,
              errors: []
            };
            ga('send', 'exception', {
              'exDescription': "API error: ".concat(route, " ").concat(h, " ").concat(e.status),
              'exFatal': true
            });
            report(['Route Failure', "".concat(route, " ").concat(h, " ").concat(e.status), user.get('moleid')]);
          }

          lug('an api request failed ', {
            rawerror: e,
            textstatus: h,
            errors: ejson,
            route: route
          });
        }

        return reject(ejson, h);
      }).always(function (e, h, j) {
        if (config.lock) {
          setMIP(false);
        }
      });
    } else {
      return requeue();
    }
  }

  if (user.attributes.user_type_id > 1) {
    $('.main-nav').addClass('cms-login'); // $nav.find('.footer-navigation').append('&nbsp;<li><a href="/cms/login">CMS Login</a></li>');
  }

  function molephoto(data) {
    user.set('avatar', data.avatar, silent).store(); // $nav.data('view').render();

    return moleapi('/u/avatars', 'POST', data).done(function (d) {
      lug('adding avatar from string', d);
      user.set('avatar', d.avatar_url, silent).store(); // $nav.data('view').render();
    }).always(function (_) {
      return $body.find('.saveicon').remove();
    });
  }

  function moleavi(d) {
    lug('adding avatar from file upload', d);
    var newAvi = d.avatar_url;
    user.set('avatar', newAvi, silent).store();
    $body.find('.avatar-upload-container > img').attr('src', newAvi); // $nav.data('view').render();
  }
  /*create a function that can be called as a deferred function will add and resolve particular campaigns, alternatively passing through a tag*/


  function resolvecampaigns(tag) {
    return function (d, meta) {
      if (d && d.length) {
        return resolve(_.map(d, function (json) {
          return addCampByJSON(json, tag) && Campaigns.get(json.id).toJSON();
        }), meta);
      } else {
        return resolve([]);
      }
    };
  }

  function mergeevents(evobj, isUmbrella) {
    var mergedEvents = evobj.events || [],
        backfillEvents = evobj.backfill_events || [];
    var filteredBackfillEvents = [];

    if (backfillEvents && backfillEvents.length) {
      for (var i = 0; i < backfillEvents.length; i++) {
        if (!backfillEvents[i].private_event || backfillEvents[i].private_event === '0') {
          if (isUmbrella) {
            filteredBackfillEvents.push(backfillEvents[i]);
          } else {
            if (!backfillEvents[i].funding_type || !backfillEvents[i].funding_type.is_political || backfillEvents[i].funding_type.is_political === '0') {
              filteredBackfillEvents.push(backfillEvents[i]);
            }
          }
        }
      }
    }

    return mergedEvents.concat(evobj.main_events || [], filteredBackfillEvents);
  }

  function molecampaign(slug) {
    return $.when(moleapi('/campaigns/' + slug)).then(function (camp) {
      var mergedevents = [],
          prime_event,
          isUmbrella,
          donate,
          recruit;

      for (var i = 0; i < camp.actions.length; i++) {
        if (camp.suppress_donation_module !== 1) {
          if (camp.actions[i].action_type === "donate" && camp.actions[i].gau_id !== "") {
            donate = _.findWhere(camp.actions, {
              action_type: "donate"
            }); //find the donate event
          }
        }

        if (camp.actions[i].actionable_type === 'UmbrellaEvent') {
          camp.umbrella_event = camp.actions[i];
          camp.actions.splice(i, 1);
          isUmbrella = true;
        }
      }

      camp.stat_supporters = Stats.findWhere({
        id: 'supporters'
      }) ? Stats.findWhere({
        id: 'supporters'
      }).get('stat') : 2400000;

      if (camp.action_module_order.indexOf('event') > -1 && !camp.suppress_events_module) {
        return $.when(campaignevents(slug)).then(function (res) {
          mergedevents = !camp.suppress_more_events ? mergeevents(res, isUmbrella) : res.main_events;

          if (mergedevents[0]) {
            prime_event = mergedevents[0]; //get the first event off that stack;

            if (!camp.suppress_more_events && camp.umbrella_event) {
              var umbrellaEvent = camp.umbrella_event;

              if (umbrellaEvent) {
                prime_event.display_type = 'umbrella';
                prime_event.belongs_to_umbrella_event = {
                  title: umbrellaEvent.title,
                  slug: umbrellaEvent.slug,
                  id: umbrellaEvent.actionable_id,
                  description: umbrellaEvent.action_description
                };
                prime_event.display_title = umbrellaEvent.action_headline;

                for (var i = 0; i < mergedevents.length; i++) {
                  mergedevents[i].belongs_to_umbrella_event = {
                    title: umbrellaEvent.title,
                    slug: umbrellaEvent.slug,
                    id: umbrellaEvent.actionable_id,
                    description: umbrellaEvent.action_description
                  };
                }
              }
            } else {
              prime_event.display_title = prime_event.action_headline || 'Attend an Event';
            }

            prime_event.prime_ev = true;
            prime_event.events = mergedevents; //add the list to that prime event

            Actions.add(_.rest(mergedevents), {
              merge: true
            }); //add all those event actions for realz (the prime event will be added automatically by addCompJSON)

            prime_event.events.push(prime_event.events.shift()); //other events go on the stack
          }

          recruit = _.findWhere(camp.actions, {
            action_type: "recruit"
          }); //find the donate event

          camp.actions = _.reject(camp.actions, {
            action_type: "donate"
          }); //remove it

          camp.actions = _.reject(camp.actions, {
            action_type: "recruit"
          }); //remove it

          if (prime_event) {
            camp.actions.push(prime_event); //add the prime event
          }

          if (recruit) {
            //put recruit back on the end, if present
            camp.actions.push(recruit);
          }

          if (camp.suppress_donation_module !== 1) {
            if (donate) {
              //put donate back on the end, if present
              camp.actions.push(donate);
            }
          }

          return camp;
        });
      }

      return camp;
    }).then(addCampByJSON);
  }

  var molecollection = function molecollection(_) {
    return moleapi('/u/collection');
  };

  var searchevents = function searchevents(params) {
    return moleapi('/events/search', 'get', params, {
      ext: true
    }).then(function (d) {
      d.data = addCats(d.data);
      return d;
    });
  };

  var searchEventTypes = function searchEventTypes(params) {
    return moleapi('/event_types', 'get', params).then(function (d) {
      return d;
    });
  };

  var moleyourevents = function moleyourevents() {
    return moleapi('/u/registered-events').then(addCats).then(function (events) {
      return _.map(events, function (event) {
        return _.extend(event, {
          'yours': true
        });
      });
    });
  };

  var searchcamps = function searchcamps(params) {
    return moleapi('/campaigns/search', 'get', params).then(resolvecampaigns());
  };

  var campaignevents = function campaignevents(slug) {
    return moleapi("/campaigns/".concat(slug, "/events"), 'get');
  };

  var molecampaigns = function molecampaigns(_) {
    return moleapi('/campaigns').then(resolvecampaigns());
  };

  var molecampaignsnearby = function molecampaignsnearby(_) {
    return moleapi('/campaigns?nearby=1').then(resolvecampaigns());
  };

  var moleyourcampaigns = function moleyourcampaigns() {
    return moleapi('/u/campaigns').then(function (json) {
      return user.set('yourcampaigns', _.pluck(json, 'id')) && json;
    }).then(resolvecampaigns('your_campaign'));
  };
  /* convert actions into real models which campaigns reference by id or list. returns the campaign json in case we're chaining */


  function addCampByJSON(json, tag) {
    //lug('adding campaign from json',json,tag);
    // funding_type_id based on the following table:
    // 1	c3
    // 2	c4
    // 3	SCPC IE
    // 4	SCPC Coordinated
    // 5	SCVEF 527 - Voter Education
    // 6	SCIA - Federal Super PAC
    // 7	State PAC IE
    // 8	State PAC Coordinated
    if (json && json.id) {
      var funding_type_id = json.funding_type_id;
      var actions = json.actions;

      if (funding_type_id) {
        var fundingTypeIdInt = parseInt(funding_type_id);
        var isC3 = fundingTypeIdInt === 1;
        var isC4 = fundingTypeIdInt === 2;
        var isPAC = !(isC3 || isC4);

        if (isPAC) {
          json.is_official = 'pac';
        } else {
          json.is_official = '';
        }

        if (actions) {
          actions.forEach(function (action) {
            var isDonate = action.action_type === 'donate';

            if (isDonate) {
              var hasCCode = action.c_code;

              if (!hasCCode) {
                if (isC3) {
                  action.c_code = 'C3';
                } else if (isC4) {
                  action.c_code = 'C4';
                }
              }
            }
          });
        }
      }

      if (json.actions && json.actions.length) {
        if (json.action_module_order) {
          var sortmap = _.reduce(json.action_module_order, function (acc, item, i) {
            acc[item] = i;
            return acc;
          }, {});

          json.actions = json.actions.sort(function (a, b) {
            return sortmap[a.action_type] - sortmap[b.action_type];
          });
        }

        Actions.add(json.actions, {
          merge: true
        }); //add actions for real

        if (json.full) {
          json.action_ids = _.pluck(json.actions, 'id'); //only do this when we have a truly full list of all actions
        }
      }

      if (!json.image) {
        json.image = '/img/default_image_large.jpg';
      }

      json.url = domain + '/campaigns/' + json.slug;

      if (_.isString(tag)) {
        json[tag] = true;
      }

      return Campaigns.add(json, {
        merge: true
      }).toJSON(); //return the campaign as json so anything that wants to can store and use it all in one go
    }
  }
  /*do some detective work to figure out why this might have been recommended*/


  function recReason(action, campaign) {
    var reason,
        cat,
        item,
        c_taken,
        seen_reason_type = seen_reasons[action.action_type]; //console.log('getting reasons',campaign,action);

    campaign = campaign || Campaigns.get(action.campaign_id).toJSON(); //lug('action type reason?',seen_reasons,seen_reason_type,action.action_type,Actions.where({action_took:true,action_type:action.action_type}).length);
    //lug('camp cats?',campaign.categories,campaign.categories && campaign.categories.length);

    if (user.get('state') && (campaign.campaign_state_restriction && campaign.campaign_state_restriction.length && _.contains(campaign.campaign_state_restriction, user.get('state')) || action.state_restriction && action.state_restriction.length && _.contains(action.state_restriction, user.get('state')))) {
      //lug('reasons: STATE',user.get('state'));
      reason = {
        template: 'reason-state',
        state: window.usstates[user.get('state')]
      };
    } else if (Actions.where({
      action_took: true,
      action_type: action.action_type
    }).length > 3 && !seen_reasons[action.action_type]) {
      seen_reasons[action.action_type] = M.round(M.random()); //maybe clear it
      //lug('reasons: TYPE',action.action_type, seen_reason_type);

      reason = {
        template: 'reason-type',
        a_type: action.action_type,
        a_type_verb: action_verbs[action.action_type]
      };
    } else if (campaign.categories && campaign.categories.length) {
      cat = _.compact(_.map(_.pick(user.get('category_completion'), campaign.categories), function (v, k) {
        return _.isObject(v) && v.taken ? k : undefined$1;
      }));
      c_taken = cat.length;
      cat = _.shuffle(cat.length ? cat : campaign.categories)[0];
      item = _.findWhere(categories, {
        id: parseFloat(cat)
      });
      seen_reasons[action.action_type] = seen_reason_type ? M.round(M.random()) : seen_reason_type; //maybe clear it, maybe leave as is
      //lug('reasons: CATEGORY',cat, item);

      reason = item ? {
        template: 'reason-category',
        c_taken: c_taken,
        category: item
      } : undefined$1;
    }

    return reason;
  }
  /* add recs to local storage */


  function addRecs(r) {
    var recs = [];

    _.each(r.recommendations.campaigns, function (campaign) {
      if (campaign.actions && campaign.actions[0]) {
        recs.push(campaign.actions[0].id);
      }

      addCampByJSON(campaign); //we do not want to overwrite existing actions
    });

    user.set('recs', recs);
  }

  function molerecs_partial() {
    var email = user.get('email');
    var moleid = user.get('moleid');
    var request;

    if (moleid) {
      request = ['/u/recommendations_partial', 'GET', {
        moleid: moleid
      }];
    } else if (email) {
      request = ['/u/recommendations_partial', 'GET', {
        email: email
      }];
    } else {
      request = ['/recommendations_by_campaign', 'GET', {
        campaign_id: activeCampaign.id
      }];
    }

    return moleapi.apply($, request).then(addRecs);
  }

  var translateMoleLogin = translateMoleLoginByUser(user); //logout

  $.Topic('logout').subscribe(logout);
  $.Topic('revoke-fb').subscribe(revoke);
  $.Topic('clearactions').subscribe(function (e) {
    stopevent(e);
    Actions.invoke('set', {
      action_taken: false,
      action_took: false
    });
    user.set('historyactions', []).store();
    lug('clearing actions', user.get('historyactions'));
  });

  $.Topic('issue-selection').subscribe(function (e, $form, data) {
    var now = _.now(),
        catids = $form.find(':checkbox:checked').map(function () {
      return parseFloat($(this).val());
    }).get(),
        dfd = moleapi('/u/preferences', 'post', {
      category_ids: !catids.length ? [17] : catids //using 17 for "surprise me"

    }).done(function () {
      lug('DONE AND NAVIGATING'); /////THIS THIS THIS THIS

      report(['User Preferences', 'selected', catids.length + ' categories']);
    }).always(function () {
      $form.removeClass('submitting');
      user.set('preferences', true);
      router.navigate('supporter-home', {
        trigger: true
      });
    }),
        next = nextload(dfd, true).done(function () {
      //when next page finishes loading, check the time it too
      reportTime('preferences submit to start now', _.now() - now);
    });

    if (!catids.length) {
      //show surprise me if they didn't pick anything
      $form.find('label').last().click();
    }

    saveicon(next, $form); //need to stop the spinner on entre page load attempt, not just when preferences is done
  });

  function fbapi() {
    //takes actual FB api arguments
    if (!auth) {
      lug('fb api failed because no auth', auth);
      disconnectFB();
      return reject();
    }

    var dfd = $.Deferred(),
        args = _.toArray(arguments); //get the arguments, make them a real array


    lug('fbapi', args);
    dfd.then(null, function (e) {
      lug('FB api fail for', args, e);
      disconnectFB();

      if (e.error && e.error.type === "OAuthException") {
        lug('HIT FB OAUTH EXCEPTION', e);

        if (e.error.message.indexOf('expired') > -1) {
          return fbconnectrequest();
        } else {
          return e;
        }
      } else {
        return e;
      }
    });
    args.push(function (response) {
      if (!response.error) {
        dfd.resolve(response);
      } else {
        dfd.reject(response);
      }
    });
    fb_init.done(function () {
      FB.api.apply(null, args);
    });
    return dfd;
  }

  var appFriends = function appFriends(_) {
    return fbapi('/v2.2/me/friends');
  };

  var appFriendIds = function appFriendIds(_) {
    return appFriends().then(function (r) {
      return _.pluck(r.data, 'id');
    });
  }; // const randFriends = ops => bsdts.getTaggableFriends(ops);


  function getFBPicture(width) {
    width = width || 450;
    fbapi('/me/picture', {
      return_ssl_resources: true,
      width: width,
      height: width,
      redirect: false
    }).done(function (r) {
      user.set({
        avatar: r.data.url
      });
    });
  }

  function getPerms() {
    return fbapi('/me/permissions').then(function (r) {
      if (r && r.data && $.isArray(r.data) && r.data.length) {
        var perms = r.data[0].permission ? _.compact(_.map(r.data, function (p) {
          return p.status === 'granted' && p.permission; //v2.0
        })) : _.keys(r.data[0]); //v1.0 version

        window.FB.perms = perms;
        user.set('fbperms', perms.join(','));
        return perms; //perms returned are an array
      } else {
        return reject(r);
      }
    });
  }

  function checkPerms(need) {
    //lug('permcheck',{need:need,have:user.get('fbperms')});
    var missing,
        have = user.get('fbperms').split(',');
    need = _.uniq(_.isString(need) ? need.split(',') : []);
    missing = _.difference(need, have); //lug('perms missing',missing,'from the required',need);

    if (missing.length) {
      return missing.join(',');
    } else {
      return false;
    }
  }

  function fbconnectrequest(e, $el, data) {
    $.Topic('request-fbconnect').unsubscribe(fbconnectrequest); //prevent multiple clicks while one is in progress

    function restoreLogin() {
      $body.removeClass('requesting-fb-connect');
      $.Topic('request-fbconnect').subscribe(fbconnectrequest); //restore clickability
    }

    var scope = data && data.addperms ? {
      scope: checkPerms(permissionscope.scope + (permissionscope.scope ? ',' : '') + data.addperms)
    } : permissionscope,
        connectreq = $.Deferred();

    if (user.get('fbloggedin') || !user.get('email')) {
      $.extend(scope, {
        auth_type: 'rerequest'
      });
    }

    lug('fb login request made with permcheck scope ', scope, 'from data', [data], 'user not connected? ', fbnoauth);
    lug('FB state when clicked', fb_init.state());
    report(['Accounts', 'Requested Facebook Connect', 'while FB all.js load was ' + fb_init.state()]);
    connectreq.done(function () {
      /*if the connect is new, then it IS an interaction, as they accepted permissions*/
      report(['Accounts', 'Facebook Connect', fbnoauth ? 'New' : 'Existing', {
        'nonInteraction': fbnoauth ? 0 : 1
      }]);
      fbnoauth = false; //the user has auth now, so we no longer can prove they didn't before next time within this session (or ever really)
    }).fail(function (explicit) {
      user.set('fbloggedin', false);
      report(['Accounts', 'Facebook Connect Failed', {
        'nonInteraction': explicit ? 0 : 1
      }]);
    }).always(restoreLogin).progress(restoreLogin);

    $body.addClass('requesting-fb-connect');
    fb_init.done(function () {
      //wait for FB to actually be ready
      lug('initiating CONNECT');

      if (!user.get('fbloggedin') || checkPerms(scope.scope)) {
        var authopen = $.wait(120000).done(function () {
          report(['Accounts', 'FB Auth/Login Not Completed Within 2min', {
            'nonInteraction': 1
          }]);
          connectreq.notify();
        }),
            state = _.uniqueId('l_') + _.now();
        /* temp switch to include the horribly broken iOS8*/


        if (directlogin) {
          lug('redirectURI', domain + '/' + router.lastroute);
          window.location = 'https://www.facebook.com/dialog/oauth?client_id=203478199839346&redirect_uri=' + domain + '/' + (router.lastroute || 'supporter-home') + (scope && scope.scope ? '&scope=' + scope.scope : '') + '&state=' + state;
          setcookie('fbstate', state);
        } else {
          FB.login(function (response) {
            lug('fb login response', response, 'auth', auth);
            auth = response;
            authopen.reject(); //user did something explicitly so no need for timing info

            if (response.authResponse && response.status === 'connected') {
              //note that the heavy lifting here is handled by the event listener set up in fbAsyncInit, this is only handling the explict request to log in. It has to be separate from that, though, because logins to mole don't match up with FB auth status connect events
              fbConnected(); //we know the user has fbconnected: redundant with the auth thing though, odd? Maybe this is to fire off other events early?

              $.when(fbReg(data.post_action), fbSetup()).done(connectreq.resolve).fail(function () {
                connectreq.reject(); //avoid sending an argument here
              });
            } else {
              lug('fb auth in login process response was bad');
              disconnectFB(); //oddly, we should see "not authorized" here when we get loginstatus, showing that they logged in but refused permissions, but won't unless you refresh the page!

              connectreq.reject(true); //they probably didn't like the permissions, or forgot pwd
            }
          }, scope);
        }
      } else {
        /*seems like the user is already connected?  if there's no other attempt in the pipeline, try again? */
        lug('already connected');
        fbSetup();
        fbReg(data.post_action).done(connectreq.resolve).fail(connectreq.reject);
      }
    });
    return connectreq;
  } //when there is a request to connect to the app, start the connect process


  $.Topic('request-fbconnect').subscribe(fbconnectrequest);

  function fbSetup() {
    /*might as well get stuff we'll need for target share now*/
    // if(user.get('randfb_friends').length===0){
    //     randFriends(6000).done(function(friends){
    //         //lug('gotfriends');
    //         user.set('randfb_friends',friends);
    //     });
    // }
    return getPerms(); //have to know what perms we have
  }

  function fbReg(post_action) {
    lug('using fb login get data we need to log into mole');
    $.Deferred();
    modaldfd.notify(); //close the modal without bias

    return fbapi('/me/?fields=email,location,first_name,last_name,gender').then(function (r) {
      if (!r.email) {
        return reject({
          error: 'Email is required'
        });
      } //if cities match, accept zip automatically
      //this should probably only happen for country US though...


      var city = r.location && r.location.name.split(', ')[0] || null,
          state = r.location && _.invert(window.usstates)[r.location.name.split(', ')[1]] || null,
          data = _.extend({
        first_name: r.first_name,
        last_name: r.last_name,
        gender: r.gender,
        email: r.email
      }, city && state ? {
        city: city,
        state: state
      } : {});

      lug('got lots of fb data', data);

      if (!data.gender) {
        delete data.gender;
      }

      user.set(data, silent).store();
      delete data.activated;
      data.oauth_uid = auth.authResponse.userID;
      data.access_token = auth.authResponse.accessToken; ////lug('trying to register with mole');

      return moleapi('/register/fb', 'post', data, {
        critical: true
      }).then(function (d) {
        login(d, false, false, true, post_action); //data, allow nagging, create new ga session, do not fire another login event, go to post_login destination

        return resolve();
      });
    }).fail(function (e) {
      if (e && e.error === "Email is required") {
        lug('adding fb email fail msgs');
        $.wait(1000).done(function () {
          $body.find('.fb-button').addClass('fb-btn-email-msg');
        });
      }

      lug('FBregfail', e);
    });
  }

  function fbConnected() {
    user.set({
      fbloggedin: true,
      fbconnected: true
    }).store(); //we know the user has fbconnected
  }

  function disconnectFB() {
    lug('disconnecting with fb');
    fbnoauth = true;
    lug('user not previously connected to FB? ', fbnoauth);
    user.set({
      fbconnected: false,
      fbperms: ''
    }).store();
    $body.removeClass('fb-app-connected');
  }

  function fbStatus(response) {
    //lug('authchange ',response);
    if (response && response.status) {
      lug('new FB auth response detected', response);

      if (response.status === 'connected') {
        auth = response;
        lug('setting fb auth to', auth);
        user.set({
          fbconnected: true
        }); //we know the user has fbconnected

        /*
        if(user.get('fbloggedin')){
            user.trigger('change:fbloggedin');//to trigger ts crawling?
        }
        */

        $html.addClass('fb-app-connected');
        getPerms().then(function () {
          fb_connect.resolve(response); //this is a one time thing, only useful for restoring connection
        }); // randFriends(6000).done(function(friends){
        //     lug('gotfriends');
        //     user.set('randfb_friends',friends);
        // });
      } else {
        disconnectFB();
        fb_connect.reject(); //so that any attempt to login knows that the attempt failed

        /* we could tell if the user is logged into facebook or not here... maybe
        if(response.status === 'not_authorized'){
         }else{
         }
        */
      }

      fb_init.resolve();
    }
  }
  /*dealding with async fb inits*/


  if (w.fbqueue.s.length) {
    fbStatus(w.fbqueue.s.pop()); //if there are any saved resposnes, start with the latest
  }

  w.fbqueue.sfunc = fbStatus; //then pipe any future responses into our new function

  if (w.fbqueue.i) {
    fb_init.resolve();
  }

  w.fbqueue.ifunc = fb_init.resolve; //TEST ONLY

  fb_init.done(function () {
    $body.toggleClass('nofb fb-init');
    reportTime('FB all.js ready', _.now() - gnow);
    lug('user REPORTING fblib is ready, took ' + (_.now() - gnow) / 1000 + ' seconds');
  });
  $.Topic('check-post-perms').subscribe(function (e, $el, data) {
    var need = permissionscope.scope + (data.addperms ? (permissionscope.scope ? ',' : '') + data.addperms : ''),
        perms = checkPerms(need);

    if (perms) {
      stopevent(e);
      $.Topic('request-fbconnect').publish(e, $el, data);
    }
  });

  function formSubmit(e, $form, dfd) {
    stopevent(e);
    var submittingClassString = 'submitting',
        data = $form.data(),
        $template = $form.closest('[data-template],.modal-content').addClass(submittingClassString),
        view = $template.data('view') || {},
        viewdfd = view.dfd; //wrong dfd

    lug({
      template: $template,
      view: view,
      'viewdfd': viewdfd
    });
    lug('FORM  ', $form, 'is submitting', !$form.hasClass('submitting'), dfd ? dfd.state() : 'no dfd');

    if (!$form.hasClass(submittingClassString)) {
      $form.addClass(submittingClassString);
      $.Topic(data.formaction).publish(e, $form, $form.serializeObject(), dfd || viewdfd);
    }
  }

  $.Topic('unsubscribe').subscribe(function (e, $form, data) {
    var submittingClass = 'submitting',
        $section = $form.closest('section'),
        internal = $form.data('internal'),
        dfd = moleapi('/unsubscribe', 'post', data).done(function (d) {
      if (!internal) {
        user.set(translateMoleLogin(d), silent); //don't do this for internal users
      }

      $section.addClass('unsub-done');

      if (d && d.profile && !d.profile.email_opt_in_campaign_updates && !d.profile.email_opt_in_user_recommendations && !d.profile.email_opt_in_fb_friend_recommendations) {
        $section.find('.unsub-success .bubble-pale').find('.h2').html('Successfully Unsubscribed').after((!internal ? '<p>You\'ve been' : data.email + ' has been') + ' removed from all AddUp email communications lists.');
      } else if (!internal) {
        $section.find('.unsub-success .bubble-pale').find('.h2').after('<p>Thank you for choosing to receive our email communications, with your support we\'ll be closer to a better, fairer, greener world.');
      }
    }).fail(function (e) {
      markErrors($form, e);
    }).always($form.use('removeClass', submittingClass));
    saveicon(dfd, $form); //$form.addClass(submittingClass);
  }); //attaches behavior to a dfd that either fires immediately on fail or waits till the next pageload to resolve
  //false, false = start behavior immediately, not after another dfd, second matches page next cleared or page loaded

  function nextload(dfd, end) {
    //
    var innerdfd = $.Deferred();
    dfd = dfd || resolve(); //either wait for a particular dfd to resolve, or fire the setup/attach behavior right now and start waiting

    dfd.done(function () {
      //only trigger behavior when this dfd/request is completed. If you didn't care whether it completed or not, you should have used false as the 1st argument
      lug('nextload attaching');

      function toggle(loaded) {
        //false for unloaded, true for new page loaded
        if (loaded === end) {
          //new page loaded and we wanted that, or old page unloaded and we wanted that
          lug('nextload firing');
          innerdfd.resolve(); //fire done on the events tied to load

          $.Topic('pageloaded').unsubscribe(toggle); //stop listening, we're done
        }
      }

      $.Topic('pageloaded').subscribe(toggle);
    }).fail(function () {
      innerdfd.reject();
    });
    return innerdfd;
  } // moleapi(route,method,data){


  function moleSubmit(e, $form, dfd) {
    var data = $form.serializeObject(),
        $template = $form.closest('[data-template],.modal-content,.form-anchor').addClass('submitting'),
        view = $template.data('view') || {},
        fdata = $form.data(),
        viewdfd = view.dfd,
        //
    vcb = fdata.vcb && view[fdata.vcb] || $.noop,
        //when a view has registered a specific deferred
    tmpsilent = fdata.silent === true ? silent : {},
        action = $form.attr('action'),
        method = fdata.method || $form.attr('method') || 'POST',
        $button = $form.find('button').prop('disabled', true),
        $stack = $form.children('ul').eq(0),
        submit; //lug('formdata',fdata,view);

    if (fdata.vtransform && view[fdata.vtransform]) {
      data = view[fdata.vtransform](data);
    }

    submit = moleapi(action, method, data);
    saveicon(submit, $template, fdata.busytemplate ? $template : $form);
    lug('ADDUP form submit', $form.serializeObject(), ' to ', action, {
      view: view,
      viewdfd: viewdfd,
      is_silent: tmpsilent
    });
    submit.always(function (d) {
      if (document.activeElement) {
        $(document.activeElement).blur(); //don't allow focus on same element as pre-submit
      }

      $template.removeClass('submitting');
      $form.removeClass('submitting');
      $button.prop('disabled', false);

      if (viewdfd) {
        //viewdfd.notify({args:arguments});//i don't THINK this does anything useful, and it closes the modal!
        viewdfd.always($template.use('removeClass', 'success-state'));
      }
    }).done(vcb, function (d) {
      //console.log('submitted');
      lug('addup form response', d);
      $form.addClass('inactive').find('.value-updated').removeClass('value-updated').closest('li').addClass('inactive-field');
      clearErrors($form);

      if (d) {
        user.set(translateMoleLogin(d), tmpsilent);

        if (tmpsilent.silent) {
          user.store();
        }
      }

      $template.trigger('formsuccess', [submit, data]); //dfd and original data

      if (fdata.message) {
        if (fdata.message === "hard") {
          $stack.empty();
        }

        $stack.find('li.success-message').orPrepend('<li class="success-message"/>').html(d.message);
        $template.addClass('success-state');
      }

      if (fdata.formsuccessclass) {
        $form.closest('section').addClass(fdata.formsuccessclass);
      }

      if ($form.hasClass('reset-when-done')) {
        $form.get(0).reset();
      }

      if (fdata.success) {
        gaEventSetter("Campaign Action", "Take Action/Sign Petition", "".concat(window.hostname, "/").concat(window.location.pathname).concat(window.location.search));
        $.Topic($form.data().success).publish(d.message);
      }
    }).fail(function (e) {
      lug('addup form error response', e);
      $template.trigger('formerror', [e, submit]).addClass('failmsg');
      markErrors($template.find('form'), e); //can't use original reference because it might have been overwritten by the above

      gaEventSetter("Form Interaction", "Form Error", "".concat(e.message));
    });
    return submit;
  }

  function clearErrors($form) {
    var $stack = $form.children('ul').or('children', 'ol').eq(0);
    $stack.find('li.error-message').remove().end().find('.error').removeClass('error').each(function () {
      $(this).find('label').removeAttr('data-tooltip title');
    });
  }

  function markErrors($form, e, clear) {
    var $stack = $form.children('ul').or('children', 'ol').eq(0),
        notfound = [];
    clearErrors($form);
    lug('form marker sees:', e);

    if (!isVisible($stack)) {
      scrollorJumpTo($stack.offset().top - 20);
    }

    if (e.errors && !_.isString(e.errors) && !_.isEmpty(e.errors)) {
      _.each(e.errors, function (v, k) {
        lug('error', k, v);
        var $field = $form.find('[name="' + k + '"]'),
            fdata = $field.data();

        if ($field.length) {
          $field.closest('li').addClass('error').find('label').attr('data-tooltip', v).attr('title', v);

          if (clear) {
            $field.val('');

            if (fdata.selectize) {
              fdata.selectize.clear();
            }
          }
        } else {
          notfound.push(k + ' is ' + v);
        }
      });

      if ($form.data('listerrors') && !e.msgonly) {
        if (e.message) {
          e.message += '<br>';
        }

        e.message += _.map(e.errors, function (err, k) {
          if (k === "contacts") {
            renderContacts($form, err);
          } else {
            return _.isArray(err) ? err.join('<br>') : err;
          }
        }).join('<br>');
      } else if (notfound.length && notfound.indexOf('recipient') === -1 && !$form.hasClass('donate-form')) {
        e.message += ': ' + notfound.join(', ');
      }

      if (e.errors.password) {
        e.message += "<br>Enter a password at least 8 characters in length, containing at least one number and one letter.";
      }
    }

    $stack.find('li.error-message').orPrepend('<li class="error-message"/>').html(e.message);
    $form.find('.error').children('input').eq(0).focus().select();
  }

  window.markes = markErrors;

  var getView = function getView($node) {
    return $node.length ? $node.data().view : {};
  };

  var getAM = function getAM($node) {
    return getView($node).actionmodel || {};
  };

  function renderContacts($form, contacts) {
    lug(contacts);
    var $extrafields = $form.find('li.contacts-table').orAdd('<li class="contacts-table"/>').html(Handlebars.partials.disambiguation({
      contacts: contacts
    }));
    $form.find('li').eq(0).before($extrafields);
  }

  function clearNav() {
    $nav.removeClass('mobile-nav-menu-expanded').children('ul').removeClass().find('.nav-item a').removeClass('dropdown-active');
  }

  function scrollorJumpTo(offset, speed) {
    if ($w.width() > 767) {
      $('html, body').finish().animate({
        scrollTop: offset
      }, speed || 250);
    } else {
      $('html, body').scrollTop(offset);
    }
  }

  if (window.ios || Mod.touchevents) {
    $body.on('click', '.email-dropdown', function (e) {
      $(this).toggleClass('hover');
    });
  }

  function validateEmail(string) {
    var $test = $('<input type="email">').val(string);
    return $test.val() && $test.get(0).validity && $test.get(0).validity.valid || emregex.test(string);
  }

  function checkValidEmails(emailListString) {
    return _.chain(($.trim(emailListString) || '').split(/[,;]+/)).map(function (email) {
      return $.trim(email);
    }).compact().partition(validateEmail).value();
  }

  function matchreqs(array) {
    lug('checking', array, _.pick(user.attributes, array));

    var need = _.filter(array, function (v, i, l) {
      lug(v, !!v);
      return !user.get(v);
    });

    lug(need);
    return need.length;
  }
  /*all views get these bits of extra data*/


  function standardData(view) {
    var _user$attributes = user.attributes,
        moleid = _user$attributes.moleid,
        loggedin = _user$attributes.loggedin,
        fbloggedin = _user$attributes.fbloggedin,
        fbconnected = _user$attributes.fbconnected,
        randfb_friends = _user$attributes.randfb_friends;
    var template = view.template,
        t_id = view.t_id,
        series = view.series,
        last = view.last,
        pagetitle = view.pagetitle;
    return {
      u_id: moleid,
      t_seed: seed.config,
      t_pagetitle: pagetitle,
      t_ww: getWidth(),
      t_debug: debug,
      d_nomobos: !w.mobos,
      t_android: is_android,
      t_iOS: is_iOS,
      t_filer: Mod.filereader,
      t_retina: retina,
      //boolean for retina display or not
      t_template: template,
      //add some extra data to the model about the template
      t_id: t_id,
      //add an id value for use in prefixes, useful with form labels
      t_index: series,
      //if we're in a series, nice to know
      t_last: last,
      //if we're last in a series, nice to know
      t_token: auth,
      t_sources: tmpsrc,
      t_categories: categories,
      t_lastaction: Actions.lastaction,
      domain: domain,
      t_prevpaged: router.prevpaged,
      t_user: user.attributes,
      t_api_token: guid,
      t_loggedin: loggedin,
      t_loggedout: !loggedin,
      t_fbloggedin: fbloggedin,
      t_fbconnected: fbconnected,
      r_friends: randfb_friends
    };
  }

  function formatListener(watch, type) {
    watch = watch || '';
    return watch ? watch.replace(new RegExp(watch.split(':')[0], "gi"), type).replace(/,/g, sp) : type;
  }

  SimpleView = function SimpleView(options, extras) {
    Backbone.View.call(this, options, extras); //?
  };

  _.extend(SimpleView.prototype, Backbone.View.prototype, {
    _rendered: false,
    initialize: function initialize(options, extras) {
      //lug({template:extras.template,options:options,extras:extras});
      var self = this;
      $.extend(this, extras || {}); //add all the template specific stuffs

      this.t_id = uniqueid++; //unique id for this template, useful for #ids

      this.tpml = SC[this.template]; //define a template

      this.firstload = $.Deferred();
      this.loading = $.Callbacks();
      this.loaded = $.Callbacks();
      this.source = this.altsource || this.source;
      this.removed = this.dfd || $.Deferred(); //either the view was created with a specific dfd, or we create one

      if (this.watch) {
        if (this.listen && !this.model.attributes) {
          this.model = models[this.listen.split(':')[0]];
        }

        this.listenTo(this.model, formatListener(this.listen, 'change'), this.render);
        this.listenTo(this.model, 'error:' + this.template, this.renderError);
      }

      if (this.actionmodel) {
        //if this is an actionmodel, listen to the action as well as the above
        this.actionmodel = Actions.get(this.actionmodel);
        this.listenTo(this.actionmodel, 'change', this.render);
        this.listenTo(this.actionmodel, 'trigger', this.trigger);

        if (!this.extradata) {
          this.extradata = {};
        }
      }

      this.$el.data('view', this);

      if (this.plugins && this.plugins.length) {
        _.each(this.plugins, function (f) {
          if (_.isFunction(f)) {
            f(self);
          }
        });
      }

      this.render();
    },
    trigger: function trigger(e) {
      if (e) {
        this.$el.trigger(e);
      }
    },
    render: function render(e, extradata) {
      var outerextra = this.extradata || {};
      this.loading.fire(); //NOTE findData is causing redudant method calls to datasources.campaign at least

      findData(this.source || this.model.attributes, this.id, this).done($.proxy(function (d) {
        if (this.mutatedata) {
          d = this.mutatedata(d);
        }

        var finaldata = $.extend(standardData(this), //standard attribute addition
        d, outerextra, extradata || {}); // temporary exclude rewriting donate html when an action is taken and actBlue test is running

        var actBlue = document.getElementById('actBlue');

        if (!actBlue || actBlue && this.$el[0].id !== 'donate') {
          this.$el.html(this.tpml(finaldata)); //render using the view's template with the returned data

          this.firstload.resolve(finaldata); //flag this view having rendered at least once

          this.loaded.fire(finaldata);
          $.Topic('viewrender').publish(this.$el); //alert any global listeners that care about
        }
      }, this)).fail(this.firstload.reject, $.proxy(function (d) {
        if (this.removeonfail) {
          //delete the entire view template if its data route fails
          lug('view failed, removing', this);
          this.remove();
          this.$el.remove();
        }
      }, this));
      return this;
    },
    renderError: function renderError(e, r, t) {
      lug('rendering error', e, r, t, arguments);
      this.render(undefined$1, e);
    },
    remove: function remove() {
      this.removed.resolve();
      this.loading.empty();
      this.loaded.empty();
      this.$el.empty();
      this.undelegateEvents();
      this.stopListening();
      currentViews = _.without(currentViews, this);
      return this;
    }
  });

  SimpleView.extend = Backbone.View.extend;

  function sv_init(t, o, e) {
    SimpleView.prototype.initialize.call(t, o, e);
  }
  /*create a new view on element $el with override options options  Otherwise, the function will attempt to read configuration options out of the DOM */


  function view($el, options) {
    var data = $el.data(),
        ops = $.extend(true, //deep copy
    {
      'model': user,
      'template': 'registration-modal',
      'watch': true
    }, //defaults
    data, //data elements attached to the template
    options || {}); //extra options if called directly

    if (data) {
      if (data.mixins && mixins && mixins[data.mixins]) {
        return new mixins[data.mixins]({
          el: $el
        }, ops);
      } else {
        return new SimpleView({
          el: $el
        }, ops); //return a new view
      }
    }
  }

  function setmodalmode() {
    if (oldie || getWidth() >= tablet && getHeight() < $modal.children().eq(0).height() + 100) {
      $modalouter.addClass('abs').css({
        top: $w.scrollTop() + 60 + 'px'
      });
    } else {
      $modalouter.removeClass('abs').removeAttr('style');
    }
  }
  /*creates a modal window as a deferred object onto which we can chain additional behavior. */


  function modal(uncloseable) {
    var startpos = $(window).scrollTop(),
        view; //overlay and reveal modal
    //lug('modal',uncloseable);

    $modalouter.css('visibility', 'hidden');
    $.wait(20).done(function () {
      $body.removeClass('modal-off').addClass('modal-on'); //lug('modal opened');

      $modal.trigger('modal-open'); //lug('modal',getWidth(),tablet, getHeight(), $modal.children().eq(0).height());

      setmodalmode();
      $modalouter.css('visibility', 'visible');
      $w.on('resize.modal', setmodalmode);
    });

    if (getWidth() < 768) {
      $.wait(5).done(function () {
        $w.scrollTop(0);
      });
    }

    function final_close() {
      $body.removeClass('modal-on action-modal-on share-modal-on');
      $w.off('resize.modal');
      $modalouter.removeClass('abs').removeAttr('style');
      $modal.removeClass('successmsg failmsg').trigger('modal-close');

      if (getWidth() < tablet) {
        $w.scrollTop(startpos);
      }

      view = $modal.data().view;

      if (view && view.remove) {
        view.remove();
      }

      $w.trigger('resize', true); //lug('final close of modal');
    }

    function closeModal(fast) {
      //lug('modal was closed, fast:',fast);
      $body.off('.modal').addClass('modal-off');

      if (fast) {
        final_close();
      } else {
        $.wait(300).done(final_close);
      }
    } //create a deferred that always ends with the modal closing. Notify can be used to close it without bias


    modaldfd = $.Deferred().always(_.partial(closeModal, false)).progress(_.partial(closeModal, true));
    /*close on modal dismiss*/

    $body.on('click.modal', '.modal-bg-overlay, .modal-close', function (e) {
      //lug('clicked model close action, but is it locked? ', moleinprogress);
      if ($(this).hasClass('modal-close-only')) {
        stopevent(e);
      }

      if (!moleinprogress && !uncloseable) {
        modaldfd.reject();
      }
    });
    return modaldfd;
  }

  function modalView(options) {
    //special option: set flags
    //use {validate:true} on sets or not
    //{wait: true} confirm with server before updating
    //{silent: true} not relevant?
    options = options || {};

    _.defaults(options, {
      'model': user,
      'template': 'registration-modal',
      'watch': true
    }); //lug('modal defaults',options);


    var dfd = modal(options.uncloseable);
        //create a dfd object that is also a modal
    view($modal, $.extend(options, {
      dfd: dfd
    }));
    $modal.attr('data-template', options.template); //so it's cssy

    $modalouter.attr('data-type', options.template); //so it's cssy

    return dfd;
  }

  window.modal = modal;

  function modalAction(which_action, context) {
    var template = Handlebars.partials['single-action'](Actions.get(which_action).toJSON()),
        oldview = getView($('.modal-content').find('[data-template]')); //lug(oldview,'oldview');

    if (oldview.remove) {
      //lug('removed old view');
      oldview.remove();
    }

    $body.addClass('action-modal-on');
    currentViews.push(view($modal.html(template).find(dataTemplateSelector), context || {}));
    $modal.find('.action-section').addClass('open-section').removeClass('closed-section');
    modal();
  }

  window.modalAction = modalAction;

  function modalShare(data) {
    //lug(data);
    var cat = data.category;

    if (cat) {
      data.categoryName = (_.find(categories, {
        slug: cat
      }) || categories[0]).name; //sigh
    }

    data.share_url = domain + (cat ? '/campaigns/' + cat : '/campaigns/' + data.campaign + (data.actionType ? '/' + data.actionType + '/' + data.actionSlug : ''));
    $modal.html(Handlebars.partials['share-modal'](data));
    $body.addClass('share-modal-on');
    modal();
  }

  window.modalShare = modalShare;

  function nextAction(offset, $target) {
    $target = $target || $body.find('#single-action');
    var getAction = Actions.bestOne(offset),
        icon = saveicon($target);
    return getAction.then(function (action) {
      var $newaction,
          left = user.get('recs').length > 1;

      if (action && $target.length) {
        $newaction = $(Handlebars.partials['recommended-action']({
          actions: [action],
          actions_left: left
        }));
        $target.append($newaction);
        currentViews.push(view($newaction.find(dataTemplateSelector).eq(0), {
          single_action: true
        }));
      }

      if (!left) {
        $target.find('.try-another').hide();
      }
    }).always(icon.notify);
  }

  function takeAction(view, data, dfd, ops) {
    //if data is undefined, just hit the route, if an object
    lug('taking action');
    ops = ops || {};
    var action = view.actionmodel,
        id = parseInt(action.get('id'), 10),
        plusnum = data && data.num ? data.num : 1,
        wasoffline = !online,
        atype = action.get('action_type'),
        atitle = action.get('title'),
        $section = view.$el.closest('.action-section'),
        garep = [atype, atitle],
        formcaches = [],
        $tryanotherBtn = $body.find('.try-another'),
        haln = user.get('historyactions').length + (ops.external ? 0 : 1),
        prompt = !user.get('loggedin') && (haln === seed.initial_signup_prompt || haln === seed.secondary_signup_prompt);
    $tryanotherBtn.css({
      visibility: 'hidden'
    });

    function doaction() {
      Actions.lastaction = id;
      action.set({
        action_taken: true,
        action_took: true,
        //signup_prompt: prompt,
        current_amount: action.get('current_amount') + plusnum
      });
      lug('did actions, prompt?', prompt);
    }

    $.wait(1).done(function () {
      //next tick
      if ($section.length) {
        scrollorJumpTo($section.offset().top - 30);
      }
    });

    if (ops.immediate && !wasoffline) {
      doaction(); //claim the action is done before we even hear from the backend
    }

    if (_.isUndefined(data) || data) {
      data = _.isObject(data) ? data : {};
      lug('reporting actions', data);
      dfd = dfd ? dfd : moleapi('/u/actions/' + id, 'POST', data); //dfd from petition submission serving as proxy for taking an action, or create it directly here

      if (wasoffline) {
        saveicon(dfd, $body);
      }

      dfd.done(function (d) {
        formcaches = _.map(getCurrentActionViews('listen', atype), function (view) {
          return saveForms(view.$el); //save any form data that was already there except the one we're on
        });

        if (!ops.immediate || wasoffline) {
          doaction(); //now we can actually claim the action was done
        }

        if (d) {
          var moleid = user.get('moleid');

          if (!moleid && d.profile) {
            user.set('moleid', d.profile.user_id).store();
          }
        }
        /*now that we have real data, update the recs and trigger the other elements that signify that things worked*/


        user.set($.extend(!ops.external && _.isObject(d) ? translateMoleLogin(d) : {}, {
          recs: _.without(user.get('recs'), id)
        })).trigger('change:historyactions change:category_completion change:recs').store(); //these need to update manually, sadly

        actionViewRefresh();

        _.invoke(formcaches, 'resolve'); //restore any form data that was already there


        $section.addClass('action-complete open-section'); //gray out the action

        $.wait(1).done(function () {
          if ($section.length) {
            scrollorJumpTo($section.offset().top - 30);
          }
        });
        /**/

        if (!user.get('loggedin') && (haln === seed.initial_signup_prompt || haln === seed.secondary_signup_prompt)) {
          if (d.registered) ; else {
            $.Topic('registration-modal').publish({
              is_registered: d.registered,
              altintro: 'We&rsquo;ll show you the progress you&rsquo;re making as you continue taking action to protect the environment.',
              post_action: 'supporter-home/' + action.get('campaign_id')
            }, undefined$1, d.milestone ? function () {
              giveNotice(d.milestone); //show the milestone after the window is closed
            } : undefined$1); //                        window.optimizely.push(["activate", 2787471629]);
          }
        } else if (d.milestone) {
          lug('milestone triggered');
          giveNotice(d.milestone);
        }

        if (data.num) {
          garep.push(data.num);
        }

        report(['Action Completed'].concat(garep));
        fireFBPixelOnce('6027839267591'); //1st action/reg

        fireTWPixelOnce('l5tfb');
      }).fail(function (e) {
        report(['Action Failed'].concat(garep));
      }).always($tryanotherBtn.use('css', {
        visibility: 'visible'
      }));
    }

    return dfd;
  }

  function selectizeInputs(context) {
    context.$el.find('select').not('.email-select').not('.noselectize').each(function () {
      //lug(view.selectconfig);
      if (view.addOptions) {
        view.selectconfig.options = view.addOptions();
      }
      /*initialize with basic options + any view-specific ones*/


      $(this).selectize(_.extend({
        openOnFocus: true,
        onChange: function onChange() {
          this.$control.addClass('value-updated');
        },
        onDropdownOpen: function onDropdownOpen() {
          if (getWidth() < 480 && Mod.touchevents) {
            //selectizes bring up the keyboard, so we want to move to bring the options into view
            w.scrollTo(0, 1); //try to make sure we're going down, to avoid mobile url bars

            w.scrollTo(0, this.$input.closest('fieldset,.row,li').offset().top - 40);
          }
        }
      }, view.selectconfig || {}));
    });
  }
  /*adds chosen to the view */


  function addSelectize(view) {
    plugin(view, function () {
      selectizeInputs(view);
    }, function () {
      view.$el.find('select').each(function () {
        if (this.selectize && this.selectize.destroy) {
          this.selectize.destroy();
        }
      });
    });
  }

  function addEmail(view) {
    var maxitems = 50,
        $chooser = $();
        $();
        var tmpemail,
        am = view.actionmodel,
        selectize;

    function chooseContactsHTML(contacts) {
      //lug('showing contacts',contacts);
      return Handlebars.partials['choose-contacts']({
        contacts: contacts
      });
    }

    function checkFull() {
      if (selectize && $chooser.length) {
        if (selectize.getValue().length === maxitems) {
          $chooser.addClass('list-full');
        } else {
          $chooser.removeClass('list-full');
        }
      }
    }

    function handleContacts(contacts, source, owner) {
      //lug('correct one?',window.csam!==am);
      if (window.csam !== am) {
        return false;
      }

      view.$el.find('.email-contacts');

      view.sendemail = function () {
        //lug('EMAIL SEND DONE');
        if (!user.get('email') && tmpemail) {
          user.set('email', tmpemail).store();
        }

        view.listenTo(user, formatListener(view.listen, 'change'));
        view.listenTo(view.tsmodel, 'change'); //start listening to stuff again

        view.tempSetAction(); //temporarily bump the front-end count
      };

      view.convertemails = function (serializedObject) {
        if (_.isString(serializedObject.recipients)) {
          serializedObject.recipients = checkValidEmails(serializedObject.recipients)[0];
        }

        return serializedObject;
      };

      view.stopListening(view.tsmodel, 'change'); //prevent ts module from messing stuff up

      view.stopListening(user, formatListener(view.listen, 'change')); //prevent user module from messing stuff up, oy

      if (owner && _.isObject(owner)) {
        //lug('owner',owner,source);
        if (owner.email && owner.email.length && owner.email[0].address) {
          owner = {
            'owner-email': owner.email[0].address,
            'owner-name': owner.first_name + sp + owner.last_name
          };
          tmpemail = owner['owner-email'];
        }
      } //lug('did owner');


      if (contacts && contacts.length) {
        am.set($.extend({
          'email-step': true,
          'gotcontacts': true
        }, owner));
        view.$el.find('.email-contacts').html(chooseContactsHTML(contacts));
        $chooser = view.$el.find('.contact-chooser');
        pickContacts(contacts);
        selectize = view.$el.find('select.email-select')[0].selectize;
        view.$el.on('change', '.contact-chooser [type="checkbox"]', function (e) {
          var $el = $(this),
              em = $el.attr('value');

          if ($el.is(':checked')) {
            selectize.addItem(em);
          } else {
            selectize.removeItem(em);
          }

          checkFull();
        }).on('click', '.next-button', function (e) {
          stopevent(e);
          view.$el.find('.pick-only').removeClass('pick-only');
        });
      } else {
        am.set('email-step', true);
        view.$el.find('.pick-only').removeClass('pick-only');
      }

      view.$el.on('submit', function () {
        var val = $(this).find('[name="sharer_email"]').val();

        if (val) {
          tmpemail = val; //prepare to store the user's email even if they are anon
        }
      });
    }

    function unpick(em, which) {
      var $ck;

      if ($chooser.length) {
        $ck = view.$el.find('.contact-chooser').find('input').filter('[value="' + em + '"]').eq(0);
        $ck.prop('checked', !!which);
      }

      checkFull();
    }

    function pick(em) {
      unpick(em, true);
    }

    function pickContacts(contacts, preSel) {
      //lug('setting up email selectize');
      var sel = view.$el.find('select.email-select').selectize({
        options: contacts,
        plugins: ['remove_button'],
        persist: false,
        maxItems: maxitems,
        createOnBlur: true,
        hideSelected: true,
        openOnFocus: false,
        selectOnTab: false,
        valueField: 'emname',
        sortField: ['name email'],
        labelField: 'email',
        searchField: ['name', 'email'],
        render: {
          item: function item(_item, escape) {
            return '<div>' + (_item.name ? '<span class="name">' + escape(_item.name) + '</span>' : '') + (_item.email ? '<span class="email"> &lsaquo;' + escape(_item.email) + '&rsaquo;</span>' : '') + '</div>';
          }
        },
        onItemRemove: unpick,
        onItemAdd: pick,
        createFilter: function createFilter(input) {
          var match, regex; //console.log('input',input);
          // email@address.com

          regex = new RegExp('^' + REGEX_EMAIL + '$', 'i');
          match = input.match(regex);

          if (match) {
            return !this.options.hasOwnProperty(match[0]);
          } // name <email@address.com>


          regex = new RegExp('^([^<]*)<' + REGEX_EMAIL + '>$', 'i');
          match = input.match(regex);

          if (match) {
            return !this.options.hasOwnProperty(match[2]);
          } //console.log('input fail');


          return false;
        },
        create: function create(input) {
          //console.log('input',input);
          if (new RegExp('^' + REGEX_EMAIL + '$', 'i').test(input)) {
            return {
              email: input,
              slug: input,
              emname: input
            };
          } //window.alert('Invalid email address.');


          return false;
        }
      });
      return sel;
    }

    plugin(view, function (_) {
      return gotContacts.add(handleContacts);
    }, function () {
      gotContacts.remove(handleContacts);
      view.$el.find('select').each(function () {
        if (this.selectize && this.selectize.destroy) {
          this.selectize.destroy();
        }
      });
    });
  }

  var showLoader = function showLoader(view) {
    return saveicon(view.firstload, view.$el);
  };

  $.Topic('pageloaded').subscribe(use('toggleClass', 'pageload').on($body, 1)); //accepts a boolean argument that controls the toggleclass, on or off
  //if the user can't see the current page, redirect

  function routeCheck() {
    var route = router.current().route;

    if (user.get('loggedin') && _.contains(router.anonOnly, route)) {
      //lug('logged in but route is anon only');
      //return !router.navigate('supporter-home',{trigger:true,replace:true});
      router.navigate('/', {
        replace: true
      });
    }

    if (!user.get('loggedin')) {
      if (_.contains(router.authOnly, route)) {
        //lug('logged out but route is auth only',althomepage_url);
        var dest = althomepage_url || '';
        althomepage_url = '';
        return !router.navigate(dest, {
          trigger: true,
          replace: true
        });
      }

      if (_.contains(router.authOnlyRefresh, route.split('/')[1])) {
        return !router.navigate(route.split('/')[0], {
          trigger: true,
          replace: true
        });
      }
    }
    /*else{
        althomepage_url = '';
    }*/


    return true;
  }

  function clearPage() {
    _.each(currentViews, function (v) {
      return v.remove();
    });

    activeCampaign = {};
    moleinprogress = false;
    $pagecontent.html('');
  } //function newPage(route,id,extras,fakehash){


  function newPage(route) {
    var ops = arguments.length > 1 && arguments[1] !== undefined$1 ? arguments[1] : {};
    lug('firing NEW PAGE route', {
      current: router.current().route,
      route: route,
      hash: ops.fakehash || hash,
      rc: router.current(),
      extras: ops.extras
    });

    var title = ops.title,
        id = ops.id,
        extras = ops.extras,
        fakehash = ops.fakehash,
        route_ext = ops.route_ext,
        pagehash = fakehash || hash,
        dfd = $.Deferred(),
        displaytitle = _.unescape(_.capCase(title || route.replace('welcome', '').replace('-', sp)));

    if (router.current().route === route || route === "404") {
      //only change the route if it's _STILL_ the route by the time the newPage call is made
      modaldfd.reject(true); //close modal with bias, but fast

      document.title = displaytitle ? displaytitle + ' | ' + apptitle : apptitle; //console.log({route_ext});

      router.lastroute = route + (id || route_ext ? '/' + (id || route_ext) : ''); //not the back button, this keeps track of the last known APPROVED route the application got to. This isn't including fragments though.

      $body.find('select').remove(); //hrm?

      var insert = {
        id: id,
        pagetitle: route
      };

      if (extras && extras.slug) {
        insert.extradata = {
          outer: _.omit(extras, 'actions')
        };
      }

      clearPage();

      if (ops.activeCampaign) {
        activeCampaign = ops.activeCampaign;
      }

      lug("%cPage: ".concat(router.lastroute), 'color:blue', {
        id: id,
        pagehash: pagehash,
        page_data: $.extend({}, insert, extras, seed.config, user.attributes)
      });
      /*reset action "takens" when pages change*/

      _(Actions.where({
        action_taken: true
      })).invoke('unset', 'action_taken');

      $body.attr('data-page', route);
      $pagecontent.html(SC[ops.alttmpl || route]( //use a different template from the real route name
      $.extend({}, insert, extras, seed.config, user.attributes, {
        urlhash: fakehash,
        domain: domain,
        t_sources: tmpsrc
      }))).find(dataTemplateSelector).each(function () {
        currentViews.push(view($(this), insert));
      });
      $.Topic('pageloaded').publish(false);

      if (!pagehash || !w.history || !w.history.pushState) {
        scrollorJumpTo(0, 0);
      }

      clearNav(); //lug('view list+ hash', _.pluck(currentViews,'template'),ops.fakehash,hash);

      dfd.notify();
      ga('send', {
        'hitType': 'pageview',
        'location': w.location.href,
        'page': '/' + router.lastroute,
        'title': displaytitle
      });
      $container.removeClass('view-loading');
      $.when.apply($, _.pluck(currentViews, 'firstload')).done(dfd.resolve, function () {
        //lug('allviews loaded, plus hash',hash);
        //mainLoader.notify();
        //                window.optimizely.push(["activate", 2796230457]);
        $.Topic('pageloaded').publish(true);

        if (pagehash && w.history && w.history.pushState) {
          //lug('GOT hash',ops.fakehash);
          var target = $(document.getElementById(pagehash)); //lug(target,ops.fakehash.replace(/\//g,'\\/'));

          if (target.length) {
            if (hash) {
              hash = '';
            } //this worked once, do not keep setting it


            target.find('header').click(); //lug(target);
            //w.scrollTo(0,1);

            $.wait(500).done(function () {
              lug('moving to hash', target.attr('id'), Math.round(target.offset().top)); //why are both of these necessary? no idea - commenting out superfluous, seems fine

              scrollorJumpTo(Math.round(target.offset().top)); // on page load with an action in the URI
              //    document.body.scrollTop = (Math.round(target.offset().top) - 47);
            });
          }
        }
      });
    } else {
      lug('route has since changed', router.current().route, route);
      dfd.reject();
    }

    return dfd;
  }

  var simplePageRoute = function simplePageRoute(route) {
    return function (_) {
      return newPage(route);
    };
  };

  var badRoute = function badRoute(_) {
    return simplePageRoute('404')();
  }; //this could mostly be collapsed since almost all of these, if not all, will route into templates with the same names
  //but that would require a router with "":something and ":page" something, a sort of meta-router, which is a bit silly


  MainRouter = Backbone.Router.extend({
    routes: approutes,
    //from a separate file now
    authOnly: ['issues-you-care-about', 'supporter-home', 'account-settings'],
    anonOnly: ['welcome'],
    authOnlyRefresh: ['your-campaigns', 'your-events'],
    before: function before(route, params) {
      lug('checking route', route, 'online?', online);
      sourceUpdate(); //reset sources now that the route is changing
      //lug({tmpsrc:tmpsrc});
      //mainLoader = saveicon($body);

      $container.addClass('view-loading');
      return routeCheck() && online || $container.removeClass('view-loading') && false;
    },
    "404": badRoute,
    welcome: simplePageRoute('welcome'),
    resist: function resist() {
      (!$.isEmptyObject(tseed) ? resolve(window.tseed) : moleapi('/resist')).then(function (tseed) {
        Campaigns.add(tseed.cc_do_more_campaigns);
        tseed = $.extend(tseed, {
          historyactions: user.get('historyactions')
        });
        window.tseed = tseed;
        newPage('resist', {
          title: 'Resist',
          tseed: tseed,
          extras: {
            tseed: tseed
          }
        }).then(function (_) {
          twttr.widgets.load();
        });
      });
    },
    "issues-you-care-about": simplePageRoute('issues-you-care-about'),
    faq: function faq() {
      $.ajax("/faqcontent.html").done(function (html) {
        newPage("faq", {
          title: 'F.A.Q - Addup',
          extras: {
            faqcontent: html
          }
        });
      });
    },
    "lte": function lte() {
      newPage('lte');
      var React = window.React;
      var ReactDOM = window.ReactDOM;
      var e = React.createElement;
      var reactContainerEl = document.getElementById('lte-react-container');
      ReactDOM.render(e(Lte), reactContainerEl);
    },
    "opt-out": function optOut(internal) {
      if (!user.get('loggedin')) {
        user.set({
          email_opt_in_campaign_updates: 1,
          email_opt_in_user_recommendations: 1,
          email_opt_in_fb_friend_recommendations: 1
        });
      }

      newPage('opt-out', {
        title: 'Opt Out of Emails',
        extras: {
          optout: true,
          anon: !internal
        }
      });
    },
    "join-today": function joinToday() {
      //lug('loading join-today');
      //clearPage();
      scrollorJumpTo(0, 0);
      newPage("join-today", {
        alttmpl: 'supporter-home',
        extras: {
          actions: [Actions.at(0).toJSON()],
          t_ww: getWidth(),
          actions_left: true,
          victory_campaigns: seed.victory_campaigns,
          pageactionmodel: default_share_id,
          sa_title: neededTxt,
          stats: Stats.toJSON()
        }
      }).progress(function () {
        //progress means that all the page setup is done, prior to rendering the views
        if (!join_login) {
          //lug('prompting registration',join_login);
          $.Topic('registration-modal').publish({
            uncloseable: true
          });
        } else {
          join_login = false; //only do that once
        }
      });
    },
    "supporter-home": function supporterHome(campid) {
      var extras = {
        pageactionmodel: default_share_id,
        t_ww: getWidth(),
        victory_campaigns: seed.victory_campaigns,
        postsignupcamp: campid === 'notifications-feed' ? undefined$1 : campid,
        stats: Stats.toJSON()
      };
      newPage("supporter-home", {
        fakehash: campid === 'notifications-feed' ? campid : undefined$1,
        extras: extras
      });
      Actions.bestOne().done(function (action) {
        var $rec_section = $body.find('#rec-action-section'),
            data = _.extend({
          actions: action && action.id ? [action] : [],
          actions_left: user.get('recs').length > 1,
          sa_title: user.get('first_name') + ": " + neededTxt
        }, extras, seed.config);

        if (action && action.id && $rec_section.length) {
          $rec_section.html(Handlebars.partials['top-action-section'](data) + Handlebars.partials['rec-campaigns-slider'](data)).find(dataTemplateSelector).each(function () {
            currentViews.push(view($(this)));
          });
        }
      });
    },
    "events/your-events": function eventsYourEvents(pageid, hash) {
      newPage('events');
      var React = window.React;
      var ReactDOM = window.ReactDOM;
      var e = React.createElement;
      var reactContainerEl = document.getElementById('events-react-container');
      ReactDOM.render(e(Events, {
        user: user,
        getGuid: getGuid
      }), reactContainerEl);
    },
    "events": function events() {
      var pathname = window.location.pathname;
      var EVENT_DETAIL_REGEX = /\/events\/(.+)/;
      var React = window.React;
      var ReactDOM = window.ReactDOM;
      var e = React.createElement;

      for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        args[_key2] = arguments[_key2];
      }

      var slug = args[0];
      var pageid = args[0];
          args[1];
      var slugish = !!(slug || '').toString().match(/[a-z]/i);
      _.isFinite(parseInt(pageid, 10)) && !slugish;
      var urlparams = args[4]; // is there a better way than this?

      if (urlparams && urlparams.indexOf('forgetme') > -1) {
        clearAuth();
      }

      if (EVENT_DETAIL_REGEX.test(pathname)) {
        pathname.match(EVENT_DETAIL_REGEX)[1];
        datasources.event(slug).done(function (r) {
          newPage('events', {
            alttmpl: 'event-detail',
            title: r.title,
            slug: slug,
            id: slug,
            extras: r
          });
        }).fail(function (e) {
          lug('no campaign', e.message);

          if (e.message === "Event not found") {
            badRoute();
          } else {
            router.navigate(router.lastroute, {
              trigger: false,
              replace: true
            }); //no 404 possible, change slug back to a known working route

            meerkatMsg('Server Error: unable to load campaign' + reloadBtnHTML);
          }
        });
        return;
      }

      newPage('events');
      var reactContainerEl = document.getElementById('events-react-container');
      ReactDOM.render(e(Events, {
        user: user,
        getGuid: getGuid
      }), reactContainerEl);
    },
    "campaigns/your-campaigns": function campaignsYourCampaigns(pageid, hash) {
      var isPaged = _.isFinite(parseInt(pageid, 10));

      if (!user.get('loggedin')) {
        router.navigate('campaigns', {
          trigger: true,
          replace: true
        });
        return false;
      }

      newPage('campaigns/your-campaigns', {
        alttmpl: 'campaigns',
        fakehash: 'your-campaigns',
        title: 'your-campaigns',
        extras: isPaged ? {
          pageid: pageid,
          hash: hash
        } : {}
      });
    },
    campaigns: function campaigns() {
      for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
        args[_key3] = arguments[_key3];
      }

      //lug('ROUTE LOOKING FOR CAMPAIGN, '+slugOrCategory, actiontype);
      var slugOrCategory = args[0],
          actiontype = args[1],
          action = args[2],
          urlparams = args[3];
      var pageid = args[0],
          hash = args[1];
      var ypageid = args[1],
          yhash = args[2];
      var slugish = !!(slugOrCategory || '').toString().match(/[a-z]/i);
      var isPaged = _.isFinite(parseInt(pageid, 10)) && !slugish;
      _.isFinite(parseInt(ypageid, 10)) && !slugish;

      if (urlparams && urlparams.indexOf('forgetme') > -1) {
        clearAuth();
      }

      if (catsObject[slugOrCategory]) {
        var category = _.clone(catsObject[slugOrCategory]);

        category.title = 'How you can help: ' + category.name;
        newPage('campaigns', {
          route_ext: slugOrCategory,
          extras: {
            start_category: category,
            pageid: ypageid,
            hash: yhash
          }
        });
      } else if (slugOrCategory && !isPaged) {
        if (action && actiontype) {
          setTimeout(function () {
            $('.action-bar-item').filter('.action-' + actiontype).click();
          }, 2000);
        }

        if (router.lastroute && slugOrCategory === router.lastroute.split('/')[1]) {
          $container.removeClass('view-loading');
          return false;
        }

        datasources.campaign(slugOrCategory).done(function (r) {
          // funding_type_id based on the following table:
          // 1	c3
          // 2	c4
          // 3	SCPC IE
          // 4	SCPC Coordinated
          // 5	SCVEF 527 - Voter Education
          // 6	SCIA - Federal Super PAC
          // 7	State PAC IE
          // 8	State PAC Coordinated
          var funding_type_id = r.funding_type_id;
          var isC3 = funding_type_id === 1;
          var isC4 = funding_type_id === 2;
          var isPAC = !(isC3 || isC4);
          var actions = r.actions;

          if (isPAC) {
            r.is_official = 'pac';
          } else {
            r.is_official = '';
          }

          if (actions) {
            actions.forEach(function (action) {
              var isDonate = action.action_type === 'donate';

              if (isDonate) {
                var hasCCode = action.c_code;

                if (!hasCCode) {
                  if (isC3) {
                    action.c_code = 'C3';
                  } else if (isC4) {
                    action.c_code = 'C4';
                  }
                }
              }
            });
          } //lug('campaign id',slug,r.id);
          //lug('campaign loading',slug,r,actiontype,action);


          if (parseFloat(slugOrCategory) === r.id) {
            //we can handle ids, but we shouldn't display them if we don't have to
            router.navigate('campaigns/' + r.slug, {
              replace: true
            });
          }

          sourceUpdate(r); //update sourcing

          if (actiontype === "updates") {
            r.updatepage = true;
          }

          newPage('campaigns', {
            activeCampaign: {
              slug: r.slug,
              id: r.id
            },
            alttmpl: 'campaign-detail',
            title: r.title,
            id: r.slug,
            extras: r,
            fakehash: actiontype ? action ? actiontype + '/' + action : actiontype : undefined$1
          }); // if(r.is_official!=="pac"){

          window.test = window.test || r;
          recommendCampaignSectionifDone(); // }
        }).fail(function (e) {
          lug('no campaign', e.message);

          if (e.message === "Campaign not found") {
            badRoute();
          } else {
            router.navigate(router.lastroute, {
              trigger: false,
              replace: true
            }); //no 404 possible, change slug back to a known working route

            meerkatMsg('Server Error: unable to load campaign' + reloadBtnHTML);
          }
        });
      } else {
        newPage('campaigns', isPaged ? {
          fakehash: 'all-campaigns',
          extras: {
            pageid: pageid,
            hash: hash
          }
        } : {});
      }
    },
    "account-settings": function accountSettings() {
      newPage('account-settings');

      if (typeof getOrientation === "undefined") {
        yepnope({
          test: Mod.filereader && Mod.canvas,
          yep: 'js/filefunctions.js',
          nope: ['js/vendor/jquery.iframe.transport.js', 'js/filefunctions.js'],
          complete: function complete() {
            $body.addClass('file-api');
          }
        });
      }
    },
    "share": function share() {
      newPage('share', {
        extras: _.extend({
          type: 'share',
          id: 'site',
          pageactionmodel: default_share_id
        })
      });
    },
    lastroute: '',
    prevpaged: '',
    current: function current() {
      var Router = this,
          fragment = Backbone.history.fragment,
          routes = _.pairs(Router.routes),
          route = null,
          params = null,
          matched;

      matched = _.find(routes, function (handler) {
        route = _.isRegExp(handler[0]) ? handler[0] : Router._routeToRegExp(handler[0]);
        return route.test(fragment);
      });

      if (matched) {
        // NEW: Extracts the params using the internal
        // function _extractParameters
        params = Router._extractParameters(route, fragment);
        route = matched[1];
      }

      return {
        route: route,
        fragment: fragment,
        params: params
      };
    }
  });
  router = new MainRouter();
  var reroute = rerouter(router);
  mixins = {
    loader: SimpleView.extend({
      plugins: [showLoader]
    }),
    swiper: SimpleView.extend({
      plugins: [addSwiper],
      mutatedata: function mutatedata(d) {
        if (d && d.data) {
          if (d.data.length === 0) {
            this.$el.closest('section').hide();
          } else {
            this.$el.closest('section').show();
          }

          d.data = d.data.slice(0, this.limit || 10);
        }

        return d;
      },
      events: {
        'click .swiper-prev': 'prev',
        'click .swiper-next': 'next'
      },
      next: function next() {
        this.$swiper.swipeNext();
      },
      prev: function prev() {
        this.$swiper.swipePrev();
      }
    }),
    swiperv: SimpleView.extend({
      plugins: [addSwiperV],
      mutatedata: function mutatedata(d) {
        if (d && d.data) {
          if (d.data.length === 0) {
            this.$el.closest('section').hide();
          } else {
            this.$el.closest('section').show();
          }

          d.data = d.data.slice(0, this.limit || 10);
        }

        return d;
      }
    }),

    /**
     *
     * This is a new mixin to go along with the rsvpinline.hbs view.
     *
     * There is a lot that loads in the "this" object when the page loads a view. Usually, all the info about that view's particular campaign or event. But this particular mixin is being called from within another mixin, so all the relevant data had to be manually attached to this mixin.
     *
     * Perhaps the most important piece of data was the 'actionmodel' which is necessary to actually apply to these events or get the status if action was taken on the event.
     *
     * Everything else that would normally be in a page view's "this" object is manually put in the "options" parameter. if it turns out you need more data for this mixin that's now stored in options, you can assign it the way actionmodel is here:
     *
     * this.actionmodel = options.actionmodel;
     *
     */
    "rsvpinline": SimpleView.extend({
      plugins: [addSelectize],
      //Use this below if you evoke this mixin by calling function view():
      // initialize: function(el,options){
      //     this.extradata = options;
      //     this.actionmodel = options.actionmodel;
      //
      //Use this below instead, when calling the mixin directly:
      initialize: function initialize(options, extras) {
        this.extradata = options;
        this.actionmodel = options.actionmodel;
        sv_init(this, options, extras);
        this.setnewAM(this.actionmodel.attributes.id);
      },
      events: {
        'click .event-rsvp': 'submit',
        'click .action-button': 'simplersvp',
        'formsuccess': 'rsvp',
        'click .event-un-rsvp': 'unrsvp',
        'click .inline-rsvp-show-form': 'showform',
        'click .inline-cancel-rsvp': 'cancelrsvp',
        'click .close-popup': 'closepopup'
      },
      setnewAM: function setnewAM(id) {
        if (Actions.get(id)) {
          this.stopListening(this.actionmodel); //stop listening to the old event action

          this.actionmodel = Actions.get(id); //find the new event action

          this.listenTo(this.actionmodel, 'change', this.render); //set the standard listening behaviors on the new action

          this.listenTo(this.actionmodel, 'trigger', this.trigger);
        }
      },
      mutatedata: function mutatedata(d) {
        this.setnewAM(d.id); //make sure the current action is current

        return $.extend({}, d, _.pick(user.attributes, pinfokeys), {
          required_params: ['zipcode', 'email', 'first_name', 'last_name', 'phone']
        });
      },
      showform: function showform(e) {
        /** this is the button to show the form if the user clicks the RSVP button while not logged in: */
        var elem = $(e.target);
        elem.parent().next().addClass('active');
        elem.addClass('hidden');
      },
      submit: function submit(e) {
        /** we add to the default submit method in mixins here, by also giving an identifying class to the current active form, so that when the action completes, the JS can easily target which inline RSVP form on the page to return to. */
        var elem = $(e.target);
        elem.closest('.inline-event-rsvp').addClass('active');
        this.postsubmit();
      },
      //triggers validation attempt without triggering a real submit
      postsubmit: stopevent,
      simplersvp: function simplersvp(e) {
        /** this is the function to save the inline RSVP on the one-click button in the list view for people logged in */
        stopevent(e);
        var spinnerTarget = this.template === "events-search" ? this.$el.find('.inline-event-rsvp.active') : this.$el;
        $('.inline-event-rsvp').removeClass('active');
        saveicon(takeAction(this, {
          email: user.get('email'),
          zipcode: user.get('zipcode')
        }), spinnerTarget);
      },
      rsvp: function rsvp(e, dfd, orgdata) {
        //sign via actual form

        /** This is the RSVP action for when a user submits the full RSVP form if they are not logged in */
        var fillindata,
            fields = ['first_name', 'last_name', 'email', 'zipcode', 'phone'];

        if (!user.get('loggedin')) {
          fillindata = _.pick(orgdata, fields); //total overwrite that's NOT stored prior to registration/login

          var oldphone = fillindata['phone'];
          var newphone = oldphone.toString().replace(/^1/, '');
          fillindata['phone'] = newphone;
          lug(orgdata, 'filling in missing data', fillindata); //if user isn't logged in, let's store their field entries localy for now

          user.set(fillindata, silent);
        } else if (!user.get('phone')) {
          var oldorgphone = orgdata.phone;
          orgdata.phone = oldorgphone.toString().replace(/^1/, '');
          user.set('phone', orgdata.phone, silent);
        } //in this case, the form submission was _already_ the action getting taken, so the dfd is sent into takeAction such that it can chain its behavior off that request rather than making a new one


        takeAction(this, user.pick(fields), dfd, {
          external: true
        });
      },
      cancelrsvp: function cancelrsvp(e) {
        var elem = $(e.target);
        this.unrsvpaction(elem);
      },
      unrsvpaction: function unrsvpaction(elem) {
        var unrsvpbare = elem.parent().parent().find('form').attr('action');
        var thiseventlisting = elem.closest('.rsvpinline');
        var currentactionmodel = this.actionmodel;
        var currentactionid = currentactionmodel.get('id');
        var hisactions = user.get('historyactions');
        var newactionsarray = hisactions;

        for (var i = 0; i < newactionsarray.length; i++) {
          if (newactionsarray[i] === currentactionid) {
            newactionsarray.splice(i, 1);
          }
        }
        /** delete the https://sc-feature.dev.bsd.net part when checking in: */


        var unrsvpurl = 'https://sc-feature.dev.bsd.net' + unrsvpbare + user.get('moleid') + '/' + currentactionid + '?api_token=' + guid;
        var xhr = new XMLHttpRequest();

        xhr.onreadystatechange = function () {
          if (xhr.readyState === XMLHttpRequest.DONE) {
            var bareText = xhr.responseText;
            var jsonResponse = JSON.parse(bareText);

            if (jsonResponse.status === "error") {
              console.log('error is ', jsonResponse.message);
              elem.closest('error-message').addClass('active');
              elem.closest('error-message').html(jsonResponse.message);
            } else {
              thiseventlisting.find('.inline-rsvp-cancel-popout').addClass('active');
              currentactionmodel.set({
                action_taken: false,
                action_took: false,
                current_amount: currentactionmodel.get('current_amount') - 1
              });
              user.set('historyactions', newactionsarray).store();
              user.trigger('change:historyactions');
            }
          }
        };

        xhr.open('POST', unrsvpurl, true);
        xhr.send(null);
      },
      unrsvp: function unrsvp(e) {
        var elem = $(e.target);
        elem.closest('.inline-rsvp-placeholder-button').next().addClass('active');
      },
      closepopup: function closepopup(e) {
        var elem = $(e.target);
        elem.closest('.inline-rsvp-popout-form').removeClass('active');
        elem.closest('.rsvpinline').find('.inline-rsvp-show-form').removeClass('hidden');
      }
    }),
    "events-gallery": SimpleView.extend({
      plugins: [addSelectize],
      initialize: function initialize(options, extras) {
        var React = window.React;
        var ReactDOM = window.ReactDOM;
        var e = React.createElement;
        var reactContainerEl = document.getElementById('all-events');
        ReactDOM.render(e(Events), reactContainerEl);
      },
      lastsearch: [],
      searchhash: null,
      sorteddata: [],
      rec: null,
      offset: 0,
      events: {
        'click .page': 'page',
        'click .prev': 'prev',
        'click .next': 'next',
        'change [name="umbrella_event_id"]': 'filterUmbrella',
        'change select': 'filter',
        'input [name="zip"], change [name="zip"]': 'filterifvalid',
        'change [name="start_date"]': 'filter',
        'change [name="end_date"]': 'filter',
        'submit #search-form': 'filter',
        'change [name="lat"]': 'mapfilter',
        'click .pagination .page, click .pagination .newPageNum': 'pagerender'
      },
      mutatedata: function mutatedata(d) {
        this.perpage = 20;
        this.sorteddata = d.data || [];
        return this.recalc(d);
      },
      recalc: recalc,
      more: stopevent,
      none: stopevent,
      page: page,
      next: next,
      prev: prev,
      byPage: byPage,
      reroute: reroute,
      filterUmbrella: function filterUmbrella(e) {
        this.umbrellaChosen = true;
        this.filter.call(this, e); //this.rerender();
      },
      filterifvalid: function filterifvalid(e) {
        var zip = $(e.currentTarget).val();

        if (/^\d{5}$/.test(zip)) {
          this.filter.call(this, e);
        }
      },
      filterYours: filterYours,
      pages: {},
      setPages: setPages,
      getPage: getPage,
      downloadPage: downloadPage,
      searchapi: searchevents,
      filter: function filter(e) {
        this.reradiusing = false;
        this.postfilter();
      },
      mapfilter: function mapfilter(e) {
        /** here we set the reradius var to true, so the event search function knows not to instantiate and then end the spinner upon the initial search; we need to keep the spinner going until all repeated searches end */
        this.reradiusing = true; // /** here we hide the elements that would visibly flicker as they constantly reload as the search mechanism looks for a minimum number of results */
        // $('.events-cover').addClass('spinnerfade');
        // this.$el.find('.events-map').addClass('spinnermap');

        /** now, we manually put a spinner on the page, and we won't remove it until all searches are done */
        // $('.events-grid-and-map').addClass('saveicon');

        $('[name="zip"]').val('');
        this.postfilter();
      },
      postfilter: eventFilter,
      spinner: spinner,
      searchevents: _.throttle(function (data, thishash, keeppage) {
        var _this = this;

        //prevents fast typers from triggering too many search attempts
        this.firstfilter = true;
        this.pages = {};
        var dataObject;
        /**
         *
         * the search query is sometimes a string and sometimes an object. Not sure what the original expectation was for when it should be one or the other but the functionality here has grown way beyond original expectations so we have to adapt.
         *
         * sometimes the search query needs to be an object, to easily read and manipulate specific data like zip or lat/lng
         *
         * here, however, it needs to be a string, as the searchevents function uses string replace to separate the search criteria
         *
         * so here, we make sure that no matter which form it used to be, it becomes a string here to make sure the eventsearch function works.
         *
         */

        if (typeof data === 'string') {
          dataObject = JSON.parse(data);
        } else {
          dataObject = data;
        }

        var search = searchevents(dataObject);
        var categoryId = dataObject.category;
        var eventTypeId = dataObject.type_id;
        var umbrellaEventId = dataObject.umbrella_event_id;
        var startDate = dataObject.start_date;
        var endDate = dataObject.end_date;
        var zip = dataObject.zip;
        var radius = dataObject.radius;
        var keywords = dataObject.keywords;
        var eventTypesSearch = categoryId ? searchEventTypes({
          category_id: categoryId
        }) : null;
        var searchPromises = [];
        var self = this;
            this.$el.find('.events-grid').empty();
            this.$el.find('.pagination').empty();
        /** So here, check reradiusing, and if it's true, that means we're doing repeated searches. In which case, don't instantiate a spinner on this individual search. */
        // if (!this.reradiusing) {

        this.spinner(search); // }

        var searchEventsPromise = search.then(_.mergeTo(this, ['offset', 'total', 'total_pages', 'cur_page'])).then(function (response) {
          return response.data;
        }).then(_.bind(self.setPages, this));
        searchPromises.push(searchEventsPromise);

        if (eventTypesSearch) {
          searchPromises.push(eventTypesSearch);
        }

        Promise.all(searchPromises).then(function (values) {
          if (_this.searchhash === thishash) {
            var events = values[0];
            var eventTypesArray = values[1];
            var eventTypes = {};

            if (!!eventTypesArray && eventTypesArray.length > 0) {
              eventTypesArray.forEach(function (eventType, idx) {
                return eventTypes[idx] = {
                  "name": eventType.name,
                  "id": eventType.id.toString()
                };
              });
              _this.extradata.types = eventTypes;
              _this.extradata.category = categoryId || 'none';
              _this.extradata.type = eventTypeId;
              _this.extradata.umbrella_event_id = umbrellaEventId;
            } else {
              _this.extradata.category = 'none';
              _this.extradata.types = {};
              _this.extradata.type = 'none';
              _this.extradata.umbrella_event_id = umbrellaEventId;
            }

            _this.extradata.end_date = endDate;
            _this.extradata.start_date = startDate;
            _this.extradata.zip = zip;
            _this.extradata.radius = radius;
            _this.extradata.keywords = keywords;
            _this.paginationData.offset = _this.offset;
            _this.paginationData.total = _this.total;
            _this.paginationData.pages = _this.pages;
            _this.paginationData.total_pages = Math.floor(_this.total / 20);
            _this.paginationData.cur_page = _this.cur_page - 1;
            _this.paginationData.currentpage = _this.cur_page - 1;
            _this.paginationData.pagenum = _this.cur_page - 1;

            _this.resort(events, keeppage); // this.rerender();

          }
        });
      }, 500),
      resort: function resort(data, keeppage) {
        //prevent infinite loop of reloading if no results returned from search
        if (data.length > 0) {
          this.sorteddata = data;

          if (keeppage !== true) {
            this.extradata.currentpage = 0;
          }

          this.rerender(data);

          if (keeppage !== true) {
            this.reroute();
            this.rerender(data);
          }
        } else {
          // if no results, empty search queries and reload
          this.previoussearch = {};
          this.lastsearch = [];
          this.rerender();
        }
      },
      reradius: function reradius() {
        /** the expanding-radius functionality should be kept separate from the rerender function. So it's now its own */
        var oldradius = 0;
        var radiusArray = [5, 10, 50, 100, 200, 500, 3000];
        var newradius = 0;
        var jsonsearchhash;
        /** Below, this looks for the radius of the previous search. And it increases the radius by the next increment. */

        if (!!this.previoussearch) {
          if (this.previoussearch.radius && parseInt(this.previoussearch.radius) <= 3000) {
            oldradius = parseInt(this.previoussearch.radius);
          }
        } else if (!!this.searchhash) {
          /**
           *
           * Again, we have to manipulate the search query, because it's sometimes a string and sometimes an object.
           *
           * Here, we need to find the query's radius, whether it's a string or object
           *
           */
          if (typeof this.searchhash === 'string') {
            jsonsearchhash = JSON.parse(this.searchhash);
          } else {
            jsonsearchhash = this.searchhash;
          }

          if (jsonsearchhash.radius) {
            oldradius = parseInt(jsonsearchhash.radius);
          }
        }

        if (!!oldradius) {
          for (var i = 0; i < radiusArray.length; i++) {
            if (radiusArray[i] > oldradius && newradius === 0) {
              newradius = radiusArray[i];
            }
          }
          /** It takes the new search radius it just figured out, and then adds it to the current search hash, whether it's a string or object */


          if (typeof this.searchhash === 'string') {
            var oldradiusString = '"' + oldradius.toString() + '"';
            var newradiusString = '"' + newradius.toString() + '"';
            var toReplace = new RegExp(oldradiusString, "g");
            var newSearchhash = this.searchhash.replace(toReplace, newradiusString);
            this.searchhash = newSearchhash;
          } else {
            this.searchhash.radius = newradius;
          }
          /**
           *
           *  Note: search by zip from the point of origin of the current center of the map, does not always work.
           *
           *  This is because the center of the map may be over something which Google Maps does not return with a zip code in the API data. Like "[something] Lake" for instance.
           *
           *  Eventually, in this portion of the events search functionality, we will have to replace the zip code search, with the lat/lng of the center of the map.
           *
           */
          // this.searchhash = JSON.parse(newSearchhash);


          this.searchevents(this.searchhash, this.searchhash, false);
          /** however if the previous conditions failed, then this is the first search on page load. Look for the user's zip and add it to the initial search hash */
        } else if (user.get('zipcode')) {
          /** same as above, eventually the zip code here must be replaced with the lat/lng of the current center of the map */
          var todaysDate = new Date();
          var todayParsed = todaysDate.getFullYear() + '-' + ("0" + (todaysDate.getMonth() + 1)).slice(-2) + '-' + ("0" + todaysDate.getDate()).slice(-2);
          var newSearchString = '{"limit":"20","radius":"500","zip":"' + user.get('zipcode') + '","start_date":"' + todayParsed + '"}';
          this.searchevents(newSearchString, newSearchString, false);
        }
      },
      rerender: function rerender(mydata, currentPageNum) {
        var _this2 = this;

        /** setting the results per page limit to 20 instead of the default 6 */
        this.perpage = 20;
        /**
         *  This is the redo-search map button functionality
         *
         *  First, it runs if the following conditions are matched:
         *      - are there less than 20 results?
         *      - is the search radius less than 3000 miles?
         *      - is there nothing in the keywords search box? (if there is, then we can't force 20 results, if they search something specific like "red elk lodge hike")
         *
         */

        var jsonsearchhash;
        /** Here, we need to find out if there are classification modifiers in the search query. So we turn the query into a json object in all cases, so we can search the query parameters easily */

        if (this.searchhash) {
          if (typeof this.searchhash === 'string') {
            jsonsearchhash = JSON.parse(this.searchhash);
          } else {
            jsonsearchhash = this.searchhash;
          }
        }

        setTimeout(function () {
          $('.events-cover').removeClass('spinnerfade').promise().then(function () {
            $('.events-map').removeClass('spinnermap');
          });
        }, 1000);
        /** Since user data is not included as part of the event data passed to the individual event tiles in the list view, we have to overtly add them. If there's a better way to do this I'm open to it. */

        var userholder = this.extradata.userattributes;
        var currentDataSet;

        if (!!mydata) {
          currentDataSet = mydata;
          this.total = mydata.length;
        } else {
          currentDataSet = this.sorteddata;
        }

        var revised = this.recalc({
          data: currentDataSet
        });

        if (revised.data) {
          jQuery.each(revised.data, function (index, value) {
            value['moleid'] = userholder.moleid;
            value['email'] = userholder.email;
            value['first_name'] = userholder.first_name;
            value['last_name'] = userholder.last_name;
            value['zipcode'] = userholder.zipcode;
            value['phone'] = userholder.phone;
          });
        } // lug('revised grid', revised && revised.data && revised.data.length,revised);

        /**
         *
         * Event search result endpoints now return with an additional root node besides "data" if this is an umbrella search.
         *
         * The new root node is named "metadata" and it returns if the umbrella filter returns an active umbrella ID
         *
         * "metadata" will hold the data needed for the Umbrella Header, like the name and description.
         *
         * Our existing moleapi depends on the root node being "data" so we can't use our existing functions to search the endpoint for this.
         *
         * Thus, the below XHR is to grab that Umbrella data:
         *
         */


        if (!this.yours) {
          var hasUmbrellaId = false;

          if (!!jsonsearchhash) {
            if (!!jsonsearchhash.umbrella_event_id) {
              hasUmbrellaId = true;
            }
          }

          if (!!this.umbrellaChosen || jsonsearchhash && !!window.location.href.indexOf("umbrella_event_id") && !window.location.href.indexOf("umbrella_event_id=0") || hasUmbrellaId) {
            lug('umbrella chosen');
            var params = '?';
            var dataKeys = Object.keys(jsonsearchhash);
            var queryString = dataKeys.reduce(function (acc, key, idx) {
              var value = jsonsearchhash[key];
              return "".concat(acc).concat(idx ? '&' : '').concat(key, "=").concat(value);
            }, params);
            /** delete the https://sc-feature.dev.bsd.net part when checking in: */

            var umbrellaSearchURL = '/api/v1/events/search' + queryString;
            var umbrellaObject;
            var xhrUmbrella = new XMLHttpRequest();

            xhrUmbrella.onreadystatechange = function () {
              if (xhrUmbrella.readyState === XMLHttpRequest.DONE) {
                var bareText = xhrUmbrella.responseText;
                var umbrellaObjectRaw = JSON.parse(bareText);
                umbrellaObject = umbrellaObjectRaw.metadata;

                if (!!umbrellaObject) {
                  var newHeader = function newHeader(umbrellaObject) {
                    lug('there is umbrella name ', umbrellaObject.name);
                    $('.events-header').replaceWith(Handlebars.partials['events-header']({
                      data: umbrellaObject,
                      umbrellaChosen: true,
                      template: 'events-header'
                    }));
                  };

                  newHeader(umbrellaObject);
                }
              }
            };

            xhrUmbrella.open('GET', umbrellaSearchURL, true);
            xhrUmbrella.send(null);
          } else {
            lug('get rid of umbrella header');
            $('.events-header').replaceWith(Handlebars.partials['events-header']({
              data: {
                id: 0,
                name: '',
                description: '',
                start_date: ''
              },
              umbrellaChosen: false,
              template: 'events-header'
            }));
          }
        }
        /** There were too many fragmentation JS errors using '.orAppend' here, so we're just referring to the dom object directly */


        $('.events-search-large').replaceWith(Handlebars.partials['events-search-large'](this.extradata)).promise().then(function () {
          selectizeInputs(_this2);
        });
        /** Same here, the '.orAppend' method was resulting in too many JS fragment errors so we just refer to the dom object directly */

        var $eventsGrid;

        if (this.yours) {
          $eventsGrid = $('#your-events .events-grid');
        } else {
          $eventsGrid = $('#all-events .events-grid');
        }

        $eventsGrid.replaceWith(Handlebars.partials['events-grid']({
          data: mydata,
          template: 'event-tile',
          total: this.total
        })).promise().done(function () {
          /**
           *
           * Here we load the RSVP actions for each inline event that we just loaded into the events-grid.
           *
           * To do this, we are manually calling for a view to load inside each event listing.
           *
           * The challenge here is how to attach all the object data that would normally be attached to a page, to only this inline view.
           *
           */
          lug('actions ', Actions);
          $('.rsvpinline').each(function () {
            var currentInline = $(this);
            var inlineSlug = currentInline.attr('data-slug');
            var thisactionmodel;
            moleapi('/events/' + inlineSlug, 'get', {}).then(function (d) {
              thisactionmodel = d;

              if (thisactionmodel) {
                datasources.event(currentInline.attr('data-slug')).done(function (r) {
                  /** So now we have the action model and the data for the view. Now we can call the function view(), and pass it the current event's RSVP dom object, as well as all the event data as the function parameters */
                  var newoptions = r;
                  newoptions.actionmodel = thisactionmodel;
                  newoptions.el = currentInline; // new mixins['rsvpinline'](newoptions, $.extend( true, {'model': user, 'template': 'rsvpinline', 'watch': true}));

                  view(currentInline, newoptions);
                });
              }
            });
            /** So, for each event in the listing, we will grab all the data associated with this event - it is still in revised.data which we have access to, but we would have to loop through the revised.data object again to find it, while we could just get that from the event datasources directly. */
          });
        });

        if (!this.yours) {
          if (!$('#all-events .events-grid').children('div').hasClass('no-results-section')) {
            $('.events-map-data').replaceWith(Handlebars.partials['events-map-data']({
              data: mydata
            }));
          } else {
            $('.events-map-data').replaceWith(Handlebars.partials['events-map-data']({
              data: 0
            }));
          }
        }

        var $eventsPagination;

        if (this.yours) {
          $eventsPagination = $('#your-events .pagination');
        } else {
          $eventsPagination = $('#all-events .pagination');
        }

        if (currentPageNum !== undefined$1) {
          this.paginationData.currentpage = currentPageNum;
        }

        $eventsPagination.replaceWith(Handlebars.partials.eventspagination(this.paginationData));
        $('.search-types select').selectize();
      },
      pagerender: function pagerender(e) {
        var $elem = $(e.target);
        var newPageNumRaw = parseInt($elem.html());
        var newPageNum = newPageNumRaw - 1;
        var newPageData;
        $.each(this.paginationData.pages, function (index, value) {
          if (parseInt(index) === newPageNum) {
            newPageData = value;
          }
        });
        this.rerender(newPageData, newPageNum);
      }
    }),
    gallery: SimpleView.extend({
      plugins: [addSelectize],
      initialize: function initialize(options, extras) {
        var _this3 = this;

        var hash = {},
            currentpage = parseInt(extras.pageid || 1, 10) - 1;

        if (extras.hash) {
          // if we're using a legacy URI with the hash in it
          if (extras.hash.indexOf('=') === -1) {
            try {
              hash = JSON.parse(atob(extras.hash));
              hash.hashHash = true;
            } catch (e) {}
          } else {
            // if we're using a query string like the Framers of the Constitution intended
            var queryString = {};
            extras.hash.split("&").forEach(function (pair) {
              if (pair === "") {
                return;
              }

              var parts = pair.split("=");
              queryString[parts[0]] = parts[1] && decodeURIComponent(parts[1].replace(/\+/g, " "));
            });

            try {
              hash = queryString || null;
              hash.hashHash = true;
            } catch (e) {}
          }
        }

        this.apilimit = 36;
        this.perpage = extras.perpage;
        this.offset = currentpage * extras.perpage;
        extras.extradata = $.extend(extras.extradata || {}, {
          items: 0,
          pages: 0,
          perpage: extras.perpage,
          currentpage: currentpage,
          category: this.$el.data('start_category'),
          categories: categories,
          apilimit: this.apilimit
        }, hash);
        sv_init(this, options, extras);
        saveicon(this.firstload, this.$el);

        if (hash.hashHash) {
          this.firstload.done(function (_) {
            return _this3.filter(true);
          });
        }
      },
      lastsearch: [],
      searchhash: null,
      sorteddata: [],
      rec: null,
      offset: 0,
      events: {
        'click .more': 'more',
        'click .page': 'page',
        'click .prev': 'prev',
        'click .next': 'next',
        'change select': 'filter',
        'keyup [name="title"]': 'filter',
        'search input': 'filter',
        'submit #search-form': 'none'
      },
      mutatedata: function mutatedata(d) {
        this.sorteddata = d.data;
        return this.recalc(d);
      },
      recalc: recalc,
      rerender: function rerender() {
        var revised = this.recalc({
          data: this.sorteddata
        }),
            $grid = this.$el.find('.campaigns-grid').orAppend(['<div/>', {
          "class": 'campaigns-grid'
        }]); //lug('revised grid', revised && revised.data && revised.data.length,revised);

        if (revised && revised.data.length) {
          $grid.replaceWith(Handlebars.partials['campaigns-grid']({
            data: revised.data,
            template: 'campaign-tiles'
          }));
        } else {
          (this.rec || (this.rec = Campaigns.goodOne())).done(function (rec_campaign) {
            if (rec_campaign) {
              $grid.empty().append(Handlebars.partials['action-next-campaign-no-results']($.extend({}, rec_campaign, {
                first_name: user.get('first_name')
              })));
            }
          });
        }

        this.$el.find('.pagination').orAppend(['<div/>', {
          "class": 'pagination'
        }]).replaceWith(Handlebars.partials.pagination(this.extradata));
      },
      more: stopevent,
      none: stopevent,
      page: page,
      next: next,
      prev: prev,
      byPage: byPage,
      reroute: reroute,
      filter: function filter(keeppage) {
        var $form = this.$el.find('#search-form'),
            //$el = $(e.currentTarget),
        data,
            cat,
            thishash;
        data = _.cleanObj(_.pick($form.serializeObject(), function (d) {
          d = $.trim(d);
          return d && d !== 'none';
        })); //name = val==="national"?'national':name;

        if (this.source === "your-campaigns") {
          data.yours = true;
        }

        if (data.state && data.state === "national") {
          data.national = true;
          delete data.state;
        }

        if (data.state && data.state === "nearby") {
          this.source = "campaignsnearby";
          delete data.state;
        }

        cat = data.category ? _.findWhere(categories, {
          id: parseFloat(data.category)
        }) || null : null;

        if (this.start_category) {
          if (_.isObject(cat) && cat.id !== this.start_category) {
            router.navigate('/campaigns/' + cat.slug, {
              trigger: true
            });
            return false;
          }
        }

        this.lastsearch = _.pairs(data); //update to the latest search

        this.resort(data.title, keeppage);

        if (!this.firstfilter || _.keys(data).length) {
          this.searchhash = thishash = JSON.stringify(data);
          this.searchcamps(data, thishash, keeppage);
        }

        if (!_.keys(data).length) {
          this.searchhash = thishash = null;
        }

        this.reroute();
      },
      pages: {},
      setPages: setPages,
      getPage: getPage,
      downloadPage: downloadPage,
      spinner: spinner,
      campaigns: true,
      searchapi: searchcamps,
      searchcamps: _.throttle(function (data, thishash, keeppage) {
        //prevents fast typers from triggering too many search attempts
        this.firstfilter = true;

        var params = _.extend(data, {
          limit: 200
        });

        gaEventSetter("Campaign Search", "Search", params);
        searchcamps(params).done($.proxy(function () {
          if (this.searchhash === thishash) {
            //only complete the sort if the hash still matches. Should reduce thrashing from slow search routes
            this.resort(data.title, keeppage);
          }
        }, this));
      }, 500),
      resort: function resort(titlesort, keeppage) {
        lug('resorting', this.lastsearch, 'titlesort?', titlesort, this.sorteddata[0]); //name = val==="national"?'national':name;

        var campaigns = _.invoke(Campaigns.multifilter(this.lastsearch), 'toJSON');

        if (titlesort && _.isString(titlesort)) {
          _.each(campaigns, function (el) {
            el.score = _(titlesort.split(/\s/)).reduce(function (memo, searchterm) {
              return memo + (el.title || "").score(searchterm, 0.5);
            }, 0);
          });

          campaigns.sort(function (a, b) {
            return a.score - b.score;
          }).reverse();
        }

        this.sorteddata = campaigns;

        if (keeppage !== true) {
          this.extradata.currentpage = 0;
        }

        this.rerender();

        if (keeppage !== true) {
          this.reroute();
        }
      }
    }),
    recruit: SimpleView.extend({
      plugins: [addSelectize, addEmail],
      selectconfig: recruitSelectConfig,
      who: [],
      addOptions: function addOptions() {
        return this.tsmodel.attributes.friends; //user.get('randfb_friends');
      },
      shuffler: false,
      initialize: function initialize(options, extras) {
        //lug('init recruit',{extras:extras,ops:options});
        var action = Actions.get(extras.actionmodel),
            camp = action.getCampJSON(),
            //since this happens in the init step we need to get it separately here
        ltlng;
        extras.shuffler = false;
        extras.listen = "user:fbloggedin,user:fbperms,user:fbconnected,user:randfb_friends,user:loggedin";
        extras.tsmodel = action.get('model'); //get the TSModel model, oy
        //can we make this more performant at some point? yeah probably should live up top maybeish

        if (_.isString(extras.tsmodel)) {
          action.set('model', extras.tsmodel = TSModels.findWhere({
            model: extras.tsmodel
          }));
        }

        extras.extradata = $.extend(extras.extradata || {}, {
          csclients: csclients
        }); //lug(Actions.get(extras.actionmodel),extras.tsmodel);

        if (camp && camp.latitude) {
          ltlng = camp.latitude + ',' + camp.longitude;

          if (!extras.tsmodel.get('loc') || extras.tsmodel.get('loc') !== ltlng) {
            //create a new model with the same slug, but with a ltlng parameter
            extras.tsmodel = newTS(extras.tsmodel, {
              loc: ltlng
            });
            action.set('model', extras.tsmodel); //lug('NEW LAT LNG = new model',extras.tsmodel);
          }
        }

        extras.tsmodel.resetSelected();
        sv_init(this, options, extras); //lug({initmodel:this.tsmodel});

        this.listenTo(this.tsmodel, 'change', this.render);
      },
      mutatedata: function mutatedata(d) {
        return _.extend({}, d, this.tsmodel.attributes);
      },
      events: {
        'click [data-action-id]': 'tweeted',
        'click [data-fbaction-id]': 'nativeFB',
        'click [data-email-id]': 'emailed',
        'click .back-button': 'reset'
      },
      tempSetAction: function tempSetAction(stype) {
        lug('temp set action', stype);
        gaEventSetter("Campaign Action", "Recruit", "".concat(stype));

        var am = this.actionmodel,
            aid = am.get('id'),
            hisactions = user.get('historyactions'),
            data = _.cleanObj({
          recruit_type: stype,
          email: user.get('email')
        });

        saveicon(takeAction(this, data).done(function () {
          if (!_.contains(hisactions, aid)) {
            user.set('historyactions', hisactions.concat([aid])).store();
            user.trigger('change:historyactions');
          }
        }), this.$el);
        $('#a_id' + aid).find('a').click();
      },
      tweeted: function tweeted() {
        this.tempSetAction('tweet');
      },
      nativeFB: function nativeFB() {
        this.tempSetAction('fb-share');
      },
      emailed: function emailed() {
        this.tempSetAction('email');
      },
      reset: function reset(e) {
        stopevent(e);
        var $emform = this.$el.find('.send-email-form'),
            contacts = this.actionmodel.get('gotcontacts'),
            $section = this.$el.closest('.action-section');

        if ($emform.length && contacts && !$emform.hasClass('pick-only')) {
          this.$el.find('form').addClass('pick-only');
        } else {
          this.actionmodel.set({
            'email-step': false,
            action_taken: false,
            gotcontacts: false
          });
        }

        $.wait(2).done(function () {
          scrollorJumpTo($section.offset().top - 30);
        });
      },
      render: function render() {
        var $inner,
            $done,
            that = this,
            val,
            selectize = this.$el.find('select')[0];
        this.$highlighter = this.$el.find('.highlighter');
        this.$msgarea = this.$el.find('[name="sharetext"]');
        val = this.$msgarea.val();

        if (selectize) {
          selectize = selectize.selectize;
          this.who = selectize.getValue();
        }

        SimpleView.prototype.render.call(this);
        this.$msgarea = this.$el.find('[name="sharetext"]');

        if (val) {
          val = this.$msgarea.val(val);
        } //lug('animate shuffler?',{shuffler:JSON.stringify(this.shuffler)});


        if (this.$el.find('.shuffler-outer').length) {
          //lug('ani',!this.shuffler);
          if (!this.shuffler) {
            this.shuffler = true;
            that.start = true;
            that.stop = false; //lug('animating shuffler',{shuffler:JSON.stringify(this.shuffler)});

            (function poll() {
              $inner = that.$el.find('.shuffler-inner').eq(0);
              $done = that.$el.find('.friend-list-results').eq(0); //lug({present:$inner.length,start:that.start,stop:that.stop,shuffler: that.shuffler});

              if ($inner.length) {
                $inner.prepend($inner.children().filter(':last-child'));
              } else {
                that.stop = true;
                that.shuffler = false;
              }

              if ($done.length || !$('body').find('.recruit-action-section').length) {
                that.stop = true;
                that.shuffler = false;
              } //lug($inner.length,that.start,that.stop);


              if (!that.stop) {
                $.wait(400).done(function (_) {
                  return poll();
                });
              } //else{ lug('shuffler stopped'); }

            })();
          }
        } else {
          this.stop = true;
          this.shuffler = false;
        }
      },
      tsshare: function tsshare(e) {
        stopevent(e);
      },
      more: function more(e) {
        stopevent(e);
      },
      edittext: function edittext(e) {},
      toggle: toggleFriend,
      selectli: selectli
    }),
    more: SimpleView.extend({
      initialize: function initialize(options, extras) {
        if (getWidth() < tablet) {
          extras.limit = 2;
        } else {
          extras.limit = 8;
        }

        sv_init(this, options, extras);
      },
      events: {
        'click .n-more': 'getMore'
      },
      getMore: function getMore(e) {
        //lug('asking for more notice view',this.limit);
        stopevent(e);

        if (getWidth() < tablet) {
          this.limit = 8;
        } else {
          this.limit = this.limit + 8;
        }

        this.render();
      }
    }),
    limit: SimpleView.extend({
      mutatedata: function mutatedata(d) {
        var limit = this.$el.data('limit') || 10;
        d.data = d.data.slice(0, limit);
        return d;
      }
    }),
    chosen: SimpleView.extend({
      plugins: [addSelectize],
      mutatedata: function mutatedata(d) {
        return $.extend({}, d, _.pick(user.attributes, pinfokeys), {
          show_fields: pinfokeys
        });
      }
    }),
    volunteer: SimpleView.extend({
      plugins: [addSelectize],
      initialize: function initialize(options, extras) {
        extras.watch = false;
        sv_init(this, options, extras);
      },
      events: {
        'click .action-button': 'newsign',
        // 'click .action-button': 'signed',
        // 'formsuccess':'newsign',
        'formerror': 'activate'
      },
      mutatedata: function mutatedata(d) {
        var req_array = ['volunteer_options', 'email'];
        return $.extend({}, d, _.pick(user.attributes, pinfokeys), {
          formactive: req_array
        });
      },
      activate: function activate() {
        if (!!user.get('loggedin')) {
          //not sure this is true/needed anymore
          this.actionmodel.set('formactive', true);
        }
      },
      newsign: function newsign(e, dfd, orgdata) {

        var $el = $(e.currentTarget).closest('form');

        if ($('.volunteer-options-container-all :checkbox:checked').length === 0 || !$el.find('input[name="email"]').val()) {
          if ($('.volunteer-options-container-all :checkbox:checked').length === 0) {
            $('.volunteer-options-all').addClass('show');
          } else {
            $('.volunteer-options-all').text('Cannot submit with current data');
            $('.volunteer-options-all').addClass('show');
          }
        } else {
          $(document.body).append($el);
          var volArray = [];
          $el.find('input[name="volunteer_options"]:checked').each(function () {
            volArray.push($(this).val());
          });
          takeAction(this, {
            email: $el.find('input[name="email"]').val(),
            volunteer_options: volArray
          }, undefined$1, {
            immediate: true
          }, gaEventSetter("Campaign Action", "Volunteer", "".concat(window.hostname, "/").concat(window.location.pathname).concat(window.location.search)));
          $el.hide();
        }

        e.preventDefault();
      }
    }),
    message: SimpleView.extend({
      plugins: [addSelectize],
      initialize: function initialize(options, extras) {
        extras.watch = false;
        sv_init(this, options, extras);
      },
      events: {
        'click .action-button': 'signed',
        'formsuccess': 'newsign',
        'formerror': 'activate'
      },
      mutatedata: function mutatedata(d) {
        var show_fields = [];
        var req_array = [];
        var op_array = [];
        var req_params = this.actionmodel.get('required_params');

        if (req_params.required) {
          req_params.required.forEach(function (item) {
            req_array.push(item);
          });
        } else {
          req_array = req_params;
        }

        req_array.forEach(function (item) {
          show_fields.push(item);
        });

        if (req_params.optional) {
          req_params.optional.forEach(function (item) {
            op_array.push(item);
          });
        } else {
          op_array = this.actionmodel.get('optional_params');
        }

        op_array.forEach(function (item) {
          show_fields.push(item);
        });
        return $.extend({}, d, _.pick(user.attributes, pinfokeys), {
          show_fields: show_fields
        }, {
          formactive: matchreqs(req_array)
        });
      },
      signed: function signed(e) {
        //one click signs
        stopevent(e);
        $(e.currentTarget); //if (checkReq(e,$(e.currentTarget)) ){
        //lug('got petition event',e,this.actionmodel.required_params);

        /*see if the logged in user has everything we ned to sign, otherwise reveal the form*/

        if (!matchreqs(this.actionmodel.get('required_params'))) {
          var dfd = takeAction(this, user.pick(this.actionmodel.get('required_params')), undefined$1, {
            wait: true
          }).then(gaEventSetter("Campaign Action", "Take Action/Sign Petition", "".concat(window.hostname, "/").concat(window.location.pathname).concat(window.location.search))).fail($.proxy(function (e) {
            this.actionmodel.set('formactive', true);
            this.actionmodel.set('error_on_required', true);
            markErrors(this.$el, e);
          }, this));
          saveicon(dfd, this.$el);
        } else {
          this.activate();
        } //}

      },
      activate: function activate() {
        if (!!user.get('loggedin')) {
          //not sure this is true/needed anymore
          this.actionmodel.set('formactive', true);
        }
      },
      newsign: function newsign(e, dfd, orgdata) {
        //sign via actual form
        var fillindata;

        if (!user.get('loggedin')) {
          fillindata = _.pick(orgdata, this.actionmodel.get('required_params')); //total overwrite that's NOT stored prior to registration/login
          //lug(orgdata,'filling in missing data',fillindata);
          //if user isn't logged in, let's store their field entries localy for now

          user.set(fillindata, silent);
        } //in this case, the form submission was _already_ the action getting taken, so the dfd is sent into takeAction such that it can chain its behavior off that request rather than making a new one


        takeAction(this, user.pick(this.actionmodel.get('required_params')), dfd, {
          external: true
        }).then(gaEventSetter("Campaign Action", "Take Action/Sign Petition", "".concat(window.hostname, "/").concat(window.location.pathname).concat(window.location.search)));
      }
    }),
    petition: SimpleView.extend({
      plugins: [addSelectize],
      initialize: function initialize(options, extras) {
        extras.watch = false;
        sv_init(this, options, extras);
      },
      events: {
        'click .action-button': 'signed',
        'formsuccess': 'newsign',
        'formerror': 'activate'
      },
      mutatedata: function mutatedata(d) {
        var show_fields = [];
        var req_array = [];
        var op_array = [];
        var req_params = this.actionmodel.get('required_params');

        if (req_params.required) {
          req_params.required.forEach(function (item) {
            req_array.push(item);
          });
        } else {
          req_array = req_params;
        }

        if (this.actionmodel.get('can_edit_subject')) {
          req_array.push('subject');
        }

        if (this.actionmodel.get('can_edit_message')) {
          req_array.push('message');
        }

        req_array.forEach(function (item) {
          show_fields.push(item);
        });

        if (req_params.optional) {
          req_params.optional.forEach(function (item) {
            op_array.push(item);
          });
        } else {
          op_array = this.actionmodel.get('optional_params');
        }

        if (this.actionmodel.get('display_personal_message')) {
          op_array.push('pmessage');
        }

        op_array.forEach(function (item) {
          show_fields.push(item);
        });
        return $.extend({}, d, _.pick(user.attributes, pinfokeys), {
          show_fields: show_fields
        }, {
          formactive: matchreqs(req_array)
        });
      },
      signed: function signed(e) {
        //one click signs
        stopevent(e); //if (checkReq(e,$(e.currentTarget)) ){
        //lug('got petition event',e,this.actionmodel.required_params);

        /*see if the logged in user has everything we ned to sign, otherwise reveal the form*/

        $(e.currentTarget);

        if (!matchreqs(this.actionmodel.get('required_params'))) {
          // var dfd = takeAction(this, user.pick(this.actionmodel.get('required_params')),undefined,{wait:true} )
          var dfd = takeAction(this, {
            prefix: user.get('prefix'),
            first_name: user.get('first_name'),
            last_name: user.get('last_name'),
            street_address: user.get('street_address'),
            additional_address: user.get('additional_address'),
            city: user.get('city'),
            state: user.get('state'),
            zipcode: user.get('zipcode'),
            phone: user.get('phone'),
            email: user.get('email'),
            pmessage: $('textarea[name="pmessage"]').val()
          }, undefined$1, {
            wait: true
          }).then(gaEventSetter("Campaign Action", "Take Action/Sign Petition", "".concat(window.hostname, "/").concat(window.location.pathname).concat(window.location.search))).fail($.proxy(function (e) {
            this.actionmodel.set('formactive', true);
            this.actionmodel.set('error_on_required', true);
            markErrors(this.$elForm, e);
            gaEventSetter("Form Interaction", "Form Error", "".concat(e.message));
          }, this));
          saveicon(dfd, this.$el);
        } else {
          var formphoneold = this.$el.find('input[name="phone"]').val();
          var formphonenew = formphoneold.toString().replace(/^1/, '');
          this.$el.find('input[name="phone"]').val(formphonenew);

          if (this.actionmodel.get('display_personal_message')) {
            takeAction(this, {
              prefix: user.get('prefix'),
              first_name: user.get('first_name'),
              last_name: user.get('last_name'),
              street_address: user.get('street_address'),
              additional_address: user.get('additional_address'),
              city: user.get('city'),
              state: user.get('state'),
              zipcode: user.get('zipcode'),
              phone: user.get('phone'),
              email: user.get('email'),
              pmessage: $('textarea[name="pmessage"]').val()
            }, undefined$1, {
              external: true
            }).then(gaEventSetter("Campaign Action", "Take Action/Sign Petition", "".concat(window.hostname, "/").concat(window.location.pathname).concat(window.location.search)));
          } else {
            this.activate();
          }
        } //}

      },
      activate: function activate() {
        if (!!user.get('loggedin')) {
          //not sure this is true/needed anymore
          this.actionmodel.set('formactive', true);
        }
      },
      newsign: function newsign(e, dfd, orgdata) {
        //sign via actual form
        var fillindata;

        if (!user.get('loggedin')) {
          fillindata = _.pick(orgdata, this.actionmodel.get('required_params')); //total overwrite that's NOT stored prior to registration/login
          //lug(orgdata,'filling in missing data',fillindata);
          //if user isn't logged in, let's store their field entries localy for now

          if (fillindata['phone']) {
            var oldphone = fillindata['phone'];
            var newphone = oldphone.toString().replace(/^1/, '');
            fillindata['phone'] = newphone;
          }

          user.set(fillindata, silent);
        } //in this case, the form submission was _already_ the action getting taken, so the dfd is sent into takeAction such that it can chain its behavior off that request rather than making a new one


        var oldpicks = user.pick(this.actionmodel.get('required_params'));

        if (oldpicks['phone']) {
          var newpicks = oldpicks['phone'].toString().replace(/^1/, '');
          oldpicks['phone'] = newpicks;
        }

        takeAction(this, user.pick(this.actionmodel.get('required_params')), dfd, {
          external: true
        }).then(gaEventSetter("Campaign Action", "Take Action/Sign Petition", "".concat(window.hostname, "/").concat(window.location.pathname).concat(window.location.search)));
      }
    }),
    tweet: SimpleView.extend({
      plugins: [addSwiperV],
      initialize: function initialize(options, extras) {
        extras.watch = false;
        sv_init(this, options, extras);
      },
      mutatedata: function mutatedata(d) {
        //add data to the main t_sources
        var t_sources = $.extend({}, tmpsrc, {
          utm_medium: 'recruit',
          utm_source: 'twitter',
          utm_campaign: 'addup',
          tc: user.get('share_code')
        });
        return $.extend({}, d, _.pick(user.attributes, pinfokeys), {
          t_sources: t_sources
        });
      },
      checkfields: function checkfields(e) {
        /*makes sure the fields are valid then if there's no hope of getting a twitter event, fires*/
        if (no_twitter_api) {
          /*call it but add a delay*/
          $.wait(is_android ? undefined$1 : 3000).then(_.bind(this.tweeted, this, {}, true));
        }
      },
      submit: stopevent,
      //triggers validation attempt without triggering a real submit
      events: {
        'action [data-action-id]': 'tweeted',
        'click .action-button': 'checkfields',
        'submit': 'submit'
      },
      tweeted: function tweeted(e, delay) {
        //lug('got TWITTER EVENT',e);
        gaEventSetter("Campaign Action", "Tweet", "".concat(window.hostname, "/").concat(window.location.pathname).concat(window.location.search));
        takeAction(this, {
          email: user.get('email'),
          zipcode: user.get('zipcode')
        }, undefined$1, {
          immediate: !delay
        });
      }
    }),
    "impact-graph": SimpleView.extend({
      events: {
        "click a": 'checkmobile'
      },
      checkmobile: function checkmobile(e) {
        /*if the viewpoint is in mobile mode, change behavior of clicks to cancel navigation on the left side, turning it into a selection UI instead */
        if (getWidth() < 768) {
          var $el = $(e.currentTarget),
              width = $el.width(),
              $container = $el.closest('.catsofar').addClass('empty-clicked');
          $container.find('.mobile-bar').removeClass('mobile-bar');
          $el.addClass('mobile-bar');

          if (e.pageX <= width) {
            stopevent(e, true);
          } //console.log('CLACK',_.pick(e,['clientX','offsetX','pageX']),width);

        }
      }
    }),
    'event': SimpleView.extend({
      plugins: [addSwiper],
      events: {
        'click .action-button': 'simplersvp',
        'submit #events-search-form': 'submit',
        'click .search-button': 'filter',
        'click .clickable': 'changemain',
        'submit': 'submit',
        'formsuccess': 'rsvp',
        'click .swiper-prev': 'prev',
        'click .swiper-next': 'next'
      },
      next: function next() {
        this.$swiper.swipeNext();
      },
      prev: function prev() {
        this.$swiper.swipePrev();
      },
      changemain: function changemain(e) {
        stopevent(e);
        var $el = $(e.currentTarget).closest('[data-index]'),
            index = $el.data('index'),
            newid = this.currentlist && this.currentlist[index] && this.currentlist[index].id,
            explore_page = !activeCampaign.id,
            colNum = explore_page ? "col-8" : "col-12";

        if (!newid) {
          return false;
        }

        $('.events-slider .slide').removeClass('currentEvent');
        $el.addClass('currentEvent');
        $('html, body').animate({
          scrollTop: this.$el.offset().top
        }, 400);
        this.setnewAM(newid);
        var eventTitle = document.getElementById('event-title');

        if (eventTitle) {
          var eventDescription = document.getElementsByClassName('action-content__umbrella-description')[0];
          var action_headline = this.actionmodel.get('action_headline');
          var action_description = this.actionmodel.get('action_description');
          eventTitle.innerText = action_headline || 'ATTEND AN EVENT';

          if (eventDescription) {
            eventDescription.innerText = action_description || '';
          }
        }

        this.source = 'action:' + newid;
        this.$el.find('.event-display-row').html(Handlebars.partials[explore_page ? 'featured-event' : 'display-event']($.extend(standardData(this), _.pick(user.attributes, pinfokeyslogin), this.actionmodel.attributes, {
          col: colNum
        }, this.extradata)));
      },
      setnewAM: function setnewAM(id) {
        this.stopListening(this.actionmodel); //sop listening to the old event action

        this.actionmodel = Actions.get(id); //find the new event action

        this.listenTo(this.actionmodel, 'change', this.render); //set the standard listening behaviors on the new action

        this.listenTo(this.actionmodel, 'trigger', this.trigger);
      },
      mutatedata: function mutatedata(d) {
        this.setnewAM(d.id); //make sure the current action is current

        d.events = this.currentlist = _.first(d.events, 10);
        return $.extend({}, d, _.pick(user.attributes, pinfokeys), {
          required_params: ['zipcode', 'email', 'first_name', 'last_name', 'phone']
        });
      },
      filter: function filter() {
        var modelDetails = $.extend(standardData(this), this.actionmodel.attributes);
        var searchVal = this.$el.find('#events-search').val(),
            self = this,
            $target = this.$el.find('.events-slider').or('.more-events-stacked').or('.no-near-events');

        if (searchVal && /\d/.test(searchVal)) {
          //need regexp to validate that it's a zip
          var searchOptions = {};
          searchOptions.zip = searchVal;
          searchOptions.radius = 500;

          if (!!modelDetails.belongs_to_umbrella_event) {
            searchOptions.umbrella_event_id = modelDetails.belongs_to_umbrella_event.id;
          }

          var dfd = searchevents(searchOptions).then(function (r) {
            return _.first(_.values(r.data), 10) || [];
          }, function () {
            return resolve(self.currentlist); //failures can revert to the old list
          }).done(function (more_events) {
            var $eventList = self.$el.find('.event-list');

            if (!more_events[0]) {
              if ($eventList.hasClass('w-swiper')) {
                self['p_swiper'].remove();
              }

              $eventList.html("<p class='no-near-events'>Sorry, we can't find any events near this location.</p>");
            } else {
              Actions.add(more_events, {
                merge: true,
                silent: true
              });
              self.currentlist = more_events;

              if ($eventList.hasClass('w-swiper')) {
                self['p_swiper'].remove();

                if (more_events.length < 3) {
                  $eventList.html(Handlebars.partials['events-slider']({
                    events: _.first(more_events, 10),
                    numslide: more_events.length
                  }));
                } else {
                  $eventList.html(Handlebars.partials['events-slider']({
                    events: _.first(more_events, 10),
                    numslide: 3
                  }));
                }

                self['p_swiper'].add();
              } else {
                $eventList.html(Handlebars.partials['more-events-stacked']({
                  events: _.first(more_events, 6)
                }));
              }
            }
          });
          saveicon(dfd, $target);
        }
      },
      submit: stopevent,
      //triggers validation attempt without triggering a real submit
      simplersvp: function simplersvp(e) {
        stopevent(e);
        var spinnerTarget = this.template === "event-fullpage" ? this.$el.find('.rsvp-block') : this.$el;
        saveicon(takeAction(this, {
          email: user.get('email'),
          zipcode: user.get('zipcode')
        }), spinnerTarget);
      },
      rsvp: function rsvp(e, dfd, orgdata) {
        //sign via actual form
        var fillindata,
            fields = ['first_name', 'last_name', 'email', 'zipcode', 'phone'];

        if (!user.get('loggedin')) {
          fillindata = _.pick(orgdata, fields); //total overwrite that's NOT stored prior to registration/login

          var oldphone = fillindata['phone'];
          var newphone = oldphone.toString().replace(/^1/, '');
          fillindata['phone'] = newphone;
          lug(orgdata, 'filling in missing data', fillindata); //if user isn't logged in, let's store their field entries localy for now

          user.set(fillindata, silent);
        } else if (!user.get('phone')) {
          var oldorgphone = orgdata.phone;
          orgdata.phone = oldorgphone.toString().replace(/^1/, '');
          user.set('phone', orgdata.phone, silent);
        } //in this case, the form submission was _already_ the action getting taken, so the dfd is sent into takeAction such that it can chain its behavior off that request rather than making a new one


        takeAction(this, user.pick(fields), dfd, {
          external: true
        }).then(gaEventSetter("Campaign Action", "RSVP", "".concat(window.origin, "/events/").concat(this.actionmodel.get('slug'))));
      }
    }),
    donate: SimpleView.extend({
      plugins: [addSelectize],
      initialize: function initialize(options, extras) {
        extras.watch = true;
        window.Stripe.setPublishableKey(seed.stripe_key);
        sv_init(this, options, extras);
        this.actionmodel.set('step', 0);
        this.actionmodel.set('curAmount', 0);
        this.actionmodel.set('savedData', {});
      },
      stepFields: [['amount', 'donate-other'], ['email', 'first_name', 'last_name', 'city', 'state', 'zipcode', 'country', 'street_address'], ["card_number", "exp_month", "exp_year", "card_cvv_number"]],
      events: {
        'click .passed-step': 'updateStep',
        'click .next-button.to-1': 'valOther',
        'focus .donate-other': 'other',
        'blur .donate-other': 'checkhidden',
        'focus .zipcode, .card-cvv-field': 'ie11',
        'keyup .card-field': 'cardtype',
        'change [name="country"]': 'statereq',
        'submit form': 'formSubmit',
        'click .donate-radio-label': 'uncheckhidden',
        'click .donate-button': 'stripeToken',
        'formerror': 'handleError',
        'formsuccess': 'donated'
      },
      mutatedata: function mutatedata(d) {
        var oldData = this.actionmodel.get('savedData'); //fields so far, which get saved above user-entered ones

        d.savedData = _.omit(d.savedData, this.stepFields[2]); //leave cc fields out

        if (d.savedData.amount) {
          if (d.savedData.amount.match(/[^\d\.\,\$](\.[0-9][0-9])?/)) ; else {
            d.savedData.amount.replace(/[^\d\.]+/g, '').replace(/^(0*)(.*\.\d{2})(.*)/, '$2');
          }

          this.actionmodel.set('curAmount', d.savedData.amount);
        }

        return $.extend({}, d, {
          required_params: ['email', 'first_name', 'last_name', 'city', 'state', 'zipcode', 'street_address', 'country']
        }, _.pick(user.attributes, pinfokeys), oldData);
      },
      updateStep: function updateStep(e) {
        // programatically change steps, with checking.
        var currentStep = this.actionmodel.get('step');
        var clickedStep = $(e.currentTarget).data('stepnum');

        if (clickedStep > currentStep) {
          this.saveForm();
          this.$el.find('[type="submit"]').trigger('click'); //trigger validation, does this instead of submit because of reasons
        } else if (currentStep !== clickedStep) {
          this.saveForm();
          this.changeStep(clickedStep); //just move to the new step, it's ok

          if (clickedStep === 0 && this.actionmodel.get('curAmount') !== 0) {
            this.$el.find('input[value="' + this.actionmodel.get('curAmount') + '"]').attr('checked', 'checked');
            var sanitizedVal = this.$el.find('.donate-other').val().replace(/[^\d\.]+/g, '').replace(/^(0*)(.*\.\d{2})(.*)/, '$2');

            if (sanitizedVal === this.actionmodel.get('curAmount')) {
              this.$el.find('.donate-other').focus();
            }
          }
        }
      },
      changeStep: function changeStep(toStep) {
        this.actionmodel.set('step', toStep); //just move steps with no checking or logic (so it's an internal usage)

        $('.donation-step-action').removeClass('passed-step');

        if (toStep === 1) {
          $('.donation-step-action[data-stepnum="0"]').addClass('passed-step');
          this.statereq();
        }

        if (toStep === 2) {
          var donateAmount = 'Donate $' + this.actionmodel.get('curAmount');
          $('.donation-step-action[data-stepnum="0"], .donation-step-action[data-stepnum="1"]').addClass('passed-step');
          $('.donate-button').html(donateAmount);
        }

        var donation = {
          step: this.actionmodel.get('step'),
          formId: this.actionmodel.get('c_code'),
          amount: this.actionmodel.get('curAmount'),
          campaign: $('.main-title').first().text()
        };
        gaEventSetter(null, null, null, donation);
      },
      updateData: function updateData(data) {
        var oldData = this.actionmodel.get('savedData');

        if ($('[data-stripe="number"]').val()) {
          data.card_number = $('[data-stripe="number"]').val();
        }

        if ($('[data-stripe="cvv"]').val()) {
          data.card_cvv_number = $('[data-stripe="cvv"]').val();
        }

        if ($('[data-stripe="exp_month"]').val()) {
          data.exp_month = $('[data-stripe="exp_month"]').val();
        }

        if ($('[data-stripe="exp_year"]').val()) {
          data.exp_year = $('[data-stripe="exp_year"]').val();
        }

        this.actionmodel.set('savedData', _.extend(oldData, data), silent);
      },
      saveForm: function saveForm() {
        //save the current state of the current form at this point
        var data = _.omit(this.$el.find('form').serializeObject(), 'card_cvv_number');

        this.updateData(data);
      },
      ie11: function ie11() {
        $('.form2 .zipcode, .form3 .card-cvv-field').find('input').removeAttr('placeholder');
      },
      statereq: function statereq() {
        if ($('[name="country"]').val() !== 'United States' && $('[name="country"]').val() !== '') {
          $('[name="state"]').removeAttr('required');
        } else {
          $('[name="state"]').attr('required', 'required');
        }
      },
      valOther: function valOther() {
        if ($('.form1').find('input[name="amount"]:checked').val() !== undefined$1 && $('.form1').find('.donate-other').val().match(/[^\d\.\,\$](\.[0-9][0-9])?/g)) {
          $('.form1').find('.donate-other').val('');
        }
      },
      stripeToken: function stripeToken(e) {
        $('.form3 input[name="stripeToken"]').remove();
        var $form = $('.form3');
        $form.find('.stripe-error').remove();
        var actionmodel = this.actionmodel;
        window.Stripe.card.createToken($form, function (status, response) {
          var errorMsgPre = '';

          if (response.error) {
            errorMsgPre = response.error.message;
            var donation = {
              step: 3,
              formId: actionmodel.get('c_code'),
              amount: actionmodel.get('curAmount'),
              campaign: $('.main-title').first().text(),
              orderId: response.error
            };
            gaEventSetter(null, null, null, donation);
          } else if ($form.find('[data-stripe="exp_month"]').val() === '' || $form.find('[data-stripe="exp_year"]').val() === '') {
            errorMsgPre = "Your card's expiration date is invalid.";
          } else if ($form.find('[data-stripe="cvc"]').val() === '') {
            errorMsgPre = "Your card's security code is invalid.";
          }

          if (errorMsgPre !== '') {
            $form.find('.card-info li').removeClass('error');
            $form.find('.error-message').remove();
            var errorMsg = '<div class="stripe-error error-message">' + errorMsgPre + '</div>';
            var cardError = /card number/i;
            var dateError = /expiration/i;
            var cvvError = /security code/i;

            if (errorMsgPre.match(cardError)) {
              $form.find('.card').addClass('error').append(errorMsg);
            } else if (errorMsgPre.match(dateError)) {
              $form.find('.exp-month').addClass('error').append(errorMsg);
            } else if (errorMsgPre.match(cvvError)) {
              $form.find('.card-cvv').addClass('error').append(errorMsg);
            }

            return false;
          } else {
            // Token was created!
            var token = response.id;
            $form.append($('<input type="hidden" name="stripeToken">').val(token));
            $form.submit();
            var _donation = {
              step: 3,
              formId: actionmodel.get('c_code'),
              amount: actionmodel.get('curAmount'),
              campaign: $('.main-title').first().text(),
              orderId: token
            };
            gaEventSetter(null, null, null, _donation);
          }
        });
      },
      formSubmit: function formSubmit(e) {
        var currentStep = this.actionmodel.get('step');
        var formNum = parseInt(currentStep, 10) + 1;
        var passes = true;

        if (currentStep === 0) {
          stopevent(e);

          if ($('.form' + formNum).find('input[name="amount"]:checked').val() === undefined$1 || $('.form' + formNum).find('input[name="amount"]:checked').val().match(/[^\d\.]/g) || $('.form' + formNum).find('input[name="amount"]:checked').val() <= 0) {
            passes = false;
          }
        } else if (currentStep === 1) {
          stopevent(e);
          this.stepFields[currentStep].forEach(function (curReq) {
            if (curReq === 'email') {
              //var emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+(\.)?)+([a-zA-Z]{2,3})?))$/;
              if (!$('.form' + formNum).find('input[name="email"]').val().match(emregex)) {
                passes = false;
              }
            } else if (curReq === 'state') {
              if ($('.form2 [name="country"]').val() === 'United States' && $('.form2 [name="state"]').val() === '') {
                passes = false;
              }
            } else if ($('.form' + formNum).find('input[name="' + curReq + '"]').val() === "") {
              passes = false;
            }
          });
        }

        this.saveForm();

        if (passes && currentStep !== 2) {
          this.changeStep(currentStep + 1);
        }
      },
      other: function other(o) {
        this.$el.find('.donate-other-radio').prop('checked', true);
        $('.donate-radio').removeAttr('required');
        $('.donate-other').attr('required', 'required').attr('placeholder', '');
      },
      checkhidden: function checkhidden(n) {
        var sanitizedVal = this.$el.find('.donate-other').val().replace(/[^\d\.]+/g, '').replace(/^(0*)(.*\.\d{2})(.*)/, '$2');
        var unSanitizedVal = this.$el.find('.donate-other').val(); // this.$el.find('.donate-other-radio, .donate-other').val(sanitizedVal);

        this.$el.find('.donate-other-radio').val(sanitizedVal);
        $('.donate-other').attr('placeholder', 'Other'); // this.$el.find('.donate-other-radio').prop('checked', sanitizedVal > 0);

        if (unSanitizedVal === '') {
          this.$el.find('.donate-other-radio').prop('checked', false);
          $('.donate-other').removeAttr('required');
          $('.donate-radio').attr('required', 'required');
        } else if (unSanitizedVal.match(/^\$?(([1-9]\d{0,2}(,\d{3})*)|(([1-9]\d*)?\d))(\.\d\d)?$/) && sanitizedVal > 0) {
          this.$el.find('.donate-other-radio').prop('checked', true);
          $('.donate-other').removeAttr('required');
          $('.donate-radio').attr('required', 'required');
        } else {
          this.$el.find('.donate-other-radio').prop('checked', false);
          $('.donate-radio').removeAttr('required');
          $('.donate-other').attr('required', 'required');
        }
      },
      uncheckhidden: function uncheckhidden() {
        this.$el.find('.donate-other-radio').prop('checked', false);
        $('.donate-other').removeAttr('required');
        $('.donate-radio').attr('required', 'required');
      },
      cardtype: function cardtype() {
        $('.card-type').removeClass('active');
        var cardType = $('.card-field').val();
        var isVisa = /^4/;
        var isMC = /^5/;
        var isAmex = /^3/;
        var isDisc = /^6/;

        if (cardType.match(isVisa)) {
          $('.visa').addClass('active');
        }

        if (cardType.match(isMC)) {
          $('.mc').addClass('active');
        }

        if (cardType.match(isAmex)) {
          $('.amex').addClass('active');
        }

        if (cardType.match(isDisc)) {
          $('.disc').addClass('active');
        }
      },
      handleError: function handleError(e, errorResponse) {
        this.$el.find('.donation-step ol li, .donation-step ul li').removeClass('error'); //markErrors will run after this, on the form that's active, so all we need to do is set the form

        var errorKeys = _.keys(errorResponse.errors),
            firstErrorStep = _.findIndex(this.stepFields, function (list) {
          return _.intersection(list, errorKeys).length;
        });

        if (firstErrorStep === -1) {
          firstErrorStep = 2;
        }

        this.changeStep(firstErrorStep);
        this.$el.find('.donation-step').removeClass('error');
        this.$el.find('[data-stepnum="' + firstErrorStep + '"]').addClass('step-error');
      },
      donated: function donated(e, dfd, orgdata) {
        stopevent(e);
        saveicon(takeAction(this, user.pick(this.actionmodel.get('required_params')), dfd, {
          external: true
        }), this.$el);
      }
    })
  };

  function loginRequest(popup) {
    $body.addClass('login-request');
    var pollTimer = setInterval(function () {
      if (popup.closed !== false) {
        // !== is required for compatibility with Opera
        clearInterval(pollTimer);
        $body.removeClass('login-request');
      }
    }, 200);
  }
  /*bind all general ui behavior*/


  $body.on('click', '[data-event]', function (e) {
    var $el = $(this),
        data = $el.data();
    $.Topic(data.event).publish(e, $el, data);
  }).on('submit', '.page-form', function (e) {
    //lug('pageform fired');
    formSubmit(e, $(this));
  }).on('click', '.dimmed', function (e) {
    $(this).removeClass('dimmed');
  }).on('submit', '[data-formaction="moleform"]', function (e) {
    stopevent(e);
    var $el = $(this);

    if ($el.find('.phone').val()) {
      var oldphone = $el.find('.phone').val().toString();
      $el.find('.phone').val(oldphone.replace(/^1/, ''));
    }

    if (!$el.hasClass('inactive') && !$el.hasClass('submitting')) {
      moleSubmit(e, $el.addClass('submitting'));
    }
  }).on('blur', 'input[name="phone"]', function () {
    var oldnumber = $(this).val();
    var newnumber = oldnumber.toString().replace(/^1(-)?/, '');
    $(this).val(newnumber);
  }).on('change keyup', '[data-formaction="moleform"] :input', function () {
    $(this).addClass('value-updated').closest('.inactive').removeClass('inactive');
    var clean = $(this).val().replace(/[<*]/gm, '').replace(/[>*]/gm, '');
    $(this).val(clean);
  }).on('click', '.surprise-me label', function () {
    var $form = $(this).closest('form');
    $form.trigger('reset').find('label').removeClass('checked');
    $form.one('click', 'label', function () {
      $form.find('li').last().find('label').removeClass('checked').prev('input').click();
    });
  }).on('click', '.check-labels label', function () {
    var $label = $(this);

    if ($label.prev(':radio').length) {
      $label.closest('li').find('label').removeClass('checked');
    }

    if ($label.is('.email_opt_in_yes')) {
      $('.check-labels').removeClass('inactive');
      $label.closest('ul').find('li').removeClass('inactive');
    }

    if ($label.is('.email_opt_in_no')) {
      $('.check-labels').removeClass('inactive');
      $label.closest('ul').find('li').each(function () {
        if (!$(this).hasClass('opt-out-all')) {
          $(this).addClass('inactive');
        }
      });
    }

    $label.toggleClass('checked');
  }).on('click', '.account-radio-group li.inactive label', function () {
    if ($(this).hasClass('checked')) {
      $(this).removeClass('checked');
    } else {
      $(this).addClass('checked');
    }

    return false;
  }).on('click', '.smooth-scroll', function (e) {
    var $el = $(this),
        href = $el.attr('href') || $el.data('href'),
        //search for the id or maybe also the nearest class
    lookup = href.replace(/\//g, '\\/') + ',.' + href.replace(/[#\/]/g, ''),
        //basically, if the hash link is next, go to the next section unless it doesn't exist, in which case go to the top, same it it's specific
    target = href === '#next' ? $($el.closest('section').next('section:not(.donate-action-section)').get(0) || $('.action-section').last().get(0)) : href === "#take-action" ? $($body.find('.action-section').not('.action-complete').get(0)) : $($body.find(lookup).get(0) || '#page-header'),
        offset = target.eq(0).offset().top;
    lug(href, 'got', target); //animate only if the device is big

    stopevent(e);
    scrollorJumpTo(offset); // any marquee click and all Next Action clicks

    if (target.eq(0).closest('section').hasClass('closed-section')) {
      target.eq(0).find('.marquee-header-left').click();
    }

    if ($el.is('.action-bar-item')) {
      router.navigate(router.lastroute + '/' + href.replace(/[#]/g, ''));
    }
  }).on('click', '[data-expand-section]', function (e) {
    stopevent(e, true);
    var $el = $(this),
        $top = $el.closest(dataTemplateSelector),
        target = $el.data('expand-section') + '-expanded';
    $top.toggleClass(target + ' collapsed');
  }).on('click', '[data-expand]', function (e) {
    stopevent(e);
    var $el = $(this),
        $scope = $el.closest(dataTemplateSelector).or('closest', 'section'),
        what = $el.data('expand'),
        $target = $scope.find('.expandable').filter('[data-expandable="' + what + '"]'),
        open = !$target.hasClass('closed'),
        //
    $togglers = $scope.find('[data-expand="' + what + '"]'); //console.log('hrm',{clicked:$el,template:$scope,expandwhat:what});

    $target.toggleClass('pre-close', open);
    $.wait(20).done(function () {
      $target.toggleClass('closed', open);
      $togglers.toggleClass('hide-expander', open);

      if (oldie) {
        $.wait(100).done(function () {
          $target.css('top', open ? 0 : 1).parent().css('top', open ? 0 : 1);
        });
      }

      $.wait(300).done(function () {
        $target.removeClass('pre-close');
      });
      $togglers.filter('.hide-when-expanded').toggleClass('hide-expander', !open);
      $togglers.filter('.hide-when-closed').toggleClass('hide-expander', open);
    });
  }).on('mouseup', '.action-section .marquee-header-left', function () {
    var $what = $(this).closest(dataTemplateSelector).closest('section').removeClass('default-state'),
        $content = $what.find('.action-content').addClass('pre-close'),
        open = $what.hasClass('open-section');
    $.wait(20).done(function () {
      $content.removeClass('pre-close');
      $what.toggleClass('open-section', !open);
      $what.toggleClass('closed-section', open);
    });
  }).on('click', '[data-toggle-dropdown]', function (e) {
    //dropdowns
    var $el = $(this),
        $top = $el.closest('ul'),
        target = $el.attr('data-toggle-dropdown'); // Toggle dropdown display class on $top

    if ($top.hasClass('show-' + target)) {
      $top.removeClass();
    } else if (!$top.hasClass()) {
      $top.removeClass();
      $top.addClass('show-' + target);
    } // Toogle active class on nav items


    if ($el.hasClass('dropdown-active')) {
      $el.removeClass('dropdown-active');
    } else {
      $('[data-toggle-dropdown]').not($el).removeClass('dropdown-active');
      $el.addClass('dropdown-active');
    }

    stopevent(e, true);
  }).on('click', 'a[href^="mailto"]', function (e) {
    //lug('MAILTO');
    var popup;

    if (!navigator.userAgent.match(/Android|iPhone|iPad|iPod/i)) {
      popup = window.open('/mailto#' + $(this).attr('href'), 'Mailer', "height=420,width=480,fulscreen=no,location=yes,status=no,toolbar=no,resizeable=yes", !1);
    }

    if (popup && !popup.closed) {
      //if open failed, this will be null, so last ditch lets let the email open
      stopevent(e, true);
    }
  }).on('click', '#fbOverlay', function (_) {
    return $body.find('#fbClose').click();
  }).on('click', '.fb-share-campaign', function (e) {
    stopevent(e);
    var $share_section = $('[data-template="action-recruit"]');
    getAM($share_section).set({
      action_taken: false
    });
    $share_section.find('.fb-share-btn').trigger('click');
  }).on('click', '.email-share-campaign', function (e) {
    stopevent(e);
    var $section = $('[data-template="action-recruit"]'),
        am = getAM($section); //lug('email click',$section.data().view);

    if ($section.length) {
      window.csam = am;
      am.set({
        action_taken: false
      });
      gotContacts.fire(null);
      scrollorJumpTo(Math.round($section.offset().top) - 47);
    }
  }).on('click', '.action-reset', function (e) {
    stopevent(e);
    var $node = $(this).closest(dataTemplateSelector),
        am = getAM($node);
    am.set({
      action_taken: !am.get('action_taken'),
      action_took: !am.get('action_taken'),
      "email-step": false
    });
    user.trigger('change:historyactions');
    window.am = am;
    window.view = getView($node);
  }).on('click', '.action-open', function (e) {
    stopevent(e);
    var $node = $(this).closest(dataTemplateSelector),
        am = getAM($node);
    am.set({
      formactive: !am.get('formactive'),
      action_took: am.get('formactive')
    });
    window.am = am;
    window.view = getView($node);
  }).on('click', '.fb-share-btn', function (e, data) {
    var $el = $(this),
        url = data && data.url || $el.data('url'),
        scroll = $w.scrollTop(); //fb share dialog tries to scroll the page automatically to the top for some dumb reason, this gives us a chance to cancel that

    $w.one('scroll.fb', $w.use('scrollTop', scroll));
    fb_init.done(function () {
      FB.ui({
        method: 'share',
        href: url
      }, function (response) {
        $w.off('scroll.fb'); //ok stop trying to cancel it

        if (response && !response.error_code && !$.isEmptyObject(response)) {
          lug('fb share', response); //alert('Posting completed.');

          $el.trigger('action');
        } else {
          lug('no fb share'); //alert('Error while posting.');
        }
      });
    });
  }).on('click', '.try-another', function (e) {
    var $el = $(this),
        offset = ($el.data('offset') || 0) + 1,
        $existing = $('#single-action').find(dataTemplateSelector);
    $el.data('offset', offset);
    $existing.data('view').remove();
    $existing.closest('section').remove();
    nextAction(offset);
  }).on('click', '.tabs li', function (e) {
    var $el = $(this),
        tabId = $el.data('tab'),
        route = $el.data('route'),
        $tabNav = $el.closest('.tabs');
        $tabNav.find('li').removeClass('selected');
        var $tabs = $tabNav.nextAll('.tab');

    if (route) {
      router.navigate(route, {
        trigger: false
      });
      router.lastroute = route;
    }

    $el.addClass('selected');
    $tabs.addClass('hidden').filter(tabId ? "#".concat(tabId) : ':first').removeClass('hidden');
  }).on('click', '.tooltip, .tooltip .closebutton', function (e) {
    var $tt = $(this).closest('.tooltip'),
        on = $tt.hasClass('show');

    if (Modernizr.touchevents) {
      stopevent(e, true, true);
      $body.find('.tooltip').removeClass('show');
      $(this).closest('.tooltip').toggleClass('show', !on);
    }
  }).on('click', '.main-nav', function (e) {
    return e.stopPropagation();
  }).on('click', clearNav) //all clicks not canceled by this point should clear the nav
  .on('click', '.clickthrough', function () {
    $(this).data('ahref') || $(this).attr('href');
  }).on('click', '.popup', function (e) {
    var link = $(this).data('ahref') || $(this).attr('href'),
        login = link && link.indexOf('/login') > -1,
        popup;

    if (login && directlogin || $(this).hasClass('clickthrough') && window.innerWidth < 769) {
      window.location = link;
    } else if (login && directlogin) {
      window.location = link;
    } else {
      popup = window.open(link, "ConnectWithOAuth", (login ? "height=380,width=420" : "height=520,width=480") + "fullscreen=no,location=yes,status=no,scrollbars=no,toolbar=no,resizeable=yes", !1); //lug('opening',link,login);

      if (popup) {
        stopevent(e);
        e.stopImmediatePropagation();

        if (login) {
          loginRequest(popup);
        }
      }
    }
  }).on('click', '.n-share', function (e) {
    stopevent(e);
    modalShare($(this).data());
  }).on('click', '.cms-url', function () {
    window.open('/cms');
  }).on('mouseover', '[type="date"]', function () {
    if (window.ios) {
      $(this).removeAttr('value'); //attempt to allow iOS to clear date fields that are prefilled
    }
  }).on('keydown', '.petition-longtext', function (e) {
    if ($('.petition-longtext').val().length >= 5000) {
      if (event.keyCode !== 8 && event.keyCode !== 46) {
        e.preventDefault();
        return false;
      }
    } else if (!$('.petition-charcount-container').is(':visible')) {
      $('.petition-charcount-container').css({
        'display': 'block'
      });
    }
  }).on('keyup', '.petition-longtext', function (e) {
    $('.petition-charcount').html($('.petition-longtext').val().length);
  }).on('keydown', '.petition-personal', function (e) {
    if ($('.petition-personal').val().length >= 1000) {
      if (event.keyCode !== 8 && event.keyCode !== 46) {
        e.preventDefault();
        return false;
      }
    } else if (!$('.petition-personal-container').is(':visible')) {
      $('.petition-personal-container').css({
        'display': 'block'
      });
    }
  }).on('keyup', '.petition-personal', function (e) {
    $('.petition-charcount-personal').html($('.petition-personal').val().length);
  }).on('click', '#events-header a[target="_blank"], .events-section a[target="_blank"], .featured-event a.umbrella-event-listing__partof, a[href="/events/your-events"]', function (e) {
    e.preventDefault();
    window.open($(this).attr('href'));
  }).on('click', '.resetForm', function (e) {
    e.preventDefault();
    clearAuth();
  }).on('change', '.prefix', function (e) {
    $('span.prefix').text($('select[name="prefix"] option[selected="selected"]').val());
  }).on('keyup', '.firstname', function (e) {
    $('span.firstname').text($('input[name="firstname"]').val() || $('input[name="first_name"]').val());
  }).on('keyup', '.lastname', function (e) {
    $('span.lastname').text($('input[name="lastname"]').val() || $('input[name="last_name"]').val());
  }).on('keyup', '.street_address', function (e) {
    if ($('input[name="street_address"]').val().length && !$('input[name="additional_address"]').val().length) {
      $('span.street_address').text($('input[name="street_address"]').val() + ';');
    } else {
      $('span.street_address').text($('input[name="street_address"]').val());
    }
  }).on('keyup', '.additional_address', function (e) {
    if ($('input[name="additional_address"]').val().length) {
      $('span.street_address').text($('input[name="street_address"]').val() + ' ');
      $('span.additional_address').text($('input[name="additional_address"]').val() + ';');
    } else {
      $('span.additional_address').text($('input[name="additional_address"]').val());

      if ($('input[name="street_address"]').val().length) {
        $('span.street_address').text($('input[name="street_address"]').val() + ';');
      }
    }
  }).on('keyup', '.city', function (e) {
    if ($('input[name="city"]').val().length) {
      $('span.city').text($('input[name="city"]').val() + ',');
    } else {
      $('span.city').text($('input[name="city"]').val());
    }
  }).on('change', '.state', function (e) {
    $('span.state').text($('select[name="state"] option[selected="selected"]').val());
  }).on('keyup', '.zipcode', function (e) {
    $('span.zipcode').text($('input[name="zipcode"]').val());
  }).on('keyup', '.phone', function (e) {
    $('span.phone').text($('input[name="phone"]').val() + ' | ');
  }).on('keyup', '.email', function (e) {
    $('span.email').text($('input[name="email"]').val());
  }).on('click', 'a[href^="/"]', function (e) {
    // on every click, check if it's an href that can be handled by the router
    // clean out leading/trailing slashes
    lug('link click');
    var $el = $(e.currentTarget),
        href = $el.attr('href').replace(/^\/+/, '').replace(/\/+$/, ''),
        action,
        loc = $el.closest(dataTemplateSelector).data('template');
    lug('router caught it');

    if (/refresh/.test(href)) {
      stopevent(e);
      location.reload();
    } else {
      if (e.isDefaultPrevented() || e.metaKey || e.ctrlKey) {
        return;
      }

      if (/^campaigns/.test(href)) {
        //lug('campaign link click',href);
        if (!loc || loc === 'swiper-slider') {
          loc = $el.closest('section').attr('class').split(' ')[0];
        } //lug('router loc',loc,$el.closest('section'));


        report(['Campaign Click', loc, href.split('/')[1]], 0, {
          'nonInteraction': 1
        });
      }

      if (/^saml/.test(href)) {
        setcookie('saml_login_redirect_url', encodeURI(window.location.href), 1 / 1440 * 15); // 15 minutes
      }

      if (!$el.hasClass('static-page')) {
        stopevent(e);
        action = $.Deferred().done(function () {
          Backbone.history.navigate(href, {
            trigger: true
          });
        });

        if (online) {
          action.resolve();
        } else {
          cachedNav = action; //last nav attempt is the one we'll use

          $notify.html("<br>/" + href);
        }
      }
    }
  });

  function checkNotify() {
    if (notify) {
      if (notify === "verified" || notify === "not-verified" || notify === "login-error") {
        nextload(false, false).done(function (_) {
          return modalView({
            'template': notify,
            'watch': false
          });
        });
      }
    } else if (firstvisit) {
      nextload(false, false).done(function (_) {
        return setcookie('splashed', true, 32);
      });
    }
  } //lug('now trying to start the app');


  if (guid && mid && !user.get('email')) {
    //lug('attempting to transfer the user session over to a new tab');
    user.set('moleid', mid, silent);
    online = true;
    molecollection().done(function (d) {
      login(d, true, true, true); //don't nag, don't create ga, don't log new login event

      if (fbmaybe) {
        fb_connect.done(fbConnected, function () {
          if (user.get('fbconnected') && user.get('fbloggedin') && !user.get('avatar')) {
            getFBPicture();
          }
        }); //local data claimed we were logged in, so assuming fb connects, say we're logged in w/ fb
      } else {
        user.set({
          'fbloggedin': false
        }, silent);
      }
    }).fail(function () {
      user.set({
        'fbloggedin': false,
        'moleid': false
      }, silent);
    }).always(startApp.resolve); //if we still have a valid session, activate it, and regardless, let's load the page
  } else if (fbmaybe) {
    //lug('trying to start app via fb');
    //once fb is ready, attempt to connect via fb then set user to fb connect before starting app
    fb_init.then(fbReg).then(fbConnected).fail(disconnectFB).always(startApp.resolve); //start the application regardless of what happened
  } else {
    if (user.get('loggedin') && guid && mid) {
      refreshSession(guid, mid);
    } //lug('already logged in or no existing session',guid,mid,JSON.parse(JSON.stringify(_.omit(user.attributes,'randfb_friends'))));


    startApp.resolve(); //no weird states, just load serially in the next step
  } // set GA params for crometrics


  var gaEventSetter = function gaEventSetter(eventCategory, eventAction, eventLabel, donation) {
    window.dataLayer = window.dataLayer || [];

    if (donation) {
      if (donation.step === 1 || donation.step === 2) {
        window.dataLayer.push('event', 'begin_checkout', {
          'currency': 'USD',
          'value': donation.amount,
          'items': [{
            'item_brand': 'addup',
            'item_category': 'donation',
            'item_id': donation.formId,
            'item_name': donation.campaign,
            'quantity': 1,
            'price': donation.amount
          }]
        });
      } else {
        window.dataLayer.push('event', 'purchase', {
          'currency': 'USD',
          'value': donation.amount,
          'transaction_id': donation.orderId,
          'items': [{
            'item_brand': 'addup',
            'item_category': 'donation',
            'item_id': donation.formId,
            'item_name': donation.campaign,
            'quantity': 1,
            'price': donation.amount
          }]
        });
      }
    } else {
      window.dataLayer.push({
        'event': 'eventCategory',
        'eventCategory': eventCategory,
        'eventAction': eventAction,
        'eventLabel': eventLabel
      });
    }
  };

  function checkForToken() {
    var token = gup('db_token');
    return !token ? resolve() : moleapi("/users/token_collection/".concat(token)).then(function (json) {
      var userjson = json.user || {};
      var obj = {
        address: {
          additional_address: userjson.additional_address,
          city: userjson.city,
          country: userjson.country,
          phone: userjson.phone,
          state: userjson.state,
          street_address: userjson.street_address,
          zipcode: userjson.zipcode
        },
        email: userjson.email,
        id: userjson.id,
        profile: {
          first_name: userjson.first_name,
          last_name: userjson.last_name,
          prefix: userjson.prefix
        }
      };
      return user.set(_.pick(translateMoleLogin(obj), pinfokeys)), function (e) {
        return resolve();
      };
    });
  }

  startApp.then(checkForToken).done(checkNotify).done(function () {
    lug('starting app', {
      verified: notify,
      althomepage_url: althomepage_url,
      join_login: join_login,
      prompt_login: prompt_login
    });

    var string = _.map(_.pairs(urlpinfo), function (a) {
      return a[0] + '=' + a[1];
    }).join('&'),
        regex = new RegExp(string.replace(/\&/g, '\\&?|').replace(/\+/g, '\\+').replace(/\./g, '\\.').replace(/\(/g, '\\(').replace(/\)/g, '\\)'), 'gi');

    var zipRegex = /^(\d{5}){1}(-?\d{4})?/; // first use param zipcode if available

    if (zipRegex.test(urlpinfo.zipcode)) {
      urlpinfo.zipcode = urlpinfo.zipcode.match(zipRegex)[1];
    } else {
      // try user account zipcode next
      if (zipRegex.test(user.get('zipcode'))) {
        urlpinfo.zipcode = user.get('zipcode').match(zipRegex)[1];
      } else {
        urlpinfo.zipcode = '';
      }
    }
    /*set fields from url before app start IF the user is not logged in*/


    if (!user.get('loggedin')) {
      if (!$.isEmptyObject(urlpinfo) && !gup('code')) {
        //lug('setting because no code',gup('code'));
        _.each(urlpinfo, function (v, k) {
          urlpinfo[k] = decodeURIComponent(k !== "email" ? v.replace(/\+/g, '%20') : v);
        }); //lug('settinguser data from url',urlpinfo,string,$.param(urlpinfo),$.param(urlpinfo).replace(/\+/g,'%20').replace(/\&/g,'\\&?|'),window.location.toString().replace(regex,''));
        //lug('urlpinfo',urlpinfo);


        urlpinfo.prospective = true;
        user.set(urlpinfo, silent).store();

        if (history && history.replaceState) {
          history.replaceState({}, '', window.location.toString().replace(regex, ''));
        }
      }
    }

    gacreate();
    Backbone.history.start({
      root: '/',
      pushState: true
    });
    view($body.find('.user-info').eq(0), {
      model: user
    }); //create the nav
  });

  function ph($el) {
    var $els = $el.find('.placehold').find('input, textarea').not('.placeheld');

    if ($els.length) {
      $els.placeholder(); //need to do this because things can vanish
    }
  }

  function dp($el) {
    $el.find('[type="date"]').each(function () {
      $(this).datepicker({
        dateFormat: "yy-mm-dd",
        maxDate: new Date(2022, 4 - 1, 26),
        minDate: new Date(2016, 4 - 1, 26),
        changeMonth: true,
        changeYear: true,
        yearRange: "2016:2020"
      });
    });
  }

  if (!Modernizr.inputtypes.date) {
    $('head').append('<link type="text/css" rel="stylesheet" href="//code.jquery.com/ui/1.12.1/themes/base/jquery-ui.css">');
    $.ajax({
      url: "/js/vendor/jquery-ui.min.js",
      dataType: "script",
      cache: true
    }).done(function () {
      dp($body);
      $.Topic('viewrender').subscribe(function ($el) {
        return dp($el);
      });
    });
  }

  yepnope([{
    test: Mod.placeholder,
    nope: "/js/polyfills/jquery.placeholder.js",
    callback: function callback() {
      ph($body);
      $.Topic('viewrender').subscribe(function ($el) {
        return ph($el);
      });
    }
  }, {
    "test": Mod.formvalidation,
    "nope": '/js/polyfills/form.validate.js'
  }, {
    "test": Mod.touchevents,
    "yep": ['/js/polyfills/fastclick.js']
  }]);

  function twitterAction(event) {
    var $what = $(event.target),
        id = $what.data('action-id'); //lug('tweet action taken ',event.target,$what,$what.length,id);

    if ($what.length > 0) {
      //record the action and remove anything that should be removed.  Ultimately we should just redo the data here though and let that re-render things
      $what.trigger('action').closest('[data-action-id-remove="' + id + '"]').remove();
    }
  }

  yepnope({
    'load': no_twitter_api ? //events are pointless/broken for these
    '/js/vendor/simple-twitter-intents.js' : '//platform.twitter.com/widgets.js',
    'complete': function complete() {
      $.wait(500).done(function () {
        if (window.twttr && window.twttr.events) {
          window.twttr.events.bind('tweet', function (event) {
            //lug('TWITTER TWEET EVENT', event && event.target, arguments);
            twitterAction(event);
          });
          $body.addClass('twttr-init');
        } else {
          lug('%ctwttr didn\'t load', 'color:red');
        }
      });
    }
  });

  var mobileSize = _.debounce(function (e, force) {
    var oldwidth = wwidth,
        nowWidth = getWidth();
    lug('resizing');
    $body.find('[data-mobile-resize]').each(function () {
      var $el = $(this),
          thresh = $el.data('resize-threshold'),
          formsaver = saveForms($el);

      if (force || oldwidth !== nowWidth && (!thresh || oldwidth >= thresh && nowWidth < thresh || oldwidth < thresh && nowWidth >= thresh)) {
        wwidth = nowWidth;
        $el.data('view').render();
        formsaver.resolve();
      }
    });
  }, 500);

  $w.on('resize', mobileSize);
  /*connectivity*/

  function testConnect() {
    return $.wait(1000).then(function () {
      return $.ajax({
        url: '/js/static-online.json',
        cache: false
      }).done(function () {
        $.wait(1500).done(function () {
          if (!window.FB || !window.twttr) {
            //things are super bad, need to try reloading
            window.location.reload();
          }

          if (!currentViews.length) {
            //things are ok, let's just remake the page
            newPage(router.current().route);
          }
        });
      }).fail(isOffline);
    });
  }

  function hideOffline() {
    $body.removeClass('notify');
  }
  /*fired when the user comes back online AFTER having been offline*/


  function isOnline() {
    //lug('SETTING APP BACK ONLINE');
    online = true;
    hideOffline();
    testConnect().done(function () {
      $notify.html("");
      $.wait(50).done(cachedNav.resolve);

      _.each(cachedReqs, function (dfd) {
        //lug('calling cached stuff',dfd);
        $.wait(50).done(dfd.resolve);
      });

      $.wait(80).done(function (_) {
        return cachedReqs.length = 0;
      });
    });
  }
  /*fired when the user goes offline */


  function isOffline() {
    //lug('SETTING APP TO OFFLINE');
    online = false;
    /*if user wants to, they can click to feel better about trying to connect*/

    $body.addClass('notify').one('click', '.notification', function () {
      hideOffline();
      testConnect().done(isOnline);
    });
    $notify.attr('data-type', 'offline');
  }

  if (window.addEventListener) {
    w.addEventListener("online", isOnline, false);
    w.addEventListener("offline", isOffline, false);
  } else {
    /*
        Works in IE with the Work Offline option in the
        File menu and pulling the ethernet cable
    */
    document.body.ononline = isOnline;
    document.body.onoffline = isOffline;
  }

  if (!online) {
    //lug('started with offline',navigator.onLine);
    isOffline();
  }

  window.ison = isOnline;
  window.csPageOptions = {
    domain_key: seed.cs_domain,
    performEmailValidation: true,
    beforeDisplayContacts: function beforeDisplayContacts(contacts, source, owner) {
      var regex = new RegExp(/^\s?/),
          sortedcontacts; // do something with the contacts array

      sortedcontacts = _.sortBy(_.compact(_.map(contacts, function (contact) {
        var email = contact && contact.email[0] && contact.email[0].address;
        return email ? {
          email: email,
          emname: email + (contact.first_name ? '|' + contact.first_name + (contact.last_name ? '|' + contact.last_name : '') : ''),
          slug: '&lsaquo;' + email + '&rsaquo;',
          name: contact.fullName().replace(regex, ''),
          lname: contact.last_name,
          fname: contact.first_name
        } : false;
      })), 'fname');
      sortedcontacts = _.partition(sortedcontacts, function (g) {
        return g.fname;
      });
      sortedcontacts = _.flatten(_.uniq(sortedcontacts[0], 'email'));
      window.cscontacts = sortedcontacts;
      gotContacts.fire(sortedcontacts, source, owner); // return false if you don't want to display the 'choose your contacts UI'

      return false; // the widget will now close..
    },
    sources: _.without(csclients, 'other') //'windowslive', 'aol', 'addressbook'

  };
  window.u = user;
  window.user = user;
  window.m = models;
  window.auth = auth;
  window.router = router;
  window.uds = user_defaults;
  window.cachedReqs = cachedReqs;
  window.dsrcs = datasources;
  window.tmpsrc = tmpsrc;
  window.tmpsign = tmpsign;
  window.ma = moleapi;
  window.crs = cachedReqs; //window.mr = molerecs;

  window.campaigns = Campaigns;
  window.actions = Actions;
  window.setcookie = setcookie;
  window.deletecookie = deletecookie;
  window.avr = actionViewRefresh;
  window.getCurrentActionViews = getCurrentActionViews;
  window.stats = Stats;
  window.fbconnect = fb_connect;
  window.tsmodels = TSModels;
  window.sourceObj = sourceObj;
  window.logout = logout;
  window.guid = guid;

  window.setguid = function (what) {
    return guid = what;
  };

  window.meerkatMsg = meerkatMsg;
  window.moleavi = moleavi;
  window.mp = molephoto;
  window.transLo = translateMoleLogin;
  window.fbapi = fbapi; // window.rf = randFriends;

  window.cp = checkPerms;
  window.nextAction = nextAction;
  window.tmpsrc = tmpsrc;
  window.fbnoauth = fbnoauth;
  window.fb_init = fb_init;
  window.findData = findData;

  window.close_modal = function (_) {
    return modaldfd.notify();
  };

  window.v = view;
  window.modalView = modalView;
  window.cvs = currentViews;
  window.recReason = recReason;
  window.appFriends = appFriendIds;
  window.molecampaigns = molecampaigns;
  window.resolve = resolve;
  window.moleyourcampaigns = moleyourcampaigns;
  window.campaignsnearby = molecampaignsnearby;
  window.resolvecampaigns = resolvecampaigns;
  window.giveNotice = giveNotice;
  window.use = use;
  window.cbs = catsObject;
  window.device = {
    nua: nua,
    is_crios: is_crios,
    directlogin: directlogin,
    is_anyIE: is_anyIE,
    is_android: is_android,
    no_twitter_api: no_twitter_api
  }; // new header

  var pollForHeader = function pollForHeader() {
    if ($('#page-header').length > 0 && localStorage.getItem('-user')) {
      var userData = sessionStorage.getItem('-user') || localStorage.getItem('-user') || {};

      var _user = JSON.parse(userData);

      var loggedin = _user.loggedin ? 'loggedin' : '';
      var user_image = _user.avatar || '/img/user_no_image.png'; // make header

      var header = '' + '<header id="page-header">' + '   <a class="header-menu">Menu<br/><b>☰</b></a>' + '   <a class="header-logo" href="/" title="AddUp" alt="AddUp"></a>' + '   <nav>' + '       <span class="nav-donate"><a href="https://act.sierraclub.org/donate/rc_connect__campaign_designform?id=7013q000001bNLaAAM&promoid=701i00000015KrcAAE" target="_blank">Donate</a></span>' + '       <span><a href="/campaigns">Browse Campaigns</a></span>' + '       <span><a href="/events">Browse Events</a></span>';

      if (loggedin) {
        header += '       <span><a class="static-page" href="/cms">My Campaigns/Events</a></span>' + '       <span class="nav-loggedin"><a><img src="' + user_image + '" /></a></span>' + '       <div>' + '           <span><a href="/supporter-home">My Impact</a></span>' + '           <span><a href="/account-settings">Account Settings</a></span>' + '           <span><a href="#" class="nav-logout">Log Out</a></span>' + '       </div>';
      } else {
        header += '       <span><a class="static-page" href="/register">Register</a></span>' + '       <span><a class="static-page" href="/saml/login">Login</a></span>';
      }

      header += '   </nav>' + '   <span class="header-donate"><a href="https://act.sierraclub.org/donate/rc_connect__campaign_designform?id=7013q000001bNLaAAM&promoid=701i00000015KrcAAE" target="_blank">Donate</a></span>' + '</header>'; // replace

      $('#page-header').replaceWith(header);

      if (loggedin) {
        $('#page-header').addClass(loggedin);
      }

      checkSize();
      $('#page-header .header-menu').mouseup(function (e) {
        e.preventDefault();
        $('#page-header nav').toggle('fast');
      });
      $('#page-header .nav-loggedin').mouseup(function (e) {
        e.preventDefault();
        $('#page-header nav div').toggle('fast');
      });
      $('.nav-logout').mouseup(function (e) {
        e.preventDefault();
        window.logout();
      });
      $('#page-header div span').mouseup(function (e) {
        $('#page-header div').toggle('fast');
      });
    } else {
      setTimeout(pollForHeader, 50);
    }
  };

  pollForHeader();
  var resized;
  $(window).resize(function () {
    clearTimeout(resized);
    resized = setTimeout(checkSize, 50);
  });

  function checkSize() {
    var width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    if (width < 910) {
      $('#page-header nav div').show();
      $('#page-header nav').hide();
    } else {
      $('#page-header nav').show();
      $('#page-header nav div').hide();
    }
  } // /new header

})(jQuery, window, document, Modernizr, Math);
;(function(d){
    //quick cookie checker
    function c(k){return(d.cookie.match('(^|; )'+k+'=([^;]*)')||0)[2];}

    var ua = navigator.userAgent, //get the user agent string
        ismobile = / mobile/i.test(ua), //android and firefox mobile both use android in their UA, and both remove it from the UA in their "pretend desktop mode"
        mgecko = !!( / gecko/i.test(ua) && / firefox\//i.test(ua)), //test for firefox
        wasmobile = c('wasmobile') === "was", //save the fact that the browser once claimed to be mobile
        desktopvp = 'user-scalable=yes, width=1000, minimum-width=1000',
        el;

    if(ismobile && !wasmobile){
        d.cookie = "wasmobile=was"; //if the browser claims to be mobile and doesn't yet have a session cookie saying so, set it
    }
    else if (!ismobile && wasmobile){
        //if the browser once claimed to be mobile but has stopped doing so, change the viewport tag to allow scaling and then to max out at whatever makes sense for your site (could use an ideal max-width if there is one)
        if (mgecko) {
            el = d.createElement('meta');
            el.setAttribute('content',desktopvp);
            el.setAttribute('name','viewport');
            d.getElementsByTagName('head')[0].appendChild( el );
        }else{
            d.getElementsByName('viewport')[0].setAttribute('content',desktopvp); //if not Gecko, we can just update the value directly
        }
    }
}(document));